import React, { PureComponent } from 'react'
import { createPortal } from 'react-dom'
import { withStyles } from '@material-ui/core/styles'
import {connect} from 'react-redux'
import AboutDlg from './components/AboutDlg'
import Dicomdir from './components/Dicomdir'
import DicomViewer from './components/DicomViewer'
import DicomHeader from './components/DicomHeader'
import DownloadZipDlg from './components/DownloadZipDlg'
import Explorer from './components/Explorer'
import FsUI from './components/FsUI'
import Histogram from './components/Histogram'
import LayoutTool from './components/LayoutTool'
import ToolsPanel from './components/ToolsPanel'
import ToolsEdgePanel from './components/ToolsEdgePanel'
import Measurements from './components/Measurements'
import OpenMultipleFilesDlg from './components/OpenMultipleFilesDlg'
import JoystickMenu from './components/JoystickMenu'
import { CaptureChooser, CaptureToast } from './components/CaptureChooser'
import ShareSheet from './components/ShareSheet'
import ScpDestinationsDlg from './components/ScpDestinationsDlg'
import { getJoystickSlots, filterJoystickSlotsForPortal } from './utils/joystickPrefs'
import {
  buildCaptureFile,
  buildStudyZipFile,
  buildStudyZipFromUrl,
  buildStudyZipFromIdb,
  buildStudyZipDownloadFilename,
  buildSeriesZipDownloadFilename,
  resolveStudyZipMeta,
  buildPreviewShareFiles,
  buildStudyZipFromLocal,
  buildSeriesZipFromFiles,
  getDicomBytesForFile,
  probeRemoteZipSize,
  shareOrFallback,
  zipSizeTier,
  downloadFileFallback,
  blobToFile,
} from './utils/sharePayload'
import {
  buildStudyShareUrl,
  copyTextToClipboard,
} from './utils/studyShareLink'
import {
  listScpDestinations,
  getScpDestination,
  getScpShareDestId,
  setScpShareDestId,
  toPacsConfigShape,
  SCP_SHARE_DEST_DEFAULT,
  SCP_CONNECTOR_STOW,
  SCP_CONNECTOR_ORTHANC,
} from './utils/scpDestinations'
import Settings from './components/Settings'
import AccessDeniedSplash from './components/AccessDeniedSplash'
import InstallPwaHelpDlg from './components/InstallPwaHelpDlg'
import InstallPwaSuccessDlg from './components/InstallPwaSuccessDlg'
import PortalDisclaimerDlg from './components/PortalDisclaimerDlg'
import {
  assertAccess,
  canFeature,
  FEATURE_IDS,
  subscribePlanChange,
  isDeveloperMode,
  UDV_PLANS,
  UDV_PERSONAS,
  getEffectivePacsId,
  parseTicketFromSearchParams,
  parseWorklistFromSearchParams,
  parseInviteFromSearchParams,
  parseEdgeIdFromSearchParams,
  parseElevationTokenFromSearchParams,
  parsePortalFromSearchParams,
  parseHideFullscreenPromptFromSearchParams,
  saveEntitlements,
  loadEntitlements,
  loadControllerLinks,
  saveControllerLinks,
  resolveTicketClinicalElevation,
  setPortalSim,
  getPortalChromeForPlan,
  ENTITLEMENT_SOURCES,
  ACCESS_REASONS,
  resolvePersona,
  shouldShowClinicalAuth,
  isPortalProfile,
  getPlanDisplayLabel,
  getEffectiveTheme,
  waitForWorkspaceElevate,
  redeemWorkspaceElevate,
  setStoredPersona,
} from './plan'
import {
  matchPlatformElevationSession,
  loadPlatformElevationSession,
  savePlatformElevationSession,
} from './plan/platformElevationSession'
import {
  getTicketApiBase,
  parseTicketIdFromPath,
  resolveViewerTicket,
  resolveViewerWorklist,
  pickWorklistCurrentItem,
  stripElevationFromUrl,
} from './services/viewerTicket'
import {
  startTicketLeaseHeartbeat,
  STAFF_LEASE_EXPIRED_EVENT,
  DEFAULT_EXTEND_INTERVAL_SEC,
} from './services/ticketLease'
import { resolveTicketAccessContext } from './services/ticketAccess'
import {
  resolveViewerInvite,
  applyInviteDraftEntitlements,
} from './services/viewerInvite'
import { listStudyCatalog, getStudyCatalog, STUDY_SOURCE } from './db/studyCatalog'
import {
  upsertWorklistSession,
  listWorklistSessions,
} from './db/worklistSessions'
import { resolveStudyCatalogOrigin } from './utils/resolveStudyCatalogOrigin'
import { resolveViewerSessionPolicy } from './utils/viewerTicketPolicy'
import {
  pickViewerCandidate,
  candidateToLoadTarget,
  isOriginFailure,
  ticketOriginMeta,
} from './utils/pickViewerCandidate'
import GsdfQaModal from './components/GsdfQaModal'
import OrthancConnection from './components/OrthancConnection'
import AuthGatewayModal from './components/AuthGatewayModal'
import LocalStudiesDlg from './components/LocalStudiesDlg'
import PatientRelatedStudiesDlg from './components/PatientRelatedStudiesDlg'
import WorklistDlg from './components/WorklistDlg'
import { isGsdfQaPassed } from './utils/gsdfQaPrefs'
import StudyDuplicateDlg from './components/StudyDuplicateDlg'
import StudySourceChoiceDlg from './components/StudySourceChoiceDlg'
import StudyZipDownloadDlg from './components/StudyZipDownloadDlg'
import UdvGatewayToast from './components/UdvGatewayToast'
import StudioShell from './shells/studio/StudioShell'
import OhifShell from './shells/ohif/OhifShell'
import OviyamNgShell from './shells/oviyamng/OviyamNgShell'
import { UDV_SURFACE, udvDialogProps } from './components/udvSurface'
import WWPresetManager from './components/WWPresetManager'
import DsaMenu from './components/DsaMenu'
import TranslationPreventer from './components/TranslationPreventer'
import {
  getPacsConfig,
  resolveToolbarLayout,
  resolveEffectivePacsId,
  parseToolbarQueryParam,
  isUrlParamPlaceholder,
  TOOLBAR_LAYOUTS,
  resolvePacsDefaultRenderMode,
  resolveLegacyWadoContext,
  buildDicomWebBaseUrl,
  buildWadoUriUrl,
} from './config/pacsConfig'
import { loadStudyFromManifestObject } from './utils/loadStudyFromManifest'
import { canonicalizeManifest, resolveManifestReadiness } from './utils/manifestNormalize'
import {
  startManifestPreviewPoll,
  manifestPreviewFingerprint,
  countManifestSeriesAndInstances,
} from './utils/manifestReadinessPoll'
import { sendUdvTelemetryBeacon } from './utils/udvTelemetryBeacon'
import {
  applyUdvTelemetryHintsFromUrl,
  mergeUdvTelemetryFromManifestUrl,
  setUdvTelemetrySessionFromTicket,
} from './utils/udvTelemetryContext'
import {
  resolveStudyLoadSourceFromStats,
  finishStudyLoadSource,
  isStudyLoadActive,
  isStudyLoadAbortError,
  STUDY_LOAD_SOURCE,
  resolveStudyLoadSourceFromManifest,
  studyLoadSourceChipLabel,
  studyLoadSourceDownloadTitle,
  showStudyLoadOriginHint,
  formatStudyLoadOriginLabel,
  isStudyLoadWado,
} from './utils/studyLoadSource'
import {
  mapMasterSliceToSlave,
  seriesKeyFromViewer,
  stackGeometry,
  samePlane,
  pickGeometryFields,
} from './utils/seriesLink'
import {
  mergeStudyFileLists,
  buildExplorerDataFromFiles,
  pickFirstSeriesForStudy,
  studyUidFromFile,
  buildStudySeriesMaps,
  studySeriesStats,
} from './utils/comparePriorStudy'
import CompareModeGuide from './components/CompareModeGuide'
import { resolveSeriesTotalImages } from './utils/seriesImageCounts'
import { configureWadoLoaderAuth } from './utils/wadoAuth'
import { getActiveSeriesSyncMinMs, isConstrainedUiDevice, isPhoneLikeDevice } from './utils/mobileDownloadProfile'
import { assessMprViability } from './utils/mprViability'
import {
  resolveMprProfile,
  mprProfileAllowsOrthogonal,
  mprProfileLabel,
} from './utils/mprProfile'
import {
  ensureMprStackReady,
  computeZSpacingForVolume,
  createPlaneLru,
  enrichFileGeometry,
  collectReadyMprFiles,
} from './utils/mprVolume'
import {
  pixelToVolumeIndices,
  volumeIndicesToViewportSlice,
  clampVolumePoint,
} from './utils/mprCrosshair'
import {
  shouldFlipMprZ,
  ensureMprStackGeometry,
  prepareMprStackFiles,
  registerFileImagePlane,
  registerTargetSlicePlaneOnDisplay,
  buildMprImagePlaneModule,
  registerMprImagePlane,
  buildNativeImagePlaneModule,
} from './utils/mprImagePlane'
import udvRenderActiveReferenceLine from './utils/udvRenderActiveReferenceLine'
import {
  hydratePreviewStackGeometry,
  spreadStackGeometryFromFiles,
} from './utils/previewSliceGeometry'
import { mmToHalfWidth, slabHalfWidthFromMeta } from './utils/mprSlab'
import { detectHardware, resolveSlabProfile } from './utils/hardwareDetector'
import {
  listPetSeriesFromStore,
  pickPetSeriesNotInViewport,
} from './utils/petFusion'
import { flattenPrFiles, listPrSeriesFromStore } from './utils/gspsIndex'
import { findScoutSeriesInStore } from './utils/isScoutSeries'
import {
  findRefSeriesIndices,
  seriesInstanceUidFromFiles,
} from './utils/isRefSeries'
import {
  getExplorerAutoRefPin,
  EXPLORER_AUTO_REF_PIN_EVENT,
  MANUAL_PIN_MAX,
} from './utils/explorerDownloadPrefs'
import {
  prepareSeriesRetry,
  mergeRetriedSeriesIntoStore,
  refetchExplorerSeriesFiles,
} from './utils/reloadExplorerSeries'
import ScoutFloatingPanel from './components/scout/ScoutFloatingPanel'
import DoseReportModal from './components/dose/DoseReportModal'
import { aggregateStudyDose } from './utils/dose/aggregateStudyDose'
import { buildGspsFile } from './utils/gspsBuild'
import { buildSrFile } from './utils/srBuild'
import { saveAs } from 'file-saver'
import { storeDicomInstanceToPacs } from './utils/storeDicomInstanceToPacs'
import { stowStoreDicomInstance } from './utils/pacs/stowStoreDicomInstance'
import {
  loadStudyViaLegacyWado,
  isPlaceholderPatientName,
} from './utils/pacs/wadoUriLoader'
import {
  bootstrapImportIngress,
  teardownImportIngress,
  dispatchImport,
  ImportSource,
} from './utils/importService'
import { extractDicomFilesFromZip } from './utils/extractDicomFromZipFile'
import { t, onAppLanguageChange } from './i18n'
import {
  ensurePwaFullscreenDefault,
  exitDocumentFullscreen,
  getPreferFullscreen,
  isPwaStandalone,
  requestDocumentFullscreen,
  setPreferFullscreen,
} from './utils/fullscreenPreference'
import {
  dismissPwaInstallPrompt,
  canInstallPwaInBrowser,
  getPwaInstallBannerLabel,
  maybeRedirectPwaResumeViewerUrl,
  returnToPortalFromViewerTab,
  savePwaResumeViewerUrl,
  shouldOfferPwaInstall,
} from './utils/pwaInstallPrompt'
import {
  dismissPortalDisclaimer,
  shouldShowPortalDisclaimerModal,
} from './utils/portalDisclaimer'
import {
  getInteractionState,
  scheduleWhenIdle,
  subscribeInteraction,
  resetInteractionState,
} from './utils/viewportInteractionDetector'
import { pacsAuthHeaders } from './utils/pacs/pacsAuth'
import {
  resolveMpr3dProfile,
  resolveMpr3dSize,
  mpr3dProfileLabel,
  mpr3dProfileAllows,
} from './utils/mpr3dProfile'
import { disposeVolume, remapVolumeWwwc, volumeGridToPoint, volumePointToGrid } from './utils/mpr3dVolume'
import { buildUint8VolumeMaybeWorker } from './utils/mpr3dVolumeWorker'
import { maskBBox } from './utils/mpr3dSegCoords'
import { quantifyMaskHu } from './utils/mpr3dQuantify'
import { decodeMaskBuffer, fetchMaskFromUrl } from './utils/mpr3dMaskLoad'
import { loadMpr3dSession, saveMpr3dSession } from './utils/mpr3dSession'
import { registerUdvServiceWorker, applyServiceWorkerUpdate } from './pwa/registerServiceWorker'
import VoiceNoteButton from './components/VoiceNoteButton'
import VoiceNotesPanel from './components/VoiceNotesPanel'
import VoiceRecHud from './components/VoiceRecHud'
import CopilotPanel from './components/CopilotPanel'
import {
  getCopilotCaptureBase64,
  getEnabledElementFromViewer,
} from './services/udvCopilotCapture'
import {
  solicitarPreinformeCopilot,
  COPILOT_MODELS,
  COPILOT_ERROR,
  resolveCopilotModelId,
} from './services/geminiCopilot'
import { insertCopilotReport } from './services/udvCopilotInsert'
import {
  AudioRecorder,
  isMediaRecorderSupported,
  canPauseMediaRecorder,
  encodeMp3,
  shareVoiceNote,
  audioSpatialMapper,
  ingestVoiceNote,
  resolveDeviceType,
  probeDeviceCapability,
  VOICE_NOTE_FILENAME,
  saveVoiceNote,
  listVoiceNotesForStudy,
  deleteVoiceNote,
  voiceNoteRowToUi,
  revokeVoiceNoteObjectUrls,
  updateVoiceNoteTranscript,
  transcribeLocal,
  canRunLocalWhisper,
  getLocalWhisperSkipReason,
  isTranscriptionEnabledForDevice,
  canTranscribeVoiceNotes,
  LiveSpeechSession,
  canRunLiveSpeech,
  getLiveSpeechBlockReason,
  LiveLocalSttSession,
  canRunLiveLocalStt,
  preloadLiveLocalStt,
  buildHybridTranscript,
  getWebSpeechPref,
  whisperModelIdForTier,
  subscribeSttRuntime,
  mapSttProgressToUi,
} from './voice'
import { loadSession, checkHealth } from './services/gatewayAuth'
import {
  loadControllerSession,
  checkControllerHealth,
  fetchControllerCompat,
  isUdvBelowMin,
  getControllerUrl,
} from './services/controllerAuth'
import packageJson from '../package.json'
import { startPresence, stopPresence } from './services/gatewayPresence'
import {
  acceptStudyFromGateway,
  keepLocalStudyAck,
  rejectStudyOnGateway,
} from './services/gatewayStudies'
import {
  isGatewayNotifyOnly,
  pendingPushPayloadFromUrl,
  probeEdgeLanHealth,
  normalizeEdgeLanBase,
} from './utils/gatewayPushPayload'
import { studyUidFromManifestUrl } from './utils/studyUidFromManifestUrl'
import { loadStudyFromLocalCache, applySeriesTotals } from './utils/loadStudyFromLocalCache'
import { DSA_SHIFT_STEP, DSA_SHIFT_STEP_COARSE, DSA_DEFAULTS } from './utils/dsaSubtract'
import {
  scheduleSplashHide,
  shouldDeferSplashForShell,
  armSplashUntilShellReady,
  releaseSplashShellGate,
} from './pwa/splashScreen'
import { getPaletteVoi, inferMpr3dPaletteFromFiles, mpr3dPresetLabel } from './utils/mpr3dPresets'
import Mpr3dViewport from './components/Mpr3dViewport'
import PdfViewport from './components/PdfViewport'
import { isPdfDocumentFileItem } from './utils/isNonImageInstance'
import { buildEncapsulatedPdfFile } from './utils/encapsulatedPdfBuild'
import {
  resolveStudyOpenContext,
  studyOpenContextAllowsPacsStore,
  mergeAttachedReportIntoFiles,
  STUDY_OPEN_MODE,
  STUDY_REFRESH_STRATEGY,
} from './utils/studyOpenContext'
import { limpiarCacheEstudio, vaciarCacheTotal } from './db/dicomCache'
import {
  RENDER_MODE,
  readStoredRenderMode,
  persistRenderMode,
  canUseToolInRenderMode,
  DIAGNOSTIC_TOOLS_TOOLTIP,
  PIXEL_ACCURATE_NO_DICOM_TOOLTIP,
  isPixelAccurateTool,
  parseRenderMode,
  pickRenderModeForManifest,
  isModeViableForCaps,
  isRenderModeUserSet,
} from './utils/renderMode'
import {
  frameNeedsDicomUpgrade,
  upgradePreviewFrameToDicom,
} from './utils/upgradePreviewFrameToDicom'
import {
  isQuotaLikeError,
  recoverFromQuotaPressure,
  getCombinedStudyCacheStats,
  getStudyCacheStats,
} from './db/dicomCache'
import {
  clearWriteQueue,
  resumeWriteQueue,
  getWriteQueueStats,
  whenWriteQueueIdle,
} from './db/dicomCacheWriteQueue'
import AppBar from '@material-ui/core/AppBar'
import Collapse from '@material-ui/core/Collapse'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import Drawer from '@material-ui/core/Drawer'
import LinearProgress from '@material-ui/core/LinearProgress'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuIcon from '@material-ui/icons/Menu'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Popover from '@material-ui/core/Popover'
import Slider from '@material-ui/core/Slider'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import Toolbar from '@material-ui/core/Toolbar'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import 'react-perfect-scrollbar/dist/css/styles.css'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { 
  isMobile, 
  isTablet,
} from 'react-device-detect'

// Importar el manager de líneas de referencia dual
import ReferenceLinesManager, { REFERENCE_LINES_METHODS } from './managers/ReferenceLinesManager'
import { DEFAULT_REFERENCE_LINES_METHOD } from './config/referenceLinesConfig'

import {
  clearStore,
  localFileStore,
  dcmIsOpen,
  activeDcm,
  activeDcmIndex,
  activeMeasurements,
  setLayout,
  dcmTool, 
  setDicomdir,
  setZippedFile,
  setVolume,
  filesStore,
  seriesStore,
  explorer,
  explorerActiveSeriesIndex,
} from './actions/index'

import { 
  log,
  getDicomPixelSpacing,
  getDicomSpacingBetweenSlice,
  getDicomStudyId,
  getDicomEchoNumber,
  getFileExtReal,
  isInputDirSupported,
  getSettingsFsView,
  getSettingsDicomdirView,
  getSettingsCaptureDest,
  getSettingsOfferShareAfterCapture,
  groupBy,
  objectIsEmpty,
  getDicomModality,
} from './functions'

import { 
  mdiAngleAcute,
  mdiAngleRight,
  mdiAnimationOutline,
  mdiArrowSplitHorizontal,
  mdiEyeOff,
  mdiAlphaABox,
  mdiAlphaCBox,
  mdiAlphaSBox,
  mdiAxisArrow,
  mdiCrosshairsGps,
  mdiContentCut,
  mdiCubeOutline,
  mdiViewColumn,
  mdiCamera,
  mdiShareVariant,
  mdiMicrophone,
  mdiMicrophoneOff,
  mdiChartHistogram,
  mdiCheck,
  mdiCheckboxIntermediate,
  mdiContentSaveOutline,   
  mdiCursorDefault, 
  mdiCursorPointer,
  mdiDelete,
  mdiDownload,
  mdiEllipse,
  mdiEyedropper,
  mdiFileCabinet,
  mdiFileDocument, 
  mdiFileCad, 
  mdiFolder,
  mdiFolderMultiple,
  mdiFolderOpen,
  mdiFolderZipOutline,
  mdiGesture,
  mdiCog,
  mdiMonitor,
  mdiViewGridPlusOutline,
  mdiImageEdit,
  mdiInvertColors,
  mdiMagnify,
  mdiRefresh,
  mdiRectangle,
  mdiRuler,
  mdiTools,
  mdiTrashCanOutline, 
  mdiVectorLink,
  mdiVideo,
  mdiWeb,
  mdiPlay,
  mdiPause,
  mdiSkipBackward,
  mdiSkipForward,
  mdiSkipNext,
  mdiSkipPrevious,
  mdiDatabase, // Changed from mdiDatabaseConnect to mdiDatabase
  mdiCloudLock,
  mdiCloudCheck,
  mdiInboxArrowDown,
  mdiHistory,
  mdiClipboardListOutline,
  mdiBell,
  mdiWhiteBalanceSunny,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiHospitalBox,
  mdiImageOutline,
  mdiInformationOutline,
  mdiLayers,
  mdiDotsHorizontal,
} from '@mdi/js'

import './App.css'
import './skins/studio.css'
import './shells/classic/classicToolbar.css'

import * as cornerstone from "cornerstone-core"

log()

//localStorage.setItem("debug", "cornerstoneTools")

const drawerWidth = 240
const iconColor = '#FFFFFF'
const activeColor = 'rgba(0, 255, 0, 1.0)'

const styles = theme => ({
  '@global': {
    body: {
        backgroundColor: theme.palette.common.black,
    },
  },

  grow: {
    flexGrow: 1,
  },

  root: {
    display: 'flex',
  },

  menuButton: {
    marginRight: theme.spacing(2),
  },

  title: {
    flexGrow: 1,
    // Estilos específicos para móvil
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem', // Reducir tamaño de fuente en móvil
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },

  appBar: {
    position: 'relative',
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  hide: {
    display: 'none',
  },

  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },

  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },

  // Nuevo estilo para el drawer del Explorer
  explorerDrawer: {
    width: isMobile ? '100%' : drawerWidth, // Ancho completo en móvil
    flexShrink: 0,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth, // Tamaño normal en desktop
    },
  },

  // Nuevo estilo para el contenedor del Explorer en móvil
  explorerDrawerPaper: {
    [theme.breakpoints.down('sm')]: {
      height: '40vh', // Altura del 40% de la ventana en móvil
      maxHeight: '300px', // Altura máxima para evitar que sea demasiado grande
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
    },
  },

  // Loads information about the app bar, including app bar height
  toolbar: theme.mixins.toolbar,

  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },

  listItemText: {
    fontSize: '0.85em',
    marginLeft: '-20px',
  },

})

/** Prefetch DICOM on-demand ±N cortes (planes clínicos lite/pro/premium). */
const DIAG_PREFETCH_RADIUS = 10

class App extends PureComponent {

  constructor(props) {
    super(props)
    this.files = []
    this.folder = null
    this.file = null
    this.url = null
    this.explorer = null
    this.series = null
  
    this.mprData = {}
    this.mprPlane = ''
    this.echoNumber = 0
    this._mprEnsureAbort = null
    this._mprPlaneLru = createPlaneLru(3)
    this._mpr3dVolume = null
    this._mpr3dAbort = null
    this._mpr3dSessionOff = false
    this._mpr3dSyncTimer = null
    this._mpr3dWwwcTimer = null
    this._mpr3dWwwcRaf = 0
    this._mpr3dWwwcPending = null
    this._mpr3dMprSyncTimer = null
    this._mprLiveTimer = null
    this._mpr3dLiveTimer = null
    this._mprLiveBusy = false
    this._mprLiveKeepView = false
    this._mprLiveUsable = 0
    this._mprLiveSig = ''
    this._mprLivePoll = null
    
    this.volume = []

    this.fileOpen = React.createRef()
    this.showFileOpen = this.showFileOpen.bind(this)

    this.zipOpen = React.createRef()
    this.showZipOpen = this.showZipOpen.bind(this)

    this.openDicomdir = React.createRef()
    this.showOpenDicomdir = this.showOpenDicomdir.bind(this)

    this.openFolder = React.createRef()
    this.showOpenFolder = this.showOpenFolder.bind(this)    

    this.openUrlField = React.createRef()
    
    this.dicomViewersActive = []
    this.dicomViewersActiveSameStudy = []
    this.dicomViewersActiveSamePlane = []
    this.dicomViewersRefs = []
    this.dicomViewers = []

    this.activeDcmViewersNum = 0

    this.referenceLines = {}
    this.hasAutoOpenedExplorer = false // Nuevo: flag para controlar la apertura automática
    this.autoSelectFirstSeries = true
    // Generación del poller de studyUrl: se invalida al elegir layout multi
    this._studyLayoutPollGen = 0
    // Destino de carga desde Explorer: último viewport clickeado (no el del hover)
    this._seriesLoadTargetIndex = 0
    /**
     * WW/WC clínico por SeriesInstanceUID (sesión).
     * Sobrevive a changeLayout / open en otro viewport (toolState es por instancia).
     * { [seriesUid]: { ww, wc } }
     */
    this._seriesWwwcByUid = Object.create(null)
    /**
     * Override VOI por slot de layout: misma serie en 2 viewports puede tener
     * ventanas distintas (Link Series ON + Link WW/WL OFF).
     * Clave: `${viewportIndex}|${seriesUid}` → { ww, wc }
     * Fallback: _seriesWwwcByUid (comportamiento previo).
     */
    this._viewportWwwcBySlot = Object.create(null)
    /**
     * Último corte visto por SeriesInstanceUID (sesión).
     * { [seriesUid]: { sliceIndex, sopInstanceUID, frameKey, instanceNumber } }
     */
    this._seriesSliceByUid = Object.create(null)
    /** Pins de descarga Explorer (manual max 2; auto .Ref con toggle). */
    this._manualPinnedSeriesIdx = []
    this._autoRefPinnedSeriesIdx = []
    /** Políticas portal desde ticket (?t=) + overrides URL. */
    this._viewerSessionPolicy = { pwaInstall: true, offlineCache: true }
    
    // Flag para prevenir navegación concurrente
    this.isNavigating = false

    // Modificar la detección de dispositivo móvil para tratar tablets como móviles
    this.isMobileDevice = isMobile || isTablet || window.innerWidth < 1024
    
    this.state = {
      anchorElLayout: null,   
      anchorElToolsPanel: null,
      anchorElMpr: null,
      anchorElClassicMore: null,
      anchorElClassicMprSlab: null,
      /** Ancho de ventana para overflow de toolbar Classic (desktop). */
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      openMenu: false,
      openImageEdit: false,
      openTools: false,
      mprMenu: false,
      mprMode: false,
      textMessage: '',
      titleMessage: '',
      visibleMainMenu: false,
      visibleHeader: false,
      visibleSettings: false,
      visibleGsdfQa: false,
      gsdfQaPassed: isGsdfQaPassed(),
      visibleToolbar: true,
      visibleOpenUrl: false,
      visibleToolbox: false,
      visibleTools: false,
      visibleMeasure: false,
      visibleClearMeasureDlg: false,
      visibleAbout: false,
      visibleDicomdir: false,
      visibleFileManager: false,
      visibleZippedFileDlg: false,
      visibleZipExtractDlg: false,
      visibleJoystick: false,
      visibleCaptureDlg: false,
      captureToast: null,
      visibleShareSheet: false,
      visibleScpDestDlg: false,
      shareBusy: false,
      studyZipDownloadBusy: false,
      shareStatus: '',
      shareZipInfo: null,
      scpShareDestId: getScpShareDestId(),
      scpDestTick: 0,
      joystickX: 0,
      joystickY: 0,
      joystickSlots: getJoystickSlots(),
      visibleDownloadZipDlg: false,
      visibleOpenMultipleFilesDlg: false,
      toolState: 1,
      toolActive: 'notool',
      sliceIndex: 0,
      sliceMax: 1,
      listOpenFilesScrolling: false,
      visibleVolumeBuilding: false,
      visibleMpr3D: false,
      /** Epoch para re-render cuando cambia viewportKind (pdf|dicom) por slot. */
      viewportKindEpoch: 0,
      mpr3dProfile: 'off',
      mpr3dBuilding: false,
      mpr3dClip: null,
      mpr3dDecapado: false,
      mpr3dClipAxis: 'z',
      mpr3dClipKeep: 1,
      mpr3dSlab: 8,
      mpr3dPalette: 'general',
      mpr3dPaletteInferred: 'general',
      mpr3dColorStyle: 'anatomico',
      mpr3dWw: 2000,
      mpr3dWc: 0,
      mpr3dWwwcLink: true,
      mpr3dBuildZi: 0,
      mpr3dBuildN: 0,
      mpr3dMaximized: false,
      mpr3dSegMode: 'off',
      mpr3dMaskOn: false,
      mpr3dBaseVisible: true,
      mpr3dQuant: null,
      mpr3dMaskError: null,
      mpr3dSegUrl: '',
      mpr3dLayers: [
        { id: 'ct', name: 'CT', visible: true },
        { id: 'roi', name: 'ROI', visible: true },
      ],
      mpr3dIsolateGen: 0,
      mpr3dIsolateBox: null,
      mpr3dMeshUrl: '',
      mpr3dMark: null,
      mpr3dViewHint: null,
      mpr3dViewHintGen: 0,
      visibleMprOrthogonal: false,
      visibleMprCoronal: false,
      visibleMprSagittal: false,
      visibleMprAxial: false,
      mprCrosshairActive: false,
      mprCrosshairPoint: null,
      mprCrosshairPlane: null,
      mprViability: { ok: false, code: 'too_few_slices', message: '', warnings: [] },
      mprProfile: 'light',
      mprSlabMode: 'thin',
      mprSlabMm: 0,
      petFusionOn: false,
      petFusionOpacity: 0.5,
      petFusionClut: 'hotMetal',
      petFusionWindowMax: 5,
      petFusionKey: null,
      gspsOn: true,
      mprSoftWarnShown: false,
      visibleExplorer: false,
      visibleMessage: false,
      visibleReferenceLines: true,
      /** Ocultar líneas sobre el viewport activo (OFF por defecto: scroll PREVIEW confiable). */
      hideReferenceLinesOnActiveViewport: false,
      scoutSyncEnabled: false,
      scoutPanelOpen: false,
      doseReport: null,
      doseModalOpen: false,
      referenceLinesMethod: DEFAULT_REFERENCE_LINES_METHOD, // Método por defecto: CornerstoneTools
      visibleSeriesLink: false,
      seriesLinkWwwc: false,
      seriesLinkKeys: null,
      seriesLinkOffsets: {},
      visibleOrthancConnection: false,
      visibleAuthGatewayModal: false,
      visibleLocalStudies: false,
      visibleRelatedStudies: false,
      visibleWorklist: false,
      worklistTab: 'cola',
      /** Historial paciente desde ticket.related_studies (plataforma) */
      relatedStudies: [],
      relatedPatientId: null,
      /** Worklist clínico ?w= (CONTRATO-VIEWER-WORKLIST) */
      worklistIdFromUrl: null,
      worklistTitle: null,
      worklistItems: [],
      worklistSessions: [],
      /** Catálogo local indexado por StudyInstanceUID (badge historial / Cargar) */
      localCatalogByUid: {},
      /** Tickets mergeados al Explorer (además del ?t= actual) */
      loadedRelatedTicketIds: [],
      /** Modo comparar: layout 1×2 + Link Series (toggle global) */
      compareMode: false,
      compareGuideOpen: false,
      collapsedStudyKeys: [],
      foreignStudyPrompt: null,
      /** Soft-lock: lease staff expiró (sesión plataforma caída) */
      staffLeaseLocked: false,
      gatewayConnected: Boolean(loadSession()),
      gatewayStatus: 'local_only',
      gatewayPushActive: false,
      gatewayCompatOk: true,
      controllerCompat: null,
      gatewayPushToast: null,
      /** Avisos Gateway en campanita: { id, studyInstanceUID, patientName, status, message, unread, at, payload } */
      gatewayNotices: [],
      visibleGatewayNotices: false,
      studyDuplicate: null,
      studyDuplicateBusy: false,
      studyDuplicateError: null,
      /** Local más completo que manifest R2: { …counts, pendingLoad } */
      studySourceChoice: null,
      showFullscreenPrompt: false,
      /** iframe Workspace: no mostrar prompt fullscreen */
      hideFullscreenPrompt: false,
      /** iframe Workspace: esperando postMessage elev */
      workspaceEmbedPending: false,
      swUpdateReady: false,
      swUpdatePending: false,
      swUpdateDialog: false,
      swRegistration: null,
      deferredInstallPrompt: null,
      showInstallBanner: false,
      visibleInstallHelp: false,
      visiblePwaSuccessDlg: false,
      visiblePortalDisclaimer: false,
      portalDisclaimerSensitive: false,
      pwaShowOpenStudyBanner: false,
      visiblePwaExitDialog: false,
      pwaExitHint: null,
      studyIdFromUrl: null,
      pacsIdFromUrl: null, // ID del PACS (explícito ?pacs= o inferido del path R2)
      /** Override de layout desde ?toolbar= / ?shell= / token */
      toolbarLayoutFromUrl: null,
      /** Plan comercial efectivo (token + ?plan=) */
      udvPlan: 'pro',
      /** Gate production sin udvToken */
      accessDenied: null,
      /** Persona portal | clinical */
      udvPersona: null,
      /** Invite clínico ?udvInvite= */
      inviteIdFromUrl: null,
      /** Invite resuelto: exige login */
      inviteLoginRequired: false,
      manifestUrlFromUrl: null,
      /** Indicador de origen: null | 'r2' | 'local' */
      studyLoadSource: null,
      /** Progreso descarga manifest (R2) */
      manifestDownloadProgress: null,
      /** preview | diagnostic | null — disponibilidad progresiva */
      manifestReadiness: null,
      /** Badge sutil tras upgrade preview→diagnostic */
      manifestUpgradeFlash: false,
      /** Badge sutil tras crecimiento preview (nuevas series/instancias) */
      manifestGrowFlash: false,
      /** Ticket opaco ?t= (portal / share) */
      ticketIdFromUrl: null,
      /** Deep-link legacy WADO-URI autónomo (?wadoUrl=&studyUID=) */
      legacyWadoContext: null,
      /**
       * Contexto de apertura para persistencia (adjuntar informe).
       * @type {import('./utils/studyOpenContext').StudyOpenContext|null}
       */
      studyOpenContext: null,
      /** Fuerza recarga Orthanc tras store GSPS/SR */
      orthancReloadToken: 0,
      isMobile: this.isMobileDevice,
      isFullscreen: false,
      edgeToolsOpen: false,
      // Icono Layout pinneado arriba-izquierda (toolbarEdge); default visible
      edgeLayoutPinned: true,
      /** Studio Rail: panel series izquierdo colapsado */
      studioLeftCollapsed: false,
      /** Studio Rail: panel tools derecho colapsado */
      studioRightCollapsed: false,
      /** Shell OHIF: drawer de series colapsado */
      ohifDrawerCollapsed: false,
      /** Shell Oviyam: west pane series colapsado */
      oviyamSidebarCollapsed: false,
      /** Shell Oviyam: rail tools derecho colapsado */
      oviyamToolsCollapsed: false,
      /** DIAGNOSTIC | PREVIEW */
      renderMode: readStoredRenderMode(),
      /** Caps de media del último manifest (preview/dicom) */
      manifestMediaCaps: null,
      /** Toast breve al cambiar modo */
      renderModeToast: null,
      /** URL firmada/pública de study.zip (canal aditivo) */
      studyDownloadUrl: null,
      /**
       * ZIP local (WADO/IDB) cuando no hay studyDownloadUrl remoto.
       * eligible: mostrar botón; ready: estudio completo en IndexedDB.
       */
      localStudyZip: {
        eligible: false,
        ready: false,
        expected: 0,
        cached: 0,
        studyUid: '',
        networkFallback: false,
      },
      /**
       * DSA: substracción digital XA (spec clínica).
       * invertDSA / gain / offset / noiseFloor / pixelShift (0.5) → viewer.applyDsaState
       */
      dsaUi: {
        enabled: false,
        maskMode: null, // 'current' | 'first' | null
        maskFrameIndex: null,
        pixelShift: { x: 0, y: 0 },
        invertDSA: false,
        gain: 1,
        offset: null,
        noiseFloor: 8,
        engineMode: 'cpu',
        synthMode: 'minip',
        synthRange: { start: 0, end: null },
        roadmap: {
          on: false,
          alpha: 0.45,
          lut: 'fluoro-green',
        },
      },
      dsaSynthPreview: null,
      /** Nota de voz (MediaRecorder → MP3 → lista / Web Share) */
      voiceNoteRecording: false,
      voiceNotePaused: false,
      voiceNoteBusy: false,
      visibleVoiceNotes: false,
      voiceNotes: [],
      voicePlayingId: null,
      voicePlayProgress: null,
      voiceElapsedMs: 0,
      voiceStreamEpoch: 0,
      /** Preview Web Speech mientras graba */
      voiceLiveTranscript: null,
      voiceModelProgress: null,
      voiceSttRuntime: null,
      /** UDV AI Copilot */
      visibleCopilot: false,
      copilotLoading: false,
      copilotModel: COPILOT_MODELS.FLASH,
      copilotResult: null,
      copilotHallazgos: '',
      copilotImpresion: '',
      copilotRecomendacion: '',
      explorerPinTick: 0,
    }
    this._lastManifestObject = null
    this._lastManifestHandlers = null
    this._seriesLinkSyncing = false
    this._voiceRecorder = null
    this._voiceStartPromise = null
    this._voiceElapsedTimer = null
    this._voiceAudioEl = null
    this._voiceNotesStudyUid = null
    this._voiceNotesLoadToken = 0
    this._liveSpeech = null
    this._liveLocal = null
    this._liveSpeechKickTimer = null
    this._livePreloadTimer = null
    this._livePreloadIdleHandle = null
    this._unsubSttRuntime = null
    this._manifestLoadInFlight = false
    this._pendingRenderModeReload = false
    this._pendingPreviewGrow = null
    this._previewGrowGen = 0
    this._previewGrowFingerprint = ''
    this._renderModeReloadGen = 0
    /** Generación + AbortController de la carga de estudio activa (cancel / switch). */
    this._studyLoadGen = 0
    this._studyLoadAbort = null
    /** Cola restante de candidates del ticket (fallback origen LAN/HTTP). */
    this._ticketCandidateWalk = null
    /** Invalida análisis Copilot en vuelo al cambiar de estudio. */
    this._copilotSessionGen = 0
    this._orthancConnectionRef = null
    this._openMultipleFilesDlgRef = null
    this._frameUpgradeInFlight = null
    /** Snapshot ligero de files por modo (sesión): evita re-pipeline PREVIEW↔DIAG */
    this._filesByRenderMode = {
      [RENDER_MODE.PREVIEW]: null,
      [RENDER_MODE.DIAGNOSTIC]: null,
    }
    this._filesByRenderModeStudyUid = null
    const urlParams = new URLSearchParams(window.location.search)
    const rmFromUrl = parseRenderMode(urlParams.get('renderMode'))
    if (rmFromUrl) {
      this.state.renderMode = rmFromUrl
    }
    // Viewers tras this.state (setDcmViewer lee renderMode del estado)
    for (let i = 0; i < 16; i++) {
      this.dicomViewers.push(this.setDcmViewer(i))
    }
    /** Por slot de layout: 'dicom' | 'pdf' */
    this.viewportKind = new Array(16).fill('dicom')
    /** Stack de file items PDF por slot (cuando viewportKind[i]==='pdf') */
    this.pdfFilesBySlot = {}
    this._pdfAttachInputRef = React.createRef()
    // Leer parámetros de la URL si existen
    const studyIdRaw = urlParams.get('studyId')
    const studyId = isUrlParamPlaceholder(studyIdRaw) ? null : studyIdRaw
    const pacsParamRaw = urlParams.get('pacs') || urlParams.get('pacsId')
    const pacsParam = isUrlParamPlaceholder(pacsParamRaw) ? null : pacsParamRaw
    const segUrl = urlParams.get('segUrl') || urlParams.get('maskUrl')
    if (segUrl) {
      try {
        this._mpr3dSegUrl = decodeURIComponent(segUrl)
      } catch (e) {
        this._mpr3dSegUrl = segUrl
      }
      this.state.mpr3dSegUrl = this._mpr3dSegUrl
    }
    let manifestUrl = urlParams.get('manifestUrl')
    if (manifestUrl) {
      try {
        manifestUrl = decodeURIComponent(manifestUrl)
      } catch (e) {
        /* usar valor tal cual */
      }
      this.state.manifestUrlFromUrl = manifestUrl
    }
    applyUdvTelemetryHintsFromUrl(urlParams, manifestUrl || null)

    // Lab: ?portal=1|0 persiste simulación perfil patient
    if (isDeveloperMode()) {
      const portalQ = parsePortalFromSearchParams(urlParams)
      if (portalQ === true) setPortalSim(true)
      if (portalQ === false) setPortalSim(false)
    }

    // Acceso / plan / shell: URL token → worklist ?w= → ticket ?t= → invite → entitlements
    const worklistId = parseWorklistFromSearchParams(urlParams)
    if (worklistId) {
      this.state.worklistIdFromUrl = worklistId
    }
    const ticketId =
      parseTicketFromSearchParams(urlParams) ||
      parseTicketIdFromPath(
        typeof window !== 'undefined' ? window.location.pathname : ''
      )
    if (ticketId) {
      this.state.ticketIdFromUrl = ticketId
    }
    const inviteId = parseInviteFromSearchParams(urlParams)
    if (inviteId) {
      this.state.inviteIdFromUrl = inviteId
    }

    const access = assertAccess({ searchParams: urlParams })
    this.state.udvPersona = access.persona || resolvePersona({ searchParams: urlParams })
    this.state.hideFullscreenPrompt =
      parseHideFullscreenPromptFromSearchParams(urlParams)
    if (!access.ok) {
      this.state.accessDenied = {
        reason: access.reason,
        persona: this.state.udvPersona,
      }
    } else if (
      access.source === 'workspace_embed_pending' ||
      access.workspaceEmbedPending
    ) {
      this.state.workspaceEmbedPending = true
      this.state.udvPlan = access.plan || UDV_PLANS.LITE
      this.state.udvPersona = UDV_PERSONAS.CLINICAL
    } else {
      this.state.udvPlan = access.plan || 'pro'
      this.state.inviteLoginRequired = !!access.loginRequired
      if (access.shellHint) {
        this.state.toolbarLayoutFromUrl = access.shellHint
      } else {
        this.state.toolbarLayoutFromUrl = parseToolbarQueryParam(
          urlParams.get('toolbar') || urlParams.get('toolbarLayout')
        )
      }
      // Nunca persistir plan desde query udvToken (legacy); solo tickets/Controller/invite.
      // (antes: persistEntitlementsFromUrl en lab contaminaba plan lite y disparaba plan-change)
    }

    // Portal paciente: default PREVIEW antes del primer manifest (sin ?renderMode= ni userSet)
    if (
      !rmFromUrl &&
      !isRenderModeUserSet() &&
      isPortalProfile({
        plan: this.state.udvPlan,
        persona: this.state.udvPersona,
      })
    ) {
      this.state.renderMode = RENDER_MODE.PREVIEW
    }

    const effectivePacs = resolveEffectivePacsId({
      pacsId: pacsParam,
      manifestUrl: this.state.manifestUrlFromUrl,
    })
    if (effectivePacs) {
      this.state.pacsIdFromUrl = effectivePacs
    } else if (!this.state.accessDenied) {
      const fromEnt = getEffectivePacsId({ searchParams: urlParams })
      if (fromEnt) this.state.pacsIdFromUrl = fromEnt
    }

    console.log('App - Parámetros de URL detectados:', {
      studyId: studyId,
      pacsId: this.state.pacsIdFromUrl,
      pacsParam: pacsParam,
      toolbar: this.state.toolbarLayoutFromUrl,
      udvPlan: this.state.udvPlan,
      accessDenied: this.state.accessDenied,
      hasManifest: Boolean(this.state.manifestUrlFromUrl),
      urlCompleta: window.location.href,
      search: window.location.search,
    })

    if (studyId) {
      this.state.studyIdFromUrl = studyId
      console.log('App - StudyId desde URL:', studyId)
    }

    const pendingPush = pendingPushPayloadFromUrl(urlParams)
    if (pendingPush && !studyId) {
      // Cold start desde notificationclick (incluye action/type/notify_only)
      this._pendingPushPayload = pendingPush
    }

    if (this.state.pacsIdFromUrl) {
      console.log('App - PACS efectivo:', this.state.pacsIdFromUrl)
    }

    const legacyCtx = resolveLegacyWadoContext(
      urlParams,
      getPacsConfig(this.state.pacsIdFromUrl || null)
    )
    if (legacyCtx) {
      this.state.legacyWadoContext = legacyCtx
      console.log('App - Modo legacy WADO detectado:', legacyCtx.studyUID)
    }

    if (this.state.manifestUrlFromUrl) {
      console.log('App - manifestUrl desde URL')
    }

    // Menú lateral «sistema» (file manager / abrir local): solo developer.
    {
      const bootLayout = resolveToolbarLayout(
        this.state.pacsIdFromUrl,
        this.state.toolbarLayoutFromUrl
      )
      if (isDeveloperMode()) {
        // Classic: menú lateral al inicio. Temporary (Studio/Edge/OHIF/Oviyam): cerrado.
        this.state.visibleMainMenu = bootLayout === TOOLBAR_LAYOUTS.CLASSIC
      } else {
        this.state.visibleMainMenu = false
      }
    }

    // Ticket/elev/worklist/invite: mantener #udv-splash hasta conocer shell.
    if (
      shouldDeferSplashForShell(
        urlParams,
        typeof window !== 'undefined' ? window.location.pathname : ''
      )
    ) {
      armSplashUntilShellReady()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Sync toolActive UI (ghost / toolbars) cuando el viewer cambia tool vía Redux (p. ej. Wwwc → notool)
    if (prevProps.tool !== this.props.tool && this.props.tool && this.props.tool !== this.state.toolActive) {
      this.setState({ toolActive: this.props.tool })
    }
    if (prevProps.files !== this.props.files || prevProps.series !== this.props.series) {
      if (typeof this.refreshDoseReport === 'function') this.refreshDoseReport()
      if (typeof this.syncVoiceNotesForActiveStudy === 'function') {
        this.syncVoiceNotesForActiveStudy()
      }
      if (prevProps.series !== this.props.series) {
        this.syncAutoRefPinsFromStore()
      }
    }
    // Toast Gateway: auto-ocultar (salvo progreso) sin tapar el trabajo
    if (
      this.state.gatewayPushToast &&
      (!prevState || this.state.gatewayPushToast !== prevState.gatewayPushToast)
    ) {
      if (this._gatewayToastTimer) clearTimeout(this._gatewayToastTimer)
      const sticky = /recibiendo|descargando|abriendo local/i.test(
        String(this.state.gatewayPushToast)
      )
      if (!sticky) {
        this._gatewayToastTimer = setTimeout(() => {
          this.setState({ gatewayPushToast: null })
        }, 5200)
      }
    }
    // Mientras descarga (R2/Orthanc/ZIP), ampliar el stack del viewport sin saturar el hilo UI
    // Mid-gesto: no soft-sync/forceExpand por props (onInstanceReady ya pinta RAM).
    if (prevProps.files !== this.props.files) {
      let scrollLive = false
      try {
        const v =
          this.dicomViewersRefs &&
          this.dicomViewersRefs[this.props.activeDcmIndex]
        scrollLive =
          !!(v && typeof v.isStackScrollLive === 'function' && v.isStackScrollLive())
      } catch (eLive) {
        scrollLive = false
      }
      if (scrollLive) {
        // Solo adoptar largo/pixels ligeros; evita soft-sync O(n) mid-rueda
        if (Array.isArray(this.props.files) && this.props.files.length) {
          try {
            this.forceExpandOpenSeriesBuffers(this.props.files)
          } catch (eFe) {
            /* ignore */
          }
        }
      } else {
        const minMs =
          this._compareMergeActive && !this.state.compareMode
            ? Math.max(getActiveSeriesSyncMinMs(), 900)
            : getActiveSeriesSyncMinMs()
        const now = Date.now()
        const last = this._lastActiveSeriesSyncAt || 0
        const elapsed = now - last
        const runSync = () => {
          this._lastActiveSeriesSyncAt = Date.now()
          if (Array.isArray(this.props.files) && this.props.files.length) {
            this.forceExpandOpenSeriesBuffers(this.props.files)
          }
          this.syncActiveSeriesFilesFromStore()
        }
        if (elapsed >= minMs) {
          if (this._activeSeriesSyncTimer) {
            clearTimeout(this._activeSeriesSyncTimer)
            this._activeSeriesSyncTimer = null
          }
          runSync()
        } else if (!this._activeSeriesSyncTimer) {
          this._activeSeriesSyncTimer = setTimeout(() => {
            this._activeSeriesSyncTimer = null
            runSync()
          }, Math.max(16, minMs - elapsed))
        }
      }
    }
    this.syncCineBottomCssVar()
  }

  /**
   * Clave estable StudyUID+seriesNumber (items ligeros sin image.data).
   * Nunca solo SeriesNumber: tras merge de priors choca entre estudios.
   */
  getSeriesNumberKey = (file) => {
    if (!file) return null
    const sn =
      file.series && file.series.seriesNumber != null
        ? String(file.series.seriesNumber)
        : ''
    if (!sn) return null
    const st =
      studyUidFromFile(file) ||
      (file.study && file.study.studyDateTime
        ? String(file.study.studyDateTime)
        : '') ||
      ''
    return `${st}:${sn}`
  }

  /**
   * Series id de un item: seriesInstanceUID guardado > tags image > fallback study:sn.
   * No depender de image en heap (strip de series no activas).
   */
  getFileSeriesId = (file) => {
    if (!file) return null
    const stored =
      (file.series && file.series.seriesInstanceUID) ||
      file.seriesInstanceUID ||
      null
    if (stored) return String(stored)
    try {
      if (file.image && file.image.data && typeof file.image.data.string === 'function') {
        const uid = file.image.data.string('x0020000e')
        if (uid) return uid
      }
    } catch (err) {
      // ignore
    }
    const snKey = this.getSeriesNumberKey(file)
    if (snKey) return `fallback:${snKey}`
    return null
  }

  /**
   * ¿file pertenece a seriesUID? (UID real, seriesInstanceUID en metadata o fallback study:sn).
   */
  fileMatchesSeriesUID = (file, seriesUID) => {
    if (!file || !seriesUID) return false
    const id = this.getFileSeriesId(file)
    if (id && id === seriesUID) return true

    const snKey = this.getSeriesNumberKey(file)
    if (String(seriesUID).startsWith('fallback:')) {
      const want = String(seriesUID).slice('fallback:'.length)
      return !!(snKey && snKey === want)
    }

    // seriesUID real + item ligero: por SeriesInstanceUID (nunca SN solo)
    if (file.series && file.series.seriesInstanceUID === seriesUID) return true
    if (file.seriesInstanceUID === seriesUID) return true

    if (id && String(id).startsWith('fallback:') && snKey) {
      // Resolver sn+study de la serie objetivo desde frames con el mismo UID real
      let targetSnKey = this._activeSeriesNumberKey || null
      if (!targetSnKey && Array.isArray(this.props.files)) {
        for (let i = 0; i < this.props.files.length; i++) {
          const f = this.props.files[i]
          if (!f) continue
          const fid = this.getFileSeriesId(f)
          if (
            fid === seriesUID ||
            (f.series && f.series.seriesInstanceUID === seriesUID)
          ) {
            targetSnKey = this.getSeriesNumberKey(f)
            if (targetSnKey) break
          }
        }
      }
      // snKey ya incluye StudyUID — evita mezclar priors con el mismo SeriesNumber
      return !!(targetSnKey && snKey === targetSnKey)
    }
    return false
  }

  /** Clave estable de un frame (preferir SOP/instanceNumber; imageId cambia PREVIEW↔DIAG). */
  getFileFrameKey = (file) => {
    if (!file) return null
    if (file.sopInstanceUID) return `sop:${String(file.sopInstanceUID)}`
    if (file.instanceNumber != null && !Number.isNaN(Number(file.instanceNumber))) {
      return `in:${Number(file.instanceNumber)}`
    }
    if (file.imageId) return `id:${file.imageId}`
    return null
  }

  /**
   * Snapshot del viewport activo antes de clearStore al cambiar PREVIEW↔DIAG.
   * Permite reabrir la misma serie y corte.
   */
  captureViewportBookmarkForModeSwitch = () => {
    try {
      const activeIndex =
        typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
      const viewer =
        this.dicomViewersRefs && this.dicomViewersRefs[activeIndex]
      const sliceIndex = this.getActiveSliceIndex()
      const files =
        viewer && Array.isArray(viewer.files) && viewer.files.length
          ? viewer.files
          : Array.isArray(this.files) && this.files.length
            ? this.files
            : Array.isArray(this.props.files)
              ? this.props.files
              : []
      const frame =
        files[sliceIndex] ||
        (viewer && viewer.files && viewer.files[viewer.sliceIndex]) ||
        null

      let seriesUID = this._activeSeriesUID || null
      if (!seriesUID && viewer) {
        seriesUID = this.resolveActiveSeriesUID(viewer, { localOnly: true })
      }
      if (!seriesUID && frame) {
        seriesUID = this.getFileSeriesId(frame)
      }

      let seriesNumberKey = this._activeSeriesNumberKey || null
      if (!seriesNumberKey && frame) {
        seriesNumberKey = this.getSeriesNumberKey(frame)
      }

      const explorerSeriesIndex =
        typeof this.props.explorerActiveSeriesIndex === 'number'
          ? this.props.explorerActiveSeriesIndex
          : viewer && typeof viewer.explorerIndex === 'number'
            ? viewer.explorerIndex
            : 0

      // ZOOM/Pan/VOI del viewport (si hay) para transición más “imperceptible”
      let viewport = null
      try {
        const el = viewer && viewer.dicomImage
        if (el && cornerstone.getEnabledElement(el)) {
          const vp = cornerstone.getViewport(el)
          if (vp) {
            // Preferir VOI clínico del viewer (toolState), no 255/128 del canvas preview
            let clinicalVoi = null
            if (typeof viewer.getClinicalVoiForBookmark === 'function') {
              clinicalVoi = viewer.getClinicalVoiForBookmark()
            }
            viewport = {
              scale: vp.scale,
              translation: vp.translation
                ? { x: vp.translation.x, y: vp.translation.y }
                : undefined,
              voi: clinicalVoi
                ? {
                    windowWidth: clinicalVoi.windowWidth,
                    windowCenter: clinicalVoi.windowCenter,
                  }
                : vp.voi
                  ? {
                      windowWidth: vp.voi.windowWidth,
                      windowCenter: vp.voi.windowCenter,
                    }
                  : undefined,
              invert: !!vp.invert,
              rotation: vp.rotation || 0,
              hflip: !!vp.hflip,
              vflip: !!vp.vflip,
            }
          }
        }
      } catch (e) {
        /* ignore */
      }

      return {
        seriesUID,
        seriesNumberKey,
        explorerSeriesIndex,
        activeDcmIndex: activeIndex,
        sliceIndex: sliceIndex != null && !Number.isNaN(sliceIndex) ? sliceIndex : 0,
        sopInstanceUID:
          frame && frame.sopInstanceUID != null
            ? String(frame.sopInstanceUID)
            : null,
        instanceNumber:
          frame && frame.instanceNumber != null
            ? Number(frame.instanceNumber)
            : null,
        frameKey: this.getFileFrameKey(frame),
        viewport,
      }
    } catch (e) {
      console.warn('captureViewportBookmarkForModeSwitch:', e)
      return null
    }
  }

  /**
   * Índice de corte en un stack nuevo a partir del bookmark (SOP → instanceNumber → índice).
   */
  findSliceIndexFromBookmark = (files, bookmark) => {
    if (!Array.isArray(files) || !files.length) return 0
    if (!bookmark) return 0

    if (bookmark.sopInstanceUID) {
      const bySop = files.findIndex(
        (f) =>
          f &&
          f.sopInstanceUID &&
          String(f.sopInstanceUID) === String(bookmark.sopInstanceUID)
      )
      if (bySop >= 0) return bySop
    }

    if (bookmark.frameKey) {
      const byKey = files.findIndex((f) => this.getFileFrameKey(f) === bookmark.frameKey)
      if (byKey >= 0) return byKey
    }

    if (bookmark.instanceNumber != null && !Number.isNaN(Number(bookmark.instanceNumber))) {
      const byIn = files.findIndex(
        (f) => f && Number(f.instanceNumber) === Number(bookmark.instanceNumber)
      )
      if (byIn >= 0) return byIn
    }

    const clamped = Math.min(
      bookmark.sliceIndex != null && !Number.isNaN(bookmark.sliceIndex)
        ? bookmark.sliceIndex
        : 0,
      files.length - 1
    )
    return Math.max(0, clamped)
  }

  /**
   * Tras openimage del cambio de modo, reaplicar zoom/ventana del bookmark.
   */
  applyViewportBookmark = (viewer, bookmark) => {
    if (!viewer || !bookmark || !bookmark.viewport) return
    try {
      const el = viewer.dicomImage
      if (!el || !cornerstone.getEnabledElement(el)) return
      const vp = cornerstone.getViewport(el)
      if (!vp) return
      const b = bookmark.viewport
      // En PREVIEW (CSS WWWC) o tras re-fit, VOI nativo puede divergir;
      // aplicar scale/translación siempre ayuda a percibir la misma imagen.
      if (b.scale != null && Number.isFinite(b.scale) && b.scale > 0) {
        vp.scale = b.scale
      }
      if (b.translation) {
        vp.translation = vp.translation || {}
        if (b.translation.x != null) vp.translation.x = b.translation.x
        if (b.translation.y != null) vp.translation.y = b.translation.y
      }
      if (b.rotation != null) vp.rotation = b.rotation
      if (typeof b.hflip === 'boolean') vp.hflip = b.hflip
      if (typeof b.vflip === 'boolean') vp.vflip = b.vflip
      if (typeof b.invert === 'boolean') vp.invert = b.invert

      const clinicalWw =
        b.voi && b.voi.windowWidth != null ? b.voi.windowWidth : null
      const clinicalWc =
        b.voi && b.voi.windowCenter != null ? b.voi.windowCenter : null

      const useCss =
        typeof viewer.shouldUseCssWwwc === 'function' && viewer.shouldUseCssWwwc()
      const isWebImage =
        viewer.image && typeof viewer.image.getImage === 'function'

      if (clinicalWw != null && clinicalWc != null) {
        if (typeof viewer.applyClinicalVoi === 'function') {
          viewer.applyClinicalVoi(clinicalWw, clinicalWc)
        } else if (!useCss && !isWebImage && vp.voi) {
          vp.voi = vp.voi || {}
          vp.voi.windowWidth = clinicalWw
          vp.voi.windowCenter = clinicalWc
        }
      } else if (!useCss && !isWebImage && b.voi) {
        vp.voi = vp.voi || {}
        if (b.voi.windowWidth != null) vp.voi.windowWidth = b.voi.windowWidth
        if (b.voi.windowCenter != null) vp.voi.windowCenter = b.voi.windowCenter
      }

      // Preview web: forzar VOI neutro (evita LUT rojo WC=0)
      if (isWebImage || useCss) {
        try {
          if (typeof viewer.ensureImageRender === 'function') {
            viewer.ensureImageRender(viewer.image)
          }
          if (!vp.voi) vp.voi = {}
          vp.voi.windowWidth = 255
          vp.voi.windowCenter = 128
          vp.invert = false
        } catch (eN) {
          /* ignore */
        }
      }

      try {
        cornerstone.setViewport(el, vp)
      } catch (e) {
        /* ignore */
      }
      try {
        cornerstone.updateImage(el)
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * VOI 255/128 = neutro WebP PREVIEW; no es ventaneo clínico del equipo.
   * @param {number} ww
   * @param {number} wc
   */
  isPreviewWwwcContamination = (ww, wc) => {
    const w = Number(ww)
    const c = Number(wc)
    return (
      Number.isFinite(w) &&
      Number.isFinite(c) &&
      Math.abs(w - 255) < 1.5 &&
      Math.abs(c - 128) < 1.5
    )
  }

  /**
   * Índice de layout del viewer (slot 0..15).
   * @param {object} viewer
   * @returns {number|null}
   */
  resolveViewerLayoutIndex = (viewer) => {
    if (!viewer) return null
    if (typeof viewer.layoutIndex === 'number' && viewer.layoutIndex >= 0) {
      return viewer.layoutIndex
    }
    if (
      viewer.props &&
      typeof viewer.props.index === 'number' &&
      viewer.props.index >= 0
    ) {
      return viewer.props.index
    }
    const refs = this.dicomViewersRefs || []
    for (let i = 0; i < refs.length; i++) {
      if (refs[i] === viewer) return i
    }
    return null
  }

  /**
   * Clave de override VOI por viewport + serie.
   * @param {number} viewportIndex
   * @param {string} seriesUid
   * @returns {string|null}
   */
  viewportWwwcSlotKey = (viewportIndex, seriesUid) => {
    if (viewportIndex == null || viewportIndex < 0 || !seriesUid) return null
    return `${Number(viewportIndex)}|${String(seriesUid)}`
  }

  /**
   * Persiste WW/WC de usuario por serie (continuidad tras changeLayout / open).
   * Solo valores clínicos; nunca 255/128 de preview.
   * @param {string} seriesUid
   * @param {number} ww
   * @param {number} wc
   */
  rememberSeriesWwwc = (seriesUid, ww, wc) => {
    if (!seriesUid) return null
    const w = Math.max(1, Number(ww))
    const c = Number(wc)
    if (!Number.isFinite(w) || !Number.isFinite(c) || w <= 0) return null
    if (this.isPreviewWwwcContamination(w, c)) return null
    const entry = { ww: w, wc: c }
    this._seriesWwwcByUid[String(seriesUid)] = entry
    return entry
  }

  /**
   * Override VOI por slot (misma serie, viewports distintos).
   * También actualiza el mapa global (fallback 1×1 / reopen).
   * @param {number} viewportIndex
   * @param {string} seriesUid
   * @param {number} ww
   * @param {number} wc
   */
  rememberViewportWwwc = (viewportIndex, seriesUid, ww, wc) => {
    const key = this.viewportWwwcSlotKey(viewportIndex, seriesUid)
    const entry = this.rememberSeriesWwwc(seriesUid, ww, wc)
    if (!entry || !key) return entry
    if (!this._viewportWwwcBySlot) {
      this._viewportWwwcBySlot = Object.create(null)
    }
    this._viewportWwwcBySlot[key] = { ww: entry.ww, wc: entry.wc }
    return entry
  }

  /**
   * @param {string} seriesUid
   * @returns {{ ww: number, wc: number }|null}
   */
  lookupSeriesWwwc = (seriesUid) => {
    if (!seriesUid || !this._seriesWwwcByUid) return null
    const entry = this._seriesWwwcByUid[String(seriesUid)]
    if (!entry) return null
    const w = Number(entry.ww)
    const c = Number(entry.wc)
    if (!Number.isFinite(w) || !Number.isFinite(c) || w <= 0) return null
    // Entrada contaminada (sesión previa): descartar
    if (this.isPreviewWwwcContamination(w, c)) {
      try {
        delete this._seriesWwwcByUid[String(seriesUid)]
      } catch (e) {
        /* ignore */
      }
      return null
    }
    return { ww: w, wc: c }
  }

  /**
   * VOI de slot de viewport (sin fallback global).
   * @param {number} viewportIndex
   * @param {string} seriesUid
   * @returns {{ ww: number, wc: number }|null}
   */
  lookupViewportWwwc = (viewportIndex, seriesUid) => {
    const key = this.viewportWwwcSlotKey(viewportIndex, seriesUid)
    if (!key || !this._viewportWwwcBySlot) return null
    const entry = this._viewportWwwcBySlot[key]
    if (!entry) return null
    const w = Number(entry.ww)
    const c = Number(entry.wc)
    if (!Number.isFinite(w) || !Number.isFinite(c) || w <= 0) return null
    if (this.isPreviewWwwcContamination(w, c)) {
      try {
        delete this._viewportWwwcBySlot[key]
      } catch (e) {
        /* ignore */
      }
      return null
    }
    return { ww: w, wc: c }
  }

  /**
   * Lookup VOI para un viewport: slot → mapa global (compat).
   * @param {number|null} viewportIndex
   * @param {string} seriesUid
   * @returns {{ ww: number, wc: number }|null}
   */
  lookupWwwcForViewport = (viewportIndex, seriesUid) => {
    if (viewportIndex != null && viewportIndex >= 0) {
      const slot = this.lookupViewportWwwc(viewportIndex, seriesUid)
      if (slot) return slot
    }
    return this.lookupSeriesWwwc(seriesUid)
  }

  /**
   * Quita VOI de sesión (Reset Image / cambio limpio).
   * @param {string} seriesUid
   * @param {number|null} [viewportIndex]
   */
  clearSeriesWwwcSession = (seriesUid, viewportIndex = null) => {
    if (!seriesUid) return
    const uid = String(seriesUid)
    try {
      if (this._seriesWwwcByUid) delete this._seriesWwwcByUid[uid]
    } catch (e) {
      /* ignore */
    }
    if (viewportIndex != null && viewportIndex >= 0 && this._viewportWwwcBySlot) {
      const key = this.viewportWwwcSlotKey(viewportIndex, uid)
      if (key) {
        try {
          delete this._viewportWwwcBySlot[key]
        } catch (e2) {
          /* ignore */
        }
      }
    }
  }

  /** Reset Image en un viewport → olvidar VOI de sesión de esa serie+slot. */
  handleWwwcSessionClear = (viewerIndex, seriesUid) => {
    if (!seriesUid) return
    const idx =
      typeof viewerIndex === 'number'
        ? viewerIndex
        : typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
    this.clearSeriesWwwcSession(seriesUid, idx)
  }

  /**
   * VOI clínico solo si el usuario ventaneó (wwwcUserSet).
   * Sin eso → null (abrir con tags DICOM del equipo).
   * @returns {{ seriesUid: string, ww: number, wc: number }|null}
   */
  captureClinicalWwwcFromViewer = (viewer) => {
    if (!viewer) return null
    let seriesUid = null
    try {
      seriesUid = this.resolveActiveSeriesUID(viewer, { localOnly: true })
    } catch (e) {
      seriesUid = null
    }
    if (!seriesUid && typeof viewer.getActiveSeriesUidKey === 'function') {
      try {
        seriesUid = viewer.getActiveSeriesUidKey()
      } catch (e2) {
        seriesUid = null
      }
    }
    if (!seriesUid) return null

    const st =
      viewer.toolState && seriesUid ? viewer.toolState[seriesUid] : null
    if (!st || !st.wwwcUserSet || st.ww == null || st.wc == null) return null
    const ww = Number(st.ww)
    const wc = Number(st.wc)
    if (!Number.isFinite(ww) || !Number.isFinite(wc) || ww <= 0) return null
    if (this.isPreviewWwwcContamination(ww, wc)) return null
    return { seriesUid: String(seriesUid), ww, wc }
  }

  /**
   * Guarda VOI de sesión (slot + global) solo si el usuario ventaneó esa serie.
   * @param {object} viewer
   */
  rememberWwwcFromViewer = (viewer) => {
    const snap = this.captureClinicalWwwcFromViewer(viewer)
    if (!snap) return null
    const vpIndex = this.resolveViewerLayoutIndex(viewer)
    if (vpIndex != null) {
      return this.rememberViewportWwwc(
        vpIndex,
        snap.seriesUid,
        snap.ww,
        snap.wc
      )
    }
    return this.rememberSeriesWwwc(snap.seriesUid, snap.ww, snap.wc)
  }

  /** Snapshot de todos los viewports abiertos → slots + mapa global. */
  snapshotAllViewportsWwwc = () => {
    const refs = this.dicomViewersRefs || []
    for (let i = 0; i < refs.length; i++) {
      try {
        this.rememberWwwcFromViewer(refs[i])
      } catch (e) {
        /* ignore */
      }
    }
  }

  /**
   * Persiste el último corte visto de una serie (sesión).
   * @param {string} seriesUid
   * @param {{ sliceIndex?: number, sopInstanceUID?: string|null, frameKey?: string|null, instanceNumber?: number|null }} pos
   */
  rememberSeriesSlice = (seriesUid, pos) => {
    if (!seriesUid || !pos) return null
    const sliceIndex = Math.max(0, Number(pos.sliceIndex) || 0)
    const entry = {
      sliceIndex,
      sopInstanceUID:
        pos.sopInstanceUID != null && pos.sopInstanceUID !== ''
          ? String(pos.sopInstanceUID)
          : null,
      frameKey: pos.frameKey || null,
      instanceNumber:
        pos.instanceNumber != null && !Number.isNaN(Number(pos.instanceNumber))
          ? Number(pos.instanceNumber)
          : null,
    }
    this._seriesSliceByUid[String(seriesUid)] = entry
    return entry
  }

  /**
   * @param {string} seriesUid
   * @returns {{ sliceIndex: number, sopInstanceUID: string|null, frameKey: string|null, instanceNumber: number|null }|null}
   */
  lookupSeriesSlice = (seriesUid) => {
    if (!seriesUid || !this._seriesSliceByUid) return null
    const entry = this._seriesSliceByUid[String(seriesUid)]
    if (!entry) return null
    return {
      sliceIndex: Math.max(0, Number(entry.sliceIndex) || 0),
      sopInstanceUID: entry.sopInstanceUID || null,
      frameKey: entry.frameKey || null,
      instanceNumber:
        entry.instanceNumber != null ? Number(entry.instanceNumber) : null,
    }
  }

  /**
   * Posición de corte del viewer (no planos MPR reconstruidos).
   * @returns {{ seriesUid: string, sliceIndex: number, sopInstanceUID: string|null, frameKey: string|null, instanceNumber: number|null }|null}
   */
  captureSlicePosFromViewer = (viewer) => {
    if (!viewer || viewer.isMprGenerated) return null
    let seriesUid = null
    try {
      seriesUid = this.resolveActiveSeriesUID(viewer, { localOnly: true })
    } catch (e) {
      seriesUid = null
    }
    if (!seriesUid && typeof viewer.getActiveSeriesUidKey === 'function') {
      try {
        seriesUid = viewer.getActiveSeriesUidKey()
      } catch (e2) {
        seriesUid = null
      }
    }
    if (!seriesUid) return null

    const files = Array.isArray(viewer.files) ? viewer.files : []
    let sliceIndex =
      typeof viewer.sliceIndex === 'number' ? viewer.sliceIndex : 0
    if (sliceIndex < 0) sliceIndex = 0

    // Multi-frame: un solo file, el índice es el frame
    const frame =
      files.length > 1
        ? files[Math.min(sliceIndex, files.length - 1)] || null
        : files[0] || null

    return {
      seriesUid: String(seriesUid),
      sliceIndex,
      sopInstanceUID:
        frame && frame.sopInstanceUID != null
          ? String(frame.sopInstanceUID)
          : null,
      frameKey: this.getFileFrameKey(frame),
      instanceNumber:
        frame && frame.instanceNumber != null
          ? Number(frame.instanceNumber)
          : null,
    }
  }

  rememberSliceFromViewer = (viewer) => {
    const snap = this.captureSlicePosFromViewer(viewer)
    if (!snap) return null
    return this.rememberSeriesSlice(snap.seriesUid, snap)
  }

  /** Snapshot de cortes de todos los viewports abiertos. */
  snapshotAllViewportsSlice = () => {
    const refs = this.dicomViewersRefs || []
    for (let i = 0; i < refs.length; i++) {
      try {
        this.rememberSliceFromViewer(refs[i])
      } catch (e) {
        /* ignore */
      }
    }
  }

  /** WW/WC + corte de sesión antes de changeLayout / open. */
  snapshotAllViewportsSession = () => {
    try {
      this.snapshotAllViewportsWwwc()
    } catch (e) {
      /* ignore */
    }
    try {
      this.snapshotAllViewportsSlice()
    } catch (e2) {
      /* ignore */
    }
  }

  /**
   * Siembra toolState + lógicos antes de openimage (para buildViewportPreservingView).
   * Prefiere slot del viewport; no pisa VOI local de usuario con el mapa global del hermano.
   * @param {object} viewer
   * @param {string} seriesUid
   * @param {{ ww?: number, wc?: number }|null} [voi]
   * @param {{ viewportIndex?: number, preserveLocal?: boolean }} [opts]
   */
  seedSeriesWwwcOnViewer = (viewer, seriesUid, voi = null, opts = {}) => {
    if (!viewer || !seriesUid) return false
    const vpIndex =
      opts.viewportIndex != null
        ? opts.viewportIndex
        : this.resolveViewerLayoutIndex(viewer)
    const preserveLocal = opts.preserveLocal !== false

    // Ya hay VOI de usuario en este viewer: no pisar con global del otro panel
    if (!voi && preserveLocal) {
      try {
        const st =
          viewer.toolState && viewer.toolState[seriesUid]
            ? viewer.toolState[seriesUid]
            : null
        if (
          st &&
          st.wwwcUserSet &&
          st.ww != null &&
          st.wc != null &&
          !this.isPreviewWwwcContamination(st.ww, st.wc)
        ) {
          if (vpIndex != null) {
            this.rememberViewportWwwc(vpIndex, seriesUid, st.ww, st.wc)
          }
          return true
        }
      } catch (eLocal) {
        /* ignore */
      }
    }

    const saved =
      voi && voi.ww != null && voi.wc != null
        ? { ww: Number(voi.ww), wc: Number(voi.wc) }
        : this.lookupWwwcForViewport(vpIndex, seriesUid)
    if (!saved || !Number.isFinite(saved.ww) || !Number.isFinite(saved.wc)) {
      return false
    }
    if (this.isPreviewWwwcContamination(saved.ww, saved.wc)) return false
    try {
      viewer.toolState = viewer.toolState || {}
      viewer.toolState[seriesUid] = {
        ...(viewer.toolState[seriesUid] || {}),
        ww: saved.ww,
        wc: saved.wc,
        wwwcUserSet: true,
      }
      viewer._logicalWwwc = { ww: saved.ww, wc: saved.wc }
      // Fijar slot: misma serie en otro panel no pisa este VOI vía mapa global
      if (vpIndex != null) {
        if (!this._viewportWwwcBySlot) {
          this._viewportWwwcBySlot = Object.create(null)
        }
        const key = this.viewportWwwcSlotKey(vpIndex, seriesUid)
        if (key) {
          this._viewportWwwcBySlot[key] = { ww: saved.ww, wc: saved.wc }
        }
      }
    } catch (e) {
      return false
    }
    return true
  }

  /**
   * Reaplica VOI de usuario tras paint async (solo si hay entrada de sesión válida).
   * @param {object} viewer
   * @param {string} seriesUid
   * @param {{ ww?: number, wc?: number }|null} [voi]
   * @param {{ viewportIndex?: number, preserveLocal?: boolean }} [opts]
   */
  scheduleReapplySeriesWwwc = (viewer, seriesUid, voi = null, opts = {}) => {
    if (!viewer || !seriesUid) return
    const vpIndex =
      opts.viewportIndex != null
        ? opts.viewportIndex
        : this.resolveViewerLayoutIndex(viewer)
    let saved = null
    if (voi && voi.ww != null && voi.wc != null) {
      const w = Number(voi.ww)
      const c = Number(voi.wc)
      if (
        Number.isFinite(w) &&
        Number.isFinite(c) &&
        w > 0 &&
        !this.isPreviewWwwcContamination(w, c)
      ) {
        saved = { ww: w, wc: c }
      }
    }
    if (!saved) saved = this.lookupWwwcForViewport(vpIndex, seriesUid)

    // Si no hay sesión pero el viewer ya tiene VOI local, no forzar global
    if (!saved && opts.preserveLocal !== false) {
      try {
        const st =
          viewer.toolState && viewer.toolState[seriesUid]
            ? viewer.toolState[seriesUid]
            : null
        if (
          st &&
          st.wwwcUserSet &&
          st.ww != null &&
          st.wc != null &&
          !this.isPreviewWwwcContamination(st.ww, st.wc)
        ) {
          saved = { ww: Number(st.ww), wc: Number(st.wc) }
        }
      } catch (eSt) {
        /* ignore */
      }
    }
    if (!saved || typeof viewer.applyClinicalVoi !== 'function') return

    const apply = () => {
      try {
        if (!viewer || typeof viewer.applyClinicalVoi !== 'function') return
        // Viewport destruido / otra serie
        let curUid = null
        try {
          if (typeof viewer.getActiveSeriesUidKey === 'function') {
            curUid = viewer.getActiveSeriesUidKey()
          }
        } catch (eU) {
          curUid = null
        }
        if (curUid && String(curUid) !== String(seriesUid)) return
        // Sin UID activo aún: no aplicar (evita volcar VOI de otra serie al abrir melliza)
        if (!curUid) return

        // Mid-scroll / sibling: no pisar VOI local distinto del slot
        if (opts.preserveLocal !== false && !voi) {
          const st =
            viewer.toolState && viewer.toolState[seriesUid]
              ? viewer.toolState[seriesUid]
              : null
          if (
            st &&
            st.wwwcUserSet &&
            st.ww != null &&
            st.wc != null &&
            (Math.abs(Number(st.ww) - saved.ww) > 0.5 ||
              Math.abs(Number(st.wc) - saved.wc) > 0.5)
          ) {
            const local = { ww: Number(st.ww), wc: Number(st.wc) }
            this.seedSeriesWwwcOnViewer(viewer, seriesUid, local, {
              viewportIndex: vpIndex,
              preserveLocal: false,
            })
            viewer.applyClinicalVoi(local.ww, local.wc)
            return
          }
        }

        this.seedSeriesWwwcOnViewer(viewer, seriesUid, saved, {
          viewportIndex: vpIndex,
          preserveLocal: false,
        })
        viewer.applyClinicalVoi(saved.ww, saved.wc)
      } catch (e) {
        /* ignore */
      }
    }
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        apply()
        setTimeout(apply, 60)
      })
    } else {
      setTimeout(apply, 0)
      setTimeout(apply, 80)
    }
  }

  isDisplayableFile = (file) =>
    !!(
      file &&
      (file.imageId ||
        file.sopInstanceUID ||
        file.image ||
        file.skeleton === true)
    )

  setActiveSeriesFromFiles = (files, explorerIndex) => {
    this._activeExplorerIndex = explorerIndex
    const list = Array.isArray(files) ? files : []
    let uid = null
    for (let i = 0; i < list.length; i++) {
      const id = this.getFileSeriesId(list[i])
      // Preferir UID real de DICOM sobre fallback
      if (id && !String(id).startsWith('fallback:')) {
        uid = id
        break
      }
      if (!uid && id) uid = id
    }
    this._activeSeriesUID = uid
    // seriesNumber key del stack abierto (match de ítems ligeros en soft-sync)
    this._activeSeriesNumberKey = null
    for (let i = 0; i < list.length; i++) {
      const k = this.getSeriesNumberKey(list[i])
      if (k) {
        this._activeSeriesNumberKey = k
        break
      }
    }
  }

  /**
   * UID de serie del stack local del viewer (no del store global).
   * Evita asignar la serie "activa" global a un viewport vacío.
   */
  getViewerSeriesUID = (viewer) => {
    if (!viewer || !Array.isArray(viewer.files) || viewer.files.length === 0) return null
    let fallback = null
    for (let i = 0; i < viewer.files.length; i++) {
      const id = this.getFileSeriesId(viewer.files[i])
      if (!id) continue
      if (!String(id).startsWith('fallback:')) return id
      if (!fallback) fallback = id
    }
    return fallback
  }

  /**
   * Si el active series era fallback, re-resolver UID real desde viewer/store.
   * @param {object} viewer
   * @param {{ localOnly?: boolean }} [opts] localOnly=true: solo stack del viewer (soft-sync layout)
   */
  resolveActiveSeriesUID = (viewer, opts = {}) => {
    const localOnly = !!(opts && opts.localOnly)
    const localUID = this.getViewerSeriesUID(viewer)

    if (localOnly) {
      if (!localUID) return null
      // Promover fallback del viewer a UID real del store si coincide StudyUID+SN
      if (String(localUID).startsWith('fallback:') && Array.isArray(this.props.files)) {
        const wantKey = String(localUID).slice('fallback:'.length)
        for (let i = 0; i < this.props.files.length; i++) {
          const f = this.props.files[i]
          const id = this.getFileSeriesId(f)
          if (!id || String(id).startsWith('fallback:')) continue
          const k = this.getSeriesNumberKey(f)
          if (k && k === wantKey) {
            this._activeSeriesUID = id
            return id
          }
        }
      }
      this._activeSeriesUID = localUID
      return localUID
    }

    let uid = this._activeSeriesUID
    if (uid && !String(uid).startsWith('fallback:')) return uid
    const sources = []
    if (viewer && Array.isArray(viewer.files)) sources.push(...viewer.files)
    if (Array.isArray(this.props.files)) sources.push(...this.props.files)
    for (let i = 0; i < sources.length; i++) {
      const id = this.getFileSeriesId(sources[i])
      if (id && !String(id).startsWith('fallback:')) {
        // Si teníamos fallback, exigir StudyUID:SN completo (no solo :sn)
        if (uid && String(uid).startsWith('fallback:')) {
          const wantKey = String(uid).slice('fallback:'.length)
          const k = this.getSeriesNumberKey(sources[i])
          if (!k || k !== wantKey) continue
        }
        this._activeSeriesUID = id
        return id
      }
    }
    return uid || localUID
  }

  /**
   * Archivos de la serie abierta en un viewer: SeriesInstanceUID (o fallback StudyUID:SN).
   * Nunca matchear solo por SeriesNumber (mezcla priors tras merge).
   * @param {object} viewer
   * @param {object[]} pool
   * @returns {object[]}
   */
  getSeriesFilesMatchingViewerStack = (viewer, pool) => {
    if (!viewer || !Array.isArray(pool) || !pool.length) return []
    const prev = Array.isArray(viewer.files) ? viewer.files : []
    if (!prev.length) return []
    const uid =
      this.getViewerSeriesUID(viewer) || this.getFileSeriesId(prev[0]) || null
    const snKey = this.getSeriesNumberKey(prev[0])
    const studyUid = studyUidFromFile(prev[0])

    const matched = pool.filter((f) => {
      if (!this.isDisplayableFile(f)) return false
      if (uid && this.fileMatchesSeriesUID(f, uid)) return true
      if (snKey && this.getSeriesNumberKey(f) === snKey) {
        // snKey ya incluye StudyUID; refuerzo por si falta en uno de los lados
        if (!studyUid) return true
        return studyUidFromFile(f) === studyUid
      }
      return false
    })
    return matched.slice().sort((a, b) => {
      const ia =
        a && a.instanceNumber != null ? Number(a.instanceNumber) : Number.MAX_SAFE_INTEGER
      const ib =
        b && b.instanceNumber != null ? Number(b.instanceNumber) : Number.MAX_SAFE_INTEGER
      if (ia !== ib) return ia - ib
      return 0
    })
  }

  /**
   * Amplía en vivo el stack/cine de viewports abiertos (ZIP/local progresivo).
   * Asignación directa: no depende del laberinto soft-setfiles / suppress.
   * @param {object[]} pool
   */
  forceExpandOpenSeriesBuffers = (pool) => {
    if (!Array.isArray(pool) || !pool.length) return
    const refs = this.dicomViewersRefs
    if (!refs || !refs.length) return

    const activeIndex =
      typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
    let activeMax = null
    let activeIdx = null
    let activeInPlay = false

    for (let i = 0; i < refs.length; i++) {
      const viewer = refs[i]
      if (!viewer || !Array.isArray(viewer.files) || viewer.files.length === 0) continue
      if (viewer.numberOfFrames > 1 && viewer._multiFrameStackActive) continue
      if (viewer.isMprGenerated && viewer.sliceMax > 1) continue
      // Mientras scrollea: adoptar frames nuevos del store (largo o pixels).
      // Sin esto, con skeleton de longitud fija nunca entran imágenes mid-gesto.
      try {
        if (
          typeof viewer.isStackScrollLive === 'function' &&
          viewer.isStackScrollLive()
        ) {
          const prevLiveLen = viewer.files.length
          const seriesFilesLive = this.getSeriesFilesMatchingViewerStack(
            viewer,
            pool
          )
          if (!seriesFilesLive.length) {
            continue
          }
          let adopt = seriesFilesLive.length > prevLiveLen
          if (!adopt && seriesFilesLive.length === prevLiveLen) {
            // Priorizar ventana alrededor del slice visible (no solo head 0..48).
            const focus =
              typeof viewer.sliceIndex === 'number' ? viewer.sliceIndex : 0
            const win = 24
            const start = Math.max(0, focus - win)
            const end = Math.min(seriesFilesLive.length, focus + win + 1)
            for (let j = start; j < end; j++) {
              const a = seriesFilesLive[j]
              const b = viewer.files[j]
              if (a && a.image && !(b && b.image)) {
                adopt = true
                break
              }
            }
            if (!adopt) {
              const lim = Math.min(seriesFilesLive.length, 16)
              for (let j = 0; j < lim; j++) {
                const a = seriesFilesLive[j]
                const b = viewer.files[j]
                if (a && a.image && !(b && b.image)) {
                  adopt = true
                  break
                }
              }
            }
          }
          if (adopt && Array.isArray(viewer.files)) {
            viewer.files = seriesFilesLive
            const liveTotal = Math.max(
              seriesFilesLive.length,
              resolveSeriesTotalImages(seriesFilesLive) || 0
            )
            viewer.sliceMax = Math.max(viewer.sliceMax || 0, liveTotal)
            viewer.seriesTotalImages = liveTotal
            viewer.shouldScroll = liveTotal > 1
            if (typeof viewer.refreshScrollFlags === 'function') {
              try {
                viewer.refreshScrollFlags()
              } catch (eRf) {
                /* ignore */
              }
            }
          }
          continue
        }
      } catch (eLive) {
        /* ignore */
      }

      const prev = viewer.files
      const prevLen = prev.length
      const seriesFiles = this.getSeriesFilesMatchingViewerStack(viewer, pool)
      if (!seriesFiles.length || seriesFiles.length < prevLen) continue

      if (seriesFiles.length === prevLen) {
        const sameEnds =
          this.getFileFrameKey(seriesFiles[0]) === this.getFileFrameKey(prev[0]) &&
          this.getFileFrameKey(seriesFiles[prevLen - 1]) ===
            this.getFileFrameKey(prev[prevLen - 1])
        if (sameEnds) continue
      }

      const curIdx = viewer.sliceIndex != null ? viewer.sliceIndex : 0
      const newIdx = this.remapSliceIndexByIdentity(prev, curIdx, seriesFiles)
      const inPlay = !!(viewer.state && viewer.state.inPlay)

      viewer.files = seriesFiles
      viewer.sliceMax = seriesFiles.length
      viewer.seriesTotalImages = resolveSeriesTotalImages(seriesFiles)
      viewer.shouldScroll = seriesFiles.length > 1
      // En cine: conservar el corte actual (remap por identidad), no pisar con nav viejo
      viewer.sliceIndex = newIdx

      if (typeof viewer.refreshScrollFlags === 'function') {
        try {
          viewer.refreshScrollFlags()
        } catch (e) {
          /* ignore */
        }
      }
      if (typeof viewer.afterStackBufferGrew === 'function') {
        try {
          viewer.afterStackBufferGrew(prevLen, seriesFiles.length)
        } catch (e) {
          /* ignore */
        }
      }
      if (typeof viewer.updateImageInfoDisplay === 'function') {
        try {
          viewer.updateImageInfoDisplay({ light: true })
        } catch (e) {
          /* ignore */
        }
      }

      if (i === activeIndex) {
        this.files = seriesFiles
        activeMax = Math.max(
          seriesFiles.length,
          resolveSeriesTotalImages(seriesFiles)
        )
        activeIdx = newIdx
        activeInPlay = inPlay
        if (!inPlay) this._navSliceIndex = newIdx
        else this._navSliceIndex = newIdx
      }
    }

    if (activeMax != null) {
      const patch = {}
      if (this.state.sliceMax !== activeMax) patch.sliceMax = activeMax
      if (!activeInPlay && activeIdx != null && this.state.sliceIndex !== activeIdx) {
        patch.sliceIndex = activeIdx
      }
      if (Object.keys(patch).length) this.setState(patch)
    }
  }

  /**
   * Archivos de la serie en el store: solo showables, orden InstanceNumber.
   * Incluye ítems ligeros (solo imageId): match por seriesInstanceUID metadata o study:sn.
   * @param {string} seriesUID
   * @param {object[]|null} [filesOverride] snapshot (p. ej. publish ZIP antes de que Redux propague)
   */
  getSeriesFilesFromStore = (seriesUID, filesOverride = null) => {
    const pool = Array.isArray(filesOverride) ? filesOverride : this.props.files
    if (!seriesUID || !Array.isArray(pool)) return []
    const seriesFiles = pool.filter((f) => {
      if (!this.isDisplayableFile(f)) return false
      return this.fileMatchesSeriesUID(f, seriesUID)
    })
    return seriesFiles.slice().sort((a, b) => {
      const ia = a && a.instanceNumber != null ? Number(a.instanceNumber) : Number.MAX_SAFE_INTEGER
      const ib = b && b.instanceNumber != null ? Number(b.instanceNumber) : Number.MAX_SAFE_INTEGER
      if (ia !== ib) return ia - ib
      return 0
    })
  }

  remapSliceIndexByIdentity = (prevFiles, prevIndex, nextFiles) => {
    if (!Array.isArray(nextFiles) || !nextFiles.length) return 0
    const prev = Array.isArray(prevFiles) ? prevFiles[prevIndex] : null
    if (prev) {
      const found = this.findSliceIndexFromBookmark(nextFiles, {
        sopInstanceUID: prev.sopInstanceUID || null,
        instanceNumber: prev.instanceNumber != null ? Number(prev.instanceNumber) : null,
        frameKey: this.getFileFrameKey(prev),
        sliceIndex: prevIndex,
      })
      return found
    }
    const clamped = Math.min(
      prevIndex != null && !Number.isNaN(prevIndex) ? prevIndex : 0,
      nextFiles.length - 1
    )
    return Math.max(0, clamped)
  }

  /**
   * Si el store creció la serie abierta, actualizar files/sliceMax del viewer
   * sin reiniciar el corte actual ni tocar el tool activo.
   * Remapea el índice por imageId/instanceNumber para no saltar al reordenar.
   * No inyecta series en viewports vacíos (layout multipanel: carga solo vía explorer).
   */
  /**
   * @param {object[]|null} [filesOverride]
   */
  syncActiveSeriesFilesFromStore = (filesOverride = null) => {
    try {
      this.syncActiveSeriesFilesFromStoreImpl(filesOverride)
    } finally {
      this.scheduleMprLiveRefresh()
    }
  }

  /**
   * @param {object[]|null} [filesOverride]
   */
  syncActiveSeriesFilesFromStoreImpl = (filesOverride = null) => {
    const refs = this.dicomViewersRefs
    if (!refs || !refs.length) return

    const activeIndex =
      typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
    const viewer = refs[activeIndex]
    if (!viewer) return

    // Solo ampliar stacks ya cargados de forma explícita (setfiles hard / openimage)
    const prevFiles = Array.isArray(viewer.files) ? viewer.files : []
    if (prevFiles.length === 0) return

    // No pelear con rueda/drag: soft-sync mid-scroll congela series largas
    try {
      if (
        typeof viewer.isStackScrollLive === 'function' &&
        viewer.isStackScrollLive()
      ) {
        return
      }
    } catch (eLive) {
      /* ignore */
    }

    // Tras abrir serie: no pisar openimage; sí permitir crecer buffer (ZIP/local)
    if (this._suppressSeriesSyncUntil && Date.now() < this._suppressSeriesSyncUntil) {
      if (!Array.isArray(filesOverride) || !filesOverride.length) {
        return
      }
    }

    const seriesUID = this.resolveActiveSeriesUID(viewer, { localOnly: true })
    if (!seriesUID) return

    // Tras 1×2→1×1: no dejar que un sync cambie de serie
    if (
      this._layoutRestoreLockUntil &&
      Date.now() < this._layoutRestoreLockUntil &&
      this._layoutRestoreSeriesUid &&
      seriesUID !== this._layoutRestoreSeriesUid &&
      !this.fileMatchesSeriesUID(
        prevFiles[0],
        this._layoutRestoreSeriesUid
      )
    ) {
      return
    }

    // Preferir match Explorer (UID + seriesNumber) para no perder instancias del buffer
    let seriesFiles = this.getSeriesFilesMatchingViewerStack(
      viewer,
      Array.isArray(filesOverride) ? filesOverride : this.props.files
    )
    if (!seriesFiles.length) {
      seriesFiles = this.getSeriesFilesFromStore(seriesUID, filesOverride)
    }
    if (!seriesFiles.length) return

    // Bookmark activo del cambio de modo (priorizar identidad de ese frame)
    const bookmark =
      this._renderModeViewportRestore ||
      (this._bookmarkHoldUntil && Date.now() < this._bookmarkHoldUntil
        ? this._lastModeSwitchBookmark
        : null)

    // PREVIEW: no empujar objetos Image pesados del store al viewer
    if (this.state.renderMode === RENDER_MODE.PREVIEW) {
      const keepIdx =
        bookmark != null
          ? this.findSliceIndexFromBookmark(seriesFiles, bookmark)
          : viewer.sliceIndex != null
            ? viewer.sliceIndex
            : this._navSliceIndex || 0
      seriesFiles = this.toViewportFiles(seriesFiles, {
        keepSliceIndex: keepIdx,
        keepSop: bookmark && bookmark.sopInstanceUID ? bookmark.sopInstanceUID : null,
      })
    }

    // También sincronizar si reordenaron/filtraron showables (no solo si creció)
    const currentLen = prevFiles.length
    if (seriesFiles.length === currentLen) {
      // Fast path: mismos keys en extremos + uno en el medio
      const sameEnds =
        this.getFileFrameKey(seriesFiles[0]) === this.getFileFrameKey(prevFiles[0]) &&
        this.getFileFrameKey(seriesFiles[currentLen - 1]) ===
          this.getFileFrameKey(prevFiles[currentLen - 1])
      if (sameEnds) {
        const mid = Math.floor(currentLen / 2)
        if (
          currentLen < 3 ||
          this.getFileFrameKey(seriesFiles[mid]) === this.getFileFrameKey(prevFiles[mid])
        ) {
          // Aun con misma longitud, reafirmar scroll si el viewer lo perdió
          if (viewer.files && viewer.files.length > 1 && !viewer.shouldScroll) {
            if (typeof viewer.refreshScrollFlags === 'function') {
              viewer.refreshScrollFlags()
            } else {
              viewer.shouldScroll = true
            }
            if (!(viewer.numberOfFrames > 1)) {
              viewer.sliceMax = viewer.files.length
            }
          }
          return
        }
      }
      const sameOrder =
        seriesFiles.every((f, i) => this.getFileFrameKey(f) === this.getFileFrameKey(prevFiles[i]))
      if (sameOrder) {
        if (viewer.files && viewer.files.length > 1 && !viewer.shouldScroll) {
          if (typeof viewer.refreshScrollFlags === 'function') {
            viewer.refreshScrollFlags()
          } else {
            viewer.shouldScroll = true
          }
          if (!(viewer.numberOfFrames > 1)) {
            viewer.sliceMax = viewer.files.length
          }
        }
        return
      }
    }

    // Preferir bookmark → slice del viewer (cine en play) → nav
    let sliceIndex
    if (bookmark) {
      sliceIndex = this.findSliceIndexFromBookmark(seriesFiles, bookmark)
    } else {
      const inPlay = !!(viewer.state && viewer.state.inPlay)
      const oldIndex = inPlay
        ? viewer.sliceIndex != null
          ? viewer.sliceIndex
          : 0
        : this._navSliceIndex != null
          ? this._navSliceIndex
          : viewer.sliceIndex != null
            ? viewer.sliceIndex
            : this.state.sliceIndex || 0
      sliceIndex = this.remapSliceIndexByIdentity(prevFiles, oldIndex, seriesFiles)
    }

    const keepKey = bookmark
      ? bookmark.frameKey ||
        (bookmark.sopInstanceUID
          ? `sop:${bookmark.sopInstanceUID}`
          : this.getFileFrameKey(seriesFiles[sliceIndex]))
      : this.getFileFrameKey(
          prevFiles[
            (viewer.state && viewer.state.inPlay
              ? viewer.sliceIndex
              : this._navSliceIndex != null
                ? this._navSliceIndex
                : viewer.sliceIndex) || 0
          ]
        )

    const softPayload = {
      files: seriesFiles,
      soft: true,
      keepKey,
      remappedIndex: sliceIndex,
    }
    viewer.runTool('setfiles', softPayload)

    // Misma serie en otros viewports del layout (cine / scroll deben crecer juntos)
    for (let i = 0; i < refs.length; i++) {
      if (i === activeIndex) continue
      const v = refs[i]
      if (!v || !Array.isArray(v.files) || v.files.length === 0) continue
      const vUid = this.resolveActiveSeriesUID(v, { localOnly: true })
      if (!vUid || vUid !== seriesUID) continue
      const vPrev = v.files
      const vIdx = this.remapSliceIndexByIdentity(
        vPrev,
        v.sliceIndex != null ? v.sliceIndex : 0,
        seriesFiles
      )
      v.runTool('setfiles', {
        files: seriesFiles,
        soft: true,
        keepKey: this.getFileFrameKey(vPrev[v.sliceIndex || 0]),
        remappedIndex: vIdx,
      })
      if (!(v.numberOfFrames > 1 && v._multiFrameStackActive)) {
        v.sliceMax = seriesFiles.length
        v.shouldScroll = seriesFiles.length > 1
      }
    }

    // Mantener App.files alineado al buffer de la serie abierta (sin re-click en Explorer)
    this.files = seriesFiles

    // SOP index del stack de archivos (no confudir con frame multi-frame)
    viewer.sliceIndex = sliceIndex
    // No pisar sliceMax multi-frame (frames) cuando soft-sync crece SOPs de fondo
    if (viewer.numberOfFrames > 1 && viewer._multiFrameStackActive) {
      viewer.sliceMax = viewer.numberOfFrames
      viewer.shouldScroll = true
      const frame0 =
        viewer.state && viewer.state.frame
          ? viewer.state.frame - 1
          : typeof viewer.getMultiFrameIndex0 === 'function'
            ? viewer.getMultiFrameIndex0()
            : 0
      this._navSliceIndex = frame0
      const newMax = viewer.numberOfFrames
      if (this.state.sliceMax !== newMax || this.state.sliceIndex !== frame0) {
        this.setState({ sliceMax: newMax, sliceIndex: frame0 })
      }
    } else {
      this._navSliceIndex = sliceIndex
      viewer.sliceMax = seriesFiles.length
      viewer.seriesTotalImages = resolveSeriesTotalImages(seriesFiles)
      viewer.shouldScroll = seriesFiles.length > 1
      const newMax = Math.max(
        seriesFiles.length,
        viewer.seriesTotalImages || 0
      )
      if (this.state.sliceMax !== newMax || this.state.sliceIndex !== sliceIndex) {
        this.setState({ sliceMax: newMax, sliceIndex })
      }
    }

    // Con bookmark listo: reaplicar zoom y liberar al expire
    if (bookmark && this.bookmarkFrameReady(bookmark)) {
      try {
        this.applyViewportBookmark(viewer, bookmark)
      } catch (e) {
        /* ignore */
      }
      if (
        !this._bookmarkHoldUntil ||
        Date.now() >= this._bookmarkHoldUntil
      ) {
        this._renderModeViewportRestore = null
        this._lastModeSwitchBookmark = null
        this._bookmarkHoldUntil = 0
      }
    }
  }

  setDcmViewer = (index) => {
    return (
      <DicomViewer 
        dcmRef={(ref) => {
          this.dicomViewersRefs[index] = ref
          if (ref) ref._onDsaMenuSelect = this.handleDsaMenuSelect
        }}
        index={index}
        dicomViewersRefs={this.dicomViewersRef}
        runTool={ref => (this.runTool = ref)} 
        changeTool={ref => (this.changeTool = ref)}
        onLoadedImage={this.onLoadedImage}
        onRenderedImage={this.onRenderedImage}
        onVoiChange={this.handleWwwcLiveChange}
        onWwwcSessionClear={this.handleWwwcSessionClear}
        listOpenFilesPreviousFrame={this.listOpenFilesPreviousFrame}
        listOpenFilesNextFrame={this.listOpenFilesNextFrame}
        onSliceSeek={this.handleViewportSliceSeek}
        onScrollSlicePaint={this.handleScrollSlicePaint}
        onCineSliceChange={this.handleCineSliceChange}
        onMultiFrameStackReady={this.handleMultiFrameStackReady}
        onMultiFrameIndexChange={this.handleMultiFrameIndexChange}
        onViewportActivate={this.layoutGridHover}
        onJoystickOpen={this.handleJoystickOpen}
        onCaptureResult={this.handleCaptureResult}
        overlay={true}
        visible={true}
        mprLayoutActive={false}
        visibleReferenceLines={true}
        renderMode={
          (this.state && this.state.renderMode) || RENDER_MODE.DIAGNOSTIC
        }
      />   
    )
  }

  /**
   * Slider vertical del viewport: ir a un corte concreto de ese plano.
   */
  handleViewportSliceSeek = (viewerIndex, sliceIndex) => {
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]
    if (!viewer) return
    const idx = Math.max(0, Number(sliceIndex) || 0)
    this._navActiveIndex = viewerIndex
    this._navSliceIndex = idx

    // Progreso diferido mientras scrolleaba
    if (this._deferredDownloadProgress) {
      const stats = this._deferredDownloadProgress
      this._deferredDownloadProgress = null
      try {
        this.applyManifestDownloadProgress(stats)
      } catch (e) {
        /* ignore */
      }
    }

    if (this.props.activeDcmIndex !== viewerIndex) {
      try {
        this.props.setActiveDcmIndex(viewerIndex)
      } catch (e) {
        /* ignore */
      }
    }

    if (
      viewer.numberOfFrames > 1 &&
      typeof viewer.gotoMultiFrameIndex === 'function'
    ) {
      const frames = viewer.numberOfFrames
      if (this.state.sliceIndex !== idx || this.state.sliceMax !== frames) {
        this.setState({ sliceIndex: idx, sliceMax: frames })
      }
      viewer.gotoMultiFrameIndex(idx)
      try {
        this.rememberSliceFromViewer(viewer)
      } catch (eMem) {
        /* ignore */
      }
      return
    }

    const sliceMax =
      viewer.isMprGenerated && viewer.sliceMax > 1
        ? viewer.sliceMax
        : Math.max(
            1,
            (viewer.files && viewer.files.length) || 0,
            viewer.numberOfFrames > 1 ? viewer.sliceMax || 0 : 0
          )
    const clamped = Math.min(idx, Math.max(0, sliceMax - 1))
    this._navSliceIndex = clamped
    try {
      // Alinear sliceIndex del viewer antes de persistir (algunos paths aún no lo setearon)
      if (!viewer.isMprGenerated) viewer.sliceIndex = clamped
      this.rememberSliceFromViewer(viewer)
    } catch (eMem2) {
      /* ignore */
    }
    const isMprSeek = !!(viewer.isMprGenerated || this.state.mprMode)
    if (isMprSeek) {
      this._mprUiSlice = clamped
      this._mprUiMax = sliceMax
      if (!this._mprUiThrottle) {
        this._mprUiThrottle = setTimeout(() => {
          this._mprUiThrottle = null
          if (this._unmounted) return
          const s = this._mprUiSlice
          const m = this._mprUiMax
          if (this.state.sliceIndex !== s || this.state.sliceMax !== m) {
            this.setState({ sliceIndex: s, sliceMax: m })
          }
        }, 80)
      }
    } else if (this.state.sliceIndex !== clamped || this.state.sliceMax !== sliceMax) {
      this.setState({ sliceIndex: clamped, sliceMax })
    }
    // El viewer ya pintó (rueda coalesced): sincronizar UI, no reabrir.
    // Excepción: sliceIndex avanzó en scroll-live sin pixels (stubs IDB local) →
    // hay que forzar rehidratación o el canvas queda congelado.
    if (viewer.sliceIndex === clamped) {
      if (this.state.sliceIndex !== clamped || this.state.sliceMax !== sliceMax) {
        this.setState({ sliceIndex: clamped, sliceMax })
      }
      if (isMprSeek) {
        this.markMprInteract()
        this.syncMprScrollToCrosshair(viewerIndex, clamped)
        this.isSliceChange = true
        this.handleOpenImage(clamped, viewerIndex)
      } else {
        try {
          const f =
            Array.isArray(viewer.files) && viewer.files[clamped]
              ? viewer.files[clamped]
              : null
          let hasRam = !!(f && f.image)
          if (!hasRam && f && typeof viewer.getMemoryCachedImage === 'function') {
            const mem = viewer.getMemoryCachedImage(f)
            hasRam = !!(mem && mem.image)
          }
          if (!hasRam && f && typeof viewer.ensureFileImageThenDisplay === 'function') {
            void viewer.ensureFileImageThenDisplay(clamped, f)
          } else if (
            !hasRam &&
            typeof viewer.schedulePostScrollHydrate === 'function'
          ) {
            viewer.schedulePostScrollHydrate(clamped)
          }
        } catch (eHydrate) {
          /* ignore */
        }
        this.scheduleSeriesLinkSync(viewerIndex)
      }
      if (this.state.visibleReferenceLines) {
        this.nudgeReferenceLines(viewerIndex)
      }
      return
    }
    this.isSliceChange = true
    if (isMprSeek) this.markMprInteract()
    this.handleOpenImage(clamped, viewerIndex)
    this.maybeUpgradeActiveFrameForPixelTool()
    if (isMprSeek) {
      this.syncMprScrollToCrosshair(viewerIndex, clamped)
    } else {
      this.scheduleSeriesLinkSync(viewerIndex)
    }
  }


  refreshScoutAvailability = () => {
    const found = findScoutSeriesInStore(this.props.series)
    return !!(found && found.files && found.files.length)
  }

  toggleScoutSync = () => {
    const available = this.refreshScoutAvailability()
    if (!available) {
      this.setState({
        visibleMessage: true,
        titleMessage: 'Scout Sync',
        textMessage: 'No hay serie Scout/Localizer en este estudio.',
      })
      return
    }
    this.setState((prev) => {
      const nextEnabled = !prev.scoutSyncEnabled
      return {
        scoutSyncEnabled: nextEnabled,
        scoutPanelOpen: nextEnabled ? true : prev.scoutPanelOpen,
      }
    })
  }

  toggleScoutPanel = () => {
    const available = this.refreshScoutAvailability()
    if (!available) return
    this.setState((prev) => ({
      scoutPanelOpen: !prev.scoutPanelOpen,
      scoutSyncEnabled: !prev.scoutPanelOpen ? true : prev.scoutSyncEnabled,
    }))
  }

  handleScoutSliceSelect = (sliceIndex) => {
    const idx = Math.max(0, Number(sliceIndex) || 0)
    // Preferir viewport axial MPR si está activo
    let viewerIndex = this.props.activeDcmIndex || 0
    if (this.isMprLayoutActive && this.isMprLayoutActive()) {
      const indices = this.getMprDicomIndices ? this.getMprDicomIndices() : []
      // Convención: en ortho, índice 0 suele ser el plano nativo/axial
      if (indices && indices.length) viewerIndex = indices[0]
    }
    this.handleViewportSliceSeek(viewerIndex, idx)
  }

  refreshDoseReport = () => {
    try {
      const report = aggregateStudyDose(this.props.series, this.props.files)
      this.setState({ doseReport: report })
      return report
    } catch (e) {
      console.warn('[dose] aggregate failed', e)
      this.setState({ doseReport: null })
      return null
    }
  }

  openDoseInfo = () => {
    const report = this.refreshDoseReport()
    this.setState({ doseModalOpen: true, doseReport: report })
  }

  closeDoseInfo = () => {
    this.setState({ doseModalOpen: false })
  }

  handleCineSliceChange = (index, viewerIndex) => {
    if (index == null || Number.isNaN(index)) return
    this._navSliceIndex = index
    if (this.state.mprMode) this.markMprInteract()
    if (this.state.sliceIndex !== index) {
      this.setState({ sliceIndex: index })
    }
    this.maybeUpgradeActiveFrameForPixelTool()
    this.scheduleSeriesLinkSync(
      viewerIndex != null ? viewerIndex : this.props.activeDcmIndex
    )
  }

  /**
   * Scroll en vivo (PREVIEW/diagnóstico): mover líneas de referencia y scout
   * sin re-render de App por frame (rAF coalesced desde DicomViewer).
   */
  handleScrollSlicePaint = (viewerIndex, sliceIndex) => {
    const idx = Math.max(0, Number(sliceIndex) || 0)
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]

    // Sync Link Series → openimage en slave dispara paint: no robar nav ni re-sync
    if ((viewer && viewer._seriesLinkDriven) || this._seriesLinkSyncing) {
      this._navSliceIndex = idx
      return
    }

    this._navActiveIndex = viewerIndex
    this._navSliceIndex = idx

    if (viewer && !viewer.isMprGenerated) {
      try {
        viewer.sliceIndex = idx
        this.rememberSliceFromViewer(viewer)
      } catch (eMem) {
        /* ignore */
      }
    }

    const active = this.props.activeDcmIndex
    const isActiveViewport =
      viewerIndex == null || viewerIndex === active || viewerIndex === this._navActiveIndex

    // Link Series en vivo durante rueda/arrastre (antes solo al soltar vía onSliceSeek)
    if (this.isSeriesLinkEffective()) {
      try {
        const live =
          viewer &&
          typeof viewer.isStackScrollLive === 'function' &&
          viewer.isStackScrollLive()
        if (live) {
          // Sync inmediato en el mismo tick del paint (sin 2º rAF → menos lag al acelerar)
          this.syncLinkedViewports(viewerIndex, { copyView: false, live: true })
        } else {
          this.scheduleSeriesLinkSync(viewerIndex)
        }
      } catch (eLink) {
        /* ignore */
      }
    }

    const needScout =
      isActiveViewport &&
      this.state.scoutSyncEnabled &&
      this.state.scoutPanelOpen
    const needRef = this.state.visibleReferenceLines
    const needOviyamNgGrid =
      isActiveViewport &&
      typeof this.useOviyamToolbarShell === 'function' &&
      this.useOviyamToolbarShell()

    // OviyamNG: no setState por frame (trababa el scroll). Emitir evento liviano
    // para la grilla de instancias; scout sí necesita sliceIndex en state.
    if (needOviyamNgGrid) {
      try {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('udv-nav-slice', { detail: { index: idx } })
          )
        }
      } catch (err) {
        /* ignore */
      }
    }

    if (needScout && this.state.sliceIndex !== idx) {
      if (!this._scrollSliceUiTimer) {
        this._scrollSliceUiTimer = setTimeout(() => {
          this._scrollSliceUiTimer = null
          if (this._unmounted) return
          const s = this._navSliceIndex
          if (this.state.sliceIndex !== s) {
            this.setState({ sliceIndex: s })
          }
        }, 80)
      }
    }

    if (!needRef) return

    if (this.isMprLayoutActive() || this.state.mprMode) {
      this.nudgeReferenceLines(viewerIndex)
      return
    }

    try {
      this.getActiveDcmViewers()
    } catch (e) {
      /* ignore */
    }
    if (this.dicomViewersActive && this.dicomViewersActive.length > 1) {
      this.nudgeReferenceLines(viewerIndex)
    }
  }

  /**
   * Multi-frame (XA): el viewer armó stack de frames; sincronizar slider/contador.
   */
  handleMultiFrameStackReady = (viewerIndex, info) => {
    const frames = info && info.frames != null ? Number(info.frames) : 0
    if (!(frames > 1)) return
    const active = this.props.activeDcmIndex
    if (viewerIndex != null && viewerIndex !== active) return
    const frame0 =
      info && info.frame != null && !Number.isNaN(Number(info.frame))
        ? Number(info.frame)
        : 0
    this._navSliceIndex = frame0
    const next = { sliceMax: frames, sliceIndex: frame0 }
    if (
      this.state.sliceMax !== next.sliceMax ||
      this.state.sliceIndex !== next.sliceIndex
    ) {
      this.setState(next)
    }
    // Reaplicar DSA si seguía activo (p.ej. tras recarga de stack)
    const dsaUi = this.state.dsaUi
    if (dsaUi && dsaUi.enabled) {
      const viewer =
        this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]
      if (viewer && typeof viewer.applyDsaState === 'function') {
        Promise.resolve(viewer.applyDsaState(dsaUi)).then((result) => {
          if (result && result.ok === false && result.reason !== 'stale') {
            this.setState({
              dsaUi: {
                enabled: false,
                maskMode: null,
                maskFrameIndex: null,
                pixelShift: { x: 0, y: 0 },
                invertDSA: false,
                gain: 1,
                offset: null,
                noiseFloor: 8,
              },
            })
          }
        })
      }
    }
  }

  /**
   * Multi-frame: cambio de frame (gesto/wheel/cine), sin reabrir SOP.
   */
  handleMultiFrameIndexChange = (viewerIndex, frame0, frames) => {
    const active = this.props.activeDcmIndex
    if (viewerIndex != null && viewerIndex !== active) return
    const max = frames > 1 ? frames : this.state.sliceMax
    const idx = Math.max(0, Math.min((max || 1) - 1, frame0 | 0))
    this._navSliceIndex = idx
    if (this.state.sliceIndex !== idx || (max > 1 && this.state.sliceMax !== max)) {
      const patch = { sliceIndex: idx }
      if (max > 1) patch.sliceMax = max
      this.setState(patch)
    }
  }

  onLoadedImage = () => {
    //console.log('App - onLoadedImage: ')

  }

  onRenderedImage = (index) => {
    if (this.state.visibleMpr3D) {
      this.scheduleMpr3dClipSync()
    }

    if (
      this.isSeriesLinkEffective() &&
      !this._seriesLinkSyncing &&
      (index === this.props.activeDcmIndex ||
        index === this._navActiveIndex ||
        index === this._seriesLoadTargetIndex)
    ) {
      this.scheduleSeriesLinkViewSync(index)
    }

    if (!this.state.visibleReferenceLines) return

    // Evitar bucle: refresh → updateImage → imageRendered → refresh
    if (this._refLinesRefreshing || this._mprNudgingRefLines) return

    // MPR: correlación = cruz amarilla (overlay). No repintar verdes CT.
    if (this.state.mprMode || this.isMprLayoutActive()) {
      this.isSliceChange = false
      this._mprNeedsRefLines = false
      return
    }

    if (this.dicomViewersActive.length > 1 && this.isSliceChange) {
      const activeViewer = this.dicomViewersRefs[this.props.activeDcmIndex]
      if (activeViewer && activeViewer.layoutIndex === index) {
        this.isSliceChange = false
        this.scheduleReferenceLinesRefresh()
      }
    } else if (this._needsReferenceLinesResync) {
      if (this._refLinesSetupInFlight) return
      if (
        this._refLinesSetupCooldownUntil &&
        Date.now() < this._refLinesSetupCooldownUntil
      ) {
        return
      }
      try {
        this.getActiveDcmViewers()
      } catch (e) { /* ignore */ }
      if (this.dicomViewersActive && this.dicomViewersActive.length > 1) {
        this.scheduleReferenceLinesResync(120)
      }
    }
  }

  scheduleReferenceLinesRefresh = () => {
    if (this._refLinesRefreshTimer) clearTimeout(this._refLinesRefreshTimer)
    this._refLinesRefreshTimer = setTimeout(() => {
      this._refLinesRefreshTimer = null
      this.refreshCornerstoneReferenceLines()
    }, 50)
  }

  /**
   * Legacy: tras paints MPR se reafirmaban ReferenceLines CT (verdes).
   * Ahora no-op — en MPR solo cruz amarilla UDV.
   */
  scheduleMprReferenceLinesAfterImages = () => {
    this._mprNeedsRefLines = false
    this.isSliceChange = false
    this._mprRefPaintSeen = {}
    if (this._mprRefAfterPaintTimer) {
      clearTimeout(this._mprRefAfterPaintTimer)
      this._mprRefAfterPaintTimer = null
    }
  }

  isMprLayoutActive = () =>
    !!(
      this.state.mprMode ||
      this.state.visibleMprOrthogonal ||
      this.state.visibleMprSagittal ||
      this.state.visibleMprCoronal ||
      this.state.visibleMprAxial
    )

  /**
   * En MPR la correlación entre planos es la cruz amarilla UDV (mprCrosshair overlay).
   * Las ReferenceLines verdes de CornerstoneTools se desactivan para no duplicar
   * la misma función al scrollear. Fuera de MPR siguen las verdes CT.
   * Custom canvas legacy (referenceLinesDraw / mprSliceLocationDraw) permanece off.
   * @see docs/MANUAL-0.20.75-MPR-YELLOW-CORRELATION.md
   */
  disableCornerstoneReferenceLinesForMpr = () => {
    this._mprRefLinesGen = (this._mprRefLinesGen || 0) + 1
    this._previewRefLinesSetupGen = (this._previewRefLinesSetupGen || 0) + 1
    this._seriesRefLinesGen = (this._seriesRefLinesGen || 0) + 1
    if (this._mprRefAfterPaintTimer) {
      clearTimeout(this._mprRefAfterPaintTimer)
      this._mprRefAfterPaintTimer = null
    }
    if (this._previewRefLinesSetupTimer) {
      clearTimeout(this._previewRefLinesSetupTimer)
      this._previewRefLinesSetupTimer = null
    }
    if (this._refLinesResyncTimer) {
      clearTimeout(this._refLinesResyncTimer)
      this._refLinesResyncTimer = null
    }
    if (this._refLinesRefreshTimer) {
      clearTimeout(this._refLinesRefreshTimer)
      this._refLinesRefreshTimer = null
    }
    this._refLinesSetupInFlight = false
    this._mprNeedsRefLines = false
    this._mprRefPaintSeen = {}
    // Limpiar sync primero para que Manager.disable no vuelva a setToolDisabled
    try {
      ReferenceLinesManager.clearElements()
    } catch (e) {
      /* ignore */
    }
    ReferenceLinesManager.synchronizer = null
    ReferenceLinesManager.isEnabled = false
    try {
      ReferenceLinesManager.disable()
    } catch (e2) {
      /* ignore */
    }
    // setToolDisabled → forceImageUpdate en cada elemento; solo una vez por sesión MPR
    // (evitar salto de pageToPixel entre pointerdown/up de la mira).
    if (!this._mprCtRefLinesOff) {
      try {
        if (
          window.cornerstoneTools &&
          typeof window.cornerstoneTools.setToolDisabled === 'function'
        ) {
          window.cornerstoneTools.setToolDisabled('ReferenceLines')
        }
      } catch (e3) {
        /* ignore */
      }
      this._mprCtRefLinesOff = true
    }
    // Al salir de MPR hace falta rearmar CT si el usuario vuelve a multi-viewport
    this._needsReferenceLinesResync = true
  }

  /** Descarta scroll→mira pendiente (el click/aim manda). */
  clearMprScrollCrosshairPending = () => {
    if (this._mprScrollDebounce) {
      clearTimeout(this._mprScrollDebounce)
      this._mprScrollDebounce = null
    }
    this._mprScrollPending = null
  }

  /** ¿Los elementos del Synchronizer siguen siendo los viewports MPR vivos? */
  areReferenceLineElementsCurrent = () => {
    const elements = ReferenceLinesManager.elements
    if (!elements || elements.length < 2) return false
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i]
      if (!el || (typeof el.isConnected === 'boolean' && !el.isConnected)) return false
      // Tras cornerstone.disable (cambio de serie) el nodo sigue en DOM pero ya no está enabled
      try {
        const enabled = window.cornerstone && window.cornerstone.getEnabledElement(el)
        if (!enabled) return false
      } catch (e) {
        return false
      }
    }
    if (this.isMprLayoutActive()) {
      const indices = this.getActiveMprIndices()
      if (!indices || indices.length < 2) return true
      if (elements.length !== indices.length) return false
      for (let k = 0; k < indices.length; k++) {
        const v = this.dicomViewersRefs && this.dicomViewersRefs[indices[k]]
        if (!v || !v.dicomImage || elements.indexOf(v.dicomImage) < 0) return false
      }
      return true
    }
    // Multi-viewport: el sync debe incluir todos los viewers activos
    try {
      this.getActiveDcmViewers()
    } catch (e) { /* ignore */ }
    const active = this.dicomViewersActive || []
    if (active.length < 2) return false
    for (let i = 0; i < active.length; i++) {
      const v = active[i]
      if (!v || !v.dicomImage || elements.indexOf(v.dicomImage) < 0) return false
    }
    return true
  }

  /**
   * CS ReferenceLines exige imagePlaneModule (IPP/IOP/cosines) en el imageId
   * mostrado. Sin eso sag↔cor queda asimétrico hasta un 3.er plano (axial).
   */
  activeViewersHaveUsableReferenceLinePlanes = () => {
    if (!window.cornerstone || !window.cornerstone.metaData) return false
    let viewers = []
    try {
      this.getActiveDcmViewers()
      viewers = this.dicomViewersActive || []
    } catch (e) {
      viewers = (this.dicomViewersRefs || []).filter(Boolean)
    }
    if (!viewers || viewers.length < 2) return false
    let ok = 0
    for (let i = 0; i < viewers.length; i++) {
      const v = viewers[i]
      if (!v || !v.dicomImage) continue
      let imageId = null
      try {
        const en = window.cornerstone.getEnabledElement(v.dicomImage)
        imageId =
          (en && en.image && en.image.imageId) ||
          (v.image && v.image.imageId) ||
          null
      } catch (e) {
        imageId = (v.image && v.image.imageId) || null
      }
      if (!imageId) continue
      let plane = null
      try {
        plane = window.cornerstone.metaData.get('imagePlaneModule', imageId)
      } catch (e) {
        plane = null
      }
      if (
        plane &&
        plane.rowCosines &&
        plane.columnCosines &&
        plane.imagePositionPatient
      ) {
        ok += 1
      }
    }
    return ok >= 2
  }

  /**
   * renderActiveReferenceLine exige FoR idéntico (incl. undefined===undefined).
   * Unifica FoR entre viewports activos (faltante o distinto por serie MRI/CT).
   * El override vive en el Map UDV (provider prio alta).
   */
  harmonizeReferenceLineFoRForActiveViewers = () => {
    if (!window.cornerstone || !window.cornerstone.metaData) return
    let viewers = []
    try {
      this.getActiveDcmViewers()
      viewers = this.dicomViewersActive || []
    } catch (e) {
      viewers = (this.dicomViewersRefs || []).filter(Boolean)
    }
    if (!viewers || viewers.length < 2) return

    const planes = []
    let sharedFoR = null
    for (let i = 0; i < viewers.length; i++) {
      const v = viewers[i]
      if (!v || !v.dicomImage) continue
      let imageId = null
      try {
        const en = window.cornerstone.getEnabledElement(v.dicomImage)
        imageId =
          (en && en.image && en.image.imageId) ||
          (v.image && v.image.imageId) ||
          null
      } catch (e) {
        imageId = (v.image && v.image.imageId) || null
      }
      if (!imageId) continue
      let plane = null
      try {
        plane = window.cornerstone.metaData.get('imagePlaneModule', imageId)
      } catch (e) {
        plane = null
      }
      if (!plane) continue
      planes.push({ imageId, plane })
      const foR = plane.frameOfReferenceUID
      if (foR && !sharedFoR) sharedFoR = foR
    }
    if (!sharedFoR || planes.length < 2) return
    for (let i = 0; i < planes.length; i++) {
      const { imageId, plane } = planes[i]
      if (plane.frameOfReferenceUID === sharedFoR) continue
      try {
        registerMprImagePlane(imageId, {
          ...plane,
          frameOfReferenceUID: sharedFoR,
        })
      } catch (e) {
        /* ignore */
      }
    }
  }

  /**
   * ReferenceLinesTool.enabledCallback es async y por-elemento: si el 2.º
   * viewport aún no tenía imagen, queda enabled sin renderer. Forzar
   * disable→enable re-dispara el callback en todos.
   */
  reenableReferenceLinesTool = (synchronizer) => {
    if (!window.cornerstoneTools) return
    const sync =
      synchronizer ||
      (ReferenceLinesManager && ReferenceLinesManager.synchronizer)
    if (!sync) return
    try {
      if (typeof window.cornerstoneTools.setToolDisabled === 'function') {
        window.cornerstoneTools.setToolDisabled('ReferenceLines')
      }
    } catch (e) {
      /* ignore */
    }
    try {
      if (typeof window.cornerstoneTools.setToolEnabled === 'function') {
        window.cornerstoneTools.setToolEnabled('ReferenceLines', {
          synchronizationContext: sync,
        })
      }
    } catch (e2) {
      /* ignore */
    }
    try {
      this.patchReferenceLinesRenderer()
    } catch (e3) {
      /* ignore */
    }
  }

  invalidateReferenceLinesSync = () => {
    try {
      ReferenceLinesManager.clearElements()
    } catch (e) { /* ignore */ }
    try {
      ReferenceLinesManager.synchronizer = null
    } catch (e) { /* ignore */ }
    this._needsReferenceLinesResync = true
  }

  /** Cancela timers/generaciones pendientes (toggle off / nuevo armado). */
  cancelReferenceLinesPendingSetup = () => {
    this._previewRefLinesSetupGen = (this._previewRefLinesSetupGen || 0) + 1
    this._seriesRefLinesGen = (this._seriesRefLinesGen || 0) + 1
    if (this._previewRefLinesSetupTimer) {
      clearTimeout(this._previewRefLinesSetupTimer)
      this._previewRefLinesSetupTimer = null
    }
    if (this._refLinesResyncTimer) {
      clearTimeout(this._refLinesResyncTimer)
      this._refLinesResyncTimer = null
    }
    if (this._refLinesRefreshTimer) {
      clearTimeout(this._refLinesRefreshTimer)
      this._refLinesRefreshTimer = null
    }
    this._refLinesSetupInFlight = false
    this._needsReferenceLinesResync = false
  }

  /** Repinta intersección sin destruir el Synchronizer (evita parpadeo). */
  reassertReferenceLinesQuiet = () => {
    if (!this.state.visibleReferenceLines) return
    if (this.isMprLayoutActive()) return
    if (this._refLinesRefreshing) return
    this._refLinesRefreshing = true
    try {
      try {
        this.registerReferenceLinePlanesForActiveViewers()
        this.harmonizeReferenceLineFoRForActiveViewers()
      } catch (eReg) {
        /* ignore */
      }
      const sync = ReferenceLinesManager.synchronizer
      const elements = ReferenceLinesManager.elements
      if (sync && elements && elements.length >= 2) {
        // disable→enable: re-arma renderer en viewports que fallaron el 1.er enable
        this.reenableReferenceLinesTool(sync)
      }
      if (elements && window.cornerstone) {
        for (let i = 0; i < elements.length; i++) {
          try {
            if (elements[i]) window.cornerstone.updateImage(elements[i])
          } catch (e) {
            /* ignore */
          }
        }
      }
    } finally {
      setTimeout(() => {
        this._refLinesRefreshing = false
      }, 120)
    }
  }

  scheduleReferenceLinesResync = (delayMs = 80) => {
    if (!this.state.visibleReferenceLines) return
    if (this.isMprLayoutActive()) {
      this.disableCornerstoneReferenceLinesForMpr()
      return
    }
    const gen = (this._seriesRefLinesGen = (this._seriesRefLinesGen || 0) + 1)
    if (this._refLinesResyncTimer) clearTimeout(this._refLinesResyncTimer)

    const trySetup = (attempt) => {
      if (this._unmounted || gen !== this._seriesRefLinesGen) return
      if (!this.state.visibleReferenceLines) return
      if (this._refLinesSetupInFlight && attempt === 0) {
        this._refLinesResyncTimer = setTimeout(() => trySetup(attempt), 60)
        return
      }

      try {
        this.getActiveDcmViewers()
      } catch (e) { /* ignore */ }

      if (this.dicomViewersActive && this.dicomViewersActive.length >= 2) {
        try {
          this.registerReferenceLinePlanesForActiveViewers()
          this.harmonizeReferenceLineFoRForActiveViewers()
        } catch (eReg) {
          /* ignore */
        }

        const planesOk = this.activeViewersHaveUsableReferenceLinePlanes()
        const syncReady =
          ReferenceLinesManager.synchronizer &&
          ReferenceLinesManager.isEnabled &&
          this.areReferenceLineElementsCurrent()

        // Sync "listo" pero sin IPP/IOP en ≥2 viewports → no early-out (caso sag+cor)
        if (syncReady && planesOk) {
          this._needsReferenceLinesResync = false
          this._refLinesSetupCooldownUntil = Date.now() + 500
          this.reassertReferenceLinesQuiet()
          return
        }

        if (!planesOk && attempt > 0 && attempt % 4 === 0) {
          // Re-hidratar PREVIEW mientras reintentamos
          void this.ensurePreviewReferenceLineGeometry().then(() => {
            if (this._unmounted || gen !== this._seriesRefLinesGen) return
            try {
              this.registerReferenceLinePlanesForActiveViewers()
              this.harmonizeReferenceLineFoRForActiveViewers()
            } catch (eH) {
              /* ignore */
            }
          })
        }

        this.setupCornerstoneToolsReferenceLines()
        if (
          this.areReferenceLineElementsCurrent() &&
          this.activeViewersHaveUsableReferenceLinePlanes()
        ) {
          this._needsReferenceLinesResync = false
          this._refLinesSetupCooldownUntil = Date.now() + 500
          ReferenceLinesManager.enable()
          return
        }
      }

      if (attempt < (this.isStudyBackgroundLoadActive() ? 120 : 24)) {
        this._refLinesResyncTimer = setTimeout(
          () => trySetup(attempt + 1),
          attempt === 0 ? 80 : this.isStudyBackgroundLoadActive() ? 150 : 120
        )
      } else if (this.isStudyBackgroundLoadActive()) {
        this._needsReferenceLinesResync = true
      }
    }

    this._refLinesResyncTimer = setTimeout(() => trySetup(0), delayMs)
  }

  /**
   * Entrada / re-layout MPR: apagar ReferenceLines CT (verdes).
   * La correlación queda en la cruz amarilla (`refreshMprCorrelationOverlay`).
   * Antes rearma Synchronizer; eso duplicaba verdes + amarillas al scrollear.
   */
  scheduleMprReferenceLinesSetup = (opts) => {
    const delay = (opts && opts.delay != null) ? opts.delay : 0
    const gen = (this._mprRefLinesGen = (this._mprRefLinesGen || 0) + 1)
    const run = () => {
      if (this._unmounted || gen !== this._mprRefLinesGen) return
      if (!this.isMprLayoutActive()) return
      this.disableCornerstoneReferenceLinesForMpr()
    }
    if (delay > 0) setTimeout(run, delay)
    else run()
  }

  /**
   * Al cambiar viewport activo: repintar líneas sin armar refresh pesado.
   * No setea _refLinesRefreshing (si no, nudge mid-scroll se descarta ~120ms).
   */
  repaintReferenceLinesOnActiveChange = () => {
    if (!this.state.visibleReferenceLines) return
    if (!window.cornerstone) return
    try {
      this.getActiveDcmViewers()
    } catch (e) {
      /* ignore */
    }
    const elements =
      (ReferenceLinesManager.elements && ReferenceLinesManager.elements.length >= 2
        ? ReferenceLinesManager.elements
        : null) ||
      (this.dicomViewersActive || [])
        .map((v) => v && v.dicomImage)
        .filter(Boolean)
    if (!elements || elements.length < 2) return
    for (let i = 0; i < elements.length; i++) {
      try {
        if (elements[i]) window.cornerstone.updateImage(elements[i])
      } catch (e) {
        /* ignore */
      }
    }
  }

  /**
   * Al cambiar corte (scroll/cine): updateImage en viewports hermanos para
   * que CornerstoneTools ReferenceLines repinte la intersección sin rearms.
   * En MPR no se usa (cruz amarilla); evita verdes al scrollear planos.
   */
  nudgeReferenceLines = (sourceIndex) => {
    if (!this.state.visibleReferenceLines) return
    if (this.isMprLayoutActive()) return
    if (this._refLinesRefreshing) return
    if (this._refLinesNudgeRaf) return

    const run = () => {
      this._refLinesNudgeRaf = null
      if (this._refLinesRefreshing) return
      this._mprNudgingRefLines = true
      try {
        try {
          this.registerReferenceLinePlanesForActiveViewers()
          this.harmonizeReferenceLineFoRForActiveViewers()
        } catch (eReg) {
          /* ignore */
        }
        const sync = ReferenceLinesManager.synchronizer
        if (
          sync &&
          window.cornerstoneTools &&
          typeof window.cornerstoneTools.setToolEnabled === 'function'
        ) {
          window.cornerstoneTools.setToolEnabled('ReferenceLines', {
            synchronizationContext: sync,
          })
        }

        let sourceEl = null
        if (sourceIndex != null) {
          const v = this.dicomViewersRefs && this.dicomViewersRefs[sourceIndex]
          sourceEl = v && v.dicomImage
        }

        const indices = this.isMprLayoutActive()
          ? this.getActiveMprIndices() || []
          : null
        if (indices && indices.length >= 2) {
          for (let i = 0; i < indices.length; i++) {
            if (sourceIndex != null && indices[i] === sourceIndex) continue
            const v = this.dicomViewersRefs && this.dicomViewersRefs[indices[i]]
            const el = v && v.dicomImage
            if (!el || !window.cornerstone) continue
            try {
              window.cornerstone.updateImage(el)
            } catch (e) {
              /* ignore */
            }
          }
          return
        }

        const elements = ReferenceLinesManager.elements || []
        for (let i = 0; i < elements.length; i++) {
          const el = elements[i]
          if (!el || !window.cornerstone) continue
          if (sourceEl && el === sourceEl) continue
          try {
            window.cornerstone.updateImage(el)
          } catch (e) {
            /* ignore */
          }
        }
        if (
          elements.length < 2 &&
          this.dicomViewersActive &&
          this.dicomViewersActive.length > 1
        ) {
          this._needsReferenceLinesResync = true
          this.scheduleReferenceLinesResync()
        }
      } finally {
        this._mprNudgingRefLines = false
      }
    }

    this._refLinesNudgeRaf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(run)
        : setTimeout(run, 0)
  }

  /**
   * @deprecated alias — en MPR es no-op (cruz amarilla); fuera de MPR = nudgeReferenceLines
   */
  nudgeMprReferenceLines = (sourceIndex) => {
    if (this.isMprLayoutActive()) return
    this.nudgeReferenceLines(sourceIndex)
  }

  /**
   * Legacy: reafirmaba ReferenceLines CT en MPR.
   * Ahora asegura que las verdes CT siguen apagadas en layout MPR.
   */
  reassertMprReferenceLines = () => {
    if (this.isMprLayoutActive()) {
      this.disableCornerstoneReferenceLinesForMpr()
    }
  }

  /** Actualiza líneas CT sin destruir el Synchronizer (solo fuera de MPR). */
  refreshCornerstoneReferenceLines = () => {
    if (!this.state.visibleReferenceLines) return
    if (this.isMprLayoutActive()) {
      this.disableCornerstoneReferenceLinesForMpr()
      return
    }
    if (this._refLinesRefreshing) return
    this._refLinesRefreshing = true
    try {
      try {
        this.getActiveDcmViewers()
      } catch (e) { /* ignore */ }

      // Sync inválido tras disable/cambio de serie → rearme completo
      if (
        this._needsReferenceLinesResync ||
        !this.areReferenceLineElementsCurrent() ||
        !this.activeViewersHaveUsableReferenceLinePlanes()
      ) {
        this.scheduleReferenceLinesResync()
        return
      }

      const hasSync =
        ReferenceLinesManager.synchronizer &&
        ReferenceLinesManager.isEnabled &&
        ReferenceLinesManager.elements &&
        ReferenceLinesManager.elements.length >= 2

      if (!hasSync) {
        this.scheduleReferenceLinesResync()
        return
      }

      try {
        if (window.cornerstoneTools && typeof window.cornerstoneTools.setToolEnabled === 'function') {
          window.cornerstoneTools.setToolEnabled('ReferenceLines', {
            synchronizationContext: ReferenceLinesManager.synchronizer,
          })
        }
      } catch (e) { /* ignore */ }

      const elements = ReferenceLinesManager.elements
      for (let i = 0; i < elements.length; i++) {
        try {
          if (window.cornerstone && elements[i]) {
            window.cornerstone.updateImage(elements[i])
          }
        } catch (e) { /* ignore */ }
      }
    } finally {
      setTimeout(() => {
        this._refLinesRefreshing = false
      }, 120)
    }
  }

  getDcmViewerRef = (index) => {
    return this.dicomViewersRefs[index]
  }

  getDcmViewer = (index) => {
    const el = this.dicomViewers[index]
    if (!el) return null
    // Reinyectar props vivas (JSX del constructor queda congelado)
    return React.cloneElement(el, {
      renderMode: this.state.renderMode || RENDER_MODE.DIAGNOSTIC,
      mprCrosshairActive: !!this.state.mprCrosshairActive,
      mprCrosshairPoint: this.state.mprCrosshairPoint,
      onMprCrosshairAim: this.handleMprCrosshairAim,
      seriesLinkOn: !!this.state.visibleSeriesLink && !this.state.mprMode,
      seriesLinkMember: this.isViewerSeriesLinkMember(index),
      seriesLinkOffset: this.getSeriesLinkOffsetForViewer(index),
      seriesLinkWwwc: !!this.state.seriesLinkWwwc,
      onSeriesLinkToggleMember: this.handleSeriesLinkToggleMember,
      onSeriesLinkOffset: this.handleSeriesLinkOffset,
      onSliceSeek: this.handleViewportSliceSeek,
      onScrollSlicePaint: this.handleScrollSlicePaint,
      onVoiChange: this.handleWwwcLiveChange,
      onWwwcSessionClear: this.handleWwwcSessionClear,
      mprLayoutActive: this.isMprLayoutActive(),
      visibleReferenceLines: !!this.state.visibleReferenceLines,
      mprSlabMode: this.state.mprSlabMode,
      mprSlabMm: this.state.mprSlabMm,
      petFusion: {
        on: !!this.state.petFusionOn,
        opacity: this.state.petFusionOpacity,
        clut: this.state.petFusionClut,
        windowMax: this.state.petFusionWindowMax,
        files: this.getPetFusionFiles(),
      },
      gsps: {
        on: !!this.state.gspsOn,
        files: this.getGspsFiles(),
      },
    })
  }

  getActiveDcmViewer = () => {
    return this.dicomViewersRefs[this.props.activeDcmIndex]
  }

  /** Viewport con imagen visible (fallback si activeDcmIndex quedó desfasado en móvil). */
  resolveActiveDcmViewer = () => {
    const refs = this.dicomViewersRefs
    if (!refs) return null
    const pick = (idx) => {
      const v = refs[idx]
      return v && v.image ? v : null
    }
    if (typeof this._navActiveIndex === 'number') {
      const nav = pick(this._navActiveIndex)
      if (nav) return nav
    }
    const active = pick(this.props.activeDcmIndex)
    if (active) return active
    const open = Array.isArray(this.props.isOpen) ? this.props.isOpen : []
    for (let i = 0; i < open.length; i++) {
      if (open[i]) {
        const v = pick(i)
        if (v) return v
      }
    }
    return null
  }

  handleJoystickOpen = ({ x, y }) => {
    let slots = getJoystickSlots()
    if (this.isPortalProfileSession()) {
      slots = filterJoystickSlotsForPortal(slots)
    }
    this.setState({
      visibleJoystick: true,
      joystickX: x,
      joystickY: y,
      joystickSlots: slots,
    })
  }

  hideJoystick = () => {
    this.setState({ 
      visibleJoystick: false,
      joystickX: 0,
      joystickY: 0,
      joystickSlots: null
    })
  }

  handleJoystickSelect = (actionId, payload) => {
    if (payload && payload.preset) {
      this.hideJoystick()
      this.handleWWPresetSelect(payload.preset)
      return
    }
    this.hideJoystick()
    if (!actionId) return
    if (actionId === 'series') {
      // Toggle: miniaturas / rail / drawer según shell activo
      if (
        typeof this.useOviyamToolbarShell === 'function' &&
        this.useOviyamToolbarShell()
      ) {
        this.toggleOviyamSidebar()
      } else if (
        typeof this.useOhifToolbarShell === 'function' &&
        this.useOhifToolbarShell()
      ) {
        this.toggleOhifDrawer()
      } else if (
        typeof this.useStudioToolbarShell === 'function' &&
        this.useStudioToolbarShell()
      ) {
        if (typeof this.toggleStudioLeftRail === 'function') {
          this.toggleStudioLeftRail()
        } else {
          this.toggleExplorer()
        }
      } else {
        this.toggleExplorer()
      }
      return
    }
    if (actionId === 'share') {
      this.openShareSheet()
      return
    }
    if (actionId === 'reset') {
      this.resetImage()
      return
    }
    this.toolExecute(actionId)
  }

  showFileOpen() {
    this.props.isOpenStore(false)
    this.fileOpen.current.click()
  }

  showZipOpen() {
    this.props.isOpenStore(false)
    if (this.zipOpen && this.zipOpen.current) {
      this.zipOpen.current.click()
    }
  }

  getImportHandlers = () => ({
    onSingle: (file) => this.handleOpenLocalFs([file]),
    onMultiple: (files) => this.handleOpenLocalFs(files),
    onZip: (file) => this.openZipInViewer(file),
    onFolder: (files) => this.handleOpenFolder(files),
  })

  /**
   * Extrae DICOM del ZIP y abre en el visor (sin diálogo sandbox).
   * @param {File|Blob} zipFile
   */
  openZipInViewer = async (zipFile) => {
    if (!zipFile) return
    this.beginStudyLoad('local-zip')
    this._localFilesLoadStartedAt = performance.now()
    this._localLoadExplorerReady = false
    this.setState({
      visibleZipExtractDlg: false,
      visibleZippedFileDlg: false,
      studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      studyOpenContext: resolveStudyOpenContext({
        ticketIdFromUrl: null,
        manifestUrlFromUrl: null,
        legacyWadoContext: null,
        studyIdFromUrl: null,
        pacsIdFromUrl: this.state.pacsIdFromUrl,
        studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      }),
      manifestDownloadProgress: {
        completed: 0,
        totalTasks: 0,
        loadedOk: 0,
        elapsedMs: 0,
        studyFullyLoaded: false,
        loadSource: STUDY_LOAD_SOURCE.LOCAL,
        localPhase: 'extract',
      },
    })
    try {
      const files = await extractDicomFilesFromZip(zipFile)
      if (!files.length) {
        this.setState({
          studyLoadSource: null,
          manifestDownloadProgress: null,
          titleMessage: 'ZIP',
          textMessage: t('messages.zipEmpty'),
          visibleMessage: true,
        })
        return
      }
      this.files = files
      this.changeLayout(1, 1)
      this.mprPlane = ''
      this.volume = []
      this.setState(
        {
          sliceIndex: 0,
          sliceMax: 1,
          visibleMprOrthogonal: false,
          visibleMprCoronal: false,
          visibleMprSagittal: false,
          visibleMprAxial: false,
          visibleFileManager: false,
          visibleMainMenu: false,
          visibleOpenMultipleFilesDlg: true,
          studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
          manifestDownloadProgress: {
            completed: 0,
            totalTasks: files.length,
            loadedOk: 0,
            elapsedMs: 0,
            studyFullyLoaded: false,
            loadSource: STUDY_LOAD_SOURCE.LOCAL,
            localPhase: 'index',
          },
        }
      )
    } catch (err) {
      console.error('App - openZipInViewer:', err)
      this.setState({
        studyLoadSource: null,
        manifestDownloadProgress: null,
        titleMessage: 'ZIP',
        textMessage:
          (err && err.message) || t('messages.zipReadError'),
        visibleMessage: true,
      })
    }
  }

  handleImportFromInput = (files, source = ImportSource.FILE_INPUT) => {
    if (!files || files.length === 0) return
    dispatchImport(files, this.getImportHandlers(), { source })
    if (this.fileOpen && this.fileOpen.current) {
      this.fileOpen.current.value = ''
    }
    if (this.openFolder && this.openFolder.current) {
      this.openFolder.current.value = ''
    }
  }

  isLocalZipFile = (file) => {
    if (!file) return false
    const name = (file.name || '').toLowerCase()
    return (
      file.type === 'application/zip' ||
      file.type === 'application/x-zip-compressed' ||
      /\.zip$/i.test(name)
    )
  }

  handleOpenLocalFs = (filesSelected) => {
    //console.log('handleOpenLocalFs: ', filesSelected)
    if (filesSelected.length > 1) {
      this.beginStudyLoad('local-files')
      this.files = filesSelected
      this.changeLayout(1, 1)
      this.mprPlane = ''
      this.volume = []
      this._localFilesLoadStartedAt = performance.now()
      this._localLoadExplorerReady = false
      this.setState({
        sliceIndex: 0,
        sliceMax: 1,
        visibleMprOrthogonal: false,
        visibleMprCoronal: false,
        visibleMprSagittal: false,
        visibleMprAxial: false,
        visibleOpenMultipleFilesDlg: true,
        studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
        manifestDownloadProgress: {
          completed: 0,
          totalTasks: filesSelected.length,
          loadedOk: 0,
          elapsedMs: 0,
          studyFullyLoaded: false,
          loadSource: STUDY_LOAD_SOURCE.LOCAL,
          localPhase: 'index',
        },
      })
    } else {
      const file = filesSelected[0]
      if (this.isLocalZipFile(file)) {
        this.openZipInViewer(file)
      } else {
        this.resetCopilotState()
        this.clearPreviousStudyUi()
        this.setState({sliceIndex: 0, sliceMax: 1})
        this.props.setLocalFileStore(file)
        this.dicomViewersRefs[this.props.activeDcmIndex].runTool('openLocalFs', file)      
      }
    }
  }

  handleOpenSandboxFs = (fsItem) => {
    this.resetCopilotState()
    this.clearPreviousStudyUi()
    this.dicomViewersRefs[this.props.activeDcmIndex].runTool('openSandboxFs', fsItem)
  }

  handleOpenImage = (index, dcmIndex = this.props.activeDcmIndex) => {
    //console.log('handleOpenImage: ', index, dcmIndex)
    
    // Validar que el viewer destino existe
    if (!this.dicomViewersRefs || !this.dicomViewersRefs[dcmIndex]) {
      console.warn('App - Viewer not available for index:', dcmIndex)
      return
    }

    const viewer = this.dicomViewersRefs[dcmIndex]
    // Tope = serie del viewport, no el store del estudio completo
    const liveMax = Math.max(
      (viewer.files && viewer.files.length) || 0,
      viewer.isMprGenerated && viewer.sliceMax > 1 ? viewer.sliceMax : 0,
      viewer.numberOfFrames > 1 ? viewer.numberOfFrames : 0
    )
    if (index < 0 || index >= liveMax) {
      console.warn('App - Invalid image index:', index, 'max:', liveMax)
      return
    }

    this._navSliceIndex = index
    viewer.sliceIndex = index

    const visibleMprOrthogonal = this.state.visibleMprOrthogonal
    const visibleMprSagittal = this.state.visibleMprSagittal
    const visibleMprCoronal = this.state.visibleMprCoronal
    const visibleMprAxial = this.state.visibleMprAxial
    const mprIndices = this.getActiveMprIndices()
    const i0open = (mprIndices && mprIndices[0]) || 0
    const originPlane =
      this.mprPlanePosition(true, i0open) || this._mprOriginPlane || this.mprPlane || 'axial'
    this._mprOriginPlane = originPlane
    if (visibleMprOrthogonal || visibleMprSagittal || visibleMprCoronal || visibleMprAxial) {
      this.syncMprSlabMeta()
    }
    
    if (visibleMprOrthogonal) {
        this._mprNeedsRefLines = true
        const [i0, i1, i2] = mprIndices
        const src = this.dicomViewersRefs[i0] || this.dicomViewersRefs[0]
        const filename = src && src.filename
        if (dcmIndex === i0) {
            this.renderMprNativeAt(viewer, index)
        } else if (dcmIndex === i1) {
            viewer.mprRenderXZPlane(filename, originPlane, index, this.mprData)
        } else if (dcmIndex === i2) {
            viewer.mprRenderYZPlane(filename, originPlane, index, this.mprData)
        }
        if (this.state.visibleReferenceLines) {
            this.nudgeMprReferenceLines(dcmIndex)
        }
    } else if (visibleMprSagittal || visibleMprCoronal || visibleMprAxial) {
        // Dual: origen + plano (índices según orientación)
        const selectedPlane = visibleMprSagittal
          ? 'sagittal'
          : visibleMprCoronal
            ? 'coronal'
            : 'axial'
        this._mprNeedsRefLines = true
        if (dcmIndex === i0open) {
          this.renderMprNativeAt(viewer, index)
        } else {
          this.renderMprSelectedPlaneAt(viewer, originPlane, selectedPlane, index)
        }
        if (this.state.visibleReferenceLines) {
          this.nudgeMprReferenceLines(dcmIndex)
        }
    } else { 
        if (objectIsEmpty(this.mprData)) {
            viewer.runTool('openimage', index)
        } else {
            viewer.runTool('openimage', index)           
        }
    }

    this.activeDcmViewersNum = this.getActiveDcmViewers()
    // Multi-viewport: sync barato; no validar correspondencia en cada open (bloquea PREVIEW)
    if (this.activeDcmViewersNum > 1) {
        try {
          this.getActiveDcmViewersSameStudy()
          this.getActiveDcmViewersSamePlane()
        } catch (e) {
          console.warn('App - post-openimage multi-viewport sync (preview ok):', e)
        }
        
        // ReferenceLines: rearmar completo solo si sync/planos incompletos
        // (p.ej. 2.ª serie). En scroll normal: refresh quieto.
        if (this.state.visibleReferenceLines) {
            const needFull =
              this._needsReferenceLinesResync ||
              !this.areReferenceLineElementsCurrent() ||
              !this.activeViewersHaveUsableReferenceLinePlanes()
            if (needFull) {
              this.finishPreviewReferenceLinesSetup()
            } else {
              this.scheduleReferenceLinesRefresh()
            }
        }
    }
  }
  
  handleOpenFileDicomdir = (file) => {
    this.resetCopilotState()
    this.clearPreviousStudyUi()
    this.dicomViewersRefs[this.props.activeDcmIndex].runTool('openLocalFs', file)
  }

  showOpenFolder() {
    this.openFolder.current.click()
  }

  showOpenDicomdir() {
    this.openDicomdir.current.click()
  }

  handleOpenFolder = (files) => {
    if (this.state.visibleExplorer) {
      this.setState({visibleExplorer: false}, () => {
        this.handleOpenFolderDo(files)
      })
    } else this.handleOpenFolderDo(files)
    
  }

  handleOpenFolderDo = (files) => {
    this.beginStudyLoad('local-folder')
    this.changeLayout(1, 1)
    this.mprPlane = ''
    this.volume = []
    this.files = []
    this.clear()

    this.folder = files[0].webkitRelativePath.split('/')[0]

    for (let i=0; i < files.length; i++) {
      this.files.push(files[i])
    }

    this._localFilesLoadStartedAt = performance.now()
    this._localLoadExplorerReady = false
    this.setState({
      sliceIndex: 0,
      sliceMax: 1,
      visibleMprOrthogonal: false,
      visibleMprCoronal: false,
      visibleMprSagittal: false,
      visibleMprAxial: false,
      visibleOpenMultipleFilesDlg: true,
      studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      studyOpenContext: resolveStudyOpenContext({
        ticketIdFromUrl: null,
        manifestUrlFromUrl: null,
        legacyWadoContext: null,
        studyIdFromUrl: null,
        pacsIdFromUrl: this.state.pacsIdFromUrl,
        studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      }),
      manifestDownloadProgress: {
        completed: 0,
        totalTasks: this.files.length,
        loadedOk: 0,
        elapsedMs: 0,
        studyFullyLoaded: false,
        loadSource: STUDY_LOAD_SOURCE.LOCAL,
        localPhase: 'index',
      },
    })
  }  


  handleOpenDicomdir = (files) => {
    this.setState({ visibleDicomdir: false }, () => {
      let dicomdir = null
      let datafiles = []
      for (let i=0; i < files.length; i++) {
        if (files[i].webkitRelativePath.includes('DICOMDIR')) {
          dicomdir = files[i]
        } else {
          datafiles.push(files[i])
        }
      }
      if (dicomdir !== null) {
        this.props.setDicomdirStore({origin: 'local', dicomdir: dicomdir, files: datafiles})
        this.toggleDicomdir()
      } else {
        this.setState({titleMessage: t('common.warning'), textMessage: t('messages.noDicomdir')}, () => {
          this.setState({ visibleMessage: true })
        })
      }
    })
  }

  handleOpenFsDicomdir = (fsItem) => {
    this.props.setDicomdirStore({origin: 'fs', dicomdir: fsItem, files: []})
    this.toggleDicomdir()
  }

  componentDidMount() {
    this._unmounted = false
    this._localStudyZipReadyGen = 0
    this._localZipSessionActive = false
    window.scrollTo(0, 0)
    // Sin ticket/elev: hide normal. Con gate armado en constructor, no-op.
    if (this.state.accessDenied) {
      releaseSplashShellGate()
    } else if (
      !shouldDeferSplashForShell(
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null,
        typeof window !== 'undefined' ? window.location.pathname : ''
      )
    ) {
      scheduleSplashHide()
    }
    this._unsubPlanChange = subscribePlanChange(() => {
      if (this._unmounted) return
      void this.refreshAccessGate()
    })
    void this.bootstrapPersonaAccess()
    this._unsubSttRuntime = subscribeSttRuntime((snap) => {
      if (this._unmounted) return
      this.setState({ voiceSttRuntime: snap })
    })
    this._unsubInteraction = subscribeInteraction((state) => {
      if (this._unmounted || !state || state.isInteracting) return
      if (!this._deferredDownloadProgress) return
      const stats = this._deferredDownloadProgress
      this._deferredDownloadProgress = null
      try {
        this.applyManifestDownloadProgress(stats)
      } catch (e) {
        /* ignore */
      }
    })
    this._onExplorerAutoRefPinChange = () => {
      if (this._unmounted) return
      if (!getExplorerAutoRefPin({ portalProfile: this.isPortalProfileSession() })) {
        this._autoRefPinnedSeriesIdx = []
      } else {
        this.syncAutoRefPinsFromStore()
      }
      this.setState((prev) => ({
        explorerPinTick: (prev.explorerPinTick || 0) + 1,
      }))
    }
    try {
      window.addEventListener(
        EXPLORER_AUTO_REF_PIN_EVENT,
        this._onExplorerAutoRefPinChange
      )
    } catch (e) {
      /* ignore */
    }
    if (typeof this.syncVoiceNotesForActiveStudy === 'function') {
      this.syncVoiceNotesForActiveStudy()
    }
    // Listener global para ESC: cerrar overlays que puedan bloquear la UI
    this._onGlobalEscape = (e) => {
      if (e.key === 'Escape') {
        // Forzar cierre de joystick si está abierto
        if (this.state.visibleJoystick) {
          this.hideJoystick()
        }
      }
    }
    window.addEventListener('keydown', this._onGlobalEscape)

    this._onDsaKeyDown = (e) => {
      const tag = e.target && e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.target && e.target.isContentEditable) return
      // Solo desktop: no pelear con gestos en móvil
      if (isMobile || isTablet) return

      const key = e.key
      const dsaOn = this.state.dsaUi && this.state.dsaUi.enabled
      let action = null
      let stepOverride = null

      if (key === 'd' || key === 'D') {
        action = 'toggle'
      } else if (dsaOn && (key === 'i' || key === 'I')) {
        action = 'invert-toggle'
      } else if (dsaOn && (key === 'r' || key === 'R')) {
        action = 'pixel-shift-reset'
      } else if (dsaOn && (key === 'm' || key === 'M')) {
        action = 'mask-current'
      } else if (dsaOn && key === 'ArrowLeft') {
        action = 'pixel-shift-x-'
        if (e.shiftKey) stepOverride = DSA_SHIFT_STEP_COARSE
      } else if (dsaOn && key === 'ArrowRight') {
        action = 'pixel-shift-x+'
        if (e.shiftKey) stepOverride = DSA_SHIFT_STEP_COARSE
      } else if (dsaOn && key === 'ArrowUp') {
        action = 'pixel-shift-y-'
        if (e.shiftKey) stepOverride = DSA_SHIFT_STEP_COARSE
      } else if (dsaOn && key === 'ArrowDown') {
        action = 'pixel-shift-y+'
        if (e.shiftKey) stepOverride = DSA_SHIFT_STEP_COARSE
      }

      if (!action) return
      if (action !== 'toggle' && !dsaOn) return
      e.preventDefault()
      this.handleDsaMenuSelect(action, stepOverride != null ? { step: stepOverride } : undefined)
    }
    window.addEventListener('keydown', this._onDsaKeyDown)
    if (loadSession()) {
      startPresence()
    }
    this.refreshGatewayStatus()
    this.startGatewayHealthPoll()
    this._unbindLang = onAppLanguageChange(() => {
      if (!this._unmounted) this.forceUpdate()
    })

    this._onBeforeInstallPrompt = (e) => {
      e.preventDefault()
      if (!shouldOfferPwaInstall({ pwaInstallAllowed: this.isPwaInstallAllowed() })) return
      this.setState({
        deferredInstallPrompt: e,
        showInstallBanner: true,
      })
    }
    this._onAppInstalled = () => {
      this.handlePwaInstalledHandoff()
    }
    window.addEventListener('beforeinstallprompt', this._onBeforeInstallPrompt)
    window.addEventListener('appinstalled', this._onAppInstalled)
    if (window.__UDV_DEFERRED_INSTALL) {
      this._onBeforeInstallPrompt(window.__UDV_DEFERRED_INSTALL)
    }

    if (maybeRedirectPwaResumeViewerUrl()) {
      return
    }
    
    // PWA: fullscreen por defecto; browser móvil: aviso opcional
    const pwaStandalone = isPwaStandalone()
    if (pwaStandalone) {
      ensurePwaFullscreenDefault()
      this.setupPwaBackTrap()
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setTimeout(() => this.restoreFullscreenIfPreferred(), 400)
      }
    } else {
      const isTabletDevice = isTablet || (window.innerWidth >= 768 && window.innerWidth <= 1024)
      const preferFullscreen = getPreferFullscreen()
      if (
        (isMobile || isTabletDevice) &&
        !preferFullscreen &&
        !document.fullscreenElement &&
        !document.webkitFullscreenElement &&
        !this.state.hideFullscreenPrompt
      ) {
        setTimeout(() => {
          this.setState({ showFullscreenPrompt: true })
        }, 100)
      }
    }
    
    // Carga profunda: workspace embed pending | invite → ticket (sin studyId) → legacy WADO → manifest → Orthanc studyId
    if (this.state.workspaceEmbedPending) {
      this.bootstrapWorkspaceEmbed()
    } else if (!this.state.accessDenied) {
      if (this.state.inviteIdFromUrl) {
        this.bootstrapViewerInvite(this.state.inviteIdFromUrl)
      } else if (this.state.worklistIdFromUrl) {
        this.bootstrapViewerWorklist(this.state.worklistIdFromUrl)
      } else if (this.state.ticketIdFromUrl && !this.state.studyIdFromUrl) {
        this.bootstrapViewerTicket(this.state.ticketIdFromUrl)
      } else if (this.state.legacyWadoContext) {
        releaseSplashShellGate()
        this.loadStudyViaLegacyWadoUrl()
      } else if (this.state.manifestUrlFromUrl) {
        releaseSplashShellGate()
        this.loadStudyFromManifestUrl(this.state.manifestUrlFromUrl)
      } else if (this.state.studyIdFromUrl) {
        releaseSplashShellGate()
        this.loadStudyFromUrl(this.state.studyIdFromUrl)
      } else if (this.state.ticketIdFromUrl) {
        // Ver / ticket sin pacs+studyId en URL → candidates
        this.bootstrapViewerTicket(this.state.ticketIdFromUrl)
      } else {
        releaseSplashShellGate()
      }
    }

    // Sesiones worklist locales (chip Recientes aunque no haya ?w=)
    void this.refreshWorklistSessions()

    // Agregar listener para cambios de tamaño de ventana
    window.addEventListener('resize', this.handleResize)
    this.handleResize() // Forzar detección inicial

    // En móvil sin fullscreen, el browser puede superponer barra inferior.
    // Escuchamos visualViewport para reubicar la solapa/notch del explorer.
    if (window.visualViewport) {
      this._viewportChromeHandler = () => this.scheduleViewportChromeSync()
      try {
        window.visualViewport.addEventListener('resize', this._viewportChromeHandler)
        window.visualViewport.addEventListener('scroll', this._viewportChromeHandler)
      } catch (e) {
        /* ignore */
      }
    }

    // Agregar listener para cambios de orientación en móviles
    window.addEventListener('orientationchange', this.handleOrientationChange)
    try {
      this._mprOrientMql = window.matchMedia('(orientation: portrait)')
      this._mprOrientMqlHandler = () => this.handleOrientationChange()
      if (this._mprOrientMql.addEventListener) {
        this._mprOrientMql.addEventListener('change', this._mprOrientMqlHandler)
      } else if (this._mprOrientMql.addListener) {
        this._mprOrientMql.addListener(this._mprOrientMqlHandler)
      }
    } catch (e) {
      this._mprOrientMql = null
    }

    // Agregar listeners para cambios en el estado de pantalla completa
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);

    this.syncCineBottomCssVar()

    bootstrapImportIngress(this.getImportHandlers())

    registerUdvServiceWorker((registration) => {
      this.setState({
        swUpdateReady: true,
        swUpdatePending: false,
        swUpdateDialog: false,
        swRegistration: registration,
      })
    })

    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this.handleGatewaySwMessage)
    }

    if (this._pendingPushPayload) {
      const payload = this._pendingPushPayload
      this._pendingPushPayload = null
      setTimeout(() => {
        this.handleGatewayPushClick(payload)
      }, 400)
    }
  }

  componentWillUnmount() {
    if (this._unbindLang) this._unbindLang()
    this._unmounted = true
    this.stopStaffTicketLease()
    try {
      this._cancelLiveLocalPreloadSchedule()
    } catch (e) {
      /* ignore */
    }
    if (this._stopManifestPoll) {
      this._stopManifestPoll()
      this._stopManifestPoll = null
    }
    if (this._unsubPlanChange) {
      this._unsubPlanChange()
      this._unsubPlanChange = null
    }
    if (this._unsubSttRuntime) {
      this._unsubSttRuntime()
      this._unsubSttRuntime = null
    }
    if (this._unsubInteraction) {
      this._unsubInteraction()
      this._unsubInteraction = null
    }
    if (this._onGlobalEscape) {
      window.removeEventListener('keydown', this._onGlobalEscape)
      this._onGlobalEscape = null
    }
    if (this._onDsaKeyDown) {
      window.removeEventListener('keydown', this._onDsaKeyDown)
      this._onDsaKeyDown = null
    }
    if (this._onExplorerAutoRefPinChange) {
      try {
        window.removeEventListener(
          EXPLORER_AUTO_REF_PIN_EVENT,
          this._onExplorerAutoRefPinChange
        )
      } catch (e) {
        /* ignore */
      }
      this._onExplorerAutoRefPinChange = null
    }
    teardownImportIngress()
    this.stopGatewayHealthPoll()
    window.removeEventListener('beforeinstallprompt', this._onBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this._onAppInstalled)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('orientationchange', this.handleOrientationChange)
    if (window.visualViewport && this._viewportChromeHandler) {
      try {
        window.visualViewport.removeEventListener('resize', this._viewportChromeHandler)
        window.visualViewport.removeEventListener('scroll', this._viewportChromeHandler)
      } catch (e) {
        /* ignore */
      }
      this._viewportChromeHandler = null
    }
    if (this._viewportChromeRaf) {
      cancelAnimationFrame(this._viewportChromeRaf)
      this._viewportChromeRaf = null
    }
    if (this._mprOrientTimer) {
      clearTimeout(this._mprOrientTimer)
      this._mprOrientTimer = null
    }
    if (this._mprOrientMql && this._mprOrientMqlHandler) {
      try {
        if (this._mprOrientMql.removeEventListener) {
          this._mprOrientMql.removeEventListener('change', this._mprOrientMqlHandler)
        } else if (this._mprOrientMql.removeListener) {
          this._mprOrientMql.removeListener(this._mprOrientMqlHandler)
        }
      } catch (e) { /* ignore */ }
      this._mprOrientMql = null
      this._mprOrientMqlHandler = null
    }
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    if (this._activeSeriesSyncTimer) {
      clearTimeout(this._activeSeriesSyncTimer)
      this._activeSeriesSyncTimer = null
    }
    if (this._mprLiveTimer) {
      clearTimeout(this._mprLiveTimer)
      this._mprLiveTimer = null
    }
    if (this._mpr3dLiveTimer) {
      clearTimeout(this._mpr3dLiveTimer)
      this._mpr3dLiveTimer = null
    }
    if (this._mprLivePoll) {
      clearInterval(this._mprLivePoll)
      this._mprLivePoll = null
    }
    if (this._fsViewportResizeTimer) {
      clearTimeout(this._fsViewportResizeTimer)
      this._fsViewportResizeTimer = null
    }
    if (this._pwaPopstateHandler) {
      window.removeEventListener('popstate', this._pwaPopstateHandler)
      this._pwaPopstateHandler = null
      this._pwaBackTrapReady = false
    }
    if (this._fsRestoreGestureHandler) {
      document.removeEventListener('touchstart', this._fsRestoreGestureHandler, true)
      document.removeEventListener('click', this._fsRestoreGestureHandler, true)
      this._fsRestoreGestureHandler = null
      this._fsRestoreGestureBound = false
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.style.removeProperty('--udv-cine-bottom')
    }
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.handleGatewaySwMessage)
    }
  }

  showAppBar = () => {
    window.scrollTo(0, 0)
  }
  

  toggleMainMenu = () => {
    const visibleMainMenu = this.state.visibleMainMenu
    if (getSettingsFsView() === 'left') {
      this.setState({ visibleMainMenu: !visibleMainMenu, visibleFileManager: false })
    } else {
      this.setState({ visibleMainMenu: !visibleMainMenu })
    }
  }
  
  showMainMenu = () => {
    this.setState({ visibleMainMenu: true })
  }

  hideMainMenu = () => {
    this.setState({ visibleMainMenu: false })
  }

  handleVisibility = (visibleMainMenu) => {
    this.setState({ visibleMainMenu })
  }


  toggleFileManager = () => {
    if (getSettingsFsView() === 'left') {
      this.setState({visibleMainMenu: false, visibleFileManager: !this.state.visibleFileManager})
    } else {
      const visible = !this.state.visibleFileManager
      this.setState({ visibleFileManager: visible })
      if (visible) 
        this.setState({ 
          visibleMeasure: false, 
          visibleHeader: false,
          visibleToolbox: false, 
          visibleDicomdir: false, 
          visibleExplorer: false
        })        
    }
  }
  
  toggleExplorer = () => {
    const visible = !this.state.visibleExplorer
    this.setState({ visibleExplorer: visible }, () => {
      if (!visible) {
        this.hasAutoOpenedExplorer = true // Si el usuario lo cierra, no volver a abrir automáticamente
        
        // Forzar reconfiguración de Hammer.js para todos los viewports activos después de ocultar explorer
        const isMobileDevice = isMobile || isTablet || window.innerWidth < 1024
        if (isMobileDevice) {
          // Forzar re-render de los viewports para que se ajusten al nuevo padding
          setTimeout(() => {
            this.dicomViewersRefs.forEach((viewer, viewerIndex) => {
              if (viewer && viewer.forceReconfigureHammerJS) {
                console.log(`App - Forzando reconfiguración de Hammer.js para viewport ${viewerIndex} después de toggleExplorer (ocultar)`)
                viewer.forceReconfigureHammerJS()
              }
            })
          }, 800) // Aumentar delay para que el DOM se actualice completamente incluyendo el padding
          
          // Reintento adicional después de más tiempo
          setTimeout(() => {
            console.log('App - Reintento de reconfiguración después de toggleExplorer (ocultar)')
            this.dicomViewersRefs.forEach((viewer, viewerIndex) => {
              if (viewer && viewer.forceReconfigureHammerJS) {
                console.log(`App - Reintento de reconfiguración de Hammer.js para viewport ${viewerIndex}`)
                viewer.forceReconfigureHammerJS()
              }
            })
          }, 1500) // Segundo reintento después de 1.5 segundos
        }
      }
      if (visible) {
        this.autoSelectFirstSeries = false // Si el usuario abre el Explorer, no autoseleccionar
        this.setState({ 
          visibleMeasure: false, 
          visibleHeader: false,
          visibleToolbox: false, 
          visibleDicomdir: false, 
          visibleFileManager: false
        })    
      }
    })
  }

  toggleHeader = () => {
    const visible = !this.state.visibleHeader
    this.setState({ visibleHeader: visible })
    if (visible) 
      this.setState({ 
        visibleMeasure: false, 
        visibleToolbox: false, 
        visibleDicomdir: false, 
        visibleFileManager: false,
        visibleExplorer: false
      })    
  }


  toggleToolbox = () => {
    const visible = !this.state.visibleToolbox
    this.setState({ visibleToolbox: visible })
    if (visible) 
      this.setState({ 
        visibleMeasure: false, 
        visibleHeader: false, 
        visibleDicomdir: false, 
        visibleFileManager: false,
        visibleExplorer: false 
      })
  }

  saveMeasure = () => {
    this.dicomViewersRefs[this.props.activeDcmIndex].runTool('savetools')
  }
  
  toggleMeasure = () => {
    const visible = !this.state.visibleMeasure
    this.setState({ visibleMeasure: visible })
    if (visible) 
      this.setState({ 
        visibleToolbox: false, 
        visibleHeader: false, 
        visibleDicomdir: false, 
        visibleFileManager: false,
        visibleExplorer: false
      })
  }

  hideMeasure = () => {
    this.setState({ visibleMeasure: false })
  }

  handleVisibilityMeasure = (visibleMeasure) => {
    this.setState({ visibleMeasure })
  }


  toggleDicomdir = () => {
    const visible = !this.state.visibleDicomdir
    this.setState({ visibleDicomdir: visible })
    if (visible) 
      this.setState({ 
        visibleMeasure: false, 
        visibleToolbox: false, 
        visibleHeader: false, 
        visibleFileManager: false
      })
  }
  

  clearMeasure = () => {
    this.showClearMeasureDlg()
  }

  showClearMeasureDlg = () => {
    this.setState({ visibleClearMeasureDlg: true })
  }

  hideClearMeasureDlg = () => {
      this.setState({ visibleClearMeasureDlg: false })
  }

  confirmClearMeasureDlg = () => {
    this.hideClearMeasureDlg()
    this.dicomViewersRefs[this.props.activeDcmIndex].runTool('removetools')
  }


  showZippedFileDlg = () => {
    this.setState({ visibleZippedFileDlg: true })
  }  

  hideZippedFileDlg = () => {
      this.setState({ visibleZippedFileDlg: false })
  }

  confirmZippedFileDlg = () => {
    this.hideZippedFileDlg()
    this.setState({ visibleFileManager: true }, () => {
      if (this.url !== null) {
        this.setState({visibleDownloadZipDlg: true})
      } else {
        this.props.setFsZippedFile(this.file)
      }
    })
  }


  showAbout = () => {
    this.setState({ visibleAbout: !this.state.visibleAbout })
  }
  
  showSettings = () => {
    this.setState({ 
      visibleMainMenu: false, 
      visibleSettings: true, 
      visibleToolbar: false, 
      position: 'right' 
    })
  }

  hideSettings = () => {
    const keepClassicMenu =
      isDeveloperMode() && this.getToolbarLayout() === TOOLBAR_LAYOUTS.CLASSIC
    this.setState({ 
      visibleMainMenu: keepClassicMenu, 
      visibleSettings: false, 
      visibleToolbar: true, 
      visibleFileManager: false,
      visibleDicomdir: false,
    })
  }

  handleVisibilitySettings = (visibleSettings) => {
    this.setState({ visibleSettings })
  }

  showGsdfQa = () => {
    this.setState({ visibleGsdfQa: true })
  }

  hideGsdfQa = () => {
    this.setState({ visibleGsdfQa: false })
  }

  handleGsdfQaResultChange = (_result, passed) => {
    this.setState({ gsdfQaPassed: !!passed })
  }

  hideDownloadZipDlg = () => {
    this.setState({ visibleDownloadZipDlg: false })
  }

  hideOpenMultipleFilesDlg = () => {
    this.setState({ visibleOpenMultipleFilesDlg: false })
    this.completeManifestLoadStudySource()
    this._localLoadExplorerReady = false
    this.openMultipleFilesCompleted()
  }

  handleLocalFilesLoadProgress = (info) => {
    if (!info) return
    const started = this._localFilesLoadStartedAt || performance.now()
    const completed = Number(info.completed) || 0
    const totalTasks = Number(info.totalTasks) || 0
    this.setState({
      studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      manifestDownloadProgress: {
        completed,
        totalTasks,
        loadedOk: completed,
        elapsedMs: Math.max(0, performance.now() - started),
        studyFullyLoaded: !!info.done,
        loadSource: STUDY_LOAD_SOURCE.LOCAL,
        localPhase: info.phase || 'load',
      },
    })
    if (completed > 0 && !this._localLoadExplorerReady) {
      this._localLoadExplorerReady = true
      this.syncExplorerFromOpenFiles(info.files)
    }
    // Buffer de cine/stack en vivo (cada publish del ZIP/local)
    if (Array.isArray(info.files) && info.files.length > 0) {
      this.forceExpandOpenSeriesBuffers(info.files)
    }
  }

  /** Actualiza explorer apenas hay instancias publicadas (carga silenciosa ZIP/local). */
  syncExplorerFromOpenFiles = (filesArg) => {
    const files =
      Array.isArray(filesArg) && filesArg.length > 0
        ? filesArg
        : this.props.files
    if (!Array.isArray(files) || files.length === 0) return
    applySeriesTotals(files)
    const patientList = groupBy(files, (a) => a.patient.patientName)
    const patientKeys = [...patientList.keys()]
    this.explorer = {
      folder: this.folder,
      patient: {
        list: patientList,
        keys: patientKeys,
      },
    }
    this.setState({
      sliceMax: files.length,
      visibleExplorer: true,
    })
    this.refreshMprViability(files)
  }

  openMultipleFilesCompleted = () => {
    if (this.props.files !== null) {
      applySeriesTotals(this.props.files)
      this.changeLayout(1, 1)

      const sliceMax = this.props.files.length

      this.setState({sliceMax: sliceMax}, () => {
        this.refreshMprViability(this.props.files)
      })

      const patientList = groupBy(this.props.files, a => a.patient.patientName)
      const patientKeys = [...patientList.keys()]
      const patient = {
        list: patientList,
        keys: patientKeys
      }

      this.explorer = {
        folder: this.folder,
        patient: patient,
      }
    } 
  }

  showOpenUrl = () => {
    this.setState({ visibleOpenUrl: true })
  }

  hideOpenUrl = (openDlg) => {
    this.setState({ visibleOpenUrl: false }, () => {
      if (openDlg) {
        this.handleOpenUrl(openDlg);
      }
    });
  }

  handleOpenUrl = (openDlg) => {
    const url = this.openUrlField.value;
    
    // Validar URL
    try {
      new URL(url);
    } catch (e) {
      this.setState({
        titleMessage: 'Error',
        textMessage: t('messages.invalidUrl'),
        visibleMessage: true
      });
      return;
    }

    this.setState({ visibleOpenUrl: false },
      async () => {
        if (openDlg) {
          this.hideMainMenu();
          this.file = null;
          this.url = url;

          // ZIP remoto: descargar y abrir en el visor
          if (getFileExtReal(url) === 'zip') {
            try {
              const res = await fetch(url)
              if (!res.ok) throw new Error(`HTTP ${res.status}`)
              const blob = await res.blob()
              const file = new File([blob], 'study.zip', {
                type: 'application/zip',
              })
              await this.openZipInViewer(file)
            } catch (error) {
              this.setState({
                studyLoadSource: null,
                manifestDownloadProgress: null,
                titleMessage: 'Error',
                textMessage: t('messages.zipLoadFailed', { error: error.message }),
                visibleMessage: true,
              })
            }
            return
          }

          try {
            let imageId;

            // Configurar autenticación WADO (omite auth en R2/S3, Basic solo Orthanc)
            const pacsId = this.state.pacsIdFromUrl || null
            const pacsConfig = getPacsConfig(pacsId)
            configureWadoLoaderAuth({
              username: pacsConfig.USUARIO,
              password: pacsConfig.PASS,
            })

            // Usar la URL directamente sin las credenciales en la URL y usando el proxy Nginx
            const cleanUrl = url.replace('orthanc:orthanc@', '').replace(':8044', '').replace('localhost', 'demoportal.tanjousoft.com.ar/visorweb');
            imageId = `wadouri:${cleanUrl}`;

            // Verificar si la imagen se cargó correctamente
            if (!imageId) {
              throw new Error('Failed to load WADO image');
            }

            // Cargar en el visor
            return this.dicomViewersRefs[this.props.activeDcmIndex].runTool('openurl', imageId);
          } catch (error) {
            console.error('Error loading image:', error);
            this.setState({
              titleMessage: 'Error',
              textMessage: t('messages.imageLoadFailed', { error: error.message }),
              visibleMessage: true
            });
          }
        }
      }
    );
  }

  downloadOpenUrl = () => {
    this.setState({ visibleOpenUrl: false, visibleToolbar: true })
  }
  
  resetImage = () => {
    const viewer = this.resolveActiveDcmViewer()
    if (!viewer || typeof viewer.runTool !== 'function') return
    viewer.runTool('reset')
    if (this.useEdgeToolbarShell()) {
      this.closeEdgeTools()
    }
  }
  
  saveShot = () => {
    if (
      this.promptPortalDisclaimer(() => this.saveShotCore(), { sensitive: true })
    ) {
      return
    }
    this.saveShotCore()
  }

  saveShotCore = () => {
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer || typeof viewer.runTool !== 'function') return
    if (this.useEdgeToolbarShell && this.useEdgeToolbarShell()) {
      this.closeEdgeTools()
    }
    const dest = getSettingsCaptureDest()
    if (dest === 'clipboard' || dest === 'file') {
      viewer.runTool('saveas', dest)
      return
    }
    this.setState({ visibleCaptureDlg: true })
  }

  hideCaptureDlg = () => {
    this.setState({ visibleCaptureDlg: false })
  }

  confirmCaptureDest = (dest) => {
    this.setState({ visibleCaptureDlg: false }, () => {
      const viewer =
        this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
      if (!viewer || typeof viewer.runTool !== 'function') return
      viewer.runTool('saveas', dest === 'clipboard' ? 'clipboard' : 'file')
    })
  }

  handleCaptureResult = ({ ok, dest }) => {
    if (dest === 'clipboard') {
      this.setState({
        captureToast: {
          ok: !!ok,
          message: ok
            ? t('dialogs.captureClipboardOk')
            : t('dialogs.captureClipboardFail'),
        },
      })
    } else if (ok && dest === 'file') {
      this.setState({
        captureToast: {
          ok: true,
          message: t('dialogs.captureFileOk'),
        },
      })
    }
    if (ok && getSettingsOfferShareAfterCapture()) {
      window.setTimeout(() => {
        this.openShareSheet({ preferCapture: true })
      }, 400)
    }
  }

  hideCaptureToast = () => {
    this.setState({ captureToast: null })
  }

  /**
   * ¿Hay PACS default con IP o destinos SCP de usuario?
   */
  canShareToPacs = () => {
    try {
      const cfg = getPacsConfig(this.state.pacsIdFromUrl || null)
      if (cfg && cfg.IP) return true
      return listScpDestinations().length > 0
    } catch (e) {
      return listScpDestinations().length > 0
    }
  }

  getSharePacsDestOptions = () => {
    const opts = [{ id: SCP_SHARE_DEST_DEFAULT, label: t('share.destDefault') }]
    listScpDestinations().forEach((d) => {
      opts.push({ id: d.id, label: d.name })
    })
    return opts
  }

  handleScpShareDestChange = (id) => {
    const next = setScpShareDestId(id)
    this.setState({ scpShareDestId: next })
  }

  openScpDestDlg = () => {
    this.setState({ visibleScpDestDlg: true })
  }

  hideScpDestDlg = () => {
    this.setState({
      visibleScpDestDlg: false,
      scpShareDestId: getScpShareDestId(),
      scpDestTick: (this.state.scpDestTick || 0) + 1,
    })
  }

  /**
   * Config + modo de store para Compartir.
   * @returns {{ pacsConfig: object|null, mode: 'cascade'|'stow-rs'|'orthanc-rest' }}
   */
  resolveSharePacsConfig = () => {
    const destId = getScpShareDestId()
    if (destId && destId !== SCP_SHARE_DEST_DEFAULT) {
      const userDest = getScpDestination(destId)
      const shape = userDest ? toPacsConfigShape(userDest) : null
      if (shape) {
        return {
          pacsConfig: shape,
          mode:
            userDest.connector === SCP_CONNECTOR_ORTHANC
              ? SCP_CONNECTOR_ORTHANC
              : SCP_CONNECTOR_STOW,
        }
      }
    }
    return {
      pacsConfig: getPacsConfig(this.state.pacsIdFromUrl || null),
      mode: 'cascade',
    }
  }

  /**
   * @param {Uint8Array} bytes
   * @returns {Promise<string|null>}
   */
  storeBytesToShareDestination = async (bytes) => {
    const { pacsConfig, mode } = this.resolveSharePacsConfig()
    if (!pacsConfig || !pacsConfig.IP) return null
    if (mode === SCP_CONNECTOR_STOW) {
      const wadoUrl =
        this.state.legacyWadoContext && this.state.legacyWadoContext.wadoUrl
      const ok = await stowStoreDicomInstance(bytes, pacsConfig, { wadoUrl })
      return ok ? 'stow' : null
    }
    if (mode === SCP_CONNECTOR_ORTHANC) {
      const ok = await storeDicomInstanceToPacs(bytes, pacsConfig)
      return ok ? 'orthanc' : null
    }
    return this.storeDicomToPacsCascade(bytes, pacsConfig)
  }

  getActiveViewerFile = () => {
    const viewer = this.resolveActiveDcmViewer
      ? this.resolveActiveDcmViewer()
      : this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer) return null
    const files = viewer.files || this.props.files
    const idx =
      viewer.sliceIndex != null
        ? viewer.sliceIndex
        : this.state.sliceIndex != null
          ? this.state.sliceIndex
          : 0
    if (!Array.isArray(files) || !files[idx]) return null
    return files[idx]
  }

  getShareFilesPool = () => {
    const propsFiles = Array.isArray(this.props.files) ? this.props.files : []
    const viewer = this.resolveActiveDcmViewer
      ? this.resolveActiveDcmViewer()
      : this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    const viewerFiles =
      viewer && Array.isArray(viewer.files) ? viewer.files : []
    // Preferir pool global (multi-serie + informe adjunto) sobre la serie del viewport.
    if (propsFiles.length >= viewerFiles.length && propsFiles.length) {
      return propsFiles
    }
    if (viewerFiles.length) return viewerFiles
    return propsFiles
  }

  /**
   * Contexto de apertura vigente (siempre re-resuelto para adjuntar informe).
   */
  getStudyOpenContextForAttach = () => {
    return resolveStudyOpenContext({
      ticketIdFromUrl: this.state.ticketIdFromUrl,
      manifestUrlFromUrl: this.state.manifestUrlFromUrl,
      legacyWadoContext: this.state.legacyWadoContext,
      studyIdFromUrl: this.state.studyIdFromUrl,
      pacsIdFromUrl: this.state.pacsIdFromUrl,
      studyLoadSource: this.state.studyLoadSource,
    })
  }

  syncStudyOpenContext = (overrides = {}) => {
    const ctx = resolveStudyOpenContext({
      ticketIdFromUrl: this.state.ticketIdFromUrl,
      manifestUrlFromUrl: this.state.manifestUrlFromUrl,
      legacyWadoContext: this.state.legacyWadoContext,
      studyIdFromUrl: this.state.studyIdFromUrl,
      pacsIdFromUrl: this.state.pacsIdFromUrl,
      studyLoadSource: this.state.studyLoadSource,
      ...overrides,
    })
    if (!this._unmounted) {
      this.setState({ studyOpenContext: ctx })
    }
    return ctx
  }

  /** ZIP compartible: remoto R2, o (portal/clínico) estudio WADO completo en IDB. */
  canShareStudyZip = () => {
    const url = this.state.studyDownloadUrl
    const hasRemote = !!(url && /^https?:\/\//i.test(String(url)))
    if (hasRemote) return true
    const local = this.state.localStudyZip
    if (local && local.ready && local.expected > 0) return true
    if (this.isPortalProfileSession()) return false
    return !!(this.getShareFilesPool().length)
  }

  /**
   * Candidato a ZIP desde IndexedDB (WADO/legacy sin study.zip remoto).
   * @param {{ loadSource?: string|null, progressStats?: object|null }} [opts]
   */
  isLocalStudyZipEligible = (opts = {}) => {
    if (!this.isOfflineCacheAllowed()) return false
    const url = this.state.studyDownloadUrl
    if (url && /^https?:\/\//i.test(String(url))) return false
    if (this._localZipSessionActive) return true
    if (this.state.legacyWadoContext && this.state.legacyWadoContext.wadoUrl) {
      return true
    }
    const src = opts.loadSource != null ? opts.loadSource : this.state.studyLoadSource
    if (isStudyLoadWado(src)) return true
    const local = this.state.localStudyZip
    if (local && local.eligible && local.studyUid) return true
    const st = opts.progressStats
    if (
      st &&
      Number(st.totalTasks) > 0 &&
      (st.studyInstanceUID || (st.offlineCache && st.offlineCache.studyInstanceUID))
    ) {
      return true
    }
    return false
  }

  getStudyZipDownloadTitle = () => {
    if (this.state.studyZipDownloadBusy) {
      return t('share.preparingZip')
    }
    const url = this.state.studyDownloadUrl
    if (url && /^https?:\/\//i.test(String(url))) {
      return t('share.downloadStudyTitle')
    }
    const local = this.state.localStudyZip || {}
    if (local.ready) return t('share.downloadStudyTitle')
    if (local.eligible && local.expected > 0) {
      return t('share.preparingZipProgress', {
        cached: local.cached || 0,
        expected: local.expected,
      })
    }
    if (local.eligible) return t('share.preparingZip')
    return t('share.downloadStudyUnavailable')
  }

  canClickStudyZipDownload = () => {
    if (this.state.studyZipDownloadBusy) return false
    const url = this.state.studyDownloadUrl
    if (url && /^https?:\/\//i.test(String(url))) return true
    const local = this.state.localStudyZip
    return !!(local && local.ready)
  }

  showStudyZipDownloadButton = () => {
    const url = this.state.studyDownloadUrl
    if (url && /^https?:\/\//i.test(String(url))) return true
    const local = this.state.localStudyZip
    return !!(local && local.eligible)
  }

  /**
   * Actualiza elegibilidad/completitud del ZIP local según progreso WADO + IDB.
   * @param {object|null} [progressStats]
   */
  syncLocalStudyZipFromProgress = (progressStats) => {
    const eligible = this.isLocalStudyZipEligible({
      loadSource: progressStats && progressStats.loadSource,
      progressStats,
    })
    if (!eligible) {
      const cur = this.state.localStudyZip
      if (cur && (cur.eligible || cur.ready || cur.expected)) {
        this.setState({
          localStudyZip: {
            eligible: false,
            ready: false,
            expected: 0,
            cached: 0,
            studyUid: '',
            networkFallback: false,
          },
        })
      }
      return
    }

    const studyUid =
      (progressStats && progressStats.studyInstanceUID) ||
      (progressStats &&
        progressStats.offlineCache &&
        progressStats.offlineCache.studyInstanceUID) ||
      (this.getCurrentStudyUid && this.getCurrentStudyUid()) ||
      (this.state.legacyWadoContext && this.state.legacyWadoContext.studyUID) ||
      ''
    const totalTasks = Math.max(
      0,
      Number(progressStats && progressStats.totalTasks) || 0
    )
    const loadedOk = Math.max(
      0,
      Number(progressStats && progressStats.loadedOk) || 0
    )
    const failed = Math.max(
      0,
      Number(progressStats && progressStats.failed) || 0
    )
    const fullyLoaded = !!(progressStats && progressStats.studyFullyLoaded)
    const prev = this.state.localStudyZip || {}
    const expected = totalTasks > 0 ? totalTasks : Number(prev.expected) || 0
    const offlineReady = !!(
      progressStats &&
      progressStats.offlineCache &&
      progressStats.offlineCache.studyOffline
    )

    this.setState({
      localStudyZip: {
        eligible: true,
        ready: !!(
          prev.ready &&
          studyUid &&
          prev.studyUid === String(studyUid) &&
          expected > 0
        ),
        expected,
        cached: Math.min(
          Math.max(
            loadedOk,
            Number(
              progressStats &&
                progressStats.offlineCache &&
                progressStats.offlineCache.completeCount
            ) || 0
          ),
          expected || loadedOk
        ),
        studyUid: studyUid ? String(studyUid) : prev.studyUid || '',
        networkFallback: !!prev.networkFallback,
      },
    })

    if (fullyLoaded && failed === 0 && expected > 0 && studyUid) {
      void this.refreshLocalStudyZipReady(String(studyUid), expected, {
        allowNetworkFallback: true,
        loadedOk,
        offlineReady,
      })
    }
  }

  /**
   * Confirma IndexedDB (o fallback red) tras flush de cola.
   * @param {string} studyUid
   * @param {number} expected
   * @param {{ allowNetworkFallback?: boolean, loadedOk?: number, offlineReady?: boolean }} [opts]
   */
  refreshLocalStudyZipReady = async (studyUid, expected, opts = {}) => {
    const uid = String(studyUid || '')
    const exp = Math.max(0, Number(expected) || 0)
    if (!uid || !exp || this._unmounted) return

    if (
      this._localZipRefreshInFlight &&
      this._localZipRefreshUid === uid &&
      this._localZipRefreshExpected === exp
    ) {
      return
    }

    this._localZipRefreshInFlight = true
    this._localZipRefreshUid = uid
    this._localZipRefreshExpected = exp
    const gen = ++this._localStudyZipReadyGen

    try {
      await whenWriteQueueIdle({ timeoutMs: 90000, pollMs: 100 })
      if (this._unmounted || gen !== this._localStudyZipReadyGen) return

      let stats = await getStudyCacheStats(uid)
      let cached = Number(stats && stats.cachedComplete) || 0
      let tries = 0
      while (cached < exp && tries < 40 && !this._unmounted) {
        const q = getWriteQueueStats()
        if (q.queueDepth > 0) {
          await whenWriteQueueIdle({ timeoutMs: 15000, pollMs: 80 })
        } else {
          await new Promise((r) => setTimeout(r, 200))
        }
        if (gen !== this._localStudyZipReadyGen) return
        stats = await getStudyCacheStats(uid)
        cached = Number(stats && stats.cachedComplete) || 0
        tries += 1
      }

      if (this._unmounted || gen !== this._localStudyZipReadyGen) return
      if (!this.isLocalStudyZipEligible()) return

      const loadedOk = Math.max(0, Number(opts.loadedOk) || 0)
      const idbReady = cached >= exp
      const networkFallback =
        !idbReady &&
        !!opts.allowNetworkFallback &&
        (loadedOk >= exp || !!opts.offlineReady)

      this.setState({
        localStudyZip: {
          eligible: true,
          ready: idbReady || networkFallback,
          expected: exp,
          cached: idbReady ? cached : Math.max(cached, loadedOk),
          studyUid: uid,
          networkFallback: !!networkFallback,
        },
      })
    } catch (e) {
      console.warn('refreshLocalStudyZipReady', e)
    } finally {
      if (gen === this._localStudyZipReadyGen) {
        this._localZipRefreshInFlight = false
      }
    }
  }

  getVoiceNoteStudyContext = () => {
    const file = this.getActiveViewerFile && this.getActiveViewerFile()
    let studyUid = ''
    let seriesUid = this._activeSeriesUID || ''
    let modality = ''
    let patientName = ''
    let patientId = ''
    let studyDate = ''
    let studyDescription = ''
    try {
      if (file && file.patient) {
        patientName = file.patient.patientName || ''
        patientId = file.patient.patientID || file.patient.patientId || ''
      }
      if (file && file.study) {
        if (!studyUid) studyUid = file.study.studyId || ''
        studyDate =
          file.study.studyDate ||
          (file.study.studyDateTime
            ? String(file.study.studyDateTime).split('.')[0]
            : '') ||
          ''
        studyDescription = file.study.studyDescription || ''
      }
      if (file && file.image) {
        studyUid = studyUid || getDicomStudyId(file.image) || ''
        if (!studyUid && file.image.data && typeof file.image.data.string === 'function') {
          studyUid = file.image.data.string('x0020000d') || ''
        }
        modality = getDicomModality(file.image) || modality
        if (!seriesUid && file.image.data && typeof file.image.data.string === 'function') {
          seriesUid = file.image.data.string('x0020000e') || ''
        }
        if (file.image.data && typeof file.image.data.string === 'function') {
          if (!patientName) patientName = file.image.data.string('x00100010') || ''
          if (!patientId) patientId = file.image.data.string('x00100020') || ''
          if (!studyDate) studyDate = file.image.data.string('x00080020') || ''
          if (!studyDescription) {
            studyDescription = file.image.data.string('x00081030') || ''
          }
          if (!modality) modality = file.image.data.string('x00080060') || ''
        }
      }
    } catch (e) {
      /* ignore */
    }
    if (!studyUid && this._lastManifestObject && this._lastManifestObject.studyInstanceUID) {
      studyUid = this._lastManifestObject.studyInstanceUID
    }
    if (!studyUid && this.state.legacyWadoContext && this.state.legacyWadoContext.studyUID) {
      studyUid = this.state.legacyWadoContext.studyUID
    }
    const manifest = this._lastManifestObject
    if (manifest) {
      if (!patientName) patientName = manifest.patientName || ''
      if (!patientId) patientId = manifest.patientID || manifest.patientId || ''
      if (!studyDate) studyDate = manifest.studyDate || ''
      if (!studyDescription) {
        studyDescription = manifest.studyDescription || ''
      }
      if (!modality) modality = manifest.modality || ''
    }
    if (!seriesUid && this.resolveActiveSeriesUID) {
      try {
        const viewer =
          this.resolveActiveDcmViewer && this.resolveActiveDcmViewer()
        seriesUid = this.resolveActiveSeriesUID(viewer, { localOnly: true }) || ''
      } catch (e) {
        /* ignore */
      }
    }
    if (!studyUid) {
      try {
        const uid = this.getCurrentStudyUid && this.getCurrentStudyUid()
        if (uid) studyUid = uid
      } catch (e) {
        /* ignore */
      }
    }
    return {
      studyUid: studyUid ? String(studyUid) : '',
      seriesUid: seriesUid ? String(seriesUid) : '',
      modality: modality ? String(modality) : '',
      patientName: patientName ? String(patientName) : '',
      patientId: patientId ? String(patientId) : '',
      studyDate: studyDate ? String(studyDate) : '',
      studyDescription: studyDescription ? String(studyDescription) : '',
    }
  }

  /**
   * Carga notas persistidas del estudio activo (IndexedDB).
   */
  syncVoiceNotesForActiveStudy = () => {
    const ctx = this.getVoiceNoteStudyContext()
    const uid = (ctx && ctx.studyUid) || ''
    if (uid === this._voiceNotesStudyUid) return
    void this.loadVoiceNotesForStudy(uid)
  }

  loadVoiceNotesForStudy = async (studyUid) => {
    const uid = studyUid != null ? String(studyUid).trim() : ''
    const prev = this.state.voiceNotes || []
    revokeVoiceNoteObjectUrls(prev)
    this._voiceNotesStudyUid = uid
    this._voiceNotesLoadToken = (this._voiceNotesLoadToken || 0) + 1
    const token = this._voiceNotesLoadToken
    if (!uid) {
      this.setState({ voiceNotes: [] })
      return
    }
    try {
      const rows = await listVoiceNotesForStudy(uid)
      if (token !== this._voiceNotesLoadToken || this._voiceNotesStudyUid !== uid) {
        return
      }
      const notes = []
      for (let i = 0; i < rows.length; i++) {
        const ui = voiceNoteRowToUi(rows[i])
        if (ui) notes.push(ui)
      }
      this.setState({ voiceNotes: notes })
    } catch (e) {
      if (token === this._voiceNotesLoadToken) {
        this.setState({ voiceNotes: [] })
      }
    }
  }

  _cancelLiveLocalPreloadSchedule = () => {
    if (this._livePreloadTimer) {
      clearTimeout(this._livePreloadTimer)
      this._livePreloadTimer = null
    }
    if (this._livePreloadIdleHandle != null) {
      try {
        if (
          typeof window !== 'undefined' &&
          typeof window.cancelIdleCallback === 'function'
        ) {
          window.cancelIdleCallback(this._livePreloadIdleHandle)
        }
      } catch (e) {
        /* ignore */
      }
      this._livePreloadIdleHandle = null
    }
  }

  /**
   * Precarga Whisper/VAD bajo demanda (panel de voz / cambio de prefs STT).
   * No se dispara al terminar la descarga DICOM: onnxruntime WASM competía con el cine.
   * @param {{ mode?: 'live'|'final'|'all' }} [opts]
   */
  _scheduleLiveLocalPreload = (opts = {}) => {
    const mode = opts.mode === 'final' || opts.mode === 'all' ? opts.mode : 'live'
    const delayMs = typeof opts.delayMs === 'number' ? Math.max(0, opts.delayMs) : 800
    this._cancelLiveLocalPreloadSchedule()
    if (!canRunLocalWhisper()) return
    if (this.isStudyBackgroundLoadActive()) {
      this._livePreloadTimer = setTimeout(() => {
        this._livePreloadTimer = null
        this._scheduleLiveLocalPreload(opts)
      }, 10000)
      return
    }
    this._livePreloadTimer = setTimeout(() => {
      this._livePreloadTimer = null
      if (this.isStudyBackgroundLoadActive()) {
        this._scheduleLiveLocalPreload(opts)
        return
      }
      const startPreload = () => {
        if (this.isStudyBackgroundLoadActive()) {
          this._scheduleLiveLocalPreload(opts)
          return
        }
        this.setState({
          voiceModelProgress: {
            message: 'Inicializando modelos Whisper…',
            percent: null,
          },
        })
        void preloadLiveLocalStt({
          mode,
          onProgress: (p) => {
            const ui = mapSttProgressToUi(p)
            this.setState({
              voiceModelProgress: {
                message: ui.message,
                percent: ui.percent,
              },
            })
          },
        })
          .then(() => {
            this.setState({
              voiceModelProgress: {
                message: 'Modelos Whisper listos',
                percent: 100,
              },
            })
            setTimeout(() => {
              this.setState((prev) =>
                prev.voiceModelProgress &&
                prev.voiceModelProgress.message === 'Modelos Whisper listos'
                  ? { voiceModelProgress: null }
                  : null
              )
            }, 2500)
          })
          .catch(() => {
            this.setState({ voiceModelProgress: null })
          })
      }
      if (
        typeof window !== 'undefined' &&
        typeof window.requestIdleCallback === 'function'
      ) {
        this._livePreloadIdleHandle = window.requestIdleCallback(
          () => {
            this._livePreloadIdleHandle = null
            startPreload()
          },
          { timeout: 15000 }
        )
      } else {
        startPreload()
      }
    }, delayMs)
  }

  _onSttPrefsChanged = () => {
    this._scheduleLiveLocalPreload({ mode: 'live', delayMs: 400 })
  }

  openVoiceNotesPanel = () => {
    const patch = { visibleVoiceNotes: true }
    if (this.useStudioToolbarShell && this.useStudioToolbarShell()) {
      patch.studioRightCollapsed = false
    }
    this.setState(patch)
    // Igual que share: liberar el drawer Edge para ver el dock de notas
    if (this.useEdgeToolbarShell && this.useEdgeToolbarShell()) {
      this.closeEdgeTools()
    }
    // Whisper solo al abrir el panel (no al terminar la descarga DICOM).
    this._scheduleLiveLocalPreload({ mode: 'live', delayMs: 200 })
  }

  hideVoiceNotesPanel = () => {
    if (this.state.voiceNoteBusy) return
    this.setState({ visibleVoiceNotes: false })
  }

  _clearVoiceElapsedTimer = () => {
    if (this._voiceElapsedTimer) {
      clearInterval(this._voiceElapsedTimer)
      this._voiceElapsedTimer = null
    }
  }

  _startVoiceElapsedTimer = () => {
    this._clearVoiceElapsedTimer()
    this._voiceElapsedTimer = setInterval(() => {
      if (!this._voiceRecorder) return
      this.setState({ voiceElapsedMs: this._voiceRecorder.getElapsedMs() })
    }, 200)
  }

  _startLiveSpeech = () => {
    if (!isTranscriptionEnabledForDevice()) return
    this._stopLiveSpeech(false)
    const preferWebSpeech = getWebSpeechPref() && canRunLiveSpeech()

    // Chrome/Edge con preferencia: Web Speech primero
    if (preferWebSpeech) {
      try {
        this._liveSpeech = new LiveSpeechSession({
          lang: 'es-ES',
          onUpdate: (snap) => {
            this.setState({ voiceLiveTranscript: snap })
          },
        })
        const ok = this._liveSpeech.start({ delayMs: 450 })
        if (!ok) {
          this._liveSpeech = null
        } else {
          this.setState({
            voiceLiveTranscript: {
              finalText: '',
              interimText: '',
              text: '',
              status: 'listening',
              error: null,
              engine: 'web_speech',
            },
          })
          return
        }
      } catch (e) {
        this._liveSpeech = null
      }
    }

    // Primario / fallback: Whisper+VAD local
    if (canRunLiveLocalStt()) {
      const stream =
        this._voiceRecorder && this._voiceRecorder.getStream
          ? this._voiceRecorder.getStream()
          : null
      if (!stream) {
        this.setState({
          voiceLiveTranscript: {
            finalText: '',
            interimText: '',
            text: '',
            status: 'error',
            vadState: 'idle',
            error: 'Sin stream de micrófono',
          },
        })
        return
      }
      try {
        this._liveLocal = new LiveLocalSttSession({
          onUpdate: (snap) => {
            this.setState({ voiceLiveTranscript: snap })
          },
        })
        this.setState({
          voiceLiveTranscript: {
            finalText: '',
            interimText: '',
            text: '',
            status: 'listening',
            vadState: 'listening',
            error: null,
            engine: 'local_whisper',
          },
        })
        void this._liveLocal.start(stream).then((ok) => {
          if (!ok) {
            this._liveLocal = null
          }
        })
        return
      } catch (e) {
        this._liveLocal = null
        this.setState({
          voiceLiveTranscript: {
            finalText: '',
            interimText: '',
            text: '',
            status: 'error',
            vadState: 'idle',
            error: (e && e.message) || 'No se pudo iniciar dictado local',
          },
        })
        return
      }
    }

    this.setState({
      voiceLiveTranscript: {
        finalText: '',
        interimText: '',
        text: '',
        status: 'error',
        vadState: 'idle',
        error:
          'Dictado en vivo no disponible (activá Whisper local o Web Speech en Chrome)',
      },
    })
  }

  /**
   * @param {boolean} [keepState]
   * @returns {Promise<string>|string}
   */
  _stopLiveSpeech = (keepState = false) => {
    if (this._liveSpeechKickTimer) {
      clearTimeout(this._liveSpeechKickTimer)
      this._liveSpeechKickTimer = null
    }

    const finish = (text) => {
      const t = String(text || '').trim()
      if (!keepState) {
        this.setState({ voiceLiveTranscript: null })
      } else if (t) {
        this.setState({
          voiceLiveTranscript: {
            finalText: t,
            interimText: '',
            text: t,
            status: 'idle',
            vadState: 'idle',
            error: null,
          },
        })
      }
      return t
    }

    if (this._liveLocal) {
      const session = this._liveLocal
      this._liveLocal = null
      return session
        .stop()
        .then((text) => finish(text))
        .catch(() => finish(''))
    }

    let text = ''
    try {
      if (this._liveSpeech) {
        text = this._liveSpeech.stop() || ''
      }
    } catch (e) {
      text = ''
    }
    this._liveSpeech = null
    return finish(text)
  }

  startVoiceNote = async () => {
    if (this.state.voiceNoteBusy || this.state.voiceNoteRecording) return
    if (this._voiceStartPromise) return this._voiceStartPromise
    if (!isMediaRecorderSupported()) {
      this.setState({
        captureToast: {
          ok: false,
          message: 'Audio no soportado en este dispositivo',
          subtle: true,
          placement: 'top',
        },
      })
      return
    }
    this._voiceStartPromise = (async () => {
      try {
        if (!this._voiceRecorder) this._voiceRecorder = new AudioRecorder()
        const ctx = this.getVoiceNoteStudyContext()
        audioSpatialMapper.start({
          study_instance_uid: ctx.studyUid,
          series_uid: ctx.seriesUid,
          device_type: resolveDeviceType(),
        })
        await this._voiceRecorder.start()
        this._startVoiceElapsedTimer()
        this._startLiveSpeech()
        // Kick solo para Web Speech (local no lo necesita)
        if (this._liveSpeechKickTimer) {
          clearTimeout(this._liveSpeechKickTimer)
        }
        this._liveSpeechKickTimer = setTimeout(() => {
          this._liveSpeechKickTimer = null
          try {
            if (!this._liveSpeech || this._liveLocal || !this.state.voiceNoteRecording) {
              return
            }
            const snap = this._liveSpeech.getSnapshot()
            if (snap.status === 'error') return
            this._liveSpeech.kick()
          } catch (e) {
            /* ignore */
          }
        }, 1100)
        // Solo HUD flotante; el panel lateral se abre a demanda
        this.setState({
          visibleVoiceNotes: false,
          voiceNoteRecording: true,
          voiceNotePaused: false,
          voiceElapsedMs: 0,
          voiceStreamEpoch: (this.state.voiceStreamEpoch || 0) + 1,
        })
      } catch (e) {
        audioSpatialMapper.reset()
        this._stopLiveSpeech(false)
        const denied =
          e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')
        const msg = denied
          ? 'Permiso de micrófono denegado'
          : (e && e.message) || 'No se pudo iniciar la grabación'
        this.setState({
          voiceNoteRecording: false,
          voiceNotePaused: false,
          voiceLiveTranscript: null,
          captureToast: {
            ok: false,
            message: msg,
            subtle: true,
            placement: 'top',
          },
        })
      } finally {
        this._voiceStartPromise = null
      }
    })()
    return this._voiceStartPromise
  }

  pauseVoiceNote = () => {
    if (!this._voiceRecorder || !this.state.voiceNoteRecording) return
    if (this._voiceRecorder.pause()) {
      try {
        if (this._liveLocal) this._liveLocal.pause()
        else if (this._liveSpeech) this._liveSpeech.pause()
      } catch (e) {
        /* ignore */
      }
      this.setState({
        voiceNotePaused: true,
        voiceElapsedMs: this._voiceRecorder.getElapsedMs(),
      })
    }
  }

  resumeVoiceNote = () => {
    if (!this._voiceRecorder || !this.state.voiceNotePaused) return
    if (this._voiceRecorder.resume()) {
      try {
        if (this._liveLocal) this._liveLocal.resume()
        else if (this._liveSpeech) this._liveSpeech.resume()
      } catch (e) {
        /* ignore */
      }
      this.setState({
        voiceNotePaused: false,
      })
    }
  }

  cancelVoiceNote = () => {
    this._clearVoiceElapsedTimer()
    this._stopLiveSpeech(false)
    try {
      if (this._voiceRecorder) this._voiceRecorder.cancel()
    } catch (e) {
      /* ignore */
    }
    audioSpatialMapper.reset()
    this.setState({
      voiceNoteRecording: false,
      voiceNotePaused: false,
      voiceNoteBusy: false,
      voiceElapsedMs: 0,
      voiceLiveTranscript: null,
      captureToast: {
        ok: true,
        message: 'Grabación cancelada',
        subtle: true,
        placement: 'top',
      },
    })
  }

  stopVoiceNote = async () => {
    if (this.state.voiceNoteBusy) return
    if (this._voiceStartPromise) {
      try {
        await this._voiceStartPromise
      } catch (e) {
        this.setState({ voiceNoteRecording: false, voiceNoteBusy: false })
        return
      }
    }
    if (
      !this.state.voiceNoteRecording &&
      !(this._voiceRecorder && this._voiceRecorder.isActive())
    ) {
      return
    }
    this._clearVoiceElapsedTimer()
    let liveDraft = ''
    try {
      const stopped = this._stopLiveSpeech(true)
      liveDraft =
        stopped && typeof stopped.then === 'function'
          ? await stopped
          : stopped || ''
    } catch (e) {
      liveDraft = ''
    }
    this.setState({
      voiceNoteRecording: false,
      voiceNotePaused: false,
      voiceNoteBusy: true,
      captureToast: {
        ok: true,
        message: 'Procesando MP3…',
        subtle: true,
        placement: 'top',
      },
    })
    try {
      const recorded = await this._voiceRecorder.stop()
      audioSpatialMapper.stop()
      const alignment = audioSpatialMapper.getAlignment()
      const mp3Blob = await encodeMp3(recorded.blob)
      const ctx = this.getVoiceNoteStudyContext()
      const studyUid =
        (ctx && ctx.studyUid) ||
        (this.getCurrentStudyUid && this.getCurrentStudyUid()) ||
        ''
      const n = (this.state.voiceNotes || []).length + 1
      const name = `nota_medica_${n}.mp3`
      const id = `vn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const objectUrl = URL.createObjectURL(mp3Blob)
      const cap = probeDeviceCapability()
      const transcribeOn = isTranscriptionEnabledForDevice(cap)
      const wantLocalAsr = transcribeOn && canRunLocalWhisper()
      if (!wantLocalAsr) {
        try {
          console.info(
            '[UDV] whisper skip',
            transcribeOn ? getLocalWhisperSkipReason() : 'transcribe_disabled',
            {
              transcribeOn,
              canLocalWhisper: cap.canLocalWhisper,
              whisperSkipReason: cap.whisperSkipReason,
              hw: cap.hardwareConcurrency,
              ram: cap.deviceMemory,
              device_type: cap.device_type,
            }
          )
        } catch (e) {}
      }
      const liveOnlyTranscript =
        transcribeOn && !wantLocalAsr && liveDraft
          ? {
              text: liveDraft,
              words: [],
              source: 'live_speech',
              liveText: liveDraft,
            }
          : null
      const entry = {
        id,
        name,
        mp3Blob,
        objectUrl,
        durationMs: recorded.durationMs,
        size: mp3Blob.size,
        createdAt: Date.now(),
        alignment,
        studyInstanceUID: studyUid || '',
        patientName: (ctx && ctx.patientName) || '',
        patientId: (ctx && ctx.patientId) || '',
        studyDate: (ctx && ctx.studyDate) || '',
        studyDescription: (ctx && ctx.studyDescription) || '',
        modality: (ctx && ctx.modality) || '',
        liveDraft: liveDraft || '',
        transcript: liveOnlyTranscript,
        transcriptStatus: wantLocalAsr
          ? 'pending'
          : liveOnlyTranscript
            ? 'ready'
            : 'skipped',
      }
      const donePatch = {
        voiceNotes: [entry, ...(this.state.voiceNotes || [])],
        voiceNoteBusy: false,
        voiceElapsedMs: recorded.durationMs,
        visibleVoiceNotes: true,
        voiceLiveTranscript: null,
        captureToast: {
          ok: true,
          message: wantLocalAsr
            ? `${name} guardada · transcribiendo…`
            : `${name} guardada`,
          subtle: true,
          placement: 'top',
        },
      }
      if (this.useStudioToolbarShell && this.useStudioToolbarShell()) {
        donePatch.studioRightCollapsed = false
      }
      this.setState((prev) => ({
        ...donePatch,
        voiceNotes: [entry, ...(prev.voiceNotes || [])],
      }))
      if (studyUid) {
        this._voiceNotesStudyUid = studyUid
        void saveVoiceNote({
          id: entry.id,
          studyInstanceUID: studyUid,
          name: entry.name,
          mp3Blob,
          durationMs: entry.durationMs,
          size: entry.size,
          createdAt: entry.createdAt,
          alignment: entry.alignment,
          patientName: entry.patientName,
          patientId: entry.patientId,
          studyDate: entry.studyDate,
          studyDescription: entry.studyDescription,
          modality: entry.modality,
          transcript: entry.transcript,
          transcriptStatus: entry.transcriptStatus,
        }).catch(() => {
          this.setState({
            captureToast: {
              ok: false,
              message: 'No se pudo persistir en este dispositivo',
              subtle: true,
              placement: 'top',
            },
          })
        })
      }

      void ingestVoiceNote({
        audio: mp3Blob,
        alignment,
        metadata: {
          device_type: cap.device_type,
          study_instance_uid: studyUid || undefined,
          series_uid: (ctx && ctx.seriesUid) || undefined,
          modality: (ctx && ctx.modality) || undefined,
          duration_ms: recorded.durationMs,
          created_at: new Date().toISOString(),
        },
        transcript: liveOnlyTranscript || undefined,
      }).catch(() => {})

      if (wantLocalAsr) {
        void this._runLocalVoiceTranscript(entry, {
          alignment,
          studyUid,
          seriesUid: (ctx && ctx.seriesUid) || undefined,
          modality: (ctx && ctx.modality) || undefined,
          device_type: cap.device_type,
          liveDraft,
        })
      }
    } catch (e) {
      try {
        if (this._voiceRecorder) this._voiceRecorder.cancel()
      } catch (err) {
        /* ignore */
      }
      audioSpatialMapper.reset()
      this._stopLiveSpeech(false)
      const msg = (e && e.message) || 'Error al procesar la nota de voz'
      this.setState({
        voiceNoteBusy: false,
        voiceNoteRecording: false,
        voiceNotePaused: false,
        voiceLiveTranscript: null,
        captureToast: {
          ok: false,
          message: msg,
          subtle: true,
          placement: 'top',
        },
      })
    } finally {
      audioSpatialMapper.reset()
      this.setState({
        voiceNoteBusy: false,
        voiceNoteRecording: false,
        voiceNotePaused: false,
        voiceLiveTranscript: null,
      })
    }
  }

  /**
   * Whisper ONNX + alineación con Web Speech → transcript hybrid.
   * @param {object} entry
   * @param {object} meta
   */
  _runLocalVoiceTranscript = async (entry, meta) => {
    if (!entry || !entry.id || !entry.mp3Blob) return
    const noteId = entry.id
    const liveDraft =
      (meta && meta.liveDraft) || entry.liveDraft || ''
    const modelTier = (meta && meta.modelTier) || undefined
    const modelId =
      (meta && meta.modelId) ||
      (modelTier ? whisperModelIdForTier(modelTier) : undefined)
    try {
      const initialUi = mapSttProgressToUi(null)
      this.setState({
        voiceModelProgress: {
          message: modelTier
            ? `Transcribiendo con ${modelTier}…`
            : initialUi.message,
          percent: null,
        },
      })
      const whisper = await transcribeLocal(entry.mp3Blob, {
        modelTier,
        modelId,
        onProgress: (p) => {
          const ui = mapSttProgressToUi(p)
          this.setState({
            voiceModelProgress: {
              message: ui.message,
              percent: ui.percent,
            },
          })
        },
      })
      this.setState({ voiceModelProgress: null })
      if ((!whisper || !whisper.text) && !liveDraft) {
        this._patchVoiceNoteTranscript(noteId, null, 'error')
        return
      }
      const transcript = buildHybridTranscript(liveDraft, whisper)
      if (!transcript || !transcript.text) {
        this._patchVoiceNoteTranscript(noteId, null, 'error')
        return
      }
      if (whisper && whisper.modelTier) transcript.modelTier = whisper.modelTier
      if (whisper && whisper.modelId) transcript.modelId = whisper.modelId
      this._patchVoiceNoteTranscript(noteId, transcript, 'ready')
      this.setState({
        captureToast: {
          ok: true,
          message:
            transcript.source === 'hybrid'
              ? 'Transcripción lista (hybrid)'
              : modelTier
                ? `Transcripción lista (${modelTier})`
                : 'Transcripción lista',
          subtle: true,
          placement: 'top',
        },
      })
      if (meta && meta.studyUid) {
        void updateVoiceNoteTranscript(noteId, {
          transcript,
          transcriptStatus: 'ready',
        }).catch(() => {})
      }
      void ingestVoiceNote({
        audio: entry.mp3Blob,
        alignment: (meta && meta.alignment) || entry.alignment || { events: [] },
        metadata: {
          device_type:
            (meta && meta.device_type) ||
            probeDeviceCapability().device_type,
          study_instance_uid: (meta && meta.studyUid) || entry.studyInstanceUID,
          series_uid: (meta && meta.seriesUid) || undefined,
          modality: (meta && meta.modality) || entry.modality || undefined,
          duration_ms: entry.durationMs,
          created_at: new Date(entry.createdAt || Date.now()).toISOString(),
        },
        transcript,
      }).catch(() => {})
    } catch (e) {
      this.setState({ voiceModelProgress: null })
      if (e && e.name === 'AbortError') return
      if (liveDraft && !(meta && meta.forceModel)) {
        const fallback = buildHybridTranscript(liveDraft, null)
        this._patchVoiceNoteTranscript(noteId, fallback, 'ready')
        if (meta && meta.studyUid) {
          void updateVoiceNoteTranscript(noteId, {
            transcript: fallback,
            transcriptStatus: 'ready',
          }).catch(() => {})
        }
        return
      }
      this._patchVoiceNoteTranscript(noteId, null, 'error')
      if (meta && meta.studyUid) {
        void updateVoiceNoteTranscript(noteId, {
          transcript: null,
          transcriptStatus: 'error',
        }).catch(() => {})
      }
    }
  }

  /**
   * Re-transcribe una nota ya guardada con otro modelo Whisper.
   * @param {string} id
   * @param {'tiny'|'base'|'small'} modelTier
   */
  retranscribeVoiceNote = async (id, modelTier) => {
    if (this.state.voiceNoteBusy || this.state.voiceNoteRecording) return
    const note = (this.state.voiceNotes || []).find((n) => n && n.id === id)
    if (!note || !note.mp3Blob) {
      this.setState({
        captureToast: {
          ok: false,
          message: 'No hay audio para re-transcribir',
          subtle: true,
          placement: 'top',
        },
      })
      return
    }
    if (!canTranscribeVoiceNotes()) {
      this.setState({
        captureToast: {
          ok: false,
          message: 'Transcripción desactivada o no disponible',
          subtle: true,
          placement: 'top',
        },
      })
      return
    }
    const tier = modelTier || 'base'
    this._patchVoiceNoteTranscript(id, note.transcript, 'pending')
    this.setState({
      voiceNoteBusy: true,
      captureToast: {
        ok: true,
        message: `Re-transcribiendo con ${tier}…`,
        subtle: true,
        placement: 'top',
      },
    })
    try {
      await this._runLocalVoiceTranscript(note, {
        liveDraft: note.liveDraft || '',
        studyUid: note.studyInstanceUID,
        alignment: note.alignment,
        modality: note.modality,
        modelTier: tier,
        modelId: whisperModelIdForTier(tier),
        forceModel: true,
      })
    } finally {
      this.setState({ voiceNoteBusy: false, voiceModelProgress: null })
    }
  }

  _patchVoiceNoteTranscript = (noteId, transcript, transcriptStatus) => {
    this.setState((prev) => {
      const list = prev.voiceNotes || []
      let changed = false
      const next = list.map((n) => {
        if (!n || n.id !== noteId) return n
        changed = true
        return {
          ...n,
          transcript: transcript || null,
          transcriptStatus: transcriptStatus || n.transcriptStatus,
        }
      })
      return changed ? { voiceNotes: next } : null
    })
  }

  /** Click en mic Edge: grabar / detener (el panel se abre con long-press o al terminar). */
  toggleVoiceNote = () => {
    if (this.state.voiceNoteBusy) return
    if (this.state.voiceNoteRecording) {
      void this.stopVoiceNote()
      return
    }
    void this.startVoiceNote()
  }

  _stopVoicePlayback = () => {
    if (this._voicePlayRaf) {
      cancelAnimationFrame(this._voicePlayRaf)
      this._voicePlayRaf = null
    }
    if (this._voiceAudioEl) {
      try {
        this._voiceAudioEl.pause()
        this._voiceAudioEl.removeAttribute('src')
        this._voiceAudioEl.load()
      } catch (e) {
        /* ignore */
      }
      this._voiceAudioEl = null
    }
    if (this.state.voicePlayingId || this.state.voicePlayProgress) {
      this.setState({ voicePlayingId: null, voicePlayProgress: null })
    }
  }

  _syncVoicePlayProgress = () => {
    const audio = this._voiceAudioEl
    const id = this.state.voicePlayingId
    if (!audio || !id) {
      this._voicePlayRaf = null
      return
    }
    const duration =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.duration
        : (this.state.voicePlayProgress && this.state.voicePlayProgress.duration) || 0
    this.setState({
      voicePlayProgress: {
        id,
        current: audio.currentTime || 0,
        duration,
      },
    })
    if (!audio.paused && !audio.ended) {
      this._voicePlayRaf = requestAnimationFrame(this._syncVoicePlayProgress)
    } else {
      this._voicePlayRaf = null
    }
  }

  playVoiceNote = (id) => {
    const note = (this.state.voiceNotes || []).find((n) => n.id === id)
    if (!note || !note.objectUrl) return
    this._stopVoicePlayback()
    const audio = new Audio(note.objectUrl)
    this._voiceAudioEl = audio
    const fallbackDur = note.durationMs ? note.durationMs / 1000 : 0
    audio.onloadedmetadata = () => {
      const duration =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : fallbackDur
      this.setState({
        voicePlayProgress: { id, current: 0, duration },
      })
    }
    audio.onended = () => {
      if (this._voicePlayRaf) {
        cancelAnimationFrame(this._voicePlayRaf)
        this._voicePlayRaf = null
      }
      if (this.state.voicePlayingId === id) {
        this.setState({
          voicePlayingId: null,
          voicePlayProgress: {
            id,
            current:
              (this.state.voicePlayProgress && this.state.voicePlayProgress.duration) ||
              fallbackDur,
            duration:
              (this.state.voicePlayProgress && this.state.voicePlayProgress.duration) ||
              fallbackDur,
          },
        })
      }
      this._voiceAudioEl = null
    }
    this.setState({
      voicePlayingId: id,
      voicePlayProgress: { id, current: 0, duration: fallbackDur },
      visibleVoiceNotes: true,
    })
    void audio.play()
      .then(() => {
        this._voicePlayRaf = requestAnimationFrame(this._syncVoicePlayProgress)
      })
      .catch(() => {
        this.setState({ voicePlayingId: null, voicePlayProgress: null })
        this._voiceAudioEl = null
      })
  }

  seekVoiceNote = (id, timeSec) => {
    const t = Math.max(0, Number(timeSec) || 0)
    const note = (this.state.voiceNotes || []).find((n) => n.id === id)
    if (!note) return

    const applyProgress = (audio) => {
      const duration =
        Number.isFinite(audio.duration) && audio.duration > 0
          ? audio.duration
          : note.durationMs
            ? note.durationMs / 1000
            : t
      try {
        audio.currentTime = Math.min(t, duration || t)
      } catch (e) {
        /* ignore */
      }
      this.setState({
        voicePlayingId: id,
        voicePlayProgress: {
          id,
          current: audio.currentTime || t,
          duration,
        },
      })
    }

    if (this._voiceAudioEl && this.state.voicePlayingId === id) {
      applyProgress(this._voiceAudioEl)
      if (this._voiceAudioEl.paused) {
        void this._voiceAudioEl.play()
          .then(() => {
            this._voicePlayRaf = requestAnimationFrame(this._syncVoicePlayProgress)
          })
          .catch(() => {})
      }
      return
    }

    // Arranca reproducción en la posición pedida
    this._stopVoicePlayback()
    const audio = new Audio(note.objectUrl)
    this._voiceAudioEl = audio
    const seekWhenReady = () => {
      applyProgress(audio)
      void audio.play()
        .then(() => {
          this._voicePlayRaf = requestAnimationFrame(this._syncVoicePlayProgress)
        })
        .catch(() => {})
    }
    if (audio.readyState >= 1) seekWhenReady()
    else audio.onloadedmetadata = seekWhenReady
    audio.onended = () => {
      this._stopVoicePlayback()
    }
    this.setState({ visibleVoiceNotes: true, voicePlayingId: id })
  }

  shareVoiceNoteById = async (id) => {
    const note = (this.state.voiceNotes || []).find((n) => n.id === id)
    if (!note || !note.mp3Blob) return
    this.setState({
      voiceNoteBusy: true,
      captureToast: {
        ok: true,
        message: 'Compartiendo…',
        subtle: true,
        placement: 'top',
      },
    })
    try {
      const ctx = this.getVoiceNoteStudyContext()
      const result = await shareVoiceNote({
        mp3Blob: note.mp3Blob,
        filename: note.name || VOICE_NOTE_FILENAME,
        studyUrl: typeof window !== 'undefined' ? window.location.href : '',
        patientName: note.patientName || (ctx && ctx.patientName) || '',
        patientId: note.patientId || (ctx && ctx.patientId) || '',
        studyDate: note.studyDate || (ctx && ctx.studyDate) || '',
        studyDescription:
          note.studyDescription || (ctx && ctx.studyDescription) || '',
        modality: note.modality || (ctx && ctx.modality) || '',
        createdAt: note.createdAt,
        transcriptText:
          (note.transcript && note.transcript.text) || undefined,
      })
      if (result.mode === 'abort') {
        this.setState({
          captureToast: {
            ok: true,
            message: 'Compartir cancelado',
            subtle: true,
            placement: 'top',
          },
        })
      } else if (result.ok) {
        const clipHint = result.clipboard ? ' · texto copiado' : ''
        this.setState({
          captureToast: {
            ok: true,
            message:
              result.mode === 'download'
                ? `${note.name} descargado${clipHint}`
                : `Compartida${clipHint}`,
            subtle: true,
            placement: 'top',
          },
        })
      } else {
        this.setState({
          captureToast: {
            ok: false,
            message: 'No se pudo compartir',
            subtle: true,
            placement: 'top',
          },
        })
      }
    } catch (e) {
      this.setState({
        captureToast: {
          ok: false,
          message: (e && e.message) || 'Error al compartir',
          subtle: true,
          placement: 'top',
        },
      })
    } finally {
      this.setState({ voiceNoteBusy: false })
    }
  }

  downloadVoiceNoteById = (id) => {
    const note = (this.state.voiceNotes || []).find((n) => n.id === id)
    if (!note || !note.mp3Blob) return
    const file = blobToFile(note.mp3Blob, note.name || VOICE_NOTE_FILENAME, 'audio/mpeg')
    downloadFileFallback(file, note.name || VOICE_NOTE_FILENAME)
  }

  deleteVoiceNoteById = (id) => {
    if (this.state.voicePlayingId === id) this._stopVoicePlayback()
    this.setState((prev) => {
      const list = prev.voiceNotes || []
      const target = list.find((n) => n.id === id)
      if (target && target.objectUrl) {
        try {
          URL.revokeObjectURL(target.objectUrl)
        } catch (e) {
          /* ignore */
        }
      }
      return { voiceNotes: list.filter((n) => n.id !== id) }
    })
    void deleteVoiceNote(id).catch(() => {})
  }

  renderVoiceNotesPanel = (layout, skin) => (
    <VoiceNotesPanel
      open={!!this.state.visibleVoiceNotes}
      skin={skin}
      layout={layout}
      recording={!!this.state.voiceNoteRecording}
      paused={!!this.state.voiceNotePaused}
      busy={!!this.state.voiceNoteBusy}
      canPause={canPauseMediaRecorder()}
      stream={
        this._voiceRecorder && this.state.voiceNoteRecording
          ? this._voiceRecorder.getStream()
          : null
      }
      elapsedMs={this.state.voiceElapsedMs || 0}
      liveTranscript={this.state.voiceLiveTranscript}
      notes={this.state.voiceNotes || []}
      playingId={this.state.voicePlayingId}
      playProgress={this.state.voicePlayProgress}
      modelProgress={this.state.voiceModelProgress}
      sttRuntime={this.state.voiceSttRuntime}
      canLocalWhisper={canRunLocalWhisper()}
      transcribeOnDevice={isTranscriptionEnabledForDevice()}
      onSttPrefsChange={this._onSttPrefsChanged}
      onClose={this.hideVoiceNotesPanel}
      onStart={this.startVoiceNote}
      onPause={this.pauseVoiceNote}
      onResume={this.resumeVoiceNote}
      onStop={this.stopVoiceNote}
      onCancel={this.cancelVoiceNote}
      onPlay={this.playVoiceNote}
      onStopPlay={this._stopVoicePlayback}
      onSeek={this.seekVoiceNote}
      onShare={this.shareVoiceNoteById}
      onDownload={this.downloadVoiceNoteById}
      onDelete={this.deleteVoiceNoteById}
      onRetranscribe={this.retranscribeVoiceNote}
    />
  )

  openCopilotPanel = () => {
    if (this.useEdgeToolbarShell && this.useEdgeToolbarShell()) {
      this.closeEdgeTools()
    }
    const model = resolveCopilotModelId(
      this.state.copilotModel || COPILOT_MODELS.FLASH
    )
    this.setState({ visibleCopilot: true, copilotModel: model })
  }

  /** Limpia preinforme Copilot (otro estudio / nueva carga). */
  resetCopilotState = () => {
    this._copilotSessionGen = (this._copilotSessionGen || 0) + 1
    if (this._unmounted) return
    this.setState({
      copilotLoading: false,
      copilotResult: null,
      copilotHallazgos: '',
      copilotImpresion: '',
      copilotRecomendacion: '',
    })
  }

  /** Vuelve a 1×1 al abrir otro estudio (no en reload PREVIEW↔DIAG del mismo). */
  resetViewportLayoutForNewStudy = () => {
    try {
      const lay = this.props.layout
      if (!lay || lay[0] !== 1 || lay[1] !== 1) {
        this.changeLayout(1, 1)
      }
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * Limpia viewports + Explorer/miniaturas del estudio anterior
   * (evita residuales mientras carga el nuevo). No toca studyLoadSource.
   */
  clearPreviousStudyUi = () => {
    this.resetViewportLayoutForNewStudy()
    try {
      if (this.dicomViewersRefs) {
        for (let i = 0; i < this.dicomViewersRefs.length; i++) {
          const v = this.dicomViewersRefs[i]
          if (v && typeof v.runTool === 'function') {
            try {
              v.runTool('clear')
            } catch (eClear) {
              /* ignore */
            }
          }
        }
      }
    } catch (eViewers) {
      /* ignore */
    }
    try {
      if (typeof this.invalidateReferenceLinesSync === 'function') {
        this.invalidateReferenceLinesSync()
      }
    } catch (eRef) {
      /* ignore */
    }
    try {
      ReferenceLinesManager.disable()
      ReferenceLinesManager.clearElements()
    } catch (eRef2) {
      /* ignore */
    }
    try {
      if (typeof this.clearMprState === 'function') this.clearMprState()
    } catch (eMpr) {
      /* ignore */
    }
    // Explorer App (Studio/Edge) + Redux files/series → miniaturas vacías
    this.explorer = {
      folder: null,
      patient: { list: new Map(), keys: [] },
    }
    try {
      if (typeof this.props.setFilesStore === 'function') {
        this.props.setFilesStore(null)
      }
    } catch (eFiles) {
      /* ignore */
    }
    try {
      if (typeof this.props.setSeriesStore === 'function') {
        this.props.setSeriesStore({
          seriesList: new Map(),
          seriesKeys: [],
        })
      }
    } catch (eSeries) {
      /* ignore */
    }
    try {
      if (typeof this.props.setExplorerStore === 'function') {
        this.props.setExplorerStore({
          folder: null,
          patient: { list: new Map(), keys: [] },
        })
      }
    } catch (eExp) {
      /* ignore */
    }
    try {
      if (this.props.setExplorerActiveSeriesIndex) {
        this.props.setExplorerActiveSeriesIndex(0)
      }
    } catch (eIdx) {
      /* ignore */
    }
    try {
      this.setState({
        visibleExplorer: true,
        sliceIndex: 0,
        sliceMax: 1,
      })
    } catch (eState) {
      /* ignore */
    }
  }

  hideCopilotPanel = () => {
    if (this.state.copilotLoading) return
    this.setState({ visibleCopilot: false })
  }

  _copilotErrorMessage = (err) => {
    const code = err && err.code
    if (code === COPILOT_ERROR.NO_KEY) return t('copilot.errNoKey')
    if (code === COPILOT_ERROR.PROXY_UNAVAILABLE) return t('copilot.errProxy')
    if (code === COPILOT_ERROR.HTTP) return t('copilot.errHttp')
    if (code === COPILOT_ERROR.TIMEOUT) return t('copilot.errTimeout')
    if (code === COPILOT_ERROR.INVALID_JSON || code === COPILOT_ERROR.EMPTY) {
      return t('copilot.errJson')
    }
    if (code === COPILOT_ERROR.MEDGEMMA_UNAVAILABLE) return t('copilot.errMedgemma')
    return t('copilot.analyzeFail')
  }

  runCopilotAnalyze = async () => {
    if (this.state.copilotLoading) return
    const viewer = this.resolveActiveDcmViewer
      ? this.resolveActiveDcmViewer()
      : this.getActiveDcmViewer()
    const enabled = getEnabledElementFromViewer(viewer)
    const hasAnyOpen =
      Array.isArray(this.dicomViewersRefs) &&
      this.dicomViewersRefs.some((v) => v && v.image)
    if ((!enabled || !enabled.image) && !hasAnyOpen) {
      this.setState({
        titleMessage: t('common.warning'),
        textMessage: t('copilot.noImage'),
        visibleMessage: true,
      })
      return
    }
    // setState es async: ceder un frame para que el spinner pinte antes de la captura sync.
    const analyzeGen = this._copilotSessionGen || 0
    this.setState({ copilotLoading: true }, () => {
      this._runCopilotAnalyzeAfterPaint(enabled, analyzeGen)
    })
  }

  _runCopilotAnalyzeAfterPaint = async (enabled, analyzeGen) => {
    await new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      } else {
        setTimeout(resolve, 0)
      }
    })
    const t0 =
      typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now()
    try {
      const capture = getCopilotCaptureBase64({
        viewers: this.dicomViewersRefs,
        layout: this.props.layout,
        fallbackEnabled: enabled,
      })
      const base64 = capture.base64
      const tCapture =
        (typeof performance !== 'undefined' && performance.now
          ? performance.now()
          : Date.now()) - t0
      const model = this.state.copilotModel || COPILOT_MODELS.FLASH
      const resultado = await solicitarPreinformeCopilot(base64, model)
      if (typeof console !== 'undefined' && console.info) {
        const total =
          (typeof performance !== 'undefined' && performance.now
            ? performance.now()
            : Date.now()) - t0
        console.info(
          'UDV AI Copilot timing:',
          `capture=${Math.round(tCapture)}ms`,
          `total=${Math.round(total)}ms`,
          `tiles=${capture.tileCount}`,
          `layout=${capture.rows}x${capture.cols}`,
          `model=${model}`,
          resultado && resultado.model_used
            ? `used=${resultado.model_used}`
            : ''
        )
      }
      if (this._unmounted) return
      if (analyzeGen !== (this._copilotSessionGen || 0)) return
      this.setState({
        copilotLoading: false,
        copilotResult: resultado,
        copilotHallazgos: resultado.hallazgos || '',
        copilotImpresion: resultado.impresion_diagnostica || '',
        copilotRecomendacion: resultado.recomendacion || '',
      })
    } catch (err) {
      console.error('UDV AI Copilot:', err)
      if (this._unmounted) return
      if (analyzeGen !== (this._copilotSessionGen || 0)) return
      this.setState({
        copilotLoading: false,
        titleMessage: t('common.error'),
        textMessage: this._copilotErrorMessage(err),
        visibleMessage: true,
      })
    }
  }

  insertCopilotToReport = async () => {
    const result = {
      triage_level:
        (this.state.copilotResult && this.state.copilotResult.triage_level) || '',
      urgency_reason:
        (this.state.copilotResult && this.state.copilotResult.urgency_reason) || '',
      hallazgos: this.state.copilotHallazgos || '',
      impresion_diagnostica: this.state.copilotImpresion || '',
      recomendacion: this.state.copilotRecomendacion || '',
    }
    try {
      const out = await insertCopilotReport(result)
      this.setState({
        captureToast: {
          ok: true,
          message:
            out.mode === 'postMessage'
              ? t('copilot.insertedPlatform')
              : t('copilot.copiedClipboard'),
        },
      })
    } catch (err) {
      console.error('UDV AI Copilot insert:', err)
      this.setState({
        captureToast: { ok: false, message: t('copilot.insertFail') },
      })
    }
  }

  renderCopilotPanel = (layout, skin) => (
    <CopilotPanel
      open={!!this.state.visibleCopilot}
      skin={skin}
      layout={layout}
      loading={!!this.state.copilotLoading}
      model={this.state.copilotModel || COPILOT_MODELS.FLASH}
      result={this.state.copilotResult}
      hallazgos={this.state.copilotHallazgos || ''}
      impresion={this.state.copilotImpresion || ''}
      recomendacion={this.state.copilotRecomendacion || ''}
      disabledAnalyze={
        !(Array.isArray(this.props.isOpen) && this.props.isOpen.some(Boolean))
      }
      onClose={this.hideCopilotPanel}
      onModelChange={(model) => this.setState({ copilotModel: model })}
      onAnalyze={this.runCopilotAnalyze}
      onChangeHallazgos={(v) => this.setState({ copilotHallazgos: v })}
      onChangeImpresion={(v) => this.setState({ copilotImpresion: v })}
      onChangeRecomendacion={(v) => this.setState({ copilotRecomendacion: v })}
      onInsert={this.insertCopilotToReport}
    />
  )

  openShareSheet = async (opts = {}) => {
    const run = () => this.openShareSheetCore(opts)
    if (this.promptPortalDisclaimer(run, { sensitive: true })) return
    await run()
  }

  openShareSheetCore = async (opts = {}) => {
    if (this.useEdgeToolbarShell && this.useEdgeToolbarShell()) {
      this.closeEdgeTools()
    }
    this.setState({
      visibleShareSheet: true,
      visibleCaptureDlg: false,
      shareBusy: false,
      shareStatus: '',
      shareZipInfo: null,
      sharePreferCapture: !!opts.preferCapture,
      scpShareDestId: getScpShareDestId(),
    })
    const url = this.state.studyDownloadUrl
    const localFiles = this.getShareFilesPool()
    const portalSession = this.isPortalProfileSession()
    const localZip = this.state.localStudyZip
    if (url && /^https?:\/\//i.test(String(url))) {
      try {
        const probe = await probeRemoteZipSize(url)
        this.setState({
          shareZipInfo: {
            byteLength: probe.byteLength,
            tier: zipSizeTier(probe.byteLength || 0),
            source: 'url',
          },
        })
      } catch (e) {
        this.setState({
          shareZipInfo: { byteLength: null, tier: 'ok', source: 'url' },
        })
      }
    } else if (localZip && localZip.ready) {
      this.setState({
        shareZipInfo: { byteLength: null, tier: 'ok', source: 'local-idb' },
      })
    } else if (!portalSession && localFiles.length) {
      this.setState({
        shareZipInfo: { byteLength: null, tier: 'ok', source: 'local' },
      })
    }
  }

  hideShareSheet = () => {
    if (this.state.shareBusy) return
    this.setState({
      visibleShareSheet: false,
      shareStatus: '',
      shareBusy: false,
    })
  }

  finishShareResult = (result) => {
    if (!result) {
      this.setState({
        shareBusy: false,
        shareStatus: t('share.fail'),
        captureToast: { ok: false, message: t('share.fail') },
      })
      return
    }
    if (result.mode === 'abort') {
      this.setState({
        shareBusy: false,
        shareStatus: t('share.abort'),
      })
      return
    }
    if (result.ok && result.mode === 'share') {
      this.setState({
        shareBusy: false,
        visibleShareSheet: false,
        captureToast: { ok: true, message: t('share.okShare') },
      })
      return
    }
    if (result.ok && result.mode === 'download') {
      this.setState({
        shareBusy: false,
        visibleShareSheet: false,
        captureToast: { ok: true, message: t('share.okDownload') },
      })
      return
    }
    this.setState({
      shareBusy: false,
      shareStatus: t('share.fail'),
      captureToast: { ok: false, message: t('share.fail') },
    })
  }

  handleShareCapture = async () => {
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer) return
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const file = await buildCaptureFile(viewer)
      if (!file) {
        this.setState({
          shareBusy: false,
          shareStatus: t('share.fail'),
        })
        return
      }
      this.setState({ shareStatus: t('share.sharing') })
      const result = await shareOrFallback({
        files: [file],
        title: t('share.capture'),
        text: file.name,
      })
      this.finishShareResult(result)
    } catch (e) {
      console.warn('handleShareCapture', e)
      this.finishShareResult({ ok: false })
    }
  }

  handleShareZip = async () => {
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const studyUid = this.getCurrentStudyUid ? this.getCurrentStudyUid() : ''
      const local = this.state.localStudyZip || {}
      const filename = buildStudyZipDownloadFilename(
        resolveStudyZipMeta(
          this.getShareFilesPool(),
          this._lastManifestObject,
          studyUid
        )
      )
      const hasRemote =
        this.state.studyDownloadUrl &&
        /^https?:\/\//i.test(String(this.state.studyDownloadUrl))
      const built = await buildStudyZipFile({
        url: this.state.studyDownloadUrl,
        // En portal/WADO IDB completo: no mezclar con pool de viewport (serie parcial).
        files:
          !hasRemote && local.ready
            ? []
            : this.getShareFilesPool(),
        studyUid: local.studyUid || studyUid,
        filename,
        expectedCount: local.ready ? local.expected : 0,
        allowLarge: false,
      })
      if (built.tier === 'block' || !built.file) {
        this.setState({
          shareBusy: false,
          shareZipInfo: {
            byteLength: built.byteLength,
            tier: built.tier || 'block',
            source: 'built',
          },
          shareStatus: t('share.failZip'),
        })
        return
      }
      this.setState({
        shareZipInfo: {
          byteLength: built.byteLength,
          tier: built.tier,
          source: 'built',
        },
        shareStatus: t('share.sharing'),
      })
      const result = await shareOrFallback({
        files: [built.file],
        title: t('share.zip'),
        text: built.file.name,
      })
      this.finishShareResult(result)
    } catch (e) {
      console.warn('handleShareZip', e)
      this.setState({ shareBusy: false, shareStatus: t('share.failZip') })
    }
  }

  getStudyShareUrl = () => {
    const ticketId = this.state.ticketIdFromUrl
    if (!ticketId) return ''
    const params =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : null
    return buildStudyShareUrl({
      ticketId,
      ticketApi: getTicketApiBase(params),
      edgeId: parseEdgeIdFromSearchParams(params),
      clinicName:
        params && params.get('clinic_name')
          ? String(params.get('clinic_name')).trim()
          : null,
    })
  }

  handleCopyStudyLink = async () => {
    const url = this.getStudyShareUrl()
    if (!url) return
    const ok = await copyTextToClipboard(url)
    this.setState({
      captureToast: {
        ok,
        message: ok ? t('share.studyLinkCopied') : t('share.studyLinkCopyFail'),
      },
    })
  }

  handleShareStudyLink = async () => {
    const url = this.getStudyShareUrl()
    if (!url) return
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const result = await shareOrFallback({
        title: t('share.studyLink'),
        text: t('share.studyLinkHint'),
        url,
      })
      if (result && result.ok) {
        this.setState({
          shareBusy: false,
          visibleShareSheet: false,
          captureToast: {
            ok: true,
            message:
              result.mode === 'share'
                ? t('share.studyLinkShared')
                : t('share.studyLinkCopied'),
          },
        })
        return
      }
      this.setState({
        shareBusy: false,
        shareStatus: result && result.mode === 'abort' ? t('share.abort') : t('share.studyLinkCopyFail'),
      })
    } catch (e) {
      console.warn('handleShareStudyLink', e)
      this.setState({ shareBusy: false, shareStatus: t('share.studyLinkCopyFail') })
    }
  }

  handleSharePreviews = async (mode) => {
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const files = this.getShareFilesPool()
      const active = this.getActiveViewerFile()
      const seriesUid =
        (active && (active.seriesInstanceUID || active.seriesUID)) || null
      const built = await buildPreviewShareFiles({
        files,
        mode: mode === 'study' ? 'study' : 'series',
        seriesUid,
        studyUid: this.getCurrentStudyUid ? this.getCurrentStudyUid() : null,
      })
      if (!built.files || !built.files.length) {
        this.setState({
          shareBusy: false,
          shareStatus: t('share.failPreviews'),
          captureToast: { ok: false, message: t('share.failPreviews') },
        })
        return
      }
      this.setState({ shareStatus: t('share.sharing') })
      const result = await shareOrFallback({
        files: built.files,
        title:
          mode === 'study' ? t('share.studyPreviews') : t('share.seriesPreviews'),
      })
      this.finishShareResult(result)
    } catch (e) {
      console.warn('handleSharePreviews', e)
      this.setState({ shareBusy: false, shareStatus: t('share.failPreviews') })
    }
  }

  handleStowCurrentSop = async () => {
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const file = this.getActiveViewerFile()
      const bytes = await getDicomBytesForFile(file)
      if (!bytes || !bytes.length) {
        this.setState({
          shareBusy: false,
          shareStatus: t('share.failStowBytes'),
          captureToast: { ok: false, message: t('share.failStowBytes') },
        })
        return
      }
      const channel = await this.storeBytesToShareDestination(bytes)
      if (!channel) {
        this.setState({
          shareBusy: false,
          shareStatus: t('share.failStow'),
          captureToast: { ok: false, message: t('share.failStow') },
        })
        return
      }
      this.setState({
        shareBusy: false,
        visibleShareSheet: false,
        captureToast: {
          ok: true,
          message: t('share.okStow', { channel }),
        },
      })
    } catch (e) {
      console.warn('handleStowCurrentSop', e)
      this.setState({
        shareBusy: false,
        shareStatus: t('share.failStow'),
      })
    }
  }

  handleStowActiveSeries = async () => {
    this.setState({ shareBusy: true, shareStatus: t('share.preparing') })
    try {
      const files = this.getShareFilesPool()
      const active = this.getActiveViewerFile()
      const seriesUid =
        active && (active.seriesInstanceUID || active.seriesUID)
          ? String(active.seriesInstanceUID || active.seriesUID)
          : null
      let list = files
      if (seriesUid) {
        list = files.filter(
          (f) =>
            f &&
            ((f.seriesInstanceUID && String(f.seriesInstanceUID) === seriesUid) ||
              (f.seriesUID && String(f.seriesUID) === seriesUid))
        )
      }
      if (!list.length) list = active ? [active] : []
      let done = 0
      let ok = 0
      for (let i = 0; i < list.length; i++) {
        this.setState({
          shareStatus: t('share.stowProgress', {
            done: i,
            total: list.length,
          }),
        })
        const bytes = await getDicomBytesForFile(list[i])
        if (!bytes || !bytes.length) continue
        const channel = await this.storeBytesToShareDestination(bytes)
        done += 1
        if (channel) ok += 1
      }
      if (!ok) {
        this.setState({
          shareBusy: false,
          shareStatus: t('share.failStow'),
          captureToast: { ok: false, message: t('share.failStow') },
        })
        return
      }
      this.setState({
        shareBusy: false,
        visibleShareSheet: false,
        captureToast: {
          ok: true,
          message: t('share.okStow', { channel: `${ok}/${done}` }),
        },
      })
    } catch (e) {
      console.warn('handleStowActiveSeries', e)
      this.setState({
        shareBusy: false,
        shareStatus: t('share.failStow'),
      })
    }
  }

  cinePlayer = () => {
    const viewer = this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer) return
    viewer.runTool('cine')
    // Forzar re-render para resaltar botón filmadora (cineOpen)
    this.setState({ cineUiTick: (this.state.cineUiTick || 0) + 1 })
  }

  clear = () => {  
    this.clearMprState()
    this.layoutGridClick(0)
    if (this.props.isOpen && Array.isArray(this.props.isOpen)) {
      for(let i=0; i < this.props.isOpen.length; i++) {
        if (this.props.isOpen[i] && this.dicomViewersRefs[i]) {
          this.dicomViewersRefs[i].runTool('clear')
        }
      }
    }
    setTimeout(() => {
      this.setState({openImageEdit: false, 
                    openTools: false, 
                    mprMenu: false, 
                    visibleToolbox: false, 
                    visibleMeasure: false, 
                    visibleHeader: false, 
                    visibleDicomdir: false,
                    studyLoadSource: null,
                    manifestDownloadProgress: null}, () => { 
                    })
      this.changeLayout(1, 1)
      this.props.setFilesStore(null)
      this.props.setDicomdirStore(null)
      //this.props.clearingStore()      
    }, 100) 
  }

  /** Vaciar IndexedDB del study del último progress (no limpia sesión de UI). */
  handleClearStudyDicomCache = async () => {
    const progress = this.state.manifestDownloadProgress
    const uid =
      (progress && progress.studyInstanceUID) ||
      (progress && progress.offlineCache && progress.offlineCache.studyInstanceUID) ||
      null
    if (!uid) {
      console.warn('handleClearStudyDicomCache: sin studyInstanceUID')
      return
    }
    try {
      await limpiarCacheEstudio(uid)
      this.setState((prev) => {
        const p = prev.manifestDownloadProgress
        if (!p) return null
        return {
          manifestDownloadProgress: {
            ...p,
            offlineCache: {
              ...(p.offlineCache || {}),
              completeCount: 0,
              fromCacheHits: 0,
              studyOffline: false,
              bytesCached: 0,
              targetCount: (p.offlineCache && p.offlineCache.targetCount) || p.totalTasks,
              studyInstanceUID: uid,
            },
          },
        }
      })
    } catch (e) {
      console.warn('handleClearStudyDicomCache', e)
    }
  }

  handleClearAllDicomCache = async () => {
    try {
      await vaciarCacheTotal()
      this.setState((prev) => {
        const p = prev.manifestDownloadProgress
        if (!p) return null
        return {
          manifestDownloadProgress: {
            ...p,
            offlineCache: {
              ...(p.offlineCache || {}),
              completeCount: 0,
              fromCacheHits: 0,
              studyOffline: false,
              bytesCached: 0,
              targetCount: (p.offlineCache && p.offlineCache.targetCount) || p.totalTasks,
              studyInstanceUID:
                p.studyInstanceUID ||
                (p.offlineCache && p.offlineCache.studyInstanceUID) ||
                '',
            },
          },
        }
      })
    } catch (e) {
      console.warn('handleClearAllDicomCache', e)
    }
  }

  //#region Layout handler
  handleLayout = (event) => {
    const el = event && event.currentTarget ? event.currentTarget : null
    this.setState({ anchorElLayout: el })
  }

  /** Edge: FAB layout — abrir selector sin cambiar pin */
  handleEdgeLayoutOpen = (event) => {
    this.handleLayout(event)
  }

  /**
   * Edge: en el drawer, Layout con reborde activo si el FAB está pinneado.
   * Tocar activo → despinnea y oculta el FAB.
   * Tocar inactivo → pinnea, muestra FAB y abre selector.
   */
  handleEdgeLayoutToggle = (event) => {
    if (this.state.edgeLayoutPinned) {
      this.setState({ edgeLayoutPinned: false, anchorElLayout: null })
      return
    }
    this.setState({
      edgeLayoutPinned: true,
      anchorElLayout: event && event.currentTarget ? event.currentTarget : null,
    })
  }

  closeLayout = () => {
    this.setState({ anchorElLayout: null })
  }

  changeLayout = (row, col) => {
    console.log(`changeLayout called with row: ${row}, col: ${col}`);
    console.log('Current layout:', this.props.layout);

    // Conservar WW/WC + corte de usuario antes de destruir / reabrir viewports
    try {
      this.snapshotAllViewportsSession()
    } catch (eSnap) {
      /* ignore */
    }

    // Multi-panel: invalidar poll de studyUrl para no forzar 1x1 después de openimage
    if (row > 1 || col > 1) {
      this._studyLayoutPollGen = (this._studyLayoutPollGen || 0) + 1
      if (this._studyLayoutPollTimer) {
        clearTimeout(this._studyLayoutPollTimer)
        this._studyLayoutPollTimer = null
      }
    }
    
    // ✅ DETECTAR CAMBIO A LAYOUT 1x1 DESDE LAYOUT MÚLTIPLE
    const isChangingToSingleViewport = (row === 1 && col === 1)
    const wasMultipleViewports = (this.props.layout[0] > 1 || this.props.layout[1] > 1)
    
    let activeViewportData = null
    
    if (isChangingToSingleViewport && wasMultipleViewports) {
      // Click pin (_seriesLoadTargetIndex), NO hover: el mouse al ir al menú
      // Layout suele pasar por el otro viewport y corrompería la serie.
      const layoutNow = this.props.layout || [1, 1]
      let srcIdx = this._seriesLoadTargetIndex
      if (!this.isLayoutSlotIndex(srcIdx, layoutNow[0], layoutNow[1])) {
        srcIdx =
          typeof this.props.activeDcmIndex === 'number'
            ? this.props.activeDcmIndex
            : 0
      }
      if (!this.isLayoutSlotIndex(srcIdx, layoutNow[0], layoutNow[1])) {
        srcIdx = 0
      }

      const activeViewer =
        this.dicomViewersRefs && this.dicomViewersRefs[srcIdx]

      if (activeViewer && (activeViewer.files || activeViewer.image)) {
        const filesSnap = Array.isArray(activeViewer.files)
          ? activeViewer.files.slice()
          : null
        const sliceMax = Math.max(
          Number(activeViewer.sliceMax) || 0,
          (filesSnap && filesSnap.length) || 0,
          activeViewer.numberOfFrames > 1 ? activeViewer.numberOfFrames : 0,
          1
        )
        let sliceIndex =
          typeof activeViewer.sliceIndex === 'number'
            ? activeViewer.sliceIndex
            : 0
        if (sliceIndex < 0) sliceIndex = 0
        if (sliceIndex >= sliceMax) sliceIndex = Math.max(0, sliceMax - 1)

        let seriesUid = null
        try {
          seriesUid = this.resolveActiveSeriesUID(activeViewer, {
            localOnly: true,
          })
        } catch (eUid) {
          seriesUid = null
        }

        // VOI solo si el usuario ventaneó (si no → tags DICOM al reabrir)
        let voiSnap = null
        try {
          voiSnap = this.captureClinicalWwwcFromViewer(activeViewer)
          if (voiSnap) {
            this.rememberViewportWwwc(
              srcIdx,
              voiSnap.seriesUid,
              voiSnap.ww,
              voiSnap.wc
            )
            if (!seriesUid) seriesUid = voiSnap.seriesUid
          }
        } catch (eVoi) {
          voiSnap = null
        }

        try {
          this.rememberSliceFromViewer(activeViewer)
        } catch (eSl) {
          /* ignore */
        }

        activeViewportData = {
          sourceIndex: srcIdx,
          files: filesSnap,
          sliceIndex,
          sliceMax,
          explorerIndex: activeViewer.explorerIndex,
          measurements: activeViewer.measurements,
          toolState: activeViewer.toolState,
          numberOfFrames: activeViewer.numberOfFrames || 1,
          seriesUid,
          voi: voiSnap
            ? { ww: voiSnap.ww, wc: voiSnap.wc }
            : this.lookupWwwcForViewport(srcIdx, seriesUid),
        }

        // Evitar que soft-sync / Explorer pisen la serie durante la transición
        this._suppressSeriesSyncUntil = Date.now() + 1800
        this._layoutRestoreLockUntil = Date.now() + 1800
        this._layoutRestoreSeriesUid = seriesUid
      }

      try {
        if (ReferenceLinesManager.isUsingCornerstoneTools()) {
          ReferenceLinesManager.clearElements()
        }
      } catch (eRef) {
        /* ignore */
      }
      for (let i = 0; i < 16; i++) {
        if (this.dicomViewersRefs[i] && this.dicomViewersRefs[i].referenceLines) {
          this.dicomViewersRefs[i].referenceLines = {}
        }
      }
    }
    
    // if reduce the grid clear the unused views
    if (row < this.props.layout[0] || col < this.props.layout[1]) {
      const mprRelayout =
        this.state.mprMode ||
        this.state.visibleMprOrthogonal ||
        this.state.visibleMprSagittal ||
        this.state.visibleMprCoronal ||
        this.state.visibleMprAxial

      for(let i=0; i < 4; i++) {
        for(let j=0; j < 4; j++) {
          if ((i+1 > row) || (j+1 > col)) {
            const index = i*4+j
            
            if (this.dicomViewersRefs[index]) {
              if (mprRelayout) {
                // Soft clear: no runTool('clear') — destruye Cornerstone y deja
                // pantalla blanca al rotar MPR (p.ej. 2×1→1×2 libera el idx 4).
                try {
                  this.dicomViewersRefs[index].referenceLines = {}
                  this.dicomViewersRefs[index].isMprGenerated = false
                } catch (e) { /* ignore */ }
              } else {
              console.log(`App - Desactivando completamente viewport ${index}`)
              
              try {
                // 1. Ejecutar limpieza completa del viewport
                this.dicomViewersRefs[index].runTool('clear', null)
                
                // 2. Limpiar líneas de referencia específicamente
                if (this.dicomViewersRefs[index].referenceLines) {
                  this.dicomViewersRefs[index].referenceLines = {}
                }
                
                // 3. Remover del ReferenceLinesManager
                if (this.dicomViewersRefs[index].dicomImage) {
                  const ReferenceLinesManager = require('./managers/ReferenceLinesManager').default
                  if (ReferenceLinesManager && ReferenceLinesManager.getInstance) {
                    const manager = ReferenceLinesManager.getInstance()
                    manager.removeElement(this.dicomViewersRefs[index].dicomImage)
                  }
                }
                
                // 4. Limpiar canvas manualmente
                if (this.dicomViewersRefs[index].dicomImage) {
                  const canvas = this.dicomViewersRefs[index].dicomImage.querySelector('.cornerstone-canvas')
                  if (canvas) {
                    const ctx = canvas.getContext('2d')
                    ctx.clearRect(0, 0, canvas.width, canvas.height)
                  }
                }
                
                // 5. Limpiar eventos y referencias adicionales
                if (this.dicomViewersRefs[index].updateTimeout) {
                  clearTimeout(this.dicomViewersRefs[index].updateTimeout)
                }
                
                console.log(`App - Viewport ${index} desactivado completamente`)
                
              } catch (error) {
                console.error(`App - Error desactivando viewport ${index}:`, error)
              }
              }
            }
            
            // ✅ AHORA SÍ ELIMINAR LA REFERENCIA
            this.dicomViewersRefs[index] = undefined
            
            if (index === this.props.activeDcmIndex || index === this._navActiveIndex) {
              if (mprRelayout) {
                this._navActiveIndex = 0
                this._seriesLoadTargetIndex = 0
                try { this.props.setActiveDcmIndex(0) } catch (e) { /* ignore */ }
              } else if (isChangingToSingleViewport && activeViewportData) {
                // La restauración a 1×1 se encarga; no abrir el viewport 0 viejo
                this._navActiveIndex = 0
                this._seriesLoadTargetIndex = 0
                this._navSliceIndex = activeViewportData.sliceIndex
                try { this.props.setActiveDcmIndex(0) } catch (e) { /* ignore */ }
              } else {
                const newIndex = this.findFirstValidViewport(row, col)
                if (newIndex !== -1) {
                  this.layoutGridClick(newIndex)
                }
              }
            }
          }
        }
      }
      this.activeDcmViewersNum = this.getActiveDcmViewers()
      if (this.isMultipleFiles && this.activeDcmViewersNum === 1) {
        this.setState({mprMenu: true})
        setTimeout(() => this.refreshMprViability(), 300)
      }
    }
    
    console.log('Setting new layout to:', [row, col]);
    this.props.setLayoutStore(row, col)
    this.clampSeriesLoadTarget(row, col)
    // Al salir de multi (p.ej. 1×2 → 1×1): apagar Link Series (+ WW link)
    if (
      isChangingToSingleViewport &&
      wasMultipleViewports &&
      (this.state.visibleSeriesLink || this.state.seriesLinkWwwc)
    ) {
      this.setState({
        visibleSeriesLink: false,
        seriesLinkWwwc: false,
        seriesLinkKeys: null,
        seriesLinkOffsets: {},
      })
    }
    // No series-link sync al colapsar a 1×1 (no hay peers; evita thrash)
    if (this.isSeriesLinkEffective() && !(isChangingToSingleViewport && wasMultipleViewports)) {
      setTimeout(() => {
        try {
          this.scheduleSeriesLinkSync(this.props.activeDcmIndex)
        } catch (e) {
          /* ignore */
        }
      }, 220)
    }
    
    // Restaurar stack + corte del viewport clickeado en el 1×1 (índice 0)
    if (isChangingToSingleViewport && wasMultipleViewports && activeViewportData) {
      const restoreTo1x1 = (attempt) => {
        const newActiveIndex = 0
        const dest = this.dicomViewersRefs && this.dicomViewersRefs[newActiveIndex]
        if (!dest) {
          if (attempt < 12) {
            setTimeout(() => restoreTo1x1(attempt + 1), 40)
          }
          return
        }

        this._seriesLoadTargetIndex = newActiveIndex
        this._navActiveIndex = newActiveIndex
        this._navSliceIndex = activeViewportData.sliceIndex
        this._suppressSeriesSyncUntil = Date.now() + 1200
        try {
          this.props.setActiveDcmIndex(newActiveIndex)
        } catch (e) {
          /* ignore */
        }

        const sameSlot = activeViewportData.sourceIndex === newActiveIndex
        const sameSeriesAlready =
          sameSlot &&
          Array.isArray(dest.files) &&
          Array.isArray(activeViewportData.files) &&
          dest.files.length > 0 &&
          activeViewportData.files[0] &&
          dest.files[0] &&
          this.getFileFrameKey(dest.files[0]) ===
            this.getFileFrameKey(activeViewportData.files[0])
        const sliceAlreadyOnScreen =
          sameSeriesAlready &&
          dest.image &&
          typeof dest.sliceIndex === 'number' &&
          dest.sliceIndex === activeViewportData.sliceIndex

        // toolState + VOI ANTES de setfiles/openimage (buildViewportPreservingView)
        if (activeViewportData.toolState) {
          dest.toolState = activeViewportData.toolState
        }
        if (activeViewportData.seriesUid) {
          this.seedSeriesWwwcOnViewer(
            dest,
            activeViewportData.seriesUid,
            activeViewportData.voi,
            { viewportIndex: newActiveIndex, preserveLocal: false }
          )
          if (activeViewportData.voi) {
            this.rememberViewportWwwc(
              newActiveIndex,
              activeViewportData.seriesUid,
              activeViewportData.voi.ww,
              activeViewportData.voi.wc
            )
          }
        }

        // Si ya estábamos en el viewport 0 con esa serie: no hard setfiles (congela scroll)
        if (
          activeViewportData.files &&
          activeViewportData.files.length &&
          !sameSeriesAlready
        ) {
          dest.runTool('setfiles', {
            files: activeViewportData.files,
            remappedIndex: activeViewportData.sliceIndex,
          })
        } else if (sameSeriesAlready) {
          if (typeof dest.refreshScrollFlags === 'function') {
            try {
              dest.refreshScrollFlags()
            } catch (eScr) {
              /* ignore */
            }
          }
        }

        if (
          activeViewportData.explorerIndex !== undefined &&
          activeViewportData.explorerIndex !== null
        ) {
          dest.explorerIndex = activeViewportData.explorerIndex
          try {
            if (this.props.setExplorerActiveSeriesIndex) {
              this.props.setExplorerActiveSeriesIndex(
                activeViewportData.explorerIndex
              )
            }
          } catch (eExp) {
            /* ignore */
          }
        }
        if (activeViewportData.measurements) {
          try {
            this.props.setActiveMeasurements(activeViewportData.measurements)
          } catch (eM) {
            /* ignore */
          }
        }
        if (activeViewportData.numberOfFrames > 1) {
          dest.numberOfFrames = activeViewportData.numberOfFrames
          dest.sliceMax = Math.max(
            dest.sliceMax || 0,
            activeViewportData.sliceMax || activeViewportData.numberOfFrames
          )
        }

        this.files = activeViewportData.files || this.files
        this.setState({
          sliceMax: activeViewportData.sliceMax,
          sliceIndex: activeViewportData.sliceIndex,
        })

        if (
          !sliceAlreadyOnScreen &&
          typeof activeViewportData.sliceIndex === 'number'
        ) {
          this.handleOpenImage(activeViewportData.sliceIndex, newActiveIndex)
        }

        if (activeViewportData.seriesUid) {
          this.scheduleReapplySeriesWwwc(
            dest,
            activeViewportData.seriesUid,
            activeViewportData.voi,
            { viewportIndex: newActiveIndex, preserveLocal: false }
          )
        } else if (sameSeriesAlready && activeViewportData.voi) {
          try {
            if (typeof dest.applyClinicalVoi === 'function') {
              dest.applyClinicalVoi(
                activeViewportData.voi.ww,
                activeViewportData.voi.wc
              )
            }
          } catch (eVoiApply) {
            /* ignore */
          }
        }

        if (typeof dest.refreshScrollFlags === 'function') {
          try {
            dest.refreshScrollFlags()
          } catch (e2) {
            /* ignore */
          }
        }
        dest.shouldScroll =
          (activeViewportData.files && activeViewportData.files.length > 1) ||
          activeViewportData.numberOfFrames > 1

        try {
          this.props.setActiveDcm(dest)
        } catch (eDcm) {
          /* ignore */
        }
      }

      // Tras layout Redux: un frame basta; retry si el ref aún no está
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => restoreTo1x1(0))
      } else {
        setTimeout(() => restoreTo1x1(0), 16)
      }
    }

    // Multi-viewport + ref-lines: armar ya; en PREVIEW upgrade en background
    if ((row > 1 || col > 1) && this.state.visibleReferenceLines) {
      setTimeout(() => {
        try {
          this.finishPreviewReferenceLinesSetup()
        } catch (e) { /* ignore */ }
      }, 250)
    }
  }

  // Nuevo método para encontrar el primer viewport válido
  findFirstValidViewport = (row, col) => {
    for(let i=0; i < row; i++) {
      for(let j=0; j < col; j++) {
        const index = i*4+j
        if (this.dicomViewersRefs[index] !== undefined) {
          return index
        }
      }
    }
    return -1
  }

  //#endregion 

  //#region Tools panel handler
  handleToolsPanel = (event) => {
    this.setState({anchorElToolsPanel: event.currentTarget})
  }

  closeToolsPanel = () => {
    this.setState({anchorElToolsPanel: null})
  }

  handleMprMenuOpen = (event) => {
    const result = this.refreshMprViability()
    this.setState({
      anchorElMpr: event.currentTarget,
      mprViability: (result && result.viability) || this.state.mprViability,
      mprProfile: (result && result.profile) || this.state.mprProfile,
    })
  }

  handleMprMenuClose = () => {
    this.setState({ anchorElMpr: null })
  }

  handleMprMenuSelect = (mode) => {
    this.handleMprMenuClose()
    if (mode === 'exit') {
      this.exitMpr()
      return
    }
    this.beginMpr(mode)
  }

  toggleEdgeTools = () => {
    this.setState((prev) => ({ edgeToolsOpen: !prev.edgeToolsOpen }))
  }

  closeEdgeTools = () => {
    this.setState({ edgeToolsOpen: false })
  }

  hideTopToolbar = () => {
    this.setState({ visibleToolbar: false })
  }

  showTopToolbar = () => {
    this.setState({ visibleToolbar: true })
  }

  getToolbarLayout = () =>
    resolveToolbarLayout(this.state.pacsIdFromUrl, this.state.toolbarLayoutFromUrl)

  /**
   * Studio Rail (desktop ≥1024): STUDIO, o EDGE legacy en desktop.
   * Classic / OHIF / Oviyam no entran aquí.
   */
  useStudioToolbarShell = () => {
    if (this.state.isMobile) return false
    const layout = this.getToolbarLayout()
    return (
      layout === TOOLBAR_LAYOUTS.STUDIO || layout === TOOLBAR_LAYOUTS.EDGE
    )
  }

  /**
   * OHIF canvas-first (desktop ≥1024).
   */
  useOhifToolbarShell = () => {
    if (this.state.isMobile) return false
    return this.getToolbarLayout() === TOOLBAR_LAYOUTS.OHIF
  }

  /**
   * OviyamNG paneles fijos (desktop ≥1024). En móvil cae a Classic.
   */
  useOviyamToolbarShell = () => {
    if (this.state.isMobile) return false
    const layout = this.getToolbarLayout()
    return (
      layout === TOOLBAR_LAYOUTS.OVIYAM_NG || layout === TOOLBAR_LAYOUTS.OVIYAM
    )
  }

  /**
   * Edge compact (móvil): EDGE, STUDIO u OHIF en viewport < 1024.
   * Classic y OviyamNG siguen AppBar también en móvil.
   */
  useEdgeToolbarShell = () => {
    if (!this.state.isMobile) return false
    const layout = this.getToolbarLayout()
    return (
      layout === TOOLBAR_LAYOUTS.EDGE ||
      layout === TOOLBAR_LAYOUTS.STUDIO ||
      layout === TOOLBAR_LAYOUTS.OHIF
    )
  }

  toggleStudioLeftRail = () => {
    this.setState((prev) => ({
      studioLeftCollapsed: !prev.studioLeftCollapsed,
    }))
  }

  toggleStudioRightRail = () => {
    this.setState((prev) => ({
      studioRightCollapsed: !prev.studioRightCollapsed,
    }))
  }

  toggleOhifDrawer = () => {
    this.setState((prev) => ({
      ohifDrawerCollapsed: !prev.ohifDrawerCollapsed,
    }))
  }

  toggleOviyamSidebar = () => {
    this.setState((prev) => ({
      oviyamSidebarCollapsed: !prev.oviyamSidebarCollapsed,
    }))
  }

  toggleOviyamToolsRail = () => {
    this.setState((prev) => ({
      oviyamToolsCollapsed: !prev.oviyamToolsCollapsed,
    }))
  }

  /**
   * Props compartidas Studio/Oviyam ToolsRail (portalChrome + plan).
   * @param {object} ctx locals de render() + onExplorer
   */
  buildChromeToolsRailProps = (ctx) => {
    const {
      anyOpen,
      showCineBtn,
      cineOpen,
      showDsa,
      dsaUi,
      dsaFrameCount,
      showOrthancBtn,
      showClinicalAuth,
      showPresets,
      canInstallPwa,
      showRenderModeUi,
      visibleReferenceLines,
      visibleSeriesLink,
      visibleToolbox,
      mprAllowed,
      mprOrthogonalAllowed,
      mprActive,
      mprViability,
      mprProfile,
      mprSlabAllowed,
      mpr3dAllowed,
      mpr3dProfile,
      visibleMpr3D,
      visibleMprOrthogonal,
      visibleMprCoronal,
      visibleMprSagittal,
      visibleMprAxial,
      chromeOnMpr,
      chromeOnMpr3d,
      planAllowsPet,
      planAllowsCopilot,
      planAllowsVoice,
      portalChrome,
      isPortalPlan,
      gsdfQaPassed,
    } = ctx
    return {
              toolActive: this.state.toolActive,
              toolExecute: this.toolExecute,
              referenceLines: visibleReferenceLines,
              hideReferenceLinesOnActive: !!this.state.hideReferenceLinesOnActiveViewport,
              seriesLink: visibleSeriesLink,
              seriesLinkWwwc: !!this.state.seriesLinkWwwc,
              anyOpen,
              showCine: !!showCineBtn,
              cineActive: !!cineOpen,
              showDsa: !!showDsa,
              dsaEnabled: !!(dsaUi && dsaUi.enabled),
              dsaMaskMode: dsaUi && dsaUi.maskMode,
              dsaFrameCount,
              dsaPixelShift: dsaUi && dsaUi.pixelShift,
              dsaInvert: !!(dsaUi && dsaUi.invertDSA),
              dsaGain: dsaUi && dsaUi.gain,
              dsaOffset: dsaUi && dsaUi.offset,
              dsaNoiseFloor: dsaUi && dsaUi.noiseFloor,
              dsaEngineMode: (dsaUi && dsaUi.engineMode) || 'auto',
              dsaSynthMode: (dsaUi && dsaUi.synthMode) || 'minip',
              dsaRoadmap: dsaUi && dsaUi.roadmap,
              onDsaSelect: this.handleDsaMenuSelect,
              showOrthanc: !!showOrthancBtn,
              onOrthanc: () =>
                this.setState({ visibleOrthancConnection: true }),
              showGateway: showClinicalAuth,
              gatewayConnected: !!this.state.gatewayConnected,
              gatewayStatus: this.state.gatewayStatus || 'local_only',
              gatewayCompatOk: this.state.gatewayCompatOk !== false,
              onGateway: showClinicalAuth ? this.showAuthGatewayModal : null,
              onGatewayOpen: showClinicalAuth ? this.onGatewayOpen : null,
              showLocalStudies: true,
              onLocalStudies: this.showLocalStudies,
              showRelatedStudies:
                Array.isArray(this.state.relatedStudies) &&
                this.state.relatedStudies.length > 0,
              onRelatedStudies: this.showRelatedStudies,
              showWorklist: this.hasWorklistUi(),
              onWorklist: this.showWorklist,
              compareMode: !!this.state.compareMode,
              compareModeEnabled: this.canEnableCompareMode(),
              onToggleCompareMode: this.toggleCompareMode,
              isFullscreen: this.state.isFullscreen,
              showPresets: !!showPresets,
              currentModality: this.getCurrentModality(),
              onPresetSelect: this.handleWWPresetSelect,
              onLayout: this.handleLayout,
              onLayoutOpen: this.handleEdgeLayoutOpen,
              onLayoutToggle: this.handleEdgeLayoutToggle,
              layoutPinned: !!this.state.edgeLayoutPinned,
              onFullscreen: this.toggleFullscreen,
              onPwaExit: this.handlePwaExit,
              pwaStandalone: isPwaStandalone(),
              onInstallPwa: canInstallPwa ? this.handleInstallApp : null,
              swUpdatePending:
                !!this.state.swUpdatePending ||
                (this.state.gatewayNotices || []).length > 0,
              onOpenSwUpdate: this.openNotificationCenter,
              onReset: this.resetImage,
              onExplorer: ctx.onExplorer,
              onCine: this.cinePlayer,
              loadProgress: this.state.manifestDownloadProgress,
              loadSource: this.state.studyLoadSource,
              onCancelStudyLoad: () =>
                this.cancelStudyLoad({ userInitiated: true }),
              onClearStudyCache: this.handleClearStudyDicomCache,
              onClearAllCache: this.handleClearAllDicomCache,
              renderMode: this.state.renderMode,
              onToggleRenderMode: showRenderModeUi ? this.toggleRenderMode : null,
              onSetRenderMode: showRenderModeUi ? this.setRenderMode : null,
              onDiagnosticToolBlocked: this.showDiagnosticModeHint,
              studyDownloadUrl: this.state.studyDownloadUrl,
              localStudyZipEligible: !!(
                this.state.localStudyZip && this.state.localStudyZip.eligible
              ),
              localStudyZipReady: !!(
                this.state.localStudyZip && this.state.localStudyZip.ready
              ),
              studyZipDownloadTitle: this.getStudyZipDownloadTitle(),
              studyZipDownloadBusy: !!this.state.studyZipDownloadBusy,
              onDownloadStudy: this.handleDownloadStudyZip,
              mprAllowed,
              mprOrthogonalAllowed,
              mprActive,
              mprMessage:
                (mprViability && mprViability.message) ||
                (mprProfile === 'blocked'
                  ? mprProfileLabel('blocked')
                  : ''),
              visibleMprOrthogonal,
              visibleMprCoronal,
              visibleMprSagittal,
              visibleMprAxial,
              onMpr: chromeOnMpr,
              onMprExit: this.exitMpr,
              mprCrosshairActive: !!this.state.mprCrosshairActive,
              onMprCrosshairToggle: this.toggleMprCrosshair,
              mprSlabMode: this.state.mprSlabMode || 'thin',
              mprSlabMm: Number(this.state.mprSlabMm) || 0,
              onMprSlabMode: mprSlabAllowed ? this.setMprSlabMode : null,
              onMprSlabMm: mprSlabAllowed ? this.setMprSlabMm : null,
              petFusionOn: !!this.state.petFusionOn,
              petFusionOpacity: Math.round(
                (this.state.petFusionOpacity || 0) * 100
              ),
              petFusionClut: this.state.petFusionClut || 'hotMetal',
              petFusionWindowMax:
                Number(this.state.petFusionWindowMax) || 5,
              petFusionAvailable:
                planAllowsPet &&
                listPetSeriesFromStore(this.props.series).length > 0,
              onPetFusionToggle: this.togglePetFusion,
              onPetFusionOpacity: this.setPetFusionOpacity,
              onPetFusionClut: this.setPetFusionClut,
              onPetFusionWindowMax: this.setPetFusionWindowMax,
              gspsAvailable:
                listPrSeriesFromStore(this.props.series).length > 0,
              gspsOn: !!this.state.gspsOn,
              onGspsToggle: this.toggleGspsOverlay,
              onGspsExport: this.exportActiveGsps,
              onSrExport: this.exportActiveSr,
              onAttachPdf: this.openAttachPdfPicker,
              mpr3dAllowed,
              visibleMpr3D: !!visibleMpr3D,
              mpr3dDecapado: !!this.state.mpr3dDecapado,
              mpr3dMessage: mpr3dProfileLabel(mpr3dProfile),
              onMpr3d: chromeOnMpr3d,
              onMpr3dDecapado: this.toggleMpr3dDecapado,
              scoutAvailable: !!findScoutSeriesInStore(this.props.series),
              scoutSyncEnabled: !!this.state.scoutSyncEnabled,
              scoutPanelOpen: !!this.state.scoutPanelOpen,
              onScoutSyncToggle: this.toggleScoutSync,
              onScoutPanelToggle: this.toggleScoutPanel,
              doseReport: this.state.doseReport,
              onDoseInfo: this.openDoseInfo,
              headerOpen: !!this.state.visibleHeader,
              onHeader: this.toggleHeader,
              histogramOpen: !!visibleToolbox,
              onHistogram: this.toggleToolbox,
              onGsdfQa: this.showGsdfQa,
              gsdfQaPassed,
              onCaptureViewport: this.saveShot,
              onShareViewport: this.openShareSheet,
              onCopilotOpen: planAllowsCopilot ? this.openCopilotPanel : null,
              voiceNoteRecording: !!this.state.voiceNoteRecording,
              voiceNoteBusy: !!this.state.voiceNoteBusy,
              voiceNoteHoldToTalk: false,
              onVoiceNoteStart: planAllowsVoice ? this.startVoiceNote : null,
              onVoiceNoteStop: planAllowsVoice ? this.stopVoiceNote : null,
              onVoiceNoteToggle: planAllowsVoice ? this.toggleVoiceNote : null,
              onVoiceNoteOpenPanel: planAllowsVoice
                ? this.openVoiceNotesPanel
                : null,
              portalChrome,
              showPortalDisclaimerRail: isPortalPlan,
    }
  }


  handleOhifAdvancedAction = (action) => {
    switch (action) {
      case 'mpr':
        if (typeof this.handleMprMenuOpen === 'function') {
          this.handleMprMenuOpen({ currentTarget: document.body })
        }
        break
      case 'cine':
        this.cinePlayer()
        break
      case 'capture':
        this.saveShot()
        break
      case 'share':
        this.openShareSheet()
        break
      case 'attachPdf':
        this.openAttachPdfPicker()
        break
      case 'header':
        this.toggleHeader()
        break
      case 'reset':
        this.resetImage()
        break
      case 'localStudies':
        this.showLocalStudies()
        break
      case 'relatedStudies':
        this.showRelatedStudies()
        break
      case 'worklist':
        this.showWorklist()
        break
      default:
        break
    }
  }

  getStudioTitleParts = (dcmViewer) => {
    try {
      const files = Array.isArray(this.props.files) ? this.props.files : []
      const f = files.find((x) => x && x.patient) || files[0]
      const filePatient = f && f.patient && f.patient.patientName
      const patient =
        (!isPlaceholderPatientName(filePatient) ? filePatient : '') ||
        (dcmViewer && dcmViewer.patientName) ||
        ''
      const modality =
        (f && f.series && f.series.modality) ||
        (dcmViewer && dcmViewer.modality) ||
        ''
      const date =
        (f && f.study && f.study.studyDate) ||
        ''
      let dateLabel = ''
      if (date && String(date).length >= 8) {
        const s = String(date)
        dateLabel = `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
      }
      const title = patient ? String(patient).trim() : ''
      const subtitle = [modality, dateLabel].filter(Boolean).join(' · ')
      const patientId =
        (f && f.patient && (f.patient.patientId || f.patient.patientID)) ||
        (dcmViewer && (dcmViewer.patientId || dcmViewer.patientID)) ||
        ''
      return { title, subtitle, patientId: patientId ? String(patientId) : '' }
    } catch (e) {
      return { title: '', subtitle: '', patientId: '' }
    }
  }

  /** explorer chrome: muesca even if isOpen is late */
  shouldShowEdgeExplorerChrome = () => {
    if (!this.useEdgeToolbarShell()) return false
    const anyOpen = Array.isArray(this.props.isOpen) && this.props.isOpen.some(Boolean)
    const hasExplorerSeries =
      this.explorer &&
      this.explorer.series &&
      Array.isArray(this.explorer.series.seriesKeys) &&
      this.explorer.series.seriesKeys.length > 0
    return anyOpen || hasExplorerSeries || !!this.state.visibleExplorer
  }

  /**
   * Inset inferior (px) para no solapar la pestaña/panel del explorer ni el chrome del browser.
   * Misma métrica para padding del layout y para el Cine Player (position:fixed).
   */
  getBottomChromeInset = () => {
    const explorerBarHeight = 110
    const explorerPillHeight = 28
    const isFs = !!(
      typeof document !== 'undefined' &&
      (document.fullscreenElement || document.webkitFullscreenElement)
    )
    // Fuera de fullscreen el browser/OS deja menos margen usable
    const browserExtra = isFs || this.state.isFullscreen ? 0 : 12
    const viewportOverlay = isFs || this.state.isFullscreen ? 0 : this.getViewportBottomOverlay()
    const dynamicExtra = Math.max(browserExtra, viewportOverlay)

    if (this.useEdgeToolbarShell() && this.shouldShowEdgeExplorerChrome()) {
      if (this.state.visibleExplorer) {
        // Panel de miniaturas + holgura respecto a la pastilla
        return explorerBarHeight + Math.round(explorerPillHeight * 0.55) + dynamicExtra
      }
      // Solo pastilla asomando (~28px) + margen al contador del cine
      return explorerPillHeight + 24 + dynamicExtra
    }
    if (this.state.isMobile && this.state.visibleExplorer) {
      return 80 + dynamicExtra
    }
    return 8 + dynamicExtra
  }

  /**
   * Alto (px) del chrome inferior que puede superponerse a elementos fixed
   * cuando NO estamos en fullscreen (barra inferior del browser / gestos SO).
   */
  getViewportBottomOverlay = () => {
    if (typeof window === 'undefined') return 0
    const vv = window.visualViewport
    if (!vv) return 0
    const layoutH = Math.max(
      window.innerHeight || 0,
      (typeof document !== 'undefined' && document.documentElement
        ? document.documentElement.clientHeight
        : 0)
    )
    const overlap = layoutH - (vv.height + vv.offsetTop)
    if (!Number.isFinite(overlap) || overlap <= 2) return 0
    // Margen de seguridad para que la pastilla no quede “tocando” el chrome.
    return Math.max(0, Math.min(64, Math.round(overlap + 8)))
  }

  scheduleViewportChromeSync = () => {
    if (this._viewportChromeRaf) return
    this._viewportChromeRaf = requestAnimationFrame(() => {
      this._viewportChromeRaf = null
      this.syncCineBottomCssVar()
      this.forceUpdate()
    })
  }

  /** CSS var para anclar el overlay de cine por encima del chrome inferior */
  syncCineBottomCssVar = () => {
    if (typeof document === 'undefined' || !document.documentElement) return
    const inset = this.getBottomChromeInset()
    document.documentElement.style.setProperty('--udv-cine-bottom', `${inset}px`)
  }

  renderEdgeExplorerChrome = () => {
    if (typeof document === 'undefined' || !document.body) return null
    if (!this.shouldShowEdgeExplorerChrome()) return null

    const open = !!this.state.visibleExplorer
    // Pastilla compacta (no franja a lo ancho); no resta altura a miniaturas
    const pillH = 28
    const pillW = 72
    const barH = 110
    const accent = '#64B5F6'
    // Misma curva típica de Material-UI Drawer (~225–250ms ease)
    const slideEase = 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
    const pillBase = {
      border: 'none',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      color: accent,
      background: 'rgba(28, 28, 28, 0.98)',
      boxSizing: 'border-box',
      width: pillW,
      minWidth: pillW,
      height: pillH,
      borderRadius: '12px 12px 0 0',
      borderTop: `2px solid ${accent}`,
      borderLeft: '1px solid #444',
      borderRight: '1px solid #444',
      boxShadow: '0 -2px 10px rgba(0,0,0,0.4)',
      padding: '2px 8px 4px',
      touchAction: 'manipulation',
    }

    // Un solo shell: cerrado = translateY(barH) deja solo la pastilla visible;
    // abierto = translateY(0). Mismo principio que el drawer de tools.
    return createPortal(
      <div
        data-udv-explorer-edge={open ? 'open' : 'closed'}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: this.state.isFullscreen ? 0 : this.getViewportBottomOverlay(),
          width: '100%',
          height: barH,
          paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
          zIndex: 200000,
          background: '#181818',
          borderTop: '1px solid #333',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.4)',
          boxSizing: 'border-box',
          overflow: 'visible',
          // Cerrado: el panel baja y solo asoma la pastilla (arriba, top: -pillH)
          transform: open ? 'translateY(0)' : `translateY(${barH}px)`,
          transition: slideEase,
          willChange: 'transform',
          // Sin pointer-events en el área del panel cuando está abajo (evita capturar tocques del viewer)
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <button
          type="button"
          data-udv-explorer-notch={open ? 'close' : 'open'}
          onClick={this.toggleExplorer}
          aria-label={open ? 'Ocultar series' : 'Mostrar series'}
          aria-expanded={open}
          style={{
            ...pillBase,
            position: 'absolute',
            top: -pillH + 2,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            pointerEvents: 'auto',
          }}
        >
          <span style={{ width: 28, height: 3, borderRadius: 2, background: accent }} />
          <span style={{ fontSize: 12, lineHeight: 1, fontWeight: 800 }}>
            {open ? '▼' : '▲'}
          </span>
        </button>
        <div
          className="explorer-container"
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            // Cuando está cerrado (no fullscreen en móvil), algunas UIs dejan
            // “líneas” de los bordes/frames de miniaturas visibles por sub-pixels.
            // Colapsamos el alto para que no haya contenido asomando.
            height: open ? '100%' : '0px',
            overflow: 'hidden',
            background: '#181818',
          }}
        >
          {open && (
            <Explorer
              explorer={this.explorer}
              onSelectSeries={this.explorerOnSelectSeries}
              color={iconColor}
              isMobile={true}
              embedded={true}
              autoSelectFirstSeries={this.autoSelectFirstSeries}
              onAutoShowExplorer={() => {
                if (!this.hasAutoOpenedExplorer) {
                  this.setState({ visibleExplorer: true })
                  this.hasAutoOpenedExplorer = true
                }
              }}
              {...this.getExplorerPinProps()}
            />
          )}
        </div>
      </div>,
      document.body
    )
  }
  //#endregion

  toolExecute = (tool) => {
    this.closeToolsPanel()
    // Cerrar panel edge solo al elegir tool de modo (no toggles multi-viewport)
    if (tool !== 'referencelines' && tool !== 'referencelinesactivehide' && tool !== 'serieslink' && tool !== 'serieslinkwwwc') {
      this.closeEdgeTools()
    }
    if (tool === 'referencelines') {
      this.referenceLinesToggle()
    } else if (tool === 'referencelinesactivehide') {
      this.hideReferenceLinesOnActiveToggle()
    } else if (tool === 'serieslink') {
      this.seriesLinkToggle()
    } else if (tool === 'serieslinkwwwc') {
      this.seriesLinkWwwcToggle()
    } else {
      if (this.isPortalProfileSession() && isPixelAccurateTool(tool)) {
        return
      }
      // PREVIEW + medición: upgrade del frame activo a DICOM y luego activar tool
      if (
        isPixelAccurateTool(tool) &&
        this.state.renderMode === RENDER_MODE.PREVIEW
      ) {
        void this.ensureActiveFrameDiagnostic({ pendingTool: tool })
        return
      }
      this.activateViewerTool(tool)
    }
  }

  /**
   * Activa tool en el viewer activo + estado UI.
   * @param {string} tool
   */
  activateViewerTool = (tool) => {
    // Invertir: toggle puntual. Captura: el usuario elige destino (diálogo).
    if (tool === 'Invert') {
      const v =
        this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
      if (v) {
        try {
          v.runTool('Invert')
        } catch (e) {
          /* ignore */
        }
      }
      return
    }
    if (tool === 'saveas') {
      this.saveShot()
      return
    }
    if (tool === 'share') {
      this.openShareSheet()
      return
    }
    const patch = { toolActive: tool }
    const turningOffCrosshair =
      tool !== 'mprCrosshair' && this.state.mprCrosshairActive
    if (turningOffCrosshair) {
      patch.mprCrosshairActive = false
      patch.mprCrosshairPoint = null
      patch.mprCrosshairPlane = null
      patch.mpr3dMark = null
    }
    this.setState(patch)
    this.props.toolStore(tool)
    if (turningOffCrosshair) {
      this.releaseAllCrosshairCaptures()
    }
    const v = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (v) {
      try {
        if (typeof v.ensureToolsRegistered === 'function') {
          v.ensureToolsRegistered({ activateScroll: false })
        }
      } catch (e) {
        /* ignore */
      }
      if (tool !== 'mprCrosshair') {
        v.runTool(tool)
      }
    }
  }

  releaseAllCrosshairCaptures = () => {
    const refs = this.dicomViewersRefs || []
    for (let i = 0; i < refs.length; i++) {
      const v = refs[i]
      if (v && typeof v.releaseCrosshairCapture === 'function') {
        try {
          v.releaseCrosshairCapture()
        } catch (e) {
          /* ignore */
        }
      }
    }
  }

  /**
   * Re-activa tool tras paint del upgrade PREVIEW→DICOM (forceFull async finish).
   * @param {string} tool
   * @param {object} [viewer]
   */
  activatePendingToolAfterUpgrade = (tool, viewer) => {
    if (!tool) return
    const v =
      viewer ||
      (this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex])
    const fire = () => {
      try {
        if (v && typeof v.ensureToolsRegistered === 'function') {
          v.ensureToolsRegistered({ activateScroll: false })
        }
      } catch (e) {
        /* ignore */
      }
      try {
        this.activateViewerTool(tool)
      } catch (e2) {
        /* ignore */
      }
    }
    // paint síncrono + rAF + reintentos (displayImage finishAfterPaint es diferido)
    fire()
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        fire()
        setTimeout(fire, 80)
        setTimeout(fire, 250)
      })
    } else {
      setTimeout(fire, 60)
      setTimeout(fire, 200)
    }
  }

  /**
   * Index de corte del viewer activo (nav + viewer + state).
   */
  getActiveSliceIndex = () => {
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (this._navSliceIndex != null && !Number.isNaN(this._navSliceIndex)) {
      return this._navSliceIndex
    }
    if (viewer && viewer.sliceIndex != null) return viewer.sliceIndex
    return this.state.sliceIndex || 0
  }

  /**
   * Soft-merge item actualizado en viewer stack + Redux files.
   * @param {object} viewer
   * @param {number} index
   * @param {object} patched
   */
  softMergeUpgradedFile = (viewer, index, patched) => {
    if (!viewer || !patched) return
    if (Array.isArray(viewer.files) && index >= 0 && index < viewer.files.length) {
      const nextViewerFiles = viewer.files.slice()
      nextViewerFiles[index] = patched
      viewer.files = nextViewerFiles
      try {
        viewer.runTool('setfiles', {
          files: nextViewerFiles,
          soft: true,
          remappedIndex: index,
          keepKey: this.getFileFrameKey
            ? this.getFileFrameKey(patched)
            : patched.imageId,
        })
      } catch (e) {
        /* ya mutamos viewer.files */
      }
    }
    if (Array.isArray(this.files) && this.files.length > 0) {
      const nextLocal = this.files.slice()
      const sop = patched.sopInstanceUID
      let localIdx = index
      if (sop) {
        const bySop = nextLocal.findIndex(
          (f) => f && f.sopInstanceUID && String(f.sopInstanceUID) === String(sop)
        )
        if (bySop >= 0) localIdx = bySop
      }
      if (localIdx >= 0 && localIdx < nextLocal.length) {
        nextLocal[localIdx] = { ...nextLocal[localIdx], ...patched }
        this.files = nextLocal
      }
    }
    if (Array.isArray(this.props.files) && typeof this.props.setFilesStore === 'function') {
      const sop = patched.sopInstanceUID
      let matched = false
      const nextStore = this.props.files.map((f) => {
        if (!f) return f
        if (sop && f.sopInstanceUID && String(f.sopInstanceUID) === String(sop)) {
          matched = true
          return { ...f, ...patched }
        }
        return f
      })
      if (matched) this.props.setFilesStore(nextStore)
    }
  }

  /**
   * assetBaseUrl del último manifest (RANGE / DIRECT).
   * @returns {string|null}
   */
  getUpgradeAssetBaseUrl = () => {
    try {
      const h = this._lastManifestHandlers
      if (h && h.assetBaseUrl) return h.assetBaseUrl
    } catch (e) {
      /* ignore */
    }
    return null
  }

  /**
   * Descarga .dcm de un frame de un viewer y lo mergea in-place (sin cambiar renderMode).
   * Ante QuotaExceeded IDB: libera cuota y reintenta en modo memoria (sin escribir IDB).
   * @param {object} viewer
   * @param {number} index
   * @param {{ silent?: boolean, redisplay?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  ensureFrameDiagnostic = async (viewer, index, opts = {}) => {
    const silent = !!opts.silent
    const redisplay = opts.redisplay !== false
    if (!viewer) return false
    const files = Array.isArray(viewer.files)
      ? viewer.files
      : Array.isArray(this.files)
        ? this.files
        : []
    const fileData = files[index]
    if (!fileData) return false

    if (!frameNeedsDicomUpgrade(fileData)) {
      return true
    }

    const sopKey =
      (fileData.sopInstanceUID && String(fileData.sopInstanceUID)) ||
      fileData.dicomUrl ||
      fileData.archiveUrl ||
      `idx:${viewer.props && viewer.props.index}:${index}`

    if (!this._frameUpgradeInFlightMap) {
      this._frameUpgradeInFlightMap = new Set()
    }
    if (this._frameUpgradeInFlightMap.has(sopKey)) {
      return false
    }
    this._frameUpgradeInFlight = sopKey
    this._frameUpgradeInFlightMap.add(sopKey)

    if (!silent) {
      this.setState({ renderModeToast: DIAGNOSTIC_TOOLS_TOOLTIP })
    }

    const studyUid =
      (fileData.study && fileData.study.studyId) ||
      this.getCurrentStudyUid() ||
      null
    const baseOpts = {
      studyInstanceUID: studyUid,
      assetBaseUrl: this.getUpgradeAssetBaseUrl(),
      memoryOnly: true,
      skipIdbWrite: true,
    }

    const runUpgrade = async (extra) =>
      upgradePreviewFrameToDicom(fileData, { ...baseOpts, ...(extra || {}) })

    const applyPatched = (patched) => {
      this.softMergeUpgradedFile(viewer, index, patched)
      if (!redisplay) return
      try {
        if (typeof viewer.displayImageFromFiles === 'function') {
          viewer.displayImageFromFiles(index)
        } else {
          viewer.runTool('openimage', index)
        }
      } catch (e) {
        try {
          viewer.runTool('openimage', index)
        } catch (e2) {
          /* ignore */
        }
      }
    }

    try {
      let patched
      try {
        patched = await runUpgrade({ skipIdbRead: false })
      } catch (err) {
        if (isQuotaLikeError(err)) {
          console.warn(
            'ensureFrameDiagnostic: cuota IDB, liberando y reintentando en RAM',
            err && err.message
          )
          try {
            clearWriteQueue()
            await recoverFromQuotaPressure()
          } catch (e2) {
            /* ignore */
          }
          try {
            if (
              typeof cornerstone !== 'undefined' &&
              cornerstone.imageCache &&
              typeof cornerstone.imageCache.purgeCache === 'function'
            ) {
              cornerstone.imageCache.purgeCache()
            }
          } catch (e3) {
            /* ignore */
          }
          // Reintento: sin leer ni escribir IDB
          patched = await runUpgrade({
            skipIdbRead: true,
            skipIdbWrite: true,
            memoryOnly: true,
          })
          try {
            resumeWriteQueue()
          } catch (e4) {
            /* ignore */
          }
        } else {
          throw err
        }
      }

      if (!patched || !patched.image) {
        throw new Error('Upgrade sin imagen decodificada')
      }

      applyPatched(patched)
      return true
    } catch (err) {
      console.warn('ensureFrameDiagnostic:', err)
      if (!silent) {
        const msg = isQuotaLikeError(err)
          ? 'Almacenamiento del navegador lleno. Se liberó caché; reintente la medición.'
          : (err && err.message) || PIXEL_ACCURATE_NO_DICOM_TOOLTIP
        this.setState({
          renderModeToast: null,
          titleMessage: 'Medición',
          textMessage: msg,
          visibleMessage: true,
        })
      }
      return false
    } finally {
      if (this._frameUpgradeInFlightMap) {
        this._frameUpgradeInFlightMap.delete(sopKey)
      }
      if (this._frameUpgradeInFlight === sopKey) {
        this._frameUpgradeInFlight = null
      }
      if (!silent) {
        this.setState((prev) =>
          prev.renderModeToast === DIAGNOSTIC_TOOLS_TOOLTIP
            ? { renderModeToast: null }
            : null
        )
      }
    }
  }

  /**
   * En PREVIEW, descarga .dcm del SOP del frame activo y conmuta ese slot a dicom.
   * Prefetch ±1 solo tras éxito del frame activo.
   * @param {{ pendingTool?: string|null, silent?: boolean }} [opts]
   */
  ensureActiveFrameDiagnostic = async (opts = {}) => {
    const pendingTool = opts.pendingTool || null
    const silent = !!opts.silent
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer) {
      if (pendingTool) this.activateViewerTool(pendingTool)
      return false
    }

    const index = this.getActiveSliceIndex()
    const files = Array.isArray(viewer.files)
      ? viewer.files
      : Array.isArray(this.files)
        ? this.files
        : []
    const fileData = files[index]
    if (!fileData) {
      if (pendingTool) this.activateViewerTool(pendingTool)
      return false
    }

    if (!frameNeedsDicomUpgrade(fileData)) {
      if (pendingTool) this.activateViewerTool(pendingTool)
      if (pendingTool && isPixelAccurateTool(pendingTool)) {
        this.prefetchNeighborFramesDiagnostic(viewer, index)
      }
      return true
    }

    try {
      const ok = await this.ensureFrameDiagnostic(viewer, index, {
        silent,
        redisplay: true,
      })

      const currentIdx = this.getActiveSliceIndex()
      if (currentIdx !== index) {
        return false
      }

      if (ok && pendingTool) {
        this.activatePendingToolAfterUpgrade(pendingTool, viewer)
      }

      if (ok) {
        this.prefetchNeighborFramesDiagnostic(viewer, index)
      }
      return ok
    } catch (err) {
      console.warn('ensureActiveFrameDiagnostic:', err)
      return false
    }
  }

  /**
   * Prefetch ligero ±1 SOP (background, silent).
   * @param {object} viewer
   * @param {number} index
   */
  prefetchNeighborFramesDiagnostic = (viewer, index, opts = {}) => {
    if (!viewer || !Array.isArray(viewer.files)) return
    const radius =
      opts.radius != null
        ? Math.max(0, Number(opts.radius))
        : DIAG_PREFETCH_RADIUS
    for (let d = 1; d <= radius; d++) {
      for (let n = 0; n < 2; n++) {
        const i = n === 0 ? index - d : index + d
        if (i < 0 || i >= viewer.files.length) continue
        const f = viewer.files[i]
        if (!f || !frameNeedsDicomUpgrade(f)) continue
        void this.ensureFrameDiagnostic(viewer, i, {
          silent: true,
          redisplay: false,
        })
      }
    }
  }

  isPortalProfileSession = () =>
    isPortalProfile({
      plan: this.state.udvPlan,
      persona: this.state.udvPersona,
    })

  /**
   * Upgrade frames visibles de todos los viewports activos (ref-lines / multi-view).
   * @param {{ silent?: boolean }} [opts]
   * @returns {Promise<boolean>}
   */
  ensureActiveViewportsDiagnostic = async (opts = {}) => {
    const silent = opts.silent !== false
    const viewers = this.dicomViewersActive || []
    if (!viewers.length) return false
    if (!silent) {
      this.setState({ renderModeToast: DIAGNOSTIC_TOOLS_TOOLTIP })
    }
    try {
      const tasks = []
      for (let i = 0; i < viewers.length; i++) {
        const v = viewers[i]
        if (!v) continue
        let idx =
          v.sliceIndex != null
            ? v.sliceIndex
            : this.getActiveSliceIndex()
        if (v === this.dicomViewersRefs[this.props.activeDcmIndex]) {
          idx = this.getActiveSliceIndex()
        }
        tasks.push(
          this.ensureFrameDiagnostic(v, idx, {
            silent: true,
            redisplay: true,
          })
        )
      }
      const results = await Promise.all(tasks)
      return results.some(Boolean) || results.every((r) => r === true)
    } finally {
      if (!silent) {
        this.setState((prev) =>
          prev.renderModeToast === DIAGNOSTIC_TOOLS_TOOLTIP
            ? { renderModeToast: null }
            : null
        )
      }
    }
  }

  /**
   * Si hay tool de medición activa en PREVIEW, asegurar dicom del frame actual.
   * También con líneas de referencia visibles en multi-viewport.
   */
  maybeUpgradeActiveFrameForPixelTool = () => {
    if (this.state.renderMode !== RENDER_MODE.PREVIEW) return
    const tool = this.state.toolActive || this.props.tool
    if (isPixelAccurateTool(tool)) {
      void this.ensureActiveFrameDiagnostic({ pendingTool: tool, silent: true })
      return
    }
    if (
      this.state.visibleReferenceLines &&
      this.dicomViewersActive &&
      this.dicomViewersActive.length > 1
    ) {
      try {
        if (typeof this.referenceLinesShow === 'function') {
          this.referenceLinesShow()
        }
      } catch (e) { /* ignore */ }
      if (
        this.state.renderMode === RENDER_MODE.PREVIEW &&
        this.isStudyBackgroundLoadActive()
      ) {
        void this.ensurePreviewReferenceLineGeometry().then(() => {
          try {
            this.registerReferenceLinePlanesForActiveViewers()
            this.nudgeReferenceLines(this.props.activeDcmIndex)
          } catch (e) {
            /* ignore */
          }
        })
        return
      }
      void this.ensureActiveViewportsDiagnostic({ silent: true }).then(() => {
        try {
          this.scheduleReferenceLinesRefresh()
        } catch (e) { /* ignore */ }
      })
    }
  }

  showDiagnosticModeHint = () => {
    this.setState({
      titleMessage: 'Medición',
      textMessage: PIXEL_ACCURATE_NO_DICOM_TOOLTIP,
      visibleMessage: true,
    })
  }

  toggleRenderMode = () => {
    if (
      this.state.manifestReadiness === 'preview' &&
      this.state.renderMode === RENDER_MODE.PREVIEW
    ) {
      this.setState({
        titleMessage: t('renderMode.readinessTitle'),
        textMessage: t('renderMode.readinessNotReady'),
        visibleMessage: true,
      })
      return
    }
    const next =
      this.state.renderMode === RENDER_MODE.PREVIEW
        ? RENDER_MODE.DIAGNOSTIC
        : RENDER_MODE.PREVIEW
    this.setRenderMode(next)
  }

  /**
   * Elige modo según caps del manifest + userSet + ?renderMode= + DEFAULT_RENDER_MODE PACS.
   * No persiste defaults auto (solo toggles usan persist + userSet).
   * @returns {'DIAGNOSTIC'|'PREVIEW'}
   */
  resolveModeForManifestLoad = (manifest, assetBaseUrl = null, opts = {}) => {
    let urlMode = null
    try {
      urlMode = parseRenderMode(
        new URLSearchParams(window.location.search).get('renderMode')
      )
    } catch (e) {
      /* ignore */
    }
    const pacsDefaultMode = resolvePacsDefaultRenderMode(this.state.pacsIdFromUrl)
    const portalProfile = isPortalProfile({
      plan: this.state.udvPlan,
      persona: this.state.udvPersona,
    })
    const archiveUrlOverride =
      (opts.archiveUrlOverride != null && String(opts.archiveUrlOverride).trim()) ||
      (this._lastManifestHandlers &&
        this._lastManifestHandlers.archiveUrlOverride) ||
      null
    // Caps / modo sobre forma canónica (base ZIP o legacy)
    const canonical = canonicalizeManifest(manifest, {
      assetBaseUrl: assetBaseUrl || null,
      archiveUrlOverride,
    })
    const pick = pickRenderModeForManifest(canonical, {
      assetBaseUrl: assetBaseUrl || null,
      archiveUrlOverride,
      urlMode,
      pacsDefaultMode,
      portalProfile,
    })
    this._lastManifestCaps = pick.caps
    const downloadUrl =
      (canonical &&
        canonical.downloadUrl &&
        String(canonical.downloadUrl).trim()) ||
      null
    if (downloadUrl) {
      this._localZipSessionActive = false
    }
    if (this.state.renderMode !== pick.mode) {
      this.setState({
        renderMode: pick.mode,
        manifestMediaCaps: pick.caps,
        studyDownloadUrl: downloadUrl,
        ...(downloadUrl
          ? {
              localStudyZip: {
                eligible: false,
                ready: false,
                expected: 0,
                cached: 0,
                studyUid: '',
              },
            }
          : {}),
      })
    } else {
      this.setState({
        manifestMediaCaps: pick.caps,
        studyDownloadUrl: downloadUrl,
        ...(downloadUrl
          ? {
              localStudyZip: {
                eligible: false,
                ready: false,
                expected: 0,
                cached: 0,
                studyUid: '',
              },
            }
          : {}),
      })
    }
    // state.set es async: source of truth inmediato para el loader
    this.state.renderMode = pick.mode
    return pick.mode
  }

  /**
   * Descarga study.zip remoto (CDN/R2) o ZIP local desde IndexedDB (WADO).
   */
  handleDownloadStudyZip = () => {
    const run = () => this.handleDownloadStudyZipCore()
    if (this.promptPortalDisclaimer(run, { sensitive: true })) return
    void run()
  }

  handleDownloadStudyZipCore = async () => {
    if (this._studyZipDownloadLock || this.state.studyZipDownloadBusy) return
    if (!this.canClickStudyZipDownload()) return

    const url = this.state.studyDownloadUrl
    const hasRemote = !!(url && /^https?:\/\//i.test(String(url)))
    const studyUid = this.getCurrentStudyUid ? this.getCurrentStudyUid() : ''
    const filename = buildStudyZipDownloadFilename(
      resolveStudyZipMeta(this.props.files, this._lastManifestObject, studyUid)
    )

    this._studyZipDownloadLock = true
    this.setState({ studyZipDownloadBusy: true })

    try {
      let built = null
      if (hasRemote) {
        built = await buildStudyZipFromUrl({
          url,
          filename,
          allowLarge: true,
        })
      } else {
        const local = this.state.localStudyZip || {}
        const uid = local.studyUid || studyUid
        built = await buildStudyZipFromIdb({
          studyUid: uid,
          filename,
          allowLarge: true,
          expectedCount: local.networkFallback ? 0 : local.expected || 0,
        })
        const needFetch =
          !built ||
          !built.file ||
          built.error === 'incomplete' ||
          built.error === 'no_cached'
        if (needFetch) {
          const files = this.getShareFilesPool()
          if (files.length) {
            built = await buildSeriesZipFromFiles({
              files,
              filename,
              allowLarge: true,
            })
          }
        }
      }
      if (!built || !built.file) {
        this.setState({
          studyZipDownloadBusy: false,
          captureToast: {
            ok: false,
            message: t('share.downloadStudyFail'),
          },
        })
        return
      }
      downloadFileFallback(built.file, filename)
      this.setState({
        studyZipDownloadBusy: false,
        captureToast: {
          ok: true,
          message: t('share.downloadStudyOk'),
        },
      })
    } catch (e) {
      console.warn('handleDownloadStudyZip', e)
      this.setState({
        studyZipDownloadBusy: false,
        captureToast: {
          ok: false,
          message: t('share.downloadStudyFail'),
        },
      })
    } finally {
      this._studyZipDownloadLock = false
    }
  }

  /**
   * studyUID del manifiesto / store (para validar snapshot de modo).
   */
  getCurrentStudyUid = () => {
    try {
      if (this._lastManifestObject && this._lastManifestObject.studyInstanceUID) {
        return String(this._lastManifestObject.studyInstanceUID)
      }
      const files = this.props.files
      if (Array.isArray(files)) {
        for (let i = 0; i < files.length; i++) {
          const f = files[i]
          if (f && f.study && f.study.studyId) return String(f.study.studyId)
        }
      }
    } catch (e) {
      /* ignore */
    }
    return null
  }

  /**
   * Copia ligera del stack (sin image RGBA) para snapshot de sesión por modo.
   */
  cloneFilesForModeSnapshot = (files) => {
    if (!Array.isArray(files) || !files.length) return null
    const out = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!f || !f.imageId) continue
      out.push({
        imageId: f.imageId,
        instanceNumber: f.instanceNumber,
        name: f.name,
        image: null,
        sopInstanceUID: f.sopInstanceUID || null,
        seriesInstanceUID:
          f.seriesInstanceUID ||
          (f.series && f.series.seriesInstanceUID) ||
          null,
        dicomUrl: f.dicomUrl || null,
        previewUrl: f.previewUrl || null,
        archiveUrl: f.archiveUrl || null,
        byteOffset: f.byteOffset != null ? f.byteOffset : null,
        byteLength: f.byteLength != null ? f.byteLength : null,
        dicomFetchType: f.dicomFetchType || null,
        renderKind: f.renderKind || null,
        usedFallback: f.usedFallback,
        patient: f.patient,
        study: f.study,
        series: f.series,
        fromCache: f.fromCache,
        skeleton: f.skeleton === true,
        ...pickGeometryFields(f),
      })
    }
    return out.length ? out : null
  }

  /**
   * Guarda el stack Redux del modo actual (antes de clearStore).
   */
  snapshotFilesForRenderMode = (mode, filesOverride) => {
    if (mode !== RENDER_MODE.PREVIEW && mode !== RENDER_MODE.DIAGNOSTIC) return
    try {
      const files =
        Array.isArray(filesOverride) && filesOverride.length
          ? filesOverride
          : this.props.files
      if (!Array.isArray(files) || !files.some((f) => this.isDisplayableFile(f))) {
        return
      }
      const clone = this.cloneFilesForModeSnapshot(files)
      if (!clone) return
      const studyUid = this.getCurrentStudyUid()
      // Si cambia el estudio, invalidar snapshots previos
      if (
        this._filesByRenderModeStudyUid &&
        studyUid &&
        this._filesByRenderModeStudyUid !== studyUid
      ) {
        this._filesByRenderMode = {
          [RENDER_MODE.PREVIEW]: null,
          [RENDER_MODE.DIAGNOSTIC]: null,
        }
      }
      if (studyUid) this._filesByRenderModeStudyUid = studyUid
      this._filesByRenderMode[mode] = clone
    } catch (e) {
      console.warn('snapshotFilesForRenderMode:', e)
    }
  }

  /**
   * Snapshot usable del modo destino (mismo estudio).
   */
  getModeFilesSnapshot = (mode) => {
    if (mode !== RENDER_MODE.PREVIEW && mode !== RENDER_MODE.DIAGNOSTIC) return null
    const snap = this._filesByRenderMode && this._filesByRenderMode[mode]
    if (!Array.isArray(snap) || !snap.length) return null
    const studyUid = this.getCurrentStudyUid()
    if (
      studyUid &&
      this._filesByRenderModeStudyUid &&
      studyUid !== this._filesByRenderModeStudyUid
    ) {
      return null
    }
    if (!snap.some((f) => f && f.imageId)) return null
    // Clonar shallow para no mutar el snapshot al soft-sync
    return snap.map((f) => ({ ...f, image: null }))
  }

  setRenderMode = (mode) => {
    if (mode !== RENDER_MODE.PREVIEW && mode !== RENDER_MODE.DIAGNOSTIC) return
    if (mode === this.state.renderMode) return
    if (this.isPortalProfileSession() && mode === RENDER_MODE.DIAGNOSTIC) {
      this.setState({
        renderModeToast: t('renderMode.portalOnlyPreview'),
      })
      return
    }
    const caps = this.state.manifestMediaCaps || this._lastManifestCaps
    if (caps && !isModeViableForCaps(mode, caps)) {
      this.setState({
        renderModeToast:
          mode === RENDER_MODE.PREVIEW
            ? 'Este estudio no tiene previews'
            : 'Este estudio no tiene DICOM diagnóstico',
      })
      return
    }
    const prevMode = this.state.renderMode
    // Snapshot del modo saliente ANTES de vaciar store / UI
    this.snapshotFilesForRenderMode(prevMode)
    // Guardar serie + corte + viewport ANTES de clearStore/reload
    this._renderModeViewportRestore = this.captureViewportBookmarkForModeSwitch()
    this._lastModeSwitchBookmark = this._renderModeViewportRestore
    this._bookmarkHoldUntil = Date.now() + 4000
    // Solo toggles explícitos marcan userSet
    persistRenderMode(mode, { userSet: true })
    const destHasSnap = !!this.getModeFilesSnapshot(mode)
    const toast = destHasSnap
      ? mode === RENDER_MODE.DIAGNOSTIC
        ? t('renderMode.toastDiagnostic')
        : t('renderMode.toastPreview')
      : mode === RENDER_MODE.DIAGNOSTIC
        ? t('renderMode.toastDiagnosticSwitching')
        : t('renderMode.toastPreviewSwitching')
    // Quitar filtro CSS + reconfigurar WW/WC en TODOS los viewports
    // (evita path CSS “stuck” al volver a DIAG y sensibilidad anómala)
    try {
      const refs = this.dicomViewersRefs || []
      for (let i = 0; i < refs.length; i++) {
        const v = refs[i]
        if (!v) continue
        if (typeof v.reconfigureWwwcForRenderMode === 'function') {
          v.reconfigureWwwcForRenderMode(mode)
        } else if (typeof v.resetPreviewWwwcState === 'function') {
          v.resetPreviewWwwcState()
        }
      }
    } catch (e) {
      /* ignore */
    }

    this.setState(
      {
        renderMode: mode,
        renderModeToast: toast,
        toolActive: 'notool',
      },
      () => {
        try {
          this.props.toolStore('notool')
          const v = this.dicomViewersRefs[this.props.activeDcmIndex]
          if (v) {
            if (typeof v.reconfigureWwwcForRenderMode === 'function') {
              v.reconfigureWwwcForRenderMode(mode)
            }
            v.runTool('notool')
          }
        } catch (e) {
          /* ignore */
        }
        this.reloadStudyForRenderMode()
      }
    )
  }

  /**
   * ¿Ya está en el store el frame del bookmark (SOP / instanceNumber / índice)?
   */
  bookmarkFrameReady = (bookmark) => {
    if (!bookmark) return true
    let seriesUID = bookmark.seriesUID || this._activeSeriesUID
    if (!seriesUID && bookmark.seriesNumberKey && Array.isArray(this.props.files)) {
      const match = this.props.files.find(
        (f) =>
          this.isDisplayableFile(f) &&
          this.getSeriesNumberKey(f) === bookmark.seriesNumberKey
      )
      if (match) seriesUID = this.getFileSeriesId(match)
    }
    if (!seriesUID) return false
    const seriesFiles = this.getSeriesFilesFromStore(seriesUID)
    if (!seriesFiles.length) return false
    if (bookmark.sopInstanceUID) {
      return seriesFiles.some(
        (f) =>
          f &&
          f.sopInstanceUID &&
          String(f.sopInstanceUID) === String(bookmark.sopInstanceUID)
      )
    }
    if (bookmark.instanceNumber != null && !Number.isNaN(Number(bookmark.instanceNumber))) {
      return seriesFiles.some(
        (f) => f && Number(f.instanceNumber) === Number(bookmark.instanceNumber)
      )
    }
    return seriesFiles.length > (bookmark.sliceIndex || 0)
  }

  /**
   * Tras reload de modo: reabrir stack de la serie activa en el mismo corte.
   */
  reopenActiveStackForRenderMode = () => {
    try {
      const bookmark = this._renderModeViewportRestore || null
      let seriesUID =
        (bookmark && bookmark.seriesUID) || this._activeSeriesUID || null
      if (!seriesUID) {
        const activeViewer =
          this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
        seriesUID = this.resolveActiveSeriesUID(activeViewer || null, {
          localOnly: false,
        })
      }
      if (!seriesUID && Array.isArray(this.props.files)) {
        // Intentar por seriesNumber del bookmark
        if (bookmark && bookmark.seriesNumberKey) {
          const match = this.props.files.find((f) => {
            if (!this.isDisplayableFile(f)) return false
            return this.getSeriesNumberKey(f) === bookmark.seriesNumberKey
          })
          if (match) seriesUID = this.getFileSeriesId(match)
        }
      }
      if (!seriesUID && Array.isArray(this.props.files)) {
        const first = this.props.files.find((f) => this.isDisplayableFile(f))
        if (first) seriesUID = this.getFileSeriesId(first)
      }
      if (!seriesUID) return
      const seriesFiles = this.getSeriesFilesFromStore(seriesUID)
      if (!seriesFiles.length) return
      const explorerIndex =
        bookmark && typeof bookmark.explorerSeriesIndex === 'number'
          ? bookmark.explorerSeriesIndex
          : typeof this.props.explorerActiveSeriesIndex === 'number'
            ? this.props.explorerActiveSeriesIndex
            : 0
      const index =
        bookmark && typeof bookmark.activeDcmIndex === 'number'
          ? bookmark.activeDcmIndex
          : typeof this.props.activeDcmIndex === 'number'
            ? this.props.activeDcmIndex
            : 0
      this.loadNewSeries(seriesFiles, explorerIndex, index, {
        bookmark,
        preserveView: true,
      })
    } catch (e) {
      console.warn('reopenActiveStackForRenderMode:', e)
      try {
        this.syncActiveSeriesFilesFromStore()
      } catch (e2) {
        /* ignore */
      }
    }
  }

  /**
   * Restaura stack de un modo ya visitado (sin re-descargar el estudio).
   * @returns {boolean} true si restauró
   */
  restoreRenderModeFromSnapshot = (mode) => {
    const snap = this.getModeFilesSnapshot(mode)
    if (!snap) return false
    try {
      if (typeof this.props.setFilesStore === 'function') {
        this.props.setFilesStore(snap)
      }
      try {
        const patientList = groupBy(snap, (a) =>
          a.patient && a.patient.patientName ? a.patient.patientName : 'Unknown'
        )
        this.handleExplorerUpdate({
          folder: null,
          patient: {
            list: patientList,
            keys: [...patientList.keys()],
          },
        })
      } catch (e) {
        /* ignore */
      }
      try {
        if (typeof this.props.isOpenStore === 'function') {
          this.props.isOpenStore({
            index:
              typeof this.props.activeDcmIndex === 'number'
                ? this.props.activeDcmIndex
                : 0,
            value: true,
          })
        }
      } catch (e) {
        /* ignore */
      }
      return true
    } catch (e) {
      console.warn('restoreRenderModeFromSnapshot:', e)
      return false
    }
  }

  /**
   * Re-carga estudio manifest con el modo activo (serie activa priorizada por scheduler).
   * Si hay snapshot de sesión del modo destino, restaura sin pipeline de descarga.
   */
  reloadStudyForRenderMode = async () => {
    if (this._manifestLoadInFlight) {
      this._pendingRenderModeReload = true
      return
    }
    const mode = this.state.renderMode || RENDER_MODE.DIAGNOSTIC
    const gen = ++this._renderModeReloadGen

    // Path rápido: modo ya visitado en esta sesión
    const snap = this.getModeFilesSnapshot(mode)
    if (snap) {
      try {
        this.props.clearingStore()
      } catch (e) {
        /* ignore */
      }
      if (this.restoreRenderModeFromSnapshot(mode)) {
        // Reinyectar snapshot (clearStore lo vació; restore lo repuso)
        if (typeof this.props.setFilesStore === 'function') {
          this.props.setFilesStore(snap)
        }
        this.setState({
          studyLoadSource: null,
          manifestDownloadProgress: null,
        })
        // Props Redux un tick tarde
        let attempts = 0
        const reopenFromSnap = () => {
          if (gen !== this._renderModeReloadGen) return
          const hasFiles =
            Array.isArray(this.props.files) &&
            this.props.files.some((f) => this.isDisplayableFile(f))
          if (!hasFiles) {
            // forzar files si connect no llegó
            if (typeof this.props.setFilesStore === 'function') {
              this.props.setFilesStore(snap)
            }
            if (attempts++ < 20) {
              setTimeout(reopenFromSnap, 16)
              return
            }
          }
          try {
            this.reopenActiveStackForRenderMode()
          } catch (e) {
            console.warn('reloadStudyForRenderMode snapshot reopen:', e)
          }
          try {
            const refs = this.dicomViewersRefs || []
            for (let i = 0; i < refs.length; i++) {
              const v = refs[i]
              if (v && typeof v.reconfigureWwwcForRenderMode === 'function') {
                v.reconfigureWwwcForRenderMode(mode)
              }
            }
          } catch (e) {
            /* ignore */
          }
        }
        setTimeout(reopenFromSnap, 0)
        return
      }
    }

    const manifest = this._lastManifestObject
    if (!manifest) {
      try {
        this.syncActiveSeriesFilesFromStore()
      } catch (e) {
        /* ignore */
      }
      return
    }
    this._manifestLoadInFlight = true
    this._pendingRenderModeReload = false
    let reopenedForGen = false
    const loadToken = this.beginStudyLoad('render-mode')
    const remoteSource =
      this._preferredRemoteLoadSource ||
      resolveStudyLoadSourceFromManifest(this._lastManifestObject) ||
      STUDY_LOAD_SOURCE.R2
    this._preferredRemoteLoadSource = remoteSource
    this.setState({
      studyLoadSource: remoteSource,
      manifestDownloadProgress: null,
    })
    const pacsCfg = getPacsConfig(this.state.pacsIdFromUrl)
    const saved = this._lastManifestHandlers || {}

    const finishReopenTools = () => {
      try {
        const refs = this.dicomViewersRefs || []
        for (let i = 0; i < refs.length; i++) {
          const v = refs[i]
          if (v && typeof v.reconfigureWwwcForRenderMode === 'function') {
            v.reconfigureWwwcForRenderMode(mode)
          }
        }
      } catch (e) {
        /* ignore */
      }
    }

    /**
     * Reabrir viewport cuando hay stack esqueleto (o final).
     * Prioriza el frame del bookmark para scroll inmediato.
     */
    const tryReopen = (fromSeed) => {
      if (gen !== this._renderModeReloadGen) return
      if (reopenedForGen && fromSeed) return
      const hasFiles =
        Array.isArray(this.props.files) &&
        this.props.files.some((f) => this.isDisplayableFile(f))
      if (!hasFiles) return false
      const bookmark = this._renderModeViewportRestore
      if (bookmark && !this.bookmarkFrameReady(bookmark) && !fromSeed) {
        return false
      }
      try {
        this.reopenActiveStackForRenderMode()
        reopenedForGen = true
        finishReopenTools()
        return true
      } catch (e) {
        console.warn('reloadStudyForRenderMode tryReopen:', e)
        return false
      }
    }

    try {
      // Snapshot del modo actual por si no se capturó (defensivo)
      this.snapshotFilesForRenderMode(
        mode === RENDER_MODE.PREVIEW
          ? RENDER_MODE.DIAGNOSTIC
          : RENDER_MODE.PREVIEW
      )
      try {
        this.props.clearingStore()
      } catch (e) {
        /* ignore */
      }
      const handlers = {
        ...this.buildManifestLoadHandlers(pacsCfg, mode, loadToken),
        manifestUrl: saved.manifestUrl,
        assetBaseUrl: saved.assetBaseUrl,
        renderMode: mode,
        onStackSeeded: () => {
          if (gen !== this._renderModeReloadGen) return
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          let attempts = 0
          const poll = () => {
            if (gen !== this._renderModeReloadGen || reopenedForGen) return
            if (!this.isStudyLoadCurrent(loadToken.gen)) return
            if (tryReopen(true)) return
            if (attempts++ < 40) {
              setTimeout(poll, 16)
            }
          }
          setTimeout(poll, 0)
        },
      }
      if (saved.orthancCredentials) {
        handlers.orthancCredentials = saved.orthancCredentials
      }
      const loadResult = await loadStudyFromManifestObject(manifest, handlers)
      if (gen !== this._renderModeReloadGen) return
      if (!this.isStudyLoadCurrent(loadToken.gen)) return
      // Guardar stack del modo recién cargado para futuros toggles
      try {
        if (loadResult && Array.isArray(loadResult.items) && loadResult.items.length) {
          this.snapshotFilesForRenderMode(mode, loadResult.items)
        } else if (Array.isArray(this.props.files) && this.props.files.length) {
          this.snapshotFilesForRenderMode(mode)
        }
      } catch (e) {
        /* ignore */
      }
      this.completeManifestLoadStudySource()
      if (!reopenedForGen) {
        let attempts = 0
        const finalReopen = () => {
          if (gen !== this._renderModeReloadGen) return
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          if (tryReopen(false)) return
          if (attempts++ < 40) {
            setTimeout(finalReopen, 40)
          }
        }
        setTimeout(finalReopen, 0)
      } else {
        try {
          this.syncActiveSeriesFilesFromStore()
        } catch (e) {
          /* ignore */
        }
        finishReopenTools()
      }
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      console.error('reloadStudyForRenderMode:', error)
      this.setState({
        titleMessage: 'Modo de render',
        textMessage: error.message || String(error),
        visibleMessage: true,
        studyLoadSource: null,
      })
    } finally {
      this._manifestLoadInFlight = false
      if (this._pendingRenderModeReload && gen === this._renderModeReloadGen) {
        this._pendingRenderModeReload = false
        this.reloadStudyForRenderMode()
      } else if (this._pendingPreviewGrow) {
        const pending = this._pendingPreviewGrow
        this._pendingPreviewGrow = null
        this.applyPreviewManifestGrow(pending)
      }
    }
  }

  /**
   * Hot-update: el manifest preview creció (nuevas series/instancias) sin salir de preview.
   * Soft-reload WebP preservando serie/corte activo — sin refresh de página/PWA.
   * @param {object} nextJson
   */
  applyPreviewManifestGrow = async (nextJson) => {
    if (!nextJson || typeof nextJson !== 'object') return
    if (this.state.manifestReadiness !== 'preview') return
    if (this._manifestLoadInFlight) {
      this._pendingPreviewGrow = nextJson
      return
    }

    const saved = this._lastManifestHandlers || {}
    const assetBaseUrl = saved.assetBaseUrl || null
    const archiveUrlOverride = saved.archiveUrlOverride || null
    const next = canonicalizeManifest(nextJson, {
      assetBaseUrl,
      archiveUrlOverride,
    })
    const fp = manifestPreviewFingerprint(next)
    if (!fp || fp === this._previewGrowFingerprint) return
    const prevFp = manifestPreviewFingerprint(this._lastManifestObject)
    if (fp === prevFp) {
      this._previewGrowFingerprint = fp
      return
    }

    this._previewGrowFingerprint = fp
    this._lastManifestObject = next
    // Invalidar snapshot PREVIEW incompleto (modo switch no debe restaurar estudio viejo)
    try {
      if (this._filesByRenderMode) {
        this._filesByRenderMode[RENDER_MODE.PREVIEW] = null
      }
    } catch (e) {
      /* ignore */
    }

    const reuseItems = Array.isArray(this.props.files)
      ? this.props.files.filter((f) => f && f.sopInstanceUID && f.imageId)
      : []

    const prevProgress = this.state.manifestDownloadProgress
    const progressFloor = Math.max(
      0,
      Number(prevProgress && prevProgress.completed) || 0,
      Number(prevProgress && prevProgress.loadedOk) || 0,
      reuseItems.length
    )
    this._previewGrowProgressFloor = progressFloor

    this._manifestLoadInFlight = true
    const growGen = ++this._previewGrowGen
    this._renderModeViewportRestore = this.captureViewportBookmarkForModeSwitch()
    const loadToken = this.beginStudyLoad('preview-grow')
    const pacsCfg = getPacsConfig(this.state.pacsIdFromUrl)
    const mode = RENDER_MODE.PREVIEW
    let reopenedForGen = false

    const softSyncOnly = () => {
      if (growGen !== this._previewGrowGen) return false
      if (!this.isStudyLoadCurrent(loadToken.gen)) return false
      const hasFiles =
        Array.isArray(this.props.files) &&
        this.props.files.some((f) => this.isDisplayableFile(f))
      if (!hasFiles) return false
      try {
        // Continuidad: no reabrir serie (evita “estudio de nuevo” / contador a 0)
        this.syncActiveSeriesFilesFromStore()
        this.forceExpandOpenSeriesBuffers(this.props.files)
        reopenedForGen = true
        return true
      } catch (e) {
        console.warn('applyPreviewManifestGrow softSync:', e)
        return false
      }
    }

    const tryReopen = (fromSeed) => {
      if (growGen !== this._previewGrowGen) return false
      if (!this.isStudyLoadCurrent(loadToken.gen)) return false
      if (reopenedForGen && fromSeed) return false
      if (softSyncOnly()) return true
      const hasFiles =
        Array.isArray(this.props.files) &&
        this.props.files.some((f) => this.isDisplayableFile(f))
      if (!hasFiles) return false
      try {
        this.reopenActiveStackForRenderMode()
        reopenedForGen = true
        return true
      } catch (e) {
        console.warn('applyPreviewManifestGrow tryReopen:', e)
        return false
      }
    }

    try {
      // NO clearingStore: conservar viewport y reusar ítems ya cargados
      this.setState({
        studyLoadSource:
          reuseItems.length > 0
            ? STUDY_LOAD_SOURCE.LOCAL
            : this._preferredRemoteLoadSource || STUDY_LOAD_SOURCE.R2,
        manifestGrowFlash: true,
        renderMode: RENDER_MODE.PREVIEW,
        // Mantener progreso previo (piso) hasta que el grow emita stats ≥ floor
        manifestDownloadProgress: prevProgress
          ? {
              ...prevProgress,
              completed: Math.max(
                Number(prevProgress.completed) || 0,
                progressFloor
              ),
              loadedOk: Math.max(
                Number(prevProgress.loadedOk) || 0,
                progressFloor
              ),
            }
          : prevProgress,
      })
      if (this._growFlashTimer) {
        clearTimeout(this._growFlashTimer)
      }
      this._growFlashTimer = setTimeout(() => {
        if (!this._unmounted) {
          this.setState({ manifestGrowFlash: false })
        }
      }, 2800)

      const handlers = {
        ...this.buildManifestLoadHandlers(pacsCfg, mode, loadToken),
        manifestUrl: saved.manifestUrl,
        assetBaseUrl: saved.assetBaseUrl,
        renderMode: mode,
        reuseItems,
        onDownloadProgress: (stats) => {
          if (this._unmounted || !this.isStudyLoadCurrent(loadToken.gen)) return
          const floor = this._previewGrowProgressFloor || 0
          const next = stats
            ? {
                ...stats,
                completed: Math.max(Number(stats.completed) || 0, floor),
                loadedOk: Math.max(Number(stats.loadedOk) || 0, floor),
              }
            : stats
          this.applyManifestDownloadProgress(next)
        },
        onStackSeeded: () => {
          if (growGen !== this._previewGrowGen) return
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          let attempts = 0
          const poll = () => {
            if (growGen !== this._previewGrowGen || reopenedForGen) return
            if (!this.isStudyLoadCurrent(loadToken.gen)) return
            if (tryReopen(true)) return
            if (attempts++ < 40) setTimeout(poll, 16)
          }
          setTimeout(poll, 0)
        },
      }
      if (saved.orthancCredentials) {
        handlers.orthancCredentials = saved.orthancCredentials
      }

      const loadResult = await loadStudyFromManifestObject(next, handlers)
      if (growGen !== this._previewGrowGen) return
      if (!this.isStudyLoadCurrent(loadToken.gen)) return

      try {
        if (loadResult && Array.isArray(loadResult.items) && loadResult.items.length) {
          this.snapshotFilesForRenderMode(mode, loadResult.items)
        } else if (Array.isArray(this.props.files) && this.props.files.length) {
          this.snapshotFilesForRenderMode(mode)
        }
      } catch (e) {
        /* ignore */
      }

      this.completeManifestLoadStudySource()
      if (!reopenedForGen) {
        let attempts = 0
        const finalReopen = () => {
          if (growGen !== this._previewGrowGen) return
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          if (tryReopen(false)) return
          if (attempts++ < 40) setTimeout(finalReopen, 40)
        }
        setTimeout(finalReopen, 0)
      } else {
        try {
          this.syncActiveSeriesFilesFromStore()
        } catch (e) {
          /* ignore */
        }
      }
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      console.warn('applyPreviewManifestGrow:', error)
    } finally {
      this._previewGrowProgressFloor = 0
      this._manifestLoadInFlight = false
      if (this._pendingPreviewGrow) {
        const pending = this._pendingPreviewGrow
        this._pendingPreviewGrow = null
        this.applyPreviewManifestGrow(pending)
      } else if (this._pendingRenderModeReload) {
        this._pendingRenderModeReload = false
        this.reloadStudyForRenderMode()
      }
    }
  }

  /** ¿Diferir progreso de descarga para no re-render App mid-scroll? */
  shouldDeferManifestDownloadProgress = (_stats) => {
    try {
      const v =
        this.dicomViewersRefs &&
        this.dicomViewersRefs[this.props.activeDcmIndex]
      if (v && typeof v.isStackScrollLive === 'function' && v.isStackScrollLive()) {
        return true
      }
    } catch (e) {
      /* ignore */
    }
    return false
  }

  /**
   * Progreso manifest → UI. El estado ZIP local se sincroniza siempre
   * (también si el chip de progreso se difiere mid-scroll).
   */
  applyManifestDownloadProgress = (stats, opts = {}) => {
    if (this._unmounted || !stats) return
    const { skipRefLinesResync = false } = opts
    try {
      this.syncLocalStudyZipFromProgress(stats)
    } catch (eSync) {
      /* ignore */
    }
    if (this.shouldDeferManifestDownloadProgress(stats)) {
      this._deferredDownloadProgress = stats
      return
    }
    this.setState((prev) => ({
      manifestDownloadProgress: stats,
      studyLoadSource: stats.studyFullyLoaded
        ? finishStudyLoadSource(prev.studyLoadSource, stats)
        : resolveStudyLoadSourceFromStats(stats, {
            preferredRemote: this._preferredRemoteLoadSource || null,
          }),
    }))
    if (
      !skipRefLinesResync &&
      stats.studyFullyLoaded &&
      this.state.visibleReferenceLines
    ) {
      if (
        ReferenceLinesManager.synchronizer &&
        this.areReferenceLineElementsCurrent() &&
        this.activeViewersHaveUsableReferenceLinePlanes()
      ) {
        this.reassertReferenceLinesQuiet()
      } else {
        this.scheduleReferenceLinesResync(200)
      }
    }
  }

  /**
   * Frame listo en RAM (loader) → viewport sin esperar Redux/Explorer.
   * Crítico en RANGE_ZIP: skeleton sin imageId no puede hidratar por red.
   */
  handleManifestInstanceReady = (item) => {
    if (this._unmounted || !item) return
    const sop = item.sopInstanceUID ? String(item.sopInstanceUID) : ''
    const imageId = item.imageId ? String(item.imageId) : ''
    if (!sop && !imageId) return
    const image = item.image || null
    if (!image) return

    const refs = this.dicomViewersRefs
    if (!refs || !refs.length) return

    const tryPatch = () => {
      let patched = false
      for (let i = 0; i < refs.length; i++) {
        const viewer = refs[i]
        if (!viewer || !Array.isArray(viewer.files) || !viewer.files.length) continue
        const files = viewer.files
        let idx = -1
        if (sop) {
          for (let j = 0; j < files.length; j++) {
            const f = files[j]
            if (f && f.sopInstanceUID && String(f.sopInstanceUID) === sop) {
              idx = j
              break
            }
          }
        }
        if (idx < 0 && imageId) {
          for (let j = 0; j < files.length; j++) {
            const f = files[j]
            if (
              f &&
              (String(f.imageId || '') === imageId ||
                String(f.decodedImageId || '') === imageId)
            ) {
              idx = j
              break
            }
          }
        }
        if (idx < 0) continue

        const prev = files[idx] || {}
        const merged = {
          ...prev,
          ...item,
          image,
          skeleton: false,
        }
        files[idx] = merged
        patched = true
        try {
          if (typeof viewer.rememberDecodedImage === 'function') {
            viewer.rememberDecodedImage(merged, image, imageId || merged.imageId)
          }
        } catch (eMem) {
          /* ignore */
        }
      }
      return patched
    }

    if (tryPatch()) return
    // Viewport aún con buffer corto: adoptar stubs del store y reintentar.
    try {
      if (Array.isArray(this.props.files) && this.props.files.length) {
        this.forceExpandOpenSeriesBuffers(this.props.files)
        tryPatch()
      }
    } catch (eExp) {
      /* ignore */
    }
  }

  peekManifestLoadEndProgress = (stateProgress) => {
    const deferred = this._deferredDownloadProgress
    if (
      deferred &&
      (!stateProgress ||
        (deferred.studyFullyLoaded && !stateProgress.studyFullyLoaded) ||
        (Number(deferred.completed) || 0) > (Number(stateProgress.completed) || 0))
    ) {
      return deferred
    }
    return stateProgress || deferred || null
  }

  /** UDV Fase 4: duración desde inicio de carga manifest (ms). */
  _udvTelemetryElapsedMs = () => {
    const t0 = this._udvTelemetrySessionStart
    if (!Number.isFinite(t0)) return 0
    return Math.max(0, Math.round(performance.now() - t0))
  }

  _udvTelemetryModality = () => {
    const m =
      (this._lastManifestObject &&
        this._lastManifestObject.series &&
        this._lastManifestObject.series[0] &&
        (this._lastManifestObject.series[0].modality ||
          this._lastManifestObject.series[0].Modality)) ||
      null
    return m ? String(m) : null
  }

  _maybeSendUdvFirstPaintBeacon = () => {
    if (this._udvFirstPaintBeaconSent) return
    this._udvFirstPaintBeaconSent = true
    sendUdvTelemetryBeacon('first_preview_paint', {
      durationMs: this._udvTelemetryElapsedMs(),
      modality: this._udvTelemetryModality(),
    })
  }

  _sendUdvManifestUpgradeBeacons = () => {
    const ms = this._udvTelemetryElapsedMs()
    const modality = this._udvTelemetryModality()
    sendUdvTelemetryBeacon('manifest_upgrade', { durationMs: ms, modality })
    sendUdvTelemetryBeacon('diag_available', { durationMs: ms, modality })
  }

  completeManifestLoadStudySource = () => {
    const stats = this.peekManifestLoadEndProgress(
      this.state.manifestDownloadProgress
    )
    this._deferredDownloadProgress = null
    this.setState((prev) => {
      const progress = stats || prev.manifestDownloadProgress
      return {
        manifestDownloadProgress: progress,
        studyLoadSource: finishStudyLoadSource(
          prev.studyLoadSource,
          progress
        ),
      }
    })
    try {
      if (stats) this.syncLocalStudyZipFromProgress(stats)
    } catch (eSync) {
      /* ignore */
    }
    if (stats && stats.studyFullyLoaded) {
      this._maybeSendUdvFirstPaintBeacon()
    }
    if (stats && stats.studyFullyLoaded && this.state.visibleReferenceLines) {
      if (
        ReferenceLinesManager.synchronizer &&
        this.areReferenceLineElementsCurrent() &&
        this.activeViewersHaveUsableReferenceLinePlanes()
      ) {
        this.reassertReferenceLinesQuiet()
      } else {
        this.finishPreviewReferenceLinesSetup()
      }
    }
  }

  /** Descarga manifest / preview-grow aún en curso (no confundir con chip r2-done). */
  isStudyBackgroundLoadActive = () => {
    if (this._manifestLoadInFlight) return true
    const p =
      this._deferredDownloadProgress || this.state.manifestDownloadProgress
    if (p && p.studyFullyLoaded) return false
    return isStudyLoadActive(this.state.studyLoadSource)
  }

  /** IPP/IOP desde manifest en PREVIEW (antes de que baje DICOM o WebP en RAM). */
  registerReferenceLinePlanesForActiveViewers = () => {
    let viewers = []
    try {
      this.getActiveDcmViewers()
      viewers = this.dicomViewersActive || []
    } catch (e) {
      viewers = this.dicomViewersRefs || []
    }
    const mprData = this.mprData || {}
    for (let i = 0; i < viewers.length; i++) {
      const v = viewers[i]
      if (!v || !Array.isArray(v.files) || !v.files.length) continue

      const idx =
        typeof v.sliceIndex === 'number' && v.sliceIndex >= 0
          ? v.sliceIndex
          : 0
      const img =
        (v.image && v.image.imageId ? v.image : null) ||
        null

      // Planos MPR reconstruidos: IPP/IOP del corte proyectado, no del stack nativo
      if (v.isMprGenerated && v._mprPlaneKind && img) {
        try {
          const planeMod = buildMprImagePlaneModule(v._mprPlaneKind, idx, {
            files: v.files,
            mprData: v.mprData || mprData,
            originImage: v.originImage,
            columns: img.columns,
            rows: img.rows,
          })
          if (planeMod) registerMprImagePlane(img.imageId, planeMod)
        } catch (eMpr) {
          /* ignore */
        }
        continue
      }

      try {
        spreadStackGeometryFromFiles(v.files)
      } catch (eSpread) {
        /* ignore */
      }
      const file = v.files[idx]
      if (!file) continue
      const imgFallback =
        img ||
        (file.image && file.image.imageId ? file.image : null) ||
        (file.imageId
          ? {
              imageId: file.imageId,
              rows: file.rows || 512,
              columns: file.columns || 512,
            }
          : null)
      if (!imgFallback) continue
      try {
        if (this.isMprLayoutActive() && !v.isMprGenerated && v.image && v.image.imageId) {
          const nativePlane = buildNativeImagePlaneModule(v.image)
          if (nativePlane) {
            registerMprImagePlane(v.image.imageId, nativePlane)
            continue
          }
        }
        if (v.image && v.image.imageId && v.image !== imgFallback) {
          registerTargetSlicePlaneOnDisplay(file, v.image)
        } else {
          registerFileImagePlane(file, imgFallback, { force: true })
        }
      } catch (e) {
        /* ignore */
      }
    }
  }

  /** PREVIEW sin IPP/IOP en manifest: leer tags DICOM (range/cache) por serie activa. */
  ensurePreviewReferenceLineGeometry = async () => {
    if (this.state.renderMode !== RENDER_MODE.PREVIEW) return true
    let viewers = []
    try {
      this.getActiveDcmViewers()
      viewers = this.dicomViewersActive || []
    } catch (e) {
      viewers = (this.dicomViewersRefs || []).filter(Boolean)
    }
    const archiveUrlFallback =
      this.state.studyDownloadUrl || this.state.manifestDownloadProgress?.archiveUrl || null
    const seen = new Set()
    const tasks = []
    for (let i = 0; i < viewers.length; i++) {
      const v = viewers[i]
      if (!v || !Array.isArray(v.files) || !v.files.length) continue
      const uid =
        (v.files[0] && v.files[0].seriesInstanceUID) ||
        (v.explorerIndex != null ? `ex:${v.explorerIndex}` : `v:${i}`)
      if (seen.has(uid)) continue
      seen.add(uid)
      tasks.push(
        hydratePreviewStackGeometry(v.files, { archiveUrlFallback })
      )
    }
    if (!tasks.length) return false
    try {
      const results = await Promise.all(tasks)
      return results.some(Boolean)
    } catch (e) {
      return false
    }
  }

  /** Ref-lines en PREVIEW: hidratar geometría + un solo armado (sin upgrade DICOM). */
  finishPreviewReferenceLinesSetup = () => {
    const gen = (this._previewRefLinesSetupGen =
      (this._previewRefLinesSetupGen || 0) + 1)
    if (this._previewRefLinesSetupTimer) {
      clearTimeout(this._previewRefLinesSetupTimer)
      this._previewRefLinesSetupTimer = null
    }

    const run = async () => {
      if (this._unmounted || gen !== this._previewRefLinesSetupGen) return
      if (!this.state.visibleReferenceLines) return
      if (this.isMprLayoutActive()) {
        this.disableCornerstoneReferenceLinesForMpr()
        return
      }

      this._refLinesSetupInFlight = true
      try {
        if (this.state.renderMode === RENDER_MODE.PREVIEW) {
          await this.ensurePreviewReferenceLineGeometry()
        }
        if (this._unmounted || gen !== this._previewRefLinesSetupGen) return
        if (!this.state.visibleReferenceLines) return

        ReferenceLinesManager.enable()
        this._needsReferenceLinesResync = true
        this.scheduleReferenceLinesResync(0)
      } finally {
        this._refLinesSetupInFlight = false
      }
    }

    this._previewRefLinesSetupTimer = setTimeout(() => {
      this._previewRefLinesSetupTimer = null
      void run()
    }, 40)
  }

  applyViewerSessionPolicy = (opts = {}) => {
    const policy = resolveViewerSessionPolicy(opts)
    this._viewerSessionPolicy = policy
    if (!policy.pwaInstall) {
      this.setState((prev) =>
        prev.showInstallBanner || prev.deferredInstallPrompt
          ? { showInstallBanner: false, deferredInstallPrompt: null }
          : null
      )
    }
  }

  isPwaInstallAllowed = () =>
    !this._viewerSessionPolicy || this._viewerSessionPolicy.pwaInstall !== false

  isOfflineCacheAllowed = () =>
    !this._viewerSessionPolicy || this._viewerSessionPolicy.offlineCache !== false

  buildManifestLoadHandlers = (pacsCfg, renderModeOverride, loadToken, handlerOpts = {}) => {
    const mode =
      renderModeOverride || this.state.renderMode || RENDER_MODE.DIAGNOSTIC
    const gen = loadToken && loadToken.gen
    const signal = loadToken && loadToken.signal
    const isCurrent = () =>
      gen == null || this.isStudyLoadCurrent(gen)
    const offlineCacheAllowed = this.isOfflineCacheAllowed()
    const compareMerge =
      !!handlerOpts.compareMerge || !!this._compareMergeActive
    // Cargar (merge) sin modo Comparar: no montar viewport derecho ni cambiar active
    const mergeOnly = compareMerge && !this.state.compareMode
    return {
      preferredLoadSource: this._preferredRemoteLoadSource || STUDY_LOAD_SOURCE.R2,
      setFilesStore: (files) => {
        if (!isCurrent()) return
        let next = files
        if (compareMerge) {
          next = mergeStudyFileLists(this._compareBaseFiles || [], files)
          this._compareMergedFiles = next
          if (!mergeOnly) {
            try {
              this.maybeOpenComparePriorSeries(next)
            } catch (eOpen) {
              /* ignore */
            }
          }
        }
        if (typeof this.props.setFilesStore === 'function') {
          this.props.setFilesStore(next)
        }
      },
      onExplorerUpdate: (data) => {
        if (!isCurrent()) return
        if (compareMerge) {
          const merged =
            this._compareMergedFiles ||
            mergeStudyFileLists(this._compareBaseFiles || [], [])
          this.handleExplorerUpdate(buildExplorerDataFromFiles(merged))
          return
        }
        this.handleExplorerUpdate(data)
      },
      isOpenStore: (payload) => {
        if (!isCurrent()) return
        if (typeof this.props.isOpenStore !== 'function') return
        // Merge-only: no abrir slot 1 (congela UI en layout 1×1)
        if (mergeOnly) return
        if (compareMerge) {
          const idx =
            typeof this._compareRightViewportIndex === 'number'
              ? this._compareRightViewportIndex
              : 1
          this.props.isOpenStore({ index: idx, value: true })
          return
        }
        this.props.isOpenStore(payload)
      },
      activeDcmIndex: mergeOnly
        ? this.props.activeDcmIndex
        : compareMerge
          ? this._compareRightViewportIndex != null
            ? this._compareRightViewportIndex
            : 1
          : this.props.activeDcmIndex,
      concurrency: mergeOnly ? 2 : 0,
      orthancCredentials: {
        username: pacsCfg && pacsCfg.USUARIO,
        password: pacsCfg && pacsCfg.PASS,
      },
      getActiveSeriesIdx: () => {
        // En merge-only el índice Explorer es del primario; no priorizar por él
        if (mergeOnly) return -1
        const bookmark = this._renderModeViewportRestore
        if (bookmark && typeof bookmark.explorerSeriesIndex === 'number') {
          return bookmark.explorerSeriesIndex
        }
        return typeof this.props.explorerActiveSeriesIndex === 'number'
          ? this.props.explorerActiveSeriesIndex
          : 0
      },
      getPinnedSeriesIdx: () => this.getPinnedSeriesIdxForScheduler(),
      getScrollFocus: () => this.getScrollFocusForScheduler(),
      onInstanceReady: (item, meta) => {
        if (!isCurrent()) return
        this.handleManifestInstanceReady(item, meta)
        if (compareMerge && !mergeOnly) {
          try {
            this.maybeOpenComparePriorSeries(this._compareMergedFiles)
          } catch (e) {
            /* ignore */
          }
        }
      },
      renderMode: mode,
      getRenderMode: () => mode,
      signal,
      isCancelled: () => !isCurrent() || !!(signal && signal.aborted),
      onDownloadProgress: (stats) => {
        if (this._unmounted || !isCurrent()) return
        this.applyManifestDownloadProgress(stats)
      },
      offlineCacheAllowed,
      skipIdbWrite: !offlineCacheAllowed,
      skipIdbRead: !offlineCacheAllowed,
    }
  }

  /**
   * P0 del scheduler: serie del Explorer + corte visible (± vecinos).
   * Fase A — preempt sin pausar fetch de fondo.
   */
  getScrollFocusForScheduler = () => {
    try {
      const seriesIdx =
        typeof this.props.explorerActiveSeriesIndex === 'number'
          ? this.props.explorerActiveSeriesIndex
          : 0
      const v =
        this.dicomViewersRefs &&
        this.dicomViewersRefs[this.props.activeDcmIndex]
      let sliceIndex = 0
      if (v) {
        if (typeof v.getStackNavIndex === 'function') {
          sliceIndex = v.getStackNavIndex()
        } else {
          sliceIndex = v.sliceIndex || 0
        }
      }
      return {
        seriesIdx: Math.max(0, Math.floor(seriesIdx) || 0),
        sliceIndex: Math.max(0, Math.floor(Number(sliceIndex) || 0)),
        radius: 2,
      }
    } catch (e) {
      return { seriesIdx: 0, sliceIndex: 0, radius: 2 }
    }
  }

  // Método para obtener la modalidad DICOM actual
  getCurrentModality = () => {
    const activeViewer = this.dicomViewersRefs[this.props.activeDcmIndex]
    if (activeViewer && activeViewer.image) {
      const modality = getDicomModality(activeViewer.image)
      console.log('Modalidad detectada:', modality)
      return modality
    }
    console.log('No se pudo detectar modalidad - visor o imagen no disponible')
    return null
  }

  /**
   * ¿Mostrar menú DSA? XA/XRF/RF, o multi-frame de un solo SOP (angio típica).
   */
  shouldShowDsaMenu = (dcmViewer, multiFrameCine) => {
    if (!dcmViewer || !dcmViewer.isDicom) return false
    const mod = (this.getCurrentModality() || '').toUpperCase()
    if (mod === 'XA' || mod === 'XRF' || mod === 'RF') return true
    // Multi-frame mono-SOP sin modalidad leída aún (run XA)
    if (
      multiFrameCine &&
      Array.isArray(dcmViewer.files) &&
      dcmViewer.files.length <= 1
    ) {
      return true
    }
    return false
  }

  /**
   * DSA — substracción real vía viewer.applyDsaState (spec clínica + DSA 2.0).
   * @param {string} actionId
   * @param {{ label?: string, quiet?: boolean, step?: number }} [option]
   */
  handleDsaMenuSelect = (actionId, option) => {
    const label = (option && option.label) || actionId
    const quiet = !!(option && option.quiet)
    const viewer = this.getActiveDcmViewer && this.getActiveDcmViewer()
    if (viewer) viewer._onDsaMenuSelect = this.handleDsaMenuSelect
    const prev = this.state.dsaUi || {
      enabled: false,
      maskMode: null,
      maskFrameIndex: null,
      pixelShift: { ...DSA_DEFAULTS.pixelShift },
      invertDSA: DSA_DEFAULTS.invertDSA,
      gain: DSA_DEFAULTS.gain,
      offset: DSA_DEFAULTS.offset,
      noiseFloor: DSA_DEFAULTS.noiseFloor,
      engineMode: 'cpu',
      synthMode: 'minip',
      synthRange: { start: 0, end: null },
      roadmap: { on: false, alpha: 0.45, lut: 'fluoro-green' },
    }
    const dsaUi = {
      ...prev,
      invertDSA: !!prev.invertDSA,
      gain:
        prev.gain != null && Number.isFinite(Number(prev.gain))
          ? Number(prev.gain)
          : 1,
      offset: prev.offset,
      noiseFloor:
        prev.noiseFloor != null && Number.isFinite(Number(prev.noiseFloor))
          ? Number(prev.noiseFloor)
          : DSA_DEFAULTS.noiseFloor,
      engineMode: prev.engineMode || 'cpu',
      synthMode: prev.synthMode === 'mip' ? 'mip' : 'minip',
      synthRange: {
        start:
          prev.synthRange && prev.synthRange.start != null
            ? prev.synthRange.start
            : 0,
        end: prev.synthRange ? prev.synthRange.end : null,
      },
      roadmap: {
        on: !!(prev.roadmap && prev.roadmap.on),
        alpha:
          prev.roadmap && prev.roadmap.alpha != null
            ? Number(prev.roadmap.alpha)
            : 0.45,
        lut: (prev.roadmap && prev.roadmap.lut) || 'fluoro-green',
      },
      pixelShift: {
        x:
          prev.pixelShift && prev.pixelShift.x != null
            ? Number(prev.pixelShift.x)
            : 0,
        y:
          prev.pixelShift && prev.pixelShift.y != null
            ? Number(prev.pixelShift.y)
            : 0,
      },
    }

    const currentFrame0 =
      viewer && typeof viewer.getMultiFrameIndex0 === 'function'
        ? viewer.getMultiFrameIndex0()
        : 0
    const step =
      option && option.step != null && Number.isFinite(Number(option.step))
        ? Number(option.step)
        : DSA_SHIFT_STEP

    const resetParams = () => {
      dsaUi.maskMode = null
      dsaUi.maskFrameIndex = null
      dsaUi.pixelShift = { x: 0, y: 0 }
      dsaUi.invertDSA = DSA_DEFAULTS.invertDSA
      dsaUi.gain = 1
      dsaUi.offset = null
      dsaUi.noiseFloor = DSA_DEFAULTS.noiseFloor
      dsaUi.roadmap = { on: false, alpha: 0.45, lut: 'fluoro-green' }
    }

    // Acciones async (synth / cardiac / roadmap) salen temprano
    if (actionId === 'vascular-synth') {
      if (!dsaUi.enabled || !viewer || typeof viewer.runDsaVascularSynth !== 'function') {
        this.setState({ renderModeToast: 'Activá DSA para sintetizar' })
        return
      }
      const nFrames =
        viewer.numberOfFrames > 1 ? viewer.numberOfFrames : currentFrame0 + 1
      const fStart = dsaUi.synthRange.start != null ? dsaUi.synthRange.start : 0
      const fEnd =
        dsaUi.synthRange.end != null ? dsaUi.synthRange.end : nFrames - 1
      this.setState({ renderModeToast: 'DSA · sintetizando árbol vascular…' })
      viewer
        .runDsaVascularSynth({
          fStart,
          fEnd,
          mode: dsaUi.synthMode,
        })
        .then((res) => {
          if (!res || !res.ok) {
            this.setState({
              renderModeToast: `DSA · síntesis falló (${(res && res.reason) || '?'})`,
            })
            return
          }
          this.setState({
            dsaSynthPreview: {
              dataUrl: res.dataUrl,
              mode: res.mode,
              frames: res.frames,
            },
            renderModeToast: `DSA · síntesis ${String(res.mode).toUpperCase()} · ${res.frames} frames`,
          })
        })
      return
    }

    if (actionId === 'synth-mode-minip' || actionId === 'synth-mode-mip') {
      dsaUi.synthMode = actionId === 'synth-mode-mip' ? 'mip' : 'minip'
      this.setState({
        dsaUi,
        renderModeToast: `DSA · modo ${dsaUi.synthMode.toUpperCase()}`,
      })
      return
    }

    if (actionId === 'synth-range-visible-end') {
      const nFrames =
        viewer && viewer.numberOfFrames > 1 ? viewer.numberOfFrames : currentFrame0 + 1
      dsaUi.synthRange = { start: currentFrame0, end: nFrames - 1 }
      this.setState({
        dsaUi,
        renderModeToast: `DSA · rango F${currentFrame0 + 1}→${nFrames}`,
      })
      return
    }

    if (actionId === 'mask-auto-cardiac') {
      if (!viewer || typeof viewer.suggestDsaCardiacMask !== 'function') {
        this.setState({ renderModeToast: 'DSA · auto-máscara no disponible' })
        return
      }
      this.setState({ renderModeToast: 'DSA · estimando máscara cardíaca…' })
      viewer.suggestDsaCardiacMask().then(async (res) => {
        if (!res || !res.ok) {
          this.setState({
            renderModeToast: 'DSA · no se pudo estimar máscara',
          })
          return
        }
        const next = {
          ...(this.state.dsaUi || {}),
          enabled: true,
          maskMode: 'auto-cardiac',
          maskFrameIndex: res.maskIndex,
        }
        const method = (res.method || '').toUpperCase()
        try {
          await viewer.applyDsaState(next)
          this.setState({
            dsaUi: next,
            renderModeToast: `DSA · máscara ${method} · frame ${res.maskIndex + 1}`,
          })
        } catch (e) {
          this.setState({ renderModeToast: 'DSA · error al aplicar auto-máscara' })
        }
      })
      return
    }

    if (
      actionId === 'roadmap-toggle' ||
      actionId === 'roadmap-alpha+' ||
      actionId === 'roadmap-alpha-' ||
      actionId === 'roadmap-lut-green' ||
      actionId === 'roadmap-lut-red' ||
      actionId === 'roadmap-lut-gray'
    ) {
      if (!dsaUi.enabled) {
        this.setState({ renderModeToast: 'Activá DSA para roadmap' })
        return
      }
      if (actionId === 'roadmap-toggle') {
        dsaUi.roadmap.on = !dsaUi.roadmap.on
      } else if (actionId === 'roadmap-alpha+') {
        dsaUi.roadmap.alpha = Math.min(1, Math.round((dsaUi.roadmap.alpha + 0.1) * 10) / 10)
        dsaUi.roadmap.on = true
      } else if (actionId === 'roadmap-alpha-') {
        dsaUi.roadmap.alpha = Math.max(0, Math.round((dsaUi.roadmap.alpha - 0.1) * 10) / 10)
        dsaUi.roadmap.on = true
      } else if (actionId === 'roadmap-lut-green') {
        dsaUi.roadmap.lut = 'fluoro-green'
        dsaUi.roadmap.on = true
      } else if (actionId === 'roadmap-lut-red') {
        dsaUi.roadmap.lut = 'alert-red'
        dsaUi.roadmap.on = true
      } else if (actionId === 'roadmap-lut-gray') {
        dsaUi.roadmap.lut = 'gray'
        dsaUi.roadmap.on = true
      }
      const applyRoadmap = async () => {
        if (viewer && typeof viewer.setDsaRoadmap === 'function') {
          await viewer.setDsaRoadmap(
            dsaUi.roadmap.on,
            dsaUi.roadmap.alpha,
            dsaUi.roadmap.lut
          )
        }
        await viewer.applyDsaState(dsaUi)
        this.setState({
          dsaUi,
          renderModeToast: dsaUi.roadmap.on
            ? `Roadmap · α${dsaUi.roadmap.alpha} · ${dsaUi.roadmap.lut}`
            : 'Roadmap OFF',
        })
      }
      applyRoadmap()
      return
    }

    if (actionId === 'engine-auto' || actionId === 'engine-gpu' || actionId === 'engine-cpu') {
      dsaUi.engineMode = actionId === 'engine-gpu' ? 'gpu' : actionId === 'engine-cpu' ? 'cpu' : 'auto'
      if (!dsaUi.enabled) {
        this.setState({
          dsaUi,
          renderModeToast: `DSA engine · ${dsaUi.engineMode}`,
        })
        return
      }
    }

    if (actionId === 'export-synth-png') {
      const preview = this.state.dsaSynthPreview
      if (!preview || !preview.dataUrl) {
        this.setState({ renderModeToast: 'No hay síntesis para exportar' })
        return
      }
      const a = document.createElement('a')
      a.href = preview.dataUrl
      a.download = `dsa-synth-${preview.mode || 'minip'}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      this.setState({ renderModeToast: 'Síntesis exportada (PNG)' })
      return
    }

    if (actionId === '_apply-only') {
      // fall through to applyAndToast with current dsaUi from closure after setState race —
      // handled below via re-entry; no-op here if we got here without mutation
    }

    switch (actionId) {
      case 'toggle':
        dsaUi.enabled = !dsaUi.enabled
        if (!dsaUi.enabled) {
          resetParams()
        } else if (dsaUi.maskFrameIndex == null) {
          dsaUi.maskMode = 'first'
          dsaUi.maskFrameIndex = 0
        }
        break
      case 'mask-current':
        dsaUi.maskMode = 'current'
        dsaUi.maskFrameIndex = currentFrame0
        dsaUi.enabled = true
        break
      case 'mask-first':
        dsaUi.maskMode = 'first'
        dsaUi.maskFrameIndex = 0
        dsaUi.enabled = true
        break
      case 'invert-toggle':
        if (!dsaUi.enabled && actionId !== 'toggle') {
          /* allow only when on — keyboard gates this */
        }
        if (!dsaUi.enabled) return
        dsaUi.invertDSA = !dsaUi.invertDSA
        break
      case 'gain+':
        if (!dsaUi.enabled) return
        dsaUi.gain = Math.min(4, Math.round((dsaUi.gain + 0.25) * 100) / 100)
        break
      case 'gain-':
        if (!dsaUi.enabled) return
        dsaUi.gain = Math.max(0.25, Math.round((dsaUi.gain - 0.25) * 100) / 100)
        break
      case 'offset+':
        if (!dsaUi.enabled) return
        dsaUi.offset =
          (dsaUi.offset != null ? Number(dsaUi.offset) : 2048) + 64
        break
      case 'offset-':
        if (!dsaUi.enabled) return
        dsaUi.offset =
          (dsaUi.offset != null ? Number(dsaUi.offset) : 2048) - 64
        break
      case 'noise+':
        if (!dsaUi.enabled) return
        dsaUi.noiseFloor = Math.min(64, dsaUi.noiseFloor + 2)
        break
      case 'noise-':
        if (!dsaUi.enabled) return
        dsaUi.noiseFloor = Math.max(0, dsaUi.noiseFloor - 2)
        break
      case 'pixel-shift-x+':
        if (!dsaUi.enabled) return
        dsaUi.pixelShift = {
          x: Math.round((dsaUi.pixelShift.x + step) * 10) / 10,
          y: dsaUi.pixelShift.y,
        }
        break
      case 'pixel-shift-x-':
        if (!dsaUi.enabled) return
        dsaUi.pixelShift = {
          x: Math.round((dsaUi.pixelShift.x - step) * 10) / 10,
          y: dsaUi.pixelShift.y,
        }
        break
      case 'pixel-shift-y+':
        if (!dsaUi.enabled) return
        dsaUi.pixelShift = {
          x: dsaUi.pixelShift.x,
          y: Math.round((dsaUi.pixelShift.y + step) * 10) / 10,
        }
        break
      case 'pixel-shift-y-':
        if (!dsaUi.enabled) return
        dsaUi.pixelShift = {
          x: dsaUi.pixelShift.x,
          y: Math.round((dsaUi.pixelShift.y - step) * 10) / 10,
        }
        break
      case 'pixel-shift-reset':
        if (!dsaUi.enabled) return
        dsaUi.pixelShift = { x: 0, y: 0 }
        break
      case 'pixel-shift':
        this.setState({
          renderModeToast: 'DSA: flechas ±0.5 · Shift+flechas ±2 · R reset',
        })
        return
      case 'engine-auto':
      case 'engine-gpu':
      case 'engine-cpu':
      case '_apply-only':
        break
      case 'reset':
        dsaUi.enabled = false
        resetParams()
        break
      default:
        break
    }

    const applyAndToast = async () => {
      let toast = dsaUi.enabled
        ? dsaUi.maskMode
          ? `DSA · ${label}`
          : 'DSA activado'
        : actionId === 'reset'
          ? 'DSA restablecido'
          : 'DSA desactivado'

      if (String(actionId).indexOf('pixel-shift') === 0 && dsaUi.enabled) {
        toast = `DSA · shift ${dsaUi.pixelShift.x},${dsaUi.pixelShift.y}`
      } else if (actionId === 'invert-toggle' && dsaUi.enabled) {
        toast = dsaUi.invertDSA
          ? 'DSA · Invert LUT ON (vasos claros)'
          : 'DSA · Invert LUT OFF (vasos oscuros · clínico)'
      } else if (
        (actionId.indexOf('gain') === 0 ||
          actionId.indexOf('offset') === 0 ||
          actionId.indexOf('noise') === 0) &&
        dsaUi.enabled
      ) {
        toast = `DSA · G${dsaUi.gain} O${
          dsaUi.offset != null ? dsaUi.offset : 'auto'
        } N${dsaUi.noiseFloor}`
      } else if (String(actionId).indexOf('engine-') === 0) {
        toast = `DSA engine · ${dsaUi.engineMode}`
      } else if (option && option.label && actionId === '_apply-only') {
        toast = option.label
      }

      if (viewer && typeof viewer.applyDsaState === 'function') {
        try {
          const result = await viewer.applyDsaState(dsaUi)
          if (result && result.ok === false) {
            dsaUi.enabled = false
            dsaUi.maskMode = null
            dsaUi.maskFrameIndex = null
            if (result.reason === 'no-pixels') {
              toast =
                'DSA requiere imagen diagnóstica (DICOM). Cambiá a modo DIAGNOSTIC.'
            } else if (result.reason === 'need-multiframe') {
              toast = 'DSA requiere un run multi-frame (XA).'
            } else if (result.reason !== 'stale') {
              toast = 'DSA no disponible en este viewport.'
            }
          } else {
            if (viewer.getDsaPixelShift) {
              const sh = viewer.getDsaPixelShift()
              dsaUi.pixelShift = {
                x: Number(sh.x) || 0,
                y: Number(sh.y) || 0,
              }
            }
            if (result && result.engine && String(actionId).indexOf('engine-') === 0) {
              toast = `DSA engine · ${dsaUi.engineMode} → ${result.engine}`
            }
          }
        } catch (e) {
          console.warn('handleDsaMenuSelect applyDsaState:', e)
          toast = 'DSA: error al aplicar substracción'
          dsaUi.enabled = false
        }
      } else if (dsaUi.enabled) {
        toast = 'DSA: no hay visor activo'
        dsaUi.enabled = false
      }

      this.setState(
        quiet ? { dsaUi } : { dsaUi, renderModeToast: toast }
      )

      if (
        dsaUi.enabled &&
        viewer &&
        typeof viewer.canUseCine === 'function' &&
        viewer.canUseCine()
      ) {
        try {
          if (viewer.state && !viewer.state.visibleCinePlayer) {
            viewer.setState({ visibleCinePlayer: true })
          }
        } catch (e) {
          /* ignore */
        }
      }
    }

    applyAndToast()
  }

  /**
   * Aplica preset WW/WC al viewport (VOI clínico en frame con DICOM on-demand).
   * @param {object} preset
   * @param {number} targetIndex
   */
  _applyWwwcPresetCore = (preset, targetIndex) => {
    const activeViewer =
      this.dicomViewersRefs && this.dicomViewersRefs[targetIndex]
    if (!activeViewer || !activeViewer.image || !activeViewer.dicomImage) {
      console.warn('No hay visor activo, imagen o elemento DICOM disponible')
      return
    }

    try {
      this._navActiveIndex = targetIndex
      this._seriesLoadTargetIndex = targetIndex
      if (this.props.activeDcmIndex !== targetIndex) {
        try {
          this.props.setActiveDcmIndex(targetIndex)
        } catch (eAct) {
          /* ignore */
        }
      }

      const element = activeViewer.dicomImage
      const isWeb =
        typeof activeViewer.shouldUseCssWwwc === 'function'
          ? activeViewer.shouldUseCssWwwc()
          : typeof activeViewer.isCurrentWebPreview === 'function' &&
            activeViewer.isCurrentWebPreview()

      if (preset.name === 'Reset') {
        if (typeof activeViewer.ensureLogicalWwwcInitialized === 'function') {
          const f =
            Array.isArray(activeViewer.files) &&
            activeViewer.files[activeViewer.sliceIndex]
              ? activeViewer.files[activeViewer.sliceIndex]
              : null
          activeViewer.ensureLogicalWwwcInitialized(activeViewer.image, f)
        }
        const ref = activeViewer._wwwcRef || { ww: 400, wc: 50 }
        if (typeof activeViewer.applyClinicalVoi === 'function') {
          activeViewer.applyClinicalVoi(ref.ww, ref.wc)
        } else if (isWeb) {
          activeViewer._previewWwwc = activeViewer._previewWwwc || {
            brightness: 1,
            contrast: 1,
            invert: false,
          }
          activeViewer._previewWwwc.brightness = 1
          activeViewer._previewWwwc.contrast = 1
          activeViewer.applyPreviewWwwcCss()
        } else {
          const viewport = cornerstone.getDefaultViewportForImage(
            element,
            activeViewer.image
          )
          cornerstone.setViewport(element, viewport)
        }
      } else {
        const ww = Number(preset.ww)
        const wc = Number(preset.wc)
        if (typeof activeViewer.applyClinicalVoi === 'function') {
          activeViewer.applyClinicalVoi(ww, wc)
        } else if (isWeb) {
          if (!activeViewer._previewWwwc) {
            activeViewer._previewWwwc = {
              brightness: 1,
              contrast: 1,
              invert: false,
            }
          }
          activeViewer._previewWwwc.contrast = Math.max(
            0.15,
            Math.min(6, 400 / Math.max(1, ww))
          )
          activeViewer._previewWwwc.brightness = Math.max(
            0.15,
            Math.min(4, 1 + (50 - wc) / 400)
          )
          activeViewer.applyPreviewWwwcCss()
        } else {
          const viewport = cornerstone.getViewport(element)
          if (viewport) {
            viewport.voi.windowWidth = ww
            viewport.voi.windowCenter = wc
            cornerstone.setViewport(element, viewport)
          }
        }
      }

      try {
        const live =
          typeof activeViewer.getLogicalWwwc === 'function'
            ? activeViewer.getLogicalWwwc()
            : null
        if (live && live.ww != null && live.wc != null) {
          this.broadcastWwwcLive(targetIndex, live.ww, live.wc)
        }
      } catch (eSync) {
        /* ignore */
      }
      if (this.isSeriesLinkEffective() && this.state.seriesLinkWwwc) {
        try {
          this.syncLinkedViewports(targetIndex, {
            copyView: true,
            slices: false,
          })
        } catch (eSync2) {
          /* ignore */
        }
      }
    } catch (error) {
      console.error('Error aplicando preset WW/WC:', error)
    }

    try {
      this.toolExecute('notool')
    } catch (eTool) {
      try {
        this.setState({ toolActive: 'notool' })
        if (this.props.toolStore) this.props.toolStore('notool')
      } catch (e2) {
        /* ignore */
      }
    }
  }

  handleWWPresetSelect = async (preset) => {
    if (this.isPortalProfileSession()) return

    let targetIndex =
      typeof this.getSeriesLoadTargetIndex === 'function'
        ? this.getSeriesLoadTargetIndex()
        : typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
    if (
      !this.dicomViewersRefs ||
      !this.dicomViewersRefs[targetIndex] ||
      !this.dicomViewersRefs[targetIndex].image
    ) {
      targetIndex =
        typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
    }

    if (this.state.manifestReadiness === 'preview') {
      this.setState({
        titleMessage: 'Preset WW/WC',
        textMessage: t('renderMode.presetsNeedDiagnostic'),
        visibleMessage: true,
      })
      return
    }

    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[targetIndex]
    if (!viewer) return

    const sliceIdx =
      viewer.sliceIndex != null
        ? viewer.sliceIndex
        : this.getActiveSliceIndex()

    if (this.state.renderMode === RENDER_MODE.PREVIEW) {
      const caps = this.state.manifestMediaCaps || this._lastManifestCaps
      if (!caps || !isModeViableForCaps(RENDER_MODE.DIAGNOSTIC, caps)) {
        this.setState({
          titleMessage: 'Preset WW/WC',
          textMessage: 'Este estudio no tiene DICOM diagnóstico disponible.',
          visibleMessage: true,
        })
        return
      }
      const ok = await this.ensureFrameDiagnostic(viewer, sliceIdx, {
        silent: false,
        redisplay: true,
      })
      if (!ok) {
        this.setState({
          titleMessage: 'Preset WW/WC',
          textMessage: 'No se pudo cargar DICOM para este corte.',
          visibleMessage: true,
        })
        return
      }
      this.prefetchNeighborFramesDiagnostic(viewer, sliceIdx, {
        radius: DIAG_PREFETCH_RADIUS,
      })
    }

    this._applyWwwcPresetCore(preset, targetIndex)
    const label =
      preset && preset.name === 'Reset'
        ? 'Ventana reiniciada'
        : preset && preset.name
          ? `Preset: ${preset.name}`
          : 'Preset aplicado'
    this.setState({ renderModeToast: label })
  }

  toolChange = () => {
    const toolState = 1-this.state.toolState
    this.setState({toolState: toolState}, () => {
      this.changeTool.changeTool(this.props.tool, toolState)
    })
  }

  toolRemove = (index) => {
    this.dicomViewersRefs[this.props.activeDcmIndex].runTool('removetool', index)
  }

  toolCalibrate = (index, knownMm) => {
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!viewer || typeof viewer.calibrateFromLengthMeasurement !== 'function') return
    const ok = viewer.calibrateFromLengthMeasurement(index, knownMm)
    if (!ok) {
      this.setState({
        textMessage: 'No se pudo calibrar: trazá una línea válida e ingresá mm > 0.',
        visibleMessage: true,
      })
    }
  }

  toggleOpenMenu = () => {
    this.setState({openMenu: !this.state.openMenu})
  } 

  toggleImageEdit = () => {
    this.setState({openImageEdit: !this.state.openImageEdit})
  }

  toggleTools = () => {
    this.setState({openTools: !this.state.openTools})
  } 

  toggleMpr = () => {
    const next = !this.state.mprMenu
    if (next) {
      this.refreshMprViability()
      const v = this.state.mprViability
      if (v && !v.ok && v.message) {
        this.setState({
          titleMessage: 'MPR',
          textMessage: v.message,
          visibleMessage: true,
        })
      }
    }
    this.setState({ mprMenu: next })
  }

  refreshMprViability = (filesOverride) => {
    try {
      const viewer = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
      const files =
        filesOverride ||
        (viewer && viewer.files) ||
        this.props.files ||
        []
      const viability = assessMprViability(files, {
        renderMode: this.state.renderMode,
      })
      let profile = this.state.mprProfile || 'light'
      let cols = 512
      let rows = 512
      if (viability.ok && files.length) {
        const f0 = files.find((f) => f && f.image) || files[0]
        cols = (f0 && (f0.columns || (f0.image && f0.image.columns))) || 512
        rows = (f0 && (f0.rows || (f0.image && f0.image.rows))) || 512
        profile = resolveMprProfile({
          cols,
          rows,
          nSlices: files.length,
        })
      }
      if (viability.plane) this.mprPlane = viability.plane
      const mpr3dProfile = resolveMpr3dProfile({
        cols,
        rows,
        nSlices: files.length,
        mprBlocked: profile === 'blocked' || !viability.ok,
        sessionOff: !!this._mpr3dSessionOff,
      })
      this.setState({ mprViability: viability, mprProfile: profile, mpr3dProfile })
      return { viability, profile, mpr3dProfile }
    } catch (e) {
      const viability = {
        ok: false,
        code: 'error',
        message: 'No se pudo evaluar MPR para esta serie.',
        warnings: [],
      }
      this.setState({ mprViability: viability, mpr3dProfile: 'off' })
      return { viability, profile: 'blocked', mpr3dProfile: 'off' }
    }
  }

  disposeMpr3dVolume = () => {
    if (this._mpr3dAbort) this._mpr3dAbort.aborted = true
    if (this._mpr3dSyncTimer) {
      clearTimeout(this._mpr3dSyncTimer)
      this._mpr3dSyncTimer = null
    }
    if (this._mpr3dWwwcTimer) {
      clearTimeout(this._mpr3dWwwcTimer)
      this._mpr3dWwwcTimer = null
    }
    if (this._mpr3dWwwcRaf) {
      cancelAnimationFrame(this._mpr3dWwwcRaf)
      this._mpr3dWwwcRaf = 0
    }
    this._mpr3dWwwcPending = null
    if (this._mpr3dMprSyncTimer) {
      clearTimeout(this._mpr3dMprSyncTimer)
      this._mpr3dMprSyncTimer = null
    }
    disposeVolume(this._mpr3dVolume)
    this._mpr3dVolume = null
  }

  clearMprState = () => {
    if (this._mprEnsureAbort) this._mprEnsureAbort.aborted = true
    this._mprFillGen = (this._mprFillGen || 0) + 1
    this._mprRefLinesGen = (this._mprRefLinesGen || 0) + 1
    this._mprStackFiles = null
    this._mprLiveUsable = 0
    this._mprLiveKeepView = false
    this._mprLiveSig = ''
    if (this._mprLivePoll) {
      clearInterval(this._mprLivePoll)
      this._mprLivePoll = null
    }
    this.volume = []
    this.mprData = {}
    this._mprIndices = null
    if (this._mprPlaneLru) this._mprPlaneLru.clear()
    if (this._mprInteractTimer) {
      clearTimeout(this._mprInteractTimer)
      this._mprInteractTimer = null
    }
    this._mprInteract = false
    this.disposeMpr3dVolume()
    for (let i = 0; i < 16; i++) {
      const v = this.dicomViewersRefs && this.dicomViewersRefs[i]
      if (v) {
        v.volume = null
        v.mprData = {}
        v.isMprGenerated = false
        v._mprFitted = false
        if (v.mpr) v.mpr.sliceLocation = null
      }
    }
    // Restaurar ReferenceLines CT (verdes) fuera de MPR si estaban activas
    this._mprCtRefLinesOff = false
    this._mprAimLockUntil = 0
    this.clearMprScrollCrosshairPending()
    try {
      this._needsReferenceLinesResync = true
      if (this.state.visibleReferenceLines && ReferenceLinesManager.isUsingCornerstoneTools()) {
        // enable solo marca flag; el Synchronizer se rearma en multi-viewport
        ReferenceLinesManager.isEnabled = true
      }
    } catch (e) {
      /* ignore */
    }
    this.setState({
      mprMode: false,
      mprMenu: false,
      visibleMprOrthogonal: false,
      visibleMprCoronal: false,
      visibleMprSagittal: false,
      visibleMprAxial: false,
      visibleMpr3D: false,
      mpr3dBuilding: false,
      mpr3dClip: null,
      mpr3dDecapado: false,
      mpr3dClipAxis: 'z',
      mpr3dClipKeep: 1,
      mpr3dSlab: 8,
      mprCrosshairActive: false,
      mprCrosshairPoint: null,
      mprCrosshairPlane: null,
      visibleVolumeBuilding: false,
      mprSoftWarnShown: false,
      // Sin Link Series, el toggle WW/WL no tiene UI fuera de MPR
      seriesLinkWwwc: this.state.visibleSeriesLink
        ? this.state.seriesLinkWwwc
        : false,
    })
  }

  exitMpr = () => {
    this.clearMprState()
    this.changeLayout(1, 1)
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[0]
    if (viewer && viewer.files && viewer.files.length) {
      const mid = Math.trunc(viewer.files.length / 2)
      this.setState({ sliceIndex: mid, sliceMax: viewer.files.length }, () => {
        viewer.sliceMax = viewer.files.length
        viewer.runTool('openimage', mid)
      })
    }
  }

  /**
   * Gate común: viabilidad → perfil RAM → ensure stack → build/view.
   * @param {'orthogonal'|'sagittal'|'coronal'|'axial'} mode
   */
  beginMpr = async (mode) => {
    const { viability, profile } = this.refreshMprViability()
    if (!viability.ok) {
      this.setState({
        titleMessage: 'MPR no disponible',
        textMessage: viability.message || 'Esta serie no permite MPR.',
        visibleMessage: true,
      })
      return
    }
    if (profile === 'blocked') {
      this.setState({
        titleMessage: 'MPR',
        textMessage: mprProfileLabel('blocked'),
        visibleMessage: true,
      })
      return
    }
    if (mode === 'orthogonal' && !mprProfileAllowsOrthogonal(profile)) {
      this.setState({
        titleMessage: 'MPR',
        textMessage: 'Orthogonal no está disponible en este dispositivo.',
        visibleMessage: true,
      })
      return
    }

    if (this.state.renderMode === RENDER_MODE.PREVIEW) {
      this.setState({
        titleMessage: 'MPR',
        textMessage:
          'MPR requiere modo diagnóstico. Cambiá a Calidad diagnóstico e intentá de nuevo.',
        visibleMessage: true,
      })
      return
    }

    if (
      viability.warnings &&
      viability.warnings.length &&
      !this.state.mprSoftWarnShown
    ) {
      this.setState({
        mprSoftWarnShown: true,
        titleMessage: 'Aviso MPR',
        textMessage: viability.warnings.join(' '),
        visibleMessage: true,
      })
    }

    const viewer = this.dicomViewersRefs[this.props.activeDcmIndex]
    const files = (viewer && viewer.files) || this.props.files || []
    this._mprEnsureAbort = { aborted: false }

    this.setState({ visibleVolumeBuilding: true, mprProfile: profile })

    try {
      const ensured = await ensureMprStackReady(files, {
        signal: this._mprEnsureAbort,
        allowPartial: true,
        onProgress: () => {},
      })
      if (!ensured.ok) {
        this.setState({
          visibleVolumeBuilding: false,
          titleMessage: 'MPR',
          textMessage: ensured.error || 'No se pudo preparar la serie para MPR.',
          visibleMessage: true,
        })
        return
      }

      // Re-evaluar con metadatos completos
      const again = this.refreshMprViability(ensured.files)
      if (!again.viability.ok) {
        this.setState({
          visibleVolumeBuilding: false,
          titleMessage: 'MPR no disponible',
          textMessage: again.viability.message,
          visibleMessage: true,
        })
        return
      }
      if (again.profile === 'blocked') {
        this.setState({
          visibleVolumeBuilding: false,
          titleMessage: 'MPR',
          textMessage: mprProfileLabel('blocked'),
          visibleMessage: true,
        })
        return
      }

      this.mprPlanePosition(true)
      if (mode !== 'orthogonal' && this.state.visibleMpr3D) {
        this.disposeMpr3dVolume()
      }
      const planeFlags = {
        visibleMprOrthogonal: mode === 'orthogonal',
        visibleMprSagittal: mode === 'sagittal',
        visibleMprCoronal: mode === 'coronal',
        visibleMprAxial: mode === 'axial',
        visibleMpr3D: mode === 'orthogonal' && this.state.visibleMpr3D,
        mpr3dBuilding: mode === 'orthogonal' && this.state.mpr3dBuilding,
      }
      this.mprData.plane = { from: this.mprPlane, to: mode }
      const stack = prepareMprStackFiles(ensured.files, this.mprData)
      this._mprStackFiles = stack
      this._mprLiveUsable = stack.length
      this._mprLiveSig = this.mprUsableSig(stack)
      this.startMprLivePoller()
      this.setState(planeFlags, () => {
        // Usar stack enriquecido en viewer 0
        if (this.dicomViewersRefs[0]) {
          this.dicomViewersRefs[0].runTool('setfiles', stack)
        }
        setTimeout(() => {
          try {
            this.mprBuildVolume(again.profile)
          } catch (err) {
            console.error('mprBuildVolume', err)
            this.clearMprState()
            this.setState({
              titleMessage: 'MPR',
              textMessage:
                'Error al construir el volumen (posible falta de memoria).',
              visibleMessage: true,
            })
          }
        }, 50)
      })
    } catch (err) {
      console.error('beginMpr', err)
      this.setState({
        visibleVolumeBuilding: false,
        titleMessage: 'MPR',
        textMessage:
          (err && err.message) ||
          'Error al preparar MPR (posible falta de memoria).',
        visibleMessage: true,
      })
    }
  }

  layoutGridClick = (index) => {
    this._navActiveIndex = index
    // Click fija destino de serie del Explorer (hover solo navega)
    if (typeof index === 'number' && index >= 0) {
      this._seriesLoadTargetIndex = index
    }
    const sliceMax = this.dicomViewersRefs[index].sliceMax
    const sliceIndex = this.dicomViewersRefs[index].sliceIndex
    this._navSliceIndex = sliceIndex
    this.setState({sliceMax: sliceMax, sliceIndex: sliceIndex})

    this.props.setActiveDcmIndex(index)

    if (this.state.visibleMprOrthogonal) {
        this.setState({sliceMax: sliceMax, sliceIndex: sliceIndex})
        // Reafirmar sliceMax MPR (setfiles puede haberlo pisado con files.length)
        if (index > 0 && this.dicomViewersRefs[index]) {
          this.dicomViewersRefs[index].shouldScroll = sliceMax > 1
        }
    }  

    const dcmViewer = this.getDcmViewerRef(index)
    if (dcmViewer.image === null) return // click on empty frame

    this.props.setActiveMeasurements(dcmViewer.measurements)
    this.props.setActiveDcm(dcmViewer)
    this.props.setExplorerActiveSeriesIndex(dcmViewer.explorerIndex)

    // Repintar líneas (ligero): no scheduleReferenceLinesRefresh — bloquea nudge ~120ms
    if (
      this.state.visibleReferenceLines &&
      this.dicomViewersActive.length > 1
    ) {
      this.repaintReferenceLinesOnActiveChange()
    }
  }

  /** Activa el viewport al pasar el mouse (sin click) para poder scrollear de inmediato. */
  layoutGridHover = (index) => {
    if (typeof index !== 'number' || index < 0) return
    if (index === this.props.activeDcmIndex && this._navActiveIndex === index) return
    const dcmViewer = this.dicomViewersRefs && this.dicomViewersRefs[index]
    if (!dcmViewer || dcmViewer.image == null) return

    // Índice síncrono: el wheel puede llegar antes de que Redux/setState actualicen props
    this._navActiveIndex = index

    const sliceMax = dcmViewer.sliceMax
    const sliceIndex = dcmViewer.sliceIndex != null ? dcmViewer.sliceIndex : 0
    this._navSliceIndex = sliceIndex
    this.setState({ sliceMax, sliceIndex })
    this.props.setActiveDcmIndex(index)

    if (this.state.visibleMprOrthogonal) {
      const indices = this.getActiveMprIndices()
      if (indices.length > 1 && index !== indices[0]) {
        dcmViewer.shouldScroll = sliceMax > 1
      }
    }

    this.props.setActiveMeasurements(dcmViewer.measurements)
    this.props.setActiveDcm(dcmViewer)
    if (dcmViewer.explorerIndex != null) {
      this.props.setExplorerActiveSeriesIndex(dcmViewer.explorerIndex)
    }
    // Hide-on-active: repintar sin bloquear nudges del scroll que sigue al hover
    if (
      this.state.visibleReferenceLines &&
      this.state.hideReferenceLinesOnActiveViewport
    ) {
      this.repaintReferenceLinesOnActiveChange()
    }
  }

  getNavActiveIndex = () => {
    if (typeof this._navActiveIndex === 'number') return this._navActiveIndex
    return typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
  }

  /** Índice de celda válido para el layout actual (slots i*4+j). */
  isLayoutSlotIndex = (index, row, col) => {
    if (typeof index !== 'number' || index < 0) return false
    const r = Math.floor(index / 4)
    const c = index % 4
    const rows = row != null ? row : (this.props.layout && this.props.layout[0]) || 1
    const cols = col != null ? col : (this.props.layout && this.props.layout[1]) || 1
    return r < rows && c < cols
  }

  /**
   * Destino de carga desde Explorer: último viewport clickeado.
   * El hover no lo modifica (sigue sirviendo solo para navegación/herramientas).
   */
  getSeriesLoadTargetIndex = () => {
    const layout = this.props.layout || [1, 1]
    const pinned = this._seriesLoadTargetIndex
    if (this.isLayoutSlotIndex(pinned, layout[0], layout[1])) {
      return pinned
    }
    const active =
      typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
    if (this.isLayoutSlotIndex(active, layout[0], layout[1])) {
      return active
    }
    return 0
  }

  clampSeriesLoadTarget = (row, col) => {
    const pinned = this._seriesLoadTargetIndex
    if (this.isLayoutSlotIndex(pinned, row, col)) return
    const active =
      typeof this.props.activeDcmIndex === 'number' ? this.props.activeDcmIndex : 0
    if (this.isLayoutSlotIndex(active, row, col)) {
      this._seriesLoadTargetIndex = active
      return
    }
    this._seriesLoadTargetIndex = 0
  }

  layoutGridTouch = (index) => {
    // Eliminar la restricción que impedía la selección en desktop
    // if (!isMobile && index === this.props.activeDcmIndex) return
    
    // Usar el mismo comportamiento que layoutGridClick
    this.layoutGridClick(index)
  }

  renderMpr3dViewport = () => (
    <Mpr3dViewport
      volume={this._mpr3dVolume}
      volumeGen={this.state.mpr3dGen || 0}
      clip={this.state.mpr3dClip || { x: 0, y: 0, z: 0 }}
      clipAxis={this.state.mpr3dClipAxis || 'z'}
      keepSign={this.state.mpr3dClipKeep < 0 ? -1 : 1}
      slab={this.state.mpr3dDecapado ? (Number(this.state.mpr3dSlab) || 0) : 0}
      decapado={!!this.state.mpr3dDecapado}
      lut="bone"
      mode="dvr"
      palette={this.state.mpr3dPalette || 'general'}
      inferredPalette={this.state.mpr3dPaletteInferred || 'general'}
      colorStyle={this.state.mpr3dColorStyle || 'anatomico'}
      ww={this.state.mpr3dWw}
      wc={this.state.mpr3dWc}
      wwwcLink={!!this.state.mpr3dWwwcLink}
      onToggleWwwcLink={this.toggleMpr3dWwwcLink}
      maximized={!!this.state.mpr3dMaximized}
      onToggleMaximize={this.toggleMpr3dMaximize}
      onMeasureTool={this.mpr3dMeasureTool}
      onQuickPreset={this.mpr3dQuickPreset}
      mask={this._mpr3dMask}
      maskOn={!!this.state.mpr3dMaskOn && !!this._mpr3dMask && this.state.mpr3dSegMode !== 'mesh'}
      baseVisible={this.state.mpr3dBaseVisible !== false}
      volOff={
        this.state.mpr3dSegMode === 'mesh' ||
        (this.state.mpr3dBaseVisible === false && !this.state.mpr3dMaskOn)
      }
      quant={this.state.mpr3dQuant}
      maskError={this.state.mpr3dMaskError}
      segUrl={this.state.mpr3dSegUrl || this._mpr3dSegUrl || ''}
      onToggleBase={this.toggleMpr3dBaseVisible}
      onToggleMask={this.toggleMpr3dMaskVisible}
      onIsolateStructure={this.isolateMpr3dStructure}
      onLoadMaskFile={this.loadMpr3dMaskFile}
      isolateBox={this.state.mpr3dIsolateBox}
      isolateGen={this.state.mpr3dIsolateGen || 0}
      meshUrl={this.state.mpr3dMeshUrl}
      segMode={this.state.mpr3dSegMode || 'off'}
      mark={this.state.mpr3dMark}
      markOn={!!(this.state.visibleMpr3D && this.state.mprCrosshairPoint && this.state.mpr3dMark)}
      viewHint={this.state.mpr3dViewHint}
      viewHintGen={this.state.mpr3dViewHintGen || 0}
      building={!!this.state.mpr3dBuilding}
      buildingLabel={
        this.state.mpr3dBuilding
          ? (this.state.mpr3dBuildN
            ? `Preparando 3D ${this.state.mpr3dBuildZi}/${this.state.mpr3dBuildN}…`
            : `Preparando 3D (${mpr3dPresetLabel(this.state.mpr3dPalette)})…`)
          : ''
      }
      onPalette={this.setMpr3dPalette}
      onColorStyle={this.setMpr3dColorStyle}
      onWwwc={this.setMpr3dWwwc}
      onWwwcFromMpr={this.syncMpr3dWwwcFromMpr}
      onToggleDecapado={this.toggleMpr3dDecapado}
      onClipAxis={this.setMpr3dClipAxis}
      onToggleKeep={this.toggleMpr3dClipKeep}
      onClipNudge={this.nudgeMpr3dClip}
      onClipTo={this.setMpr3dClipTo}
      onClipDelta={this.nudgeMpr3dClipBy}
      onSlab={this.setMpr3dSlab}
      onSlabNudge={this.nudgeMpr3dSlab}
      onError={() => {
        this._mpr3dSessionOff = true
        this.hideMpr3D()
        this.setState({
          mpr3dProfile: 'off',
          titleMessage: '3D',
          textMessage: mpr3dProfileLabel('off'),
          visibleMessage: true,
        })
      }}
    />
  )

  renderPdfViewport = (index) => {
    const files = (this.pdfFilesBySlot && this.pdfFilesBySlot[index]) || []
    const active =
      typeof this.props.activeDcmIndex === 'number' &&
      this.props.activeDcmIndex === index
    return (
      <PdfViewport
        key={`pdf-${index}-${(files[0] && files[0].sopInstanceUID) || index}`}
        files={files}
        active={active}
        layoutIndex={index}
      />
    )
  }

  setViewportKind = (index, kind, pdfFiles) => {
    if (!this.viewportKind) this.viewportKind = new Array(16).fill('dicom')
    if (!this.pdfFilesBySlot) this.pdfFilesBySlot = {}
    const i = Math.max(0, Math.min(15, Math.floor(Number(index) || 0)))
    const next = kind === 'pdf' ? 'pdf' : 'dicom'
    this.viewportKind[i] = next
    if (next === 'pdf') {
      this.pdfFilesBySlot[i] = Array.isArray(pdfFiles) ? pdfFiles : []
    } else {
      delete this.pdfFilesBySlot[i]
    }
    this.setState((s) => ({
      viewportKindEpoch: (s.viewportKindEpoch || 0) + 1,
    }))
  }

  buildLayoutGrid = () => {
    let dicomviewers = []
    const skip3dCell =
      this.state.visibleMpr3D &&
      !isPhoneLikeDevice() &&
      !this.state.mpr3dMaximized &&
      this.props.layout[0] === 2 &&
      this.props.layout[1] === 2
    const slot3d = this.getMpr3dCellIndex()
    for(let i=0; i < this.props.layout[0]; i++) {
      for(let j=0; j < this.props.layout[1]; j++) {
        const styleLayoutGrid = {
          border: this.props.layout[0] === 1 && this.props.layout[1] === 1 ? 'solid 1px #000000' : 'solid 1px #444444',
          cursor: 'pointer', // Añadir cursor pointer para mejor UX
          position: 'relative', // Asegurar que el viewport sea clickeable
          zIndex: 1 // Asegurar que el viewport esté por encima de otros elementos
        }
        const index = i*4+j
        const is3dCell = skip3dCell && index === slot3d
        const isPdfCell =
          !is3dCell &&
          this.viewportKind &&
          this.viewportKind[index] === 'pdf'
        dicomviewers.push(
          <div 
            key={index} 
            style={styleLayoutGrid} 
            onMouseEnter={() => {
              if (!is3dCell) this.layoutGridHover(index)
            }}
            onClick={(e) => {
              if (!is3dCell) this.handleViewportTap(index, e)
            }}
            onTouchStart={(e) => {
              if (!is3dCell) this.handleViewportTap(index, e)
            }}
          >
            {is3dCell
              ? this.renderMpr3dViewport()
              : isPdfCell
                ? this.renderPdfViewport(index)
                : this.getDcmViewer(index)}
          </div>        
        )
      }
    }

    return (
      <div
        id="dicomviewer-grid"
        style={{
          display: 'grid',
          gridTemplateRows: `repeat(${this.props.layout[0]}, ${100 / this.props.layout[0]}%)`,
          gridTemplateColumns: `repeat(${this.props.layout[1]}, ${100 / this.props.layout[1]}%)`,
          height: '100%',
          width: '100%',
          position: 'relative' // Asegurar que el grid sea clickeable
        }}
      >
        {dicomviewers}
        <CompareModeGuide
          open={!!this.state.compareMode}
          guideOpen={!!this.state.compareGuideOpen}
          onDismiss={this.dismissCompareGuide}
          onExit={() => this.setCompareMode(false)}
        />
      </div>
    )
  }

  getStringVisiblePlane = () => {
    if (this.state.visibleMprOrthogonal) 
      return 'orthogonal'    
    else if (this.state.visibleMprSagittal) 
      return 'sagittal'
    else if (this.state.visibleMprAxial) 
      return 'axial'
    else if (this.state.visibleMprCoronal) 
      return 'coronal'
  }

  getClassicToolbarTier = (width = this.state.viewportWidth) => {
    const w = Number(width) || 1440
    return {
      /** Export/QA van al menú Más */
      collapseExport: w < 1440,
      /** Estudio (salvo Gateway) va al menú Más */
      collapseStudy: w < 1280,
    }
  }

  openClassicMoreMenu = (event) => {
    this.setState({ anchorElClassicMore: event.currentTarget })
  }

  closeClassicMoreMenu = () => {
    this.setState({ anchorElClassicMore: null })
  }

  openClassicMprSlabMenu = (event) => {
    this.setState({ anchorElClassicMprSlab: event.currentTarget })
  }

  closeClassicMprSlabMenu = () => {
    this.setState({ anchorElClassicMprSlab: null })
  }

  getMprSlabSummaryLabel = () => {
    const mode = this.state.mprSlabMode || 'thin'
    const mm = Number(this.state.mprSlabMm) || 0
    const modeLabel =
      mode === 'mip'
        ? 'MIP'
        : mode === 'minip'
          ? 'MinIP'
          : mode === 'avg'
            ? 'Avg'
            : 'Fino'
    if (mode === 'thin' || mm === 0) return modeLabel
    return `${modeLabel} ${mm}`
  }

  appBarTitle = (classes, isOpen, dcmViewer, planLabel) => {
    const plane = this.getStringVisiblePlane()
    const showMprTitle =
      isOpen &&
      this.state.sliceMax > 1 &&
      this.mprPlane !== '' &&
      plane &&
      this.mprPlane !== plane
    const { title, subtitle } = this.getStudioTitleParts(dcmViewer)
    const studyTitle = showMprTitle
      ? `MPR ${plane}`
      : title || (isOpen ? '' : '')
    const studySub = showMprTitle ? '' : subtitle
    const showStudy = Boolean(studyTitle || studySub)
    return (
      <div className="udv-classic-topbar-brand-wrap">
        <div className="udv-classic-topbar-brand">
          <span className="udv-classic-topbar-brand-mark">UDV</span>
          {planLabel ? (
            <span className="udv-classic-topbar-brand-mode">{planLabel}</span>
          ) : null}
        </div>
        {showStudy ? (
          <div className="udv-classic-topbar-study">
            {studyTitle ? (
              <div className="udv-classic-topbar-study-title" title={studyTitle}>
                {studyTitle}
              </div>
            ) : null}
            {studySub ? (
              <div className="udv-classic-topbar-study-sub" title={studySub}>
                {studySub}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }

  // ---------------------------------------------------------------------------------------------- LINK SERIES
  // #region LINK SERIES

  seriesLinkToggle = () => {
    // En MPR el link espacial no aplica; usar solo Link WW/WL
    if (this.state.mprMode || (this.isMprLayoutActive && this.isMprLayoutActive())) {
      return
    }
    const next = !this.state.visibleSeriesLink
    const patch = { visibleSeriesLink: next }
    if (!next) {
      patch.seriesLinkWwwc = false
    } else {
      // Link Series solo sincroniza cortes; Link WW/WL queda OFF (opt-in)
      patch.seriesLinkWwwc = false
    }
    this.setState(patch, () => {
      if (next && !this.state.mprMode) {
        this.syncLinkedViewports(this.props.activeDcmIndex, {
          copyView: true,
          slices: true,
        })
      }
    })
  }

  /**
   * Toggle independiente de sync WW/WC (Link Series o planos MPR).
   */
  seriesLinkWwwcToggle = () => {
    const inMpr =
      this.state.mprMode ||
      (this.isMprLayoutActive && this.isMprLayoutActive())
    if (!this.state.visibleSeriesLink && !inMpr) return
    this.setState({ seriesLinkWwwc: !this.state.seriesLinkWwwc }, () => {
      if (!this.state.seriesLinkWwwc) return
      const masterIndex =
        typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
      const master =
        this.dicomViewersRefs && this.dicomViewersRefs[masterIndex]
      if (
        master &&
        typeof master.getLogicalWwwc === 'function'
      ) {
        try {
          const live = master.getLogicalWwwc()
          if (live && live.ww != null && live.wc != null) {
            this.broadcastWwwcLive(masterIndex, live.ww, live.wc)
          }
        } catch (e) {
          /* ignore */
        }
      }
      if (!inMpr) {
        this.syncLinkedViewports(masterIndex, {
          copyView: true,
          slices: false,
        })
      }
    })
  }

  /** ¿Se puede mostrar/usar el toggle Link WW/WL? */
  isWwwcLinkAvailable = () => {
    if (this.state.mprMode || (this.isMprLayoutActive && this.isMprLayoutActive())) {
      return true
    }
    return !!this.state.visibleSeriesLink
  }

  isSeriesLinkEffective = () => {
    return !!this.state.visibleSeriesLink && !this.state.mprMode
  }

  getSeriesLinkOffsetForViewer = (layoutIndex) => {
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[layoutIndex]
    if (!viewer) return 0
    const key = seriesKeyFromViewer(viewer)
    const n = this.state.seriesLinkOffsets && this.state.seriesLinkOffsets[key]
    return Number(n) || 0
  }

  isViewerSeriesLinkMember = (layoutIndex) => {
    if (!this.isSeriesLinkEffective()) return false
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[layoutIndex]
    if (!viewer || viewer.isMprGenerated) return false
    const members = this.collectLinkedViewers(this.props.activeDcmIndex)
    if (layoutIndex === this.props.activeDcmIndex) return members.length > 0
    return members.some((v) => {
      const idx =
        v.layoutIndex != null
          ? v.layoutIndex
          : v.props && v.props.index
      return idx === layoutIndex
    })
  }

  collectLinkedViewers = (masterIndex) => {
    const refs = this.dicomViewersRefs || []
    const master = refs[masterIndex]
    if (!master || !master.files || !master.files.length) return []
    if (master.isMprGenerated) return []
    const keys = this.state.seriesLinkKeys
    const geoM = stackGeometry(master.files)
    const isOpen = this.props.isOpen
    const out = []
    for (let i = 0; i < refs.length; i++) {
      const v = refs[i]
      if (!v || v === master) continue
      if (Array.isArray(isOpen) && !isOpen[i]) continue
      if (v.isMprGenerated) continue
      if (!v.files || !v.files.length) continue
      const key = seriesKeyFromViewer(v)
      if (Array.isArray(keys)) {
        if (keys.indexOf(key) >= 0) out.push(v)
        continue
      }
      const geoS = stackGeometry(v.files)
      if (geoM.normal && geoS.normal) {
        if (samePlane(geoM.normal, geoS.normal)) out.push(v)
      } else {
        out.push(v)
      }
    }
    return out
  }

  handleSeriesLinkToggleMember = (layoutIndex) => {
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[layoutIndex]
    if (!viewer) return
    const key = seriesKeyFromViewer(viewer)
    const auto = this.collectLinkedViewers(this.props.activeDcmIndex)
    const current = Array.isArray(this.state.seriesLinkKeys)
      ? this.state.seriesLinkKeys.slice()
      : auto.map((v) => seriesKeyFromViewer(v))
    const pos = current.indexOf(key)
    if (pos >= 0) current.splice(pos, 1)
    else current.push(key)
    this.setState({ seriesLinkKeys: current }, () => {
      this.syncLinkedViewports(this.props.activeDcmIndex, { copyView: false })
    })
  }

  handleSeriesLinkOffset = (layoutIndex, delta) => {
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[layoutIndex]
    if (!viewer) return
    const key = seriesKeyFromViewer(viewer)
    const prev = { ...(this.state.seriesLinkOffsets || {}) }
    prev[key] = (Number(prev[key]) || 0) + (Number(delta) || 0)
    this.setState({ seriesLinkOffsets: prev }, () => {
      this.syncLinkedViewports(this.props.activeDcmIndex, { copyView: false })
    })
  }

  scheduleSeriesLinkSync = (masterIndex) => {
    if (!this.isSeriesLinkEffective()) return
    this._pendingSeriesLinkMaster = masterIndex
    // Ya hay un tick armado: solo actualizar el master pendiente (NO cancelar).
    // Cancelar en cada paint hacía que el sync nunca corriera en arrastre rápido.
    if (this._seriesLinkSyncArmed) {
      this._seriesLinkSyncDirty = true
      return
    }
    this._seriesLinkSyncArmed = true
    this._seriesLinkSyncDirty = false
    const tick = () => {
      this._syncNavRaf = null
      this._seriesLinkSyncArmed = false
      const idx =
        this._pendingSeriesLinkMaster != null
          ? this._pendingSeriesLinkMaster
          : masterIndex
      const master =
        this.dicomViewersRefs && this.dicomViewersRefs[idx]
      const live = !!(
        master &&
        typeof master.isStackScrollLive === 'function' &&
        master.isStackScrollLive()
      )
      try {
        this.syncLinkedViewports(idx, { copyView: false, live })
      } catch (e) {
        /* ignore */
      }
      if (this._seriesLinkSyncDirty) {
        this._seriesLinkSyncDirty = false
        this.scheduleSeriesLinkSync(this._pendingSeriesLinkMaster)
      }
    }
    if (typeof requestAnimationFrame === 'function') {
      this._syncNavRaf = requestAnimationFrame(tick)
    } else {
      this._syncNavTimer = setTimeout(tick, 16)
    }
  }

  scheduleSeriesLinkViewSync = (masterIndex) => {
    if (!this.isSeriesLinkEffective()) return
    if (this._seriesLinkSyncing) return
    if (this._seriesLinkViewTimer) clearTimeout(this._seriesLinkViewTimer)
    this._seriesLinkViewTimer = setTimeout(() => {
      this._seriesLinkViewTimer = null
      try {
        this.syncLinkedViewports(masterIndex, { copyView: true, slices: false })
      } catch (e) {
        /* ignore */
      }
    }, 40)
  }

  captureViewerViewState = (viewer) => {
    if (!viewer || !viewer.dicomImage) return null
    try {
      const vp = window.cornerstone && window.cornerstone.getViewport(viewer.dicomImage)
      if (typeof viewer.cloneViewportViewState === 'function') {
        const cloned = viewer.cloneViewportViewState(vp)
        if (cloned && typeof viewer.getLogicalWwwc === 'function') {
          const logical = viewer.getLogicalWwwc()
          if (logical) {
            cloned.ww = logical.ww
            cloned.wc = logical.wc
          }
        }
        return cloned
      }
    } catch (e) {
      return null
    }
    return null
  }

  syncLinkedViewports = (masterIndex, opts = {}) => {
    if (!this.isSeriesLinkEffective()) return
    if (this._seriesLinkSyncing) return
    const master =
      this.dicomViewersRefs &&
      this.dicomViewersRefs[masterIndex != null ? masterIndex : this.props.activeDcmIndex]
    if (!master || !master.files || !master.files.length) return
    const slaves = this.collectLinkedViewers(master.layoutIndex != null ? master.layoutIndex : masterIndex)
    if (!slaves.length) return
    const copyView = opts.copyView !== false
    const doSlices = opts.slices !== false
    const live = !!opts.live
    const viewState = copyView ? this.captureViewerViewState(master) : null
    this._seriesLinkSyncing = true
    const driven = []
    try {
      for (let i = 0; i < slaves.length; i++) {
        const slave = slaves[i]
        if (!slave) continue
        const key = seriesKeyFromViewer(slave)
        const offset = (this.state.seriesLinkOffsets && this.state.seriesLinkOffsets[key]) || 0
        if (doSlices) {
          const hit = mapMasterSliceToSlave(master, slave, offset)
          if (hit && hit.index >= 0 && hit.index !== slave.sliceIndex) {
            try {
              slave._seriesLinkDriven = true
              slave._seriesLinkDrivenGen = (slave._seriesLinkDrivenGen || 0) + 1
              driven.push({ slave, gen: slave._seriesLinkDrivenGen })
              // Arrastre/rueda en vivo: paint ligero (mismo path que el master)
              if (
                live &&
                typeof slave.paintWheelSeek === 'function' &&
                !slave.isMprGenerated
              ) {
                slave.paintWheelSeek(hit.index)
              } else {
                slave.runTool('openimage', hit.index)
              }
            } catch (e) {
              /* ignore */
            }
          }
        }
        // Durante scroll en vivo no copiar pan/zoom/WW cada frame (cuesta FPS)
        if (!live && viewState && typeof slave.applyLinkedViewState === 'function') {
          try {
            slave.applyLinkedViewState(viewState, {
              wwwc: !!this.state.seriesLinkWwwc,
            })
          } catch (e2) {
            /* ignore */
          }
        }
      }
    } finally {
      this._seriesLinkSyncing = false
      // Mantener flag un frame más para que onScrollSlicePaint del slave no re-linke.
      // Generación: un clear viejo no apaga un sync más reciente (arrastre rápido).
      if (driven.length) {
        const clear = () => {
          for (let d = 0; d < driven.length; d++) {
            const entry = driven[d]
            try {
              if (entry.slave && entry.slave._seriesLinkDrivenGen === entry.gen) {
                entry.slave._seriesLinkDriven = false
              }
            } catch (e3) {
              /* ignore */
            }
          }
        }
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => {
            setTimeout(clear, live ? 48 : 50)
          })
        } else {
          setTimeout(clear, live ? 48 : 50)
        }
      }
    }
  }

  // ---------------------------------------------------------------------------------------------- REFERENCE LINES
  // #region REFERENCE LINES

  referenceLinesToggle = () => {
    const visible = !this.state.visibleReferenceLines
    this.setState({ visibleReferenceLines: visible }, () => {
      if (!visible) {
        this.cancelReferenceLinesPendingSetup()
        ReferenceLinesManager.disable()
        // MPR: ocultar también las líneas de correlación H/V
        try {
          this.refreshMprCorrelationOverlay(this.state.mprCrosshairPoint)
        } catch (e) {
          /* ignore */
        }
        return
      }
      try {
        this.getActiveDcmViewers()
      } catch (e) { /* ignore */ }
      try {
        this.refreshMprCorrelationOverlay(this.state.mprCrosshairPoint)
      } catch (e2) {
        /* ignore */
      }
      // MPR: solo cruz amarilla; no armar ReferenceLines CT
      if (this.isMprLayoutActive()) {
        this.disableCornerstoneReferenceLinesForMpr()
        return
      }
      if (this.dicomViewersActive.length <= 1) {
        return
      }

      // Armar ya (PREVIEW: manifest IPP/IOP mientras descarga)
      this.finishPreviewReferenceLinesSetup()
    })
  }

  hideReferenceLinesOnActiveToggle = () => {
    const next = !this.state.hideReferenceLinesOnActiveViewport
    this.setState({ hideReferenceLinesOnActiveViewport: next }, () => {
      this.patchReferenceLinesRenderer()
      this.scheduleReferenceLinesRefresh()
    })
  }

referenceLinesShow = () => {
    // Personalizado desactivado; delegar a CornerstoneTools
    if (this.state.visibleReferenceLines && this.dicomViewersActive.length >= 2) {
      this.setupCornerstoneToolsReferenceLines()
    }
}

referenceLinesHide = () => {
    ReferenceLinesManager.disable()
}

  // #endregion 

  // ---------------------------------------------------------------------------------------------- MPR
  // #region MPR

  mprBuildVolume = (profileOverride, opts = {}) => {
    //console.log('mprBuildVolume: ', this.state.sliceIndex)
    const profile = profileOverride || this.state.mprProfile || 'full'

    // "For the specific case of dual-echo MR images select files with same EchoNumber tag of selected image.
    // see https://groups.google.com/forum/#!topic/comp.protocols.dicom/zh2TzgbjvdE    
    const echoViewer =
      this.dicomViewersRefs[0] || this.dicomViewersRefs[this.props.activeDcmIndex]
    const echoNumber = getDicomEchoNumber(echoViewer && echoViewer.image)

    if (this.volume.length > 0 && echoNumber === this.echoNumber && profile === 'full' && !opts.force) {
      this.setState({ visibleVolumeBuilding: false }, () => {
        if (this.state.visibleMprOrthogonal) this.changeToOrthogonalView()
        else if (this.state.visibleMprSagittal) this.changeToSagittalView()
        else if (this.state.visibleMprCoronal) this.changeToCoronalView()
        else this.changeToAxialView()
      })
      return
    }

    this.t0 = performance.now()

    const allFiles =
      (this._mprStackFiles && this._mprStackFiles.length
        ? this._mprStackFiles
        : (this.dicomViewersRefs[0] && this.dicomViewersRefs[0].files) || [])
    let files = allFiles.filter((a) => a && a.series && a.series.echoNumber === echoNumber)
    if (!files.length) files = allFiles
    files = prepareMprStackFiles(files, this.mprData)
    this._mprStackFiles = files
    if (this.dicomViewersRefs[0]) {
      this.dicomViewersRefs[0].runTool('setfiles', files)
    }
    if (files.length < allFiles.length) {
      this.echoNumber = echoNumber
    }

    const sliceIndex = Math.min(
      this.state.sliceIndex || 0,
      Math.max(0, files.length - 1)
    )
    const img0 = files[sliceIndex].image
    const xPixelSpacing = parseFloat(getDicomPixelSpacing(img0, 1)) || img0.columnPixelSpacing || 1
    const yPixelSpacing = parseFloat(getDicomPixelSpacing(img0, 0)) || img0.rowPixelSpacing || 1
    const spacingBetweenSlice = getDicomSpacingBetweenSlice(img0)
    const zSpacingNative = computeZSpacingForVolume(files, sliceIndex)

    this.mprData.columnPixelSpacing = xPixelSpacing
    this.mprData.rowPixelSpacing = yPixelSpacing
    ensureMprStackGeometry(files, this.mprData)

    // ---- Light profile: sin volumen isótropo; planos on-demand desde files ----
    if (profile === 'light') {
      this.volume = []
      this.mprData.zDim = files.length
      this.mprData.zStep = 1
      this.mprData.indexMax = files.length
      this.mprData.instanceNumberOrder = 1
      if (!(this.mprData.zSpacing > 0)) this.mprData.zSpacing = zSpacingNative
      this.mprData.light = true
      const index = opts.keepIndex != null
        ? Math.max(0, Math.min(files.length - 1, Number(opts.keepIndex) || 0))
        : Math.round(files.length / 2)
      this.setState({ sliceIndex: index, visibleVolumeBuilding: false, mprProfile: profile }, () => {
        if (this.state.visibleMprOrthogonal) {
          this.changeToOrthogonalView()
        } else if (this.state.visibleMprSagittal) {
          this.changeToSagittalView()
        } else if (this.state.visibleMprCoronal) {
          this.changeToCoronalView()
        } else {
          this.changeToAxialView()
        }
      })
      return
    }

    this.mprData.light = false

    // Siempre 1:1 con el stack (sin resample isótropo a N×matrix).
    // Resamplear 321→512 con spacing incorrecto estiraba los planos en Z.
    const zStepMm = zSpacingNative || spacingBetweenSlice || xPixelSpacing
    this.mprData.zDim = files.length
    this.mprData.zStep = 1
    if (!(this.mprData.zSpacing > 0)) {
      this.mprData.zSpacing = zStepMm > 0 ? zStepMm : xPixelSpacing
    }
    this.mprData.indexMax = files.length
    this.mprData.instanceNumberOrder =
      files.length >= 2 &&
      files[0].instanceNumber != null &&
      files[1].instanceNumber != null &&
      files[0].instanceNumber > files[1].instanceNumber
        ? -1
        : 1

    this.volume = []
    for (let i = 0, len = files.length; i < len; i++) {
      const img = files[i] && files[i].image
      if (img && typeof img.getPixelData === 'function') {
        this.volume.push(img.getPixelData())
      } else {
        this.volume.push(null)
      }
    }
    // Rellenar huecos con vecino
    let last = null
    for (let i = 0; i < this.volume.length; i++) {
      if (this.volume[i]) last = this.volume[i]
      else if (last) this.volume[i] = last
    }
    for (let i = this.volume.length - 1; i >= 0; i--) {
      if (this.volume[i]) last = this.volume[i]
      else if (last) this.volume[i] = last
    }

    this.t1 = performance.now()
    console.log(
      `performance volume building: ${this.t1 - this.t0} ms (zDim=${this.mprData.zDim}, zSpacing=${this.mprData.zSpacing})`
    )

    const index = opts.keepIndex != null
      ? Math.max(0, Math.min(files.length - 1, Number(opts.keepIndex) || 0))
      : Math.round(files.length / 2)
    this.setState({sliceIndex: index, mprProfile: profile}, () => {
      if (this.state.visibleMprOrthogonal) {
        this.changeToOrthogonalView()

      } else if (this.state.visibleMprSagittal) {
        this.changeToSagittalView()

      } else if (this.state.visibleMprCoronal) {
        this.changeToCoronalView()  

      } else { // axial
        this.changeToAxialView()
      }      
    })
          
  }


  /** Portrait = alto >= ancho (móvil en vertical). */
  isPortraitOrientation = () => {
    try {
      if (typeof window.matchMedia === 'function') {
        if (window.matchMedia('(orientation: portrait)').matches) return true
        if (window.matchMedia('(orientation: landscape)').matches) return false
      }
    } catch (e) { /* ignore */ }
    return (window.innerHeight || 0) >= (window.innerWidth || 0)
  }

  /** Dual MPR: portrait apilado 2×1; landscape lado a lado 1×2 */
  getMprDualLayoutDims = () => (this.isPortraitOrientation() ? [2, 1] : [1, 2])

  /** Orthogonal: portrait 3×1; landscape 1×3. Con 3D en tablet/desktop: 2×2. */
  getMprOrthoLayoutDims = () => {
    if (
      this.state.visibleMpr3D &&
      this.state.visibleMprOrthogonal &&
      !isPhoneLikeDevice()
    ) {
      return [2, 2]
    }
    return this.isPortraitOrientation() ? [3, 1] : [1, 3]
  }

  getMpr3dCellIndex = () => 5

  getMprDicomIndices = () => {
    const [rows, cols] = this.getMprOrthoLayoutDims()
    const all = this.getMprLayoutIndices(rows, cols)
    if (this.state.visibleMpr3D && !isPhoneLikeDevice()) {
      const skip = this.getMpr3dCellIndex()
      return all.filter((i) => i !== skip)
    }
    return all
  }

  /**
   * Índices de celdas en la grilla fija 4×4 (stride 4).
   * 1×2 → [0,1]; 2×1 → [0,4]; 1×3 → [0,1,2]; 3×1 → [0,4,8]
   */
  getMprLayoutIndices = (rows, cols) => {
    const r = Math.max(1, Number(rows) || 1)
    const c = Math.max(1, Number(cols) || 1)
    const out = []
    for (let i = 0; i < r; i++) {
      for (let j = 0; j < c; j++) out.push(i * 4 + j)
    }
    return out
  }

  getMprDualIndices = () => {
    const [rows, cols] = this.getMprDualLayoutDims()
    return this.getMprLayoutIndices(rows, cols)
  }

  getMprOrthoIndices = () => this.getMprDicomIndices()

  /** Índices MPR activos según flags actuales (o layout vivo). */
  getActiveMprIndices = () => {
    if (this._mprIndices && this._mprIndices.length) return this._mprIndices
    if (this.state.visibleMprOrthogonal) return this.getMprOrthoIndices()
    if (
      this.state.visibleMprSagittal ||
      this.state.visibleMprCoronal ||
      this.state.visibleMprAxial
    ) {
      return this.getMprDualIndices()
    }
    return [0]
  }

  /** Stack MPR: cache de beginMpr o primer viewer con files. */
  getMprStackFiles = () => {
    if (this._mprStackFiles && this._mprStackFiles.length) return this._mprStackFiles
    const prefer = [0].concat(this._mprIndices || [], [1, 2, 4, 8])
    const seen = {}
    for (let k = 0; k < prefer.length; k++) {
      const idx = prefer[k]
      if (seen[idx]) continue
      seen[idx] = true
      const v = this.dicomViewersRefs && this.dicomViewersRefs[idx]
      if (v && v.files && v.files.length) {
        this._mprStackFiles = prepareMprStackFiles(v.files, this.mprData)
        return this._mprStackFiles
      }
    }
    return null
  }

  isMprLiveSession = () =>
    !!(
      this.state.visibleMprOrthogonal ||
      this.state.visibleMprSagittal ||
      this.state.visibleMprCoronal ||
      this.state.visibleMprAxial ||
      this.state.visibleMpr3D
    )

  collectMprUsableFiles = (files) => {
    if (!Array.isArray(files) || !files.length) return []
    const out = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!f || !f.image || typeof f.image.getPixelData !== 'function') continue
      try {
        enrichFileGeometry(f)
      } catch (e) { /* ignore */ }
      out.push(f)
    }
    return out
  }

  getMprLiveSourceFiles = () => {
    const seed = (this._mprStackFiles && this._mprStackFiles[0]) ||
      (this.dicomViewersRefs[0] && this.dicomViewersRefs[0].files && this.dicomViewersRefs[0].files[0])
    const uid = seed ? this.getFileSeriesId(seed) : null
    if (uid) {
      const series = this.getSeriesFilesFromStore(uid)
      if (series && series.length) return series
    }
    const v0 = this.dicomViewersRefs && this.dicomViewersRefs[0]
    if (v0 && v0.files && v0.files.length) return v0.files
    return this.props.files || []
  }

  mprUsableSig = (files) => {
    if (!Array.isArray(files) || !files.length) return '0'
    const first = this.getFileFrameKey(files[0]) || ''
    const last = this.getFileFrameKey(files[files.length - 1]) || ''
    const mid = this.getFileFrameKey(files[Math.floor(files.length / 2)]) || ''
    return `${files.length}:${first}:${mid}:${last}`
  }

  startMprLivePoller = () => {
    if (this._mprLivePoll) return
    this._mprLivePoll = setInterval(() => {
      if (!this.isMprLiveSession() || this._unmounted) {
        this.stopMprLivePoller()
        return
      }
      this.scheduleMprLiveRefresh()
    }, 700)
  }

  stopMprLivePoller = () => {
    if (this._mprLivePoll) {
      clearInterval(this._mprLivePoll)
      this._mprLivePoll = null
    }
  }

  scheduleMprLiveRefresh = () => {
    if (!this.isMprLiveSession()) return
    if (this.state.renderMode === RENDER_MODE.PREVIEW) return
    if (this._mprLiveTimer) return
    this._mprLiveTimer = setTimeout(() => {
      this._mprLiveTimer = null
      this.applyMprLiveRefresh().catch((e) => {
        console.warn('MPR live refresh', e)
      })
    }, 280)
  }

  applyMprLiveRefresh = async () => {
    if (this._mprLiveBusy || this._unmounted) return
    if (!this.isMprLiveSession()) return
    const src = this.getMprLiveSourceFiles()
    const usable = await collectReadyMprFiles(src)
    if (usable.length < 3) return

    const sig = this.mprUsableSig(usable)
    if (sig === this._mprLiveSig) {
      if (usable.length >= src.length) this.stopMprLivePoller()
      return
    }

    this._mprLiveBusy = true
    const prev = this._mprStackFiles || []
    const usableSorted = prepareMprStackFiles(usable, this.mprData)
    const keepIndex = this.remapSliceIndexByIdentity(
      prev.length ? prev : usableSorted,
      this.state.sliceIndex || 0,
      usableSorted
    )
    if (this.state.mprCrosshairPoint && prev.length) {
      const p = { ...this.state.mprCrosshairPoint }
      p.z = this.remapSliceIndexByIdentity(prev, p.z, usableSorted)
      this.setState({ mprCrosshairPoint: p })
    }
    this._mprStackFiles = usableSorted
    this._mprLiveUsable = usableSorted.length
    this._mprLiveSig = sig
    this._mprLiveKeepView = true
    try {
      this.rebuildMprVolumePixels(usableSorted)
      this.refreshMprViewportsInPlace(usableSorted, keepIndex)
      if (this.state.visibleMpr3D) this.scheduleMpr3dLiveRebuild()
      if (usable.length >= src.length) this.stopMprLivePoller()
    } finally {
      this._mprLiveBusy = false
    }
  }

  rebuildMprVolumePixels = (files) => {
    if (!files || !files.length) return
    const profile = this.state.mprProfile || 'full'
    const sliceIndex = Math.min(
      this.state.sliceIndex || 0,
      Math.max(0, files.length - 1)
    )
    const img0 = files[sliceIndex] && files[sliceIndex].image
    if (img0) {
      this.mprData.columnPixelSpacing =
        parseFloat(getDicomPixelSpacing(img0, 1)) || img0.columnPixelSpacing || this.mprData.columnPixelSpacing
      this.mprData.rowPixelSpacing =
        parseFloat(getDicomPixelSpacing(img0, 0)) || img0.rowPixelSpacing || this.mprData.rowPixelSpacing
    }
    ensureMprStackGeometry(files, this.mprData)
    this.mprData.zDim = files.length
    this.mprData.zStep = 1
    this.mprData.indexMax = files.length
    if (!(this.mprData.zSpacing > 0) && img0) {
      this.mprData.zSpacing = computeZSpacingForVolume(files, sliceIndex) || img0.columnPixelSpacing || 1
    }
    if (profile === 'light') {
      this.volume = []
      this.mprData.light = true
      return
    }
    this.mprData.light = false
    this.volume = []
    for (let i = 0; i < files.length; i++) {
      const img = files[i] && files[i].image
      this.volume.push(img && typeof img.getPixelData === 'function' ? img.getPixelData() : null)
    }
    let last = null
    for (let i = 0; i < this.volume.length; i++) {
      if (this.volume[i]) last = this.volume[i]
      else if (last) this.volume[i] = last
    }
    for (let i = this.volume.length - 1; i >= 0; i--) {
      if (this.volume[i]) last = this.volume[i]
      else if (last) this.volume[i] = last
    }
  }

  syncMprSlabMeta = () => {
    if (!this.mprData) this.mprData = {}
    const prof = resolveSlabProfile(detectHardware(), this.state.mprProfile)
    this.mprData.slabMode = this.state.mprSlabMode || 'thin'
    this.mprData.slabMm = Number(this.state.mprSlabMm) || 0
    this.mprData.slabMaxHalfWidth = prof.maxHalfWidth
    this.mprData.slabForceThin = !!this._mprInteract
    return this.mprData
  }

  wantsMprSlab = () => {
    const mode = this.state.mprSlabMode || 'thin'
    const mm = Number(this.state.mprSlabMm) || 0
    return this.isMprLayoutActive() && mode !== 'thin' && mm > 0
  }

  renderMprNativeAt = (viewer, index) => {
    if (!viewer) return
    this.syncMprSlabMeta()
    viewer.volume = this.volume
    viewer.mprData = this.mprData
    const idx = Math.max(0, Number(index) || 0)
    viewer.sliceIndex = idx
    const k = slabHalfWidthFromMeta(
      this.mprData,
      (this.mprData && this.mprData.zSpacing) || 1
    )
    if (
      k <= 0 ||
      this.mprData.slabMode === 'thin' ||
      typeof viewer.mprRenderAxialSlab !== 'function'
    ) {
      viewer.isMprGenerated = false
      viewer.runTool('openimage', idx)
      return
    }
    viewer.isMprGenerated = false
    viewer.mprRenderAxialSlab(viewer.filename, idx, this.mprData)
  }

  markMprInteract = () => {
    if (!this.isMprLayoutActive()) return
    this._mprInteract = true
    this.syncMprSlabMeta()
    if (this._mprInteractTimer) clearTimeout(this._mprInteractTimer)
    this._mprInteractTimer = setTimeout(() => {
      this._mprInteractTimer = null
      this._mprInteract = false
      if (!this.isMprLayoutActive() || this._unmounted) return
      // Tras un aim, no pisar el punto ni re-proyectar con navIndex del plano clickeado
      if (this._mprAimLockUntil && Date.now() < this._mprAimLockUntil) {
        this.clearMprScrollCrosshairPending()
        return
      }
      // Flush mira pendiente al terminar el gesto de scroll
      if (this._mprScrollPending) {
        if (this._mprScrollDebounce) {
          clearTimeout(this._mprScrollDebounce)
          this._mprScrollDebounce = null
        }
        const p = this._mprScrollPending
        this.flushMprScrollToCrosshair(p.viewerIndex, p.sliceIndex)
      }
      if (!this.wantsMprSlab()) return
      this.syncMprSlabMeta()
      const files = this.getMprStackFiles()
      if (files && files.length) {
        this.refreshMprViewportsInPlace(files, this._navSliceIndex)
      }
    }, 110)
  }

  setMprSlabMode = (mode) => {
    const next = String(mode || 'thin').toLowerCase()
    const patch = { mprSlabMode: next }
    if (next !== 'thin' && !(Number(this.state.mprSlabMm) > 0)) {
      patch.mprSlabMm = 5
    }
    this._mprInteract = false
    this.setState(patch, () => this.reprojectMprSlab())
  }

  setMprSlabMm = (mm) => {
    const n = Number(mm) || 0
    this.warnIfSlabClamped(n)
    this._mprInteract = false
    this.setState({ mprSlabMm: n }, () => this.reprojectMprSlab())
  }

  getPetFusionFiles = () => {
    if (!this.state.petFusionOn) return null
    const picked = this.resolvePetFusionSeries()
    return picked && picked.files ? picked.files : null
  }

  resolvePetFusionSeries = () => {
    const store = this.props.series
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    const files = (viewer && viewer.files) || this.files || []
    const pets = listPetSeriesFromStore(store)
    if (!pets.length) return null
    if (this.state.petFusionKey != null) {
      const hit = pets.find((p) => String(p.key) === String(this.state.petFusionKey))
      if (hit) return hit
    }
    return pickPetSeriesNotInViewport(store, files)
  }

  togglePetFusion = () => {
    const turningOn = !this.state.petFusionOn
    if (!turningOn) {
      this.setState({ petFusionOn: false, petFusionKey: null })
      return
    }
    const picked = this.resolvePetFusionSeries()
    if (!picked) {
      this.setState({
        titleMessage: 'Fusión PET',
        textMessage: 'No hay una serie PET en este estudio para superponer.',
        visibleMessage: true,
        petFusionOn: false,
      })
      return
    }
    this.setState({ petFusionOn: true, petFusionKey: picked.key })
  }

  setPetFusionOpacity = (pct) => {
    const n = Math.max(0, Math.min(100, Number(pct) || 0))
    this.setState({ petFusionOpacity: n / 100 })
  }

  setPetFusionClut = (id) => {
    this.setState({ petFusionClut: id || 'hotMetal' })
  }

  setPetFusionWindowMax = (n) => {
    const v = Number(n)
    this.setState({ petFusionWindowMax: v > 0 ? v : 5 })
  }

  getGspsFiles = () => {
    const store = this.props.series
    let count = 0
    const keys = store && Array.isArray(store.seriesKeys) ? store.seriesKeys.join(',') : ''
    if (store && store.seriesList && typeof store.seriesList.forEach === 'function') {
      store.seriesList.forEach((files) => {
        count += (files && files.length) || 0
      })
    }
    const stamp = `${keys}:${count}`
    if (this._gspsFilesStamp === stamp && this._gspsFiles) return this._gspsFiles
    const files = flattenPrFiles(store)
    this._gspsFilesStamp = stamp
    this._gspsFiles = files
    return files
  }

  toggleGspsOverlay = () => {
    this.setState((prev) => ({ gspsOn: !prev.gspsOn }))
  }

  exportActiveGsps = async () => {
    const viewer = this.getActiveDcmViewer()
    if (!viewer || !viewer.image) {
      this.setState({
        titleMessage: 'Exportar GSPS',
        textMessage: 'No hay una imagen activa para exportar.',
        visibleMessage: true,
      })
      return
    }
    if (viewer.isMprGenerated) {
      this.setState({
        titleMessage: 'Exportar GSPS',
        textMessage: 'El MPR reconstruido no es un SOP nativo. Exportá desde la serie original.',
        visibleMessage: true,
      })
      return
    }
    const measurements = viewer.measurements || []
    if (!measurements.length) {
      this.setState({
        titleMessage: 'Exportar GSPS',
        textMessage: 'No hay mediciones de sesión (Length, Angle, Cobb o ROI) para exportar.',
        visibleMessage: true,
      })
      return
    }
    const files = viewer.files === null ? viewer.props.files : viewer.files
    const fileData = files && files[viewer.sliceIndex]
    try {
      const built = buildGspsFile({
        measurements,
        image: viewer.image,
        fileData,
      })
      if (!built.graphics.length) {
        this.setState({
          titleMessage: 'Exportar GSPS',
          textMessage: 'Ninguna medición se pudo mapear a Graphic Annotation Sequence.',
          visibleMessage: true,
        })
        return
      }
      const pacsConfig = getPacsConfig(this.state.pacsIdFromUrl || null)
      const storeChannel = await this.storeDicomToPacsCascade(built.bytes, pacsConfig)
      if (storeChannel === 'stow') {
        this.setState({
          titleMessage: 'Exportar GSPS',
          textMessage: 'Presentación de estado guardada en el PACS vía STOW-RS.',
          visibleMessage: true,
        })
        await this.refreshStudyAfterStore()
      } else if (storeChannel === 'orthanc') {
        this.setState({
          titleMessage: 'Exportar GSPS',
          textMessage: 'Presentación de estado guardada en el PACS.',
          visibleMessage: true,
        })
        await this.refreshStudyAfterStore()
      } else {
        const blob = new Blob([built.bytes], { type: 'application/dicom' })
        saveAs(blob, `udv-gsps-${built.sopInstanceUid.slice(-8)}.dcm`)
      }
    } catch (e) {
      this.setState({
        titleMessage: 'Exportar GSPS',
        textMessage: (e && e.message) || 'No se pudo generar el GSPS.',
        visibleMessage: true,
      })
    }
  }

  exportActiveSr = async () => {
    const viewer = this.getActiveDcmViewer()
    if (!viewer || !viewer.image) {
      this.setState({
        titleMessage: 'Exportar SR',
        textMessage: 'No hay una imagen activa para exportar.',
        visibleMessage: true,
      })
      return
    }
    if (viewer.isMprGenerated) {
      this.setState({
        titleMessage: 'Exportar SR',
        textMessage: 'El MPR reconstruido no es un SOP nativo. Exportá desde la serie original.',
        visibleMessage: true,
      })
      return
    }
    const measurements = viewer.measurements || []
    const files = viewer.files === null ? viewer.props.files : viewer.files
    const fileData = files && files[viewer.sliceIndex]
    try {
      const built = buildSrFile({
        measurements,
        image: viewer.image,
        fileData,
      })
      if (!built.json.observations.length) {
        this.setState({
          titleMessage: 'Exportar SR',
          textMessage: 'No hay Cobb, distancias ni SUV μ/max para el informe.',
          visibleMessage: true,
        })
        return
      }
      const pacsConfig = getPacsConfig(this.state.pacsIdFromUrl || null)
      const storeChannel = await this.storeDicomToPacsCascade(built.bytes, pacsConfig)
      if (storeChannel === 'stow') {
        this.setState({
          titleMessage: 'Exportar SR',
          textMessage: 'Informe estructurado guardado en el PACS vía STOW-RS.',
          visibleMessage: true,
        })
        await this.refreshStudyAfterStore()
      } else if (storeChannel === 'orthanc') {
        this.setState({
          titleMessage: 'Exportar SR',
          textMessage: 'Informe estructurado guardado en el PACS.',
          visibleMessage: true,
        })
        await this.refreshStudyAfterStore()
      } else {
        const blob = new Blob([built.bytes], { type: 'application/dicom' })
        saveAs(blob, `udv-sr-${built.sopInstanceUid.slice(-8)}.dcm`)
      }
    } catch (e) {
      this.setState({
        titleMessage: 'Exportar SR',
        textMessage: (e && e.message) || 'No se pudo generar el SR.',
        visibleMessage: true,
      })
    }
  }

  /**
   * Contexto Patient/Study del estudio abierto (para Encapsulated PDF).
   */
  getActiveStudyPatientContext = () => {
    const storeFiles = this.props.files || []
    let patientName = 'ANONYMOUS'
    let patientId = '0'
    let studyInstanceUID = ''
    let studyDescription = ''
    for (let i = 0; i < storeFiles.length; i++) {
      const f = storeFiles[i]
      if (!f) continue
      if (f.patient) {
        if (f.patient.patientName) patientName = f.patient.patientName
        if (f.patient.patientId) patientId = f.patient.patientId
      }
      if (f.study) {
        if (f.study.studyId) studyInstanceUID = f.study.studyId
        if (f.study.studyDescription) studyDescription = f.study.studyDescription
      }
      if (f.studyInstanceUID) studyInstanceUID = f.studyInstanceUID
      if (studyInstanceUID) break
    }
    if (!studyInstanceUID) {
      try {
        const v = this.getActiveDcmViewer && this.getActiveDcmViewer()
        const files = v && (v.files || (v.props && v.props.files))
        const f0 = files && files[0]
        if (f0 && f0.study && f0.study.studyId) {
          studyInstanceUID = f0.study.studyId
        }
        if (f0 && f0.patient) {
          patientName = f0.patient.patientName || patientName
          patientId = f0.patient.patientId || patientId
        }
      } catch (e) {
        /* ignore */
      }
    }
    return { patientName, patientId, studyInstanceUID, studyDescription }
  }

  openAttachPdfPicker = () => {
    const ctx = this.getActiveStudyPatientContext()
    if (!ctx.studyInstanceUID) {
      this.setState({
        titleMessage: 'Adjuntar PDF',
        textMessage: 'Abrí un estudio primero para adjuntar el informe PDF.',
        visibleMessage: true,
      })
      return
    }
    const el =
      (this._pdfAttachInputRef && this._pdfAttachInputRef.current) ||
      document.getElementById('udv_attach_pdf_input')
    if (el) el.click()
  }

  /**
   * Inyecta serie DOC informe en store + Explorer + viewport.
   * @param {object} pdfItem
   */
  injectAttachedReportSeries = (pdfItem) => {
    if (!pdfItem) return
    const prev = Array.isArray(this.props.files) ? this.props.files : []
    const merged = mergeAttachedReportIntoFiles(prev, pdfItem)
    this.files = merged
    if (typeof this.props.setFilesStore === 'function') {
      this.props.setFilesStore(merged)
    }
    this.handleExplorerUpdate(buildExplorerDataFromFiles(merged))
    const slot =
      typeof this.props.activeDcmIndex === 'number'
        ? this.props.activeDcmIndex
        : 0
    this.setViewportKind(slot, 'pdf', [pdfItem])
    this._lastAttachedReportItem = pdfItem
    if (!this._unmounted) {
      this.setState({ visibleExplorer: true })
    }
  }

  handleAttachPdfSelected = async (event) => {
    const input = event && event.target
    const file = input && input.files && input.files[0]
    if (input) input.value = ''
    if (!file) return
    const name = String(file.name || '').toLowerCase()
    const isPdf =
      file.type === 'application/pdf' || name.endsWith('.pdf')
    if (!isPdf) {
      this.setState({
        titleMessage: 'Adjuntar PDF',
        textMessage: 'Seleccioná un archivo PDF.',
        visibleMessage: true,
      })
      return
    }
    try {
      const pdfBytes = new Uint8Array(await file.arrayBuffer())
      const patientCtx = this.getActiveStudyPatientContext()
      if (!patientCtx.studyInstanceUID) {
        throw new Error('Sin StudyInstanceUID activo')
      }
      const built = buildEncapsulatedPdfFile({
        pdfBytes,
        patientName: patientCtx.patientName,
        patientId: patientCtx.patientId,
        studyInstanceUID: patientCtx.studyInstanceUID,
        studyDescription: patientCtx.studyDescription,
        seriesDescription: 'INFORME PDF',
        documentTitle: file.name || 'Informe',
      })
      const openCtx = this.getStudyOpenContextForAttach()
      this.setState({ studyOpenContext: openCtx })

      const pdfItem = {
        name: file.name || 'informe.pdf',
        data: built.bytes,
        sopInstanceUID: built.sopInstanceUid,
        sopClassUID: built.sopClassUID,
        seriesInstanceUID: built.seriesInstanceUID,
        renderKind: 'pdf',
        nonImage: true,
        isPdfDocument: true,
        imageId: `udv-pdf:${built.sopInstanceUid}`,
        patient: {
          patientName: patientCtx.patientName,
          patientId: patientCtx.patientId,
        },
        study: {
          studyId: built.studyInstanceUID,
          studyDescription: patientCtx.studyDescription,
        },
        series: {
          modality: 'DOC',
          seriesDescription: 'INFORME PDF',
          seriesInstanceUID: built.seriesInstanceUID,
          sopClassUID: built.sopClassUID,
          isNonImage: true,
          isPdfDocument: true,
          totalImages: 1,
        },
      }

      let storeChannel = null
      if (studyOpenContextAllowsPacsStore(openCtx)) {
        const wt = openCtx.writeTarget || {}
        storeChannel = await this.storeDicomToPacsCascade(built.bytes, {
          pacsConfig:
            wt.pacsConfig || getPacsConfig(this.state.pacsIdFromUrl || null),
          wadoUrl: wt.wadoUrl || null,
        })
      }
      // ticket_r2 / local / platform_report: no STOW al hub (ver CONTRATO-UDV-ATTACH-REPORT).

      this.injectAttachedReportSeries(pdfItem)

      if (storeChannel === 'stow' || storeChannel === 'orthanc') {
        this.setState({
          titleMessage: 'Adjuntar PDF',
          textMessage: `Informe PDF guardado en el PACS (${storeChannel}).`,
          visibleMessage: true,
        })
        const strategy =
          (openCtx && openCtx.refreshStrategy) ||
          STUDY_REFRESH_STRATEGY.NONE
        if (
          strategy !== STUDY_REFRESH_STRATEGY.LOCAL_INJECT &&
          strategy !== STUDY_REFRESH_STRATEGY.NONE
        ) {
          try {
            await this.refreshStudyAfterStore()
            // Re-inyectar si el catálogo remoto aún no trae la serie DOC.
            this.injectAttachedReportSeries(pdfItem)
          } catch (eRef) {
            /* viewport/explorer local ya muestran el PDF */
          }
        }
      } else if (openCtx.mode === STUDY_OPEN_MODE.TICKET_R2) {
        const blob = new Blob([built.bytes], { type: 'application/dicom' })
        saveAs(blob, `udv-pdf-${built.sopInstanceUid.slice(-8)}.dcm`)
        this.setState({
          titleMessage: 'Adjuntar PDF',
          textMessage:
            'Informe en viewport y Explorer (sesión). Persistencia en plataforma pendiente.',
          visibleMessage: true,
        })
      } else if (openCtx.mode === STUDY_OPEN_MODE.LOCAL) {
        this.setState({
          titleMessage: 'Adjuntar PDF',
          textMessage:
            'Serie informe agregada. Podés compartir/guardar el ZIP del estudio.',
          visibleMessage: true,
        })
      } else {
        const blob = new Blob([built.bytes], { type: 'application/dicom' })
        saveAs(blob, `udv-pdf-${built.sopInstanceUid.slice(-8)}.dcm`)
        this.setState({
          titleMessage: 'Adjuntar PDF',
          textMessage:
            'No se pudo guardar en el PACS: se descargó el DICOM Encapsulated PDF. El informe está en viewport y Explorer.',
          visibleMessage: true,
        })
      }
    } catch (e) {
      this.setState({
        titleMessage: 'Adjuntar PDF',
        textMessage: (e && e.message) || 'No se pudo encapsular/subir el PDF.',
        visibleMessage: true,
      })
    }
  }

  warnIfSlabClamped = (mm) => {
    const prof = resolveSlabProfile(detectHardware(), this.state.mprProfile)
    if (!(prof.maxHalfWidth > 0)) return
    const sp = (this.mprData && this.mprData.zSpacing) || 1
    const k = mmToHalfWidth(mm, sp)
    if (k > prof.maxHalfWidth) {
      this.setState({
        titleMessage: 'Slab MPR',
        textMessage: 'Grosor limitado en este dispositivo para mantener fluidez.',
        visibleMessage: true,
      })
    }
  }

  reprojectMprSlab = () => {
    if (!this.isMprLayoutActive()) return
    this.syncMprSlabMeta()
    const files = this.getMprStackFiles()
    if (files && files.length) {
      this.refreshMprViewportsInPlace(files, this._navSliceIndex)
    }
  }

  refreshMprViewportsInPlace = (files, keepIndex) => {
    this.syncMprSlabMeta()
    const { filename } = this.getMprSourceMeta()
    const size = this.getMprMatrixSize(files)
    const origin = this.mprPlanePosition(true, 0) || this.mprPlane || 'axial'
    const index = Math.max(0, Math.min(files.length - 1, Number(keepIndex) || 0))
    const keepKey = this.getFileFrameKey(files[index])
    const softFiles = { files, soft: true, remappedIndex: index, keepKey }

    const applyNative = (v) => {
      if (!v) return
      v.volume = this.volume
      v.mprData = this.mprData
      v.sliceMax = files.length
      v.shouldScroll = files.length > 1
      v.sliceIndex = index
      v.runTool('setfiles', softFiles)
      if (!v.isMprGenerated) this.renderMprNativeAt(v, index)
    }
    const applyGen = (v, kind) => {
      if (!v || !size) return
      v.volume = this.volume
      v.mprData = this.mprData
      v.isMprGenerated = true
      v.runTool('setfiles', files)
      if (kind === 'xz') {
        const yi = Math.max(0, Math.min(size.rows - 1, v.sliceIndex != null ? v.sliceIndex : Math.trunc(size.rows / 2)))
        v.sliceMax = size.rows
        v.shouldScroll = size.rows > 1
        v.sliceIndex = yi
        v.mprRenderXZPlane(filename, origin, yi, this.mprData)
      } else {
        const xi = Math.max(0, Math.min(size.cols - 1, v.sliceIndex != null ? v.sliceIndex : Math.trunc(size.cols / 2)))
        v.sliceMax = size.cols
        v.shouldScroll = size.cols > 1
        v.sliceIndex = xi
        v.mprRenderYZPlane(filename, origin, xi, this.mprData)
      }
    }

    if (this.state.visibleMprOrthogonal) {
      const [i0, i1, i2] = this.getMprDicomIndices()
      applyNative(this.dicomViewersRefs[i0])
      applyGen(this.dicomViewersRefs[i1], 'xz')
      applyGen(this.dicomViewersRefs[i2], 'yz')
    } else if (this.state.visibleMprSagittal || this.state.visibleMprCoronal || this.state.visibleMprAxial) {
      const indices = this.getActiveMprIndices() || [0, 1]
      const selected = this.state.visibleMprSagittal
        ? 'sagittal'
        : this.state.visibleMprCoronal
          ? 'coronal'
          : 'axial'
      const vSrc = this.dicomViewersRefs[indices[0]]
      const vSel = this.dicomViewersRefs[indices[1]] || this.dicomViewersRefs[indices[0]]
      applyNative(vSrc)
      if (vSel && vSel !== vSrc) {
        if (origin === selected) applyNative(vSel)
        else if (origin === 'axial' && selected === 'sagittal') applyGen(vSel, 'yz')
        else if (origin === 'axial' && selected === 'coronal') applyGen(vSel, 'xz')
        else if (this.renderMprSelectedPlaneAt) {
          vSel.volume = this.volume
          vSel.mprData = this.mprData
          this.renderMprSelectedPlaneAt(vSel, origin, selected, vSel.sliceIndex || 0)
        }
      }
    }

    this._navSliceIndex = index
    this.setState({ sliceIndex: index, sliceMax: files.length, visibleVolumeBuilding: false })
    this._mprNeedsRefLines = true
  }

  scheduleMpr3dLiveRebuild = () => {
    if (!this.state.visibleMpr3D) return
    if (this._mpr3dLiveTimer) clearTimeout(this._mpr3dLiveTimer)
    this._mpr3dLiveTimer = setTimeout(() => {
      this._mpr3dLiveTimer = null
      if (!this.state.visibleMpr3D || this._unmounted) return
      this.rebuildMpr3dVolume().catch((e) => {
        if (e && e.message === '3D cancelado.') return
        console.warn('Vol3D live rebuild', e)
      })
    }, 1600)
  }

  getMprSourceMeta = () => {
    const files = this.getMprStackFiles()
    const v0 = this.dicomViewersRefs && this.dicomViewersRefs[0]
    let filename = (v0 && v0.filename) || ''
    let explorerIndex = v0 && v0.explorerIndex
    if (!filename) {
      for (let i = 0; i < 16; i++) {
        const v = this.dicomViewersRefs && this.dicomViewersRefs[i]
        if (v && v.filename) {
          filename = v.filename
          explorerIndex = v.explorerIndex
          break
        }
      }
    }
    return { files, filename, explorerIndex }
  }

  /**
   * Tras armar viewports MPR: restaurar WW/WC de la serie origen.
   * @param {Array} stackFiles
   * @param {Array<object>} viewers
   */
  reapplyWwwcToMprViewers = (stackFiles, viewers) => {
    const seriesUid =
      stackFiles && stackFiles[0] ? this.getFileSeriesId(stackFiles[0]) : null
    if (!seriesUid) return
    const saved = this.lookupSeriesWwwc(seriesUid)
    if (!saved) return
    const list = Array.isArray(viewers) ? viewers : []
    for (let i = 0; i < list.length; i++) {
      const v = list[i]
      if (!v) continue
      try {
        const vpIndex = this.resolveViewerLayoutIndex(v)
        this.seedSeriesWwwcOnViewer(v, seriesUid, saved, {
          viewportIndex: vpIndex,
          preserveLocal: false,
        })
        this.scheduleReapplySeriesWwwc(v, seriesUid, saved, {
          viewportIndex: vpIndex,
          preserveLocal: false,
        })
      } catch (e) {
        /* ignore */
      }
    }
  }

  getMprMatrixSize = (files) => {
    if (!files || !files.length) return null
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!f) continue
      const cols = f.columns != null ? f.columns : f.image && f.image.columns
      const rows = f.rows != null ? f.rows : f.image && f.image.rows
      if (cols > 0 && rows > 0) return { cols: Number(cols), rows: Number(rows) }
    }
    return null
  }

  /** Cancela fills previos y reintenta hasta que los viewports existan. */
  scheduleMprViewportFill = (fillFn) => {
    const gen = (this._mprFillGen = (this._mprFillGen || 0) + 1)
    const tryFill = (attempt) => {
      if (this._unmounted || gen !== this._mprFillGen) return
      let ready = false
      try {
        ready = !!fillFn()
      } catch (err) {
        console.error('MPR fill error', err)
        ready = false
      }
      if (ready) return
      if (attempt >= 20) {
        console.warn('MPR fill: viewports no listos tras reintentos')
        return
      }
      setTimeout(() => tryFill(attempt + 1), 80)
    }
    setTimeout(() => tryFill(0), 80)
  }

  changeToOrthogonalView = () => {
    const { files: stackFiles, filename, explorerIndex } = this.getMprSourceMeta()
    if (!stackFiles || !stackFiles.length) {
      console.warn('changeToOrthogonalView: sin stack')
      return
    }
    this.syncMprSlabMeta()

    const size = this.getMprMatrixSize(stackFiles)
    if (!size) {
      console.warn('changeToOrthogonalView: sin tamaño de matriz')
      return
    }

    // 1×3 [0,1,2] → 2×2+3D [0,1,4]: el sagital cambia de viewer.
    // No usar sliceIndex del viewer nuevo (arranca en 0 → línea en el borde, plano vacío).
    const prevIdx = this._mprIndices && this._mprIndices.length >= 3
      ? [this._mprIndices[0], this._mprIndices[1], this._mprIndices[2]]
      : null
    const readSlice = (idx) => {
      if (idx == null) return null
      const v = this.dicomViewersRefs && this.dicomViewersRefs[idx]
      if (!v || v.sliceIndex == null) return null
      const n = Number(v.sliceIndex)
      return Number.isFinite(n) ? n : null
    }
    const keptNative = readSlice(prevIdx && prevIdx[0])
    const keptXz = readSlice(prevIdx && prevIdx[1])
    const keptYz = readSlice(prevIdx && prevIdx[2])

    const [rows, cols] = this.getMprOrthoLayoutDims()
    const indices = this.getMprDicomIndices()
    this._mprIndices = indices
    const [i0, i1, i2] = indices
    const live = !!this._mprLiveKeepView
    const index = keptNative != null
      ? Math.max(0, Math.min(stackFiles.length - 1, keptNative))
      : live && this.state.sliceIndex != null
        ? Math.max(0, Math.min(stackFiles.length - 1, Number(this.state.sliceIndex) || 0))
        : Math.trunc(stackFiles.length / 2)

    const cur = this.props.layout || [1, 1]
    if (cur[0] !== rows || cur[1] !== cols) {
      try {
        ReferenceLinesManager.clearElements()
      } catch (e) { /* ignore */ }
      this._mprNeedsRefLines = true
      this.changeLayout(rows, cols)
    }

    this.setState({ visibleVolumeBuilding: false, sliceIndex: index, mprMode: true, visibleSeriesLink: false, seriesLinkWwwc: true, sliceMax: stackFiles.length, mprCrosshairPoint: this.state.mprCrosshairPoint || this.seedMprCorrelationPoint() }, () => {
      this.scheduleMprViewportFill(() => {
        const plane = this.mprPlanePosition(true, 0) || this.mprPlanePosition() || 'axial'
        this._mprNeedsRefLines = true
        this._navActiveIndex = i0

        const v0 = this.dicomViewersRefs[i0]
        const v1 = this.dicomViewersRefs[i1]
        const v2 = this.dicomViewersRefs[i2]
        if (!v0 || !v1 || !v2 || !v0.dicomImage || !v1.dicomImage || !v2.dicomImage) return false

        ;[0, 1, 2, 4, 8].forEach((idx) => {
          const v = this.dicomViewersRefs[idx]
          if (v) {
            v.referenceLines = {}
            if (v.mpr && !this._mprLiveKeepView) {
              v.mpr.sliceLocation = null
              v.mpr.isSliceLocation = false
            }
          }
        })

        v0.volume = this.volume
        v0.mprData = this.mprData
        v0.isMprGenerated = false
        v0.sliceMax = stackFiles.length
        v0.shouldScroll = stackFiles.length > 1
        v0.sliceIndex = index
        v0.runTool('setfiles', stackFiles)
        v0.explorerIndex = explorerIndex
        v0.filename = filename
        this.renderMprNativeAt(v0, index)

        const prepGen = (v) => {
          v.volume = this.volume
          v.mprData = this.mprData
          if (!this._mprLiveKeepView) {
            v._mprFitted = false
            v._mprLastSpacing = null
          }
          v.runTool('setfiles', stackFiles)
          v.isMprGenerated = true
          v.explorerIndex = explorerIndex
          v.filename = filename
        }

        prepGen(v1)
        v1.sliceMax = size.rows
        v1.shouldScroll = size.rows > 1
        let xzIndex
        let yzIndex
        let nativeIdx = index
        const pt = this.state.mprCrosshairPoint
        if (pt) {
          if (plane === 'axial') {
            nativeIdx = pt.z
            xzIndex = pt.y
            yzIndex = pt.x
          } else if (plane === 'coronal') {
            nativeIdx = pt.y
            xzIndex = pt.z
            yzIndex = pt.x
          } else {
            nativeIdx = pt.x
            xzIndex = pt.z
            yzIndex = pt.y
          }
          nativeIdx = Math.max(0, Math.min(stackFiles.length - 1, Number(nativeIdx) || 0))
          xzIndex = Math.max(0, Math.min(size.rows - 1, Number(xzIndex) || 0))
          yzIndex = Math.max(0, Math.min(size.cols - 1, Number(yzIndex) || 0))
          v0.sliceIndex = nativeIdx
          this.renderMprNativeAt(v0, nativeIdx)
          this._navSliceIndex = nativeIdx
        } else {
          xzIndex = keptXz != null
            ? Math.max(0, Math.min(size.rows - 1, keptXz))
            : Math.trunc(size.rows / 2)
          yzIndex = keptYz != null
            ? Math.max(0, Math.min(size.cols - 1, keptYz))
            : Math.trunc(size.cols / 2)
        }
        v1.sliceIndex = xzIndex
        v1.mprRenderXZPlane(filename, plane, xzIndex, this.mprData)

        prepGen(v2)
        v2.sliceMax = size.cols
        v2.shouldScroll = size.cols > 1
        v2.sliceIndex = yzIndex
        v2.mprRenderYZPlane(filename, plane, yzIndex, this.mprData)

        this._navSliceIndex = nativeIdx
        this.props.setActiveDcmIndex(i0)
        this.setState({ sliceIndex: nativeIdx, sliceMax: stackFiles.length }, () => {
          ;[v0, v1, v2].forEach((v) => {
            if (v && typeof v.handleResize === 'function') {
              v._lastViewerSize = null
              v.handleResize()
            }
          })
          this.reapplyWwwcToMprViewers(stackFiles, [v0, v1, v2])
          this.scheduleMprReferenceLinesSetup({ delay: 250 })
          this.reapplyMprCrosshairAfterLayout()
        })
        return true
      })
    })
  }

  /**
   * MPR de un plano: serie origen + plano elegido.
   * Portrait 2×1 (arriba/abajo); landscape 1×2 (lado a lado).
   * @param {'sagittal'|'coronal'|'axial'} selectedPlane
   */
  changeToDualPlaneMprView = (selectedPlane) => {
    const { files: stackFiles, filename, explorerIndex } = this.getMprSourceMeta()
    if (!stackFiles || !stackFiles.length) {
      console.warn('changeToDualPlaneMprView: sin stack')
      return
    }

    const size = this.getMprMatrixSize(stackFiles)
    if (!size) {
      console.warn('changeToDualPlaneMprView: sin tamaño de matriz')
      return
    }

    const [rows, cols] = this.getMprDualLayoutDims()
    const indices = this.getMprLayoutIndices(rows, cols)
    this._mprIndices = indices
    const [i0, i1] = indices

    const cur = this.props.layout || [1, 1]
    if (cur[0] !== rows || cur[1] !== cols) {
      try {
        ReferenceLinesManager.clearElements()
      } catch (e) { /* ignore */ }
      this._mprNeedsRefLines = true
      this.changeLayout(rows, cols)
    }

    const originPlane = this.mprPlanePosition(true, 0) || this.mprPlanePosition() || 'axial'
    const midSrc = Math.trunc(stackFiles.length / 2)
    const imgCols = size.cols
    const imgRows = size.rows

    this.setState(
      {
        visibleVolumeBuilding: false,
        mprMode: true,
        visibleSeriesLink: false,
        seriesLinkWwwc: true,
        sliceIndex: midSrc,
        sliceMax: stackFiles.length,
        mprCrosshairPoint: this.state.mprCrosshairPoint || this.seedMprCorrelationPoint(),
      },
      () => {
        this.scheduleMprViewportFill(() => {
          this._mprNeedsRefLines = true
          this._navActiveIndex = i0

          ;[0, 1, 2, 4, 8].forEach((idx) => {
            const v = this.dicomViewersRefs[idx]
            if (v) {
              v.referenceLines = {}
              if (v.mpr) {
                v.mpr.sliceLocation = null
                v.mpr.isSliceLocation = false
              }
            }
          })

          const v0 = this.dicomViewersRefs[i0]
          const target = this.dicomViewersRefs[i1]
          if (!v0 || !target) return false

          v0.volume = this.volume
          v0.mprData = this.mprData
          v0.isMprGenerated = false
          v0.sliceMax = stackFiles.length
          v0.shouldScroll = stackFiles.length > 1
          v0.sliceIndex = midSrc
          v0.runTool('setfiles', stackFiles)
          v0.explorerIndex = explorerIndex
          v0.filename = filename
          this.renderMprNativeAt(v0, midSrc)

          target.volume = this.volume
          target.mprData = this.mprData
          target._mprFitted = false
          target._mprLastSpacing = null
          target.runTool('setfiles', stackFiles)
          target.explorerIndex = explorerIndex
          target.filename = filename

          const afterApply = () => {
            this._navSliceIndex = midSrc
            this.props.setActiveDcmIndex(i0)
            this.setState({ sliceIndex: midSrc, sliceMax: stackFiles.length }, () => {
              ;[v0, target].forEach((v) => {
                if (v && typeof v.handleResize === 'function') {
                  v._lastViewerSize = null
                  v.handleResize()
                }
              })
              this.reapplyWwwcToMprViewers(stackFiles, [v0, target])
              // Tras fill + resize; reafirma otra vez cuando DicomViewer termina su orientationchange
              this.scheduleMprReferenceLinesSetup({ delay: 250 })
              this.reapplyMprCrosshairAfterLayout()
            })
          }

          const applyNative = () => {
            target.isMprGenerated = false
            target.sliceMax = stackFiles.length
            target.shouldScroll = stackFiles.length > 1
            target.sliceIndex = midSrc
            this.renderMprNativeAt(target, midSrc)
            afterApply()
          }

          const applyYZ = (sliceMax) => {
            const planeIndex = Math.trunc(sliceMax / 2)
            target.isMprGenerated = true
            target.sliceMax = sliceMax
            target.shouldScroll = sliceMax > 1
            target.sliceIndex = planeIndex
            target.mprRenderYZPlane(filename, originPlane, planeIndex, this.mprData)
            afterApply()
          }

          const applyXZ = (sliceMax) => {
            const planeIndex = Math.trunc(sliceMax / 2)
            target.isMprGenerated = true
            target.sliceMax = sliceMax
            target.shouldScroll = sliceMax > 1
            target.sliceIndex = planeIndex
            target.mprRenderXZPlane(filename, originPlane, planeIndex, this.mprData)
            afterApply()
          }

          if (selectedPlane === originPlane) {
            applyNative()
            return true
          }

          if (originPlane === 'axial') {
            if (selectedPlane === 'sagittal') applyYZ(imgCols)
            else if (selectedPlane === 'coronal') applyXZ(imgRows)
            else applyNative()
          } else if (originPlane === 'sagittal') {
            if (selectedPlane === 'axial') applyXZ(imgCols)
            else if (selectedPlane === 'coronal') applyYZ(imgRows)
            else applyNative()
          } else {
            if (selectedPlane === 'axial') applyYZ(imgRows)
            else if (selectedPlane === 'sagittal') applyXZ(imgCols)
            else applyNative()
          }
          return true
        })
      }
    )
  }

  /** Render del plano MPR seleccionado en un viewport (scroll). */
  renderMprSelectedPlaneAt = (viewer, originPlane, selectedPlane, index) => {
    if (!viewer) return
    const { filename } = this.getMprSourceMeta()
    if (!filename) return
    this.syncMprSlabMeta()
    viewer.volume = this.volume
    viewer.mprData = this.mprData

    if (selectedPlane === originPlane) {
      viewer.isMprGenerated = false
      this.renderMprNativeAt(viewer, index)
      return
    }

    viewer.isMprGenerated = true
    if (originPlane === 'axial') {
      if (selectedPlane === 'sagittal') {
        viewer.mprRenderYZPlane(filename, originPlane, index, this.mprData)
      } else if (selectedPlane === 'coronal') {
        viewer.mprRenderXZPlane(filename, originPlane, index, this.mprData)
      }
    } else if (originPlane === 'sagittal') {
      if (selectedPlane === 'axial') {
        viewer.mprRenderXZPlane(filename, originPlane, index, this.mprData)
      } else if (selectedPlane === 'coronal') {
        viewer.mprRenderYZPlane(filename, originPlane, index, this.mprData)
      }
    } else {
      // coronal
      if (selectedPlane === 'axial') {
        viewer.mprRenderYZPlane(filename, originPlane, index, this.mprData)
      } else if (selectedPlane === 'sagittal') {
        viewer.mprRenderXZPlane(filename, originPlane, index, this.mprData)
      }
    }
  }

  changeToSagittalView = () => {
    this.changeToDualPlaneMprView('sagittal')
  }

  changeToCoronalView = () => {
    this.changeToDualPlaneMprView('coronal')
  }

  changeToAxialView = () => {
    this.changeToDualPlaneMprView('axial')
  }

  getMprVolumeSize = () => {
    const files = this.getMprStackFiles()
    const size = this.getMprMatrixSize(files)
    if (!size) return null
    const zDim =
      (this.mprData && this.mprData.zDim) ||
      (files && files.length) ||
      1
    return { cols: size.cols, rows: size.rows, zDim }
  }

  /** Punto 3D para cruz H/V de correlación (sagital=vertical en coronal). */
  seedMprCorrelationPoint = () => {
    if (this.state.mprCrosshairPoint) return this.state.mprCrosshairPoint
    const size = this.getMprVolumeSize()
    if (!size) return null
    return {
      x: Math.trunc(size.cols / 2),
      y: Math.trunc(size.rows / 2),
      z: Math.trunc(size.zDim / 2),
    }
  }

  /** Cruz H/V de correlación en todos los planos MPR (sagital → vertical en coronal). */
  refreshMprCorrelationOverlay = (point) => {
    const indices = this.getActiveMprIndices() || []
    for (let i = 0; i < indices.length; i++) {
      const v = this.dicomViewersRefs && this.dicomViewersRefs[indices[i]]
      if (v && typeof v.updateCrosshairOverlay === 'function') {
        try {
          v.updateCrosshairOverlay(point)
        } catch (e) {
          /* ignore */
        }
      }
    }
  }

  toggleMprCrosshair = () => {
    if (!this.isMprLayoutActive()) return
    const next = !this.state.mprCrosshairActive
    this.setState({
      mprCrosshairActive: next,
      toolActive: next ? 'mprCrosshair' : 'notool',
      mprCrosshairPoint: this.state.mprCrosshairPoint || this.seedMprCorrelationPoint(),
    })
    if (!next) {
      try {
        this.props.toolStore('notool')
      } catch (e) {
        /* ignore */
      }
      this.releaseAllCrosshairCaptures()
      const v =
        this.dicomViewersRefs &&
        this.dicomViewersRefs[this.props.activeDcmIndex]
      if (v && typeof v.runTool === 'function') {
        try {
          v.runTool('notool')
        } catch (e2) {
          /* ignore */
        }
      }
    }
  }

  /**
   * Tap/click en un viewport MPR → punto 3D → reposicionar planos.
   * No usa markMprInteract: ese flush de scroll pendiente / refresh slab
   * pisaba el punto justo después del click.
   */
  handleMprCrosshairAim = (viewerIndex, event) => {
    if (!this.state.mprCrosshairActive || !this.isMprLayoutActive()) return
    this.clearMprScrollCrosshairPending()
    this._mprAimLockUntil = Date.now() + 280
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]
    if (!viewer || !viewer.dicomImage || !viewer.image) return

    let pageX
    let pageY
    if (event && event.changedTouches && event.changedTouches[0]) {
      pageX = event.changedTouches[0].pageX
      pageY = event.changedTouches[0].pageY
    } else if (event) {
      pageX = event.pageX
      pageY = event.pageY
    }
    if (pageX == null || pageY == null) return

    let pixel
    try {
      pixel = window.cornerstone.pageToPixel(viewer.dicomImage, pageX, pageY)
    } catch (e) {
      return
    }
    if (!pixel) return

    const size = this.getMprVolumeSize()
    if (!size) return
    const files = this.getMprStackFiles()
    if (this.mprData && this.mprData.flipZ == null && files) {
      try {
        this.mprData.flipZ = shouldFlipMprZ(files, this.mprData)
      } catch (e) {
        this.mprData.flipZ = false
      }
    }
    const flipZ = !!(this.mprData && this.mprData.flipZ)
    const plane =
      (viewer.mprPlane && String(viewer.mprPlane)) ||
      this.mprPlanePosition(false, viewerIndex) ||
      'axial'
    const sliceIndex =
      viewer.sliceIndex != null ? viewer.sliceIndex : this.state.sliceIndex || 0

    const point = pixelToVolumeIndices({
      plane,
      pixel,
      sliceIndex,
      size,
      flipZ,
    })
    this.applyMprCrosshairPoint(point, { plane })
  }

  applyMprCrosshairPoint = (rawPoint, opts = {}) => {
    const size = this.getMprVolumeSize()
    if (!size || !rawPoint) return
    const point = clampVolumePoint(rawPoint, size)
    if (!point) return

    const { files: stackFiles, filename } = this.getMprSourceMeta()
    if (!stackFiles || !stackFiles.length || !filename) return
    this.syncMprSlabMeta()

    const indices = this.getActiveMprIndices()
    const originIdx = (indices && indices[0]) || 0
    const originPlane =
      this.mprPlanePosition(true, originIdx) || this.mprPlane || 'axial'
    const quiet = !!opts.quiet
    const plane = String(opts.plane || this.state.mprCrosshairPlane || '').toLowerCase()
    const clipAxis =
      plane === 'sagittal' ? 'x' : plane === 'coronal' ? 'y' : plane === 'axial' ? 'z' : null

    const mark =
      this.state.visibleMpr3D && this._mpr3dVolume
        ? this.computeMpr3dClip(point)
        : null
    const patch = {
      mprCrosshairPoint: point,
    }
    if (plane) patch.mprCrosshairPlane = plane

    // Guardar intención de decapado aunque el 3D esté cerrado (p. ej. teléfono).
    if (clipAxis && !quiet) {
      patch.mpr3dClipAxis = clipAxis
      patch.mpr3dClipKeep = plane === 'sagittal' ? -1 : 1
      if (this.state.visibleMpr3D && mark) {
        patch.mpr3dMark = mark
        patch.mpr3dDecapado = true
        patch.mpr3dClip = mark
        patch.mpr3dViewHint = plane
        patch.mpr3dViewHintGen = (this.state.mpr3dViewHintGen || 0) + 1
        if (!(Number(this.state.mpr3dSlab) > 0)) patch.mpr3dSlab = 8
      }
    } else if (this.state.visibleMpr3D) {
      patch.mpr3dMark = mark
    }

    this.setState(patch)
    this.refreshMprCorrelationOverlay(point)
    if (this.state.visibleMpr3D && !opts.from3d) this.scheduleMpr3dClipSync()

    if (this.state.visibleMprOrthogonal && indices.length >= 3) {
      const [i0, i1, i2] = indices
      const v0 = this.dicomViewersRefs[i0]
      const v1 = this.dicomViewersRefs[i1]
      const v2 = this.dicomViewersRefs[i2]
      if (!v0 || !v1 || !v2) return

      // Orthogonal: i0 stack nativo, i1 XZ, i2 YZ (como changeToOrthogonalView)
      let nativeIdx
      let xzIdx
      let yzIdx
      if (originPlane === 'axial') {
        nativeIdx = point.z
        xzIdx = point.y
        yzIdx = point.x
      } else if (originPlane === 'coronal') {
        nativeIdx = point.y
        xzIdx = point.z
        yzIdx = point.x
      } else {
        nativeIdx = point.x
        xzIdx = point.z
        yzIdx = point.y
      }

      let planeV0 = originPlane
      let planeV1
      let planeV2
      if (originPlane === 'axial') {
        planeV1 = 'coronal'
        planeV2 = 'sagittal'
      } else if (originPlane === 'coronal') {
        planeV1 = 'axial'
        planeV2 = 'sagittal'
      } else {
        planeV1 = 'axial'
        planeV2 = 'coronal'
      }

      // No cinear el plano donde se arrastra la mira; solo los perpendiculares
      if (plane !== planeV0) {
        v0.isMprGenerated = false
        v0.sliceMax = stackFiles.length
        if (v0.sliceIndex !== nativeIdx) {
          v0.sliceIndex = nativeIdx
          this.renderMprNativeAt(v0, nativeIdx)
        }
      }

      if (plane !== planeV1) {
        if (v1.sliceIndex !== xzIdx) {
          v1.volume = this.volume
          v1.mprData = this.mprData
          v1.isMprGenerated = true
          v1.sliceIndex = xzIdx
          v1.mprRenderXZPlane(filename, originPlane, xzIdx, this.mprData)
        }
      }

      if (plane !== planeV2) {
        if (v2.sliceIndex !== yzIdx) {
          v2.volume = this.volume
          v2.mprData = this.mprData
          v2.isMprGenerated = true
          v2.sliceIndex = yzIdx
          v2.mprRenderYZPlane(filename, originPlane, yzIdx, this.mprData)
        }
      }

      if (!quiet) {
        let navIdx = nativeIdx
        let navMax = stackFiles.length
        if (plane === planeV1) {
          navIdx = xzIdx
          navMax = size.rows
        } else if (plane === planeV2) {
          navIdx = yzIdx
          navMax = size.cols
        }
        this._navSliceIndex = navIdx
        this.setState({ sliceIndex: navIdx, sliceMax: navMax })
      }
    } else if (
      this.state.visibleMprSagittal ||
      this.state.visibleMprCoronal ||
      this.state.visibleMprAxial
    ) {
      const selected = this.state.visibleMprSagittal
        ? 'sagittal'
        : this.state.visibleMprCoronal
          ? 'coronal'
          : 'axial'
      const [i0, i1] = indices
      const v0 = this.dicomViewersRefs[i0]
      const target = this.dicomViewersRefs[i1]
      if (!v0 || !target) return

      const nativeZ =
        originPlane === 'axial'
          ? point.z
          : originPlane === 'coronal'
            ? point.y
            : point.x

      if (plane !== originPlane) {
        v0.isMprGenerated = false
        v0.sliceMax = stackFiles.length
        if (v0.sliceIndex !== nativeZ) {
          v0.sliceIndex = nativeZ
          this.renderMprNativeAt(v0, nativeZ)
        }
      }

      if (plane !== selected) {
        const selSlice = volumeIndicesToViewportSlice({
          plane: selected,
          x: point.x,
          y: point.y,
          z: point.z,
          size,
        })
        if (target.sliceIndex !== selSlice) {
          target.volume = this.volume
          target.mprData = this.mprData
          target.sliceIndex = selSlice
          this.renderMprSelectedPlaneAt(target, originPlane, selected, selSlice)
        }
      }

      if (!quiet) {
        let navIdx = nativeZ
        let navMax = stackFiles.length
        if (plane === selected) {
          const selSlice = volumeIndicesToViewportSlice({
            plane: selected,
            x: point.x,
            y: point.y,
            z: point.z,
            size,
          })
          navIdx = selSlice
          navMax =
            selected === 'sagittal'
              ? size.cols
              : selected === 'coronal'
                ? size.rows
                : stackFiles.length
        }
        this._navSliceIndex = navIdx
        this.setState({ sliceIndex: navIdx, sliceMax: navMax })
      }
    }
  }

  /**
   * Scroll de un plano MPR actualiza el punto mira y mueve los otros dos.
   * Debounced durante el gesto para no reconstruir perpendiculares en cada tick.
   */
  syncMprScrollToCrosshair = (viewerIndex, sliceIndex) => {
    if (this._mprAimLockUntil && Date.now() < this._mprAimLockUntil) return
    this._mprScrollPending = { viewerIndex, sliceIndex }
    if (this._mprScrollDebounce) return
    this._mprScrollDebounce = setTimeout(() => {
      this._mprScrollDebounce = null
      const p = this._mprScrollPending
      if (!p || this._unmounted) return
      this.flushMprScrollToCrosshair(p.viewerIndex, p.sliceIndex)
    }, 100)
  }

  flushMprScrollToCrosshair = (viewerIndex, sliceIndex) => {
    if (!this.isMprLayoutActive()) return
    if (this._mprAimLockUntil && Date.now() < this._mprAimLockUntil) return
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]
    if (!viewer) return
    const size = this.getMprVolumeSize && this.getMprVolumeSize()
    if (!size) return
    const plane =
      (viewer.mprPlane && String(viewer.mprPlane)) ||
      this.mprPlanePosition(false, viewerIndex) ||
      'axial'
    const prev = this.state.mprCrosshairPoint || {
      x: Math.trunc(size.cols / 2),
      y: Math.trunc(size.rows / 2),
      z: Math.trunc(size.zDim / 2),
    }
    const next = { x: prev.x, y: prev.y, z: prev.z }
    const idx = Number(sliceIndex) || 0
    if (plane === 'coronal') next.y = idx
    else if (plane === 'sagittal') next.x = idx
    else next.z = idx
    if (this._mprScrollSyncing) return
    this._mprScrollSyncing = true
    try {
      this.applyMprCrosshairPoint(next, { plane, quiet: true })
    } finally {
      this._mprScrollSyncing = false
    }
  }

  /** Tras rotar layout MPR, reaplicar el punto si sigue activo. */
  reapplyMprCrosshairAfterLayout = () => {
    if (!this.state.mprCrosshairPoint) return
    setTimeout(() => {
      if (this._unmounted || !this.state.mprCrosshairPoint) return
      this.applyMprCrosshairPoint(this.state.mprCrosshairPoint, { quiet: true })
    }, 320)
  }

  mprPlanePosition = (force = false, index = this.props.activeDcmIndex) => {
    try {
      const viewer = this.dicomViewersRefs && this.dicomViewersRefs[index]
      if (viewer && viewer.isMprGenerated) {
        return viewer.mprPlane || this.mprPlane || ''
      }
      if (this.mprPlane === '' || force) {
        if (!viewer || typeof viewer.mprPlanePosition !== 'function') {
          this.mprPlane = ''
          return this.mprPlane
        }
        this.mprPlane = viewer.mprPlanePosition() || ''
      }
    } catch (e) {
      this.mprPlane = ''
    }
    return this.mprPlane  
  }

  mpr3D = () => {
    if (!this.isMprLayoutActive()) return
    if (this.state.visibleMpr3D) {
      this.hideMpr3D()
      return
    }
    this.showMpr3D()
  }

  getActiveWwwc = () => {
    const indices = this.getActiveMprIndices() || [0, 1, 2]
    const refs = this.dicomViewersRefs || []
    const tryViewer = (v) => {
      if (!v || v.isMpr3d) return null
      try {
        const el = v.dicomImage
        if (el && window.cornerstone) {
          const vp = window.cornerstone.getViewport(el)
          if (vp && vp.voi && vp.voi.windowWidth > 0) {
            return { ww: vp.voi.windowWidth, wc: vp.voi.windowCenter }
          }
        }
      } catch (e) { /* ignore */ }
      const img = v.image
      if (img && img.windowWidth > 0) {
        return {
          ww: img.windowWidth,
          wc: img.windowCenter != null ? img.windowCenter : 40,
        }
      }
      return null
    }
    for (let k = 0; k < indices.length; k++) {
      const hit = tryViewer(refs[indices[k]])
      if (hit) return hit
    }
    for (let i = 0; i < refs.length; i++) {
      const hit = tryViewer(refs[i])
      if (hit) return hit
    }
    return { ww: 400, wc: 40 }
  }

  computeMpr3dClip = (ptOverride) => {
    const vol = this._mpr3dVolume
    if (!vol || !vol.size) return { x: 0, y: 0, z: 0 }
    const size = this.getMprVolumeSize() || {
      cols: vol.cols,
      rows: vol.rows,
      zDim: vol.zDim,
    }
    let x
    let y
    let z
    const pt = ptOverride || this.state.mprCrosshairPoint
    if (pt) {
      x = pt.x
      y = pt.y
      z = pt.z
    } else {
      const idx = this.getActiveMprIndices() || []
      const v0 = this.dicomViewersRefs[idx[0]]
      const v1 = this.dicomViewersRefs[idx[1]]
      const v2 = this.dicomViewersRefs[idx[2]]
      z = v0 && v0.sliceIndex != null ? v0.sliceIndex : Math.trunc((size.zDim || 1) / 2)
      y = v1 && v1.sliceIndex != null ? v1.sliceIndex : Math.trunc((size.rows || 1) / 2)
      x = v2 && v2.sliceIndex != null ? v2.sliceIndex : Math.trunc((size.cols || 1) / 2)
    }
    return volumePointToGrid({ x, y, z }, size, vol)
  }

  scheduleMpr3dClipSync = () => {
    if (!this.state.visibleMpr3D) return
    if (this._mpr3dSyncTimer) clearTimeout(this._mpr3dSyncTimer)
    this._mpr3dSyncTimer = setTimeout(() => {
      this._mpr3dSyncTimer = null
      if (!this.state.visibleMpr3D) return
      const mark = this.computeMpr3dClip()
      const patch = { mpr3dMark: this.state.mprCrosshairPoint ? mark : null }
      if (this.state.mpr3dDecapado) patch.mpr3dClip = mark
      this.setState(patch)
    }, 80)
  }

  /**
   * Aplica decapado + marca + orientación 3D desde el último punto mira.
   * Sirve en desktop (ya abierto) y teléfono (mira en MPR → abrir 3D).
   */
  applyCrosshairPeelToMpr3d = (opts = {}) => {
    if (!this.state.visibleMpr3D || !this._mpr3dVolume) return false
    const point = this.state.mprCrosshairPoint
    if (!point) return false
    const plane = String(
      opts.plane || this.state.mprCrosshairPlane || this.state.mpr3dViewHint || 'axial'
    ).toLowerCase()
    const clipAxis =
      plane === 'sagittal' ? 'x' : plane === 'coronal' ? 'y' : 'z'
    const mark = this.computeMpr3dClip(point)
    if (!mark) return false
    const orient = opts.orient !== false
    this.setState({
      mpr3dMark: mark,
      mpr3dDecapado: true,
      mpr3dClipAxis: clipAxis,
      mpr3dClip: mark,
      mpr3dClipKeep: plane === 'sagittal' ? -1 : 1,
      mpr3dSlab: Number(this.state.mpr3dSlab) > 0 ? this.state.mpr3dSlab : 8,
      mprCrosshairPlane: plane,
      ...(orient
        ? {
            mpr3dViewHint: plane,
            mpr3dViewHintGen: (this.state.mpr3dViewHintGen || 0) + 1,
          }
        : {}),
    })
    return true
  }

  toggleMpr3dDecapado = () => {
    if (!this.state.visibleMpr3D) return
    const next = !this.state.mpr3dDecapado
    this.setState({
      mpr3dDecapado: next,
      mpr3dClip: next ? this.computeMpr3dClip() : this.state.mpr3dClip,
      mpr3dClipAxis: next ? (this.state.mpr3dClipAxis || 'z') : this.state.mpr3dClipAxis,
    })
  }

  setMpr3dClipAxis = (axis) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const next = axis === 'x' || axis === 'y' ? axis : 'z'
    this.setState({ mpr3dClipAxis: next })
  }

  toggleMpr3dClipKeep = () => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    this.setState({ mpr3dClipKeep: this.state.mpr3dClipKeep < 0 ? 1 : -1 })
  }

  nudgeMpr3dClip = (dir) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const vol = this._mpr3dVolume
    if (!vol || !vol.size) return
    const n1 = Math.max(1, vol.size - 1)
    const step = Math.max(1, Math.round(n1 * 0.02))
    this.nudgeMpr3dClipBy(dir > 0 ? step : -step)
  }

  nudgeMpr3dClipBy = (delta) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const vol = this._mpr3dVolume
    if (!vol || !vol.size) return
    const axis = this.state.mpr3dClipAxis === 'x' || this.state.mpr3dClipAxis === 'y'
      ? this.state.mpr3dClipAxis
      : 'z'
    const clip = this.state.mpr3dClip || this.computeMpr3dClip() || { x: 0, y: 0, z: 0 }
    this.setMpr3dClipTo((Number(clip[axis]) || 0) + Number(delta || 0))
  }

  remapMpr3dIfCached = (ww, wc) => {
    const vol = this._mpr3dVolume
    if (vol && vol.hu && vol.data) {
      if (!remapVolumeWwwc(vol, ww, wc)) return false
      this.setState({ mpr3dGen: (this.state.mpr3dGen || 0) + 1, mpr3dBuilding: false })
      return true
    }
    return false
  }

  setMpr3dPalette = (id, opts = {}) => {
    if (!this.state.visibleMpr3D) return
    const next = id || 'general'
    const prev = this.state.mpr3dPalette || 'general'
    const b = getPaletteVoi(next)
    const keepWwwc = !!opts.keepWwwc
    const paletteChanged = next !== prev
    if (keepWwwc) {
      if (!paletteChanged) return
      this.setState({ mpr3dPalette: next })
      return
    }
    const nextWw = b.ww
    const nextWc = b.wc
    const vol = this._mpr3dVolume
    const voiMismatch = !!(vol && (Number(vol.ww) !== nextWw || Number(vol.wc) !== nextWc))
    if (!paletteChanged && !voiMismatch) return
    this.setState({ mpr3dPalette: next, mpr3dWw: nextWw, mpr3dWc: nextWc }, () => {
      if (this.remapMpr3dIfCached(nextWw, nextWc)) return
      this.setState({ mpr3dBuilding: true }, () => {
        this.rebuildMpr3dVolume().catch((e) => {
          console.error('setMpr3dPalette', e)
          this.setState({ mpr3dBuilding: false })
        })
      })
    })
  }

  setMpr3dColorStyle = (id) => {
    if (!this.state.visibleMpr3D) return
    const next = id || 'anatomico'
    const prev = this.state.mpr3dColorStyle || 'anatomico'
    const voi = {
      ww: this.state.mpr3dWw != null ? Number(this.state.mpr3dWw) : getPaletteVoi(this.state.mpr3dPalette).ww,
      wc: this.state.mpr3dWc != null ? Number(this.state.mpr3dWc) : getPaletteVoi(this.state.mpr3dPalette).wc,
    }
    const vol = this._mpr3dVolume
    const stale = !!(vol && (Number(vol.ww) !== voi.ww || Number(vol.wc) !== voi.wc))
    if (next === prev && !stale) return
    this.setState({ mpr3dColorStyle: next }, () => {
      if (!stale) return
      if (this.remapMpr3dIfCached(voi.ww, voi.wc)) return
      this.setState({ mpr3dBuilding: true }, () => {
        this.rebuildMpr3dVolume().catch((e) => {
          console.error('setMpr3dColorStyle', e)
          this.setState({ mpr3dBuilding: false })
        })
      })
    })
  }

  setMpr3dWwwc = (ww, wc) => {
    if (!this.state.visibleMpr3D) return
    const nextWw = Math.max(1, Math.min(4000, Number(ww) || 400))
    const nextWc = Math.max(-1000, Math.min(3000, Number(wc) || 0))
    if (nextWw === this.state.mpr3dWw && nextWc === this.state.mpr3dWc) return
    this.setState({ mpr3dWw: nextWw, mpr3dWc: nextWc })
    if (this.state.mpr3dWwwcLink) this.applyWwwcToMprViewports(nextWw, nextWc)

    // Con caché HU: remapeo en vivo (sin “Preparando volumen 3D…”).
    const vol = this._mpr3dVolume
    if (vol && vol.hu && vol.data) {
      if (this._mpr3dWwwcTimer) {
        clearTimeout(this._mpr3dWwwcTimer)
        this._mpr3dWwwcTimer = null
      }
      this._mpr3dWwwcPending = { ww: nextWw, wc: nextWc }
      if (this._mpr3dWwwcRaf) return
      this._mpr3dWwwcRaf = requestAnimationFrame(() => {
        this._mpr3dWwwcRaf = 0
        const pend = this._mpr3dWwwcPending
        this._mpr3dWwwcPending = null
        if (!pend || !this.state.visibleMpr3D) return
        const cur = this._mpr3dVolume
        if (!cur || !remapVolumeWwwc(cur, pend.ww, pend.wc)) return
        this.setState({ mpr3dGen: (this.state.mpr3dGen || 0) + 1 })
      })
      return
    }

    // Fallback: rebuild completo (solo si aún no hay HU).
    if (this._mpr3dWwwcTimer) clearTimeout(this._mpr3dWwwcTimer)
    this._mpr3dWwwcTimer = setTimeout(() => {
      this._mpr3dWwwcTimer = null
      if (!this.state.visibleMpr3D) return
      const showBusy = !this._mpr3dVolume
      this.setState(showBusy ? { mpr3dBuilding: true } : {}, () => {
        this.rebuildMpr3dVolume({ ww: this.state.mpr3dWw, wc: this.state.mpr3dWc }).catch((e) => {
          console.error('setMpr3dWwwc', e)
          this.setState({ mpr3dBuilding: false })
        })
      })
    }, 280)
  }

  syncMpr3dWwwcFromMpr = () => {
    if (!this.state.visibleMpr3D) return
    const voi = this.getActiveWwwc()
    this.setMpr3dWwwc(voi.ww, voi.wc)
  }

  toggleMpr3dWwwcLink = () => {
    const next = !this.state.mpr3dWwwcLink
    this.setState({ mpr3dWwwcLink: next }, () => {
      saveMpr3dSession({ wwwcLink: next })
      if (next) this.syncMpr3dWwwcFromMpr()
    })
  }

  applyWwwcToMprViewports = (ww, wc) => {
    if (typeof window === 'undefined' || !window.cornerstone) return
    this._applyingWwwcToMpr = true
    try {
      const indices = this.getActiveMprIndices() || []
      const refs = this.dicomViewersRefs || []
      for (let k = 0; k < indices.length; k++) {
        const v = refs[indices[k]]
        if (!v || v.isMpr3d) continue
        const el = v.dicomImage
        if (!el) continue
        try {
          const vp = window.cornerstone.getViewport(el)
          if (vp && vp.voi) {
            vp.voi.windowWidth = ww
            vp.voi.windowCenter = wc
            window.cornerstone.setViewport(el, vp)
          }
        } catch (e) { /* ignore */ }
      }
    } finally {
      this._applyingWwwcToMpr = false
    }
  }

  onMprVoiChange = (ww, wc) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dWwwcLink) return
    if (this._applyingWwwcToMpr) return
    this.setMpr3dWwwc(ww, wc)
  }

  /**
   * ¿Propagar WW/WC? Solo si el toggle Link WW/WL está ON
   * (válido para Link Series y para planos MPR).
   */
  shouldBroadcastWwwcLive = () => !!this.state.seriesLinkWwwc

  /**
   * Destinos del ventaneo sincronizado: slots visibles del layout con imagen.
   * @param {number} masterIndex
   * @returns {Array<{ viewer: object, index: number }>}
   */
  collectWwwcBroadcastTargets = (masterIndex) => {
    const refs = this.dicomViewersRefs || []
    const layout = this.props.layout || [1, 1]
    const rows = layout[0] || 1
    const cols = layout[1] || 1
    const isOpen = this.props.isOpen
    const inMpr =
      this.state.mprMode ||
      (this.isMprLayoutActive && this.isMprLayoutActive())
    const out = []

    if (inMpr && typeof this.getActiveMprIndices === 'function') {
      const indices = this.getActiveMprIndices() || []
      for (let k = 0; k < indices.length; k++) {
        const i = indices[k]
        if (i === masterIndex) continue
        const v = refs[i]
        if (!v || (!v.image && !v.dicomImage)) continue
        if (v.isMpr3d) continue
        out.push({ viewer: v, index: i })
      }
      if (out.length) return out
    }

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * 4 + c
        if (i === masterIndex) continue
        const v = refs[i]
        if (!v) continue
        if (Array.isArray(isOpen) && !isOpen[i]) continue
        if (!v.image && !v.dicomImage) continue
        if (v.isMpr3d) continue
        out.push({ viewer: v, index: i })
      }
    }
    return out
  }

  /**
   * Aplica WW/WC en vivo a viewports hermanos (Link WW/WL ON).
   * @param {number} masterIndex
   * @param {number} ww
   * @param {number} wc
   */
  broadcastWwwcLive = (masterIndex, ww, wc) => {
    if (this._wwwcBroadcasting) return
    if (!this.shouldBroadcastWwwcLive()) return
    const w = Math.max(1, Number(ww))
    const c = Number(wc)
    if (!Number.isFinite(w) || !Number.isFinite(c)) return

    const targets = this.collectWwwcBroadcastTargets(masterIndex)
    if (!targets.length) return

    this._wwwcBroadcasting = true
    try {
      for (let i = 0; i < targets.length; i++) {
        const { viewer: v, index: tIdx } = targets[i]
        if (!v || typeof v.applyClinicalVoi !== 'function') continue
        try {
          v.applyClinicalVoi(w, c, { fromBroadcast: true })
          // Link WW ON: alinear slots de los hermanos al mismo VOI
          let seriesUid = null
          try {
            seriesUid = this.resolveActiveSeriesUID(v, { localOnly: true })
          } catch (eUid) {
            seriesUid = null
          }
          if (!seriesUid && typeof v.getActiveSeriesUidKey === 'function') {
            seriesUid = v.getActiveSeriesUidKey()
          }
          if (seriesUid && tIdx != null) {
            this.rememberViewportWwwc(tIdx, seriesUid, w, c)
          }
        } catch (e) {
          /* ignore */
        }
      }
    } finally {
      this._wwwcBroadcasting = false
    }
  }

  /**
   * Callback de DicomViewer al ventanear / preset: sync en tiempo real.
   * Persiste WW/WC de sesión solo si el usuario ventaneó (no 255/128 preview).
   */
  handleWwwcLiveChange = (ww, wc, viewerIndex) => {
    const idx =
      typeof viewerIndex === 'number'
        ? viewerIndex
        : typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
    // Persistencia de sesión: solo VOI clínico de usuario (no SC/preview local)
    try {
      if (!this.isPreviewWwwcContamination(ww, wc)) {
        const viewer = this.dicomViewersRefs && this.dicomViewersRefs[idx]
        if (
          viewer &&
          typeof viewer.isActiveLocalWwwcFrame === 'function' &&
          viewer.isActiveLocalWwwcFrame()
        ) {
          // Frame SC/preview: no recordar en mapa de serie
        } else {
        let seriesUid = null
        if (viewer) {
          try {
            seriesUid = this.resolveActiveSeriesUID(viewer, { localOnly: true })
          } catch (eUid) {
            seriesUid = null
          }
          if (!seriesUid && typeof viewer.getActiveSeriesUidKey === 'function') {
            seriesUid = viewer.getActiveSeriesUidKey()
          }
        }
        let userSet = false
        if (seriesUid && viewer) {
          if (typeof viewer.isSeriesWwwcUserSet === 'function') {
            userSet = !!viewer.isSeriesWwwcUserSet(seriesUid)
          } else {
            const st =
              viewer.toolState && viewer.toolState[seriesUid]
                ? viewer.toolState[seriesUid]
                : null
            userSet = !!(st && st.wwwcUserSet)
          }
        }
        if (seriesUid && userSet) {
          this.rememberViewportWwwc(idx, seriesUid, ww, wc)
        }
        }
      }
    } catch (eMem) {
      /* ignore */
    }

    if (this._wwwcBroadcasting) return
    if (!this.shouldBroadcastWwwcLive()) return
    this._pendingWwwcLive = { ww, wc, viewerIndex: idx }
    if (this._wwwcLiveRaf) return
    const flush = () => {
      this._wwwcLiveRaf = null
      const p = this._pendingWwwcLive
      if (!p) return
      this._pendingWwwcLive = null
      this.broadcastWwwcLive(p.viewerIndex, p.ww, p.wc)
      try {
        this.onMprVoiChange(p.ww, p.wc)
      } catch (e) {
        /* ignore */
      }
    }
    if (typeof requestAnimationFrame === 'function') {
      this._wwwcLiveRaf = requestAnimationFrame(flush)
    } else {
      this._wwwcLiveRaf = setTimeout(flush, 0)
    }
  }

  toggleMpr3dMaximize = () => {
    this.setState({ mpr3dMaximized: !this.state.mpr3dMaximized })
  }

  mpr3dMeasureTool = (tool) => {
    this.activateViewerTool(tool || 'Length')
  }

  mpr3dQuickPreset = (id) => {
    if (id === 'lesion') {
      this.setMpr3dPalette && this.setMpr3dPalette('cerebro')
      this.setMpr3dWwwc(80, 40)
      this.setState({ mpr3dColorStyle: 'anatomico' })
    }
  }

  setMpr3dSegMode = (mode) => {
    const next = mode === 'mask' || mode === 'mesh' || mode === 'overlay' ? mode : 'off'
    const phone = isPhoneLikeDevice()
    const resolved = phone && next === 'overlay' ? 'mesh' : next
    this.setState({
      mpr3dSegMode: resolved,
      mpr3dMaskOn: resolved === 'mask' || resolved === 'overlay',
    })
  }

  toggleMpr3dLayer = (id) => {
    const layers = (this.state.mpr3dLayers || []).map((ly) =>
      ly.id === id ? { ...ly, visible: !ly.visible } : ly
    )
    this.setState({ mpr3dLayers: layers })
    const roi = layers.find((l) => l.id === 'roi')
    if (id === 'roi') this.setState({ mpr3dMaskOn: !!(roi && roi.visible) })
  }

  applyMpr3dMask = (mask) => {
    const vol = this._mpr3dVolume
    if (!vol || !mask) return
    this._mpr3dMask = mask
    const quant = quantifyMaskHu(mask, vol)
    const box = maskBBox(mask, vol.size)
    this.setState({
      mpr3dMaskOn: true,
      mpr3dSegMode: 'mask',
      mpr3dQuant: quant,
      mpr3dIsolateBox: box,
      mpr3dMaskError: null,
      mpr3dGen: (this.state.mpr3dGen || 0) + 1,
    })
  }

  toggleMpr3dBaseVisible = () => {
    this.setState({ mpr3dBaseVisible: this.state.mpr3dBaseVisible === false })
  }

  toggleMpr3dMaskVisible = () => {
    if (!this._mpr3dMask) {
      this.setState({ mpr3dMaskError: 'No hay máscara. Cargá un DICOM-SEG o ?segUrl=' })
      return
    }
    const next = !this.state.mpr3dMaskOn
    this.setState({
      mpr3dMaskOn: next,
      mpr3dSegMode: next ? 'mask' : 'off',
    })
  }

  isolateMpr3dStructure = () => {
    const vol = this._mpr3dVolume
    const mask = this._mpr3dMask
    if (!vol || !mask) {
      this.setState({ mpr3dMaskError: 'No hay lesión para aislar.' })
      return
    }
    const box = maskBBox(mask, vol.size)
    const quant = quantifyMaskHu(mask, vol)
    this.setState({
      mpr3dSegMode: 'mask',
      mpr3dMaskOn: true,
      mpr3dBaseVisible: false,
      mpr3dIsolateBox: box,
      mpr3dQuant: quant,
      mpr3dIsolateGen: (this.state.mpr3dIsolateGen || 0) + 1,
      mpr3dGen: (this.state.mpr3dGen || 0) + 1,
    })
  }

  loadMpr3dMaskFile = (file) => {
    if (!file || !this._mpr3dVolume) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const mask = decodeMaskBuffer(new Uint8Array(reader.result || []), this._mpr3dVolume)
        this.applyMpr3dMask(mask)
      } catch (e) {
        this.setState({ mpr3dMaskError: (e && e.message) || 'No se pudo leer el SEG.' })
      }
    }
    reader.readAsArrayBuffer(file)
  }

  loadMpr3dMaskFromUrl = async (url) => {
    if (!url || !this._mpr3dVolume) return
    try {
      const mask = await fetchMaskFromUrl(url, this._mpr3dVolume)
      this.applyMpr3dMask(mask)
    } catch (e) {
      this.setState({ mpr3dMaskError: (e && e.message) || 'Fallo al traer la máscara.' })
    }
  }

  loadMpr3dMeshFile = (file) => {
    if (!file) return
    if (this._mpr3dMeshUrl) {
      try { URL.revokeObjectURL(this._mpr3dMeshUrl) } catch (e) { /* ignore */ }
    }
    const url = URL.createObjectURL(file)
    this._mpr3dMeshUrl = url
    this.setState({ mpr3dMeshUrl: url, mpr3dSegMode: 'mesh' })
  }

  setMpr3dClipTo = (value) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const vol = this._mpr3dVolume
    if (!vol || !vol.size) return
    const axis = this.state.mpr3dClipAxis === 'x' || this.state.mpr3dClipAxis === 'y'
      ? this.state.mpr3dClipAxis
      : 'z'
    const n1 = Math.max(1, vol.size - 1)
    const clip = { ...(this.state.mpr3dClip || this.computeMpr3dClip() || { x: 0, y: 0, z: 0 }) }
    clip[axis] = Math.max(0, Math.min(n1, Number(value) || 0))
    this.setState({ mpr3dClip: clip, mpr3dMark: clip })
    this.scheduleMprFromMpr3dClip(clip)
  }

  scheduleMprFromMpr3dClip = (clip) => {
    if (this._mpr3dMprSyncTimer) clearTimeout(this._mpr3dMprSyncTimer)
    this._mpr3dMprSyncTimer = setTimeout(() => {
      this._mpr3dMprSyncTimer = null
      this.syncMprFromMpr3dClip(clip)
    }, 70)
  }

  syncMprFromMpr3dClip = (clipOverride) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const vol = this._mpr3dVolume
    const size = this.getMprVolumeSize()
    if (!vol || !size) return
    const clip = clipOverride || this.state.mpr3dClip
    if (!clip) return
    const mapped = volumeGridToPoint(clip, size, vol)
    const cur = this.state.mprCrosshairPoint || mapped
    const axis = this.state.mpr3dClipAxis === 'x' || this.state.mpr3dClipAxis === 'y'
      ? this.state.mpr3dClipAxis
      : 'z'
    const next = { x: cur.x, y: cur.y, z: cur.z }
    if (axis === 'x') next.x = mapped.x
    else if (axis === 'y') next.y = mapped.y
    else next.z = mapped.z
    const plane = axis === 'x' ? 'sagittal' : axis === 'y' ? 'coronal' : 'axial'
    this.applyMprCrosshairPoint(next, { plane, quiet: true, from3d: true })
  }

  setMpr3dSlab = (value) => {
    if (!this.state.visibleMpr3D) return
    const vol = this._mpr3dVolume
    const n1 = vol && vol.size ? Math.max(1, vol.size - 1) : 64
    const next = Math.max(0, Math.min(n1, Math.round(Number(value) || 0)))
    this.setState({ mpr3dSlab: next, mpr3dDecapado: next > 0 || this.state.mpr3dDecapado })
  }

  nudgeMpr3dSlab = (dir) => {
    if (!this.state.visibleMpr3D || !this.state.mpr3dDecapado) return
    const vol = this._mpr3dVolume
    const n1 = vol && vol.size ? Math.max(1, vol.size - 1) : 64
    const cur = Number(this.state.mpr3dSlab) > 0 ? Number(this.state.mpr3dSlab) : 8
    const step = Math.max(1, Math.round(n1 * 0.02))
    const next = Math.max(1, Math.min(Math.round(n1 * 0.45), cur + (dir > 0 ? step : -step)))
    this.setState({ mpr3dSlab: next })
  }

  scheduleMpr3dWwwcRebuild = () => {
    /* El 3D usa ventana CT amplia fija para poder separar hueso / tejido / parénquima. */
  }

  hideMpr3D = () => {
    this.disposeMpr3dVolume()
    this._mpr3dMask = null
    this.setState({
      visibleMpr3D: false,
      mpr3dBuilding: false,
      mpr3dClip: null,
      mpr3dDecapado: false,
      mpr3dClipAxis: 'z',
      mpr3dClipKeep: 1,
      mpr3dMark: null,
      mpr3dMaximized: false,
      mpr3dBuildZi: 0,
      mpr3dBuildN: 0,
      mpr3dSegMode: 'off',
      mpr3dMaskOn: false,
      mpr3dBaseVisible: true,
      mpr3dQuant: null,
      mpr3dMaskError: null,
      mpr3dMeshUrl: '',
    }, () => {
      if (!isPhoneLikeDevice() && this.state.visibleMprOrthogonal) {
        this.changeToOrthogonalView()
      }
    })
  }

  showMpr3D = async () => {
    const files = this.getMprStackFiles()
    const size = this.getMprMatrixSize(files)
    const profile = resolveMpr3dProfile({
      cols: size && size.cols,
      rows: size && size.rows,
      nSlices: files && files.length,
      mprBlocked: this.state.mprProfile === 'blocked',
      sessionOff: !!this._mpr3dSessionOff,
    })
    if (!mpr3dProfileAllows(profile)) {
      this.setState({
        mpr3dProfile: profile,
        titleMessage: '3D',
        textMessage: mpr3dProfileLabel('off'),
        visibleMessage: true,
      })
      return
    }
    const inferred = inferMpr3dPaletteFromFiles(files)
    const voi0 = getPaletteVoi(inferred)
    const paint = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

    await new Promise((resolve) => {
      this.setState({
        mpr3dProfile: profile,
        visibleMpr3D: true,
        mpr3dBuilding: true,
        mpr3dBuildZi: 0,
        mpr3dBuildN: 0,
        mpr3dDecapado: false,
        mpr3dClipAxis: this.state.mprCrosshairPlane
          ? (this.state.mprCrosshairPlane === 'sagittal'
            ? 'x'
            : this.state.mprCrosshairPlane === 'coronal'
              ? 'y'
              : 'z')
          : 'z',
        mpr3dClipKeep: this.state.mprCrosshairPlane
          ? (this.state.mprCrosshairPlane === 'sagittal' ? -1 : 1)
          : 1,
        mpr3dPalette: inferred,
        mpr3dPaletteInferred: inferred,
        mpr3dWw: voi0.ww,
        mpr3dWc: voi0.wc,
        visibleMprOrthogonal: true,
        visibleMprSagittal: false,
        visibleMprCoronal: false,
        visibleMprAxial: false,
      }, resolve)
    })
    await paint()

    this.startMprLivePoller()
    if (!isPhoneLikeDevice()) {
      const cur = this.props.layout || [1, 1]
      if (cur[0] !== 2 || cur[1] !== 2) this.changeLayout(2, 2)
      await paint()
      this.changeToOrthogonalView()
      await paint()
    }

    try {
      await this.rebuildMpr3dVolume()
    } catch (e) {
      if (e && e.message === '3D cancelado.') return
      console.error('showMpr3D', e)
      this._mpr3dSessionOff = true
      this.disposeMpr3dVolume()
      this.setState({
        visibleMpr3D: false,
        mpr3dBuilding: false,
        mpr3dClip: null,
        mpr3dDecapado: false,
        mpr3dClipAxis: 'z',
        mpr3dClipKeep: 1,
        mpr3dProfile: 'off',
        titleMessage: '3D',
        textMessage: 'No se pudo armar el 3D en este dispositivo.',
        visibleMessage: true,
      })
      if (!isPhoneLikeDevice() && this.state.visibleMprOrthogonal) {
        this.changeToOrthogonalView()
      }
    }
  }

  rebuildMpr3dVolume = async (voiOverride) => {
    const files = this.getMprStackFiles()
    if (!files || !files.length) throw new Error('Sin stack para 3D.')
    if (this._mpr3dAbort) this._mpr3dAbort.aborted = true
    const signal = { aborted: false }
    this._mpr3dAbort = signal
    const voi = voiOverride || {
      ww: this.state.mpr3dWw != null ? Number(this.state.mpr3dWw) : getPaletteVoi(this.state.mpr3dPalette).ww,
      wc: this.state.mpr3dWc != null ? Number(this.state.mpr3dWc) : getPaletteVoi(this.state.mpr3dPalette).wc,
    }
    const n = resolveMpr3dSize()
    let flipZ = !!(this.mprData && this.mprData.flipZ)
    if (this.mprData && this.mprData.flipZ == null) {
      try {
        flipZ = shouldFlipMprZ(files, this.mprData)
      } catch (e) {
        flipZ = false
      }
    }
    const vol = await buildUint8VolumeMaybeWorker(files, {
      n,
      ww: voi.ww,
      wc: voi.wc,
      flipZ,
      signal,
      dx: this.mprData && this.mprData.columnPixelSpacing,
      dy: this.mprData && this.mprData.rowPixelSpacing,
      dz: this.mprData && this.mprData.zSpacing,
      image: files[0] && files[0].image,
      onProgress: (zi, nn) => {
        const now = Date.now()
        if (zi < nn && this._mpr3dProgAt && now - this._mpr3dProgAt < 80) return
        this._mpr3dProgAt = now
        this.setState({ mpr3dBuildZi: zi, mpr3dBuildN: nn })
      },
    })
    if (signal.aborted) return
    disposeVolume(this._mpr3dVolume)
    this._mpr3dVolume = vol
    const hasAim = !!this.state.mprCrosshairPoint
    const after = () => {
      const url = this._mpr3dSegUrl || this.state.mpr3dSegUrl
      if (url) this.loadMpr3dMaskFromUrl(url)
    }
    if (hasAim) {
      const shouldOrient = !this.state.mpr3dDecapado
      this.setState({ mpr3dBuilding: false, mpr3dGen: (this.state.mpr3dGen || 0) + 1 }, () => {
        this.applyCrosshairPeelToMpr3d({ orient: shouldOrient })
        after()
      })
      return
    }
    this.setState({
      mpr3dBuilding: false,
      mpr3dClip: this.state.mpr3dDecapado ? this.computeMpr3dClip() : this.state.mpr3dClip,
      mpr3dMark: null,
      mpr3dGen: (this.state.mpr3dGen || 0) + 1,
    }, after)
  }

  mprOrthogonal = () => {
    if (this.state.visibleMprOrthogonal) {
      this.exitMpr()
      return
    }
    this.beginMpr('orthogonal')
  }

  mprSagittal = () => {
    if (this.state.visibleMprSagittal) {
      this.exitMpr()
      return
    }
    this.beginMpr('sagittal')
  }
    
  mprCoronal = () => {
    if (this.state.visibleMprCoronal) {
      this.exitMpr()
      return
    }
    this.beginMpr('coronal')
  }

  mprAxial = () => {
    if (this.state.visibleMprAxial) {
      this.exitMpr()
      return
    }
    this.beginMpr('axial')
  }

  // Nuevo método para cambiar el método de líneas de referencia
  changeReferenceLinesMethod = (method) => {
    // Solo CornerstoneTools; el selector de UI fue eliminado
    const forced = REFERENCE_LINES_METHODS.CORNERSTONE_TOOLS
    this.setState({ referenceLinesMethod: forced }, () => {
      ReferenceLinesManager.setMethod(forced)
      if (this.state.visibleReferenceLines) {
        this.setupCornerstoneToolsReferenceLines()
      }
    })
  }

    // Configurar elementos para CornerstoneTools siguiendo el patrón oficial
    setupCornerstoneToolsReferenceLines = () => {
      if (this.isMprLayoutActive()) {
        this.disableCornerstoneReferenceLinesForMpr()
        return
      }
      console.log('🔧 setupCornerstoneToolsReferenceLines - Iniciando configuración')
      console.log('   this.dicomViewersActive:', this.dicomViewersActive)
      console.log('   Longitud de dicomViewersActive:', this.dicomViewersActive ? this.dicomViewersActive.length : 'undefined')
      
      if (this.dicomViewersActive && this.dicomViewersActive.length >= 2) {
        console.log('   ✅ Suficientes viewers activos, configurando CornerstoneTools')
        
        // Limpiar elementos anteriores
        ReferenceLinesManager.clearElements()
        console.log('   Elementos anteriores limpiados')
        
        // Obtener elementos habilitados
        const enabledElements = []
        this.dicomViewersActive.forEach((viewer, index) => {
          console.log(`   Viewer ${index + 1}:`, {
            viewer: viewer,
            dicomImage: viewer.dicomImage,
            id: viewer.dicomImage ? viewer.dicomImage.id : 'sin-id'
          })
          
          if (viewer.dicomImage) {
            try {
              const enabledElement = window.cornerstone && window.cornerstone.getEnabledElement(viewer.dicomImage)
              if (enabledElement) {
                enabledElements.push(viewer.dicomImage)
                console.log(`   ✅ Elemento ${index + 1} está habilitado`)
              } else {
                console.log(`   ⚠️ Elemento ${index + 1} no está habilitado por Cornerstone`)
              }
            } catch (error) {
              console.log(`   ❌ Error verificando elemento ${index + 1}: ${error.message}`)
            }
          } else {
            console.log(`   ⚠️ Viewer ${index + 1} no tiene dicomImage`)
          }
        })
        
        console.log(`   Total elementos habilitados: ${enabledElements.length}`)
        
        if (enabledElements.length >= 2) {
          // Configurar CornerstoneTools siguiendo el patrón oficial
          this.configureCornerstoneToolsReferenceLines(enabledElements)
        } else {
          console.log('   ⏳ Esperando a que los elementos estén habilitados...')
          // Esperar a que los elementos estén habilitados
          this.waitForEnabledElementsAndConfigure()
        }
      } else {
        console.log('   ❌ No hay suficientes viewers activos para CornerstoneTools')
        console.log('   💡 Se requiere al menos 2 viewers activos')
        
        // Intentar agregar elementos manualmente desde el DOM
        console.log('   🔄 Intentando agregar elementos manualmente desde el DOM...')
        const viewports = document.querySelectorAll('.cornerstone-enabled-image, .cornerstone-canvas')
        console.log('   Viewports encontrados en DOM:', viewports.length)
        
        const enabledViewports = []
        viewports.forEach((viewport, index) => {
          try {
            const enabled = window.cornerstone && window.cornerstone.getEnabledElement(viewport)
            if (enabled) {
              enabledViewports.push(viewport)
              console.log(`   ✅ Viewport ${index + 1} está habilitado:`, viewport.id || 'sin-id')
            } else {
              console.log(`   ⚠️ Viewport ${index + 1} no está habilitado`)
            }
          } catch (error) {
            console.log(`   ❌ Error verificando viewport ${index + 1}: ${error.message}`)
          }
        })
        
        console.log(`   Total viewports habilitados: ${enabledViewports.length}`)
        
        if (enabledViewports.length >= 2) {
          console.log('   ✅ Suficientes viewports habilitados')
          this.configureCornerstoneToolsReferenceLines(enabledViewports)
        } else {
          console.log('   ⏳ Esperando a que los viewports estén habilitados...')
          this.waitForEnabledElementsAndConfigure()
        }
      }
    }

    // Configurar CornerstoneTools Reference Lines siguiendo el patrón oficial
    configureCornerstoneToolsReferenceLines = (elements) => {
      if (this.isMprLayoutActive()) {
        this.disableCornerstoneReferenceLinesForMpr()
        return
      }
      console.log('🔧 configureCornerstoneToolsReferenceLines - Configurando con elementos:', elements.length)
      
      try {
        if (!window.cornerstoneTools || !window.cornerstoneTools.Synchronizer) {
          throw new Error('CornerstoneTools o Synchronizer no disponible')
        }

        try {
          this.registerReferenceLinePlanesForActiveViewers()
          this.harmonizeReferenceLineFoRForActiveViewers()
        } catch (eReg) {
          /* ignore */
        }

        // Tirar sync anterior (elementos desconectados tras rotación)
        try {
          if (ReferenceLinesManager.synchronizer && ReferenceLinesManager.elements) {
            ReferenceLinesManager.elements.forEach((el) => {
              try { ReferenceLinesManager.synchronizer.remove(el) } catch (e) { /* ignore */ }
            })
          }
        } catch (e) { /* ignore */ }
        ReferenceLinesManager.elements = []
        
        const synchronizer = new window.cornerstoneTools.Synchronizer(
          'cornerstonenewimage',
          window.cornerstoneTools.updateImageSynchronizer
        )
        
        elements.forEach((element, index) => {
          try {
            synchronizer.add(element)
            console.log(`   ✅ Elemento ${index + 1} agregado al sincronizador`)
          } catch (error) {
            console.log(`   ❌ Error agregando elemento ${index + 1}: ${error.message}`)
          }
        })
        
        if (window.cornerstoneTools.ReferenceLinesTool) {
          try {
            window.cornerstoneTools.addTool(window.cornerstoneTools.ReferenceLinesTool)
          } catch (error) {
            console.log('   ℹ️ Herramienta ReferenceLines ya existe o error:', error.message)
          }
        }

        // disable→enable: enabledCallback async por elemento (2.º viewport)
        try {
          if (typeof window.cornerstoneTools.setToolDisabled === 'function') {
            window.cornerstoneTools.setToolDisabled('ReferenceLines')
          }
        } catch (eDis) {
          /* ignore */
        }
        
        window.cornerstoneTools.setToolEnabled('ReferenceLines', {
          synchronizationContext: synchronizer,
          // Renderer custom se aplica vía patch (oculta líneas en viewport activo)
        })

        ReferenceLinesManager.currentMethod = DEFAULT_REFERENCE_LINES_METHOD
        ReferenceLinesManager.isEnabled = true
        ReferenceLinesManager.synchronizer = synchronizer
        ReferenceLinesManager.elements = elements.slice()

        this.patchReferenceLinesRenderer()
        this._needsReferenceLinesResync = false
        this._refLinesSetupCooldownUntil = Date.now() + 500

        // Pintar líneas de inmediato
        this._refLinesRefreshing = true
        elements.forEach((el) => {
          try {
            if (window.cornerstone) window.cornerstone.updateImage(el)
          } catch (e) { /* ignore */ }
        })
        setTimeout(() => {
          this._refLinesRefreshing = false
        }, 120)
        
        console.log('   🎉 CornerstoneTools ReferenceLines configurado exitosamente')
      } catch (error) {
        console.log(`   ❌ Error configurando CornerstoneTools: ${error.message}`)
      }
    }

  patchReferenceLinesRenderer = () => {
      try {
        const store = window.cornerstoneTools && window.cornerstoneTools.store
        const tools =
          (store && store.state && Array.isArray(store.state.tools) && store.state.tools) ||
          []
        const self = this
        for (let i = 0; i < tools.length; i++) {
          const tool = tools[i]
          if (!tool || tool.name !== 'ReferenceLines') continue
          const cfg = tool.configuration || (tool.configuration = {})
          if (!tool._udvOrigRenderer && typeof cfg.renderer === 'function') {
            tool._udvOrigRenderer = cfg.renderer
          }
          const orig =
            tool._udvOrigRenderer ||
            (typeof cfg.renderer === 'function' ? cfg.renderer : null)

          const wrapped = function (context, eventData, targetElement, referenceElement) {
            if (self.state && self.state.hideReferenceLinesOnActiveViewport) {
              const idx =
                typeof self._navActiveIndex === 'number'
                  ? self._navActiveIndex
                  : self.props && self.props.activeDcmIndex
              const v =
                self.dicomViewersRefs && typeof idx === 'number'
                  ? self.dicomViewersRefs[idx]
                  : null
              if (v && v.dicomImage && targetElement === v.dicomImage) {
                return
              }
            }
            // Proyección sobre target (FOV asimétrico sag↔cor)
            try {
              if (
                udvRenderActiveReferenceLine(
                  context,
                  eventData,
                  targetElement,
                  referenceElement
                )
              ) {
                return
              }
            } catch (eUdv) {
              /* fall through a CT stock */
            }
            if (typeof orig === 'function') {
              return orig(context, eventData, targetElement, referenceElement)
            }
          }
          cfg.renderer = wrapped
          tool.renderer = wrapped
        }
      } catch (e) { /* ignore */ }
    }

    // Esperar a que los elementos estén habilitados y luego configurar
    waitForEnabledElementsAndConfigure = () => {
      console.log('⏳ waitForEnabledElementsAndConfigure - Esperando elementos habilitados...')
      
      let attempts = 0
      const maxAttempts = 20
      const delay = 500
      
      const checkElements = () => {
        attempts++
        console.log(`   🔍 Intento ${attempts}/${maxAttempts}`)
        
        const viewports = document.querySelectorAll('.cornerstone-enabled-image, .cornerstone-canvas')
        const enabledElements = []
        
        viewports.forEach((viewport, index) => {
          try {
            const enabled = window.cornerstone && window.cornerstone.getEnabledElement(viewport)
            if (enabled) {
              enabledElements.push(viewport)
              console.log(`   ✅ Viewport ${index + 1}: Habilitado`)
            } else {
              console.log(`   ❌ Viewport ${index + 1}: No habilitado`)
            }
          } catch (error) {
            console.log(`   ❌ Viewport ${index + 1}: Error - ${error.message}`)
          }
        })
        
        console.log(`   Total habilitados: ${enabledElements.length}`)
        
        if (enabledElements.length >= 2) {
          console.log('   ✅ Suficientes elementos habilitados!')
          this.configureCornerstoneToolsReferenceLines(enabledElements)
        } else if (attempts < maxAttempts) {
          console.log(`   ⏳ Esperando ${delay}ms antes del siguiente intento...`)
          setTimeout(checkElements, delay)
        } else {
          console.log('   ❌ Timeout: No se pudieron habilitar suficientes elementos')
        }
      }
      
      checkElements()
    }

  // #endregion 
  
  // ---------------------------------------------------------------------------------------------- FILES/SLICE MANIPULATION
  //#region FILES/SLICE MANIPULATION

  listOpenFilesFirstFrame = () => {
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (viewer && viewer.numberOfFrames > 1 && typeof viewer.gotoMultiFrameIndex === 'function') {
      viewer.gotoMultiFrameIndex(0)
      return
    }
    const index = 0
    this.setState({sliceIndex: index}, () => {
      this.isSliceChange = true
      this.handleOpenImage(index)
    })
  }

  listOpenFilesPreviousFrame = () => {
    this.advanceStackBySteps(-1)
  }

  listOpenFilesNextFrame = () => {
    this.advanceStackBySteps(1)
  }

  /**
   * Avanza el stack N slices (positivo = next). Índice síncrono para gestos
   * táctiles rápidos (evita setState async + isNavigating que perdían frames).
   * Multi-frame (XA): navega frames del SOP (scrollToIndex), no file index.
   */
  advanceStackBySteps = (delta) => {
    if (!delta) return
    const activeIdx = this.getNavActiveIndex()
    const viewer = this.dicomViewersRefs && this.dicomViewersRefs[activeIdx]

    if (
      viewer &&
      viewer.numberOfFrames > 1 &&
      typeof viewer.advanceMultiFrameBySteps === 'function'
    ) {
      viewer.advanceMultiFrameBySteps(delta)
      return
    }

    // MPR reconstruido: navegar solo por sliceMax del viewport (cols/rows), no por files.length
    const mprGenerated =
      this.state.mprMode &&
      activeIdx > 0 &&
      viewer &&
      viewer.isMprGenerated

    const sliceMax = mprGenerated
      ? Math.max(1, (viewer && viewer.sliceMax) || this.state.sliceMax || 0)
      : Math.max(
          this.state.sliceMax || 0,
          (viewer && viewer.files && viewer.files.length) || 0,
          (viewer && viewer.sliceMax) || 0
        )
    if (!sliceMax || sliceMax < 2) return

    if (this._navSliceIndex == null || Number.isNaN(this._navSliceIndex)) {
      this._navSliceIndex =
        viewer && viewer.sliceIndex != null
          ? viewer.sliceIndex
          : this.state.sliceIndex || 0
    }

    let index = this._navSliceIndex + delta
    while (index < 0) index += sliceMax
    while (index >= sliceMax) index -= sliceMax
    this._navSliceIndex = index

    if (this.state.sliceIndex !== index || this.state.sliceMax !== sliceMax) {
      this.setState({ sliceIndex: index, sliceMax })
    }
    this.isSliceChange = true
    if (mprGenerated || this.state.mprMode) this.markMprInteract()
    this.handleOpenImage(index, activeIdx)

    this.maybeUpgradeActiveFrameForPixelTool()

    // En MPR el Link 2D cede a la mira; el scroll mueve los tres planos
    if (mprGenerated || this.state.mprMode) {
      this.syncMprScrollToCrosshair(activeIdx, index)
      return
    }

    this.scheduleSeriesLinkSync(activeIdx)
  }

  listOpenFilesLastFrame = () => {
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (viewer && viewer.numberOfFrames > 1 && typeof viewer.gotoMultiFrameIndex === 'function') {
      viewer.gotoMultiFrameIndex(viewer.numberOfFrames - 1)
      return
    }
    const index = this.state.sliceMax - 1
    this._navSliceIndex = index
    this.setState({ sliceIndex: index }, () => {
      this.isSliceChange = true
      this.handleOpenImage(index)
    })
  }

  listOpenFilesScrolling = () => {
    const scrolling = this.state.listOpenFilesScrolling
    this.setState({ listOpenFilesScrolling: !scrolling }, () => {
      if (scrolling) {
        clearInterval(this.timerScrolling)
      } else {
        this.timerScrolling = setInterval(() => {
          this.listOpenFilesNextFrame()
        }, 500)
      }
    })
  }

  handleSliceChange = (event, value) => {
    const newIndex = Math.floor(value)
    const viewer =
      this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]

    // Multi-frame XA: el slider mueve frames, no SOP del stack
    if (
      viewer &&
      viewer.numberOfFrames > 1 &&
      typeof viewer.gotoMultiFrameIndex === 'function'
    ) {
      this._navSliceIndex = newIndex
      this.setState({
        sliceIndex: newIndex,
        sliceMax: viewer.numberOfFrames,
      })
      viewer.gotoMultiFrameIndex(newIndex)
      return
    }

    this._navSliceIndex = newIndex
    this.setState({sliceIndex: newIndex}, () => {
        // Actualizar el índice en el viewport activo
        this.dicomViewersRefs[this.props.activeDcmIndex].sliceIndex = newIndex

        // Si las líneas de referencia están visibles, actualizarlas inmediatamente
        if (this.state.visibleReferenceLines && this.dicomViewersActive.length > 1) {
            this.dicomViewersRefs[this.props.activeDcmIndex].updateImage()
        }
        
        // Llamar a handleOpenImage para actualizar la imagen
        this.handleOpenImage(newIndex)
        this.maybeUpgradeActiveFrameForPixelTool()
    })
  }

  //#endregion

  // ---------------------------------------------------------------------------------------------- EXPLORER
  //#region EXPLORER

  isMultiViewportLayout = () => {
    const layout = this.props.layout || [1, 1]
    return (layout[0] || 1) > 1 || (layout[1] || 1) > 1
  }

  studyUidFromViewportIndex = (index) => {
    const v = this.dicomViewersRefs && this.dicomViewersRefs[index]
    if (!v) return ''
    const files = Array.isArray(v.files) ? v.files : null
    if (!files || !files.length) return ''
    const slice =
      typeof v.sliceIndex === 'number' && files[v.sliceIndex]
        ? files[v.sliceIndex]
        : files[0]
    return studyUidFromFile(slice)
  }

  /**
   * Tras abrir una serie: si Comparar ON pero todos los viewports
   * abiertos son del mismo StudyUID → apagar Comparar.
   */
  maybeDisableCompareIfSameStudy = () => {
    if (!this.state.compareMode) return
    const layout = this.props.layout || [1, 1]
    const rows = layout[0] || 1
    const cols = layout[1] || 1
    const uids = new Set()
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const idx = i * 4 + j
        const u = this.studyUidFromViewportIndex(idx)
        if (u) uids.add(u)
      }
    }
    if (uids.size <= 1) {
      this.setCompareMode(false)
      this.setState({
        gatewayPushToast: t('relatedStudies.compareOffSameStudy'),
      })
    }
  }

  /**
   * Multi-layout + Comparar OFF: ¿abrir esta serie dejaría ≥2 StudyUID visibles?
   */
  wouldMixStudiesInLayout = (incomingFiles, targetIndex) => {
    const incoming = studyUidFromFile(
      Array.isArray(incomingFiles) && incomingFiles[0] ? incomingFiles[0] : null
    )
    if (!incoming) return false
    const layout = this.props.layout || [1, 1]
    const rows = layout[0] || 1
    const cols = layout[1] || 1
    const uids = new Set()
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const idx = i * 4 + j
        if (idx === targetIndex) {
          uids.add(incoming)
          continue
        }
        const u = this.studyUidFromViewportIndex(idx)
        if (u) uids.add(u)
      }
    }
    return uids.size >= 2
  }

  explorerOnSelectSeries = (files, explorerIndex) => {
    try {
      // El usuario (o una selección intencional) eligió serie: no volver a la 0 al completar descarga
      this.autoSelectFirstSeries = false
      // Destino = último viewport clickeado (no el activado por hover)
      let index = this.getSeriesLoadTargetIndex()
      if (this.state.mprMode || this.state.visibleMprOrthogonal || this.state.visibleMprSagittal || this.state.visibleMprCoronal || this.state.visibleMprAxial) {
        index = 0
        this.clearMprState()
        this._seriesLoadTargetIndex = 0
        this._navActiveIndex = 0
        this.props.setActiveDcmIndex(index)
        this.changeLayout(1, 1)
      } else {
        // Comparar OFF + multi + distintos StudyUID → pedir activar Comparar
        if (
          !this.state.compareMode &&
          this.isMultiViewportLayout() &&
          this.wouldMixStudiesInLayout(files, index)
        ) {
          this.handleForeignStudySeries(files, index)
          return
        }
        this._seriesLoadTargetIndex = index
        this._navActiveIndex = index
        if (this.props.activeDcmIndex !== index) {
          try {
            this.props.setActiveDcmIndex(index)
          } catch (e) {
            /* ignore */
          }
        }
      }

      // Detectar si estamos cambiando a una serie diferente
      const currentViewer = this.dicomViewersRefs && this.dicomViewersRefs[index]
      const isDifferentSeries = this.detectSeriesChange(files, currentViewer)

      // Guardar corte (+ WW de usuario si hubo) de la serie que se deja
      if (currentViewer) {
        try {
          this.rememberSliceFromViewer(currentViewer)
          this.rememberWwwcFromViewer(currentViewer)
        } catch (eLeave) {
          /* ignore */
        }
      }
      
      if (isDifferentSeries && currentViewer) {
        console.log('App - Different series detected, reinitializing viewport')
        this.reinitializeViewport(files, explorerIndex, index)
      } else {
        this.loadNewSeries(files, explorerIndex, index)
      }
      setTimeout(() => this.refreshMprViability(files), 400)
      if (this.state.compareMode) {
        setTimeout(() => this.maybeDisableCompareIfSameStudy(), 500)
      }
      // Refrescar marcas "en vista" del Explorer tras abrir serie en viewport
      setTimeout(() => {
        this.setState((prev) => ({
          explorerPinTick: (prev.explorerPinTick || 0) + 1,
        }))
      }, 120)
    } catch (err) {
      console.error('App - explorerOnSelectSeries:', err)
      try {
        this.loadNewSeries(files, explorerIndex, this.getSeriesLoadTargetIndex())
      } catch (e2) {
        console.error('App - explorerOnSelectSeries recovery failed:', e2)
      }
    }
  }

  // Detectar si estamos cambiando a una serie diferente
  detectSeriesChange = (newFiles, currentViewer) => {
    if (!currentViewer || !currentViewer.files || !Array.isArray(newFiles) || newFiles.length === 0) {
      return true // Si no hay viewer actual o archivos nuevos, consideramos que es un cambio
    }

    try {
      const currentFirstFile = currentViewer.files[0]
      const newFirstFile = newFiles[0]
      if (!currentFirstFile || !newFirstFile) return true

      const currentSeriesUID = this.getFileSeriesId(currentFirstFile)
      const newSeriesUID = this.getFileSeriesId(newFirstFile)
      if (!currentSeriesUID || !newSeriesUID) {
        const ck = this.getSeriesNumberKey(currentFirstFile)
        const nk = this.getSeriesNumberKey(newFirstFile)
        if (ck && nk) return ck !== nk
        return true
      }

      // Misma serie aunque uno sea fallback y el otro UID real
      if (currentSeriesUID === newSeriesUID) return false
      if (this.fileMatchesSeriesUID(newFirstFile, currentSeriesUID)) return false
      if (this.fileMatchesSeriesUID(currentFirstFile, newSeriesUID)) return false

      const ck = this.getSeriesNumberKey(currentFirstFile)
      const nk = this.getSeriesNumberKey(newFirstFile)
      if (ck && nk) return ck !== nk

      return currentSeriesUID !== newSeriesUID
    } catch (error) {
      console.warn('App - Error detecting series change:', error)
      return true
    }
  }

  /**
   * Stack ligero para PREVIEW (sobre todo móvil): quita image de decenas/cientos
   * de frames del viewport. Dejar solo ~primer frame con píxeles → paint inmediato
   * sin bloquear el hilo con getPixelData/Render de todo el stack.
   * No muta los items del store Redux (clones).
   * @param {Array} files
   * @param {{ keepSliceIndex?: number, keepSop?: string|null }} [opts]
   */
  toViewportFiles = (files, opts = {}) => {
    if (!Array.isArray(files) || files.length === 0) return files
    if (this.state.renderMode !== RENDER_MODE.PREVIEW) return files

    const keepIdx =
      opts && opts.keepSliceIndex != null && !Number.isNaN(Number(opts.keepSliceIndex))
        ? Number(opts.keepSliceIndex)
        : 0
    const keepSop =
      opts && opts.keepSop != null ? String(opts.keepSop) : null

    return files.map((f, i) => {
      if (!f) return f
      const isPrev =
        f.renderKind === 'preview' ||
        (!f.renderKind && f.image && !f.image.data) ||
        (f.previewUrl && f.renderKind !== 'dicom')
      if (!isPrev) return f
      const keepPixels =
        i === keepIdx ||
        i === 0 ||
        (keepSop &&
          f.sopInstanceUID &&
          String(f.sopInstanceUID) === keepSop)
      // Conservar frames ya decodificados: si se stripean, el scroll PREVIEW
      // queda ciego mientras baja el estudio (solo contador / cine lento).
      if (keepPixels || f.image) return f
      // Ítem liviano: imageId basta para scrollear/rehidratar
      return {
        imageId: f.imageId || f.previewUrl || null,
        instanceNumber: f.instanceNumber,
        name: f.name,
        image: null,
        sopInstanceUID: f.sopInstanceUID || null,
        seriesInstanceUID:
          f.seriesInstanceUID ||
          (f.series && f.series.seriesInstanceUID) ||
          null,
        dicomUrl: f.dicomUrl || null,
        previewUrl: f.previewUrl || null,
        archiveUrl: f.archiveUrl || null,
        byteOffset: f.byteOffset != null ? f.byteOffset : null,
        byteLength: f.byteLength != null ? f.byteLength : null,
        dicomFetchType: f.dicomFetchType || null,
        renderKind: 'preview',
        usedFallback: f.usedFallback,
        patient: f.patient,
        study: f.study,
        series: f.series,
        fromCache: f.fromCache,
        ...pickGeometryFields(f),
      }
    })
  }

  /**
   * Precarga en RAM según deviceMemory (scroll fluido en estudios grandes).
   * Preview: estudio completo si cabe; DIAG: serie actual.
   */
  kickScrollWarmupForViewer = (viewerIndex, centerIndex, seriesFiles) => {
    try {
      const viewer =
        this.dicomViewersRefs && this.dicomViewersRefs[viewerIndex]
      if (!viewer || typeof viewer.scheduleWarmScrollBuffer !== 'function') {
        return
      }
      const studyFiles = Array.isArray(this.props.files)
        ? this.props.files
        : Array.isArray(seriesFiles)
          ? seriesFiles
          : null
      // Idle: no competir con el primer scroll del usuario (IDB/decode)
      const run = () => {
        try {
          if (
            typeof viewer.isStackScrollLive === 'function' &&
            viewer.isStackScrollLive()
          ) {
            return
          }
          try {
            const ix = getInteractionState()
            if (ix && ix.isInteracting) return
          } catch (eIx) {
            /* ignore */
          }
          viewer.scheduleWarmScrollBuffer({
            centerIndex:
              centerIndex != null ? centerIndex : viewer.sliceIndex || 0,
            studyFiles,
            renderMode: this.state.renderMode,
            direction:
              typeof viewer._scrollDirection === 'number'
                ? viewer._scrollDirection
                : 1,
            boostForward: 0,
          })
        } catch (e) {
          /* ignore */
        }
      }
      try {
        scheduleWhenIdle(run, { timeout: 1200 })
      } catch (eIdle) {
        setTimeout(run, 400)
      }
    } catch (e) {
      /* ignore */
    }
  }

  // Reinicializar viewport al cambiar de serie (sin blankear chrome con setIsOpen false)
  reinitializeViewport = (files, explorerIndex, index) => {
    console.log('App - Reinitializing viewport for different series')
    try {
      const viewer = this.dicomViewersRefs && this.dicomViewersRefs[index]
      if (viewer) {
        try {
          if (typeof viewer.cancelStackInertia === 'function') viewer.cancelStackInertia()
          viewer._stackQueue = 0
          if (viewer._stackRaf) {
            cancelAnimationFrame(viewer._stackRaf)
            viewer._stackRaf = null
          }
        } catch (e) { /* ignore */ }

        viewer.isChangingSeries = true
        // Si el cine estaba en play, reanudar en la nueva serie al terminar de cargar
        try {
          const wasPlaying = !!(viewer.state && viewer.state.inPlay)
          const cineOpen = !!(viewer.state && viewer.state.visibleCinePlayer)
          if (wasPlaying) {
            viewer._pendingCineResume = {
              play: true,
              visible: true,
              fps: (viewer.state && viewer.state.cineFps) || 30,
              loop: viewer.state ? viewer.state.cineLoop !== false : true,
            }
            viewer._cineUserClosed = false
          } else if (cineOpen) {
            // Mantener panel abierto (sin play) tras el cambio de serie
            viewer._pendingCineResume = {
              play: false,
              visible: true,
              fps: (viewer.state && viewer.state.cineFps) || 30,
              loop: viewer.state ? viewer.state.cineLoop !== false : true,
            }
          } else {
            viewer._pendingCineResume = null
          }
        } catch (e) {
          viewer._pendingCineResume = null
        }
        try {
          if (viewer.runCinePlayer) viewer.runCinePlayer('pause')
        } catch (e) { /* ignore */ }

        viewer.image = null
        viewer.imageId = null
        viewer.sliceIndex = 0
        viewer.mprPlane = ''
        try {
          if (viewer.imageCache && typeof viewer.imageCache.clear === 'function') {
            viewer.imageCache.clear()
          }
        } catch (e) { /* ignore */ }

        // PREVIEW móvil: disable síncrono cuelga el hilo (canvas grande RGBA).
        // loadNewSeries/display reutiliza el elemento enabled.
        const skipDisable =
          this.state.renderMode === RENDER_MODE.PREVIEW && isPhoneLikeDevice()
        if (viewer.dicomImage && !skipDisable) {
          try {
            cornerstone.disable(viewer.dicomImage)
          } catch (e) { /* ignore */ }
        }
        // disable (o cambio de serie) deja el Synchronizer de ReferenceLines inválido
        if (this.state.visibleReferenceLines) {
          this.invalidateReferenceLinesSync()
        }
      }
      this.loadNewSeries(files, explorerIndex, index)
      if (this.state.visibleReferenceLines) {
        this.finishPreviewReferenceLinesSetup()
      }
    } catch (err) {
      console.error('App - reinitializeViewport failed, fallback loadNewSeries:', err)
      try {
        if (this.state.visibleReferenceLines) {
          this.invalidateReferenceLinesSync()
        }
        this.loadNewSeries(files, explorerIndex, index)
        if (this.state.visibleReferenceLines) {
          this.finishPreviewReferenceLinesSetup()
        }
      } catch (e2) {
        console.error('App - loadNewSeries failed:', e2)
      }
    }
  }

  loadNewSeries = (files, explorerIndex, index, opts = {}) => {
    const listRaw = Array.isArray(files) ? files : []
    // Preferir el pool Redux completo (stubs R2/local) frente a la lista parcial del click
    let list = listRaw
    try {
      const seedUid = listRaw[0] ? this.getFileSeriesId(listRaw[0]) : null
      let fromStore = []
      if (seedUid && !String(seedUid).startsWith('fallback:')) {
        fromStore = this.getSeriesFilesFromStore(seedUid, this.props.files)
      } else {
        fromStore = this.getSeriesFilesMatchingViewerStack(
          { files: listRaw },
          this.props.files
        )
      }
      // Solo ampliar si no mezclamos otro StudyUID / SeriesUID
      if (Array.isArray(fromStore) && fromStore.length > listRaw.length) {
        const seedStudy = studyUidFromFile(listRaw[0])
        const mixed = fromStore.some((f) => {
          if (seedUid && !String(seedUid).startsWith('fallback:')) {
            return !this.fileMatchesSeriesUID(f, seedUid)
          }
          if (seedStudy && studyUidFromFile(f) !== seedStudy) return true
          return false
        })
        if (!mixed) list = fromStore
      }
    } catch (eExp) {
      list = listRaw
    }
    const isPreviewMode = this.state.renderMode === RENDER_MODE.PREVIEW
    const bookmark = (opts && opts.bookmark) || null
    const preserveView = !!(opts && opts.preserveView)

    // Preferir set de índice de serie aquí (no en Explorer antes del paint)
    if (
      typeof explorerIndex === 'number' &&
      this.props.setExplorerActiveSeriesIndex &&
      this.props.explorerActiveSeriesIndex !== explorerIndex
    ) {
      try {
        this.props.setExplorerActiveSeriesIndex(explorerIndex)
      } catch (e) {
        /* ignore */
      }
    }

    // Corte de sesión (misma serie reabierta en otro viewport / tras layout)
    const seriesUidForSlice =
      (list[0] && this.getFileSeriesId(list[0])) || null
    const savedSlice =
      !preserveView && seriesUidForSlice
        ? this.lookupSeriesSlice(seriesUidForSlice)
        : null
    const sliceBookmark = preserveView ? bookmark : savedSlice

    // Corte preferido antes del strip PREVIEW (para keepSliceIndex + open)
    const preferredSliceRaw = sliceBookmark
      ? this.findSliceIndexFromBookmark(list, sliceBookmark)
      : 0

    const fullLight = this.toViewportFiles(list, {
      keepSliceIndex: preferredSliceRaw,
      keepSop:
        (sliceBookmark && sliceBookmark.sopInstanceUID) ||
        (bookmark && bookmark.sopInstanceUID) ||
        null,
    })
    const preferredSlice = sliceBookmark
      ? this.findSliceIndexFromBookmark(fullLight, sliceBookmark)
      : 0

    // Encapsulated PDF → PdfViewport en este slot (no Cornerstone)
    if (fullLight[0] && isPdfDocumentFileItem(fullLight[0])) {
      this.files = fullLight
      this.setActiveSeriesFromFiles(fullLight, explorerIndex)
      this.setViewportKind(index, 'pdf', fullLight)
      this.setState({
        sliceMax: Math.max(1, fullLight.length),
        sliceIndex: 0,
        mprMode: false,
      })
      return
    }
    this.setViewportKind(index, 'dicom', null)

    // restore: SIEMPRE stack completo + open en el índice correcto (sin fase 1-frame)
    // Sin restore en PREVIEW largo: 2 fases (frame0 → expand) para paint rápido
    // Si el corte de sesión no es 0, abrir directo en ese índice (sin two-phase)
    const useTwoPhase =
      !preserveView &&
      preferredSlice === 0 &&
      isPreviewMode &&
      fullLight.length > 8

    const viewportFiles =
      useTwoPhase && fullLight.length > 1 ? [fullLight[0]] : fullLight

    this.files = useTwoPhase ? fullLight : viewportFiles
    this.setActiveSeriesFromFiles(fullLight, explorerIndex)
    this.mprPlane = ''
    this.mprData = {}
    this.volume = []
    this._suppressSeriesSyncUntil =
      Date.now() +
      (preserveView
        ? 200
        : isPreviewMode && isPhoneLikeDevice()
          ? 2500
          : isPreviewMode
            ? 1200
            : 300)

    // Serie destino: sembrar WW/WC solo si HAY sesión de ESA serie (no heredar melliza)
    const seriesUidForWwwc =
      seriesUidForSlice ||
      (fullLight[0] && this.getFileSeriesId(fullLight[0])) ||
      this._activeSeriesUID ||
      null
    const viewerForWwwc =
      seriesUidForWwwc && this.dicomViewersRefs
        ? this.dicomViewersRefs[index]
        : null
    let prevSeriesUid = null
    if (viewerForWwwc) {
      try {
        prevSeriesUid = this.resolveActiveSeriesUID(viewerForWwwc, {
          localOnly: true,
        })
      } catch (ePrev) {
        prevSeriesUid = null
      }
      if (
        !prevSeriesUid &&
        typeof viewerForWwwc.getActiveSeriesUidKey === 'function'
      ) {
        try {
          prevSeriesUid = viewerForWwwc.getActiveSeriesUidKey()
        } catch (ePrev2) {
          prevSeriesUid = null
        }
      }
    }
    const sameSeriesOpen =
      !!(
        prevSeriesUid &&
        seriesUidForWwwc &&
        String(prevSeriesUid) === String(seriesUidForWwwc)
      )
    let wwwcToReapply = null
    if (viewerForWwwc && seriesUidForWwwc) {
      // Guardar VOI de la serie que se deja (pulmón, etc.)
      try {
        this.rememberWwwcFromViewer(viewerForWwwc)
      } catch (eRem) {
        /* ignore */
      }

      const savedForDest = this.lookupWwwcForViewport(index, seriesUidForWwwc)
      if (sameSeriesOpen) {
        // Misma serie (otro layout / reopen): conservar local o sesión
        this.seedSeriesWwwcOnViewer(viewerForWwwc, seriesUidForWwwc, null, {
          viewportIndex: index,
          preserveLocal: true,
        })
        wwwcToReapply = savedForDest
      } else if (savedForDest) {
        // Otra serie con VOI de usuario previo → restaurar solo el suyo
        this.seedSeriesWwwcOnViewer(
          viewerForWwwc,
          seriesUidForWwwc,
          savedForDest,
          { viewportIndex: index, preserveLocal: false }
        )
        wwwcToReapply = savedForDest
      } else {
        // Melliza / serie nueva sin sesión: tags DICOM (no heredar VOI del hermano)
        try {
          if (
            viewerForWwwc.toolState &&
            viewerForWwwc.toolState[seriesUidForWwwc]
          ) {
            const st = { ...viewerForWwwc.toolState[seriesUidForWwwc] }
            delete st.wwwcUserSet
            delete st.ww
            delete st.wc
            viewerForWwwc.toolState[seriesUidForWwwc] = st
          }
          viewerForWwwc._logicalWwwc = {
            ww: null,
            wc: null,
          }
        } catch (eClr) {
          /* ignore */
        }
      }
    }

    const metaTotal = Math.max(
      fullLight.length,
      resolveSeriesTotalImages(fullLight) || 0
    )
    let sliceMax = metaTotal
    const remapOnOpen = !!(preserveView || savedSlice)
    if (viewportFiles.length > 0 && this.dicomViewersRefs[index]) {
      this.dicomViewersRefs[index].isChangingSeries = true
      try {
        if (typeof this.dicomViewersRefs[index].cancelStackInertia === 'function') {
          this.dicomViewersRefs[index].cancelStackInertia()
        }
        this.dicomViewersRefs[index]._stackQueue = 0
      } catch (e) {
        /* ignore */
      }
      this.dicomViewersRefs[index].runTool(
        'setfiles',
        remapOnOpen
          ? {
              files: viewportFiles,
              remappedIndex: preferredSlice,
            }
          : viewportFiles
      )
      this.dicomViewersRefs[index].explorerIndex = explorerIndex
      // Stack scrolleable YA (totalImages / stubs), no esperar a que IDB termine
      this.dicomViewersRefs[index].files = fullLight
      this.dicomViewersRefs[index].sliceMax = metaTotal
      this.dicomViewersRefs[index].seriesTotalImages = metaTotal
      this.dicomViewersRefs[index].shouldScroll = metaTotal > 1
      this.dicomViewersRefs[index].sliceIndex = preferredSlice
      if (typeof this.dicomViewersRefs[index].refreshScrollFlags === 'function') {
        this.dicomViewersRefs[index].refreshScrollFlags()
      }
      sliceMax = metaTotal
    }
    if (this.dicomViewersRefs[index]) {
      this.dicomViewersRefs[index].explorerIndex = explorerIndex
    }

    const openIndex = useTwoPhase ? 0 : preferredSlice
    this._navSliceIndex = preferredSlice

    this.setState(
      {
        sliceMax: sliceMax,
        sliceIndex: preferredSlice,
        mprMode: false,
      },
      () => {
        const open = () => {
          try {
            this.handleOpenImage(openIndex, index)
          } catch (e) {
            console.error('App - handleOpenImage after series select:', e)
          }
          // Reaplicar WW/WC solo si esa serie tiene sesión de usuario
          if (
            wwwcToReapply &&
            seriesUidForWwwc &&
            this.dicomViewersRefs[index]
          ) {
            this.scheduleReapplySeriesWwwc(
              this.dicomViewersRefs[index],
              seriesUidForWwwc,
              wwwcToReapply,
              {
                viewportIndex: index,
                preserveLocal: sameSeriesOpen,
              }
            )
          }
          // Precarga RAM adaptativa (preview: estudio si cabe; DIAG: serie)
          this.kickScrollWarmupForViewer(index, preferredSlice, fullLight)
          setTimeout(() => {
            try {
              this.refreshMprViability(fullLight)
            } catch (e) {
              /* ignore */
            }
          }, 500)
          // Fase B solo para PREVIEW sin restore
          if (useTwoPhase && this.dicomViewersRefs[index]) {
            setTimeout(() => {
              try {
                this.dicomViewersRefs[index].runTool('setfiles', {
                  files: fullLight,
                  soft: true,
                })
                this.dicomViewersRefs[index].shouldScroll = fullLight.length > 1
                this.dicomViewersRefs[index].sliceMax = fullLight.length
                this.setState({ sliceMax: fullLight.length })
                this.kickScrollWarmupForViewer(index, preferredSlice, fullLight)
              } catch (e) {
                /* ignore */
              }
            }, 120)
          } else if (preserveView) {
            // Afirmar índice + scroll y reaplicar viewport (zoom)
            const v = this.dicomViewersRefs[index]
            if (v) {
              v.sliceIndex = preferredSlice
              v.sliceMax = fullLight.length
              v.shouldScroll = fullLight.length > 1
              this._navSliceIndex = preferredSlice
            }
            if (bookmark) {
              setTimeout(() => {
                try {
                  // Reabrir por si un soft-sync intermedio la movió
                  if (v && v.sliceIndex !== preferredSlice) {
                    this.handleOpenImage(preferredSlice, index)
                  }
                  this.applyViewportBookmark(v, bookmark)
                } catch (e) {
                  /* ignore */
                }
              }, 60)
              // Segunda pasada (render async) fija el slice hasta que el stack crezca
              setTimeout(() => {
                try {
                  if (!v) return
                  if (
                    (bookmark.sopInstanceUID || bookmark.instanceNumber != null) &&
                    Array.isArray(v.files) &&
                    v.files.length
                  ) {
                    const want = this.findSliceIndexFromBookmark(v.files, bookmark)
                    if (v.sliceIndex !== want) {
                      this._navSliceIndex = want
                      this.handleOpenImage(want, index)
                    }
                  }
                  v.shouldScroll = (v.files && v.files.length) > 1
                  this.applyViewportBookmark(v, bookmark)
                } catch (e) {
                  /* ignore */
                }
              }, 350)
            }
            // Liberar bookmark solo si el frame ya está en el stack del viewer
            if (bookmark) {
              const matched =
                v &&
                Array.isArray(v.files) &&
                this.findSliceIndexFromBookmark(v.files, bookmark) === preferredSlice &&
                preferredSlice >= 0
              if (matched) {
                // Mantener bookmark un poco para que soft-sync use el SOP al crecer
                this._lastModeSwitchBookmark = bookmark
                this._bookmarkHoldUntil = Date.now() + 2500
                // No borrar todavía _renderModeViewportRestore: soft-sync lo usa
              }
            }
          }
        }
        if (isPreviewMode) {
          setTimeout(open, 0)
        } else {
          open()
        }
        if (this._postSeriesSyncTimer) clearTimeout(this._postSeriesSyncTimer)
        const syncDelay = preserveView
          ? 600
          : isPreviewMode && isPhoneLikeDevice()
            ? 2500
            : isPreviewMode
              ? 1200
              : 400
        this._postSeriesSyncTimer = setTimeout(() => {
          this._postSeriesSyncTimer = null
          try {
            this.syncActiveSeriesFilesFromStore()
          } catch (e) {
            console.warn('App - deferred syncActiveSeriesFilesFromStore:', e)
          }
        }, syncDelay)
      }
    )
  }

  //#endregion EXPLORER

  getActiveDcmViewers = () => {
    //console.log('getActiveDcmViewers', this.dicomViewersRefs)
    this.dicomViewersActive = this.dicomViewersRefs.filter(v => v !== undefined && v.image !== null)
    return this.dicomViewersActive.length
  }  

  getActiveDcmViewersSameStudy = () => {
    const active = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
    if (!active || !active.image) {
      this.dicomViewersActiveSameStudy = []
      return 0
    }
    const studyId = getDicomStudyId(active.image)
    this.dicomViewersActiveSameStudy = this.dicomViewersRefs.filter(
      (v) => v !== undefined && v.image && getDicomStudyId(v.image) === studyId
    )
    return this.dicomViewersActiveSameStudy.length
  }  

  getActiveDcmViewersSamePlane = () => {
    this.dicomViewersActiveSamePlane = []
    try {
      const active = this.dicomViewersRefs && this.dicomViewersRefs[this.props.activeDcmIndex]
      if (!active) return
      const plane = active.mprPlane
      console.log('App - getActiveDcmViewersSamePlane - Plano activo:', plane)
      for(let i=0; i < this.dicomViewersActiveSameStudy.length; i++) {
        if (this.dicomViewersActiveSameStudy[i].mprPlane === plane && 
            this.dicomViewersActiveSameStudy[i].layoutIndex !== this.props.activeDcmIndex)
          this.dicomViewersActiveSamePlane.push(this.dicomViewersActiveSameStudy[i])
      }
      console.log('App - getActiveDcmViewersSamePlane - Viewports del mismo plano:', this.dicomViewersActiveSamePlane.length)
    } catch (e) {
      console.warn('App - getActiveDcmViewersSamePlane:', e)
    }
  }

  // Método para validar la correspondencia entre series en múltiples viewports
  validateSeriesCorrespondence = () => {
    if (!this.dicomViewersRefs || this.dicomViewersRefs.length === 0) {
      console.warn('App - No viewers available for validation')
      return { isValid: true, warnings: [] }
    }

    const warnings = []
    const seriesInfo = []

    // Recopilar información de todas las series activas
    for (let i = 0; i < this.dicomViewersRefs.length; i++) {
      const viewer = this.dicomViewersRefs[i]
      if (viewer && viewer.image && viewer.image.data) {
        try {
          const seriesUID = viewer.image.data.string('x0020000e')
          const studyUID = viewer.image.data.string('x0020000d')
          const patientName = viewer.image.data.string('x00100010')
          const seriesDescription = viewer.image.data.string('x0008103e')
          
          seriesInfo.push({
            viewerIndex: i,
            seriesUID,
            studyUID,
            patientName,
            seriesDescription,
            sliceIndex: viewer.sliceIndex,
            sliceMax: viewer.sliceMax
          })
        } catch (error) {
          warnings.push(`Error getting series info from viewer ${i}: ${error.message}`)
        }
      }
    }

    // Validar correspondencia de StudyInstanceUID
    const studyUIDs = seriesInfo.map(s => s.studyUID).filter(Boolean)
    const uniqueStudyUIDs = new Set(studyUIDs)
    if (uniqueStudyUIDs.size > 1) {
      warnings.push(`Multiple StudyInstanceUIDs detected: ${Array.from(uniqueStudyUIDs).join(', ')}`)
    }

    // Validar correspondencia de PatientName
    const patientNames = seriesInfo.map(s => s.patientName).filter(Boolean)
    const uniquePatientNames = new Set(patientNames)
    if (uniquePatientNames.size > 1) {
      warnings.push(`Multiple PatientNames detected: ${Array.from(uniquePatientNames).join(', ')}`)
    }

    // Validar que no hay SeriesInstanceUIDs duplicados
    const seriesUIDs = seriesInfo.map(s => s.seriesUID).filter(Boolean)
    const uniqueSeriesUIDs = new Set(seriesUIDs)
    if (seriesUIDs.length !== uniqueSeriesUIDs.size) {
      warnings.push(`Duplicate SeriesInstanceUIDs detected`)
    }

    const result = {
      isValid: warnings.length === 0,
      warnings,
      seriesInfo,
      totalViewers: this.dicomViewersRefs.length,
      activeViewers: seriesInfo.length
    }

    if (warnings.length > 0) {
      console.warn('App - Series correspondence validation warnings:', result)
    } else {
      console.log('App - Series correspondence validation passed:', result)
    }

    return result
  }

  // Método para obtener información detallada de la imagen actual en todos los viewports
  getCurrentViewportsInfo = () => {
    const viewportsInfo = []
    
    if (!this.dicomViewersRefs) {
      return viewportsInfo
    }

    for (let i = 0; i < this.dicomViewersRefs.length; i++) {
      const viewer = this.dicomViewersRefs[i]
      if (viewer && viewer.getCurrentImageInfo) {
        const imageInfo = viewer.getCurrentImageInfo()
        if (imageInfo) {
          viewportsInfo.push({
            viewerIndex: i,
            isActive: i === this.props.activeDcmIndex,
            ...imageInfo
          })
        }
      }
    }

    return viewportsInfo
  }

  syncActiveDcmViewersSamePlane = (direction) => {
    this.syncLinkedViewports(this.props.activeDcmIndex, { copyView: false })
  }

  // Método para forzar re-sincronización de viewports
  forceViewportSync = () => {
    console.log('App - forceViewportSync - Iniciando re-sincronización forzada')
    
    // Re-obtener viewports activos
    this.getActiveDcmViewers()
    this.getActiveDcmViewersSameStudy()
    this.getActiveDcmViewersSamePlane()
    this.scheduleSeriesLinkSync(this.props.activeDcmIndex)
    
    // Forzar actualización de líneas de referencia
    if (this.state.visibleReferenceLines && this.dicomViewersActive.length > 1) {
      console.log('App - forceViewportSync - Forzando actualización de líneas de referencia')
      setTimeout(() => {
        this.setupCornerstoneToolsReferenceLines()
      }, 100)
    }
    
    console.log('App - forceViewportSync - Re-sincronización completada')
  }

  colorIcon = (tool) => {
    return this.state.toolActive === tool ? activeColor : iconColor
  }

  showOrthancConnection = () => {
    console.log('App - showOrthancConnection called')
    console.log('Current state:', this.state)
    this.setState({ visibleOrthancConnection: true }, () => {
      console.log('State after update:', this.state)
    })
  }

  hideOrthancConnection = () => {
    console.log('App - hideOrthancConnection called')
    this.setState({ visibleOrthancConnection: false })
  }

  /**
   * Re-evalúa assertAccess (p.ej. tras login o caché local).
   * @param {{ hasLocalStudies?: boolean }} [opts]
   * @returns {Promise<object|null>} resultado assertAccess o null si unmounted
   */
  refreshAccessGate = async (opts = {}) => {
    // Durante bootstrap de ticket+elev no pisar plan clínico con portal.
    // Chequear ANTES del await: si no, un refresh encolado por saveEntitlements
    // termina después del strip de elev y puede forzar patient.
    if (this._ticketBootstrapInFlight) {
      return null
    }
    let hasLocal = opts.hasLocalStudies
    if (hasLocal == null) {
      try {
        const rows = await listStudyCatalog()
        hasLocal = Array.isArray(rows) && rows.length > 0
      } catch (e) {
        hasLocal = false
      }
    }
    if (this._unmounted) return null
    if (this._ticketBootstrapInFlight) {
      return null
    }
    const access = assertAccess({ hasLocalStudies: !!hasLocal })
    const persona = access.persona || resolvePersona()
    if (!access.ok) {
      this.setState({
        accessDenied: { reason: access.reason, persona },
        udvPersona: persona,
        inviteLoginRequired: false,
      })
      return access
    }
    const prevPlan = this.state.udvPlan
    const prevPersona = this.state.udvPersona
    let nextPlan = access.plan || prevPlan
    let nextPersona = persona

    const ticketIdNow =
      this.state.ticketIdFromUrl ||
      (typeof window !== 'undefined'
        ? parseTicketFromSearchParams(new URLSearchParams(window.location.search))
        : null)
    const platNow = ticketIdNow
      ? matchPlatformElevationSession(ticketIdNow)
      : null

    // Sesión de elevación de esta pestaña: nunca downgrade a patient.
    if (platNow && String(nextPlan || '').toLowerCase() === 'patient') {
      nextPlan = platNow.plan
      nextPersona = UDV_PERSONAS.CLINICAL
    } else if (
      // No downgrade clinical→patient tras elev (notifyPlanChange race).
      ['lite', 'pro', 'premium', 'supremo', 'demo'].includes(
        String(prevPlan || '').toLowerCase()
      ) &&
      String(nextPlan || '').toLowerCase() === 'patient' &&
      (prevPersona === UDV_PERSONAS.CLINICAL ||
        access.source === 'elev_pending' ||
        access.source === ENTITLEMENT_SOURCES.PLATFORM_ELEVATION ||
        access.ticketElevated ||
        !!platNow)
    ) {
      nextPlan = prevPlan
      nextPersona = UDV_PERSONAS.CLINICAL
    }
    const next = {
      accessDenied: null,
      udvPlan: nextPlan,
      udvPersona: nextPersona,
      inviteLoginRequired: !!access.loginRequired,
    }
    if (access.shellHint) {
      next.toolbarLayoutFromUrl = access.shellHint
    } else if (platNow && platNow.shell) {
      const shellLayout = parseToolbarQueryParam(platNow.shell)
      if (shellLayout) next.toolbarLayoutFromUrl = shellLayout
    }
    const pacsFromEnt = getEffectivePacsId()
    if (pacsFromEnt && !this.state.pacsIdFromUrl) {
      next.pacsIdFromUrl = pacsFromEnt
    }
    this.setState(next)
    return access
  }

  /**
   * Offline local al montar: levanta gate y ofrece elegir estudio en storage.
   */
  bootstrapPersonaAccess = async () => {
    try {
      const rows = await listStudyCatalog()
      const hasLocal = Array.isArray(rows) && rows.length > 0
      if (!hasLocal || this._unmounted) return

      const deepLinkBusy = Boolean(
        this.state.ticketIdFromUrl ||
          this.state.inviteIdFromUrl ||
          this.state.studyIdFromUrl ||
          this.state.manifestUrlFromUrl ||
          this.state.legacyWadoContext
      )

      let accessOk = !this.state.accessDenied
      if (this.state.accessDenied) {
        const access = await this.refreshAccessGate({ hasLocalStudies: true })
        accessOk = !!(access && access.ok)
      }
      if (this._unmounted || !accessOk) return

      // Cold start (PWA/browser) sin deep link: mostrar selector de estudios locales
      if (!deepLinkBusy && !this.hasActiveStudyOpen()) {
        this.setState({ visibleLocalStudies: true })
      }
    } catch (e) {
      /* ignore catalog errors */
    }
  }

  showAuthGatewayModal = () => {
    if (
      !shouldShowClinicalAuth({
        persona: this.state.udvPersona,
        plan: this.state.udvPlan,
        developer: isDeveloperMode(),
      })
    ) {
      return
    }
    this.setState({ visibleAuthGatewayModal: true, visibleMainMenu: false })
  }

  /** Alias chrome contract: abre AuthGatewayModal */
  onGatewayOpen = () => {
    this.showAuthGatewayModal()
  }

  hideAuthGatewayModal = () => {
    if (this.state.inviteLoginRequired && !loadControllerSession()) {
      this.setState({
        visibleAuthGatewayModal: false,
        accessDenied: {
          reason: ACCESS_REASONS.INVITE_LOGIN_REQUIRED,
          persona: UDV_PERSONAS.CLINICAL,
        },
        udvPersona: UDV_PERSONAS.CLINICAL,
      })
      return
    }
    this.setState({ visibleAuthGatewayModal: false })
  }

  /**
   * @param {boolean} hasSession
   * @param {boolean} healthOk
   * @param {boolean} pushActive
   * @returns {'online'|'local_only'|'disconnected'}
   */
  deriveGatewayStatus = (hasSession, healthOk, pushActive) => {
    if (!hasSession) return 'local_only'
    if (!healthOk) return 'disconnected'
    if (pushActive) return 'online'
    return 'local_only'
  }

  refreshGatewayStatus = async () => {
    if (this._unmounted) return
    const ctrl = loadControllerSession()
    const edge = loadSession()
    const pushActive = !!this.state.gatewayPushActive

    if (ctrl && ctrl.token) {
      const health = await checkControllerHealth(ctrl.serverUrl)
      if (this._unmounted) return
      let gatewayCompatOk = true
      let controllerCompat = this.state.controllerCompat
      try {
        const compat = await fetchControllerCompat(ctrl.serverUrl)
        if (compat && compat.ok) {
          controllerCompat = compat
          gatewayCompatOk = !isUdvBelowMin(
            packageJson.version,
            compat.udv_min
          )
        }
      } catch (e) {
        /* fallo compat: no marcar incompat */
      }
      if (this._unmounted) return
      this.setState({
        gatewayConnected: true,
        gatewayStatus: this.deriveGatewayStatus(true, !!health.ok, pushActive),
        gatewayCompatOk,
        controllerCompat,
      })
      return
    }

    // Sin sesión Controller: intentar compat público (URL default/env)
    if (!edge || !edge.token) {
      let gatewayCompatOk = this.state.gatewayCompatOk
      let controllerCompat = this.state.controllerCompat
      try {
        const compat = await fetchControllerCompat(getControllerUrl())
        if (compat && compat.ok) {
          controllerCompat = compat
          gatewayCompatOk = !isUdvBelowMin(
            packageJson.version,
            compat.udv_min
          )
        }
      } catch (e) {
        /* ignore */
      }
      if (this._unmounted) return
      this.setState({
        gatewayConnected: false,
        gatewayStatus: 'local_only',
        gatewayCompatOk,
        controllerCompat,
      })
      return
    }
    const health = await checkHealth(edge.serverUrl)
    if (this._unmounted) return
    this.setState({
      gatewayConnected: true,
      gatewayStatus: this.deriveGatewayStatus(true, !!health.ok, pushActive),
    })
  }

  startGatewayHealthPoll = () => {
    this.stopGatewayHealthPoll()
    this._gatewayHealthTimer = setInterval(() => {
      if (this._unmounted) return
      this.refreshGatewayStatus()
    }, 60000)
  }

  stopGatewayHealthPoll = () => {
    if (this._gatewayHealthTimer) {
      clearInterval(this._gatewayHealthTimer)
      this._gatewayHealthTimer = null
    }
  }

  handleGatewayPushStatusChange = (active) => {
    const pushActive = Boolean(active)
    const hasSession = Boolean(
      loadControllerSession() || (loadSession() && loadSession().token)
    )
    this.setState(
      (prev) => ({
        gatewayPushActive: pushActive,
        gatewayConnected: hasSession,
        gatewayStatus: hasSession
          ? prev.gatewayStatus === 'disconnected'
            ? 'disconnected'
            : this.deriveGatewayStatus(
                true,
                prev.gatewayStatus !== 'disconnected',
                pushActive
              )
          : 'local_only',
      }),
      () => {
        if (hasSession) this.refreshGatewayStatus()
      }
    )
  }

  handleControllerSessionChange = (session) => {
    const hasSession = Boolean(session && session.token)
    this.setState({
      gatewayConnected: hasSession || Boolean(loadSession()),
      gatewayPushActive: hasSession ? this.state.gatewayPushActive : this.state.gatewayPushActive,
      gatewayStatus: hasSession || loadSession() ? this.state.gatewayStatus : 'local_only',
    })
    if (hasSession) {
      this.refreshGatewayStatus()
      this.startGatewayHealthPoll()
      void this.refreshAccessGate().then(() => {
        if (!this._unmounted) {
          this.setState({
            visibleAuthGatewayModal: false,
            inviteLoginRequired: false,
          })
        }
      })
    } else if (!loadSession()) {
      this.stopGatewayHealthPoll()
      this.setState({
        gatewayConnected: false,
        gatewayPushActive: false,
        gatewayStatus: 'local_only',
      })
    } else {
      this.refreshGatewayStatus()
    }
  }

  handleGatewaySessionChange = (session) => {
    const hasSession = Boolean(session && session.token)
    const hasCtrl = Boolean(loadControllerSession())
    this.setState({
      gatewayConnected: hasSession || hasCtrl,
      gatewayPushActive: hasSession || hasCtrl ? this.state.gatewayPushActive : false,
      gatewayStatus: hasSession || hasCtrl ? this.state.gatewayStatus : 'local_only',
    })
    if (hasSession) {
      startPresence()
      this.refreshGatewayStatus()
      this.startGatewayHealthPoll()
    } else {
      stopPresence()
      if (!hasCtrl) {
        this.stopGatewayHealthPoll()
        this.setState({
          gatewayConnected: false,
          gatewayPushActive: false,
          gatewayStatus: 'local_only',
        })
      } else {
        this.refreshGatewayStatus()
      }
    }
  }

  showLocalStudies = () => {
    // Abrir el diálogo primero: React lo pinta sin competir con el flush.
    // El reseteo de interacción y el flush de progreso diferido se hacen
    // en un tick separado para no bloquear la animación de apertura.
    void this.refreshLocalCatalogIndex()
    this.setState({ visibleLocalStudies: true, visibleMainMenu: false }, () => {
      setTimeout(() => {
        try {
          resetInteractionState()
        } catch (e) {
          /* ignore */
        }
        if (this._deferredDownloadProgress) {
          const stats = this._deferredDownloadProgress
          this._deferredDownloadProgress = null
          try {
            this.applyManifestDownloadProgress(stats)
          } catch (e2) {
            /* ignore */
          }
        }
      }, 0)
    })
  }

  hideLocalStudies = () => {
    this.setState({ visibleLocalStudies: false })
  }

  /**
   * Índice rápido StudyUID → meta de catálogo local (historial / Cargar).
   */
  refreshLocalCatalogIndex = async () => {
    try {
      const rows = await listStudyCatalog()
      const map = {}
      if (Array.isArray(rows)) {
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i]
          const uid =
            row && row.studyInstanceUID != null
              ? String(row.studyInstanceUID).trim()
              : ''
          if (!uid) continue
          map[uid] = {
            instanceCount: Number(row.instanceCount) || 0,
            seriesCount: Number(row.seriesCount) || 0,
            patientId: row.patientId != null ? String(row.patientId) : '',
            patientName: row.patientName != null ? String(row.patientName) : '',
          }
        }
      }
      if (!this._unmounted) {
        this.setState({ localCatalogByUid: map })
      }
      return map
    } catch (e) {
      return this.state.localCatalogByUid || {}
    }
  }

  /**
   * Stats por StudyUID de files ya en el store (historial / labels).
   * @returns {Object<string, ReturnType<typeof studySeriesStats>>}
   */
  getLoadedStudyStatsByUid = () => {
    const files = Array.isArray(this.props.files) ? this.props.files : []
    const byUid = new Map()
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      if (!f) continue
      const uid = studyUidFromFile(f)
      if (!uid) continue
      if (!byUid.has(uid)) byUid.set(uid, [])
      byUid.get(uid).push(f)
    }
    const out = {}
    byUid.forEach((list, uid) => {
      out[uid] = studySeriesStats(list)
    })
    return out
  }

  /**
   * StudyUIDs visibles en los viewports del layout actual.
   * @returns {string[]}
   */
  getViewportStudyUids = () => {
    const layout = this.props.layout || [1, 1]
    const rows = layout[0] || 1
    const cols = layout[1] || 1
    const out = []
    const seen = new Set()
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const idx = i * 4 + j
        const u = this.studyUidFromViewportIndex(idx)
        if (u && !seen.has(u)) {
          seen.add(u)
          out.push(u)
        }
      }
    }
    return out
  }

  getOpenPatientId = () => {
    const files = Array.isArray(this.props.files) ? this.props.files : []
    const f = files.find(Boolean)
    if (f && f.patient) {
      const id = f.patient.patientId || f.patient.patientID
      if (id != null && String(id).trim()) return String(id).trim()
    }
    if (this.state.relatedPatientId) {
      return String(this.state.relatedPatientId).trim()
    }
    return ''
  }

  getOpenPatientName = () => {
    const files = Array.isArray(this.props.files) ? this.props.files : []
    const f = files.find(Boolean)
    if (f && f.patient && f.patient.patientName) {
      return String(f.patient.patientName).trim()
    }
    return ''
  }

  isStudyUidAlreadyLoaded = (studyInstanceUID) => {
    const want = studyInstanceUID != null ? String(studyInstanceUID).trim() : ''
    if (!want) return false
    const files = Array.isArray(this.props.files) ? this.props.files : []
    for (let i = 0; i < files.length; i++) {
      if (studyUidFromFile(files[i]) === want) return true
    }
    return false
  }

  /**
   * LocalStudies: Cargar (merge) solo mismo paciente y no ya en pane.
   * @param {{ studyInstanceUID?: string, patientId?: string, patientName?: string }} row
   */
  canLoadLocalStudy = (row) => {
    if (!row || !row.studyInstanceUID) return false
    if (!this.hasActiveStudyOpen()) return false
    if (this.isStudyUidAlreadyLoaded(row.studyInstanceUID)) return false
    const openPid = this.getOpenPatientId()
    const rowPid = row.patientId != null ? String(row.patientId).trim() : ''
    if (openPid && rowPid) return openPid === rowPid
    const openName = this.getOpenPatientName()
    const rowName =
      row.patientName != null ? String(row.patientName).trim() : ''
    if (openName && rowName) return openName === rowName
    return false
  }

  showRelatedStudies = () => {
    void this.refreshLocalCatalogIndex()
    this.setState({ visibleRelatedStudies: true, visibleMainMenu: false })
  }

  hideRelatedStudies = () => {
    this.setState({ visibleRelatedStudies: false })
  }

  hasWorklistUi = () => {
    if (this.state.worklistIdFromUrl) return true
    if (
      Array.isArray(this.state.worklistItems) &&
      this.state.worklistItems.length > 0
    ) {
      return true
    }
    if (
      Array.isArray(this.state.worklistSessions) &&
      this.state.worklistSessions.length > 0
    ) {
      return true
    }
    return false
  }

  refreshWorklistSessions = async () => {
    try {
      const sessions = await listWorklistSessions()
      if (!this._unmounted) {
        this.setState({ worklistSessions: sessions })
      }
      return sessions
    } catch (e) {
      console.warn('refreshWorklistSessions', e)
      return []
    }
  }

  showWorklist = () => {
    void this.refreshLocalCatalogIndex()
    void this.refreshWorklistSessions()
    const hasCola =
      Array.isArray(this.state.worklistItems) &&
      this.state.worklistItems.length > 0
    this.setState({
      visibleWorklist: true,
      visibleMainMenu: false,
      worklistTab: hasCola ? 'cola' : 'recientes',
    })
  }

  hideWorklist = () => {
    this.setState({ visibleWorklist: false })
  }

  setWorklistTab = (tab) => {
    this.setState({
      worklistTab: tab === 'recientes' ? 'recientes' : 'cola',
    })
  }

  openWorklistItem = (ticketId) => {
    this.hideWorklist()
    this.openRelatedStudy(ticketId)
  }

  openWorklistLocalItem = (studyInstanceUid, item) => {
    this.hideWorklist()
    const uid = studyInstanceUid != null ? String(studyInstanceUid).trim() : ''
    if (!uid) {
      if (item && item.ticketId) this.openRelatedStudy(item.ticketId)
      return
    }
    const local = this.state.localCatalogByUid && this.state.localCatalogByUid[uid]
    if (local) {
      void this.openLocalStudyInViewer(uid, local)
      return
    }
    if (item && item.ticketId) {
      this.openRelatedStudy(item.ticketId)
    }
  }

  openWorklistManageStorage = () => {
    this.hideWorklist()
    this.showLocalStudies()
  }

  stopStaffTicketLease = () => {
    if (this._staffLeaseHeartbeat) {
      try {
        this._staffLeaseHeartbeat.stop()
      } catch (e) {
        /* ignore */
      }
      this._staffLeaseHeartbeat = null
    }
  }

  handleStaffLeaseExpired = (info) => {
    this.stopStaffTicketLease()
    if (this._unmounted) return
    this.setState({
      staffLeaseLocked: true,
      gatewayPushToast: null,
      titleMessage: t('access.staffLeaseTitle'),
      textMessage: t('access.staffLeaseExpired'),
      visibleMessage: true,
    })
    try {
      // eslint-disable-next-line no-console
      console.warn('[UDV] staff lease expired', info && info.reason)
    } catch (e) {
      /* ignore */
    }
  }

  /**
   * Heartbeat lease tras elev staff (si plataforma emitió lease_token).
   * Sin token → no-op (plataforma aún sin /extend).
   */
  startStaffTicketLease = (ticketId, ticketApiBase) => {
    this.stopStaffTicketLease()
    const id = ticketId != null ? String(ticketId).trim() : ''
    if (!id) return
    const sess = matchPlatformElevationSession(id) || loadPlatformElevationSession()
    if (!sess || !sess.leaseToken || sess.ticketId !== id) return
    const base =
      (ticketApiBase && String(ticketApiBase).replace(/\/+$/, '')) ||
      sess.ticketApiBase ||
      getTicketApiBase()
    this.setState({ staffLeaseLocked: false })
    this._staffLeaseHeartbeat = startTicketLeaseHeartbeat({
      ticketId: id,
      ticketApiBase: base,
      leaseToken: sess.leaseToken,
      intervalSec: sess.extendIntervalSeconds || DEFAULT_EXTEND_INTERVAL_SEC,
      related: true,
      onExpired: (info) => this.handleStaffLeaseExpired(info),
      onExtended: (result) => {
        if (!result || !result.ok) return
        const cur = matchPlatformElevationSession(id)
        if (!cur) return
        savePlatformElevationSession({
          ticketId: id,
          plan: cur.plan,
          shell: cur.shell,
          theme: cur.theme,
          edgeId: cur.edgeId,
          leaseToken: result.leaseToken || cur.leaseToken,
          leaseExpiresAt: result.leaseExpiresAt || cur.leaseExpiresAt,
          extendIntervalSeconds: cur.extendIntervalSeconds,
          ticketApiBase: base,
        })
      },
    })
  }

  /**
   * Un extend con lease activo (re-firma related). false si no hay lease o falló auth.
   * @returns {Promise<boolean>}
   */
  tryStaffLeaseExtendOnce = async () => {
    if (this.state.staffLeaseLocked) return false
    if (this._staffLeaseHeartbeat && this._staffLeaseHeartbeat.extendOnce) {
      const r = await this._staffLeaseHeartbeat.extendOnce()
      return !!(r && r.ok)
    }
    const id = this.state.ticketIdFromUrl
    const sess = id ? matchPlatformElevationSession(id) : null
    if (!sess || !sess.leaseToken) return false
    this.startStaffTicketLease(id, sess.ticketApiBase)
    if (!this._staffLeaseHeartbeat) return false
    const r = await this._staffLeaseHeartbeat.extendOnce()
    return !!(r && r.ok)
  }

  /**
   * Abre un estudio del historial reemplazando todo (Abrir solo).
   * No re-resuelve acceso/elevación: conserva plan/persona de la pestaña (lite/clinical).
   * @param {string} ticketId
   */
  openRelatedStudy = (ticketId) => {
    const id = ticketId != null ? String(ticketId).trim() : ''
    if (!id) return
    if (this.state.staffLeaseLocked) {
      this.setState({
        titleMessage: t('access.staffLeaseTitle'),
        textMessage: t('access.staffLeaseExpired'),
        visibleMessage: true,
      })
      return
    }
    if (
      this.state.ticketIdFromUrl &&
      String(this.state.ticketIdFromUrl) === id
    ) {
      this.hideRelatedStudies()
      return
    }
    this.hideRelatedStudies()
    this.clearComparePriorSession()
    this.setState({
      compareMode: false,
      compareGuideOpen: false,
      loadedRelatedTicketIds: [],
      collapsedStudyKeys: [],
      foreignStudyPrompt: null,
    })
    this._primaryStudyUid = null
    void this.loadRelatedStudyReplace(id)
  }

  /**
   * Carga un prior al Explorer (merge) sin cambiar layout ni abrir serie.
   * @param {string} ticketId
   */
  addRelatedStudy = (ticketId) => {
    const id = ticketId != null ? String(ticketId).trim() : ''
    if (!id) return
    if (this.state.staffLeaseLocked) {
      this.setState({
        titleMessage: t('access.staffLeaseTitle'),
        textMessage: t('access.staffLeaseExpired'),
        visibleMessage: true,
      })
      return
    }
    if (
      this.state.ticketIdFromUrl &&
      String(this.state.ticketIdFromUrl) === id
    ) {
      this.hideRelatedStudies()
      return
    }
    const loaded = this.state.loadedRelatedTicketIds || []
    if (loaded.map(String).includes(id)) {
      this.hideRelatedStudies()
      return
    }
    this.hideRelatedStudies()
    if (!this.hasActiveStudyOpen()) {
      // Sin estudio abierto: replace sin re-bootstrap de acceso (mismo gap que Abrir solo).
      void this.loadRelatedStudyReplace(id)
      return
    }
    void this.loadRelatedStudyMerge(id)
  }

  /** @deprecated usar addRelatedStudy — merge only */
  compareRelatedStudy = (ticketId) => {
    this.addRelatedStudy(ticketId)
  }

  countDistinctStudyUids = (files) => {
    const set = new Set()
    const list = Array.isArray(files) ? files : []
    for (let i = 0; i < list.length; i++) {
      const u = studyUidFromFile(list[i])
      if (u) set.add(u)
    }
    return set.size
  }

  canEnableCompareMode = () => this.countDistinctStudyUids(this.props.files) >= 2

  ensurePrimaryStudyUid = () => {
    if (this._primaryStudyUid) return this._primaryStudyUid
    const files = this.props.files
    if (!Array.isArray(files) || !files.length) return null
    const u = studyUidFromFile(files[0])
    if (u) this._primaryStudyUid = u
    return this._primaryStudyUid
  }

  /**
   * Toggle modo Comparar: ON = 1×2 + Link + guía; OFF = 1×1 + colapsar secundarios.
   * @param {boolean} [force]
   */
  setCompareMode = (force) => {
    const next =
      typeof force === 'boolean' ? force : !this.state.compareMode
    if (next && !this.canEnableCompareMode()) {
      this.setState({
        gatewayPushToast: t('relatedStudies.compareModeDisabled'),
      })
      return
    }
    if (next) {
      this.ensurePrimaryStudyUid()
      this._studyLayoutPollGen = (this._studyLayoutPollGen || 0) + 1
      this.changeLayout(1, 2)
      this._seriesLoadTargetIndex = 0
      this._navActiveIndex = 0
      try {
        if (typeof this.props.setActiveDcmIndex === 'function') {
          this.props.setActiveDcmIndex(0)
        }
      } catch (e) {
        /* ignore */
      }
      this.setState(
        {
          compareMode: true,
          compareGuideOpen: true,
          collapsedStudyKeys: [],
          foreignStudyPrompt: null,
        },
        () => {
          this.enableSeriesLinkForCompare()
        }
      )
      return
    }
    // OFF
    this.changeLayout(1, 1)
    this._seriesLoadTargetIndex = 0
    this._navActiveIndex = 0
    try {
      if (typeof this.props.setActiveDcmIndex === 'function') {
        this.props.setActiveDcmIndex(0)
      }
    } catch (e2) {
      /* ignore */
    }
    const primary = this.ensurePrimaryStudyUid()
    const { studyKeys } = buildStudySeriesMaps(this.props.files || [])
    const collapsed = primary
      ? studyKeys.filter((k) => k !== `uid:${primary}`)
      : []
    this.setState({
      compareMode: false,
      compareGuideOpen: false,
      visibleSeriesLink: false,
      collapsedStudyKeys: collapsed,
      foreignStudyPrompt: null,
    })
  }

  toggleCompareMode = () => {
    this.setCompareMode(!this.state.compareMode)
  }

  handleForeignStudySeries = (seriesFiles, targetIndex) => {
    const uid = studyUidFromFile(
      Array.isArray(seriesFiles) && seriesFiles[0] ? seriesFiles[0] : null
    )
    const idx =
      typeof targetIndex === 'number'
        ? targetIndex
        : this.getSeriesLoadTargetIndex()
    this.setState({
      foreignStudyPrompt: {
        studyUid: uid,
        seriesFiles,
        targetIndex: idx,
      },
      titleMessage: t('relatedStudies.foreignStudyTitle'),
      textMessage: t('relatedStudies.foreignStudyBody'),
      visibleMessage: true,
    })
  }

  /**
   * Confirma diálogo: activar Comparar y abrir la serie pendiente en el viewport destino.
   */
  confirmForeignStudyCompare = () => {
    const prompt = this.state.foreignStudyPrompt
    const seriesFiles = prompt && prompt.seriesFiles
    const targetIndex =
      prompt && typeof prompt.targetIndex === 'number' ? prompt.targetIndex : 1
    this.setState(
      { visibleMessage: false, foreignStudyPrompt: null },
      () => {
        this.setCompareMode(true)
        if (Array.isArray(seriesFiles) && seriesFiles.length) {
          setTimeout(() => {
            this._seriesLoadTargetIndex = targetIndex
            this._navActiveIndex = targetIndex
            try {
              if (typeof this.props.setActiveDcmIndex === 'function') {
                this.props.setActiveDcmIndex(targetIndex)
              }
            } catch (e) {
              /* ignore */
            }
            try {
              if (typeof this.props.isOpenStore === 'function') {
                this.props.isOpenStore({ index: targetIndex, value: true })
              }
            } catch (e2) {
              /* ignore */
            }
            this.explorerOnSelectSeries(seriesFiles, 0)
          }, 120)
        }
      }
    )
  }

  dismissCompareGuide = () => {
    this.setState({ compareGuideOpen: false })
  }

  toggleExplorerStudyCollapse = (studyKey) => {
    const key = String(studyKey || '')
    if (!key) return
    this.setState((prev) => {
      const cur = Array.isArray(prev.collapsedStudyKeys)
        ? prev.collapsedStudyKeys.slice()
        : []
      const i = cur.indexOf(key)
      if (i >= 0) cur.splice(i, 1)
      else cur.push(key)
      return { collapsedStudyKeys: cur }
    })
  }

  clearComparePriorSession = () => {
    this._compareMergeActive = false
    this._compareBaseFiles = null
    this._compareMergedFiles = null
    this._comparePriorStudyUid = null
    this._comparePriorSeriesOpened = false
    this._compareRightViewportIndex = 1
    this._compareTicketId = null
    this._comparePriorOpenAttempt = 0
    if (this._comparePriorOpenTimer) {
      try {
        clearTimeout(this._comparePriorOpenTimer)
      } catch (e) {
        /* ignore */
      }
      this._comparePriorOpenTimer = null
    }
  }

  enableSeriesLinkForCompare = () => {
    if (this.state.mprMode || (this.isMprLayoutActive && this.isMprLayoutActive())) {
      return
    }
    if (this.state.visibleSeriesLink) {
      try {
        this.syncLinkedViewports(this.props.activeDcmIndex, { copyView: true })
      } catch (e) {
        /* ignore */
      }
      return
    }
    this.setState(
      { visibleSeriesLink: true, seriesLinkWwwc: false },
      () => {
        try {
          this.syncLinkedViewports(0, { copyView: true })
        } catch (e2) {
          /* ignore */
        }
      }
    )
  }

  scheduleComparePriorOpen = (files) => {
    /* auto-open deshabilitado en UX comparar-como-modo */
  }

  /**
   * Auto-open de serie prior: deshabilitado — el usuario elige series.
   */
  maybeOpenComparePriorSeries = (_files) => {
    return
  }

  /**
   * Abrir solo / replace: resuelve locator del ticket y carga el estudio
   * sin re-evaluar elevación ni pisar plan/persona (conserva lite/clinical).
   * @param {string} ticketId
   */
  loadRelatedStudyReplace = async (ticketId) => {
    if (!ticketId || this._relatedReplaceInFlight) return
    this._relatedReplaceInFlight = true
    const id = String(ticketId).trim()
    try {
      this.setState({
        gatewayPushToast: t('relatedStudies.compareToast'),
      })

      const ticketApiParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null
      let resolved = await resolveViewerTicket(id, {
        baseUrl: getTicketApiBase(ticketApiParams),
      })
      if (this._unmounted) return
      if (!resolved.ok || !resolved.ticket) {
        const renewed = await this.tryStaffLeaseExtendOnce()
        if (renewed) {
          resolved = await resolveViewerTicket(id, {
            baseUrl: getTicketApiBase(ticketApiParams),
          })
        }
      }
      if (this._unmounted) return
      if (!resolved.ok || !resolved.ticket) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: t('access.invalidTicket'),
          visibleMessage: true,
        })
        return
      }

      const ticket = resolved.ticket
      this.applyViewerSessionPolicy({ ticket, searchParams: ticketApiParams })
      setUdvTelemetrySessionFromTicket(ticket)

      // Plan de la pestaña (no ticket.profile patient del prior).
      const loadProfile =
        (this.state.udvPlan && String(this.state.udvPlan)) ||
        ticket.profile ||
        'patient'
      const pickOpts = this.buildTicketPickOpts(ticket, {
        profile: loadProfile,
        ...this.resolveTicketLanHints(ticket),
      })
      const picked = await pickViewerCandidate(ticket.candidates, pickOpts)
      const target = candidateToLoadTarget(picked.candidate)
      if (!target) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: t('access.invalidTicket'),
          visibleMessage: true,
        })
        return
      }
      this.rememberTicketCandidateWalk(picked, {
        mode: 'replace',
        ticket,
        ticketApiParams,
        pickOpts,
      })

      const primaryUid =
        (target.studyUid && String(target.studyUid)) ||
        studyUidFromManifestUrl(target.kind === 'r2' ? target.manifestUrl : null) ||
        (ticket.raw &&
          (ticket.raw.study_instance_uid ||
            ticket.raw.studyInstanceUID ||
            ticket.raw.study_uid)) ||
        null
      if (primaryUid) this._primaryStudyUid = String(primaryUid)

      const nextState = {
        accessDenied: null,
        ticketIdFromUrl: id,
        relatedStudies: Array.isArray(ticket.relatedStudies)
          ? ticket.relatedStudies
          : this.state.relatedStudies || [],
        relatedPatientId:
          ticket.patientId || this.state.relatedPatientId || null,
        loadedRelatedTicketIds: [],
        collapsedStudyKeys: [],
        compareMode: false,
        compareGuideOpen: false,
        foreignStudyPrompt: null,
        gatewayPushToast: null,
      }

      if (target.kind === 'r2') {
        nextState.manifestUrlFromUrl = target.manifestUrl
        if (target.pacs) nextState.pacsIdFromUrl = target.pacs
        this.setState(nextState, () => {
          const catalogOrigin = resolveStudyCatalogOrigin({
            searchParams: ticketApiParams,
            ticket,
            controllerLinks: loadControllerLinks(),
          })
          void this.loadStudyFromManifestUrl(target.manifestUrl, {
            downloadUrl: target.downloadUrl || null,
            studyInstanceUID: primaryUid,
            catalogOrigin,
            catalogSource: STUDY_SOURCE.TICKET,
          })
        })
        return
      }

      if (target.kind === 'dicomweb') {
        const wadoUrl = target.wadoUrl
        const studyUID = target.studyUid
        if (!wadoUrl || !studyUID) {
          this.setState({
            gatewayPushToast: null,
            titleMessage: t('relatedStudies.title'),
            textMessage: t('access.invalidTicket'),
            visibleMessage: true,
          })
          return
        }
        this._primaryStudyUid = String(studyUID)
        nextState.legacyWadoContext = this.attachTicketOriginMeta(
          {
            wadoUrl: buildWadoUriUrl(null, wadoUrl),
            studyUID,
            seriesUID: null,
            qidoBase: buildDicomWebBaseUrl(null, wadoUrl),
          },
          picked,
          pickOpts,
          ticket.candidates
        )
        this.setState(nextState, () => {
          void this.loadStudyViaLegacyWadoUrl()
        })
        return
      }

      this.setState({
        gatewayPushToast: null,
        titleMessage: t('relatedStudies.title'),
        textMessage: t('access.invalidTicket'),
        visibleMessage: true,
      })
    } catch (e) {
      console.error('loadRelatedStudyReplace', e)
      if (!this._unmounted) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: (e && e.message) || String(e),
          visibleMessage: true,
        })
      }
    } finally {
      this._relatedReplaceInFlight = false
    }
  }

  /**
   * Merge on-demand de un related study al store (sin layout / sin abrir serie).
   * @param {string} ticketId
   */
  loadRelatedStudyMerge = async (ticketId) => {
    if (!ticketId || this._comparePriorInFlight) return
    this._comparePriorInFlight = true
    const id = String(ticketId).trim()
    try {
      this.setState({
        gatewayPushToast: t('relatedStudies.compareToast'),
      })

      const ticketApiParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null
      let resolved = await resolveViewerTicket(id, {
        baseUrl: getTicketApiBase(ticketApiParams),
      })
      if (this._unmounted) return
      if (!resolved.ok || !resolved.ticket) {
        const renewed = await this.tryStaffLeaseExtendOnce()
        if (renewed) {
          resolved = await resolveViewerTicket(id, {
            baseUrl: getTicketApiBase(ticketApiParams),
          })
        }
      }
      if (this._unmounted) return
      if (!resolved.ok || !resolved.ticket) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: t('access.invalidTicket'),
          visibleMessage: true,
        })
        return
      }

      const ticket = resolved.ticket
      const loadProfile =
        (this.state.udvPlan && String(this.state.udvPlan)) ||
        ticket.profile ||
        'patient'
      const pickOpts = this.buildTicketPickOpts(ticket, {
        profile: loadProfile,
        ...this.resolveTicketLanHints(ticket),
      })
      let picked = await pickViewerCandidate(ticket.candidates, pickOpts)
      let target = candidateToLoadTarget(picked.candidate)
      if (!target) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: t('access.invalidTicket'),
          visibleMessage: true,
        })
        return
      }
      this.rememberTicketCandidateWalk(picked, {
        mode: 'merge',
        ticket,
        ticketApiParams,
        pickOpts,
      })

      const priorUid =
        (target.studyUid && String(target.studyUid)) ||
        (ticket.raw &&
          (ticket.raw.study_instance_uid ||
            ticket.raw.studyInstanceUID ||
            ticket.raw.study_uid)) ||
        null

      this.ensurePrimaryStudyUid()
      this._compareBaseFiles = Array.isArray(this.props.files)
        ? this.props.files.slice()
        : []
      this._compareMergedFiles = this._compareBaseFiles.slice()
      this._compareMergeActive = true
      this._comparePriorStudyUid = priorUid ? String(priorUid) : null
      this._comparePriorSeriesOpened = true
      this._compareTicketId = id
      this.autoSelectFirstSeries = false

      this.setState((prev) => {
        const loaded = Array.from(
          new Set([...(prev.loadedRelatedTicketIds || []), id])
        )
        const collapsed = Array.isArray(prev.collapsedStudyKeys)
          ? prev.collapsedStudyKeys.slice()
          : []
        // Prior colapsado al cargar: evita generar thumbs de todas las series a la vez.
        // Al expandir, Explorer encola thumbs de ese study (0.20.94).
        if (priorUid) {
          const priorKey = `uid:${String(priorUid)}`
          if (collapsed.indexOf(priorKey) < 0) collapsed.push(priorKey)
        }
        return {
          loadedRelatedTicketIds: loaded,
          collapsedStudyKeys: collapsed,
        }
      })

      if (target.kind === 'r2') {
        this._comparePriorTicketIdForLease = id
        this._comparePriorPickOpts = pickOpts
        this._comparePriorTicketApiParams = ticketApiParams
        this._compareManifestLeaseRetried = false
        await this.loadStudyFromManifestUrl(target.manifestUrl, {
          downloadUrl: target.downloadUrl || null,
          studyInstanceUID: priorUid,
          skipSourceChoice: true,
          catalogSource: STUDY_SOURCE.TICKET,
          compareMerge: true,
        })
        if (!this._unmounted) {
          this.setState({ gatewayPushToast: t('relatedStudies.loadToast') })
        }
        return
      }

      if (target.kind === 'dicomweb') {
        const wadoUrl = target.wadoUrl
        const studyUID = target.studyUid
        if (!wadoUrl || !studyUID) {
          this.clearComparePriorSession()
          this.setState({
            gatewayPushToast: null,
            titleMessage: t('relatedStudies.title'),
            textMessage: t('access.invalidTicket'),
            visibleMessage: true,
          })
          return
        }
        this._comparePriorStudyUid = String(studyUID)
        this.setState(
          {
            legacyWadoContext: this.attachTicketOriginMeta(
              {
                wadoUrl: buildWadoUriUrl(null, wadoUrl),
                studyUID,
                seriesUID: null,
                qidoBase: buildDicomWebBaseUrl(null, wadoUrl),
              },
              picked,
              pickOpts,
              ticket.candidates
            ),
            gatewayPushToast: t('relatedStudies.loadToast'),
          },
          () => {
            void this.loadStudyViaLegacyWadoUrlCompare()
          }
        )
        return
      }

      this.clearComparePriorSession()
    } catch (e) {
      console.error('loadRelatedStudyMerge', e)
      this.clearComparePriorSession()
      if (!this._unmounted) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('relatedStudies.title'),
          textMessage: (e && e.message) || String(e),
          visibleMessage: true,
        })
      }
    } finally {
      this._comparePriorInFlight = false
    }
  }

  /** @deprecated alias */
  loadComparePriorFromTicket = (ticketId) => this.loadRelatedStudyMerge(ticketId)

  /**
   * WADO legacy en modo compare (beginStudyLoad compare-prior).
   */
  loadStudyViaLegacyWadoUrlCompare = async () => {
    const legacyCtx = this.state.legacyWadoContext
    if (!legacyCtx) {
      this.clearComparePriorSession()
      return
    }
    const pacsCfg = getPacsConfig(this.state.pacsIdFromUrl || null)
    const mode = RENDER_MODE.DIAGNOSTIC
    if (this.state.renderMode !== RENDER_MODE.DIAGNOSTIC) {
      persistRenderMode(RENDER_MODE.DIAGNOSTIC, { userSet: false })
    }

    this.setState({
      studyLoadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
      renderMode: RENDER_MODE.DIAGNOSTIC,
      manifestMediaCaps: { hasPreview: false, hasDicom: true },
      manifestDownloadProgress: null,
    })

    const loadToken = this.beginStudyLoad('compare-prior')
    this._compareMergeActive = true

    try {
      this._legacyWadoLoadInFlight = true
      configureWadoLoaderAuth({
        username: pacsCfg && pacsCfg.USUARIO,
        password: pacsCfg && pacsCfg.PASS,
      })
      const fullCtx = {
        ...legacyCtx,
        wadoUrl: buildWadoUriUrl(pacsCfg, legacyCtx.wadoUrl),
        qidoBase:
          legacyCtx.qidoBase || buildDicomWebBaseUrl(pacsCfg, legacyCtx.wadoUrl),
        authHeaders: pacsAuthHeaders(pacsCfg),
      }
      const loadResult = await loadStudyViaLegacyWado(fullCtx, {
        ...this.buildManifestLoadHandlers(pacsCfg, mode, loadToken, {
          compareMerge: true,
        }),
        renderMode: mode,
      })
      if (!this.isStudyLoadCurrent(loadToken.gen)) return
      try {
        if (loadResult && Array.isArray(loadResult.items) && loadResult.items.length) {
          this.maybeOpenComparePriorSeries(
            mergeStudyFileLists(this._compareBaseFiles || [], loadResult.items)
          )
        }
      } catch (e) {
        /* ignore */
      }
      this.completeManifestLoadStudySource()
      this.setState({ gatewayPushToast: null })
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      const skipped = await this.continueTicketCandidateAfterOriginFailure(error)
      if (skipped) return
      console.error('loadStudyViaLegacyWadoUrlCompare:', error)
      this.clearComparePriorSession()
      this.setState({
        gatewayPushToast: null,
        titleMessage: 'Legacy WADO',
        textMessage: error.message || String(error),
        visibleMessage: true,
      })
      this.completeManifestLoadStudySource()
    } finally {
      this._legacyWadoLoadInFlight = false
    }
  }

  /** Hay estudio en el visor: no interrumpir con biblioteca local. */
  hasActiveStudyOpen = () => {
    if (Array.isArray(this.props.files) && this.props.files.length > 0) return true
    if (Array.isArray(this.props.isOpen) && this.props.isOpen.some(Boolean)) {
      return true
    }
    return false
  }

  /**
   * Abre Estudios locales solo si el visor está idle.
   * @param {object} [extraState]
   */
  openLocalStudiesIfIdle = (extraState) => {
    try {
      resetInteractionState()
    } catch (e) {
      /* ignore */
    }
    const next = Object.assign({}, extraState || {})
    if (!this.hasActiveStudyOpen()) {
      next.visibleLocalStudies = true
    }
    this.setState(next)
  }

  gatewayNoticeStatusLabel = (status) => {
    const map = {
      recibiendo: 'Recibiendo',
      recibido: 'Recibido',
      duplicado: 'Ya en storage',
      incompleto: 'Ingest incompleto',
      error: 'Error',
      mantenido: 'Mantenido local',
      reemplazado: 'Reemplazado',
      rechazado: 'Rechazado',
      aviso: 'Aviso',
    }
    return map[status] || status || 'Aviso'
  }

  upsertGatewayNotice = ( partial ) => {
    const p = partial || {}
    const uid = String(p.studyInstanceUID || p.id || '')
    const id = uid || p.id || `n-${Date.now()}`
    this.setState((prev) => {
      const list = Array.isArray(prev.gatewayNotices) ? prev.gatewayNotices.slice() : []
      const idx = list.findIndex(
        (n) =>
          (uid && n.studyInstanceUID === uid) ||
          n.id === id
      )
      const base =
        idx >= 0
          ? list[idx]
          : {
              id,
              studyInstanceUID: uid,
              patientName: '',
              status: 'aviso',
              message: '',
              unread: true,
              at: Date.now(),
              payload: null,
            }
      const next = {
        ...base,
        ...p,
        id: base.id || id,
        studyInstanceUID: uid || base.studyInstanceUID,
        at: Date.now(),
        unread: p.unread != null ? !!p.unread : true,
      }
      if (idx >= 0) list[idx] = next
      else list.unshift(next)
      return { gatewayNotices: list.slice(0, 30) }
    })
  }

  setGatewayNoticeStatus = (studyInstanceUID, status, message, extra) => {
    if (!studyInstanceUID && !extra) return
    this.upsertGatewayNotice({
      studyInstanceUID: studyInstanceUID || '',
      status,
      message: message || this.gatewayNoticeStatusLabel(status),
      unread: true,
      ...(extra || {}),
    })
  }

  openNotificationCenter = () => {
    this.setState((prev) => ({
      visibleGatewayNotices: true,
      gatewayNotices: (prev.gatewayNotices || []).map((n) => ({
        ...n,
        unread: false,
      })),
      ...(prev.swUpdatePending ? { swUpdateDialog: false } : {}),
    }))
  }

  closeNotificationCenter = () => {
    this.setState({ visibleGatewayNotices: false })
  }

  clearGatewayNotices = () => {
    this.setState({ gatewayNotices: [], visibleGatewayNotices: false })
  }

  handleGatewayNoticeActivate = async (notice) => {
    if (!notice) return
    this.setState({ visibleGatewayNotices: false })
    if (notice.status === 'recibido' || notice.status === 'reemplazado') {
      this.setState({ visibleLocalStudies: true })
      return
    }
    if (notice.status === 'duplicado' && notice.payload) {
      const local = await getStudyCatalog(notice.studyInstanceUID)
      this.setState({
        studyDuplicate: {
          payload: notice.payload,
          localCatalog: local || notice.localCatalog || {},
        },
        studyDuplicateBusy: false,
        studyDuplicateError: null,
      })
      return
    }
    if (notice.payload || notice.studyInstanceUID) {
      await this.handleGatewayPushClick(notice.payload || {
        study_instance_uid: notice.studyInstanceUID,
        patient_name: notice.patientName,
      })
    }
  }

  hasNotificationBell = () => {
    const notices = this.state.gatewayNotices || []
    return !!this.state.swUpdatePending || notices.length > 0
  }

  notificationBellUnread = () => {
    const notices = this.state.gatewayNotices || []
    const unread = notices.filter((n) => n.unread).length
    return unread + (this.state.swUpdatePending ? 1 : 0)
  }

  openLocalStudyInViewer = async (studyInstanceUID, catalogHint) => {
    const loadToken = this.beginStudyLoad('local-cache')
    this.setState({
      visibleLocalStudies: false,
      visibleExplorer: true,
      studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
      manifestDownloadProgress: {
        completed: 0,
        totalTasks: 0,
        loadedOk: 0,
        localPhase: 'index',
        loadSource: 'local',
      },
      gatewayPushToast: 'Abriendo estudio local…',
    })
    // Montar viewport cuanto antes
    try {
      if (typeof this.props.isOpenStore === 'function') {
        this.props.isOpenStore({
          index:
            typeof this.props.activeDcmIndex === 'number'
              ? this.props.activeDcmIndex
              : 0,
          value: true,
        })
      }
    } catch (e) {
      /* ignore */
    }

    const openFirstSeries = (files, opts = {}) => {
      if (!this.isStudyLoadCurrent(loadToken.gen)) return false
      const list = Array.isArray(files) ? files : []
      if (list.length === 0) return false
      const idx =
        typeof this.props.activeDcmIndex === 'number'
          ? this.props.activeDcmIndex
          : 0
      const viewer = this.dicomViewersRefs && this.dicomViewersRefs[idx]
      if (!viewer) return false

      let viewportReady = false
      try {
        const hasStack =
          Array.isArray(viewer.files) &&
          viewer.files.length > 0 &&
          !!viewer.image &&
          !!viewer.dicomImage
        if (hasStack) {
          try {
            // eslint-disable-next-line global-require
            const cs = require('cornerstone-core')
            viewportReady = !!cs.getEnabledElement(viewer.dicomImage)
          } catch (eCs) {
            viewportReady = true
          }
        }
      } catch (e) {
        viewportReady = false
      }

      // Soft-sync solo si el viewport ya está enabled e interactivo
      if (
        opts.force !== true &&
        this.autoSelectFirstSeries === false &&
        viewportReady
      ) {
        try {
          this.syncActiveSeriesFilesFromStore(list)
        } catch (e) {
          /* ignore */
        }
        try {
          if (typeof viewer.ensureToolsRegistered === 'function') {
            viewer.ensureToolsRegistered({ activateScroll: true })
          }
        } catch (eTools) {
          /* ignore */
        }
        return true
      }

      // Agrupar por seriesNumber
      const bySn = new Map()
      list.forEach((f) => {
        const sn =
          f && f.series && f.series.seriesNumber != null
            ? f.series.seriesNumber
            : 0
        if (!bySn.has(sn)) bySn.set(sn, [])
        bySn.get(sn).push(f)
      })
      const sns = [...bySn.keys()].sort((a, b) => a - b)

      // Preferir serie activa del Explorer si el usuario ya eligió
      let explorerIndex = 0
      let seriesFiles = bySn.get(sns[0]) || list
      const activeExplorer =
        typeof this.props.explorerActiveSeriesIndex === 'number'
          ? this.props.explorerActiveSeriesIndex
          : -1
      if (
        this.autoSelectFirstSeries === false &&
        activeExplorer >= 0 &&
        activeExplorer < sns.length
      ) {
        explorerIndex = activeExplorer
        seriesFiles = bySn.get(sns[explorerIndex]) || seriesFiles
      } else {
        for (let i = 0; i < sns.length; i++) {
          const cand = bySn.get(sns[i]) || []
          if (cand.some((f) => f && (f.image || f.imageId))) {
            seriesFiles = cand
            explorerIndex = i
            break
          }
        }
      }
      try {
        this.explorerOnSelectSeries(seriesFiles, explorerIndex)
        try {
          const v2 = this.dicomViewersRefs && this.dicomViewersRefs[idx]
          if (v2 && typeof v2.ensureToolsRegistered === 'function') {
            v2.ensureToolsRegistered({ activateScroll: true })
          }
          if (v2 && typeof v2.reconfigureWwwcForRenderMode === 'function') {
            v2.reconfigureWwwcForRenderMode(
              this.state.renderMode || RENDER_MODE.DIAGNOSTIC
            )
          }
        } catch (ePost) {
          /* ignore */
        }
        return true
      } catch (e) {
        console.warn('openLocalStudyInViewer openFirstSeries', e)
        return false
      }
    }

    try {
      const result = await loadStudyFromLocalCache(studyInstanceUID, {
        setFilesStore: (files) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.props.setFilesStore(files)
        },
        onExplorerUpdate: (data) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.handleExplorerUpdate(data)
        },
        isOpenStore: this.props.isOpenStore,
        activeDcmIndex: this.props.activeDcmIndex,
        onPrepareNewStudy: this.prepareReplaceStudy,
        catalogHint,
        signal: loadToken.signal,
        isCancelled: () => !this.isStudyLoadCurrent(loadToken.gen),
        getActiveSeriesIdx: () =>
          typeof this.props.explorerActiveSeriesIndex === 'number'
            ? this.props.explorerActiveSeriesIndex
            : 0,
        getPinnedSeriesIdx: () => this.getPinnedSeriesIdxForScheduler(),
        getScrollFocus: () => this.getScrollFocusForScheduler(),
        onInstanceReady: (item, meta) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.handleManifestInstanceReady(item, meta)
        },
        onCacheMeta: ({ previewOnly }) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          if (!previewOnly) return
          // Caché solo WebP: forzar PREVIEW antes de abrir viewport
          if (this.state.renderMode === RENDER_MODE.PREVIEW) return
          try {
            persistRenderMode(RENDER_MODE.PREVIEW, { userSet: false })
          } catch (e) {
            /* ignore */
          }
          this.setState({ renderMode: RENDER_MODE.PREVIEW }, () => {
            try {
              const refs = this.dicomViewersRefs || []
              for (let i = 0; i < refs.length; i++) {
                const v = refs[i]
                if (v && typeof v.reconfigureWwwcForRenderMode === 'function') {
                  v.reconfigureWwwcForRenderMode(RENDER_MODE.PREVIEW)
                }
              }
            } catch (e2) {
              /* ignore */
            }
          })
        },
        onDownloadProgress: (stats) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.setState({
            studyLoadSource: STUDY_LOAD_SOURCE.LOCAL,
            manifestDownloadProgress: {
              ...stats,
              localPhase: 'load',
              loadSource: STUDY_LOAD_SOURCE.LOCAL,
            },
            gatewayPushToast: `Abriendo local… ${stats.completed}/${stats.totalTasks}`,
          })
        },
        onLoadComplete: (files) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          // Forzar paint al terminar (evita viewport negro / no interactivo)
          const tryOpen = (attempt) => {
            if (!this.isStudyLoadCurrent(loadToken.gen)) return
            if (openFirstSeries(files)) return
            if (attempt >= 8) return
            setTimeout(() => tryOpen(attempt + 1), 80 * (attempt + 1))
          }
          tryOpen(0)
        },
      })
      if (!this.isStudyLoadCurrent(loadToken.gen)) return null
      this.setState((prev) => ({
        visibleExplorer: true,
        gatewayPushToast: `Estudio local abierto (${result.count} inst.)`,
      }))
      this.completeManifestLoadStudySource()
      // Reintento final por si el viewer montó tarde
      setTimeout(() => {
        if (this.isStudyLoadCurrent(loadToken.gen)) {
          openFirstSeries(result.files)
        }
      }, 200)
      return result
    } catch (error) {
      if (isStudyLoadAbortError(error)) return null
      throw error
    }
  }

  /**
   * Cargar estudio local al Explorer (merge) sin reemplazar ni abrir serie.
   * Mismo paciente; no cambia layout.
   * @param {string} studyInstanceUID
   * @param {object} [catalogHint]
   */
  loadLocalStudyMerge = async (studyInstanceUID, catalogHint) => {
    const uid =
      studyInstanceUID != null ? String(studyInstanceUID).trim() : ''
    if (!uid) return null
    if (this.isStudyUidAlreadyLoaded(uid)) {
      this.hideLocalStudies()
      return null
    }
    if (!this.hasActiveStudyOpen()) {
      return this.openLocalStudyInViewer(uid, catalogHint)
    }
    if (this._comparePriorInFlight) return null
    this._comparePriorInFlight = true
    const loadToken = this.beginStudyLoad('local-merge')
    this.setState({
      visibleLocalStudies: false,
      visibleRelatedStudies: false,
      visibleExplorer: true,
      gatewayPushToast: t('relatedStudies.compareToast'),
    })
    try {
      this.ensurePrimaryStudyUid()
      this._compareBaseFiles = Array.isArray(this.props.files)
        ? this.props.files.slice()
        : []
      this._compareMergedFiles = this._compareBaseFiles.slice()
      this._compareMergeActive = true
      this._comparePriorStudyUid = uid
      this._comparePriorSeriesOpened = true
      this.autoSelectFirstSeries = false
      this.setState((prev) => {
        const collapsed = Array.isArray(prev.collapsedStudyKeys)
          ? prev.collapsedStudyKeys.slice()
          : []
        const priorKey = `uid:${uid}`
        if (collapsed.indexOf(priorKey) < 0) collapsed.push(priorKey)
        return { collapsedStudyKeys: collapsed }
      })

      const result = await loadStudyFromLocalCache(uid, {
        compareMerge: true,
        skipClear: true,
        setFilesStore: (files) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          if (files == null) return
          const next = mergeStudyFileLists(
            this._compareBaseFiles || [],
            files
          )
          this._compareMergedFiles = next
          if (typeof this.props.setFilesStore === 'function') {
            this.props.setFilesStore(next)
          }
        },
        onExplorerUpdate: () => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          const merged =
            this._compareMergedFiles ||
            mergeStudyFileLists(this._compareBaseFiles || [], [])
          this.handleExplorerUpdate(buildExplorerDataFromFiles(merged))
        },
        isOpenStore: this.props.isOpenStore,
        activeDcmIndex: this.props.activeDcmIndex,
        catalogHint,
        signal: loadToken.signal,
        isCancelled: () => !this.isStudyLoadCurrent(loadToken.gen),
        getActiveSeriesIdx: () =>
          typeof this.props.explorerActiveSeriesIndex === 'number'
            ? this.props.explorerActiveSeriesIndex
            : 0,
        getPinnedSeriesIdx: () => this.getPinnedSeriesIdxForScheduler(),
        getScrollFocus: () => this.getScrollFocusForScheduler(),
        onInstanceReady: (item, meta) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.handleManifestInstanceReady(item, meta)
        },
        onDownloadProgress: (stats) => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          this.setState({
            gatewayPushToast: `Cargando local… ${stats.completed}/${stats.totalTasks}`,
          })
        },
        onLoadComplete: () => {
          if (!this.isStudyLoadCurrent(loadToken.gen)) return
          // Merge only: no auto-abrir serie (como Historial → Cargar)
        },
      })
      if (!this.isStudyLoadCurrent(loadToken.gen)) return null
      if (!this._unmounted) {
        this.setState({
          visibleExplorer: true,
          gatewayPushToast: t('relatedStudies.loadToast'),
        })
      }
      return result
    } catch (error) {
      this.clearComparePriorSession()
      if (isStudyLoadAbortError(error)) return null
      if (!this._unmounted) {
        this.setState({
          gatewayPushToast: null,
          titleMessage: t('menu.localStudies'),
          textMessage: (error && error.message) || String(error),
          visibleMessage: true,
        })
      }
      throw error
    } finally {
      this._comparePriorInFlight = false
    }
  }

  handleGatewayPushClick = async (payload) => {
    const data = Object.assign({}, (payload && payload.raw) || {}, payload || {})
    const uid = data.study_instance_uid
    const name = data.patient_name || 'Estudio'
    const notifyOnly = isGatewayNotifyOnly(data)
    const edgeId = data.edge_id || ''
    const flightKey = edgeId ? `${edgeId}:${uid}` : String(uid)
    const studyOpts = {
      patientName: name,
      receivedInstances: data.received_instances,
      ingestComplete: data.ingest_complete !== false,
      edgeWadoUrl: data.edge_wado_url,
      edgeLanBase: data.edge_lan_base,
      edgeId: data.edge_id,
      clinicName: data.clinic_name,
      modality: data.modality,
    }

    if (!uid) {
      const msg = 'Notificación TJS Gateway sin StudyInstanceUID'
      this.setState({ gatewayPushToast: msg })
      this.upsertGatewayNotice({
        id: `err-${Date.now()}`,
        status: 'error',
        message: msg,
        patientName: name,
        payload: data,
      })
      return
    }

    if (notifyOnly) {
      const msg = `Aviso: ${name} (sin descarga)`
      this.setState({ gatewayPushToast: msg })
      this.upsertGatewayNotice({
        studyInstanceUID: uid,
        id: flightKey,
        patientName: name,
        status: 'aviso',
        message: msg,
        payload: data,
        unread: true,
      })
      return
    }

    if (!this._gatewayPushInFlight) this._gatewayPushInFlight = new Set()
    if (this._gatewayPushInFlight.has(flightKey)) {
      return
    }

    const existing = (this.state.gatewayNotices || []).find(
      (n) =>
        n.studyInstanceUID === uid &&
        (!edgeId || (n.payload && n.payload.edge_id) === edgeId)
    )
    if (
      existing &&
      (existing.status === 'recibido' || existing.status === 'reemplazado') &&
      Date.now() - (existing.at || 0) < 120000
    ) {
      this.openLocalStudiesIfIdle({
        gatewayPushToast: `${name}: ya recibido`,
      })
      this.setGatewayNoticeStatus(uid, existing.status, `${name}: ya en storage local`, {
        patientName: name,
        unread: true,
      })
      return
    }

    this._gatewayPushInFlight.add(flightKey)
    this.setGatewayNoticeStatus(uid, 'recibiendo', `Recibiendo: ${name}`, {
      patientName: name,
      payload: data,
    })
    this.setState({ gatewayPushToast: `Recibiendo: ${name}…` })

    try {
      if (data.ingest_complete === false) {
        const msg = `Ingest incompleto: ${name}`
        this.setState({ gatewayPushToast: msg })
        this.setGatewayNoticeStatus(uid, 'incompleto', msg, {
          patientName: name,
          payload: data,
        })
        return
      }

      const local = await getStudyCatalog(uid)

      if (local && local.instanceCount > 0) {
        this.setState({
          studyDuplicate: { payload: data, localCatalog: local },
          studyDuplicateBusy: false,
          studyDuplicateError: null,
          gatewayPushToast: `Ya en storage: ${name}`,
        })
        this.setGatewayNoticeStatus(uid, 'duplicado', `Ya en storage: ${name}`, {
          patientName: name,
          payload: data,
          localCatalog: local,
        })
        return
      }

      this.setState({
        gatewayPushToast: `Descargando: ${name}…`,
      })
      this.setGatewayNoticeStatus(uid, 'recibiendo', `Descargando: ${name}…`, {
        patientName: name,
        payload: data,
      })

      // Prefer clinic LAN API base when health probe OK; soft-fail → edge_wado_url (Tailscale/public).
      let downloadOpts = { ...studyOpts }
      const lanBase = normalizeEdgeLanBase(data.edge_lan_base)
      if (lanBase) {
        const lanOk = await probeEdgeLanHealth(lanBase)
        if (lanOk) {
          downloadOpts = {
            ...downloadOpts,
            edgeApiBase: lanBase,
            edgeWadoUrl: `${lanBase}/wado`,
          }
        }
      }

      const result = await acceptStudyFromGateway(uid, downloadOpts)
      let msg = `Recibido: ${name} (${result.stored} inst.)`
      if (result.ackSkipped || result.ackError) {
        msg += ' — ACK pendiente en Edge'
      }
      this.openLocalStudiesIfIdle({ gatewayPushToast: msg })
      this.setGatewayNoticeStatus(uid, 'recibido', msg, {
        patientName: name,
        payload: data,
      })
    } catch (e) {
      const msg = (e && e.message) || String(e)
      this.openLocalStudiesIfIdle({ gatewayPushToast: msg })
      this.setGatewayNoticeStatus(uid, 'error', msg, {
        patientName: name,
        payload: data,
      })
    } finally {
      this._gatewayPushInFlight.delete(flightKey)
    }
  }

  handleStudyDuplicateKeep = async () => {
    const dup = this.state.studyDuplicate
    if (!dup) return
    const p = dup.payload || {}
    const uid = p.study_instance_uid
    const name = p.patient_name || 'Estudio'
    this.setState({ studyDuplicateBusy: true, studyDuplicateError: null })
    try {
      await keepLocalStudyAck(uid, {
        local_instances: (dup.localCatalog && dup.localCatalog.instanceCount) || 0,
        edgeWadoUrl: p.edge_wado_url || (dup.localCatalog && dup.localCatalog.edgeWadoUrl),
        edgeApiBase: dup.localCatalog && dup.localCatalog.edgeApiBase,
        edgeId: p.edge_id || (dup.localCatalog && dup.localCatalog.edgeId),
      })
      this.setState({
        studyDuplicate: null,
        studyDuplicateBusy: false,
        gatewayPushToast: 'Se mantiene la copia local (ACK accept/keep)',
      })
      if (uid) {
        this.setGatewayNoticeStatus(uid, 'mantenido', `Mantenido local: ${name}`, {
          patientName: name,
        })
      }
    } catch (e) {
      const msg = (e && e.message) || String(e)
      // Si ACK no existe, igual cerramos con aviso
      if (e && e.code === 'GATEWAY_ENDPOINT_MISSING') {
        this.setState({
          studyDuplicate: null,
          studyDuplicateBusy: false,
          gatewayPushToast: msg,
        })
        if (uid) {
          this.setGatewayNoticeStatus(uid, 'mantenido', msg, { patientName: name })
        }
        return
      }
      this.setState({ studyDuplicateBusy: false, studyDuplicateError: msg })
    }
  }

  handleStudyDuplicateReplace = async () => {
    const dup = this.state.studyDuplicate
    if (!dup) return
    const p = dup.payload || {}
    const uid = p.study_instance_uid
    const name = p.patient_name || 'Estudio'
    this.setState({ studyDuplicateBusy: true, studyDuplicateError: null })
    try {
      const result = await acceptStudyFromGateway(uid, {
        replace: true,
        patientName: p.patient_name || (dup.localCatalog && dup.localCatalog.patientName),
        receivedInstances: p.received_instances,
        ingestComplete: p.ingest_complete !== false,
        edgeWadoUrl: p.edge_wado_url || (dup.localCatalog && dup.localCatalog.edgeWadoUrl),
        edgeApiBase: dup.localCatalog && dup.localCatalog.edgeApiBase,
        edgeId: p.edge_id || (dup.localCatalog && dup.localCatalog.edgeId),
        clinicName: p.clinic_name,
        modality: p.modality,
      })
      const msg = `Reemplazado: ${name} (${result.stored} inst.)`
      this.openLocalStudiesIfIdle({
        studyDuplicate: null,
        studyDuplicateBusy: false,
        gatewayPushToast: msg,
      })
      if (uid) {
        this.setGatewayNoticeStatus(uid, 'reemplazado', msg, { patientName: name })
      }
    } catch (e) {
      this.setState({
        studyDuplicateBusy: false,
        studyDuplicateError: (e && e.message) || String(e),
      })
    }
  }

  handleStudyDuplicateReject = async () => {
    const dup = this.state.studyDuplicate
    if (!dup) return
    const p = dup.payload || {}
    const uid = p.study_instance_uid
    const name = p.patient_name || 'Estudio'
    this.setState({ studyDuplicateBusy: true, studyDuplicateError: null })
    try {
      await rejectStudyOnGateway(uid, {
        local_instances: (dup.localCatalog && dup.localCatalog.instanceCount) || 0,
        edgeWadoUrl: p.edge_wado_url || (dup.localCatalog && dup.localCatalog.edgeWadoUrl),
        edgeApiBase: dup.localCatalog && dup.localCatalog.edgeApiBase,
        edgeId: p.edge_id || (dup.localCatalog && dup.localCatalog.edgeId),
      })
      this.setState({
        studyDuplicate: null,
        studyDuplicateBusy: false,
        gatewayPushToast: 'Estudio rechazado (ACK reject)',
      })
      if (uid) {
        this.setGatewayNoticeStatus(uid, 'rechazado', `Rechazado: ${name}`, {
          patientName: name,
        })
      }
    } catch (e) {
      const msg = (e && e.message) || String(e)
      if (e && e.code === 'GATEWAY_ENDPOINT_MISSING') {
        this.setState({
          studyDuplicate: null,
          studyDuplicateBusy: false,
          gatewayPushToast: msg,
        })
        if (uid) {
          this.setGatewayNoticeStatus(uid, 'rechazado', msg, { patientName: name })
        }
        return
      }
      this.setState({ studyDuplicateBusy: false, studyDuplicateError: msg })
    }
  }

  handleGatewayPushReceived = (payload) => {
    const data = Object.assign({}, (payload && payload.raw) || {}, payload || {})
    const uid = data.study_instance_uid
    const name = data.patient_name || 'Estudio'
    const notifyOnly = isGatewayNotifyOnly(data)
    const verBits = []
    if (data.edge_version) verBits.push(`Edge ${data.edge_version}`)
    if (data.controller_version) {
      verBits.push(`Ctrl ${data.controller_version}`)
    }
    const verSuffix = verBits.length ? ` · ${verBits.join(' · ')}` : ''
    const toast = uid
      ? notifyOnly
        ? `Aviso Gateway: ${name}${verSuffix}`
        : `Nuevo estudio Gateway: ${name}${verSuffix}`
      : 'Notificación TJS Gateway sin StudyInstanceUID'
    this.setState({ gatewayPushToast: toast })
    this.upsertGatewayNotice({
      studyInstanceUID: uid || '',
      id: uid || `n-${Date.now()}`,
      patientName: name,
      status: uid ? (notifyOnly ? 'aviso' : 'recibiendo') : 'error',
      message: toast,
      payload: data,
      unread: true,
    })
    // Solo descarga automática si es "notificar y enviar" (no study_notify)
    if (uid && !notifyOnly) {
      void this.handleGatewayPushClick(payload)
    }
  }

  handleGatewaySwMessage = (event) => {
    const data = event && event.data
    if (!data || !data.type) return
    if (data.type === 'UDV_PUSH_RECEIVED') {
      this.handleGatewayPushReceived(data.payload)
      return
    }
    if (data.type === 'UDV_PUSH_CLICK') {
      this.handleGatewayPushClick(data.payload)
    }
  }

  handleOrthancImageLoad = (wadoUrl) => {
    try {
      const pacsId = this.state.pacsIdFromUrl || null
      const pacsConfig = getPacsConfig(pacsId)
      configureWadoLoaderAuth({
        username: pacsConfig.USUARIO,
        password: pacsConfig.PASS,
      })

      // Remove any existing wadouri: prefix and clean the URL
      const cleanUrl = wadoUrl.replace(/^wadouri:/, '');
      
      // Get the active viewer
      const activeViewer = this.getActiveDcmViewer();
      if (!activeViewer) {
        throw new Error('No active DICOM viewer found');
      }

      // Load the image using the viewer's loadImage method
      // This method uses callbacks instead of returning a Promise
      activeViewer.loadImage(undefined, cleanUrl);

      // Poll solo para errores de carga — no forzar layout (multi-panel puede estar activo)
      const pollGen = this._studyLayoutPollGen
      const checkForError = () => {
        if (pollGen !== this._studyLayoutPollGen) return

        if (activeViewer.state.errorOnOpenImage) {
          this.setState({
            titleMessage: 'Error',
            textMessage: activeViewer.state.errorOnOpenImage,
            visibleMessage: true
          });
          return;
        }
        if (activeViewer.state.errorOnCors) {
          this.setState({
            titleMessage: 'CORS Error',
            textMessage: t('messages.imageCorsFailed'),
            visibleMessage: true
          });
          return;
        }

        // Éxito: no cambiar layout (ya es 1x1 por defecto; multi lo elige el usuario)
        if (activeViewer.image) {
          const modality = getDicomModality(activeViewer.image);
          console.log('DICOM Modality:', modality);
          return;
        }

        this._studyLayoutPollTimer = setTimeout(checkForError, 100);
      };
      checkForError();

    } catch (error) {
      console.error('Error in handleOrthancImageLoad:', error);
      this.setState({
        titleMessage: 'Error',
        textMessage: t('messages.imageLoadFailed', { error: error.message }),
        visibleMessage: true
      });
    }
  }

  handleExplorerUpdate = (explorerData) => {
    // Flushes progresivos no deben rearmar auto-select (eso vuelve a serie 0).
    // Solo prepareReplaceStudy / apertura nueva ponen autoSelectFirstSeries = true.
    this.explorer = explorerData
    this.syncAutoRefPinsFromStore()

    // Mostrar el explorer automáticamente en todos los dispositivos (móviles, tablets y escritorio)
    if (!this.hasAutoOpenedExplorer) {
        this.setState({ visibleExplorer: true })
        this.hasAutoOpenedExplorer = true
    }
  }

  getPinnedSeriesIdxForScheduler = () => {
    const autoOn = getExplorerAutoRefPin({
      portalProfile: this.isPortalProfileSession(),
    })
    const manual = Array.isArray(this._manualPinnedSeriesIdx)
      ? this._manualPinnedSeriesIdx
      : []
    const auto =
      autoOn && Array.isArray(this._autoRefPinnedSeriesIdx)
        ? this._autoRefPinnedSeriesIdx
        : []
    const merged = [...manual, ...auto]
    const seen = new Set()
    const out = []
    for (let i = 0; i < merged.length; i++) {
      const raw = merged[i]
      if (typeof raw !== 'number' || !Number.isFinite(raw)) continue
      const n = Math.floor(raw)
      if (seen.has(n)) continue
      seen.add(n)
      out.push(n)
      if (out.length >= 4) break
    }
    return out
  }

  syncAutoRefPinsFromStore = () => {
    if (
      !getExplorerAutoRefPin({
        portalProfile: this.isPortalProfileSession(),
      })
    ) {
      if ((this._autoRefPinnedSeriesIdx || []).length) {
        this._autoRefPinnedSeriesIdx = []
        this.setState((prev) => ({
          explorerPinTick: (prev.explorerPinTick || 0) + 1,
        }))
      } else {
        this._autoRefPinnedSeriesIdx = []
      }
      return
    }
    const store = this.props.series
    if (!store || !store.seriesList) return
    const seriesKeys = Array.isArray(store.seriesKeys)
      ? store.seriesKeys
      : [...store.seriesList.keys()]
    const next = findRefSeriesIndices(seriesKeys, store.seriesList)
    const prev = this._autoRefPinnedSeriesIdx || []
    const same =
      prev.length === next.length && prev.every((v, i) => v === next[i])
    this._autoRefPinnedSeriesIdx = next
    if (!same) {
      this.setState((prevState) => ({
        explorerPinTick: (prevState.explorerPinTick || 0) + 1,
      }))
    }
  }

  pinSeriesForDownload = (explorerIndex, opts = {}) => {
    const source = opts.source || 'manual'
    const idx = Math.floor(explorerIndex)
    if (typeof idx !== 'number' || !Number.isFinite(idx) || idx < 0) return
    if (source === 'auto') {
      if (!(this._autoRefPinnedSeriesIdx || []).includes(idx)) {
        this._autoRefPinnedSeriesIdx = [...(this._autoRefPinnedSeriesIdx || []), idx]
      }
      return
    }
    let manual = [...(this._manualPinnedSeriesIdx || [])].filter((i) => i !== idx)
    manual.push(idx)
    if (manual.length > MANUAL_PIN_MAX) {
      manual = manual.slice(manual.length - MANUAL_PIN_MAX)
    }
    this._manualPinnedSeriesIdx = manual
    this.setState((prev) => ({
      explorerPinTick: (prev.explorerPinTick || 0) + 1,
    }))
  }

  unpinSeriesForDownload = (explorerIndex, opts = {}) => {
    const source = opts.source || 'manual'
    const idx = Math.floor(explorerIndex)
    if (source === 'all' || source === 'manual') {
      this._manualPinnedSeriesIdx = (this._manualPinnedSeriesIdx || []).filter(
        (i) => i !== idx
      )
    }
    if (source === 'all' || source === 'auto') {
      this._autoRefPinnedSeriesIdx = (this._autoRefPinnedSeriesIdx || []).filter(
        (i) => i !== idx
      )
    }
    this.setState((prev) => ({
      explorerPinTick: (prev.explorerPinTick || 0) + 1,
    }))
  }

  getExplorerSeriesFilesByKey = (seriesKey) => {
    const files = Array.isArray(this.props.files) ? this.props.files : []
    if (seriesKey == null || seriesKey === '') return []
    const want = String(seriesKey)
    return files.filter((f) => {
      if (!f || !f.series) return false
      const sn =
        f.series.seriesNumber != null ? String(f.series.seriesNumber) : ''
      const uid =
        (f.series.seriesInstanceUID && String(f.series.seriesInstanceUID)) ||
        (f.seriesInstanceUID && String(f.seriesInstanceUID)) ||
        ''
      return uid === want || sn === want
    })
  }

  getExplorerSeriesFilesByIndex = (explorerIndex) => {
    const store = this.props.series
    if (
      !store ||
      !store.seriesList ||
      typeof explorerIndex !== 'number' ||
      !Number.isFinite(explorerIndex) ||
      explorerIndex < 0
    ) {
      return []
    }
    const keys = Array.isArray(store.seriesKeys)
      ? store.seriesKeys
      : [...store.seriesList.keys()]
    if (explorerIndex >= keys.length) return []
    const key = keys[explorerIndex]
    const bucket =
      typeof store.seriesList.get === 'function'
        ? store.seriesList.get(key)
        : store.seriesList[key]
    return Array.isArray(bucket) ? bucket.slice() : []
  }

  resolveExplorerSeriesFiles = (ctx = {}) => {
    if (Array.isArray(ctx.seriesFiles) && ctx.seriesFiles.length) {
      return ctx.seriesFiles
    }
    const explorerIndex =
      typeof ctx.explorerIndex === 'number' ? ctx.explorerIndex : null
    if (explorerIndex != null) {
      const byIdx = this.getExplorerSeriesFilesByIndex(explorerIndex)
      if (byIdx.length) return byIdx
    }
    const seriesKey = ctx.seriesKey != null ? String(ctx.seriesKey) : ''
    return this.getExplorerSeriesFilesByKey(seriesKey)
  }

  refetchExplorerSeriesAfterRetry = async (seriesFiles, baseStore) => {
    if (!Array.isArray(seriesFiles) || !seriesFiles.length) return
    const manifest = this._lastManifestObject
    const assetBaseUrl = this.getUpgradeAssetBaseUrl()
    const renderMode = this.state.renderMode || RENDER_MODE.DIAGNOSTIC
    let working = Array.isArray(baseStore) ? baseStore.slice() : []

    const applyPatch = (file, patch) => {
      const sop = file && file.sopInstanceUID ? String(file.sopInstanceUID) : null
      if (!sop) return
      working = working.map((f) => {
        if (f && f.sopInstanceUID && String(f.sopInstanceUID) === sop) {
          return { ...f, ...patch }
        }
        return f
      })
      if (typeof this.props.setFilesStore === 'function') {
        this.props.setFilesStore(working)
      }
    }

    try {
      await refetchExplorerSeriesFiles(seriesFiles, {
        manifest,
        assetBaseUrl,
        renderMode,
        parallel: 3,
        onFileLoaded: applyPatch,
      })
    } catch (e) {
      console.warn('refetchExplorerSeriesAfterRetry', e)
    }
  }

  handleExplorerSeriesAction = async (action, ctx = {}) => {
    const explorerIndex =
      typeof ctx.explorerIndex === 'number' ? ctx.explorerIndex : null
    const seriesKey = ctx.seriesKey != null ? String(ctx.seriesKey) : ''
    const seriesFiles = this.resolveExplorerSeriesFiles(ctx)

    if (action === 'pin') {
      if (explorerIndex != null) this.pinSeriesForDownload(explorerIndex)
      return
    }
    if (action === 'unpin') {
      if (explorerIndex != null) {
        this.unpinSeriesForDownload(explorerIndex, { source: 'manual' })
      }
      return
    }
    if (action === 'copySeriesUid') {
      const uid = seriesInstanceUidFromFiles(seriesFiles)
      if (uid) {
        try {
          await copyTextToClipboard(uid)
          this.setState({
            gatewayPushToast: t('explorer.toast.seriesUidCopied'),
          })
        } catch (e) {
          /* ignore */
        }
      }
      return
    }
    if (action === 'retryDownload') {
      if (!seriesFiles.length) return
      try {
        await prepareSeriesRetry(seriesFiles)
        const nextStore = mergeRetriedSeriesIntoStore(
          this.props.files || [],
          seriesFiles
        )
        if (typeof this.props.setFilesStore === 'function') {
          this.props.setFilesStore(nextStore)
        }
        this.setState({
          gatewayPushToast: t('explorer.toast.retryStarted'),
        })
        void this.refetchExplorerSeriesAfterRetry(seriesFiles, nextStore)
      } catch (e) {
        console.warn('handleExplorerSeriesAction retryDownload', e)
      }
      return
    }
    if (action === 'downloadSeries') {
      this.handleDownloadExplorerSeries(seriesFiles)
    }
  }

  handleDownloadExplorerSeries = (seriesFiles) => {
    const list = Array.isArray(seriesFiles) ? seriesFiles : []
    if (!list.length) return
    const run = () => void this.handleDownloadExplorerSeriesCore(list)
    if (this.promptPortalDisclaimer(run, { sensitive: true })) return
    void run()
  }

  handleDownloadExplorerSeriesCore = async (seriesFiles) => {
    if (this.state.studyZipDownloadBusy) return
    const studyUid = this.getCurrentStudyUid ? this.getCurrentStudyUid() : ''
    const sample = seriesFiles[0] || {}
    const baseMeta = resolveStudyZipMeta(
      seriesFiles,
      this._lastManifestObject,
      studyUid
    )
    const filename = buildSeriesZipDownloadFilename({
      ...baseMeta,
      seriesNumber: sample.series && sample.series.seriesNumber,
      seriesDescription: sample.series && sample.series.seriesDescription,
    })
    const isPreview =
      this.state.renderMode === RENDER_MODE.PREVIEW ||
      this.isPortalProfileSession()

    this.setState({
      studyZipDownloadBusy: true,
      gatewayPushToast: t('explorer.toast.downloadSeriesStarted'),
    })

    try {
      if (isPreview) {
        const built = await buildPreviewShareFiles({
          files: seriesFiles,
          mode: 'series',
        })
        if (!built.files || !built.files.length) {
          this.setState({
            studyZipDownloadBusy: false,
            gatewayPushToast: t('explorer.toast.downloadSeriesFail'),
          })
          return
        }
        const dl = built.files[0]
        downloadFileFallback(dl, dl.name || filename)
        this.setState({
          studyZipDownloadBusy: false,
          gatewayPushToast: built.truncated
            ? t('explorer.toast.downloadSeriesPartial')
            : t('explorer.toast.downloadSeriesOk'),
        })
        return
      }

      const built = await buildSeriesZipFromFiles({
        files: seriesFiles,
        studyUid,
        filename,
      })
      if (!built.file) {
        const msgKey =
          built.error === 'no_cached'
            ? 'explorer.toast.downloadSeriesNoCached'
            : 'explorer.toast.downloadSeriesFail'
        this.setState({
          studyZipDownloadBusy: false,
          gatewayPushToast: t(msgKey),
        })
        return
      }
      downloadFileFallback(built.file, filename)
      const partial =
        typeof built.added === 'number' &&
        typeof built.total === 'number' &&
        built.added < built.total
      this.setState({
        studyZipDownloadBusy: false,
        gatewayPushToast: partial
          ? t('explorer.toast.downloadSeriesPartial')
          : t('explorer.toast.downloadSeriesOk'),
      })
    } catch (e) {
      console.warn('handleDownloadExplorerSeriesCore', e)
      this.setState({
        studyZipDownloadBusy: false,
        gatewayPushToast: t('explorer.toast.downloadSeriesFail'),
      })
    }
  }

  getExplorerPinProps = () => ({
    onExplorerSeriesAction: this.handleExplorerSeriesAction,
    manualPinnedSeriesIndices: this._manualPinnedSeriesIdx || [],
    autoPinnedSeriesIndices: getExplorerAutoRefPin({
      portalProfile: this.isPortalProfileSession(),
    })
      ? this._autoRefPinnedSeriesIdx || []
      : [],
    isPortalProfile: this.isPortalProfileSession(),
    isPreviewMode:
      this.state.renderMode === RENDER_MODE.PREVIEW ||
      this.isPortalProfileSession(),
    explorerPinTick: this.state.explorerPinTick || 0,
    showRelatedStudies:
      Array.isArray(this.state.relatedStudies) &&
      this.state.relatedStudies.length > 0,
    relatedStudiesCount: Array.isArray(this.state.relatedStudies)
      ? this.state.relatedStudies.length
      : 0,
    onRelatedStudies: this.showRelatedStudies,
    showWorklist: this.hasWorklistUi(),
    worklistCount: Array.isArray(this.state.worklistItems)
      ? this.state.worklistItems.length
      : 0,
    onWorklist: this.showWorklist,
    compareMode: !!this.state.compareMode,
    collapsedStudyKeys: this.state.collapsedStudyKeys || [],
    onToggleStudyCollapse: this.toggleExplorerStudyCollapse,
    onForeignStudySeries: this.handleForeignStudySeries,
    primaryStudyUid: this._primaryStudyUid || null,
    viewportStudyUids: this.getViewportStudyUids(),
  })

  /**
   * Inicia una nueva carga de estudio: aborta la anterior (si hay) y
   * devuelve { gen, signal } para que los loaders dejen de escribir al store.
   */
  beginStudyLoad = (reason) => {
    try {
      if (this._studyLoadAbort) {
        this._studyLoadAbort.abort()
      }
    } catch (e) {
      /* ignore */
    }
    try {
      if (
        this._openMultipleFilesDlgRef &&
        typeof this._openMultipleFilesDlgRef.cancel === 'function'
      ) {
        this._openMultipleFilesDlgRef.cancel()
      }
    } catch (e2) {
      /* ignore */
    }
    // Soft: Orthanc ya incrementa _studyLoadSeq al elegir otro estudio;
    // cancelActiveLoad solo si venimos de Cancelar explícito.
    this._studyLoadGen += 1
    const gen = this._studyLoadGen
    let signal
    try {
      this._studyLoadAbort =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      signal = this._studyLoadAbort ? this._studyLoadAbort.signal : undefined
    } catch (e3) {
      this._studyLoadAbort = null
      signal = undefined
    }
    if (reason) {
      try {
        // eslint-disable-next-line no-console
        console.info('[UDV] beginStudyLoad', reason, 'gen=', gen)
      } catch (e4) {
        /* ignore */
      }
    }
    if (
      reason &&
      reason !== 'render-mode' &&
      reason !== 'preview-grow' &&
      reason !== 'compare-prior' &&
      reason !== 'local-merge'
    ) {
      this.resetCopilotState()
      this.clearPreviousStudyUi()
    }
    return { gen, signal }
  }

  isStudyLoadCurrent = (gen) => Number(gen) === this._studyLoadGen

  /**
   * Cancela la descarga/carga en curso (botón o switch externo).
   * @param {{ userInitiated?: boolean, clearFiles?: boolean }} [opts]
   */
  cancelStudyLoad = (opts = {}) => {
    const userInitiated = opts.userInitiated !== false
    try {
      if (this._studyLoadAbort) {
        this._studyLoadAbort.abort()
      }
    } catch (e) {
      /* ignore */
    }
    this._studyLoadAbort = null
    this._studyLoadGen += 1
    try {
      if (
        this._orthancConnectionRef &&
        typeof this._orthancConnectionRef.cancelActiveLoad === 'function'
      ) {
        this._orthancConnectionRef.cancelActiveLoad()
      }
    } catch (e2) {
      /* ignore */
    }
    try {
      if (
        this._openMultipleFilesDlgRef &&
        typeof this._openMultipleFilesDlgRef.cancel === 'function'
      ) {
        this._openMultipleFilesDlgRef.cancel()
      }
    } catch (e3) {
      /* ignore */
    }
    this._manifestLoadInFlight = false
    this._legacyWadoLoadInFlight = false
    if (this._unmounted) return
    const next = {
      studyLoadSource: null,
      manifestDownloadProgress: null,
    }
    if (userInitiated) {
      next.gatewayPushToast = 'Descarga cancelada. Podés abrir otro estudio.'
    }
    this.setState(next)
    if (opts.clearFiles) {
      try {
        if (typeof this.props.setFilesStore === 'function') {
          this.props.setFilesStore(null)
        }
      } catch (e4) {
        /* ignore */
      }
    }
  }

  prepareReplaceStudy = () => {
    this.autoSelectFirstSeries = true
    this.hasAutoOpenedExplorer = true
    this._manualPinnedSeriesIdx = []
    this._autoRefPinnedSeriesIdx = []
    this.clearPreviousStudyUi()
    try {
      this.setState({
        studyLoadSource: null,
        manifestDownloadProgress: null,
      })
    } catch (e3) {
      /* ignore */
    }
  }

  applySwUpdate = () => {
    const { swRegistration } = this.state
    if (!applyServiceWorkerUpdate(swRegistration)) {
      window.location.reload()
      return
    }
    this.setState({
      swUpdateReady: false,
      swUpdatePending: false,
      swUpdateDialog: false,
    })
  }

  dismissSwUpdateToast = () => {
    this.setState({
      swUpdateReady: false,
      swUpdatePending: true,
    })
  }

  onSwUpdateToastClose = (event, reason) => {
    if (reason === 'clickaway') return
    if (!this.state.swUpdateReady) return
    this.dismissSwUpdateToast()
  }

  openSwUpdateFromBell = () => {
    this.setState({
      swUpdateDialog: true,
      swUpdateReady: false,
      swUpdatePending: true,
    })
  }

  closeSwUpdateDialog = () => {
    this.setState({ swUpdateDialog: false, swUpdatePending: true })
  }

  handleInstallApp = async () => {
    if (isPwaStandalone()) {
      this.setState({ showInstallBanner: false, visibleInstallHelp: false })
      return
    }
    const promptEvent = this.state.deferredInstallPrompt
    if (promptEvent && typeof promptEvent.prompt === 'function') {
      try {
        promptEvent.prompt()
        if (promptEvent.userChoice) {
          const choice = await promptEvent.userChoice
          if (choice && choice.outcome === 'accepted') {
            this.handlePwaInstalledHandoff()
            return
          }
        }
      } catch (e) {
        /* ignore */
      }
      this.setState({ deferredInstallPrompt: null, showInstallBanner: false })
      try {
        window.__UDV_DEFERRED_INSTALL = null
      } catch (err) {
        /* ignore */
      }
      return
    }
    this.setState({ visibleInstallHelp: true })
  }

  /** URL del estudio actual para reanudar tras instalar PWA (?t= u otros deep links). */
  getViewerResumeUrl = () => {
    const shareUrl = this.getStudyShareUrl()
    if (shareUrl) return shareUrl
    if (typeof window === 'undefined') return ''
    try {
      const href = window.location.href
      const u = new URL(href)
      if (
        u.searchParams.has('t') ||
        u.searchParams.has('manifestUrl') ||
        u.searchParams.has('udvInvite')
      ) {
        return href.split('#')[0]
      }
    } catch (e) {
      /* ignore */
    }
    return ''
  }

  handlePwaInstalledHandoff = () => {
    const resumeUrl = this.getViewerResumeUrl()
    if (resumeUrl) {
      savePwaResumeViewerUrl(resumeUrl)
    }
    try {
      window.__UDV_DEFERRED_INSTALL = null
    } catch (err) {
      /* ignore */
    }
    this.setState({
      deferredInstallPrompt: null,
      showInstallBanner: false,
      pwaShowOpenStudyBanner: false,
      visiblePwaSuccessDlg: true,
    })
  }

  getPortalReturnUrl = () => {
    if (typeof window === 'undefined') return null
    try {
      const params = new URLSearchParams(window.location.search)
      const base = getTicketApiBase(params)
      return base && /^https?:\/\//i.test(base) ? base : null
    } catch (e) {
      return null
    }
  }

  hidePwaSuccessDlg = () => {
    this.setState({ visiblePwaSuccessDlg: false })
  }

  handleCopyStudyLinkFromPwaSuccess = () => {
    this.handleCopyStudyLink()
    this.hidePwaSuccessDlg()
  }

  handleReturnToPortalFromPwaSuccess = () => {
    const portalUrl = this.getPortalReturnUrl()
    returnToPortalFromViewerTab(portalUrl)
    this.hidePwaSuccessDlg()
  }

  hideInstallHelp = () => {
    this.setState({ visibleInstallHelp: false })
  }

  handleDismissPwaInstall = (e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    dismissPwaInstallPrompt()
    this.setState({ showInstallBanner: false, deferredInstallPrompt: null })
  }

  maybeNudgePwaInstallAfterStudyLoad = () => {
    if (!shouldOfferPwaInstall({ pwaInstallAllowed: this.isPwaInstallAllowed() })) return
    this.setState({ showInstallBanner: true })
  }

  maybeShowPortalDisclaimerAfterStudyLoad = () => {
    if (!this.isPortalProfileSession()) return
    if (!shouldShowPortalDisclaimerModal()) return
    if (this.state.visiblePortalDisclaimer) return
    this._portalDisclaimerAfterAck = null
    this.setState({
      visiblePortalDisclaimer: true,
      portalDisclaimerSensitive: false,
    })
  }

  /**
   * @param {() => void} afterAck — solo se invoca si se muestra el modal (return true)
   * @param {{ sensitive?: boolean, force?: boolean }} [opts]
   * @returns {boolean} true si el modal se mostró (caller no debe seguir)
   */
  promptPortalDisclaimer = (afterAck, opts = {}) => {
    if (!this.isPortalProfileSession()) {
      return false
    }
    const force = !!opts.force || !!opts.sensitive
    if (!force && !shouldShowPortalDisclaimerModal()) {
      return false
    }
    this._portalDisclaimerAfterAck =
      typeof afterAck === 'function' ? afterAck : null
    this.setState({
      visiblePortalDisclaimer: true,
      portalDisclaimerSensitive: !!opts.sensitive,
    })
    return true
  }

  handlePortalDisclaimerAck = () => {
    dismissPortalDisclaimer()
    const afterAck = this._portalDisclaimerAfterAck
    this._portalDisclaimerAfterAck = null
    this.setState(
      {
        visiblePortalDisclaimer: false,
        portalDisclaimerSensitive: false,
      },
      () => {
        if (typeof afterAck === 'function') afterAck()
      }
    )
  }

  renderPwaInstallBanner = (label, opts = {}) => (
    <div
      className={
        opts.belowAppBar
          ? 'udv-install-banner-wrap is-below-appbar'
          : 'udv-install-banner-wrap'
      }
    >
      <button
        type="button"
        className="udv-install-banner"
        onClick={this.handleInstallApp}
      >
        {label}
      </button>
      <button
        type="button"
        className="udv-install-banner-dismiss"
        aria-label={t('pwa.installDismiss')}
        title={t('pwa.installDismiss')}
        onClick={this.handleDismissPwaInstall}
      >
        ×
      </button>
    </div>
  )

  renderSwUpdateBell = (opts = {}) => {
    const floating = !!opts.floating
    if (!this.hasNotificationBell()) return null
    const unread = this.notificationBellUnread()
    const title =
      unread > 0
        ? `${unread} aviso(s)`
        : this.state.swUpdatePending
          ? 'Nueva versión / avisos'
          : 'Avisos Gateway'
    const btn = (
      <button
        type="button"
        className={floating ? 'udv-sw-bell-float' : undefined}
        aria-label={title}
        title={title}
        onClick={this.openNotificationCenter}
        style={
          floating
            ? undefined
            : {
                position: 'relative',
                width: 40,
                height: 40,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
              }
        }
      >
        <span className="udv-sw-bell-wrap" style={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
          <Icon path={mdiBell} size={floating ? 0.85 : 0.95} color="#FFD54F" />
          {unread > 0 ? <span className="udv-sw-bell-badge" /> : null}
        </span>
      </button>
    )
    return floating ? btn : <Tooltip title={title}>{btn}</Tooltip>
  }

  /** PWA: evita que «atrás» navegue fuera y desactive fullscreen sin querer. */
  setupPwaBackTrap = () => {
    if (!isPwaStandalone() || this._pwaBackTrapReady) return
    this._pwaBackTrapReady = true
    try {
      window.history.pushState({ udvPwaTrap: 1 }, '')
    } catch (e) {
      /* ignore */
    }
    this._pwaPopstateHandler = () => {
      try {
        window.history.pushState({ udvPwaTrap: 1 }, '')
      } catch (e) {
        /* ignore */
      }
      void this.handlePwaExit({ fromBack: true })
    }
    window.addEventListener('popstate', this._pwaPopstateHandler)
  }

  /** Salir de la PWA: pide confirmación (icono) o ejecuta directo (botón atrás). */
  handlePwaExit = (options = {}) => {
    const fromBack = !!(options && options.fromBack)
    if (!fromBack) {
      this.setState({ visiblePwaExitDialog: true })
      return
    }
    void this.executePwaExit()
  }

  executePwaExit = async () => {
    this._pwaSkipFullscreenRestore = true

    if (this._fsRestoreGestureHandler) {
      document.removeEventListener('touchstart', this._fsRestoreGestureHandler, true)
      document.removeEventListener('click', this._fsRestoreGestureHandler, true)
      this._fsRestoreGestureHandler = null
      this._fsRestoreGestureBound = false
    }

    await exitDocumentFullscreen()
    setPreferFullscreen(true)

    this.setState({
      isFullscreen: false,
      showFullscreenPrompt: false,
      visiblePwaExitDialog: false,
    }, () => this.scheduleFullscreenViewportResize())

    try {
      window.close()
    } catch (e) {
      /* ignore */
    }

    setTimeout(() => {
      this.setState({
        pwaExitHint: 'Para cerrar UDV usá el botón Inicio o el administrador de apps.',
      })
    }, 350)
  }

  cancelPwaExit = () => {
    this.setState({ visiblePwaExitDialog: false })
  }

  handleRequestFullscreen = async () => {
    const ok = await requestDocumentFullscreen()
    if (!ok) return
    setPreferFullscreen(true)
    this.setState({
      showFullscreenPrompt: false,
      isFullscreen: true,
    }, () => this.scheduleFullscreenViewportResize())
  }

  /** PWA: re-aplicar pantalla completa si el usuario ya la eligió antes. */
  restoreFullscreenIfPreferred = async () => {
    if (this._pwaSkipFullscreenRestore) return
    if (!getPreferFullscreen() || !isPwaStandalone()) return
    if (document.fullscreenElement || document.webkitFullscreenElement) return

    const ok = await requestDocumentFullscreen()
    if (ok) {
      this.setState({ showFullscreenPrompt: false, isFullscreen: true }, () =>
        this.scheduleFullscreenViewportResize()
      )
      return
    }

    // Sin gesto previo el browser puede bloquear requestFullscreen; un toque basta.
    if (this._fsRestoreGestureBound) return
    this._fsRestoreGestureBound = true
    const onGesture = () => {
      document.removeEventListener('touchstart', onGesture, true)
      document.removeEventListener('click', onGesture, true)
      this._fsRestoreGestureBound = false
      this._fsRestoreGestureHandler = null
      if (!getPreferFullscreen()) return
      void requestDocumentFullscreen().then((entered) => {
        if (entered) {
          this.setState({ showFullscreenPrompt: false, isFullscreen: true }, () =>
            this.scheduleFullscreenViewportResize()
          )
        }
      })
    }
    this._fsRestoreGestureHandler = onGesture
    document.addEventListener('touchstart', onGesture, true)
    document.addEventListener('click', onGesture, true)
  }

  handleViewportTap = (index, event) => {
    // Solo cerrar el explorer en móvil/tablet si el usuario lo toca explícitamente
    const isMobileDevice = isMobile || isTablet || window.innerWidth < 1024
    if (isMobileDevice && this.state.visibleExplorer && event.target.closest('.explorer-container')) {
        this.setState({ visibleExplorer: false }, () => {
          // Forzar reconfiguración de Hammer.js para todos los viewports activos después de ocultar explorer
        setTimeout(() => {
          this.dicomViewersRefs.forEach((viewer, viewerIndex) => {
            if (viewer && viewer.forceReconfigureHammerJS) {
              console.log(`App - Forzando reconfiguración de Hammer.js para viewport ${viewerIndex} después de ocultar explorer`)
              viewer.forceReconfigureHammerJS()
            }
          })
          
          // Forzar re-sincronización de viewports después de ocultar explorer
          console.log('App - Forzando re-sincronización de viewports después de ocultar explorer')
          this.forceViewportSync()
        }, 800) // Aumentar delay para que el DOM se actualice completamente incluyendo el padding
        
        // Reintento adicional después de más tiempo
        setTimeout(() => {
          console.log('App - Reintento de reconfiguración después de ocultar explorer')
          this.dicomViewersRefs.forEach((viewer, viewerIndex) => {
            if (viewer && viewer.forceReconfigureHammerJS) {
              console.log(`App - Reintento de reconfiguración de Hammer.js para viewport ${viewerIndex}`)
              viewer.forceReconfigureHammerJS()
            }
          })
        }, 1500) // Segundo reintento después de 1.5 segundos
        })
    }
    // Ejecutar la lógica normal de selección de viewport
    if (event.type === 'touchstart') {
        this.layoutGridTouch(index)
    } else {
        this.layoutGridClick(index)
    }
  }

  render() {
    const { classes } = this.props
    const { isMobile } = this.state
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

    const primaryClass = {primary:classes.listItemText}
    const iconSize = '1.2rem'
    const iconSizeSmall = '1.0rem'
    
    //const isMultipleView = this.props.layout[0] > 1 || this.props.layout[1] > 1
    // Nuevo: flag global para saber si hay algún viewport abierto
    const anyOpen = Array.isArray(this.props.isOpen) && this.props.isOpen.some(Boolean)
    this.isMultipleFiles = false
    if (this.dicomViewersRefs[this.props.activeDcmIndex] === undefined) {
      this.isMultipleFiles = false
    } else {
      const viewerFiles = this.dicomViewersRefs[this.props.activeDcmIndex].files
      this.isMultipleFiles = Array.isArray(viewerFiles) && viewerFiles.length > 1
    }
    //console.log('isMultipleFiles: ', this.isMultipleFiles)
    //const isMultipleView = this.activeDcmViewersNum > 1

    const openMenu = this.state.openMenu
    const openImageEdit = this.state.openImageEdit
    const openTools = this.state.openTools
    const visibleMainMenu = this.state.visibleMainMenu
    const visibleHeader = this.state.visibleHeader
    const visibleSettings = this.state.visibleSettings
    const visibleGsdfQa = this.state.visibleGsdfQa
    const gsdfQaPassed = !!this.state.gsdfQaPassed
    const visibleAbout = this.state.visibleAbout
    const visibleMeasure = this.state.visibleMeasure
    const visibleToolbox = this.state.visibleToolbox
    const visibleDicomdir = this.state.visibleDicomdir
    const visibleFileManager = this.state.visibleFileManager
    const visibleClearMeasureDlg = this.state.visibleClearMeasureDlg
    const visibleZippedFileDlg = this.state.visibleZippedFileDlg 
    const visibleZipExtractDlg = !!this.state.visibleZipExtractDlg
    const visibleDownloadZipDlg = this.state.visibleDownloadZipDlg
    const visibleOpenMultipleFilesDlg = this.state.visibleOpenMultipleFilesDlg
    const visibleLayout = Boolean(this.state.anchorElLayout)
    const visibleVolumeBuilding = this.state.visibleVolumeBuilding
    //const visibleMpr3D = this.state.visibleMpr3D
    const visibleMprOrthogonal = this.state.visibleMprOrthogonal
    const visibleMprCoronal = this.state.visibleMprCoronal
    const visibleMprSagittal = this.state.visibleMprSagittal
    const visibleMprAxial = this.state.visibleMprAxial
    const visibleExplorer = this.state.visibleExplorer
    const visibleReferenceLines = this.state.visibleReferenceLines
    const visibleSeriesLink = this.state.visibleSeriesLink
    const visibleToolsPanel = Boolean(this.state.anchorElToolsPanel)
    const mprMenu = this.state.mprMenu
    const mprViability = this.state.mprViability || { ok: false }
    const mprProfile = this.state.mprProfile || 'light'
    const plan = this.state.udvPlan || 'pro'
    const planAllowsMpr = canFeature(FEATURE_IDS.MPR, { plan })
    const planAllowsSlab = canFeature(FEATURE_IDS.MPR_SLAB, { plan })
    const planAllowsVol3d = canFeature(FEATURE_IDS.VOL3D, { plan })
    const planAllowsPet = canFeature(FEATURE_IDS.PET_FUSION, { plan })
    const planAllowsDsa = canFeature(FEATURE_IDS.DSA, { plan })
    const planAllowsVoice = canFeature(FEATURE_IDS.VOICE_NOTES, { plan })
    const planAllowsCopilot = canFeature(FEATURE_IDS.AI_COPILOT, { plan })
    const isPortalPlan = plan === UDV_PLANS.PATIENT
    const planDisplayLabel = getPlanDisplayLabel(plan)
    /** Preview↔Diagnóstico solo si el estudio trae webp/jpg (R2). WADO es DICOM. */
    const hasPreviewMedia = !!(
      this.state.manifestMediaCaps && this.state.manifestMediaCaps.hasPreview
    )
    const showRenderModeUi = !isPortalPlan && hasPreviewMedia
    const udvPersona =
      this.state.udvPersona ||
      resolvePersona({ plan }) ||
      (isPortalPlan ? UDV_PERSONAS.PORTAL : null)
    const showClinicalAuth = shouldShowClinicalAuth({
      persona: udvPersona,
      plan,
      developer: isDeveloperMode(),
    })
    const offerPwaInstall = shouldOfferPwaInstall({
      pwaInstallAllowed: this.isPwaInstallAllowed(),
    })
    const canInstallPwa =
      canInstallPwaInBrowser() && this.isPwaInstallAllowed()
    const pwaInstallLabel = getPwaInstallBannerLabel(t, { portal: isPortalPlan })
    const portalChrome = getPortalChromeForPlan(plan)
    /** Portal: ocultar chrome de features no habilitadas (no solo disabled). */
    const chromeOnMpr =
      isPortalPlan && !planAllowsMpr
        ? null
        : (mode) => this.beginMpr(mode)
    const chromeOnMpr3d =
      isPortalPlan && !planAllowsVol3d ? null : () => this.mpr3D()
    const mprAllowed = anyOpen && mprViability.ok && mprProfile !== 'blocked' && planAllowsMpr
    const mprOrthogonalAllowed = mprAllowed && mprProfileAllowsOrthogonal(mprProfile)
    const mprActive =
      this.state.visibleMprOrthogonal ||
      this.state.visibleMprSagittal ||
      this.state.visibleMprCoronal ||
      this.state.visibleMprAxial
    const mpr3dProfile = this.state.mpr3dProfile || 'off'
    const mpr3dAllowed =
      mprActive &&
      mprAllowed &&
      mpr3dProfileAllows(mpr3dProfile) &&
      planAllowsVol3d
    const visibleMpr3D = this.state.visibleMpr3D
    const mprSlabAllowed = mprAllowed && planAllowsSlab

    //let iconToolColor = this.state.toolState === 1 ? '#FFFFFF' : '#999999'

    const dcmViewer = this.getActiveDcmViewer()

    const sliceMax = this.state.sliceMax

    const useEdgeShell = this.useEdgeToolbarShell()
    const useStudioShell = this.useStudioToolbarShell()
    const useOhifShell = this.useOhifToolbarShell()
    const useOviyamShell = this.useOviyamToolbarShell()
    // Classic AppBar: no Edge/Studio/OHIF/Oviyam desktop
    const showTopAppBar =
      !useEdgeShell && !useStudioShell && !useOhifShell && !useOviyamShell
    const classicTier = this.getClassicToolbarTier()
    const showClassicExportInline = !isMobile && anyOpen && !classicTier.collapseExport
    /** En móvil Classic la tira de estudio sigue inline; overflow solo desktop. */
    const showClassicStudyInline = isMobile || !classicTier.collapseStudy
    const showClassicMore =
      showTopAppBar &&
      !isMobile &&
      ((classicTier.collapseExport && anyOpen) || classicTier.collapseStudy)
    const hasRealPacs = Boolean(this.state.pacsIdFromUrl)
    const developerMode = isDeveloperMode()
    // Lab/supremo: no ocultar el burger por ticket/manifest (`?t=` setea manifestUrlFromUrl).
    const showSystemMenu = developerMode && !isPortalPlan
    // Orthanc: developer (salvo portal patient); plan demo → DEMOPORTAL; resto con ?pacs=
    const showOrthancBtn =
      !isIOS &&
      plan !== UDV_PLANS.PATIENT &&
      (developerMode || hasRealPacs || plan === UDV_PLANS.DEMO)
    const orthancPacsId =
      this.state.pacsIdFromUrl ||
      (plan === UDV_PLANS.DEMO ? 'DEMOPORTAL' : null)
    const multiFrameCine = anyOpen && dcmViewer && dcmViewer.numberOfFrames > 1
    const multiSliceCine = anyOpen && this.state.sliceMax > 1
    const showCineBtn = !!(multiFrameCine || multiSliceCine)
    const cineOpen = !!(dcmViewer && dcmViewer.state && dcmViewer.state.visibleCinePlayer)
    const showPresets =
      anyOpen && dcmViewer && dcmViewer.isDicom && !isPortalPlan
    const showDsa =
      anyOpen &&
      planAllowsDsa &&
      this.shouldShowDsaMenu(dcmViewer, multiFrameCine)
    const dsaUi = this.state.dsaUi || { enabled: false, maskMode: null }
    const dsaFrameCount =
      (dcmViewer && dcmViewer.numberOfFrames > 1
        ? dcmViewer.numberOfFrames
        : this.state.sliceMax) || 0
    // Edge móvil: 100vh (el padre TranslationPreventer no tiene altura).
    // Studio usa su propio shell (100dvh); Classic resta AppBar.
    const viewportHeight = useEdgeShell ? '100vh' : 'calc(100vh - 48px)'
    const voiceDockOpen = !!this.state.visibleVoiceNotes
    const voiceBottomPad =
      useEdgeShell && voiceDockOpen
        ? Math.round(Math.min((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.38, 320))
        : 0
    // Sin paddingRight: el panel de voz es overlay (no desplaza el viewport)
    const viewportPaddingBottom =
      (useStudioShell ? 0 : this.getBottomChromeInset()) + voiceBottomPad
    const r2LoadIndicator = this.renderManifestLoadIndicator(isMobile)
    const studioTitles =
      useStudioShell || useOhifShell || useOviyamShell
        ? this.getStudioTitleParts(dcmViewer)
        : null
    const ohifDrawerCollapsed = !!this.state.ohifDrawerCollapsed
    const oviyamSidebarCollapsed = !!this.state.oviyamSidebarCollapsed
    const oviyamToolsCollapsed = !!this.state.oviyamToolsCollapsed
    const studioLeftCollapsed = !!this.state.studioLeftCollapsed
    const studioRightCollapsed = !!this.state.studioRightCollapsed

    //console.log('dcmViewer: ', dcmViewer)
    //console.log('this.mprPlane: ', this.mprPlane)
    //console.log('mprMenu: ', mprMenu)
    //console.log('mprMode: ', this.state.mprMode)

    if (this.state.accessDenied) {
      const deniedPersona =
        (this.state.accessDenied && this.state.accessDenied.persona) ||
        this.state.udvPersona
      const canLogin = shouldShowClinicalAuth({
        persona: deniedPersona,
        plan: this.state.udvPlan,
        developer: isDeveloperMode(),
      })
      return (
        <React.Fragment>
          <AccessDeniedSplash
            reason={this.state.accessDenied.reason}
            persona={deniedPersona}
            onLogin={canLogin ? this.showAuthGatewayModal : null}
            onOpenLocalStudies={this.showLocalStudies}
            onInstallPwa={
              !isPwaStandalone() ? this.handleInstallApp : null
            }
          />
          {canLogin ? (
            <AuthGatewayModal
              open={!!this.state.visibleAuthGatewayModal}
              onClose={this.hideAuthGatewayModal}
              onSessionChange={this.handleGatewaySessionChange}
              onControllerSessionChange={this.handleControllerSessionChange}
              onPushStatusChange={this.handleGatewayPushStatusChange}
              pushActive={!!this.state.gatewayPushActive}
              controllerCompat={this.state.controllerCompat}
              gatewayCompatOk={this.state.gatewayCompatOk !== false}
              udvVersion={packageJson.version}
            />
          ) : null}
          <InstallPwaHelpDlg
            open={!!this.state.visibleInstallHelp}
            onClose={this.hideInstallHelp}
          />
          <LocalStudiesDlg
            open={this.state.visibleLocalStudies}
            onClose={this.hideLocalStudies}
            onOpenStudy={this.openLocalStudyInViewer}
            onLoadStudy={this.loadLocalStudyMerge}
            canLoadStudy={this.canLoadLocalStudy}
            onReopen={this.showLocalStudies}
            canInstallPwa={!isPwaStandalone()}
            onInstallPwa={this.handleInstallApp}
            offlineCacheDisabled={!this.isOfflineCacheAllowed()}
          />
        </React.Fragment>
      )
    }
    
    return (
      <TranslationPreventer>
        <div>
        { showTopAppBar && (
        <AppBar className={classes.appBar} position='static' elevation={0}>
          <Toolbar variant="dense">
            {/* Menú principal (sistema) — solo developer; oculto en deep-link */}
            { showSystemMenu && (
              <IconButton edge="start" className={classes.menuButton} color="inherit" aria-label="menu" onClick={this.toggleMainMenu}>
                <MenuIcon />
              </IconButton>
            )}
            { this.appBarTitle(classes, anyOpen, dcmViewer, planDisplayLabel) }
            { r2LoadIndicator }
            
            <div className={classes.grow} />
            {this.renderSwUpdateBell()}

            {/* toolbarClassic: grupos por tema + overflow Más */}
            <div className="udv-classic-toolbar">
              {/* Vista */}
              <div className="udv-classic-toolbar-group" data-group="vista">
                <Tooltip title="Layout">
                  <IconButton onClick={this.handleLayout}>
                    <Icon path={mdiViewGridPlusOutline} size={iconSize} color={iconColor} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={this.state.isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                  <IconButton onClick={this.toggleFullscreen}>
                    <Icon path={this.state.isFullscreen ? mdiFullscreenExit : mdiFullscreen} size={iconSize} color={iconColor} />
                  </IconButton>
                </Tooltip>
                { !isMobile && showRenderModeUi ? this.renderClassicRenderModeToggle() : null }
              </div>

              <div className="udv-classic-toolbar-sep" aria-hidden="true" />

              {/* Imagen */}
              <div className="udv-classic-toolbar-group" data-group="imagen">
                <Tooltip title="WW/WC">
                  <IconButton onClick={() => this.toolExecute('Wwwc')}>
                    <Icon path={mdiWhiteBalanceSunny} size={iconSize} color={this.colorIcon('Wwwc')} />
                  </IconButton>
                </Tooltip>
                { showPresets && (
                  <WWPresetManager
                    currentModality={this.getCurrentModality()}
                    onPresetSelect={this.handleWWPresetSelect}
                  />
                )}
                <Tooltip title="No Tool">
                  <IconButton onClick={() => this.toolExecute('notool')}>
                    <Icon path={mdiCursorDefault} size={iconSize} color={this.colorIcon('notool')} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tools">
                  <IconButton onClick={this.handleToolsPanel}>
                    <Icon path={mdiTools} size={iconSize} color={iconColor} />
                  </IconButton>
                </Tooltip>
                { anyOpen && (
                  <Tooltip title="Reset Image">
                    <IconButton onClick={this.resetImage}>
                      <Icon path={mdiRefresh} size={iconSize} color={iconColor} />
                    </IconButton>
                  </Tooltip>
                )}
                { showCineBtn && (
                  <Tooltip title="Cine Player">
                    <IconButton onClick={this.cinePlayer} color={cineOpen ? 'secondary' : 'inherit'}>
                      <Icon path={mdiVideo} size={iconSize} color={cineOpen ? 'rgba(0, 255, 0, 1.0)' : iconColor} />
                    </IconButton>
                  </Tooltip>
                )}
                { showDsa && (
                  <DsaMenu
                    enabled={!!dsaUi.enabled}
                    maskMode={dsaUi.maskMode}
                    frameCount={dsaFrameCount}
                    pixelShift={dsaUi.pixelShift}
                    invertDSA={!!dsaUi.invertDSA}
                    gain={dsaUi.gain}
                    offset={dsaUi.offset}
                    noiseFloor={dsaUi.noiseFloor}
                    engineMode={dsaUi.engineMode || 'auto'}
                    synthMode={dsaUi.synthMode || 'minip'}
                    roadmap={dsaUi.roadmap}
                    onSelect={this.handleDsaMenuSelect}
                  />
                )}
              </div>

              {/* MPR / 3D / dosis — slab agrupado en menú */}
              {this.renderClassicMprToolbarGroup({
                iconSize,
                iconColor,
                activeColor,
                chromeOnMpr,
                mprAllowed,
                mprViability,
                mprProfile,
                mprActive,
                mpr3dAllowed,
                visibleMpr3D,
                anyOpen,
              })}

              {/* Estudio */}
              <div className="udv-classic-toolbar-sep" aria-hidden="true" />
              <div className="udv-classic-toolbar-group" data-group="estudio">
                <Tooltip title={this.state.gatewayConnected ? 'TJS Gateway (conectado)' : 'TJS Gateway'}>
                  <IconButton onClick={this.showAuthGatewayModal}>
                    <Icon
                      path={this.state.gatewayConnected ? mdiCloudCheck : mdiCloudLock}
                      size={iconSize}
                      color={this.state.gatewayConnected ? activeColor : iconColor}
                    />
                  </IconButton>
                </Tooltip>
                { showClassicStudyInline && showOrthancBtn && (
                  <Tooltip title="Connect to Orthanc">
                    <IconButton onClick={() => this.setState({visibleOrthancConnection: true})}>
                      <Icon path={mdiDatabase} size={iconSize} color={iconColor} />
                    </IconButton>
                  </Tooltip>
                )}
                { showClassicStudyInline && (
                  <Tooltip title="Estudios en storage local">
                    <IconButton onClick={this.showLocalStudies}>
                      <Icon path={mdiInboxArrowDown} size={iconSize} color={iconColor} />
                    </IconButton>
                  </Tooltip>
                )}
                { showClassicStudyInline && anyOpen && (
                  <Tooltip title="Explorer">
                    <IconButton onClick={this.toggleExplorer}>
                      <Icon path={mdiAnimationOutline} size={iconSize} color={iconColor} />
                    </IconButton>
                  </Tooltip>
                )}
                { showClassicStudyInline && anyOpen && this.showStudyZipDownloadButton() && (
                  <Tooltip title={this.getStudyZipDownloadTitle()}>
                    <span>
                      <IconButton
                        onClick={this.handleDownloadStudyZip}
                        disabled={!this.canClickStudyZipDownload()}
                      >
                        <Icon path={mdiDownload} size={iconSize} color={iconColor} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>

              {/* Export / QA — desktop ancho */}
              { showClassicExportInline && (
                <>
                  <div className="udv-classic-toolbar-sep" aria-hidden="true" />
                  <div className="udv-classic-toolbar-group" data-group="export">
                    <Tooltip title={t('menu.captureViewport')}>
                      <IconButton color="inherit" onClick={this.saveShot}>
                        <Icon path={mdiCamera} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('share.short')}>
                      <IconButton color="inherit" onClick={() => this.openShareSheet()}>
                        <Icon path={mdiShareVariant} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    {planAllowsVoice ? (
                      <VoiceNoteButton
                        holdToTalk={!!isMobile}
                        recording={!!this.state.voiceNoteRecording}
                        busy={!!this.state.voiceNoteBusy}
                        disabled={!anyOpen}
                        iconSize={iconSize}
                        color={iconColor}
                        onStart={this.startVoiceNote}
                        onStop={this.stopVoiceNote}
                        onToggle={this.toggleVoiceNote}
                      />
                    ) : null}
                    <Tooltip title="Measurements">
                      <IconButton color="inherit" onClick={this.toggleMeasure}>
                        <Icon path={mdiRuler} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar GSPS (.dcm)">
                      <IconButton color="inherit" onClick={this.exportActiveGsps}>
                        <Icon path={mdiImageOutline} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar SR (.dcm)">
                      <IconButton color="inherit" onClick={this.exportActiveSr}>
                        <Icon path={mdiChartHistogram} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Adjuntar PDF / informe (DOC)">
                      <IconButton
                        color="inherit"
                        onClick={this.openAttachPdfPicker}
                        disabled={!anyOpen}
                      >
                        <Icon path={mdiFileCad} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('menu.dicomHeader')}>
                      <IconButton color="inherit" onClick={this.toggleHeader}>
                        <Icon path={mdiFileDocument} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('menu.fileManagerTooltip')}>
                      <IconButton color="inherit" onClick={this.toggleFileManager}>
                        <Icon path={mdiFileCabinet} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                  </div>
                </>
              )}

              {/* Overflow Más (<1440 export; <1280 estudio) */}
              { showClassicMore && (
                <>
                  <div className="udv-classic-toolbar-sep" aria-hidden="true" />
                  <div className="udv-classic-toolbar-group" data-group="more">
                    <Tooltip title="Más">
                      <IconButton
                        color="inherit"
                        onClick={this.openClassicMoreMenu}
                        aria-label="Más herramientas"
                        aria-haspopup="true"
                        aria-controls={this.state.anchorElClassicMore ? 'classic-more-menu' : undefined}
                      >
                        <Icon path={mdiDotsHorizontal} size={iconSize} color={iconColor} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="classic-more-menu"
                      anchorEl={this.state.anchorElClassicMore}
                      open={Boolean(this.state.anchorElClassicMore)}
                      onClose={this.closeClassicMoreMenu}
                      getContentAnchorEl={null}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {classicTier.collapseStudy && showOrthancBtn ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.setState({ visibleOrthancConnection: true })
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiDatabase} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Orthanc" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseStudy ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.showLocalStudies()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiInboxArrowDown} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Estudios locales" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseStudy && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.toggleExplorer()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiAnimationOutline} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Explorer" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseStudy && anyOpen && this.showStudyZipDownloadButton() ? (
                        <MenuItem
                          disabled={!this.canClickStudyZipDownload()}
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.handleDownloadStudyZip()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiDownload} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary={this.getStudyZipDownloadTitle()} />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.saveShot()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiCamera} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary={t('menu.captureViewport')} />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.openShareSheet()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiShareVariant} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary={t('share.short')} />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen && planAllowsVoice ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.toggleVoiceNote()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiMicrophone} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Nota de voz" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.toggleMeasure()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiRuler} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Measurements" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.exportActiveGsps()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiImageOutline} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Exportar GSPS (.dcm)" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.exportActiveSr()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiChartHistogram} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Exportar SR (.dcm)" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.openAttachPdfPicker()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiFileCad} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary="Adjuntar PDF / informe" />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.toggleHeader()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiFileDocument} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary={t('menu.dicomHeader')} />
                        </MenuItem>
                      ) : null}
                      {classicTier.collapseExport && anyOpen ? (
                        <MenuItem
                          onClick={() => {
                            this.closeClassicMoreMenu()
                            this.toggleFileManager()
                          }}
                        >
                          <ListItemIcon>
                            <Icon path={mdiFileCabinet} size={0.85} />
                          </ListItemIcon>
                          <ListItemText primary={t('menu.fileManagerTooltip')} />
                        </MenuItem>
                      ) : null}
                    </Menu>
                  </div>
                </>
              )}
            </div>
          </Toolbar>
        </AppBar>
        )}

        {useEdgeShell && this.renderSwUpdateBell({ floating: true })}

        {offerPwaInstall
          ? this.renderPwaInstallBanner(pwaInstallLabel, {
              belowAppBar: showTopAppBar,
            })
          : null}

        {/* toolbarEdge compact: panel lateral (móvil) */}
        {useEdgeShell && (
          <ToolsEdgePanel
            open={this.state.edgeToolsOpen}
            docked={false}
            onToggle={this.toggleEdgeTools}
            onClose={this.closeEdgeTools}
            onCloseAfterTool={this.closeEdgeTools}
            toolActive={this.state.toolActive}
            referenceLines={visibleReferenceLines}
            hideReferenceLinesOnActive={!!this.state.hideReferenceLinesOnActiveViewport}
            seriesLink={visibleSeriesLink}
            seriesLinkWwwc={!!this.state.seriesLinkWwwc}
            toolExecute={this.toolExecute}
            anyOpen={anyOpen}
            showCine={!!showCineBtn}
            cineActive={!!cineOpen}
            showDsa={!!showDsa}
            dsaEnabled={!!dsaUi.enabled}
            dsaMaskMode={dsaUi.maskMode}
            dsaFrameCount={dsaFrameCount}
            dsaPixelShift={dsaUi.pixelShift}
            dsaInvert={!!dsaUi.invertDSA}
            dsaGain={dsaUi.gain}
            dsaOffset={dsaUi.offset}
            dsaNoiseFloor={dsaUi.noiseFloor}
            dsaEngineMode={dsaUi.engineMode || 'auto'}
            dsaSynthMode={dsaUi.synthMode || 'minip'}
            dsaRoadmap={dsaUi.roadmap}
            onDsaSelect={this.handleDsaMenuSelect}
            showOrthanc={!!showOrthancBtn}
            onOrthanc={() => this.setState({ visibleOrthancConnection: true })}
            showGateway={showClinicalAuth}
            gatewayConnected={!!this.state.gatewayConnected}
            gatewayStatus={this.state.gatewayStatus || 'local_only'}
            gatewayCompatOk={this.state.gatewayCompatOk !== false}
            onGateway={showClinicalAuth ? this.showAuthGatewayModal : null}
            onGatewayOpen={showClinicalAuth ? this.onGatewayOpen : null}
            showLocalStudies
            onLocalStudies={this.showLocalStudies}
            showRelatedStudies={
              Array.isArray(this.state.relatedStudies) &&
              this.state.relatedStudies.length > 0
            }
            onRelatedStudies={this.showRelatedStudies}
            showWorklist={this.hasWorklistUi()}
            onWorklist={this.showWorklist}
            compareMode={!!this.state.compareMode}
            compareModeEnabled={this.canEnableCompareMode()}
            onToggleCompareMode={this.toggleCompareMode}
            isFullscreen={this.state.isFullscreen}
            showPresets={!!showPresets}
            currentModality={this.getCurrentModality()}
            onPresetSelect={this.handleWWPresetSelect}
            onLayout={this.handleLayout}
            onLayoutOpen={this.handleEdgeLayoutOpen}
            onLayoutToggle={this.handleEdgeLayoutToggle}
            layoutPinned={!!this.state.edgeLayoutPinned}
            onFullscreen={this.toggleFullscreen}
            onPwaExit={this.handlePwaExit}
            pwaStandalone={isPwaStandalone()}
            onInstallPwa={canInstallPwa ? this.handleInstallApp : null}
            swUpdatePending={!!this.state.swUpdatePending || (this.state.gatewayNotices || []).length > 0}
            onOpenSwUpdate={this.openNotificationCenter}
            onReset={this.resetImage}
            onExplorer={this.toggleExplorer}
            onCine={this.cinePlayer}
            loadProgress={this.state.manifestDownloadProgress}
            loadSource={this.state.studyLoadSource}
            onCancelStudyLoad={() => this.cancelStudyLoad({ userInitiated: true })}
            onClearStudyCache={this.handleClearStudyDicomCache}
            onClearAllCache={this.handleClearAllDicomCache}
            renderMode={this.state.renderMode}
            onToggleRenderMode={showRenderModeUi ? this.toggleRenderMode : null}
            onSetRenderMode={showRenderModeUi ? this.setRenderMode : null}
            onDiagnosticToolBlocked={this.showDiagnosticModeHint}
            studyDownloadUrl={this.state.studyDownloadUrl}
            localStudyZipEligible={
              !!(this.state.localStudyZip && this.state.localStudyZip.eligible)
            }
            localStudyZipReady={
              !!(this.state.localStudyZip && this.state.localStudyZip.ready)
            }
            studyZipDownloadTitle={this.getStudyZipDownloadTitle()}
            studyZipDownloadBusy={!!this.state.studyZipDownloadBusy}
            onDownloadStudy={this.handleDownloadStudyZip}
            mprAllowed={mprAllowed}
            mprOrthogonalAllowed={mprOrthogonalAllowed}
            mprActive={mprActive}
            mprMessage={
              (mprViability && mprViability.message) ||
              (mprProfile === 'blocked' ? mprProfileLabel('blocked') : '')
            }
            visibleMprOrthogonal={visibleMprOrthogonal}
            visibleMprCoronal={visibleMprCoronal}
            visibleMprSagittal={visibleMprSagittal}
            visibleMprAxial={visibleMprAxial}
            onMpr={chromeOnMpr}
            onMprExit={this.exitMpr}
            mprCrosshairActive={!!this.state.mprCrosshairActive}
            onMprCrosshairToggle={this.toggleMprCrosshair}
            mprSlabMode={this.state.mprSlabMode || 'thin'}
            mprSlabMm={Number(this.state.mprSlabMm) || 0}
            onMprSlabMode={mprSlabAllowed ? this.setMprSlabMode : null}
            onMprSlabMm={mprSlabAllowed ? this.setMprSlabMm : null}
            petFusionOn={!!this.state.petFusionOn}
            petFusionOpacity={Math.round((this.state.petFusionOpacity || 0) * 100)}
            petFusionClut={this.state.petFusionClut || 'hotMetal'}
            petFusionWindowMax={Number(this.state.petFusionWindowMax) || 5}
            petFusionAvailable={
              planAllowsPet && listPetSeriesFromStore(this.props.series).length > 0
            }
            onPetFusionToggle={this.togglePetFusion}
            onPetFusionOpacity={this.setPetFusionOpacity}
            onPetFusionClut={this.setPetFusionClut}
            onPetFusionWindowMax={this.setPetFusionWindowMax}
            gspsAvailable={listPrSeriesFromStore(this.props.series).length > 0}
            gspsOn={!!this.state.gspsOn}
            onGspsToggle={this.toggleGspsOverlay}
            onGspsExport={this.exportActiveGsps}
            onSrExport={this.exportActiveSr}
            onAttachPdf={this.openAttachPdfPicker}
            mpr3dAllowed={mpr3dAllowed}
            visibleMpr3D={!!visibleMpr3D}
            mpr3dDecapado={!!this.state.mpr3dDecapado}
            mpr3dMessage={mpr3dProfileLabel(mpr3dProfile)}
            onMpr3d={chromeOnMpr3d}
            onMpr3dDecapado={this.toggleMpr3dDecapado}
            scoutAvailable={!!findScoutSeriesInStore(this.props.series)}
            scoutSyncEnabled={!!this.state.scoutSyncEnabled}
            scoutPanelOpen={!!this.state.scoutPanelOpen}
            onScoutSyncToggle={this.toggleScoutSync}
            onScoutPanelToggle={this.toggleScoutPanel}
            doseReport={this.state.doseReport}
            onDoseInfo={this.openDoseInfo}
            headerOpen={!!this.state.visibleHeader}
            onHeader={this.toggleHeader}
            histogramOpen={!!visibleToolbox}
            onHistogram={this.toggleToolbox}
            onGsdfQa={this.showGsdfQa}
            gsdfQaPassed={gsdfQaPassed}
            onCaptureViewport={this.saveShot}
            onShareViewport={this.openShareSheet}
            onCopilotOpen={planAllowsCopilot ? this.openCopilotPanel : null}
            voiceNoteRecording={!!this.state.voiceNoteRecording}
            voiceNoteBusy={!!this.state.voiceNoteBusy}
            voiceNoteHoldToTalk={false}
            onVoiceNoteStart={planAllowsVoice ? this.startVoiceNote : null}
            onVoiceNoteStop={planAllowsVoice ? this.stopVoiceNote : null}
            onVoiceNoteToggle={planAllowsVoice ? this.toggleVoiceNote : null}
            onVoiceNoteOpenPanel={planAllowsVoice ? this.openVoiceNotesPanel : null}
            portalChrome={portalChrome}
            showPortalDisclaimerRail={isPortalPlan}
          />
        )}

        {/* Badge readiness R2 (preview / upgrade / grow) — oculto en portal paciente */}
        { showRenderModeUi &&
          anyOpen &&
          (this.state.manifestReadiness === 'preview' ||
            this.state.manifestUpgradeFlash ||
            this.state.manifestGrowFlash) ? (
          <div
            className={
              this.state.manifestUpgradeFlash
                ? 'udv-readiness-badge is-diagnostic'
                : this.state.manifestGrowFlash
                  ? 'udv-readiness-badge is-growing'
                  : 'udv-readiness-badge is-preview'
            }
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              top: useStudioShell ? 52 : useEdgeShell ? 40 : 56,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 150160,
              pointerEvents: 'none',
              maxWidth: '92vw',
              textAlign: 'center',
            }}
          >
            {this.state.manifestUpgradeFlash
              ? t('renderMode.badgeDiagnosticReady')
              : this.state.manifestGrowFlash
                ? t('renderMode.badgeGrowing')
                : t('renderMode.badgePreparing')}
          </div>
        ) : null}

        {/* Banner modo render — CTA tappable (solo planes clínicos) */}
        { showRenderModeUi &&
          anyOpen &&
          this.state.renderMode === RENDER_MODE.PREVIEW &&
          this.state.manifestReadiness !== 'preview' &&
          !this.state.manifestUpgradeFlash ? (
          <div
            role="button"
            tabIndex={0}
            onClick={this.toggleRenderMode}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                this.toggleRenderMode()
              }
            }}
            style={{
              position: 'fixed',
              top: useStudioShell ? 52 : useEdgeShell ? 40 : 56,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 150150,
              background: 'rgba(30, 30, 30, 0.9)',
              color: '#ffb74d',
              fontSize: 12,
              padding: '6px 14px',
              borderRadius: 4,
              pointerEvents: 'auto',
              maxWidth: '92vw',
              textAlign: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
            }}
          >
            {t('renderMode.bannerToDiagnostic')}
          </div>
        ) : null}
        { showRenderModeUi &&
          anyOpen &&
          this.state.renderMode === RENDER_MODE.DIAGNOSTIC &&
          this.state.manifestMediaCaps &&
          this.state.manifestMediaCaps.hasPreview &&
          (useEdgeShell || isConstrainedUiDevice()) ? (
          <div
            role="button"
            tabIndex={0}
            onClick={this.toggleRenderMode}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                this.toggleRenderMode()
              }
            }}
            style={{
              position: 'fixed',
              top: useStudioShell ? 52 : useEdgeShell ? 40 : 56,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 150150,
              background: 'rgba(30, 30, 30, 0.82)',
              color: '#90caf9',
              fontSize: 11,
              padding: '4px 12px',
              borderRadius: 4,
              pointerEvents: 'auto',
              maxWidth: '92vw',
              textAlign: 'center',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
            }}
          >
            {t('renderMode.bannerToPreview')}
          </div>
        ) : null}

        {/* toolbarEdge: chip mínimo solo mientras la descarga está en curso */}
        { useEdgeShell && r2LoadIndicator && isStudyLoadActive(this.state.studyLoadSource) ? (
          <div
            style={{
              position: 'fixed',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 150200,
              pointerEvents: 'auto',
              background: 'rgba(32, 32, 32, 0.82)',
              borderRadius: 4,
              padding: '2px 8px',
            }}
          >
            {r2LoadIndicator}
          </div>
        ) : null}

        {/* Drawer del menú lateral (sistema) — solo developer.
            Studio/OHIF/Oviyam: temporary (backdrop + Escape cierran).
            Classic: persistent. */}
        { showSystemMenu && (
          <Drawer 
            variant={
              useStudioShell || useOhifShell || useOviyamShell
                ? 'temporary'
                : 'persistent'
            }
            open={visibleMainMenu && !isMobile} 
            className={
              useStudioShell || useOhifShell || useOviyamShell
                ? 'udv-studio-side-drawer'
                : undefined
            }
            PaperProps={{
              className:
                useStudioShell || useOhifShell || useOviyamShell
                  ? 'udv-studio-side-drawer-paper'
                  : undefined,
              style:
                useStudioShell || useOhifShell || useOviyamShell
                  ? { zIndex: 220 }
                  : undefined,
            }}
            ModalProps={
              useStudioShell || useOhifShell || useOviyamShell
                ? { keepMounted: true, style: { zIndex: 210 } }
                : undefined
            }
            style={
              useStudioShell || useOhifShell || useOviyamShell
                ? { zIndex: 210 }
                : // Por encima del grid/canvas (zIndex 1); AppBar sigue en drawer+1 (~1201)
                  { zIndex: 1200 }
            }
            onClose={this.hideMainMenu}
          >
            <div className={classes.toolbar}>
            <PerfectScrollbar>   
              <List dense={true}>
                <ListItem
                  button
                  onClick={() => {
                    this.showAppBar()
                    if (useStudioShell || useOhifShell || useOviyamShell) {
                      this.hideMainMenu()
                    }
                  }}
                >
                  <ListItemIcon><MenuIcon /></ListItemIcon>
                  <ListItemText primary={t('menu.toolbar')} />
                </ListItem>    
                <ListItem button onClick={() => this.toggleFileManager()}>
                  <ListItemIcon><Icon path={mdiFileCabinet} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.fileManager')} />
                </ListItem>      
                
                <ListItem button onClick={() => this.toggleOpenMenu()}>
                  <ListItemIcon><Icon path={mdiFolderMultiple} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.open')} />
                  {openMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItem>                    
                <Collapse in={openMenu} timeout="auto" unmountOnExit>
                  <List dense={true} component="div">
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.showFileOpen()}>
                      <ListItemIcon><Icon path={mdiFolder} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-20px'}}>{t('menu.file')}</Typography>
                        } />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.showZipOpen()}>
                      <ListItemIcon><Icon path={mdiFolderZipOutline} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-20px'}}>{t('menu.zip')}</Typography>
                        } />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.showOpenUrl()}>
                      <ListItemIcon><Icon path={mdiWeb} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-20px'}}>{t('menu.url')}</Typography>
                        } />
                    </ListItem>
                    { isInputDirSupported() && !isMobile?
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.showOpenFolder()}>
                      <ListItemIcon><Icon path={mdiFolderOpen} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-20px'}}>{t('menu.folder')}</Typography>
                        } />
                    </ListItem>    
                    : null }                     
                    { isInputDirSupported() && !isMobile?
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.showOpenDicomdir()}>
                      <ListItemIcon><Icon path={mdiFolderOpen} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-20px'}}>{t('menu.dicomdir')}</Typography>
                        } />
                    </ListItem>    
                    : null }                                          
                  </List>
                </Collapse>  

                <ListItem button onClick={() => this.clear()}>
                  <ListItemIcon><Icon path={mdiDelete} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.clearAll')} />
                </ListItem>  
                <ListItem button onClick={this.handleLayout}>
                  <ListItemIcon><Icon path={mdiViewGridPlusOutline} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.layout')} />              
                </ListItem>   
                <ListItem button onClick={() => this.showSettings()}>
                  <ListItemIcon><Icon path={mdiCog} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.settings')} />
                </ListItem>
                <ListItem button onClick={this.showGsdfQa}>
                  <ListItemIcon>
                    <Icon
                      path={mdiMonitor}
                      size={iconSize}
                      color={gsdfQaPassed ? '#66bb6a' : '#ffca28'}
                    />
                  </ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.gsdfQa')} />
                </ListItem>
                <ListItem button onClick={this.showAuthGatewayModal}>
                  <ListItemIcon>
                    <Icon
                      path={this.state.gatewayConnected ? mdiCloudCheck : mdiCloudLock}
                      size={iconSize}
                      color={this.state.gatewayConnected ? activeColor : iconColor}
                    />
                  </ListItemIcon>
                  <ListItemText
                    classes={primaryClass}
                    primary={this.state.gatewayConnected ? t('menu.gatewayConnected') : t('menu.gateway')}
                  />
                </ListItem>
                <ListItem button onClick={this.showLocalStudies}>
                  <ListItemIcon>
                    <Icon path={mdiInboxArrowDown} size={iconSize} color={iconColor} />
                  </ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.localStudies')} />
                </ListItem>
                {Array.isArray(this.state.relatedStudies) &&
                this.state.relatedStudies.length > 0 ? (
                  <ListItem button onClick={this.showRelatedStudies}>
                    <ListItemIcon>
                      <Icon path={mdiHistory} size={iconSize} color={iconColor} />
                    </ListItemIcon>
                    <ListItemText
                      classes={primaryClass}
                      primary={t('menu.relatedStudies')}
                    />
                  </ListItem>
                ) : null}
                {this.hasWorklistUi() ? (
                  <ListItem button onClick={this.showWorklist}>
                    <ListItemIcon>
                      <Icon path={mdiClipboardListOutline} size={iconSize} color={iconColor} />
                    </ListItemIcon>
                    <ListItemText
                      classes={primaryClass}
                      primary={t('menu.worklist')}
                    />
                  </ListItem>
                ) : null}
                <Divider />
                <ListItem button onClick={() => this.toggleToolbox()} disabled={!anyOpen}>
                  <ListItemIcon><Icon path={mdiChartHistogram} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.histogram')} />
                </ListItem>  
                {chromeOnMpr ? (
                <ListItem
                  button
                  onClick={() => {
                    if (!mprAllowed) {
                      this.refreshMprViability()
                      const v = this.state.mprViability
                      this.setState({
                        titleMessage: 'MPR',
                        textMessage:
                          (v && v.message) ||
                          (mprProfile === 'blocked'
                            ? mprProfileLabel('blocked')
                            : t('menu.mprUnavailableSeries')),
                        visibleMessage: true,
                      })
                      return
                    }
                    this.toggleMpr()
                  }}
                  disabled={!anyOpen}
                >
                  <ListItemIcon><Icon path={mdiAxisArrow} size={iconSize} color={mprAllowed ? iconColor : '#666'} /></ListItemIcon>
                  <ListItemText
                    classes={primaryClass}
                    primary={mprAllowed ? t('menu.mpr') : t('menu.mprUnavailable')}
                  />
                  {mprMenu ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                ) : null}

                <Collapse in={mprMenu && mprAllowed} timeout="auto" unmountOnExit>
                  <List dense={true} component="div">
                    {mprActive ? (
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.exitMpr()}>
                      <ListItemText 
                        style={{marginLeft: '40px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprExit')}</Typography>
                        } />
                    </ListItem>
                    ) : null}
                    {mprOrthogonalAllowed ? (
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.mprOrthogonal()}>
                      {visibleMprOrthogonal ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: visibleMprOrthogonal ? 0 : 16}}><Icon path={mdiViewColumn} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={visibleMprOrthogonal ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprOrthogonal')}</Typography>
                        } />
                    </ListItem>
                    ) : null}
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.mprCoronal()}>
                      {visibleMprCoronal ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: visibleMprCoronal ? 0 : 16}}><Icon path={mdiAlphaCBox} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={visibleMprCoronal ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprCoronal')}</Typography>
                        } />
                    </ListItem>    
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.mprSagittal()}>
                      {visibleMprSagittal ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: visibleMprSagittal ? 0 : 16}}><Icon path={mdiAlphaSBox} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={visibleMprSagittal ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprSagittal')}</Typography>
                        } />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.mprAxial()}>
                      {visibleMprAxial ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: visibleMprAxial ? 0 : 16}}><Icon path={mdiAlphaABox} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={visibleMprAxial ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprAxial')}</Typography>
                        } />
                    </ListItem>
                    {mprActive ? (
                    <ListItem button style={{paddingLeft: 40}} onClick={() => this.toggleMprCrosshair()}>
                      {this.state.mprCrosshairActive ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: this.state.mprCrosshairActive ? 0 : 16}}><Icon path={mdiCrosshairsGps} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={this.state.mprCrosshairActive ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprCrosshair')}</Typography>
                        } />
                    </ListItem>
                    ) : null}
                    {mprActive ? (
                    <ListItem style={{paddingLeft: 32, display: 'block', height: 'auto'}}>
                      {this.renderMprSlabToolbarChips()}
                    </ListItem>
                    ) : null}
                    {mprActive ? (
                    <ListItem
                      button
                      style={{paddingLeft: 40}}
                      onClick={() => this.mpr3D()}
                      disabled={!mpr3dAllowed}
                    >
                      {visibleMpr3D ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: visibleMpr3D ? 0 : 16}}><Icon path={mdiCubeOutline} size={'1.0rem'} color={mpr3dAllowed ? iconColor : '#666'} /></ListItemIcon>
                      <ListItemText 
                        style={visibleMpr3D ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>
                            {visibleMpr3D ? t('menu.mprBack') : (mpr3dAllowed ? t('menu.mpr3d') : t('menu.mpr3dUnavailable'))}
                          </Typography>
                        } />
                    </ListItem>
                    ) : null}
                    {visibleMpr3D ? (
                    <ListItem button style={{paddingLeft: 40}} onClick={this.toggleMpr3dDecapado}>
                      {this.state.mpr3dDecapado ? <ListItemIcon style={{marginLeft: '-10px'}}><Icon path={mdiCheck} size={'1.0rem'} color={iconColor} /></ListItemIcon> : null}
                      <ListItemIcon style={{minWidth: 28, marginLeft: this.state.mpr3dDecapado ? 0 : 16}}><Icon path={mdiContentCut} size={'1.0rem'} color={iconColor} /></ListItemIcon>
                      <ListItemText 
                        style={this.state.mpr3dDecapado ? {marginLeft: '-7px'} : {marginLeft: '8px'}}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.mprDecapado')}</Typography>
                        } />
                    </ListItem>
                    ) : null}
                  </List>
                </Collapse>  

                <ListItem button onClick={() => this.toggleImageEdit()} disabled={!anyOpen}>
                  <ListItemIcon><Icon path={mdiImageEdit} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.edit')} />
                  {openImageEdit ? <ExpandLess /> : <ExpandMore />}
                </ListItem>          
                <Collapse in={openImageEdit} timeout="auto" unmountOnExit>
                  <List dense={true} component="div">
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Invert')}>
                      <ListItemIcon><Icon path={mdiInvertColors} size={iconSize} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} primary={t('menu.invert')} />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 30}} onClick={this.saveShot}>
                      <ListItemIcon><Icon path={mdiCamera} size={iconSize} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} primary={t('menu.captureViewport')} />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.openShareSheet()}>
                      <ListItemIcon><Icon path={mdiShareVariant} size={iconSize} color={iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass} primary={t('share.short')} />
                    </ListItem>
                    <ListItem
                      button
                      style={{ paddingLeft: 30 }}
                      disabled={!anyOpen || !!this.state.voiceNoteBusy}
                      onClick={() => {
                        if (this.state.voiceNoteRecording) {
                          this.openVoiceNotesPanel()
                        } else if ((this.state.voiceNotes || []).length) {
                          this.openVoiceNotesPanel()
                        } else {
                          void this.startVoiceNote()
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Icon
                          path={
                            this.state.voiceNoteRecording
                              ? mdiMicrophoneOff
                              : mdiMicrophone
                          }
                          size={iconSize}
                          color={
                            this.state.voiceNoteRecording ? '#e53935' : iconColor
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        classes={primaryClass}
                        primary={
                          this.state.voiceNoteRecording
                            ? 'Detener nota de voz'
                            : 'Nota de voz'
                        }
                      />
                    </ListItem>
                  </List>
                </Collapse>   
                <ListItem button onClick={() => this.toggleTools()} disabled={!anyOpen}>
                  <ListItemIcon><Icon path={mdiTools} size={iconSize} color={iconColor} /></ListItemIcon>
                  <ListItemText classes={primaryClass} primary={t('menu.tools')} />
                  {openTools ? <ExpandLess /> : <ExpandMore />}
                </ListItem>   
                <Collapse in={openTools} timeout="auto" unmountOnExit>
                  <List dense={true} component="div">
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('notool')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiCursorDefault} size={iconSizeSmall} color={this.colorIcon('notool')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolNoTool')}</Typography>
                        }
                      />
                    </ListItem>     
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('referencelines')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiArrowSplitHorizontal} size={iconSizeSmall} color={visibleReferenceLines ? activeColor : iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolReferenceLines')}</Typography>
                        }
                      />
                    </ListItem>
                    {this.state.visibleReferenceLines ? (
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('referencelinesactivehide')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiEyeOff} size={iconSizeSmall} color={this.state.hideReferenceLinesOnActiveViewport ? activeColor : iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolHideRefLinesOnActive')}</Typography>
                        }
                      />
                    </ListItem>
                    ) : null}
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('serieslink')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiVectorLink} size={iconSizeSmall} color={visibleSeriesLink ? activeColor : iconColor} /></ListItemIcon>
                      <ListItemText classes={primaryClass}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolLinkSeries')}</Typography>
                        }
                      />
                    </ListItem>                               
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Wwwc')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiWhiteBalanceSunny} size={iconSize} color={this.colorIcon('Wwwc')} /></ListItemIcon>
                      <ListItemText classes={primaryClass}
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolWwwc')}</Typography>
                        }
                      />
                    </ListItem>  
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Pan')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiCursorPointer} size={iconSize} color={this.colorIcon('Pan')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolPan')}</Typography>
                        } 
                      />
                    </ListItem>  
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Zoom')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiMagnify} size={iconSize} color={this.colorIcon('Zoom')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolZoom')}</Typography>
                        } 
                      />
                    </ListItem>      
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Magnify')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiCheckboxIntermediate} size={iconSize} color={this.colorIcon('Magnify')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolMagnify')}</Typography>
                        } 
                      />
                    </ListItem>       
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Length')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiRuler} size={iconSize} color={this.colorIcon('Length')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolLength')}</Typography>
                        }  
                      />
                    </ListItem>        
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Probe')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiEyedropper} size={iconSize} color={this.colorIcon('Probe')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolProbe')}</Typography>
                        } 
                      />
                    </ListItem> 
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('Angle')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiAngleAcute} size={iconSize} color={this.colorIcon('Angle')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolAngle')}</Typography>
                        }  
                      />
                    </ListItem>
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('CobbAngle')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiAngleRight} size={iconSize} color={this.colorIcon('CobbAngle')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolCobbAngle')}</Typography>
                        }  
                      />
                    </ListItem>  
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('EllipticalRoi')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiEllipse} size={iconSize} color={this.colorIcon('EllipticalRoi')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolEllipticalRoi')}</Typography>
                        } 
                      />
                    </ListItem>     
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('RectangleRoi')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiRectangle} size={iconSize} color={this.colorIcon('RectangleRoi')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolRectangleRoi')}</Typography>
                        } 
                      />
                    </ListItem> 
                    <ListItem button style={{paddingLeft: 30}} onClick={() => this.toolExecute('FreehandRoi')} disabled={!anyOpen}>
                      <ListItemIcon><Icon path={mdiGesture} size={iconSize} color={this.colorIcon('FreehandRoi')} /></ListItemIcon>
                      <ListItemText classes={primaryClass} 
                        primary={
                          <Typography type="body1" style={{fontSize: '0.80em', marginLeft: '-23px'}}>{t('menu.toolFreehandRoi')}</Typography>
                        } 
                      />
                    </ListItem> 
                  </List>
                </Collapse> 

              </List>
              
              { this.isMultipleFiles || mprMenu ?
                <div>     
                  <Divider />     
                  <div align='center'>
                    <IconButton onClick={this.listOpenFilesFirstFrame} size='small'>
                      <Icon path={mdiSkipBackward} size={'1.0rem'} color={iconColor} />
                    </IconButton>
                    <IconButton onClick={this.listOpenFilesPreviousFrame} size='small'>
                        <Icon path={mdiSkipPrevious} size={'1.0rem'} color={iconColor} />
                    </IconButton>
                    <IconButton onClick={this.listOpenFilesScrolling} size='small'>
                      <Icon path={this.state.listOpenFilesScrolling ? mdiPause : mdiPlay} size={'1.0rem'} color={iconColor} />
                    </IconButton>
                    <IconButton onClick={this.listOpenFilesNextFrame} size='small'>
                        <Icon path={mdiSkipNext} size={'1.0rem'} color={iconColor} />
                    </IconButton>
                    <IconButton onClick={this.listOpenFilesLastFrame} size='small'>
                        <Icon path={mdiSkipForward} size={'1.0rem'} color={iconColor} />
                    </IconButton>                
                  </div>  
                  <div style={{textAlign: 'center'}}>
                    <Typography type="body1" style={{fontSize: '0.80em'}}>{`${this.state.sliceIndex+1} / ${sliceMax}`}</Typography>
                  </div>  
                  <div style={{width: '130px', margin: 'auto'}}>
                    <Slider
                      style={{marginTop: '-4px'}}
                      value={this.state.sliceIndex}
                      onChange={this.handleSliceChange}
                      color="secondary"
                      min={0}
                      max={sliceMax-1}
                      step={100/sliceMax}
                    />
                  </div>
                </div> : null
              }

            </PerfectScrollbar>   
            </div>
          </Drawer>
        )}

        <Drawer
          variant="temporary"
          anchor="right"
          open={visibleHeader}
          onClose={this.toggleHeader}
          className={
            useStudioShell
              ? 'udv-studio-header-drawer'
              : useEdgeShell
                ? 'udv-edge-header-drawer'
                : undefined
          }
          PaperProps={{
            className: useStudioShell
              ? 'udv-header-paper udv-header-paper--studio'
              : useEdgeShell
                ? 'udv-header-paper udv-header-paper--edge'
                : 'udv-header-paper udv-header-paper--classic',
            style: {
              width: 'min(360px, 92vw)',
              maxWidth: '92vw',
              height: useStudioShell || useEdgeShell || !showTopAppBar
                ? '100%'
                : 'calc(100% - 48px)',
              boxSizing: 'border-box',
              background: '#1a1d21',
              color: '#fff',
              ...(useStudioShell || useEdgeShell
                ? {}
                : { marginTop: showTopAppBar ? 48 : 0 }),
            },
          }}
          ModalProps={{
            keepMounted: false,
            style: {
              zIndex: useStudioShell ? 225 : useEdgeShell ? 160190 : 1300,
            },
          }}
        >
          {visibleHeader ? (
            <DicomHeader
              dcmViewer={dcmViewer}
              color={iconColor}
              onClose={this.toggleHeader}
              skin={
                useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
              }
            />
          ) : null}
        </Drawer>

        <Drawer
          variant="persistent"
          anchor='right'
          open={visibleMeasure}
          onClose={this.toggleMeasure}
        >
          <div style={{marginTop: '48px'}}>
            <Toolbar variant="dense">
              <Typography variant="subtitle1" className={classes.title}>
                Measurements&nbsp;&nbsp;
              </Typography>
              <div className={classes.grow} />
              <Tooltip title="Guardar mediciones (IndexedDB)">
              <IconButton color="inherit" onClick={this.saveMeasure} edge="end">
                <Icon path={mdiContentSaveOutline} size={iconSize} color={iconColor} />
              </IconButton>
              </Tooltip>
              <Tooltip title="Exportar GSPS (.dcm)">
              <IconButton color="inherit" onClick={this.exportActiveGsps} edge="end">
                <Icon path={mdiFileCad} size={iconSize} color={iconColor} />
              </IconButton>
              </Tooltip>
              <Tooltip title="Exportar SR (.dcm)">
              <IconButton color="inherit" onClick={this.exportActiveSr} edge="end">
                <Icon path={mdiFileDocument} size={iconSize} color={iconColor} />
              </IconButton>
              </Tooltip>
              <IconButton color="inherit" onClick={this.clearMeasure} edge="end">
                <Icon path={mdiTrashCanOutline} size={iconSize} color={iconColor} />
              </IconButton>
            </Toolbar>
            <div>  
              { anyOpen ? <Measurements dcmViewer={dcmViewer} toolRemove={this.toolRemove} onCalibrate={this.toolCalibrate} classes={classes} /> : null } 
            </div>
          </div>
        </Drawer>  

        <Drawer
          variant={useStudioShell || useEdgeShell ? 'temporary' : 'persistent'}
          anchor="right"
          open={visibleToolbox}
          onClose={this.toggleToolbox}
          className={
            useStudioShell
              ? 'udv-studio-histogram-drawer'
              : useEdgeShell
              ? 'udv-edge-histogram-drawer'
              : undefined
          }
          PaperProps={{
            className: useStudioShell
              ? 'udv-histogram-paper udv-histogram-paper--studio'
              : useEdgeShell
              ? 'udv-histogram-paper udv-histogram-paper--edge'
              : 'udv-histogram-paper udv-histogram-paper--classic',
            style: {
              width: 300,
              maxWidth: '92vw',
              boxSizing: 'border-box',
              ...(useStudioShell || useEdgeShell
                ? {}
                : { marginTop: showTopAppBar ? 48 : 0 }),
            },
          }}
          ModalProps={
            useStudioShell || useEdgeShell
              ? { keepMounted: false, style: { zIndex: useStudioShell ? 225 : 160190 } }
              : undefined
          }
        >
          <div style={{ paddingTop: useStudioShell || useEdgeShell ? 0 : 8 }}>
            {anyOpen ? (
              <Histogram
                key={dcmViewer && dcmViewer.filename}
                onClose={this.toggleToolbox}
                skin={
                  useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
                }
              />
            ) : null}
          </div>
        </Drawer>          

        <Drawer
          variant="persistent"
          anchor={getSettingsDicomdirView()}
          open={visibleDicomdir}
          onClose={this.toggleDicomdir}
        >
          <div>
            <div>  
              {visibleDicomdir ? <Dicomdir onOpenFile={this.handleOpenFileDicomdir} onOpenFs={this.handleOpenSandboxFs} /> : null} 
            </div>
          </div>
        </Drawer>   

        <Drawer
          variant={
            useStudioShell || useOhifShell || useOviyamShell
              ? 'temporary'
              : 'persistent'
          }
          anchor={getSettingsFsView()}
          open={visibleFileManager}
          onClose={this.toggleFileManager}
          className={
            useStudioShell || useOhifShell || useOviyamShell
              ? 'udv-studio-side-drawer'
              : undefined
          }
          PaperProps={{
            className:
              useStudioShell || useOhifShell || useOviyamShell
                ? 'udv-studio-side-drawer-paper udv-studio-fs-paper'
                : undefined,
            style:
              useStudioShell || useOhifShell || useOviyamShell
                ? { zIndex: 220 }
                : undefined,
          }}
          ModalProps={
            useStudioShell || useOhifShell || useOviyamShell
              ? { keepMounted: true, style: { zIndex: 210 } }
              : undefined
          }
          style={
            useStudioShell || useOhifShell || useOviyamShell
              ? { zIndex: 210 }
              : { zIndex: 1200 }
          }
        >
          <div>
            <div>
              {visibleFileManager ? 
                <FsUI 
                  onOpen={this.handleOpenSandboxFs} 
                  onOpenImage={this.handleOpenImage} 
                  onOpenMultipleFilesCompleted={this.openMultipleFilesCompleted}
                  onOpenDicomdir={this.handleOpenFsDicomdir}
                  onOpenZip={this.openZipInViewer}
                  color={useStudioShell ? '#c4a574' : iconColor} 
                /> 
              : null}
            </div>
          </div>
        </Drawer>       

        {visibleSettings ? (
          <Settings
            onClose={this.hideSettings}
            skin={
              useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
            }
            onPlanChanged={() => {
              this.refreshAccessGate()
            }}
            isPortalProfile={isPortalPlan}
            renderMode={this.state.renderMode}
            manifestMediaCaps={this.state.manifestMediaCaps}
            onSetRenderMode={this.setRenderMode}
            onExplorerAutoRefPinChange={() => {
              if (!getExplorerAutoRefPin({ portalProfile: isPortalPlan })) {
                this._autoRefPinnedSeriesIdx = []
              } else {
                this.syncAutoRefPinsFromStore()
              }
              this.setState((prev) => ({
                explorerPinTick: (prev.explorerPinTick || 0) + 1,
              }))
            }}
          />
        ) : null}

        {visibleGsdfQa ? (
          <GsdfQaModal
            onClose={this.hideGsdfQa}
            onResultChange={this.handleGsdfQaResultChange}
          />
        ) : null}

        {visibleAbout ? <AboutDlg onClose={this.showAbout}/> : null}
        {this.state.scoutPanelOpen ? (() => {
          const scoutHit = findScoutSeriesInStore(this.props.series)
          const scoutFiles = (scoutHit && scoutHit.files) || []
          const scoutFile =
            scoutFiles.find((f) => f && (f.image || f.imageId)) || scoutFiles[0] || null
          // Preferir stack volumétrico activo; si el activo es scout, buscar otra serie no-scout
          let volumeFiles = this.props.files || []
          const activeIsScout =
            volumeFiles[0] &&
            volumeFiles[0].series &&
            volumeFiles[0].series.isScout
          if (activeIsScout && this.props.series && this.props.series.seriesList) {
            const keys = this.props.series.seriesKeys || [...this.props.series.seriesList.keys()]
            for (const k of keys) {
              const files = this.props.series.seriesList.get(k)
              if (
                Array.isArray(files) &&
                files.length >= 3 &&
                !(files[0] && files[0].series && files[0].series.isScout)
              ) {
                volumeFiles = files
                break
              }
            }
          }
          return (
            <ScoutFloatingPanel
              open={!!this.state.scoutPanelOpen}
              scoutSyncEnabled={!!this.state.scoutSyncEnabled}
              scoutFile={scoutFile}
              volumeFiles={volumeFiles}
              activeSliceIndex={this.state.sliceIndex || 0}
              onSliceSelect={this.handleScoutSliceSelect}
              onClose={() => this.setState({ scoutPanelOpen: false })}
            />
          )
        })() : null}
        <DoseReportModal
          open={!!this.state.doseModalOpen}
          report={this.state.doseReport}
          onClose={this.closeDoseInfo}
        />

        <JoystickMenu
          open={!!this.state.visibleJoystick}
          x={this.state.joystickX || 0}
          y={this.state.joystickY || 0}
          skin={
            useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
          }
          slots={this.state.joystickSlots}
          modality={this.getCurrentModality && this.getCurrentModality()}
          presetsEnabled={!isPortalPlan}
          onClose={this.hideJoystick}
          onSelect={this.handleJoystickSelect}
        />

        <CaptureChooser
          open={!!this.state.visibleCaptureDlg}
          skin={
            useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
          }
          onClose={this.hideCaptureDlg}
          onClipboard={() => this.confirmCaptureDest('clipboard')}
          onFile={() => this.confirmCaptureDest('file')}
          onShare={() => {
            this.setState({ visibleCaptureDlg: false }, () => {
              this.openShareSheet({ preferCapture: true })
            })
          }}
        />

        <ShareSheet
          open={!!this.state.visibleShareSheet}
          skin={
            useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
          }
          busy={!!this.state.shareBusy || !!this.state.studyZipDownloadBusy}
          status={this.state.shareStatus || ''}
          zipInfo={this.state.shareZipInfo}
          canZip={this.canShareStudyZip()}
          zipPendingLabel={
            this.state.localStudyZip &&
            this.state.localStudyZip.eligible &&
            !this.state.localStudyZip.ready
              ? this.getStudyZipDownloadTitle()
              : ''
          }
          portalMode={isPortalPlan}
          canPreviews={
            !isPortalPlan &&
            !!(this.getShareFilesPool && this.getShareFilesPool().length)
          }
          canPacs={!isPortalPlan && this.canShareToPacs()}
          pacsDestId={this.state.scpShareDestId || SCP_SHARE_DEST_DEFAULT}
          pacsDestOptions={this.getSharePacsDestOptions()}
          onPacsDestChange={this.handleScpShareDestChange}
          onManageScpDest={this.openScpDestDlg}
          onClose={this.hideShareSheet}
          onShareCapture={this.handleShareCapture}
          onShareZip={this.handleShareZip}
          onDownloadZip={this.handleDownloadStudyZip}
          onShareSeriesPreviews={() => this.handleSharePreviews('series')}
          onShareStudyPreviews={() => this.handleSharePreviews('study')}
          onStowCurrent={this.handleStowCurrentSop}
          onStowSeries={this.handleStowActiveSeries}
          studyShareUrl={this.getStudyShareUrl()}
          onCopyStudyLink={this.handleCopyStudyLink}
          onShareStudyLink={this.handleShareStudyLink}
          pwaInstallHint={offerPwaInstall}
          onInstallPwa={this.handleInstallApp}
          onDismissPwaInstall={this.handleDismissPwaInstall}
        />

        {!useStudioShell && planAllowsVoice
          ? this.renderVoiceNotesPanel(
              useEdgeShell ? 'dock-bottom' : 'dock-right',
              useEdgeShell ? 'edge' : 'classic'
            )
          : null}

        {!useStudioShell && planAllowsCopilot
          ? this.renderCopilotPanel(
              useEdgeShell ? 'dock-bottom' : 'dock-right',
              useEdgeShell ? 'edge' : 'classic'
            )
          : null}

        <VoiceRecHud
          visible={
            planAllowsVoice &&
            (!!this.state.voiceNoteRecording ||
              (!!this.state.voiceNoteBusy && !this.state.visibleVoiceNotes))
          }
          skin={
            useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
          }
          recording={!!this.state.voiceNoteRecording}
          paused={!!this.state.voiceNotePaused}
          busy={!!this.state.voiceNoteBusy}
          canPause={canPauseMediaRecorder()}
          stream={
            this._voiceRecorder && this.state.voiceNoteRecording
              ? this._voiceRecorder.getStream()
              : null
          }
          elapsedMs={this.state.voiceElapsedMs || 0}
          liveTranscript={this.state.voiceLiveTranscript}
          sttRuntime={this.state.voiceSttRuntime}
          panelOpen={!!this.state.visibleVoiceNotes}
          bottomInset={
            (useStudioShell ? 16 : this.getBottomChromeInset()) +
            (useEdgeShell && this.state.visibleVoiceNotes
              ? Math.round(
                  Math.min(
                    (typeof window !== 'undefined' ? window.innerHeight : 800) *
                      0.38,
                    320
                  )
                )
              : 12)
          }
          onPause={this.pauseVoiceNote}
          onResume={this.resumeVoiceNote}
          onStop={this.stopVoiceNote}
          onOpenPanel={this.openVoiceNotesPanel}
        />

        <ScpDestinationsDlg
          open={!!this.state.visibleScpDestDlg}
          onClose={this.hideScpDestDlg}
          onChange={() => {
            this.setState({
              scpShareDestId: getScpShareDestId(),
              scpDestTick: (this.state.scpDestTick || 0) + 1,
            })
          }}
        />

        <CaptureToast
          open={!!(this.state.captureToast && this.state.captureToast.message)}
          skin={
            useStudioShell ? 'studio' : useEdgeShell ? 'edge' : 'classic'
          }
          ok={!(this.state.captureToast && this.state.captureToast.ok === false)}
          message={
            (this.state.captureToast && this.state.captureToast.message) || ''
          }
          placement={
            (this.state.captureToast && this.state.captureToast.placement) ||
            (this.state.voiceNoteRecording || this.state.voiceNoteBusy
              ? 'top'
              : 'bottom')
          }
          subtle={
            !!(this.state.captureToast && this.state.captureToast.subtle) ||
            !!(this.state.voiceNoteRecording || this.state.voiceNoteBusy)
          }
          onClose={this.hideCaptureToast}
        />

        {visibleDownloadZipDlg ? <DownloadZipDlg onClose={this.hideDownloadZipDlg} url={this.url} /> : null}

        {visibleOpenMultipleFilesDlg ? (
          <OpenMultipleFilesDlg
            ref={(r) => {
              this._openMultipleFilesDlgRef = r
            }}
            onClose={this.hideOpenMultipleFilesDlg}
            files={this.files}
            origin={'local'}
            silent
            onProgress={this.handleLocalFilesLoadProgress}
          />
        ) : null}

        <Dialog
            open={visibleClearMeasureDlg}
            onClose={this.hideClearMeasureDlg}
        >
            <DialogTitle>{t('dialogs.clearMeasurements')}</DialogTitle>
            <DialogActions>
                <Button onClick={this.hideClearMeasureDlg}>
                    {t('common.cancel')}
                </Button>
                <Button onClick={this.confirmClearMeasureDlg} autoFocus>
                    {t('common.ok')}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={visibleZippedFileDlg}
            onClose={this.hideZippedFileDlg}
        >
            <DialogTitle>{t('dialogs.zipImportSandbox')}</DialogTitle>
            <DialogActions>
                <Button onClick={this.hideZippedFileDlg}>
                    {t('common.cancel')}
                </Button>
                <Button onClick={this.confirmZippedFileDlg} autoFocus>
                    {t('common.ok')}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={visibleZipExtractDlg} disableBackdropClick disableEscapeKeyDown>
            <DialogTitle>{t('dialogs.zipExtractTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t('dialogs.zipExtractBody')}
              </DialogContentText>
              <LinearProgress style={{ marginTop: 12 }} />
            </DialogContent>
        </Dialog>

        <Dialog
            open={this.state.visibleOpenUrl}
        >
            <DialogTitle>{t('dialogs.openUrlTitle')}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t('dialogs.openUrlHint')}
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="id-open-url"
                    inputRef={input => (this.openUrlField = input)}
                    fullWidth
                />
              </DialogContent>
            <DialogActions>
                <Button onClick={() => this.hideOpenUrl(false)} >
                    {t('common.cancel')}
                </Button>
                <Button onClick={() => this.hideOpenUrl(true)} autoFocus>
                    {t('common.ok')}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={this.state.visibleMessage}
        >
            <DialogTitle>{this.state.titleMessage}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {this.state.textMessage}
                </DialogContentText>
              </DialogContent>
            <DialogActions>
                {this.state.foreignStudyPrompt ? (
                  <>
                    <Button
                      onClick={() =>
                        this.setState({
                          visibleMessage: false,
                          foreignStudyPrompt: null,
                        })
                      }
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => this.confirmForeignStudyCompare()}
                    >
                      {t('relatedStudies.foreignStudyActivate')}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() =>
                      this.setState({
                        visibleMessage: false,
                        foreignStudyPrompt: null,
                      })
                    }
                  >
                    {t('common.ok')}
                  </Button>
                )}
            </DialogActions>
        </Dialog>

        <Popover
          id={'id-layout'}
          open={visibleLayout}
          anchorEl={this.state.anchorElLayout}
          onClose={this.closeLayout}
          // Por encima de ToolsEdgePanel (drawer zIndex ~150120)
          style={{ zIndex: 160000 }}
          anchorOrigin={{
            vertical: 'center',
            // En edge el drawer está a la derecha: abrir el layout hacia la izquierda
            horizontal: useEdgeShell ? 'left' : 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: useEdgeShell ? 'right' : 'left',
          }}
        >
          <LayoutTool 
            row={this.props.layout[0]-1} 
            col={this.props.layout[1]-1}
            onChange={this.changeLayout}
          />  
        </Popover>     
        
        { !useEdgeShell && !useStudioShell && (
        <Popover
          id={'id-tools'}
          open={visibleToolsPanel}
          anchorEl={this.state.anchorElToolsPanel}
          onClose={this.closeToolsPanel}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <ToolsPanel 
            toolActive={this.state.toolActive}
            referenceLines={visibleReferenceLines}
            hideReferenceLinesOnActive={!!this.state.hideReferenceLinesOnActiveViewport}
            seriesLink={visibleSeriesLink}
            seriesLinkWwwc={!!this.state.seriesLinkWwwc}
            mprActive={mprActive}
            toolExecute={this.toolExecute}
            onChange={this.changeLayout}
            onGsdfQa={this.showGsdfQa}
            gsdfQaPassed={gsdfQaPassed}
            gatewayStatus={this.state.gatewayStatus || 'local_only'}
            gatewayCompatOk={this.state.gatewayCompatOk !== false}
            onGatewayOpen={this.onGatewayOpen}
            onGateway={this.showAuthGatewayModal}
            anyOpen={anyOpen}
            voiceNoteRecording={!!this.state.voiceNoteRecording}
            voiceNoteBusy={!!this.state.voiceNoteBusy}
            onVoiceNoteToggle={this.toggleVoiceNote}
            onVoiceNoteStart={this.startVoiceNote}
            onVoiceNoteStop={this.stopVoiceNote}
          />  
        </Popover>
        )}

        <Menu
          id="mpr-menu"
          anchorEl={this.state.anchorElMpr}
          open={Boolean(this.state.anchorElMpr)}
          onClose={this.handleMprMenuClose}
          keepMounted
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          {!mprAllowed ? (
            <MenuItem disabled>
              {(mprViability && mprViability.message) ||
                (mprProfile === 'blocked'
                  ? mprProfileLabel('blocked')
                  : 'MPR no disponible para esta serie')}
            </MenuItem>
          ) : (
            [
              mprActive ? (
                <MenuItem key="exit" onClick={() => this.handleMprMenuSelect('exit')}>
                  Salir de MPR
                </MenuItem>
              ) : null,
              mprOrthogonalAllowed ? (
                <MenuItem
                  key="orthogonal"
                  selected={!!visibleMprOrthogonal}
                  onClick={() => this.handleMprMenuSelect('orthogonal')}
                >
                  <ListItemIcon style={{minWidth: 32}}>
                    <Icon path={mdiViewColumn} size={'1.1rem'} color={iconColor} />
                  </ListItemIcon>
                  Orthogonal
                </MenuItem>
              ) : null,
              <MenuItem
                key="coronal"
                selected={!!visibleMprCoronal}
                onClick={() => this.handleMprMenuSelect('coronal')}
              >
                <ListItemIcon style={{minWidth: 32}}>
                  <Icon path={mdiAlphaCBox} size={'1.1rem'} color={iconColor} />
                </ListItemIcon>
                Coronal
              </MenuItem>,
              <MenuItem
                key="sagittal"
                selected={!!visibleMprSagittal}
                onClick={() => this.handleMprMenuSelect('sagittal')}
              >
                <ListItemIcon style={{minWidth: 32}}>
                  <Icon path={mdiAlphaSBox} size={'1.1rem'} color={iconColor} />
                </ListItemIcon>
                Sagittal
              </MenuItem>,
              <MenuItem
                key="axial"
                selected={!!visibleMprAxial}
                onClick={() => this.handleMprMenuSelect('axial')}
              >
                <ListItemIcon style={{minWidth: 32}}>
                  <Icon path={mdiAlphaABox} size={'1.1rem'} color={iconColor} />
                </ListItemIcon>
                Axial
              </MenuItem>,
              mprActive ? (
                <MenuItem
                  key="slab"
                  disableRipple
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'block', height: 'auto', background: 'transparent' }}
                >
                  {this.renderMprSlabToolbarChips()}
                </MenuItem>
              ) : null,
              mprActive ? (
                <MenuItem
                  key="mpr3d"
                  selected={!!visibleMpr3D}
                  disabled={!mpr3dAllowed}
                  onClick={() => {
                    this.handleMprMenuClose()
                    this.mpr3D()
                  }}
                >
                  <ListItemIcon style={{minWidth: 32}}>
                    <Icon path={mdiCubeOutline} size={'1.1rem'} color={mpr3dAllowed ? iconColor : '#666'} />
                  </ListItemIcon>
                  {visibleMpr3D ? 'Volver a MPR' : '3D'}
                </MenuItem>
              ) : null,
              visibleMpr3D ? (
                <MenuItem
                  key="mpr3d-decapado"
                  selected={!!this.state.mpr3dDecapado}
                  onClick={() => {
                    this.handleMprMenuClose()
                    this.toggleMpr3dDecapado()
                  }}
                >
                  <ListItemIcon style={{minWidth: 32}}>
                    <Icon path={mdiContentCut} size={'1.1rem'} color={iconColor} />
                  </ListItemIcon>
                  {this.state.mpr3dDecapado ? 'Volumen completo' : 'Decapado'}
                </MenuItem>
              ) : null,
            ]
          )}
        </Menu>

        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={visibleVolumeBuilding || !!this.state.mpr3dBuilding}
          autoHideDuration={this.state.mpr3dBuilding ? null : 6000}
          message={
            this.state.mpr3dBuilding
              ? (this.state.mpr3dBuildN
                ? `Preparando volumen 3D… ${this.state.mpr3dBuildZi}/${this.state.mpr3dBuildN}`
                : 'Preparando volumen 3D…')
              : this.state.mprProfile === 'light'
              ? 'Preparando MPR (modo ligero)...'
              : 'Construyendo volumen MPR, espere...'
          }
        />

        {useStudioShell ? (
          <StudioShell
            leftCollapsed={studioLeftCollapsed}
            rightCollapsed={studioRightCollapsed}
            hideToolsRail={!!(portalChrome && portalChrome.hideToolsRail)}
            title={studioTitles && studioTitles.title}
            subtitle={studioTitles && studioTitles.subtitle}
            planLabel={planDisplayLabel}
            loadIndicator={r2LoadIndicator}
            isFullscreen={this.state.isFullscreen}
            onToggleMenu={this.toggleMainMenu}
            onToggleFullscreen={this.toggleFullscreen}
            onLayout={this.handleLayout}
            onToggleLeftRail={this.toggleStudioLeftRail}
            onToggleRightRail={this.toggleStudioRightRail}
            bell={this.renderSwUpdateBell()}
            showMenu={showSystemMenu}
            installBanner={
              offerPwaInstall ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={this.handleInstallApp}
                  style={{
                    textTransform: 'none',
                    fontWeight: 600,
                    marginRight: 4,
                    whiteSpace: 'nowrap',
                    color: 'var(--udv-accent, #c4a574)',
                  }}
                >
                  {t('pwa.install')}
                </Button>
              ) : null
            }
            explorer={this.explorer}
            onSelectSeries={this.explorerOnSelectSeries}
            seriesColor={iconColor}
            autoSelectFirstSeries={this.autoSelectFirstSeries}
            onAutoShowExplorer={() => {
              this.hasAutoOpenedExplorer = true
            }}
            explorerPinProps={this.getExplorerPinProps()}
            voiceOpen={planAllowsVoice && !!this.state.visibleVoiceNotes}
            voicePanel={
              planAllowsVoice && this.state.visibleVoiceNotes
                ? this.renderVoiceNotesPanel('rail', 'studio')
                : null
            }
            copilotOpen={planAllowsCopilot && !!this.state.visibleCopilot}
            copilotPanel={
              planAllowsCopilot && this.state.visibleCopilot
                ? this.renderCopilotPanel('rail', 'studio')
                : null
            }
            toolsProps={this.buildChromeToolsRailProps({
              anyOpen,
              showCineBtn,
              cineOpen,
              showDsa,
              dsaUi,
              dsaFrameCount,
              showOrthancBtn,
              showClinicalAuth,
              showPresets,
              canInstallPwa,
              showRenderModeUi,
              visibleReferenceLines,
              visibleSeriesLink,
              visibleToolbox,
              mprAllowed,
              mprOrthogonalAllowed,
              mprActive,
              mprViability,
              mprProfile,
              mprSlabAllowed,
              mpr3dAllowed,
              mpr3dProfile,
              visibleMpr3D,
              visibleMprOrthogonal,
              visibleMprCoronal,
              visibleMprSagittal,
              visibleMprAxial,
              chromeOnMpr,
              chromeOnMpr3d,
              planAllowsPet,
              planAllowsCopilot,
              planAllowsVoice,
              portalChrome,
              isPortalPlan,
              gsdfQaPassed,
              onExplorer: this.toggleStudioLeftRail,
            })}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              {this.buildLayoutGrid()}
              {visibleMpr3D && this.state.mpr3dMaximized ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 50,
                    background: '#000',
                  }}
                >
                  {this.renderMpr3dViewport()}
                </div>
              ) : null}
            </div>
          </StudioShell>
        ) : useOhifShell ? (
          <OhifShell
            drawerCollapsed={ohifDrawerCollapsed}
            planLabel={planDisplayLabel}
            chromeTheme={getEffectiveTheme()}
            isFullscreen={this.state.isFullscreen}
            toolActive={this.state.toolActive}
            anyOpen={anyOpen}
            showMenu={showSystemMenu}
            onToggleMenu={this.toggleMainMenu}
            onToggleDrawer={this.toggleOhifDrawer}
            onLayout={this.handleLayout}
            onToggleFullscreen={this.toggleFullscreen}
            toolExecute={this.toolExecute}
            onAdvancedAction={this.handleOhifAdvancedAction}
            showRelatedStudies={
              Array.isArray(this.state.relatedStudies) &&
              this.state.relatedStudies.length > 0
            }
            onRelatedStudies={this.showRelatedStudies}
            showWorklist={this.hasWorklistUi()}
            onWorklist={this.showWorklist}
            compareMode={!!this.state.compareMode}
            compareModeEnabled={this.canEnableCompareMode()}
            onToggleCompareMode={this.toggleCompareMode}
            showLocalStudies
            onLocalStudies={this.showLocalStudies}
            bell={this.renderSwUpdateBell()}
            explorer={this.explorer}
            onSelectSeries={this.explorerOnSelectSeries}
            seriesColor={iconColor}
            autoSelectFirstSeries={this.autoSelectFirstSeries}
            onAutoShowExplorer={() => {
              if (!this.hasAutoOpenedExplorer) {
                this.setState({ visibleExplorer: true })
                this.hasAutoOpenedExplorer = true
              }
            }}
            explorerPinProps={this.getExplorerPinProps()}
            onMpr3d={chromeOnMpr3d}
            onDoseInfo={this.openDoseInfo}
            mpr3dAllowed={mpr3dAllowed}
            visibleMpr3D={!!visibleMpr3D}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              {this.buildLayoutGrid()}
              {visibleMpr3D && this.state.mpr3dMaximized ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 50,
                    background: '#000',
                  }}
                >
                  {this.renderMpr3dViewport()}
                </div>
              ) : null}
            </div>
          </OhifShell>
        ) : useOviyamShell ? (
          <OviyamNgShell
            planLabel={planDisplayLabel}
            chromeTheme={getEffectiveTheme()}
            title={studioTitles && studioTitles.title}
            subtitle={studioTitles && studioTitles.subtitle}
            patientId={studioTitles && studioTitles.patientId}
            isFullscreen={this.state.isFullscreen}
            toolActive={this.state.toolActive}
            anyOpen={anyOpen}
            showMenu={showSystemMenu}
            sidebarCollapsed={oviyamSidebarCollapsed}
            toolsCollapsed={oviyamToolsCollapsed}
            hideToolsRail={!!(portalChrome && portalChrome.hideToolsRail)}
            onToggleMenu={this.toggleMainMenu}
            onToggleSidebar={this.toggleOviyamSidebar}
            onToggleToolsRail={this.toggleOviyamToolsRail}
            onLayout={this.handleLayout}
            onToggleFullscreen={this.toggleFullscreen}
            toolExecute={this.toolExecute}
            onReset={this.resetImage}
            onPresets={
              showPresets
                ? () => this.toolExecute('Wwwc')
                : null
            }
            showPresets={!!showPresets}
            onCine={this.cinePlayer}
            showCine={!!showCineBtn}
            cineActive={!!cineOpen}
            onCaptureViewport={this.saveShot}
            onShareViewport={this.openShareSheet}
            onHeader={this.toggleHeader}
            portalChrome={portalChrome}
            viewportWidth={this.state.viewportWidth || 1440}
            bell={this.renderSwUpdateBell()}
            loadIndicator={r2LoadIndicator}
            studyLoadSource={this.state.studyLoadSource}
            onSelectSeries={this.explorerOnSelectSeries}
            autoSelectFirstSeries={this.autoSelectFirstSeries}
            showRelatedStudies={
              Array.isArray(this.state.relatedStudies) &&
              this.state.relatedStudies.length > 0
            }
            relatedStudiesCount={
              Array.isArray(this.state.relatedStudies)
                ? this.state.relatedStudies.length
                : 0
            }
            onRelatedStudies={this.showRelatedStudies}
            showWorklist={this.hasWorklistUi()}
            worklistCount={
              Array.isArray(this.state.worklistItems)
                ? this.state.worklistItems.length
                : 0
            }
            onWorklist={this.showWorklist}
            collapsedStudyKeys={this.state.collapsedStudyKeys || []}
            onToggleStudyCollapse={this.toggleExplorerStudyCollapse}
            viewportStudyUids={this.getViewportStudyUids()}
            activeSliceIndex={
              this._navSliceIndex != null && !Number.isNaN(this._navSliceIndex)
                ? this._navSliceIndex
                : this.state.sliceIndex || 0
            }
            toolsProps={this.buildChromeToolsRailProps({
              anyOpen,
              showCineBtn,
              cineOpen,
              showDsa,
              dsaUi,
              dsaFrameCount,
              showOrthancBtn,
              showClinicalAuth,
              showPresets,
              canInstallPwa,
              showRenderModeUi,
              visibleReferenceLines,
              visibleSeriesLink,
              visibleToolbox,
              mprAllowed,
              mprOrthogonalAllowed,
              mprActive,
              mprViability,
              mprProfile,
              mprSlabAllowed,
              mpr3dAllowed,
              mpr3dProfile,
              visibleMpr3D,
              visibleMprOrthogonal,
              visibleMprCoronal,
              visibleMprSagittal,
              visibleMprAxial,
              chromeOnMpr,
              chromeOnMpr3d,
              planAllowsPet,
              planAllowsCopilot,
              planAllowsVoice,
              portalChrome,
              isPortalPlan,
              gsdfQaPassed,
              onExplorer: this.toggleOviyamSidebar,
            })}
            voiceOpen={planAllowsVoice && !!this.state.visibleVoiceNotes}
            voicePanel={
              planAllowsVoice && this.state.visibleVoiceNotes
                ? this.renderVoiceNotesPanel('rail', 'oviyamng')
                : null
            }
            copilotOpen={planAllowsCopilot && !!this.state.visibleCopilot}
            copilotPanel={
              planAllowsCopilot && this.state.visibleCopilot
                ? this.renderCopilotPanel('rail', 'oviyamng')
                : null
            }
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              {this.buildLayoutGrid()}
              {visibleMpr3D && this.state.mpr3dMaximized ? (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 50,
                    background: '#000',
                  }}
                >
                  {this.renderMpr3dViewport()}
                </div>
              ) : null}
            </div>
          </OviyamNgShell>
        ) : (
          <>
        <div style={{
          height: viewportHeight, 
          paddingBottom: viewportPaddingBottom,
          boxSizing: 'border-box',
          transition: useEdgeShell
            ? 'padding-bottom 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
            : undefined,
          position: 'relative',
        }}>
          {this.buildLayoutGrid()}
          {visibleMpr3D && (isPhoneLikeDevice() || this.state.mpr3dMaximized) ? (
            <div
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                zIndex: 150050,
                background: '#000',
              }}
            >
              {this.renderMpr3dViewport()}
            </div>
          ) : null}
        </div>

        {/* Chrome Explorer toolbarEdge (portal a body) */}
        {this.renderEdgeExplorerChrome()}

        {/* Classic / desktop: Drawer de Explorer sin cambiar */}
        { !useEdgeShell && (
        <Drawer
          variant="persistent"
          anchor={isMobile || isTablet ? 'bottom' : 'right'}
          open={visibleExplorer}
          onClose={this.toggleExplorer}
          classes={{
            paper: `${classes.explorerDrawer} ${classes.explorerDrawerPaper}`
          }}
          PaperProps={{
            elevation: (isMobile || isTablet) ? 8 : 1,
            style: (isMobile || isTablet) ? {
                height: 86,
                minHeight: 86,
                maxHeight: 86,
                boxSizing: 'border-box',
                padding: 0,
                margin: 0,
                overflow: 'hidden',
                background: '#181818',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.10)',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                zIndex: 1300
            } : {}
          }}
        >
          {visibleExplorer ? (
            <div className="explorer-container" style={{
              height: '100%',
              display: 'flex',
              flexDirection: (isMobile || isTablet) ? 'row' : 'column',
              backgroundColor: '#181818',
              padding: 0,
              margin: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
              position: 'relative'
            }}>
              <Explorer
                  explorer={this.explorer}
                  onSelectSeries={this.explorerOnSelectSeries}
                  color={iconColor}
                  isMobile={isMobile || isTablet}
                  autoSelectFirstSeries={this.autoSelectFirstSeries}
                  onAutoShowExplorer={() => {
                      if (!this.hasAutoOpenedExplorer) {
                          this.setState({ visibleExplorer: true })
                          this.hasAutoOpenedExplorer = true
                      }
                  }}
                  {...this.getExplorerPinProps()}
              />
            </div>
          ) : null}
        </Drawer>
        )}
          </>
        )}

        <div>
          <input
            type="file"
            id="file_open"
            style={{ display: "none" }}
            ref={this.fileOpen}
            onChange={e => this.handleImportFromInput(e.target.files, ImportSource.FILE_INPUT)}
            multiple
          />
        </div>

        <div>
          <input
            type="file"
            id="udv_attach_pdf_input"
            style={{ display: 'none' }}
            accept=".pdf,application/pdf"
            ref={this._pdfAttachInputRef}
            onChange={this.handleAttachPdfSelected}
          />
        </div>

        <div>
          <input
            type="file"
            id="file_zip_open"
            style={{ display: "none" }}
            ref={this.zipOpen}
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={e => {
              this.handleImportFromInput(e.target.files, ImportSource.FILE_INPUT)
              if (this.zipOpen && this.zipOpen.current) this.zipOpen.current.value = ''
            }}
          />
        </div>

        <div>
          <input
            type="file"
            id="file_dicomdir"
            style={{ display: "none" }}
            ref={this.openDicomdir}
            onChange={e => this.handleOpenDicomdir(e.target.files)}
            webkitdirectory="" mozdirectory="" directory="" multiple
          />
        </div>

        <div>
          <input
            type="file"
            id="file_folder"
            style={{ display: "none" }}
            ref={this.openFolder}
            onChange={e => this.handleImportFromInput(e.target.files, ImportSource.FILE_INPUT)}
            webkitdirectory="" mozdirectory="" directory="" multiple
          />
        </div>    

        <OrthancConnection
          ref={(r) => {
            this._orthancConnectionRef = r
          }}
          open={this.state.visibleOrthancConnection}
          onClose={this.hideOrthancConnection}
          onLoadImage={this.handleOrthancImageLoad}
          onExplorerUpdate={this.handleExplorerUpdate}
          onPrepareNewStudy={this.prepareReplaceStudy}
          onStudyLoadBegin={(studyId) => this.beginStudyLoad(`orthanc:${studyId || ''}`)}
          studyIdFromUrl={
            this.state.visibleOrthancConnection
              ? this.state.studyIdFromUrl
              : null
          }
          pacsIdFromUrl={orthancPacsId}
          allowPacsPick={developerMode}
          reloadToken={this.state.orthancReloadToken}
          getRenderMode={() => this.state.renderMode || RENDER_MODE.DIAGNOSTIC}
          getPinnedSeriesIdx={this.getPinnedSeriesIdxForScheduler}
          resolveRenderModeForManifest={(manifest, assetBaseUrl) =>
            this.resolveModeForManifestLoad(manifest, assetBaseUrl)
          }
          onManifestLoaded={(manifest, handlersMeta) => {
            const assetBase =
              (handlersMeta && handlersMeta.assetBaseUrl) || null
            const canonical = canonicalizeManifest(manifest, {
              assetBaseUrl: assetBase,
            })
            this._lastManifestObject = canonical
            if (handlersMeta) {
              this._lastManifestHandlers = {
                ...(this._lastManifestHandlers || {}),
                ...handlersMeta,
              }
            }
            if (canonical && canonical.downloadUrl) {
              this._localZipSessionActive = false
              this.setState({
                studyDownloadUrl: canonical.downloadUrl,
                localStudyZip: {
                  eligible: false,
                  ready: false,
                  expected: 0,
                  cached: 0,
                  studyUid: '',
                },
              })
            }
          }}
          onR2LoadStart={() => {
            const remote =
              this._preferredRemoteLoadSource ||
              resolveStudyLoadSourceFromManifest(this._lastManifestObject) ||
              STUDY_LOAD_SOURCE.R2
            this._preferredRemoteLoadSource = remote
            this.setState({
              studyLoadSource: remote,
              manifestDownloadProgress: null,
            })
          }}
          onR2DownloadProgress={(stats) => {
            this.applyManifestDownloadProgress(stats)
          }}
          onR2LoadEnd={() => this.completeManifestLoadStudySource()}
        />

        <AuthGatewayModal
          open={this.state.visibleAuthGatewayModal}
          onClose={this.hideAuthGatewayModal}
          onSessionChange={this.handleGatewaySessionChange}
          onControllerSessionChange={this.handleControllerSessionChange}
          onPushStatusChange={this.handleGatewayPushStatusChange}
          pushActive={!!this.state.gatewayPushActive}
          controllerCompat={this.state.controllerCompat}
          gatewayCompatOk={this.state.gatewayCompatOk !== false}
          udvVersion={packageJson.version}
        />

        <LocalStudiesDlg
          open={this.state.visibleLocalStudies}
          onClose={this.hideLocalStudies}
          onOpenStudy={this.openLocalStudyInViewer}
          onLoadStudy={this.loadLocalStudyMerge}
          canLoadStudy={this.canLoadLocalStudy}
          onReopen={this.showLocalStudies}
          canInstallPwa={canInstallPwa}
          onInstallPwa={this.handleInstallApp}
          offlineCacheDisabled={!this.isOfflineCacheAllowed()}
        />

        <PatientRelatedStudiesDlg
          open={this.state.visibleRelatedStudies}
          onClose={this.hideRelatedStudies}
          studies={this.state.relatedStudies}
          patientId={this.state.relatedPatientId}
          currentTicketId={this.state.ticketIdFromUrl}
          loadedTicketIds={this.state.loadedRelatedTicketIds}
          localByUid={this.state.localCatalogByUid}
          loadedStatsByUid={this.getLoadedStudyStatsByUid()}
          onLoadRelated={this.addRelatedStudy}
          onOpenRelated={this.openRelatedStudy}
        />

        <WorklistDlg
          open={!!this.state.visibleWorklist}
          onClose={this.hideWorklist}
          tab={this.state.worklistTab || 'cola'}
          onTabChange={this.setWorklistTab}
          items={this.state.worklistItems}
          title={this.state.worklistTitle}
          currentTicketId={this.state.ticketIdFromUrl}
          localByUid={this.state.localCatalogByUid}
          sessions={this.state.worklistSessions}
          onOpenItem={this.openWorklistItem}
          onOpenLocal={this.openWorklistLocalItem}
          onManageStorage={this.openWorklistManageStorage}
        />

        <InstallPwaHelpDlg
          open={!!this.state.visibleInstallHelp}
          onClose={this.hideInstallHelp}
        />

        <InstallPwaSuccessDlg
          open={!!this.state.visiblePwaSuccessDlg}
          onClose={this.hidePwaSuccessDlg}
          studyUrl={this.getViewerResumeUrl() || this.getStudyShareUrl()}
          portalUrl={this.getPortalReturnUrl()}
          onReturnToPortal={this.handleReturnToPortalFromPwaSuccess}
          onCopyStudyLink={this.handleCopyStudyLinkFromPwaSuccess}
        />

        <PortalDisclaimerDlg
          open={!!this.state.visiblePortalDisclaimer}
          sensitiveAction={!!this.state.portalDisclaimerSensitive}
          onAck={this.handlePortalDisclaimerAck}
        />

        <StudyDuplicateDlg
          open={!!this.state.studyDuplicate}
          payload={this.state.studyDuplicate && this.state.studyDuplicate.payload}
          localCatalog={this.state.studyDuplicate && this.state.studyDuplicate.localCatalog}
          busy={!!this.state.studyDuplicateBusy}
          error={this.state.studyDuplicateError}
          onKeep={this.handleStudyDuplicateKeep}
          onReplace={this.handleStudyDuplicateReplace}
          onReject={this.handleStudyDuplicateReject}
          onClose={() =>
            this.setState({
              studyDuplicate: null,
              studyDuplicateBusy: false,
              studyDuplicateError: null,
            })
          }
        />

        <StudySourceChoiceDlg
          open={!!this.state.studySourceChoice}
          patientName={
            this.state.studySourceChoice && this.state.studySourceChoice.patientName
          }
          studyInstanceUID={
            this.state.studySourceChoice &&
            this.state.studySourceChoice.studyInstanceUID
          }
          localInstances={
            this.state.studySourceChoice &&
            this.state.studySourceChoice.localInstances
          }
          localBytes={
            this.state.studySourceChoice && this.state.studySourceChoice.localBytes
          }
          remoteInstances={
            this.state.studySourceChoice &&
            this.state.studySourceChoice.remoteInstances
          }
          remoteSeries={
            this.state.studySourceChoice && this.state.studySourceChoice.remoteSeries
          }
          remoteUnavailable={
            !!(
              this.state.studySourceChoice &&
              this.state.studySourceChoice.remoteUnavailable
            )
          }
          remoteError={
            this.state.studySourceChoice && this.state.studySourceChoice.remoteError
          }
          onOpenLocal={this.handleStudySourceChooseLocal}
          onOpenRemote={this.handleStudySourceChooseRemote}
          onClose={this.handleStudySourceChoiceCancel}
        />

        <StudyZipDownloadDlg open={!!this.state.studyZipDownloadBusy} />

        <Dialog
          open={!!this.state.dsaSynthPreview}
          onClose={() => this.setState({ dsaSynthPreview: null })}
          fullWidth
          maxWidth="sm"
          {...udvDialogProps}
        >
          <DialogTitle style={UDV_SURFACE.title}>
            Síntesis vascular
            {this.state.dsaSynthPreview
              ? ` · ${String(this.state.dsaSynthPreview.mode || '').toUpperCase()}`
              : ''}
          </DialogTitle>
          <DialogContent style={UDV_SURFACE.content}>
            {this.state.dsaSynthPreview && this.state.dsaSynthPreview.dataUrl ? (
              <img
                src={this.state.dsaSynthPreview.dataUrl}
                alt="Síntesis vascular DSA"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  background: '#111',
                  borderRadius: 8,
                }}
              />
            ) : null}
            <DialogContentText style={{ marginTop: 12, color: 'inherit' }}>
              {this.state.dsaSynthPreview && this.state.dsaSynthPreview.frames
                ? `${this.state.dsaSynthPreview.frames} frames acumulados`
                : ''}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleDsaMenuSelect('export-synth-png')}
              color="primary"
            >
              Exportar PNG
            </Button>
            <Button
              onClick={() => this.setState({ dsaSynthPreview: null })}
              color="primary"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!this.state.renderModeToast}
          autoHideDuration={
            this.state.voiceNoteRecording || this.state.voiceNoteBusy ? 1600 : 2200
          }
          onClose={() => this.setState({ renderModeToast: null })}
          message={this.state.renderModeToast || ''}
          anchorOrigin={{
            vertical:
              this.state.voiceNoteRecording || this.state.voiceNoteBusy
                ? 'top'
                : 'bottom',
            horizontal: 'center',
          }}
          style={{ zIndex: 300100 }}
        />

        <UdvGatewayToast
          open={!!this.state.gatewayPushToast}
          message={this.state.gatewayPushToast}
          onClose={() => this.setState({ gatewayPushToast: null })}
          onOpenNotices={this.openNotificationCenter}
        />

        <Dialog
          open={!!this.state.visibleGatewayNotices}
          onClose={this.closeNotificationCenter}
          fullWidth
          maxWidth="sm"
          {...udvDialogProps}
        >
          <DialogTitle style={UDV_SURFACE.title}>Avisos</DialogTitle>
          <DialogContent style={UDV_SURFACE.content}>
            {this.state.swUpdatePending && (
              <List dense>
                <ListItem
                  button
                  style={UDV_SURFACE.listItem}
                  onClick={() => {
                    this.setState({
                      visibleGatewayNotices: false,
                      swUpdateDialog: true,
                    })
                  }}
                >
                  <ListItemText
                    primary={
                      <span style={{ color: '#f0f4f8', fontWeight: 600 }}>
                        Nueva versión de UDV
                      </span>
                    }
                    secondary="Tocar para actualizar cuando puedas"
                  />
                </ListItem>
              </List>
            )}
            {(this.state.gatewayNotices || []).length === 0 &&
              !this.state.swUpdatePending && (
                <DialogContentText style={UDV_SURFACE.muted}>
                  No hay avisos pendientes.
                </DialogContentText>
              )}
            <List dense>
              {(this.state.gatewayNotices || []).map((n) => (
                <ListItem
                  button
                  key={n.id || n.studyInstanceUID || n.at}
                  style={UDV_SURFACE.listItem}
                  onClick={() => void this.handleGatewayNoticeActivate(n)}
                >
                  <ListItemText
                    primary={
                      <span style={{ color: '#f0f4f8', fontWeight: 600 }}>
                        {`${this.gatewayNoticeStatusLabel(n.status)}${
                          n.patientName ? ` · ${n.patientName}` : ''
                        }`}
                      </span>
                    }
                    secondary={n.message || n.studyInstanceUID || ''}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions style={UDV_SURFACE.actions}>
            {(this.state.gatewayNotices || []).length > 0 && (
              <Button onClick={this.clearGatewayNotices} style={UDV_SURFACE.btnGhost}>
                Limpiar
              </Button>
            )}
            <Button
              onClick={this.closeNotificationCenter}
              style={UDV_SURFACE.btnPrimary}
              variant="contained"
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* PWA: nueva version del SW — toast; si expira, queda la campana */}
        <Snackbar
          open={!!this.state.swUpdateReady}
          autoHideDuration={8000}
          onClose={this.onSwUpdateToastClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          style={{ zIndex: 300200, top: 'max(12px, env(safe-area-inset-top, 0px))' }}
          ContentProps={{
            style: {
              backgroundColor: 'rgba(33, 150, 243, 0.95)',
              color: '#fff',
              borderRadius: 10,
              boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
              fontSize: '0.875rem',
              fontWeight: 500,
              padding: '6px 8px 6px 16px',
            },
          }}
          message="Nueva versión disponible"
          action={
            <Button color="inherit" size="small" onClick={this.applySwUpdate}>
              Actualizar
            </Button>
          }
        />

        <Dialog
          open={!!this.state.swUpdateDialog}
          onClose={this.closeSwUpdateDialog}
          {...udvDialogProps}
        >
          <DialogTitle style={UDV_SURFACE.title}>Nueva versión de UDV</DialogTitle>
          <DialogContent style={UDV_SURFACE.content}>
            <DialogContentText style={UDV_SURFACE.muted}>
              Hay una actualización lista. Podés seguir revisando el estudio y
              actualizar cuando termines. Recargar aplica la nueva versión.
            </DialogContentText>
          </DialogContent>
          <DialogActions style={UDV_SURFACE.actions}>
            <Button onClick={this.closeSwUpdateDialog} style={UDV_SURFACE.btnGhost}>
              Más tarde
            </Button>
            <Button
              onClick={this.applySwUpdate}
              style={UDV_SURFACE.btnPrimary}
              variant="contained"
              autoFocus
            >
              Actualizar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!this.state.visiblePwaExitDialog}
          onClose={this.cancelPwaExit}
          {...udvDialogProps}
        >
          <DialogTitle style={UDV_SURFACE.title}>Salir de UDV</DialogTitle>
          <DialogContent style={UDV_SURFACE.content}>
            <DialogContentText style={UDV_SURFACE.muted}>
              ¿Salir de la aplicación? Se desactivará la pantalla completa.
            </DialogContentText>
          </DialogContent>
          <DialogActions style={UDV_SURFACE.actions}>
            <Button onClick={this.cancelPwaExit} style={UDV_SURFACE.btnGhost}>
              Cancelar
            </Button>
            <Button
              onClick={() => void this.executePwaExit()}
              style={UDV_SURFACE.btnPrimary}
              variant="contained"
              autoFocus
            >
              Salir
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!this.state.pwaExitHint}
          autoHideDuration={6000}
          onClose={() => this.setState({ pwaExitHint: null })}
          message={this.state.pwaExitHint || ''}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          style={{ zIndex: 300250 }}
        />

        {/* Aviso discreto de pantalla completa (móvil/tablet): barra inferior, no bloquea el visor */}
        <Snackbar
          open={this.state.showFullscreenPrompt}
          onClose={() => this.setState({ showFullscreenPrompt: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          style={{
            // Por encima de toolbarEdge / explorer; respeta chrome inferior y safe-area
            zIndex: 300000,
            bottom: `max(${Math.max(16, this.getBottomChromeInset())}px, env(safe-area-inset-bottom, 0px))`,
            left: 0,
            right: 0,
          }}
          ContentProps={{
            style: {
              backgroundColor: 'rgba(28, 28, 30, 0.94)',
              color: 'rgba(255, 255, 255, 0.88)',
              borderRadius: 10,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 6px 24px rgba(0, 0, 0, 0.4)',
              minWidth: 0,
              maxWidth: 'min(440px, calc(100vw - 24px))',
              width: '100%',
              flexWrap: 'nowrap',
              alignItems: 'center',
              padding: '6px 6px 6px 14px',
              fontSize: '0.8125rem',
              lineHeight: 1.35,
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            },
          }}
          message={
            <span style={{ display: 'block', paddingRight: 4 }}>
              Mejor experiencia a pantalla completa
            </span>
          }
          action={
            <React.Fragment>
              <Button
                size="small"
                onClick={this.handleRequestFullscreen}
                style={{
                  color: '#90caf9',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  padding: '4px 10px',
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                Maximizar
              </Button>
              <IconButton
                size="small"
                aria-label="Cerrar"
                onClick={() => this.setState({ showFullscreenPrompt: false })}
                style={{ color: 'rgba(255, 255, 255, 0.55)', padding: 6 }}
              >
                <Icon path={mdiClose} size={0.85} color="currentColor" />
              </IconButton>
            </React.Fragment>
          }
        />

      </div>
      </TranslationPreventer>
    )
  }

  // Agregar nuevo método para cargar estudio desde URL
  loadStudyFromUrl = async (studyId) => {
    console.log('App - loadStudyFromUrl llamado con studyId:', studyId)
    console.log('App - pacsIdFromUrl:', this.state.pacsIdFromUrl)

    // Mostrar el diálogo de OrthancConnection pero con el StudyId
    this.setState({
      visibleOrthancConnection: true,
      studyIdFromUrl: studyId,
      studyOpenContext: resolveStudyOpenContext({
        ticketIdFromUrl: this.state.ticketIdFromUrl,
        manifestUrlFromUrl: null,
        legacyWadoContext: null,
        studyIdFromUrl: studyId,
        pacsIdFromUrl: this.state.pacsIdFromUrl,
        studyLoadSource: this.state.studyLoadSource,
      }),
    }, () => {
      console.log('App - Estado actualizado, visibleOrthancConnection:', this.state.visibleOrthancConnection)
      // Poll opcional solo para logging/modalidad; NO forzar layout 1x1
      // (el default ya es 1x1; force a 1x1 rompía multi-panel al cargar la 1ª serie)
      const pollGen = this._studyLayoutPollGen
      const checkForImage = () => {
        if (pollGen !== this._studyLayoutPollGen) return
        const activeViewer = this.getActiveDcmViewer();
        if (activeViewer && activeViewer.image) {
          const modality = getDicomModality(activeViewer.image);
          console.log('DICOM Modality from URL:', modality);
          return;
        }
        this._studyLayoutPollTimer = setTimeout(checkForImage, 100);
      };
      checkForImage();
    });
  }

  /** Formato compacto para indicador de velocidad manifest (MiB/s). */
  formatManifestMiBps = (v) => {
    if (v == null || !Number.isFinite(v)) return '—'
    if (v < 0.01) return '<0.01'
    return v >= 10 ? v.toFixed(0) : v.toFixed(1)
  }

  formatManifestImgsPerSec = (v) => {
    if (v == null || !Number.isFinite(v)) return '—'
    if (v < 0.05) return '<0.1'
    if (v >= 100) return v.toFixed(0)
    return v.toFixed(1)
  }

  formatManifestBytes = (b) => {
    if (b == null || !Number.isFinite(b)) return '—'
    const m = b / (1024 * 1024)
    return m >= 100 ? `${m.toFixed(0)} MiB` : `${m.toFixed(1)} MiB`
  }

  formatManifestDurationMs = (ms) => {
    if (ms == null || !Number.isFinite(ms)) return '—'
    if (ms < 1000) return `${Math.round(ms)} ms`
    const s = ms / 1000
    return s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${s.toFixed(1)} s`
  }

  /**
   * Chips de thick slab MPR 2D (modo + grosor) para toolbar classic / menú.
   */
  renderMprSlabToolbarChips = () => {
    const mode = this.state.mprSlabMode || 'thin'
    const mm = Number(this.state.mprSlabMm) || 0
    const chip = (title, label, active, onClick) => (
      <Tooltip title={title} key={title}>
        <button
          type="button"
          onClick={onClick}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 26,
            minWidth: 36,
            padding: '0 7px',
            marginLeft: 2,
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            cursor: 'pointer',
            borderRadius: 4,
            border: active ? '1px solid #81C784' : '1px solid rgba(255,255,255,0.28)',
            background: active ? 'rgba(129,199,132,0.28)' : 'transparent',
          }}
        >
          {label}
        </button>
      </Tooltip>
    )
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginLeft: 4,
          marginRight: 4,
        }}
      >
        {chip('Corte fino (1 vóxel)', 'Fino', mode === 'thin', () => this.setMprSlabMode('thin'))}
        {chip('MIP — máxima intensidad', 'MIP', mode === 'mip', () => this.setMprSlabMode('mip'))}
        {chip('MinIP — mínima intensidad', 'MinIP', mode === 'minip', () => this.setMprSlabMode('minip'))}
        {chip('Promedio HU', 'Avg', mode === 'avg', () => this.setMprSlabMode('avg'))}
        <span style={{ width: 8 }} />
        {[0, 3, 5, 10].map((preset) =>
          chip(
            preset === 0 ? 'Sin grosor' : `Grosor ${preset} mm`,
            preset === 0 ? 'Off' : `${preset}`,
            mm === preset,
            () => this.setMprSlabMm(preset)
          )
        )}
      </span>
    )
  }

  /**
   * Controles MPR compactos para AppBar Classic: slab en menú, iconos esenciales inline.
   */
  renderClassicMprSlabMenu = () => {
    const mode = this.state.mprSlabMode || 'thin'
    const mm = Number(this.state.mprSlabMm) || 0
    const open = Boolean(this.state.anchorElClassicMprSlab)
    const pickMode = (next) => {
      this.closeClassicMprSlabMenu()
      this.setMprSlabMode(next)
    }
    const pickMm = (next) => {
      this.closeClassicMprSlabMenu()
      this.setMprSlabMm(next)
    }
    return (
      <Menu
        id="classic-mpr-slab-menu"
        anchorEl={this.state.anchorElClassicMprSlab}
        open={open}
        onClose={this.closeClassicMprSlabMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ dense: true }}
      >
        <MenuItem disabled dense className="udv-classic-mpr-menu-heading">
          <ListItemText primary="Modo de slab" />
        </MenuItem>
        <MenuItem selected={mode === 'thin'} onClick={() => pickMode('thin')}>
          <ListItemText primary="Fino (1 vóxel)" secondary="Corte fino" />
          {mode === 'thin' ? <Icon path={mdiCheck} size={0.75} /> : null}
        </MenuItem>
        <MenuItem selected={mode === 'mip'} onClick={() => pickMode('mip')}>
          <ListItemText primary="MIP" secondary="Máxima intensidad" />
          {mode === 'mip' ? <Icon path={mdiCheck} size={0.75} /> : null}
        </MenuItem>
        <MenuItem selected={mode === 'minip'} onClick={() => pickMode('minip')}>
          <ListItemText primary="MinIP" secondary="Mínima intensidad" />
          {mode === 'minip' ? <Icon path={mdiCheck} size={0.75} /> : null}
        </MenuItem>
        <MenuItem selected={mode === 'avg'} onClick={() => pickMode('avg')}>
          <ListItemText primary="Avg" secondary="Promedio HU" />
          {mode === 'avg' ? <Icon path={mdiCheck} size={0.75} /> : null}
        </MenuItem>
        <MenuItem disabled dense className="udv-classic-mpr-menu-heading">
          <ListItemText primary="Grosor (mm)" />
        </MenuItem>
        {[0, 3, 5, 10].map((preset) => (
          <MenuItem
            key={`slab-mm-${preset}`}
            selected={mm === preset}
            onClick={() => pickMm(preset)}
          >
            <ListItemText
              primary={preset === 0 ? 'Off (sin grosor)' : `${preset} mm`}
            />
            {mm === preset ? <Icon path={mdiCheck} size={0.75} /> : null}
          </MenuItem>
        ))}
      </Menu>
    )
  }

  renderClassicMprToolbarGroup = ({
    iconSize,
    iconColor,
    activeColor,
    chromeOnMpr,
    mprAllowed,
    mprViability,
    mprProfile,
    mprActive,
    mpr3dAllowed,
    visibleMpr3D,
    anyOpen,
  }) => {
    if (!anyOpen) return null
    const hasScout = mprActive && findScoutSeriesInStore(this.props.series)
    const slabActive = mprActive && (this.state.mprSlabMode || 'thin') !== 'thin'
    return (
      <>
        <div className="udv-classic-toolbar-sep" aria-hidden="true" />
        <div className="udv-classic-toolbar-group" data-group="mpr">
          {chromeOnMpr && (
            <Tooltip
              title={
                mprAllowed
                  ? 'MPR'
                  : (mprViability && mprViability.message) ||
                    (mprProfile === 'blocked'
                      ? mprProfileLabel('blocked')
                      : 'MPR (evaluando serie…)')
              }
            >
              <IconButton
                onClick={this.handleMprMenuOpen}
                color={mprActive ? 'secondary' : 'inherit'}
              >
                <Icon
                  path={mdiAxisArrow}
                  size={iconSize}
                  color={mprActive ? activeColor : iconColor}
                />
              </IconButton>
            </Tooltip>
          )}

          {mprActive && (
            <>
              <Tooltip title="Punto mira">
                <IconButton
                  onClick={this.toggleMprCrosshair}
                  color={this.state.mprCrosshairActive ? 'secondary' : 'inherit'}
                >
                  <Icon
                    path={mdiCrosshairsGps}
                    size={iconSize}
                    color={this.state.mprCrosshairActive ? activeColor : iconColor}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Slab MPR (modo y grosor)">
                <button
                  type="button"
                  className={
                    slabActive
                      ? 'udv-classic-mpr-slab-btn is-active'
                      : 'udv-classic-mpr-slab-btn'
                  }
                  onClick={this.openClassicMprSlabMenu}
                  aria-haspopup="true"
                  aria-controls={
                    this.state.anchorElClassicMprSlab
                      ? 'classic-mpr-slab-menu'
                      : undefined
                  }
                  aria-expanded={Boolean(this.state.anchorElClassicMprSlab)}
                >
                  <Icon path={mdiArrowSplitHorizontal} size={0.7} color="#fff" />
                  <span>{this.getMprSlabSummaryLabel()}</span>
                </button>
              </Tooltip>
              {this.renderClassicMprSlabMenu()}

              <Tooltip
                title={
                  mpr3dAllowed
                    ? visibleMpr3D
                      ? 'Volver a MPR'
                      : '3D'
                    : mpr3dProfileLabel('off')
                }
              >
                <span>
                  <IconButton
                    onClick={() => this.mpr3D()}
                    disabled={!mpr3dAllowed}
                    color={visibleMpr3D ? 'secondary' : 'inherit'}
                  >
                    <Icon
                      path={mdiCubeOutline}
                      size={iconSize}
                      color={
                        visibleMpr3D
                          ? activeColor
                          : mpr3dAllowed
                            ? iconColor
                            : '#666'
                      }
                    />
                  </IconButton>
                </span>
              </Tooltip>

              {visibleMpr3D && (
                <Tooltip
                  title={
                    this.state.mpr3dDecapado
                      ? 'Volver al volumen completo'
                      : 'Decapar según planos MPR'
                  }
                >
                  <IconButton
                    onClick={this.toggleMpr3dDecapado}
                    color={this.state.mpr3dDecapado ? 'secondary' : 'inherit'}
                  >
                    <Icon
                      path={mdiContentCut}
                      size={iconSize}
                      color={
                        this.state.mpr3dDecapado ? activeColor : iconColor
                      }
                    />
                  </IconButton>
                </Tooltip>
              )}

              {hasScout ? (
                <Tooltip title="Scout Sync">
                  <IconButton
                    onClick={this.toggleScoutSync}
                    color={this.state.scoutSyncEnabled ? 'secondary' : 'inherit'}
                  >
                    <Icon
                      path={mdiLayers}
                      size={iconSize}
                      color={
                        this.state.scoutSyncEnabled ? activeColor : iconColor
                      }
                    />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}

          <Tooltip title="Info de dosis">
            <IconButton onClick={this.openDoseInfo} color="inherit">
              <Icon path={mdiInformationOutline} size={iconSize} color={iconColor} />
            </IconButton>
          </Tooltip>
        </div>
      </>
    )
  }

  renderClassicRenderModeToggle = () => {
    const isPreview = this.state.renderMode === RENDER_MODE.PREVIEW
    const isDiagnostic = !isPreview
    return (
      <div
        role="radiogroup"
        aria-label="Modo de visualización"
        className="udv-classic-render-toggle"
      >
        <Tooltip title={t('renderMode.diagnosticTooltip')}>
          <button
            type="button"
            role="radio"
            aria-checked={isDiagnostic}
            aria-label={t('renderMode.diagnostic')}
            className={isDiagnostic ? 'is-active-diag' : undefined}
            onClick={() => this.setRenderMode(RENDER_MODE.DIAGNOSTIC)}
          >
            <Icon
              path={mdiHospitalBox}
              size={0.62}
              color={isDiagnostic ? '#fff' : 'rgba(255,255,255,0.65)'}
            />
            DIAG
          </button>
        </Tooltip>
        <Tooltip title={t('renderMode.previewTooltip')}>
          <button
            type="button"
            role="radio"
            aria-checked={isPreview}
            aria-label={t('renderMode.preview')}
            className={isPreview ? 'is-active-prev' : undefined}
            onClick={() => this.setRenderMode(RENDER_MODE.PREVIEW)}
          >
            <Icon
              path={mdiImageOutline}
              size={0.62}
              color={isPreview ? '#FFE0B2' : 'rgba(255,255,255,0.65)'}
            />
            PREV
          </button>
        </Tooltip>
      </div>
    )
  }

  /**
   * Chip Local / R2 / WADO en AppBar (classic) o flotante (edge).
   * @param {boolean} isMobileUi
   */
  renderManifestLoadIndicator = (isMobileUi) => {
    const progress = this.state.manifestDownloadProgress
    // Chip solo en carga activa; ocultar también si stats ya marcaron completo
    if (
      !isStudyLoadActive(this.state.studyLoadSource) ||
      (progress && progress.studyFullyLoaded)
    ) {
      return null
    }
    const source = this.state.studyLoadSource
    const isLocal = source === STUDY_LOAD_SOURCE.LOCAL
    const isLegacyWado = isStudyLoadWado(source)
    const isR2 = source === STUDY_LOAD_SOURCE.R2
    const chipLabel = studyLoadSourceChipLabel(source)
    const originHost =
      showStudyLoadOriginHint() && progress
        ? formatStudyLoadOriginLabel(progress)
        : ''
    const offline = (progress && progress.offlineCache) || {}
    const cacheHits = Number(offline.fromCacheHits) || 0
    const downloadTitle = studyLoadSourceDownloadTitle(source)
    const title =
      isLocal && progress ? (
        <div style={{ maxWidth: 320, fontSize: 12, lineHeight: 1.45 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {progress.localPhase === 'extract'
              ? 'Extrayendo ZIP…'
              : progress.localPhase === 'index'
              ? 'Indexando series…'
              : 'Carga local'}
          </div>
          <div>
            {progress.completed}/{progress.totalTasks || '…'} inst.
            {cacheHits > 0 ? ` · cache ${cacheHits}` : ''}
            {progress.loadedOk != null ? ` · listas: ${progress.loadedOk}` : ''}
          </div>
          <div style={{ opacity: 0.92, marginTop: 4 }}>
            Tiempo: {this.formatManifestDurationMs(progress.elapsedMs)}
            {progress.studyFullyLoaded ? ' (completo)' : ' (en curso)'}
          </div>
          <div style={{ opacity: 0.88, marginTop: 4 }}>
            Podés interactuar con el estudio mientras termina de cargar.
            Si te equivocaste, cancelá o abrí otro estudio.
          </div>
        </div>
      ) : isLocal ? (
        'Cargando estudio desde archivos locales…'
      ) : (isR2 || isLegacyWado) && progress ? (
        <div style={{ maxWidth: 320, fontSize: 12, lineHeight: 1.45 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{downloadTitle}</div>
          <div>
            Total: {this.formatManifestImgsPerSec(progress.instantImgsPerSec)} img/s ·{' '}
            {this.formatManifestMiBps(progress.instantMiBps)} MiB/s · {progress.completed}/
            {progress.totalTasks} inst. · {this.formatManifestBytes(progress.totalBytes)}
          </div>
          <div style={{ opacity: 0.92, marginTop: 4 }}>
            Media: {this.formatManifestImgsPerSec(progress.avgImgsPerSec)} img/s ·{' '}
            {this.formatManifestMiBps(progress.avgMiBps)} MiB/s · decodificadas:{' '}
            {progress.loadedOk}
          </div>
          <div style={{ opacity: 0.92, marginTop: 4 }}>
            Tiempo estudio: {this.formatManifestDurationMs(progress.elapsedMs)}
            {progress.studyFullyLoaded ? ' (completo)' : ' (en curso)'}
          </div>
          {originHost ? (
            <div style={{ opacity: 0.92, marginTop: 4 }}>
              Origen: {originHost}
            </div>
          ) : null}
          <div style={{ opacity: 0.88, marginTop: 4 }}>
            Cancelá o abrí otro estudio si no es el que necesitás.
          </div>
          {!isMobileUi && Array.isArray(progress.series) && progress.series.length > 0 ? (
            <>
              <Divider style={{ margin: '8px 0', backgroundColor: 'rgba(255,255,255,0.2)' }} />
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {progress.series.map((s, i) => {
                  const desc = s && s.description != null ? String(s.description) : ''
                  return (
                    <div key={i} style={{ marginBottom: 4 }}>
                      Serie {s && s.seriesNumber != null ? s.seriesNumber : '—'}
                      {desc ? ` · ${desc.slice(0, 36)}${desc.length > 36 ? '…' : ''}` : ''}:{' '}
                      {this.formatManifestMiBps(s && s.instantMiBps)} MiB/s · {s && s.loaded}/{s && s.total} ·{' '}
                      {this.formatManifestBytes(s && s.bytes)}
                    </div>
                  )
                })}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        'Descargando estudio (almacenamiento remoto)…'
      )

    return (
      <Tooltip interactive={Boolean(progress)} title={title}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginLeft: 8,
            marginRight: 4,
            opacity: 0.9,
            maxWidth:
              progress && !isMobileUi
                ? originHost
                  ? 360
                  : 260
                : isMobileUi
                  ? 180
                  : 120,
            overflow: 'hidden',
          }}
        >
          <CircularProgress size={14} thickness={5} style={{ color: '#9e9e9e', flexShrink: 0 }} />
          <Typography
            variant="caption"
            noWrap={!progress}
            style={{
              fontSize: isMobileUi ? '0.65rem' : '0.7rem',
              letterSpacing: 0.2,
              color: '#bdbdbd',
            }}
          >
            {chipLabel}
            {originHost ? ` · ${originHost}` : ''}
            {progress && typeof progress.completed === 'number' ? (
              <>
                {' '}
                · {progress.completed}/{progress.totalTasks}
                {isR2 || isLegacyWado ? (
                  <>
                    {' '}
                    · {this.formatManifestImgsPerSec(progress.instantImgsPerSec)} img/s
                  </>
                ) : null}
              </>
            ) : null}
          </Typography>
          <IconButton
            size="small"
            title="Cancelar descarga"
            aria-label="Cancelar descarga"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              this.cancelStudyLoad({ userInitiated: true })
            }}
            style={{
              padding: 2,
              color: '#ef9a9a',
              flexShrink: 0,
            }}
          >
            <Icon path={mdiClose} size={0.55} color="currentColor" />
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  /**
   * Cascada STOW → Orthanc REST.
   * @param {Uint8Array} bytes
   * @param {object|null} pacsConfigOrOpts — pacsConfig legacy, o `{ pacsConfig, wadoUrl }`
   */
  storeDicomToPacsCascade = async (bytes, pacsConfigOrOpts) => {
    let pacsConfig = pacsConfigOrOpts
    let wadoUrl =
      this.state.legacyWadoContext && this.state.legacyWadoContext.wadoUrl
    if (
      pacsConfigOrOpts &&
      typeof pacsConfigOrOpts === 'object' &&
      (pacsConfigOrOpts.pacsConfig !== undefined ||
        pacsConfigOrOpts.wadoUrl !== undefined)
    ) {
      pacsConfig = pacsConfigOrOpts.pacsConfig
      if (pacsConfigOrOpts.wadoUrl) wadoUrl = pacsConfigOrOpts.wadoUrl
    }
    const stowOk = await stowStoreDicomInstance(bytes, pacsConfig, { wadoUrl })
    if (stowOk) return 'stow'
    const orthancOk = await storeDicomInstanceToPacs(bytes, pacsConfig)
    if (orthancOk) return 'orthanc'
    return null
  }

  refreshStudyAfterStore = async () => {
    if (this.state.legacyWadoContext) {
      await this.loadStudyViaLegacyWadoUrl()
      return
    }
    // Manifest R2 / ticket Path M: re-manifest no trae el informe adjunto local.
    // No recargar (borraría la inyección).
    if (this.state.manifestUrlFromUrl && !this.state.studyIdFromUrl) {
      return
    }
    if (this.state.studyIdFromUrl) {
      this.setState((prev) => ({
        orthancReloadToken: prev.orthancReloadToken + 1,
        visibleOrthancConnection: true,
      }))
    }
  }

  /**
   * Workspace iframe: espera postMessage elev, canjea plan, carga studyId (auth-only).
   * docs/CONTRATO-UDV-WORKSPACE-EMBED.md
   */
  bootstrapWorkspaceEmbed = async () => {
    if (this._workspaceEmbedInFlight) return
    this._workspaceEmbedInFlight = true
    try {
      let msg
      try {
        msg = await waitForWorkspaceElevate()
      } catch (e) {
        if (this._unmounted) return
        this.setState({
          workspaceEmbedPending: false,
          accessDenied: {
            reason: ACCESS_REASONS.WORKSPACE_EMBED_REQUIRED,
            persona: UDV_PERSONAS.CLINICAL,
          },
          udvPersona: UDV_PERSONAS.CLINICAL,
        })
        return
      }
      if (this._unmounted) return
      const redeemed = await redeemWorkspaceElevate({
        t: msg.t,
        elev: msg.elev,
        ticketApi: msg.ticketApi,
        edge_id: msg.edge_id,
      })
      if (this._unmounted) return
      if (!redeemed.ok || !redeemed.entitlements) {
        this.setState({
          workspaceEmbedPending: false,
          accessDenied: {
            reason: ACCESS_REASONS.WORKSPACE_EMBED_REQUIRED,
            persona: UDV_PERSONAS.CLINICAL,
          },
          udvPersona: UDV_PERSONAS.CLINICAL,
        })
        return
      }
      saveEntitlements(redeemed.entitlements)
      const shellLayout = redeemed.shell
        ? parseToolbarQueryParam(redeemed.shell)
        : null
      const studyId = this.state.studyIdFromUrl
      this.setState(
        {
          workspaceEmbedPending: false,
          accessDenied: null,
          udvPlan: redeemed.plan || UDV_PLANS.LITE,
          udvPersona: UDV_PERSONAS.CLINICAL,
          toolbarLayoutFromUrl:
            shellLayout || this.state.toolbarLayoutFromUrl,
        },
        () => {
          if (studyId) {
            this.loadStudyFromUrl(studyId)
          }
        }
      )
    } catch (e) {
      console.error('bootstrapWorkspaceEmbed:', e)
      if (!this._unmounted) {
        this.setState({
          workspaceEmbedPending: false,
          accessDenied: {
            reason: ACCESS_REASONS.WORKSPACE_EMBED_REQUIRED,
            persona: UDV_PERSONAS.CLINICAL,
          },
          udvPersona: UDV_PERSONAS.CLINICAL,
        })
      }
    } finally {
      this._workspaceEmbedInFlight = false
      releaseSplashShellGate()
    }
  }

  /**
   * Resolve invite Controller (?udvInvite=) → entitlements draft + login.
   * @param {string} inviteId
   */
  bootstrapViewerInvite = async (inviteId) => {
    if (!inviteId || this._inviteBootstrapInFlight) return
    this._inviteBootstrapInFlight = true
    try {
      const resolved = await resolveViewerInvite(inviteId)
      if (this._unmounted) return
      if (!resolved.ok) {
        this.setState({
          accessDenied: {
            reason: ACCESS_REASONS.INVALID_INVITE,
            persona: UDV_PERSONAS.CLINICAL,
          },
          udvPersona: UDV_PERSONAS.CLINICAL,
          inviteLoginRequired: false,
        })
        return
      }
      applyInviteDraftEntitlements(resolved.invite)
      const shellLayout = resolved.invite.shell
        ? parseToolbarQueryParam(resolved.invite.shell)
        : null
      this.setState(
        {
          accessDenied: null,
          udvPersona: UDV_PERSONAS.CLINICAL,
          udvPlan: resolved.invite.plan || UDV_PLANS.LITE,
          inviteLoginRequired: true,
          toolbarLayoutFromUrl: shellLayout || this.state.toolbarLayoutFromUrl,
          pacsIdFromUrl:
            resolved.invite.pacsId || this.state.pacsIdFromUrl || null,
          visibleAuthGatewayModal: true,
        },
        () => {
          void this.refreshAccessGate()
        }
      )
    } catch (e) {
      console.error('bootstrapViewerInvite:', e)
      if (!this._unmounted) {
        this.setState({
          accessDenied: {
            reason: ACCESS_REASONS.INVALID_INVITE,
            persona: UDV_PERSONAS.CLINICAL,
          },
        })
      }
    } finally {
      this._inviteBootstrapInFlight = false
      releaseSplashShellGate()
    }
  }

  /**
   * Resolve worklist plataforma (?w=) → persistir sesión → abrir ítem current.
   * @param {string} worklistId
   */
  bootstrapViewerWorklist = async (worklistId) => {
    if (!worklistId || this._worklistBootstrapInFlight) return
    this._worklistBootstrapInFlight = true
    try {
      const ticketApiParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null
      const ticketApiBase = getTicketApiBase(ticketApiParams)
      const resolved = await resolveViewerWorklist(worklistId, {
        baseUrl: ticketApiBase,
      })
      if (this._unmounted) return
      if (!resolved.ok) {
        this.setState({
          accessDenied: { reason: ACCESS_REASONS.INVALID_WORKLIST },
          worklistItems: [],
          worklistTitle: null,
        })
        return
      }
      const wl = resolved.worklist
      const id = wl.worklistId || String(worklistId)
      await upsertWorklistSession({
        worklistId: id,
        title: wl.title,
        expiresAt: wl.expiresAt,
        ticketApi: ticketApiBase,
        items: wl.items,
      })
      const sessions = await listWorklistSessions()
      if (this._unmounted) return

      const nextState = {
        accessDenied: null,
        worklistIdFromUrl: id,
        worklistTitle: wl.title,
        worklistItems: wl.items,
        worklistSessions: sessions,
        worklistTab: 'cola',
        udvPersona: UDV_PERSONAS.CLINICAL,
      }
      if (wl.shell) {
        const shellLayout = parseToolbarQueryParam(wl.shell)
        if (shellLayout) nextState.toolbarLayoutFromUrl = shellLayout
      }
      if (wl.profile) {
        nextState.udvPlan = wl.profile
      }
      this.setState(nextState)

      const current = pickWorklistCurrentItem(wl.items)
      this._worklistBootstrapInFlight = false
      if (current && current.ticketId) {
        this.bootstrapViewerTicket(current.ticketId)
        return
      }
      releaseSplashShellGate()
    } catch (e) {
      console.error('bootstrapViewerWorklist', e)
      if (!this._unmounted) {
        this.setState({
          accessDenied: { reason: ACCESS_REASONS.INVALID_WORKLIST },
        })
      }
      releaseSplashShellGate()
    } finally {
      this._worklistBootstrapInFlight = false
    }
  }

  /**
   * Resolve ticket plataforma (?t=) → profile + candidates → carga R2 o DICOMweb.
   * @param {string} ticketId
   */
  bootstrapViewerTicket = async (ticketId) => {
    if (!ticketId || this._ticketBootstrapInFlight) return
    this._ticketBootstrapInFlight = true
    try {
      const ticketApiParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null
      const resolved = await resolveViewerTicket(ticketId, {
        baseUrl: getTicketApiBase(ticketApiParams),
      })
      if (this._unmounted) return
      if (!resolved.ok) {
        let reason = ACCESS_REASONS.INVALID_TICKET
        if (resolved.reason === 'ticket_pending') {
          reason = ACCESS_REASONS.TICKET_PENDING
        }
        this.setState({
          accessDenied: { reason },
          relatedStudies: [],
          relatedPatientId: null,
        })
        return
      }
      const ticket = resolved.ticket
      this.applyViewerSessionPolicy({ ticket, searchParams: ticketApiParams })
      setUdvTelemetrySessionFromTicket(ticket)
      const ticketApiBase = getTicketApiBase(ticketApiParams)
      const originEdgeId = parseEdgeIdFromSearchParams(ticketApiParams)
      const elevationToken = parseElevationTokenFromSearchParams(ticketApiParams)
      const priorEnt = loadEntitlements()

      // Historial paciente: opcional; no bloquea la carga del estudio actual
      if (!this._unmounted) {
        this.setState({
          ticketIdFromUrl: String(ticketId),
          relatedStudies: Array.isArray(ticket.relatedStudies)
            ? ticket.relatedStudies
            : [],
          relatedPatientId: ticket.patientId || null,
        })
      }

      const accessCtx = await resolveTicketAccessContext({
        ticket,
        ticketId,
        ticketApiBase,
        elevationToken,
        originEdgeId,
        controllerLinks: loadControllerLinks(),
        cachedEnt: priorEnt,
        searchParams: ticketApiParams,
      })

      // Aplicar plan/shell YA (antes del probe): evita flash Studio/Edge→classic
      // mientras pickViewerCandidate corre, y elev_pending→patient.
      if (accessCtx.elevated) {
        setStoredPersona(UDV_PERSONAS.CLINICAL)
        if (accessCtx.entitlements && accessCtx.entitlements.plan) {
          saveEntitlements(accessCtx.entitlements)
        }
        if (!this._unmounted) {
          const early = {
            accessDenied: null,
            udvPlan: accessCtx.plan,
            udvPersona: UDV_PERSONAS.CLINICAL,
          }
          if (accessCtx.shell || ticket.shell) {
            const shellLayout = parseToolbarQueryParam(
              ticket.shell || accessCtx.shell
            )
            if (shellLayout) early.toolbarLayoutFromUrl = shellLayout
          }
          this.setState(early)
        }
        // Lease staff: heartbeat si plataforma emitió lease_token
        this.startStaffTicketLease(ticketId, ticketApiBase)
      } else if (!this._unmounted) {
        const portalShell = accessCtx.shell || ticket.shell || 'classic'
        const shellLayout = parseToolbarQueryParam(portalShell)
        const early = {
          accessDenied: null,
          udvPlan: accessCtx.plan,
          udvPersona: accessCtx.persona || UDV_PERSONAS.PORTAL,
        }
        if (shellLayout) early.toolbarLayoutFromUrl = shellLayout
        this.setState(early)
      }

      if (elevationToken) {
        stripElevationFromUrl()
      }

      const loadProfile = accessCtx.loadProfile

      if (!accessCtx.elevated) {
        // No pisar elevación de esta pestaña si el canje falló por carrera.
        if (!matchPlatformElevationSession(ticketId)) {
          saveEntitlements({
            plan: UDV_PLANS.PATIENT,
            shell: ticket.shell || accessCtx.shell || 'classic',
            theme: ticket.theme || accessCtx.theme || null,
            pacsId: null,
            udvToken: null,
            edgeId: null,
            source: ENTITLEMENT_SOURCES.TICKET,
          })
        }
      } else if (
        accessCtx.entitlements &&
        accessCtx.entitlements.plan &&
        originEdgeId &&
        accessCtx.source === ENTITLEMENT_SOURCES.ORIGIN_LINK
      ) {
        saveControllerLinks(loadControllerLinks())
      }

      const pickOpts = this.buildTicketPickOpts(ticket, {
        profile: loadProfile,
        ...this.resolveTicketLanHints(ticket),
      })
      const picked = await pickViewerCandidate(ticket.candidates, pickOpts)
      const target = candidateToLoadTarget(picked.candidate)
      if (!target) {
        this.setState({
          accessDenied: { reason: ACCESS_REASONS.INVALID_TICKET },
        })
        return
      }
      this.rememberTicketCandidateWalk(picked, {
        mode: 'bootstrap',
        ticket,
        ticketApiParams,
        pickOpts,
      })

      const effectivePlan = accessCtx.plan
      // Portal y clínico: honrar ticket.shell (override PACS / lab simulate).
      // Preferir shell del ticket sobre hint de lab para no flash Studio en dev-udv.
      const shellHint = accessCtx.elevated
        ? ticket.shell || accessCtx.shell || null
        : accessCtx.shell || ticket.shell || 'classic'

      const nextState = {
        accessDenied: null,
        udvPlan: effectivePlan,
        udvPersona: accessCtx.persona,
      }
      if (shellHint) {
        const shellLayout = parseToolbarQueryParam(shellHint)
        if (shellLayout) nextState.toolbarLayoutFromUrl = shellLayout
      }

      if (target.kind === 'r2') {
        nextState.manifestUrlFromUrl = target.manifestUrl
        if (target.pacs) {
          nextState.pacsIdFromUrl = target.pacs
        }
        nextState.studyOpenContext = resolveStudyOpenContext({
          ticketIdFromUrl: ticketId,
          manifestUrlFromUrl: target.manifestUrl,
          legacyWadoContext: null,
          studyIdFromUrl: null,
          pacsIdFromUrl: target.pacs || nextState.pacsIdFromUrl || this.state.pacsIdFromUrl,
          studyLoadSource: STUDY_LOAD_SOURCE.R2,
        })
        const primaryUid =
          target.studyUid ||
          studyUidFromManifestUrl(target.manifestUrl) ||
          (ticket &&
            (ticket.study_instance_uid ||
              ticket.studyInstanceUID ||
              ticket.study_uid)) ||
          null
        if (primaryUid) this._primaryStudyUid = String(primaryUid)
        this.setState(nextState, () => {
          const catalogOrigin = resolveStudyCatalogOrigin({
            searchParams: ticketApiParams,
            ticket,
            controllerLinks: loadControllerLinks(),
          })
          this.loadStudyFromManifestUrl(target.manifestUrl, {
            downloadUrl: target.downloadUrl || null,
            studyInstanceUID: primaryUid,
            catalogOrigin,
            catalogSource: STUDY_SOURCE.TICKET,
          })
        })
        return
      }

      if (target.kind === 'dicomweb') {
        const wadoUrl = target.wadoUrl
        const studyUID = target.studyUid
        if (!wadoUrl || !studyUID) {
          this.setState({
            accessDenied: { reason: ACCESS_REASONS.INVALID_TICKET },
          })
          return
        }
        this._primaryStudyUid = String(studyUID)
        nextState.legacyWadoContext = this.attachTicketOriginMeta(
          {
            wadoUrl: buildWadoUriUrl(null, wadoUrl),
            studyUID,
            seriesUID: null,
            qidoBase: buildDicomWebBaseUrl(null, wadoUrl),
          },
          picked,
          pickOpts,
          ticket.candidates
        )
        nextState.studyOpenContext = resolveStudyOpenContext({
          ticketIdFromUrl: ticketId,
          manifestUrlFromUrl: null,
          legacyWadoContext: nextState.legacyWadoContext,
          studyIdFromUrl: null,
          pacsIdFromUrl: this.state.pacsIdFromUrl,
          studyLoadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
        })
        this.setState(nextState, () => {
          this.loadStudyViaLegacyWadoUrl()
        })
      }
    } catch (e) {
      console.error('bootstrapViewerTicket', e)
      if (!this._unmounted) {
        this.setState({
          accessDenied: { reason: ACCESS_REASONS.INVALID_TICKET },
          relatedStudies: [],
          relatedPatientId: null,
        })
      }
    } finally {
      this._ticketBootstrapInFlight = false
      releaseSplashShellGate()
    }
  }

  /**
   * Pick opts desde el ticket (probe.order / skip_mixed_content / client_scope).
   * Sin hostnames de clínica.
   */
  buildTicketPickOpts = (ticket, extra = {}) => ({
    profile: extra.profile,
    clientScope: ticket && (ticket.clientScope || ticket.client_scope),
    edgeWadoUrl: extra.edgeWadoUrl || null,
    edgeLanBase: extra.edgeLanBase || null,
    probe: true,
    probeConfig: ticket && ticket.probe,
  })

  /**
   * LAN conocida: ticket, sesión Edge o links[] del edge_id (sin hardcodear hosts).
   */
  resolveTicketLanHints = (ticket, extra = {}) => {
    let edgeWadoUrl = extra.edgeWadoUrl || null
    let edgeLanBase =
      extra.edgeLanBase ||
      (ticket && (ticket.edgeLanBase || ticket.edge_lan_base)) ||
      null
    try {
      const edge = loadSession()
      if (!edgeWadoUrl && edge) {
        edgeWadoUrl =
          edge.edge_wado_url || edge.edgeWadoUrl || edge.serverUrl || null
      }
      if (!edgeLanBase && edge) {
        edgeLanBase = edge.edge_lan_base || edge.edgeLanBase || null
      }
    } catch (e) {
      /* ignore */
    }
    if (!edgeLanBase || !edgeWadoUrl) {
      try {
        const edgeId =
          extra.edgeId ||
          (ticket && (ticket.edgeId || ticket.edge_id)) ||
          ''
        const want = String(edgeId || '').trim()
        const links = loadControllerLinks() || []
        const link = want
          ? links.find(
              (L) => String((L && (L.edge_id || L.edgeId)) || '') === want
            )
          : null
        if (link) {
          if (!edgeLanBase) {
            edgeLanBase = link.edge_lan_base || link.edgeLanBase || null
          }
          if (!edgeWadoUrl) {
            edgeWadoUrl = link.edge_wado_url || link.edgeWadoUrl || null
          }
        }
      } catch (e2) {
        /* ignore */
      }
    }
    return { edgeWadoUrl, edgeLanBase }
  }

  attachTicketOriginMeta = (legacyCtx, picked, pickOpts, candidates) => {
    const meta = ticketOriginMeta(picked, pickOpts || {}, candidates)
    return Object.assign({}, legacyCtx, meta)
  }

  rememberTicketCandidateWalk = (picked, ctx) => {
    this._ticketCandidateWalk = {
      remaining: Array.isArray(picked && picked.remaining)
        ? picked.remaining.slice()
        : [],
      ctx: ctx || null,
      originMeta: ticketOriginMeta(
        picked,
        (ctx && ctx.pickOpts) || {},
        (ctx && ctx.ticket && ctx.ticket.candidates) || []
      ),
    }
  }

  /**
   * Mixed content / CORS / Failed to fetch de un origen LAN/HTTP:
   * siguiente candidate del ticket. No aborta el estudio.
   * @param {unknown} error
   * @returns {Promise<boolean>}
   */
  continueTicketCandidateAfterOriginFailure = async (error) => {
    if (!isOriginFailure(error)) return false
    const walk = this._ticketCandidateWalk
    if (
      !walk ||
      !walk.ctx ||
      !Array.isArray(walk.remaining) ||
      !walk.remaining.length
    ) {
      return false
    }
    const ctx = walk.ctx
    const picked = await pickViewerCandidate(walk.remaining, ctx.pickOpts || {})
    const target = candidateToLoadTarget(picked.candidate)
    if (!target) return false
    this.rememberTicketCandidateWalk(picked, ctx)
    try {
      // eslint-disable-next-line no-console
      console.info(
        '[UDV] ticket origin skip →',
        picked.reason,
        target.kind
      )
    } catch (eLog) {
      /* ignore */
    }
    this.loadNextTicketCandidateTarget(target, ctx)
    return true
  }

  loadNextTicketCandidateTarget = (target, ctx) => {
    const ticket = ctx && ctx.ticket
    const ticketApiParams = ctx && ctx.ticketApiParams
    const mode = (ctx && ctx.mode) || 'bootstrap'
    const primaryUid =
      (target.studyUid && String(target.studyUid)) ||
      (ticket &&
        ticket.raw &&
        (ticket.raw.study_instance_uid ||
          ticket.raw.studyInstanceUID ||
          ticket.raw.study_uid)) ||
      this._primaryStudyUid ||
      null
    if (primaryUid) this._primaryStudyUid = String(primaryUid)

    if (target.kind === 'r2' && target.manifestUrl) {
      const catalogOrigin = resolveStudyCatalogOrigin({
        searchParams: ticketApiParams,
        ticket,
        controllerLinks: loadControllerLinks(),
      })
      if (mode === 'merge') {
        void this.loadStudyFromManifestUrl(target.manifestUrl, {
          downloadUrl: target.downloadUrl || null,
          studyInstanceUID: primaryUid,
          skipSourceChoice: true,
          catalogSource: STUDY_SOURCE.TICKET,
          compareMerge: true,
        })
        return
      }
      this.setState(
        {
          accessDenied: null,
          manifestUrlFromUrl: target.manifestUrl,
          pacsIdFromUrl: target.pacs || this.state.pacsIdFromUrl,
          studyOpenContext: resolveStudyOpenContext({
            ticketIdFromUrl: this.state.ticketIdFromUrl,
            manifestUrlFromUrl: target.manifestUrl,
            legacyWadoContext: null,
            studyIdFromUrl: null,
            pacsIdFromUrl: target.pacs || this.state.pacsIdFromUrl,
            studyLoadSource: STUDY_LOAD_SOURCE.R2,
          }),
        },
        () => {
          void this.loadStudyFromManifestUrl(target.manifestUrl, {
            downloadUrl: target.downloadUrl || null,
            studyInstanceUID: primaryUid,
            catalogOrigin,
            catalogSource: STUDY_SOURCE.TICKET,
          })
        }
      )
      return
    }

    if (target.kind === 'dicomweb' && target.wadoUrl && target.studyUid) {
      const originMeta =
        (this._ticketCandidateWalk && this._ticketCandidateWalk.originMeta) || {}
      const legacyWadoContext = Object.assign(
        {
          wadoUrl: buildWadoUriUrl(null, target.wadoUrl),
          studyUID: target.studyUid,
          seriesUID: null,
          qidoBase: buildDicomWebBaseUrl(null, target.wadoUrl),
        },
        originMeta
      )
      if (mode === 'merge') {
        this.setState({ legacyWadoContext }, () => {
          void this.loadStudyViaLegacyWadoUrlCompare()
        })
        return
      }
      this.setState(
        {
          accessDenied: null,
          legacyWadoContext,
          studyOpenContext: resolveStudyOpenContext({
            ticketIdFromUrl: this.state.ticketIdFromUrl,
            manifestUrlFromUrl: null,
            legacyWadoContext,
            studyIdFromUrl: null,
            pacsIdFromUrl: this.state.pacsIdFromUrl,
            studyLoadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
          }),
        },
        () => {
          void this.loadStudyViaLegacyWadoUrl()
        }
      )
    }
  }

  loadStudyViaLegacyWadoUrl = async () => {
    const legacyCtx = this.state.legacyWadoContext
    if (!legacyCtx) return

    const pacsCfg = getPacsConfig(this.state.pacsIdFromUrl || null)
    const mode = RENDER_MODE.DIAGNOSTIC
    if (this.state.renderMode !== RENDER_MODE.DIAGNOSTIC) {
      persistRenderMode(RENDER_MODE.DIAGNOSTIC, { userSet: false })
    }

    this._localZipSessionActive = this.isOfflineCacheAllowed()
    this._localStudyZipReadyGen = (this._localStudyZipReadyGen || 0) + 1
    this.setState({
      studyLoadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
      renderMode: RENDER_MODE.DIAGNOSTIC,
      manifestMediaCaps: { hasPreview: false, hasDicom: true },
      manifestDownloadProgress: null,
      studyDownloadUrl: null,
      studyOpenContext: resolveStudyOpenContext({
        ticketIdFromUrl: this.state.ticketIdFromUrl,
        manifestUrlFromUrl: this.state.manifestUrlFromUrl,
        legacyWadoContext: legacyCtx,
        studyIdFromUrl: this.state.studyIdFromUrl,
        pacsIdFromUrl: this.state.pacsIdFromUrl,
        studyLoadSource: STUDY_LOAD_SOURCE.LEGACY_WADO,
      }),
      localStudyZip: {
        eligible: !!this._localZipSessionActive,
        ready: false,
        expected: 0,
        cached: 0,
        studyUid: legacyCtx.studyUID ? String(legacyCtx.studyUID) : '',
      },
    })

    const loadToken = this.beginStudyLoad('legacy-wado')

    try {
      this._legacyWadoLoadInFlight = true
      configureWadoLoaderAuth({
        username: pacsCfg && pacsCfg.USUARIO,
        password: pacsCfg && pacsCfg.PASS,
      })

      const fullCtx = {
        ...legacyCtx,
        wadoUrl: buildWadoUriUrl(pacsCfg, legacyCtx.wadoUrl),
        qidoBase:
          legacyCtx.qidoBase || buildDicomWebBaseUrl(pacsCfg, legacyCtx.wadoUrl),
        authHeaders: pacsAuthHeaders(pacsCfg),
      }

      this._filesByRenderMode = {
        [RENDER_MODE.PREVIEW]: null,
        [RENDER_MODE.DIAGNOSTIC]: null,
      }
      this._filesByRenderModeStudyUid = fullCtx.studyUID

      const loadResult = await loadStudyViaLegacyWado(fullCtx, {
        ...this.buildManifestLoadHandlers(pacsCfg, mode, loadToken, {
          compareMerge: !!this._compareMergeActive,
        }),
        renderMode: mode,
      })

      if (!this.isStudyLoadCurrent(loadToken.gen)) return

      try {
        if (loadResult && Array.isArray(loadResult.items) && loadResult.items.length) {
          this.snapshotFilesForRenderMode(mode, loadResult.items)
        }
      } catch (e) {
        /* ignore */
      }

      this.completeManifestLoadStudySource()
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      const skipped = await this.continueTicketCandidateAfterOriginFailure(error)
      if (skipped) return
      console.error('loadStudyViaLegacyWadoUrl:', error)
      this.setState({
        titleMessage: 'Legacy WADO',
        textMessage: error.message || String(error),
        visibleMessage: true,
      })
      this.completeManifestLoadStudySource()
    } finally {
      this._legacyWadoLoadInFlight = false
    }
  }

  /**
   * Si hay IDB/catálogo local con más instancias que el manifest R2,
   * preguntar cuál abrir (evita “perder” el estudio completo en storage).
   */
  maybePromptRicherLocalVsManifest = async (canonical, loadCtx) => {
    if (!canonical || !loadCtx || loadCtx.skipSourceChoice) return false
    const uid = String(canonical.studyInstanceUID || '')
    if (!uid) return false
    const remote = countManifestSeriesAndInstances(canonical)
    if (remote.instances <= 0) return false
    let localN = 0
    let localBytes = 0
    let catalog = null
    try {
      catalog = await getStudyCatalog(uid)
      const stats = await getCombinedStudyCacheStats(uid)
      localN = Math.max(
        Number(stats && stats.cachedComplete) || 0,
        Number(catalog && catalog.instanceCount) || 0
      )
      localBytes =
        Number(stats && stats.bytes) ||
        Number(catalog && catalog.bytesTotal) ||
        0
    } catch (e) {
      return false
    }
    if (localN <= remote.instances) return false

    this.setState({
      studyLoadSource: null,
      manifestDownloadProgress: null,
      studySourceChoice: {
        patientName:
          (canonical && canonical.patientName) ||
          (catalog && catalog.patientName) ||
          '',
        studyInstanceUID: uid,
        localInstances: localN,
        localBytes,
        remoteInstances: remote.instances,
        remoteSeries: remote.series,
        remoteUnavailable: false,
        catalogHint: catalog || undefined,
        pendingLoad: {
          manifestUrl: loadCtx.manifestUrl,
          downloadUrl: loadCtx.downloadUrl || null,
          assetBaseUrl: loadCtx.assetBaseUrl,
          archiveUrlOverride: loadCtx.archiveUrlOverride,
          canonical,
          catalogOrigin: loadCtx.catalogOrigin || null,
          catalogSource: loadCtx.catalogSource || null,
        },
      },
    })
    return true
  }

  /**
   * Manifest R2 falló (p.ej. 404) pero hay copia local del mismo UID → ofrecer abrirla.
   * @returns {Promise<boolean>} true si mostró el diálogo
   */
  maybeOfferLocalWhenManifestFails = async (opts = {}) => {
    const uid = String(
      opts.studyInstanceUID ||
        studyUidFromManifestUrl(opts.manifestUrl) ||
        ''
    ).trim()
    if (!uid) return false
    let catalog = null
    let localN = 0
    let localBytes = 0
    try {
      catalog = await getStudyCatalog(uid)
      const stats = await getCombinedStudyCacheStats(uid)
      localN = Math.max(
        Number(stats && stats.cachedComplete) || 0,
        Number(catalog && catalog.instanceCount) || 0
      )
      localBytes =
        Number(stats && stats.bytes) ||
        Number(catalog && catalog.bytesTotal) ||
        0
    } catch (e) {
      return false
    }
    if (localN <= 0) return false

    this.setState({
      studyLoadSource: null,
      manifestDownloadProgress: null,
      studySourceChoice: {
        patientName: (catalog && catalog.patientName) || '',
        studyInstanceUID: uid,
        localInstances: localN,
        localBytes,
        remoteInstances: 0,
        remoteSeries: 0,
        remoteUnavailable: true,
        remoteError: opts.errorMessage || 'error',
        catalogHint: catalog || undefined,
        pendingLoad: null,
      },
    })
    return true
  }

  handleStudySourceChooseLocal = () => {
    const choice = this.state.studySourceChoice
    const uid =
      (choice && choice.studyInstanceUID) ||
      (choice &&
        choice.pendingLoad &&
        choice.pendingLoad.canonical &&
        choice.pendingLoad.canonical.studyInstanceUID)
    this.setState({ studySourceChoice: null })
    if (!uid) return
    void this.openLocalStudyInViewer(String(uid), choice && choice.catalogHint)
  }

  handleStudySourceChooseRemote = () => {
    const choice = this.state.studySourceChoice
    const pending = choice && choice.pendingLoad
    this.setState({ studySourceChoice: null })
    if (!pending || !pending.canonical || !pending.manifestUrl) return
    void this.continueManifestLoadAfterChoice(pending)
  }

  handleStudySourceChoiceCancel = () => {
    this.setState({
      studySourceChoice: null,
      studyLoadSource: null,
      manifestDownloadProgress: null,
    })
  }

  /**
   * Continúa la carga R2 tras elegir “Abrir remoto” (sin re-preguntar).
   * @param {{
   *   manifestUrl: string,
   *   downloadUrl?: string|null,
   *   assetBaseUrl?: string|null,
   *   archiveUrlOverride?: string|null,
   *   canonical: object,
   * }} pending
   */
  continueManifestLoadAfterChoice = async (pending) => {
    const p = pending || {}
    const manifestUrl = p.manifestUrl
    const canonical = p.canonical
    if (!manifestUrl || !canonical) return
    const loadToken = this.beginStudyLoad('manifest-url')
    const remoteSource = resolveStudyLoadSourceFromManifest(canonical)
    this._preferredRemoteLoadSource = remoteSource
    this.setState({
      studyLoadSource: remoteSource,
      manifestDownloadProgress: null,
    })
    try {
      this._manifestLoadInFlight = true
      await this.runManifestLoadPipeline({
        loadToken,
        manifestUrl,
        canonical,
        assetBaseUrl: p.assetBaseUrl || null,
        archiveUrlOverride: p.archiveUrlOverride || null,
        catalogOrigin: p.catalogOrigin || null,
        catalogSource: p.catalogSource || null,
      })
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      console.error('continueManifestLoadAfterChoice:', error)
      this.setState({
        titleMessage: 'Manifest',
        textMessage: error.message || String(error),
        visibleMessage: true,
      })
      this.completeManifestLoadStudySource()
    } finally {
      this._manifestLoadInFlight = false
    }
  }

  /**
   * Poll + loadStudyFromManifestObject (manifest ya canónico).
   */
  runManifestLoadPipeline = async ({
    loadToken,
    manifestUrl,
    canonical,
    assetBaseUrl,
    archiveUrlOverride,
    catalogOrigin = null,
    catalogSource = null,
  }) => {
    this._udvTelemetrySessionStart = performance.now()
    this._udvFirstPaintBeaconSent = false
    const pacsCfg = getPacsConfig(this.state.pacsIdFromUrl)
    this._lastManifestObject = canonical
    this._lastManifestHandlers = {
      manifestUrl,
      assetBaseUrl,
      archiveUrlOverride,
      orthancCredentials: {
        username: pacsCfg && pacsCfg.USUARIO,
        password: pacsCfg && pacsCfg.PASS,
      },
    }
    const readiness = resolveManifestReadiness(canonical)
    const remoteSource = resolveStudyLoadSourceFromManifest(canonical)
    this._preferredRemoteLoadSource = remoteSource
    this.setState({
      manifestReadiness: readiness,
      manifestUpgradeFlash: false,
      manifestGrowFlash: false,
      studyLoadSource: remoteSource,
      manifestDownloadProgress: null,
    })
    if (this._stopManifestPoll) {
      this._stopManifestPoll()
      this._stopManifestPoll = null
    }
    this._previewGrowFingerprint = manifestPreviewFingerprint(canonical)
    if (readiness === 'preview' && manifestUrl) {
      this._stopManifestPoll = startManifestPreviewPoll({
        manifestUrl,
        intervalMs: 8000,
        fastIntervalMs: 2500,
        fastWindowMs: 45000,
        initialFingerprint: this._previewGrowFingerprint,
        shouldContinue: () =>
          this.state.manifestReadiness === 'preview' &&
          (this.state.manifestUrlFromUrl === manifestUrl ||
            (this._lastManifestHandlers &&
              this._lastManifestHandlers.manifestUrl === manifestUrl)),
        onPreviewUpdate: (nextJson) => {
          this.applyPreviewManifestGrow(nextJson)
        },
        onUpgrade: (nextJson) => {
          this._sendUdvManifestUpgradeBeacons()
          const next = canonicalizeManifest(nextJson, {
            assetBaseUrl,
            archiveUrlOverride,
          })
          this._lastManifestObject = next
          this._previewGrowFingerprint = manifestPreviewFingerprint(next)
          // Critico: refrescar caps (hasDicom) — sin esto el toggle DIAG queda bloqueado
          // aunque readiness ya sea diagnostic y el zip exista en R2.
          const pick = pickRenderModeForManifest(nextJson, {
            assetBaseUrl,
            honorUserSet: false,
            archiveUrlOverride,
          })
          this._lastManifestCaps = pick.caps
          try {
            if (this._filesByRenderMode) {
              this._filesByRenderMode[RENDER_MODE.DIAGNOSTIC] = null
            }
          } catch (e) {
            /* ignore */
          }
          const portalSession = this.isPortalProfileSession()
          this.setState({
            manifestReadiness: 'diagnostic',
            manifestMediaCaps: pick.caps,
            manifestUpgradeFlash: !portalSession,
            manifestGrowFlash: false,
            studyDownloadUrl: next.downloadUrl || this.state.studyDownloadUrl,
          })
          if (!portalSession) {
            window.setTimeout(() => {
              this.setState({ manifestUpgradeFlash: false })
            }, 3500)
          }
        },
      })
    }
    let mode = this.resolveModeForManifestLoad(canonical, assetBaseUrl, {
      archiveUrlOverride,
    })
    if (readiness === 'preview' && mode !== RENDER_MODE.PREVIEW) {
      mode = RENDER_MODE.PREVIEW
      this.state.renderMode = RENDER_MODE.PREVIEW
      this.setState({ renderMode: RENDER_MODE.PREVIEW })
    }
    this._filesByRenderMode = {
      [RENDER_MODE.PREVIEW]: null,
      [RENDER_MODE.DIAGNOSTIC]: null,
    }
    this._filesByRenderModeStudyUid =
      (canonical && canonical.studyInstanceUID) || null
    const loadResult = await loadStudyFromManifestObject(canonical, {
      ...this.buildManifestLoadHandlers(pacsCfg, mode, loadToken, {
        compareMerge: !!this._compareMergeActive,
      }),
      manifestUrl,
      assetBaseUrl,
      archiveUrlOverride,
      renderMode: mode,
      catalogOrigin,
      catalogSource: catalogSource || undefined,
    })
    if (!this.isStudyLoadCurrent(loadToken.gen)) return
    if (canonical.downloadUrl) {
      this.setState({ studyDownloadUrl: canonical.downloadUrl })
    }
    try {
      if (loadResult && Array.isArray(loadResult.items) && loadResult.items.length) {
        this.snapshotFilesForRenderMode(mode, loadResult.items)
      }
    } catch (e) {
      /* ignore */
    }
    this.completeManifestLoadStudySource()
    this.maybeNudgePwaInstallAfterStudyLoad()
    this.maybeShowPortalDisclaimerAfterStudyLoad()
  }

  loadStudyFromManifestUrl = async (manifestUrl, loadOpts = {}) => {
    mergeUdvTelemetryFromManifestUrl(manifestUrl)
    const compareMerge = !!loadOpts.compareMerge || !!this._compareMergeActive
    const loadToken = this.beginStudyLoad(
      compareMerge ? 'compare-prior' : 'manifest-url'
    )
    // Origen real (R2 vs WADO) se fija en runManifestLoadPipeline tras canonicalize.
    const hasRemoteZip =
      loadOpts.downloadUrl != null &&
      /^https?:\/\//i.test(String(loadOpts.downloadUrl).trim())
    this._localZipSessionActive =
      !hasRemoteZip && this.isOfflineCacheAllowed()
    this._localStudyZipReadyGen = (this._localStudyZipReadyGen || 0) + 1
    this._localZipRefreshInFlight = false
    if (compareMerge) {
      this._compareMergeActive = true
      this.setState({
        manifestDownloadProgress: null,
        gatewayPushToast: t('relatedStudies.compareToast'),
      })
    } else {
      this.setState({
        studyLoadSource: null,
        manifestDownloadProgress: null,
        studyOpenContext: resolveStudyOpenContext({
          ticketIdFromUrl: this.state.ticketIdFromUrl,
          manifestUrlFromUrl: manifestUrl,
          legacyWadoContext: this.state.legacyWadoContext,
          studyIdFromUrl: this.state.studyIdFromUrl,
          pacsIdFromUrl: this.state.pacsIdFromUrl,
          studyLoadSource: STUDY_LOAD_SOURCE.R2,
        }),
        ...(hasRemoteZip
          ? {}
          : {
              studyDownloadUrl: null,
              localStudyZip: {
                eligible: !!this._localZipSessionActive,
                ready: false,
                expected: 0,
                cached: 0,
                studyUid: loadOpts.studyInstanceUID
                  ? String(loadOpts.studyInstanceUID)
                  : '',
                networkFallback: false,
              },
            }),
      })
    }
    this._preferredRemoteLoadSource = null
    const archiveUrlOverride =
      loadOpts.downloadUrl != null && String(loadOpts.downloadUrl).trim()
        ? String(loadOpts.downloadUrl).trim()
        : null
    const ticketApiParams =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : null
    const catalogOrigin =
      loadOpts.catalogOrigin ||
      resolveStudyCatalogOrigin({
        searchParams: ticketApiParams,
        controllerLinks: loadControllerLinks(),
      })
    const catalogSource = loadOpts.catalogSource || null
    if (!this.state.ticketIdFromUrl && !compareMerge) {
      this.applyViewerSessionPolicy({ searchParams: ticketApiParams })
    }
    try {
      this._manifestLoadInFlight = true
      const res = await fetch(manifestUrl, {
        credentials: 'omit',
        signal: loadToken.signal,
      })
      if (!res.ok) {
        throw new Error(`No se pudo obtener el manifest (${res.status})`)
      }
      const manifestJson = await res.json()
      if (!this.isStudyLoadCurrent(loadToken.gen)) return
      let assetBaseUrl = null
      try {
        const u = new URL(manifestUrl)
        assetBaseUrl = `${u.origin}/`
      } catch (e) {
        /* ignore */
      }
      const canonical = canonicalizeManifest(manifestJson, {
        assetBaseUrl,
        archiveUrlOverride,
      })

      if (compareMerge && canonical && canonical.studyInstanceUID) {
        this._comparePriorStudyUid = String(canonical.studyInstanceUID)
      }

      const prompted = await this.maybePromptRicherLocalVsManifest(canonical, {
        manifestUrl,
        downloadUrl: loadOpts.downloadUrl || null,
        assetBaseUrl,
        archiveUrlOverride,
        skipSourceChoice: !!loadOpts.skipSourceChoice || compareMerge,
        catalogOrigin,
        catalogSource,
      })
      if (prompted) return
      if (!this.isStudyLoadCurrent(loadToken.gen)) return

      await this.runManifestLoadPipeline({
        loadToken,
        manifestUrl,
        canonical,
        assetBaseUrl,
        archiveUrlOverride,
        catalogOrigin,
        catalogSource,
      })
      if (compareMerge && !this._unmounted) {
        this.setState({ gatewayPushToast: null })
        try {
          this.maybeOpenComparePriorSeries(this._compareMergedFiles)
        } catch (eOpen) {
          /* ignore */
        }
      }
    } catch (error) {
      if (isStudyLoadAbortError(error)) return
      console.error('loadStudyFromManifestUrl:', error)
      if (compareMerge) {
        const msg = String((error && error.message) || error || '')
        const isManifestAuth = /manifest\s*\(\s*40[13]\s*\)/i.test(msg)
        if (isManifestAuth && !this._compareManifestLeaseRetried) {
          this._compareManifestLeaseRetried = true
          const renewed = await this.tryStaffLeaseExtendOnce()
          const priorTk = this._comparePriorTicketIdForLease
          const pickOpts = this._comparePriorPickOpts
          const ticketApiParams = this._comparePriorTicketApiParams
          if (renewed && priorTk && pickOpts) {
            try {
              const again = await resolveViewerTicket(priorTk, {
                baseUrl: getTicketApiBase(ticketApiParams),
              })
              if (again.ok && again.ticket) {
                const picked = await pickViewerCandidate(
                  again.ticket.candidates,
                  pickOpts
                )
                const target = candidateToLoadTarget(picked.candidate)
                if (target && target.kind === 'r2' && target.manifestUrl) {
                  this.rememberTicketCandidateWalk(picked, {
                    mode: 'merge',
                    ticket: again.ticket,
                    ticketApiParams,
                    pickOpts,
                  })
                  // Reintento: no limpia sesión compare aún
                  void this.loadStudyFromManifestUrl(target.manifestUrl, {
                    downloadUrl: target.downloadUrl || null,
                    studyInstanceUID:
                      this._comparePriorStudyUid ||
                      loadOpts.studyInstanceUID ||
                      null,
                    skipSourceChoice: true,
                    catalogSource: STUDY_SOURCE.TICKET,
                    compareMerge: true,
                  })
                  return
                }
              }
            } catch (eRetry) {
              /* cae al error UI abajo */
            }
          }
        }
        this._compareManifestLeaseRetried = false
        this.clearComparePriorSession()
        this.setState({
          gatewayPushToast: null,
          titleMessage: 'Manifest',
          textMessage: error.message || String(error),
          visibleMessage: true,
        })
        this.completeManifestLoadStudySource()
        return
      }
      const offered = await this.maybeOfferLocalWhenManifestFails({
        studyInstanceUID: loadOpts.studyInstanceUID || null,
        manifestUrl,
        errorMessage: error.message || String(error),
      })
      if (offered) return
      const orthancId = this.state.studyIdFromUrl
      if (orthancId) {
        console.warn(
          'Manifest R2 no disponible o sin imágenes válidas. Reintentando con Orthanc studyId:',
          orthancId
        )
        try {
          this.props.clearingStore()
        } catch (e) {
          /* ignore */
        }
        this.setState({ studyLoadSource: null, manifestDownloadProgress: null })
        this.loadStudyFromUrl(orthancId)
      } else {
        this.setState({
          titleMessage: 'Manifest',
          textMessage: error.message || String(error),
          visibleMessage: true,
        })
        this.completeManifestLoadStudySource()
      }
    } finally {
      this._manifestLoadInFlight = false
    }
  }

  // Nuevo método para cargar planos ortogonales
  loadOrthogonalPlanes = async (activeViewer) => {
    if (!activeViewer || !activeViewer.image) return;

    const files = this.props.files;
    if (!files || files.length === 0) return;

    // Esperar un poco para asegurar que los viewers estén completamente inicializados
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      // Obtener el plano actual
      const currentPlane = activeViewer.mprPlanePosition();
      if (!currentPlane) return;

      // Encontrar series con diferentes planos
      const seriesByPlane = {
        axial: null,
        coronal: null,
        sagittal: null
      };

      // Agrupar series por plano
      for (const file of files) {
        if (file.image) {
          const plane = this.getPlaneFromImage(file.image);
          if (plane && !seriesByPlane[plane]) {
            seriesByPlane[plane] = file;
          }
        }
      }

      // Determinar qué planos cargar basado en el plano actual
      let planesToLoad = [];
      if (currentPlane === 'axial') {
        planesToLoad = ['coronal', 'sagittal'];
      } else if (currentPlane === 'coronal') {
        planesToLoad = ['axial', 'sagittal'];
      } else if (currentPlane === 'sagittal') {
        planesToLoad = ['axial', 'coronal'];
      }

      // Cargar los planos secuencialmente
      for (let i = 0; i < planesToLoad.length; i++) {
        const plane = planesToLoad[i];
        const series = seriesByPlane[plane];
        
        // Verificar que el viewer existe antes de usarlo
        if (!this.dcmViewers || !Array.isArray(this.dcmViewers) || i + 1 >= this.dcmViewers.length) {
          console.warn(`Viewer ${i + 1} not available for plane ${plane} - dcmViewers not ready`);
          continue;
        }
        
        // Verificar que el viewer no es null o undefined
        if (!this.dcmViewers[i + 1]) {
          console.warn(`Viewer ${i + 1} is null or undefined for plane ${plane}`);
          continue;
        }
        
        // Verificar que el viewer tiene los métodos necesarios
        if (typeof this.dcmViewers[i + 1].runTool !== 'function') {
          console.warn(`Viewer ${i + 1} is not properly initialized for plane ${plane}`);
          continue;
        }
        
        const viewer = this.dcmViewers[i + 1];

        if (series && viewer) {
          try {
            // Limpiar el viewport antes de cargar
            viewer.runTool('clear');
            
            // Esperar un momento entre cargas para liberar recursos
            await new Promise(resolve => setTimeout(resolve, 100));

            // Cargar la serie
            await new Promise((resolve, reject) => {
              try {
                viewer.runTool('setfiles', [series]);
                viewer.runTool('openimage', 0);
                resolve();
              } catch (error) {
                console.error(`Error loading ${plane} plane:`, error);
                reject(error);
              }
            });

            // Esperar a que la imagen se cargue completamente
            await new Promise(resolve => setTimeout(resolve, 200));
          } catch (error) {
            console.error(`Failed to load ${plane} plane:`, error);
            // Continuar con el siguiente plano incluso si este falla
            continue;
          }
        }
      }
    } catch (error) {
      console.error('Error in loadOrthogonalPlanes:', error);
    }
  }

  // Método auxiliar para determinar el plano de una imagen
  getPlaneFromImage = (image) => {
    try {
      if (!image || !image.data) return null;

      const imageOrientation = image.data.string('x00200037');
      if (!imageOrientation) return null;

      const values = imageOrientation.split('\\').map(v => parseFloat(v));
      if (values.length !== 6) return null;

      // Normalizar los vectores
      const rowVector = [values[0], values[1], values[2]];
      const colVector = [values[3], values[4], values[5]];
      
      // Calcular el vector normal usando producto cruz
      const normalVector = [
        rowVector[1] * colVector[2] - rowVector[2] * colVector[1],
        rowVector[2] * colVector[0] - rowVector[0] * colVector[2],
        rowVector[0] * colVector[1] - rowVector[1] * colVector[0]
      ];

      // Normalizar el vector normal
      const magnitude = Math.sqrt(
        normalVector[0] * normalVector[0] +
        normalVector[1] * normalVector[1] +
        normalVector[2] * normalVector[2]
      );

      if (magnitude === 0) return null;

      const normalizedNormal = normalVector.map(v => Math.abs(v / magnitude));

      // Determinar el plano basado en el componente dominante
      const threshold = 0.9; // Umbral para considerar un vector dominante
      if (normalizedNormal[0] > threshold) return 'sagittal';
      if (normalizedNormal[1] > threshold) return 'coronal';
      if (normalizedNormal[2] > threshold) return 'axial';
      
      return null;
    } catch (error) {
      console.error('Error in getPlaneFromImage:', error);
      return null;
    }
  }

  handleResize = () => {
    // Actualizar el estado de isMobile cuando cambia el tamaño de la ventana
    const width = window.innerWidth
    const isMobileDevice = isMobile || isTablet || width < 1024
    const isTabletDevice = isTablet || (width >= 768 && width <= 1024)
    const prevTier = this.getClassicToolbarTier(this.state.viewportWidth)
    const nextTier = this.getClassicToolbarTier(width)
    const tierChanged =
      prevTier.collapseExport !== nextTier.collapseExport ||
      prevTier.collapseStudy !== nextTier.collapseStudy

    const patch = {}
    if (isMobileDevice !== this.state.isMobile) {
      patch.isMobile = isMobileDevice
    }
    if (tierChanged || Math.abs(width - (this.state.viewportWidth || 0)) >= 24) {
      patch.viewportWidth = width
    }
    if (
      isTabletDevice &&
      !isPwaStandalone() &&
      !getPreferFullscreen() &&
      !document.fullscreenElement &&
      !document.webkitFullscreenElement
    ) {
      patch.showFullscreenPrompt = true
    }
    if (Object.keys(patch).length) {
      this.setState(patch)
    }

    // Hammer/resize de canvas: lo gestiona cada DicomViewer.handleResize
    // (umbral de tamaño). Evita tormenta de forceReconfigureHammerJS en FS Android.
    this.syncCineBottomCssVar()
  }

  /**
   * Un solo resize diferido tras entrar/salir de fullscreen (layout + canvas).
   * Absorbe el cascade resize + fullscreenchange del browser.
   */
  scheduleFullscreenViewportResize = () => {
    if (this._fsViewportResizeTimer) {
      clearTimeout(this._fsViewportResizeTimer)
    }
    this._fsViewportResizeTimer = setTimeout(() => {
      this._fsViewportResizeTimer = null
      this.syncCineBottomCssVar()
      this.dicomViewersRefs.forEach((viewer) => {
        if (viewer && typeof viewer.handleResize === 'function') {
          // Invalidar tamaño cacheado para forzar un resize real tras FS
          viewer._lastViewerSize = null
          viewer.handleResize()
        }
      })
    }, 200)
  }

  // Manejar cambios de orientación en móviles
  handleOrientationChange = () => {
    // Debounce: orientationchange + matchMedia disparan a la vez
    if (this._mprOrientTimer) clearTimeout(this._mprOrientTimer)
    this._mprOrientTimer = setTimeout(() => {
      this._mprOrientTimer = null
      if (this._unmounted) return
      console.log('App - Orientación cambiada, reconfigurando viewports')

      const mprDual =
        this.state.visibleMprSagittal ||
        this.state.visibleMprCoronal ||
        this.state.visibleMprAxial

      if (this.state.visibleMprOrthogonal) {
        this.changeToOrthogonalView()
      } else if (mprDual) {
        const selected = this.state.visibleMprSagittal
          ? 'sagittal'
          : this.state.visibleMprCoronal
            ? 'coronal'
            : 'axial'
        this.changeToDualPlaneMprView(selected)
      } else {
        this.dicomViewersRefs.forEach((viewer) => {
          if (viewer && viewer.forceReconfigureHammerJS) {
            viewer.forceReconfigureHammerJS()
          }
          if (viewer && typeof viewer.handleResize === 'function') {
            viewer._lastViewerSize = null
            viewer.handleResize()
          }
        })
        try {
          this.forceViewportSync()
        } catch (e) { /* ignore */ }
      }

      // DicomViewer.handleOrientationChange corre a ~1200ms; MPR no usa CT ref-lines
      if (this.state.visibleReferenceLines && (this.state.visibleMprOrthogonal || mprDual)) {
        setTimeout(() => this.scheduleMprReferenceLinesSetup({ delay: 0 }), 1400)
      }
    }, 450)
  }

  toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        this._pwaSkipFullscreenRestore = false
        const ok = await requestDocumentFullscreen()
        if (!ok) return
        setPreferFullscreen(true)
        this.setState({
          showFullscreenPrompt: false,
          isFullscreen: true,
        }, () => this.scheduleFullscreenViewportResize())
      } else {
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen()
          }
          if (!isPwaStandalone()) {
            setPreferFullscreen(false)
          }
          this.setState({ isFullscreen: false }, () => this.scheduleFullscreenViewportResize())
        } catch (error) {
          console.warn('Exit fullscreen failed:', error)
        }
      }
    } catch (error) {
      console.error('Fullscreen operation failed:', error)
      this.setState({ isFullscreen: !!document.fullscreenElement }, () =>
        this.scheduleFullscreenViewportResize()
      )
    }
  }

  handleFullscreenChange = () => {
    const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement)
    if (this.state.isFullscreen !== isFullscreen) {
      this.setState({ isFullscreen }, () => this.scheduleFullscreenViewportResize())
    } else {
      this.scheduleFullscreenViewportResize()
    }
  }
}

const mapStateToProps = (state) => {
  return {
    localFileStore: state.localFileStore,
    //allFiles: state.allFiles,
    files: state.files,
    series: state.series,
    isOpen: state.isOpen,
    tool: state.tool,
    activeDcmIndex: state.activeDcmIndex,
    explorerActivePatientIndex: state.explorerActivePatientIndex,
    explorerActiveStudyIndex: state.explorerActiveStudyIndex,
    explorerActiveSeriesIndex: state.explorerActiveSeriesIndex,
    measurements: state.measurements,
    layout: state.layout,
    dicomdir: state.dicomdir,
    fsZippedFile: state.fsZippedFile,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    clearingStore: () => dispatch(clearStore()),
    setLocalFileStore: (file) => dispatch(localFileStore(file)),
    toolStore: (tool) => dispatch(dcmTool(tool)),
    isOpenStore: (value) => dispatch(dcmIsOpen(value)),
    setActiveDcm: (dcm) => dispatch(activeDcm(dcm)),
    setActiveDcmIndex: (index) => dispatch(activeDcmIndex(index)),
    setActiveMeasurements: (measurements) => dispatch(activeMeasurements(measurements)),
    setLayoutStore: (row, col) => dispatch(setLayout(row, col)),
    setDicomdirStore: (dicomdir) => dispatch(setDicomdir(dicomdir)),
    setFsZippedFile: (file) => dispatch(setZippedFile(file)),
    setVolumeStore: (file) => dispatch(setVolume(file)),
    setFilesStore: (files) => dispatch(filesStore(files)),
    setSeriesStore: (series) => dispatch(seriesStore(series)),
    setExplorerStore: (data) => dispatch(explorer(data)),
    setExplorerActiveSeriesIndex: (index) => dispatch(explorerActiveSeriesIndex(index)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(App))
